/* eslint-disable */
import { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { createClient } from '@supabase/supabase-js';
import TruckCheck, { truckListKey, TRUCK_LIST_NAMES } from './TruckCheck';
import JobMap from './JobMap';

// ═══════════════════════════════════════════════════════════════════════════════
//  SOLARABILITY SAFETY HUB — Full Production Build
//  ─────────────────────────────────────────────────
//  Features:
//  ✓ Supabase Auth (login/signup/password reset)
//  ✓ Supabase Database (form submissions persisted)
//  ✓ Supabase Storage (photo uploads)
//  ✓ Admin/Supervisor Dashboard (review, approve, flag)
//  ✓ PDF Export (generate compliance-ready reports)
//  ✓ Mobile responsive (field crew friendly)
//  ✓ SolarAbility branding
//
//  SETUP:
//  1. npm install @supabase/supabase-js jspdf jspdf-autotable
//  2. Replace SUPABASE_URL and SUPABASE_ANON_KEY below
//  3. Run the SQL from SETUP_GUIDE.md in Supabase SQL Editor
// ═══════════════════════════════════════════════════════════════════════════════

// ━━━ CONFIG — Replace with your Supabase project values ━━━━━━━━━━━━━━━━━━━━
const SUPABASE_URL = "https://prhdzdzgjzhlicvbklcs.supabase.co";
const SUPABASE_ANON_KEY = "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InByaGR6ZHpnanpobGljdmJrbGNzIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzI0MDAwMjksImV4cCI6MjA4Nzk3NjAyOX0.N_wO4kiq6Wpe1pcfKQZ2saZiQ8a5QKM-ppGI_L-GNPQ";
const USE_SUPABASE = SUPABASE_URL !== "YOUR_SUPABASE_URL";
const ADMIN_EMAIL = "admin@solarability.com"; // Where to send email notifications
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

// ─── i18n — English / Spanish ────────────────────────────────────────────────
const T = {
  en: {
    safetyHub: "Safety Hub", dashboard: "Dashboard", newJHA: "New JHA", tsaSurvey: "TSA Site Survey",
    projects: "Projects", adminPanel: "Admin Panel", supervisor: "Supervisor", fieldCrew: "Field Crew",
    signOut: "Sign Out", menu: "MENU", welcomeBack: "Welcome Back", createAccount: "Create Account",
    resetPassword: "Reset Password", signIn: "Sign In", forgotPassword: "Forgot Password?",
    email: "Email", password: "Password", fullName: "Full Name", demoMode: "Demo Mode",
    demoDesc: "Supabase not configured. Data is stored in memory only. Use admin@ in email for admin access.",
    portalDesc: "Employee safety & compliance portal",
    // Dashboard
    totalForms: "Total Forms", jhaReports: "JHA Reports", siteSurveys: "Site Surveys",
    thisMonth: "This Month", recentSubmissions: "Recent Submissions", noSubmissions: "No submissions yet",
    noSubmissionsDesc: "Create your first report using the buttons above",
    // JHA
    jobHazardAnalysis: "Job Hazard Analysis", jhaDesc: "Identify hazards and controls for each work step",
    generalInfo: "General Information", jobTitle: "Job Title", location: "Location", date: "Date",
    crewMembers: "Crew Members", description: "Description", hazardId: "Hazard Identification",
    addStep: "Add Step", jobStep: "Job Step", potentialHazard: "Potential Hazard", riskLevel: "Risk Level",
    controlMeasure: "Control Measure", supportingPhotos: "Supporting Photos", cancel: "Cancel",
    submitJHA: "Submit JHA", submitting: "Submitting...", step: "Step",
    // Survey
    windfreeTitle: "TSA Site Survey", surveyDesc: "Complete solar site assessment — SolarAbility LLC",
    homeownerName: "Homeowner Name", address: "Address", drawingsAvailable: "Drawings Available?",
    photoChecklist: "Photo Checklist", uploadPhoto: "Upload Photo", replacePhoto: "Replace Photo",
    markDone: "Mark as done (no photo)", photos: "photos",
    meterMake: "Meter Make / Model", locationDesc: "Location Description", utilityEntrance: "Utility Entrance",
    underground: "Underground", overhead: "Overhead", transformerVolt: "Transformer Voltage / Voltage Required",
    serviceGrounding: "Existing Service Grounding", electricalPanel: "Electrical Panel",
    mainBreakerRating: "Main Breaker Rating (A)", busBarRating: "Bus Bar Rating (A)",
    voltageMeasured: "Voltage (Measured)", voltageType: "Voltage Type", spacesAvailable: "Spaces Available",
    subpanel: "Subpanel", subpanelExists: "Subpanel Exists?", inverterComms: "Inverter & Communications",
    inverterLocation: "Proposed Inverter Location", modemPresent: "Modem/Router Present?",
    modemLocation: "Modem/Router Location", openPorts: "Open Ports on Router?",
    hardwireRun: "Hardwire: Proposed Cable Run", wifiSignal: "Wi-Fi Signal at Inverter",
    cellularCoverage: "Cellular: Verified Coverage?", interconnection: "Interconnection Strategy",
    electricalNotes: "Electrical Notes", roofingDetails: "Roofing Details",
    roofMake: "Roofing Make / Manufacturer", roofCondition: "Roof Condition",
    roofAccess: "Roof Access?", atticAccess: "Attic Access?", roofStructure: "Roof & Structural Info",
    roofPitch: "Roof Pitch (Degrees)", azimuth: "Azimuth", buildingHeight: "Building Height",
    heightToEave: "Height (Ground to Eave)", roofingType: "Roofing Type", structureType: "Structure Type",
    structureDetails: "Additional Structure Details", rafterSize: "Rafter / Truss Size (Nominal)",
    rafterSpacing: "Rafter / Truss Spacing", roofNotes: "Additional Roof Notes",
    addersChecklist: "Adders Checklist", trenching: "Trenching & Distance",
    conduitRun: "Conduit Run Distance", batteryIntegrations: "Battery Integrations",
    rooftopAccess: "Access to Rooftop or Attic", rentals: "Rentals?",
    roofAttachments: "Roof Attachments", electricalUpgrades: "Electrical Upgrades Apparent?",
    groundingViolations: "Grounding / Code Violations", other: "Other(s)",
    additionalPhotos: "Additional Site Photos", submitSurvey: "Submit TSA Survey",
    // Projects
    projectTracker: "Project Tracker", newProject: "New Project", projectName: "Project Name",
    clientName: "Client Name", systemSize: "System Size (kW)", projectStatus: "Status",
    completion: "Completion", tasks: "Tasks", materials: "Materials", notes: "Notes",
    addTask: "Add Task", addMaterial: "Add Material", addNote: "Add Note",
    taskName: "Task name", materialName: "Material", quantity: "Qty", unit: "Unit",
    assigned: "Assigned To", dueDate: "Due Date", priority: "Priority",
    notStarted: "Not Started", inProgress: "In Progress", pendingSubmission: "Pending Submission", completed: "Completed", onHold: "On Hold",
    saveProject: "Save Project", deleteProject: "Delete Project", editProject: "Edit",
    low: "Low", medium: "Medium", high: "High", critical: "Critical",
    noProjects: "No projects yet", noProjectsDesc: "Create your first project to track installations",
    projectNotes: "Project Notes", materialsList: "Materials List",
    noteText: "Write a note...", createdBy: "By", at: "at",
    // Admin
    pendingReview: "Pending Review", approved: "Approved", flagged: "Flagged", rejected: "Rejected",
    searchSubmissions: "Search submissions...", all: "All", total: "Total", pending: "Pending",
    // Status bar
    offlineMsg: "You're offline — forms will be saved locally and synced when you reconnect",
    syncingMsg: "Syncing offline submissions...", formsSavedOffline: "form(s) saved offline",
    syncNow: "Sync Now", submittedSuccess: "Submitted Successfully!",
    savedToDb: "has been saved to the database", savedOffline: "has been saved offline",
    willSync: "It will sync automatically when you're back online.",
    supervisorNotified: "Your supervisor will be notified.", backToDash: "Back to Dashboard",
    // Language
    language: "Language", english: "English", spanish: "Español",
    yes: "Yes", no: "No", back: "Back", previous: "Previous", next: "Next",
    // Inventory
    inventory: "Inventory", warehouse: "Warehouse", truck: "Truck", addItem: "Add Item",
    itemName: "Item Name", category: "Category", sku: "SKU / Part #", minStock: "Min Stock",
    currentStock: "Current Stock", transferTo: "Transfer", useOnJob: "Use on Job",
    moveQty: "Qty to Move", jobName: "Job / Site Name", lowStock: "Low Stock",
    allItems: "All Items", electrical: "Electrical", tools: "Tools", critterGuard: "Critter Guard",
    general: "General", transferHistory: "Transfer Log", noItems: "No inventory items yet",
    noItemsDesc: "Add items to start tracking your warehouse and truck inventory",
    itemAdded: "Item added", itemUpdated: "Item updated", transferred: "Transferred",
    usedOnJob: "Used on job", searchInventory: "Search inventory...",
    warehouseQty: "Warehouse", truckQty: "Truck", totalQty: "Total",
    costing: "Job Costing", truckCheck: "Truck Check", schedule: "Schedule", jobMap: "Job Map", openInJobMap: "Open in Job Map", procedures: "Procedures",
    // Service Assessment
    serviceAssessment: "Service Assessment", serviceType: "Service Type", installCloseout: "Install Closeout",
    leakAssessment: "Leak Assessment", productionIssue: "Production Issue",
    systemInspection: "System Inspection", electricalIssue: "Electrical Issue",
    generalOM: "General O&M", clientInfo: "Client Information", clientName2: "Client / Property Owner",
    clientPhone: "Phone", clientEmail: "Email", referralSource: "Referral Source",
    referralName: "Referred By (Company / Person)", issueDescription: "Issue Description",
    reportedIssue: "Reported Issue / Reason for Visit", systemInfo: "System Information",
    systemSizeKw: "System Size (kW)", originalInstaller: "Original Installer",
    installDate: "Install Date / System Age", panelMake: "Panel Make / Model",
    inverterMake: "Inverter Make / Model", numPanels: "Number of Panels",
    findings: "On-Site Findings", rootCause: "Root Cause / Diagnosis",
    recommendations: "Recommendations", assessmentPhotos: "Assessment Photos",
    submitAssessment: "Submit Assessment", assessmentFee: "Assessment Fee",
    // Partners
    partners: "Partners", partnerDirectory: "Partner Directory", addPartner: "Add Partner", crew: "Crew", contacts: "Contacts", quotes: "Service Quotes", estimator: "R&R Estimator",
    partnerName: "Partner Name", submissionMethod: "Submission Method",
    methodApp: "Their App", methodPortal: "Web Portal", methodEmail: "Email",
    methodFolder: "Shared Folder", contactInfo: "Contact Info", portalLink: "Portal / App Link",
    accessNotes: "Access / Submission Notes", customFields: "Required Fields for This Partner",
    addField: "Add Field", fieldLabel: "Field label (e.g. Ticket #)",
    partnerTasks: "Extra Workflow Steps", addStep: "Add Step", stepName: "Step (e.g. Upload to Workforce app)",
    noPartners: "No partners yet", noPartnersDesc: "Add a partner to track their submission requirements",
    selectPartner: "Partner / Source", noPartnerOption: "None — direct / internal job",
    // Safety
    safety: "Safety", incidents: "Incidents", inspections: "Inspections",
    correctiveActions: "Corrective Actions", reportIncident: "Report Incident",
    newInspection: "New Inspection", newAction: "New Action",
  },
  es: {
    safetyHub: "Centro de Seguridad", dashboard: "Tablero", newJHA: "Nuevo ART", tsaSurvey: "Encuesta TSA",
    projects: "Proyectos", adminPanel: "Panel Admin", supervisor: "Supervisor", fieldCrew: "Equipo de Campo",
    signOut: "Cerrar Sesión", menu: "MENÚ", welcomeBack: "Bienvenido", createAccount: "Crear Cuenta",
    resetPassword: "Restablecer Contraseña", signIn: "Iniciar Sesión", forgotPassword: "¿Olvidó su contraseña?",
    email: "Correo", password: "Contraseña", fullName: "Nombre Completo", demoMode: "Modo Demo",
    demoDesc: "Supabase no configurado. Los datos se guardan solo en memoria. Use admin@ para acceso de admin.",
    portalDesc: "Portal de seguridad y cumplimiento para empleados",
    totalForms: "Total de Formularios", jhaReports: "Reportes ART", siteSurveys: "Encuestas de Sitio",
    thisMonth: "Este Mes", recentSubmissions: "Envíos Recientes", noSubmissions: "Sin envíos todavía",
    noSubmissionsDesc: "Cree su primer reporte usando los botones de arriba",
    jobHazardAnalysis: "Análisis de Riesgos del Trabajo", jhaDesc: "Identifique peligros y controles para cada paso",
    generalInfo: "Información General", jobTitle: "Título del Trabajo", location: "Ubicación", date: "Fecha",
    crewMembers: "Miembros del Equipo", description: "Descripción", hazardId: "Identificación de Peligros",
    addStep: "Agregar Paso", jobStep: "Paso del Trabajo", potentialHazard: "Peligro Potencial",
    riskLevel: "Nivel de Riesgo", controlMeasure: "Medida de Control", supportingPhotos: "Fotos de Soporte",
    cancel: "Cancelar", submitJHA: "Enviar ART", submitting: "Enviando...", step: "Paso",
    windfreeTitle: "Encuesta TSA", surveyDesc: "Evaluación completa del sitio solar — SolarAbility LLC",
    homeownerName: "Nombre del Propietario", address: "Dirección", drawingsAvailable: "¿Planos Disponibles?",
    photoChecklist: "Lista de Fotos", uploadPhoto: "Subir Foto", replacePhoto: "Reemplazar Foto",
    markDone: "Marcar como listo (sin foto)", photos: "fotos",
    meterMake: "Marca / Modelo del Medidor", locationDesc: "Descripción de Ubicación",
    utilityEntrance: "Entrada de Servicio", underground: "Subterráneo", overhead: "Aéreo",
    transformerVolt: "Voltaje del Transformador", serviceGrounding: "Puesta a Tierra Existente",
    electricalPanel: "Panel Eléctrico", mainBreakerRating: "Interruptor Principal (A)",
    busBarRating: "Barra de Bus (A)", voltageMeasured: "Voltaje (Medido)", voltageType: "Tipo de Voltaje",
    spacesAvailable: "Espacios Disponibles", subpanel: "Subpanel", subpanelExists: "¿Existe Subpanel?",
    inverterComms: "Inversor y Comunicaciones", inverterLocation: "Ubicación del Inversor",
    modemPresent: "¿Módem/Router Presente?", modemLocation: "Ubicación del Módem",
    openPorts: "¿Puertos Abiertos?", hardwireRun: "Cable: Ruta Propuesta",
    wifiSignal: "Wi-Fi: Señal en Inversor", cellularCoverage: "Celular: ¿Cobertura Verificada?",
    interconnection: "Estrategia de Interconexión", electricalNotes: "Notas Eléctricas",
    roofingDetails: "Detalles del Techo", roofMake: "Marca del Techo", roofCondition: "Condición del Techo",
    roofAccess: "¿Acceso al Techo?", atticAccess: "¿Acceso al Ático?", roofStructure: "Estructura del Techo",
    roofPitch: "Inclinación (Grados)", azimuth: "Azimut", buildingHeight: "Altura del Edificio",
    heightToEave: "Altura (Suelo a Alero)", roofingType: "Tipo de Techo", structureType: "Tipo de Estructura",
    structureDetails: "Detalles Adicionales", rafterSize: "Tamaño de Viga", rafterSpacing: "Espaciado de Vigas",
    roofNotes: "Notas del Techo", addersChecklist: "Lista de Adiciones", trenching: "Zanja y Distancia",
    conduitRun: "Distancia del Conducto", batteryIntegrations: "Integración de Baterías",
    rooftopAccess: "Acceso al Techo o Ático", rentals: "¿Alquileres?",
    roofAttachments: "Fijaciones del Techo", electricalUpgrades: "¿Mejoras Eléctricas Aparentes?",
    groundingViolations: "Violaciones de Código", other: "Otro(s)",
    additionalPhotos: "Fotos Adicionales", submitSurvey: "Enviar Encuesta TSA",
    projectTracker: "Seguimiento de Proyectos", newProject: "Nuevo Proyecto", projectName: "Nombre del Proyecto",
    clientName: "Nombre del Cliente", systemSize: "Tamaño del Sistema (kW)", projectStatus: "Estado",
    completion: "Completado", tasks: "Tareas", materials: "Materiales", notes: "Notas",
    addTask: "Agregar Tarea", addMaterial: "Agregar Material", addNote: "Agregar Nota",
    taskName: "Nombre de la tarea", materialName: "Material", quantity: "Cant.", unit: "Unidad",
    assigned: "Asignado A", dueDate: "Fecha Límite", priority: "Prioridad",
    notStarted: "Sin Iniciar", inProgress: "En Progreso", pendingSubmission: "Pendiente de Envío", completed: "Completado", onHold: "En Pausa",
    saveProject: "Guardar Proyecto", deleteProject: "Eliminar Proyecto", editProject: "Editar",
    low: "Bajo", medium: "Medio", high: "Alto", critical: "Crítico",
    noProjects: "Sin proyectos todavía", noProjectsDesc: "Cree su primer proyecto para seguir instalaciones",
    projectNotes: "Notas del Proyecto", materialsList: "Lista de Materiales",
    noteText: "Escriba una nota...", createdBy: "Por", at: "a las",
    pendingReview: "Revisión Pendiente", approved: "Aprobado", flagged: "Marcado", rejected: "Rechazado",
    searchSubmissions: "Buscar envíos...", all: "Todos", total: "Total", pending: "Pendiente",
    offlineMsg: "Estás sin conexión — los formularios se guardarán localmente y se sincronizarán al reconectar",
    syncingMsg: "Sincronizando envíos...", formsSavedOffline: "formulario(s) guardados sin conexión",
    syncNow: "Sincronizar", submittedSuccess: "¡Enviado Exitosamente!",
    savedToDb: "se ha guardado en la base de datos", savedOffline: "se ha guardado sin conexión",
    willSync: "Se sincronizará automáticamente cuando vuelva a estar en línea.",
    supervisorNotified: "Su supervisor será notificado.", backToDash: "Volver al Tablero",
    language: "Idioma", english: "English", spanish: "Español",
    yes: "Sí", no: "No", back: "Volver", previous: "Anterior", next: "Siguiente",
    // Inventory
    inventory: "Inventario", warehouse: "Almacén", truck: "Camión", addItem: "Agregar Artículo",
    itemName: "Nombre del Artículo", category: "Categoría", sku: "SKU / # de Parte", minStock: "Stock Mínimo",
    currentStock: "Stock Actual", transferTo: "Transferir", useOnJob: "Usar en Trabajo",
    moveQty: "Cant. a Mover", jobName: "Trabajo / Sitio", lowStock: "Stock Bajo",
    allItems: "Todos", electrical: "Eléctrico", tools: "Herramientas", critterGuard: "Protección Animal",
    general: "General", transferHistory: "Registro de Movimientos", noItems: "Sin artículos todavía",
    noItemsDesc: "Agregue artículos para comenzar a rastrear su inventario",
    itemAdded: "Artículo agregado", itemUpdated: "Artículo actualizado", transferred: "Transferido",
    usedOnJob: "Usado en trabajo", searchInventory: "Buscar inventario...",
    warehouseQty: "Almacén", truckQty: "Camión", totalQty: "Total",
    costing: "Costos de Trabajo", truckCheck: "Revisión del Camión", schedule: "Calendario", jobMap: "Mapa de Trabajos", openInJobMap: "Ver en el mapa", procedures: "Procedimientos",
    // Service Assessment
    serviceAssessment: "Evaluación de Servicio", serviceType: "Tipo de Servicio", installCloseout: "Cierre de Instalación",
    leakAssessment: "Evaluación de Fuga", productionIssue: "Problema de Producción",
    systemInspection: "Inspección del Sistema", electricalIssue: "Problema Eléctrico",
    generalOM: "O&M General", clientInfo: "Información del Cliente", clientName2: "Cliente / Propietario",
    clientPhone: "Teléfono", clientEmail: "Correo", referralSource: "Fuente de Referencia",
    referralName: "Referido Por (Empresa / Persona)", issueDescription: "Descripción del Problema",
    reportedIssue: "Problema Reportado / Razón de Visita", systemInfo: "Información del Sistema",
    systemSizeKw: "Tamaño del Sistema (kW)", originalInstaller: "Instalador Original",
    installDate: "Fecha de Instalación / Antigüedad", panelMake: "Marca / Modelo del Panel",
    inverterMake: "Marca / Modelo del Inversor", numPanels: "Número de Paneles",
    findings: "Hallazgos en Sitio", rootCause: "Causa Raíz / Diagnóstico",
    recommendations: "Recomendaciones", assessmentPhotos: "Fotos de Evaluación",
    submitAssessment: "Enviar Evaluación", assessmentFee: "Tarifa de Evaluación",
    // Partners
    partners: "Socios", partnerDirectory: "Directorio de Socios", addPartner: "Agregar Socio", crew: "Cuadrilla", contacts: "Contactos", quotes: "Cotizaciones", estimator: "Estimador R&R",
    partnerName: "Nombre del Socio", submissionMethod: "Método de Envío",
    methodApp: "Su App", methodPortal: "Portal Web", methodEmail: "Correo",
    methodFolder: "Carpeta Compartida", contactInfo: "Información de Contacto", portalLink: "Enlace de Portal / App",
    accessNotes: "Notas de Acceso / Envío", customFields: "Campos Requeridos por Este Socio",
    addField: "Agregar Campo", fieldLabel: "Etiqueta del campo (ej. # de Ticket)",
    partnerTasks: "Pasos Adicionales del Flujo", addStep: "Agregar Paso", stepName: "Paso (ej. Subir a app Workforce)",
    noPartners: "Sin socios todavía", noPartnersDesc: "Agregue un socio para rastrear sus requisitos de envío",
    selectPartner: "Socio / Fuente", noPartnerOption: "Ninguno — trabajo directo/interno",
    // Safety
    safety: "Seguridad", incidents: "Incidentes", inspections: "Inspecciones",
    correctiveActions: "Acciones Correctivas", reportIncident: "Reportar Incidente",
    newInspection: "Nueva Inspección", newAction: "Nueva Acción",
  }
};
// Language state is stored in localStorage
function getLang() { try { return localStorage.getItem("sa_lang") || "en"; } catch(e) { return "en"; } }
// datetime-local inputs expect local wall-clock time. toISOString() returns
// UTC, so using it to populate the field shifts the displayed time by the
// timezone offset — and re-saving then compounds the shift on every edit.
// Searchable job picker. Replaces a plain dropdown that got unusable once
// there were more than a handful of jobs — you had to scroll a list of names
// with no way to search, and no address or partner to tell similar jobs apart.
function JobPicker({ projects, value, onPick, lang }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const chosen = projects.find(p => p.id === value) || null;
  const es = lang === "es";

  const matches = useMemo(() => {
    const t = q.trim().toLowerCase();
    const scored = projects.filter(p => {
      if (!t) return true;
      return [p.name, p.client, p.address, p.partner, p.serviceSubtype]
        .some(f => (f || "").toLowerCase().includes(t));
    });
    // Soonest scheduled first, unscheduled last — the job in hand is usually today's
    return scored.sort((a, b) => {
      if (!!a.scheduled_at !== !!b.scheduled_at) return a.scheduled_at ? -1 : 1;
      if (a.scheduled_at) return new Date(a.scheduled_at) - new Date(b.scheduled_at);
      return (a.name || "").localeCompare(b.name || "");
    }).slice(0, 40);
  }, [projects, q]);

  if (chosen && !open) {
    return (
      <div style={{ background: C.surface, borderRadius: 8, padding: 11, border: `1px solid ${C.gold}55` }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 10, alignItems: "flex-start" }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: C.light }}>{chosen.name}</div>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 2, lineHeight: 1.5 }}>
              {chosen.address || "—"}
              {chosen.scheduled_at ? ` · ${fmtJobDate(chosen.scheduled_at, { month: "short", day: "numeric" })}` : ""}
              {chosen.partner ? ` · ${chosen.partner}` : ""}
            </div>
          </div>
          <button type="button" onClick={() => { setOpen(true); setQ(""); }}
            style={{ ...S.btnS, padding: "5px 11px", fontSize: 12, flexShrink: 0 }}>{es ? "Cambiar" : "Change"}</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <input style={S.input} value={q} autoFocus={open} onChange={e => setQ(e.target.value)}
        placeholder={es ? "Busca por cliente, dirección o socio..." : "Search by client, address, or partner..."} />
      <div style={{ maxHeight: 260, overflowY: "auto", marginTop: 6, border: `1px solid ${C.border}`, borderRadius: 8 }}>
        <div onClick={() => { onPick(""); setOpen(false); setQ(""); }}
          style={{ padding: "11px 12px", cursor: "pointer", borderBottom: `1px solid ${C.border}`, fontSize: 13, color: C.muted }}>
          {es ? "— Trabajo nuevo (crea un proyecto) —" : "— New job (creates a new project) —"}
        </div>
        {matches.length === 0 && (
          <div style={{ padding: 14, textAlign: "center", color: C.muted, fontSize: 12 }}>
            {es ? "Ningún trabajo coincide." : "No jobs match that."}
          </div>
        )}
        {matches.map(p => (
          <div key={p.id} onClick={() => { onPick(p.id); setOpen(false); setQ(""); }}
            style={{ padding: "10px 12px", cursor: "pointer", borderBottom: `1px solid ${C.border}33` }}>
            <div style={{ fontSize: 13.5, fontWeight: 600, color: C.light }}>{p.name}</div>
            <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
              {p.address && <span style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 220 }}>{p.address}</span>}
              {p.scheduled_at && <span style={{ color: C.gold }}>{fmtJobDate(p.scheduled_at, { month: "short", day: "numeric" })}</span>}
              {p.partner && <span>{p.partner}</span>}
              {p.serviceSubtype && <span>{p.serviceSubtype}</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Logo ───────────────────────────────────────────────────────────────────
// Set LOGO_URL to a PNG or SVG in /public and it is used everywhere. Until
// then this draws a sun mark in the brand gold. One place to change, so
// swapping in the real artwork does not mean hunting through the file.
const LOGO_URL = "";   // e.g. "/logo.svg"

function LogoMark({ size = 36, onClick }) {
  const common = {
    width: size, height: size, flexShrink: 0, borderRadius: size * 0.28,
    cursor: onClick ? "pointer" : "default",
    display: "flex", alignItems: "center", justifyContent: "center",
  };
  if (LOGO_URL) {
    return <img loading="lazy" decoding="async" src={LOGO_URL} alt="SolarAbility" onClick={onClick} style={{ ...common, objectFit: "contain" }} />;
  }
  const r = size * 0.22, c0 = size / 2;
  return (
    <div onClick={onClick} style={{
      ...common,
      background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`,
      boxShadow: `0 2px 10px ${C.gold}33`,
    }}>
      <svg width={size * 0.66} height={size * 0.66} viewBox={`0 0 ${size} ${size}`} style={{ display: "block" }}>
        <circle cx={c0} cy={c0} r={r} fill={C.bg} />
        {Array.from({ length: 8 }).map((_, i) => {
          const a = (i * Math.PI) / 4;
          const x1 = c0 + Math.cos(a) * (r + size * 0.08);
          const y1 = c0 + Math.sin(a) * (r + size * 0.08);
          const x2 = c0 + Math.cos(a) * (r + size * 0.2);
          const y2 = c0 + Math.sin(a) * (r + size * 0.2);
          return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke={C.bg} strokeWidth={size * 0.07} strokeLinecap="round" />;
        })}
      </svg>
    </div>
  );
}

// Tap the logo. Nothing here does anything useful, which is the point.
function SunBreak({ onClose }) {
  const es = getLang() === "es";
  return (
    <div onClick={onClose} style={{
      position: "fixed", inset: 0, zIndex: 9999, cursor: "pointer",
      background: "linear-gradient(180deg, #0B0F16 0%, #1a1526 45%, #3d2a1f 75%, #7a4a22 100%)",
      display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
      animation: "sbFade 700ms ease",
    }}>
      <style>{`
        @keyframes sbFade { from { opacity: 0 } to { opacity: 1 } }
        @keyframes sbRise { from { transform: translateY(130px) scale(0.82); opacity: 0 } to { transform: translateY(0) scale(1); opacity: 1 } }
        @keyframes sbSpin { from { transform: rotate(0deg) } to { transform: rotate(360deg) } }
        @keyframes sbGlow { 0%,100% { opacity: .45; transform: scale(1) } 50% { opacity: .8; transform: scale(1.1) } }
        @keyframes sbWords { from { opacity: 0; transform: translateY(14px) } to { opacity: 1; transform: translateY(0) } }
        @media (prefers-reduced-motion: reduce) {
          .sb-rise, .sb-spin, .sb-glow, .sb-words { animation: none !important; opacity: 1 !important; transform: none !important; }
        }
      `}</style>

      <div style={{ position: "relative", width: 230, height: 230, display: "grid", placeItems: "center" }}>
        <div className="sb-glow" style={{
          position: "absolute", width: 230, height: 230, borderRadius: "50%",
          background: `radial-gradient(circle, ${C.gold}55 0%, transparent 68%)`,
          animation: "sbGlow 7s ease-in-out infinite",
        }} />
        <div className="sb-rise" style={{ animation: "sbRise 3.2s cubic-bezier(.22,.61,.36,1) both", display: "grid", placeItems: "center" }}>
          <svg width="190" height="190" viewBox="0 0 190 190" style={{ position: "absolute" }} className="sb-spin">
            <g style={{ transformOrigin: "95px 95px", animation: "sbSpin 90s linear infinite" }}>
              {Array.from({ length: 12 }).map((_, i) => {
                const a = (i * Math.PI) / 6;
                return (
                  <line key={i}
                    x1={95 + Math.cos(a) * 54} y1={95 + Math.sin(a) * 54}
                    x2={95 + Math.cos(a) * 82} y2={95 + Math.sin(a) * 82}
                    stroke={C.gold} strokeOpacity={0.75} strokeWidth="5" strokeLinecap="round" />
                );
              })}
            </g>
          </svg>
          <div style={{
            width: 96, height: 96, borderRadius: "50%",
            background: `radial-gradient(circle at 36% 32%, #FFE39A, ${C.gold} 58%, ${C.goldDark})`,
            boxShadow: `0 0 70px ${C.gold}88`,
          }} />
        </div>
      </div>

      <div className="sb-words" style={{
        marginTop: 34, textAlign: "center", padding: "0 30px", maxWidth: 430,
        animation: "sbWords 1.6s ease 2.4s both",
      }}>
        <div style={{ fontSize: 21, fontWeight: 700, color: "#F3E6CF", lineHeight: 1.5 }}>
          {es ? "El tiempo siempre avanza, pero despacio." : "Time is always moving. But slowly."}
        </div>
        <div style={{ fontSize: 16, color: `${C.gold}`, marginTop: 12, lineHeight: 1.6 }}>
          {es ? "Con calma, y disfruta la vida." : "So take it easy, and enjoy life."}
        </div>
        <div style={{ fontSize: 11.5, color: "#8a7f72", marginTop: 26 }}>
          {es ? "Toca para volver" : "Tap anywhere to go back"}
        </div>
      </div>
    </div>
  );
}

// ─── Job-site time ─────────────────────────────────────────────────────────
// All jobs are in Illinois, so appointments are entered and shown in Chicago
// time regardless of where the person scheduling happens to be. Using the
// browser's zone meant scheduling from California stored 8 AM Pacific, which
// the crew saw as 10 AM. The stored value is still a UTC instant, so calendar
// apps convert correctly for anyone viewing from elsewhere.
const JOB_TZ = "America/Chicago";

// Offset (minutes) of JOB_TZ from UTC at a given instant — DST-aware
function jobTzOffsetMinutes(date) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: JOB_TZ, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(date).reduce((a, p) => { a[p.type] = p.value; return a; }, {});
  const asUTC = Date.UTC(+parts.year, +parts.month - 1, +parts.day, +parts.hour, +parts.minute, +parts.second);
  return (asUTC - date.getTime()) / 60000;
}

// "2026-09-21T08:00" typed as Chicago wall-clock → UTC ISO string
function jobWallToISO(wall) {
  if (!wall) return "";
  const [d, t] = wall.split("T");
  const [y, mo, da] = d.split("-").map(Number);
  const [h, mi] = (t || "00:00").split(":").map(Number);
  const guess = new Date(Date.UTC(y, mo - 1, da, h, mi));
  // Two passes handles the DST boundary correctly
  let off = jobTzOffsetMinutes(guess);
  let instant = new Date(guess.getTime() - off * 60000);
  off = jobTzOffsetMinutes(instant);
  instant = new Date(guess.getTime() - off * 60000);
  return instant.toISOString();
}

// UTC ISO → "2026-09-21T08:00" as Chicago wall-clock, for datetime-local inputs
function isoToJobWall(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = new Intl.DateTimeFormat("en-US", {
    timeZone: JOB_TZ, hourCycle: "h23",
    year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit",
  }).formatToParts(d).reduce((a, x) => { a[x.type] = x.value; return a; }, {});
  return `${p.year}-${p.month}-${p.day}T${p.hour}:${p.minute}`;
}

// Display helpers — always in job-site time
function fmtJobTime(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleTimeString("en-US", { timeZone: JOB_TZ, hour: "numeric", minute: "2-digit" });
}
function fmtJobDate(iso, opts) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-US", { timeZone: JOB_TZ, ...(opts || {}) });
}
function fmtJobDateTime(iso) {
  if (!iso) return "";
  return `${fmtJobDate(iso)} ${fmtJobTime(iso)}`;
}
// Day bucket key in job-site time (so a 11 PM job doesn't land on the wrong day)
// What has been filed for one visit of a job. Forms match by visitId when
// they carry one; older forms (no visitId) match by the visit's day.
function visitProgress(project, visit, forms = [], runs = []) {
  if (!visit) return null;
  const day = visit.scheduled_at ? jobDayKey(visit.scheduled_at) : "";
  const sameVisit = (f) => (f.visitId && f.visitId === visit.id) || (!f.visitId && day && f.submittedAt && jobDayKey(f.submittedAt) === day);
  const mine = forms.filter(sameVisit);
  const jha = mine.find(f => f.type === "JHA") || null;
  const report = mine.find(f => f.type !== "JHA") || null;
  const run = runs.find(r => (r.visitId && r.visitId === visit.id) || (!r.visitId && day && jobDayKey(r.completedAt || r.startedAt || "") === day)) || null;
  return { jha, report, run, done: visit.status === "done", items: [
    { key: "jha", label: "JHA", ok: !!jha },
    { key: "proc", label: "Procedure", ok: !!(run && run.completedAt) },
    { key: "report", label: project?.projectType === "new_install" ? "Closeout" : project?.projectType === "survey" ? "Survey" : "Assessment", ok: !!report || visit.status === "done" },
  ] };
}
function VisitChips({ progress, size = 11 }) {
  if (!progress) return null;
  return (
    <span style={{ display: "inline-flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
      {progress.items.map(it => (
        <span key={it.key} style={{ fontSize: size, fontWeight: 700, padding: "2px 8px", borderRadius: 999, border: `1px solid ${it.ok ? C.success : C.border}`, color: it.ok ? C.success : C.muted, background: it.ok ? `${C.success}18` : "transparent" }}>
          {it.ok ? "✓" : "○"} {it.label}
        </span>
      ))}
      {progress.done && <span style={{ fontSize: size, fontWeight: 800, padding: "2px 8px", borderRadius: 999, background: C.success, color: "#071510" }}>VISIT DONE</span>}
    </span>
  );
}
function jobDayKey(iso) {
  return isoToJobWall(iso).slice(0, 10);
}

// Adds N business days (Mon–Fri) to a date and returns YYYY-MM-DD.
// Partner submission windows are counted in business days, so a Friday visit
// with a 2-day window is due Tuesday, not Sunday. Holidays are not excluded —
// around a holiday, check the partner's own terms.
function addBusinessDays(fromIso, n) {
  const wall = isoToJobWall(fromIso || new Date().toISOString()).slice(0, 10);
  const [y, m, d] = wall.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  let added = 0;
  while (added < n) {
    dt.setUTCDate(dt.getUTCDate() + 1);
    const dow = dt.getUTCDay();
    if (dow !== 0 && dow !== 6) added++;
  }
  return dt.toISOString().slice(0, 10);
}

// Navigate to an in-app deep link. Setting location.hash to the value it
// already has fires no event, so tapping the same link twice did nothing —
// e.g. back out of Truck Check, tap "Open" again, and stay put. Re-dispatch
// the event ourselves in that case.
function goHash(e, target) {
  if (e) e.preventDefault();
  const want = "#" + target;
  if (window.location.hash === want) window.dispatchEvent(new HashChangeEvent("hashchange"));
  else window.location.hash = target;
}

// ─── OEM support lines ─────────────────────────────────────────────────────
// Shown on every service job and on the Service Assessment. `tel` is what the
// phone dials; the comma is a dialing pause, so Locus goes straight to ext. 2
// on most phones (the label still says so, in case a phone ignores it).
const OEM_SUPPORT = [
  { name: "Enphase", display: "(877) 797-4743", tel: "+18777974743" },
  { name: "SolarEdge", display: "+1 (510) 498-3200", tel: "+15104983200" },
  { name: "Locus", display: "(877) 562-8736 ext. 2", tel: "+18775628736,2" },
];

function OemSupport({ compact }) {
  return (
    <div style={{ background: C.surface, borderRadius: 8, padding: compact ? 10 : 11, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 7 }}>📞 OEM Support</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 6 }}>
        {OEM_SUPPORT.map(o => (
          <a key={o.name} href={`tel:${o.tel}`}
            style={{ display: "block", padding: "8px 10px", borderRadius: 8, background: C.card, border: `1px solid ${C.border}`, textDecoration: "none" }}>
            <div style={{ fontSize: 13, color: C.light, fontWeight: 700 }}>{o.name}</div>
            <div style={{ fontSize: 12, color: C.gold, marginTop: 1 }}>{o.display}</div>
          </a>
        ))}
      </div>
    </div>
  );
}

function toLocalInputValue(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localDate() { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }
function setLangStorage(l) { try { localStorage.setItem("sa_lang", l); } catch(e) {} }

// ─── Offline Queue (IndexedDB) ───────────────────────────────────────────────
const offlineQueue = {
  DB_NAME: "solarability_offline",
  STORE: "pending_submissions",
  
  async openDB() {
    return new Promise((resolve, reject) => {
      const req = indexedDB.open(this.DB_NAME, 1);
      req.onupgradeneeded = (e) => { e.target.result.createObjectStore(this.STORE, { keyPath: "id" }); };
      req.onsuccess = (e) => resolve(e.target.result);
      req.onerror = (e) => reject(e.target.error);
    });
  },

  async save(submission) {
    const idb = await this.openDB();
    const tx = idb.transaction(this.STORE, "readwrite");
    tx.objectStore(this.STORE).put({ ...submission, id: `offline-${Date.now()}-${Math.random().toString(36).slice(2)}`, offlineAt: new Date().toISOString() });
    return new Promise((resolve, reject) => { tx.oncomplete = resolve; tx.onerror = reject; });
  },

  async getAll() {
    const idb = await this.openDB();
    const tx = idb.transaction(this.STORE, "readonly");
    const req = tx.objectStore(this.STORE).getAll();
    return new Promise((resolve, reject) => { req.onsuccess = () => resolve(req.result || []); req.onerror = reject; });
  },

  async remove(id) {
    const idb = await this.openDB();
    const tx = idb.transaction(this.STORE, "readwrite");
    tx.objectStore(this.STORE).delete(id);
    return new Promise((resolve, reject) => { tx.oncomplete = resolve; tx.onerror = reject; });
  },

  async syncAll(userId) {
    const pending = await this.getAll();
    const results = { synced: 0, failed: 0 };
    for (const item of pending) {
      try {
        if (item.formType === "jha") {
          await db._submitJHAOnline(item.dbData, userId);
        } else {
          await db._submitSurveyOnline(item.dbData, userId);
        }
        await this.remove(item.id);
        results.synced++;
      } catch (e) {
        results.failed++;
      }
    }
    return results;
  }
};

// ─── Local Storage for submissions cache ─────────────────────────────────────
const localCache = {
  save(key, data) { try { window.localStorage.setItem(`sa_${key}`, JSON.stringify(data)); } catch(e) {} },
  load(key) { try { const d = window.localStorage.getItem(`sa_${key}`); return d ? JSON.parse(d) : null; } catch(e) { return null; } },
  saveUser(user) { this.save("user", user); },
  loadUser() { return this.load("user"); },
  // Offline fallback only — keep it light: newest 150, no base64 photo data.
  saveSubmissions(subs) {
    const light = (subs || []).slice(-150).map(s => ({ ...s, photos: (s.photos || []).filter(p => !(p.url || "").startsWith("data:")), checklist: undefined, checklistData: undefined, photoChecklist: undefined, arrays: undefined }));
    this.save("submissions", light);
  },
  loadSubmissions() { return this.load("submissions") || []; },
};

// Short-lived in-memory cache for the tables every screen re-reads (projects,
// playbooks, partners). Any save invalidates its table, so screens never show
// something older than the last write from this device.
const memoCache = {
  store: {},
  ttl: 60 * 1000,
  get(key) { const e = this.store[key]; return e && Date.now() - e.at < this.ttl ? e.data : null; },
  set(key, data) { this.store[key] = { at: Date.now(), data }; return data; },
  invalidate(prefix) { Object.keys(this.store).forEach(k => { if (!prefix || k.startsWith(prefix)) delete this.store[k]; }); },
};

// Merge a job someone else saved (fresh) with the copy being saved here
// (local): lists are unioned by id (local wins on the same id), everything
// else keeps the local value. Not a true 3-way merge, but it keeps the crew's
// tasks/notes/visits when the office saves over them, and vice versa.
function mergeProjectEdits(fresh, local, base) {
  const unionById = (a, b, combine) => {
    const out = [...(a || [])];
    (b || []).forEach(x => { const i = out.findIndex(y => y && x && y.id === x.id); if (i >= 0) out[i] = combine ? combine(out[i], x) : x; else out.push(x); });
    return out;
  };
  // Same task on both sides: ticked by either stays ticked. Same visit on both
  // sides: "done", invites sent and the invite sequence are never lost.
  const taskMerge = (f, l) => ({ ...f, ...l, done: !!(f.done || l.done) });
  const visitMerge = (f, l) => ({
    ...f, ...l,
    status: (f.status === "done" || l.status === "done") ? "done" : (l.status || f.status),
    doneAt: l.doneAt || f.doneAt || "", doneBy: l.doneBy || f.doneBy || "",
    invited: [...new Set([...(f.invited || []), ...(l.invited || [])])],
    inviteLog: ((l.inviteLog || []).length >= (f.inviteLog || []).length ? l.inviteLog : f.inviteLog) || [],
    seq: Math.max(f.seq || 0, l.seq || 0),
    uidCrew: l.uidCrew || f.uidCrew || "", uidOwner: l.uidOwner || f.uidOwner || "",
  });
  const unionMembers = (a, b) => { const seen = new Set(); return [...(a || []), ...(b || [])].filter(m => { const k = (m?.name || "").toLowerCase(); if (!k || seen.has(k)) return false; seen.add(k); return true; }); };
  const out = {
    ...fresh, ...local,
    tasks: unionById(fresh.tasks, local.tasks, taskMerge),
    notes: unionById(fresh.notes, local.notes),
    visits: unionById(fresh.visits, local.visits, visitMerge),
    members: unionMembers(fresh.members, local.members),
    refPhotos: unionById(fresh.refPhotos, local.refPhotos),
    refDocs: unionById(fresh.refDocs, local.refDocs),
    // A job marked further along by either side stays there
    status: ["completed", "pending_submission"].includes(fresh.status) && !["completed", "on_hold"].includes(local.status) ? fresh.status : local.status,
    partner: local.partner || fresh.partner || "",
    updated_at: fresh.updated_at,
  };
  // Three-way: anything that was in the copy this person opened (base) and
  // they removed (not in local) stays removed — the union above would
  // otherwise bring a deleted crew member, task, note or invite right back.
  if (base) {
    const gone = (b, l, key) => { const keep = new Set((l || []).map(key)); return new Set((b || []).map(key).filter(k => k && !keep.has(k))); };
    const drop = (list, b, l, key) => { const g = gone(b, l, key); return g.size ? (list || []).filter(x => !g.has(key(x))) : list; };
    const byId = x => x?.id;
    const byName = m => (m?.name || "").toLowerCase();
    out.members = drop(out.members, base.members, local.members, byName);
    ["tasks", "notes", "visits", "refPhotos", "refDocs"].forEach(k => { out[k] = drop(out[k], base[k], local[k], byId); });
    out.visits = (out.visits || []).map(v => {
      const bv = (base.visits || []).find(x => x?.id === v.id);
      const lv = (local.visits || []).find(x => x?.id === v.id);
      if (!bv || !lv) return v;
      const g = gone(bv.invited, lv.invited, e => (e || "").toLowerCase());
      return g.size ? { ...v, invited: (v.invited || []).filter(e => !g.has((e || "").toLowerCase())) } : v;
    });
  }
  return out;
}

// Project status that follows the work instead of waiting for a button:
//   invoice paid → completed
//   every live visit done (SA / closeout filed) → pending_submission
//   anything scheduled or filed on a not-started job → in_progress
// completed / on_hold set by hand are respected (except paid → completed).
// The "share the report with the partner" step: "Submit to Omnidian",
// "Share with Windfree", "Share with partner", "Submit report to RJI"…
const isShareTask = (t) => /^(submit|share|send)\s+(the\s+)?(report\s+|photos\s+)?(to|with)\s/i.test((t?.name || "").trim()) && !/folder|drive|homeowner|customer|invoice/i.test(t?.name || "");
// When the work was finished: the share step's tick, else the last visit done
function completedOn(p) {
  const share = (p.tasks || []).filter(t => isShareTask(t) && t.done && t.doneAt).map(t => t.doneAt);
  const visits = (p.visits || []).filter(v => !v.cancelled && v.status === "done" && v.doneAt).map(v => v.doneAt);
  const all = [...share, ...visits].sort();
  return all.length ? all[all.length - 1] : "";
}
// Status follows the work:
//   a visit scheduled → In Progress
//   every visit done (assessment / closeout filed) → Pending Submission
//   report shared with the partner (that task ticked) → Completed
//   direct jobs (no partner): invoice sent → Completed
//   invoice paid → Completed
// Completed does NOT mean paid — unpaid completed jobs stay in Awaiting
// Payment and the Today queue until the payment date is entered.
function autoStatus(p) {
  const s = p.status || "not_started";
  if (s === "on_hold") return s;
  if (p.invoice?.paidDate) return "completed";
  if (s === "completed") return s;
  const live = (p.visits || []).filter(v => !v.cancelled);
  const allDone = live.length > 0 && live.every(v => v.status === "done");
  const shared = (p.tasks || []).some(t => isShareTask(t) && t.done);
  if (shared && (allDone || live.length === 0 || s === "pending_submission")) return "completed";
  if (!p.partner && (allDone || s === "pending_submission") && p.invoice?.sentDate) return "completed";
  if (allDone) return "pending_submission";
  if (s === "pending_submission") return s; // keep what a person set
  if (s === "not_started" && live.some(v => v.scheduled_at)) return "in_progress";
  return s;
}

// An open job already at this address (or for this client)? Used before any
// of the three "create a project" doors so the office doesn't end up with
// two jobs — and two checklists — for one house.
// "5354 Madison St, Skokie, IL 60077" and "5354 Madison St 5354 Madison St,
// Skokie, IL 60077, USA" are the same house. The key is house number + first
// real street word (+ ZIP when there is one), so formatting, a pasted-twice
// street, "USA", or St/Street don't hide a duplicate.
function addressKey(addr) {
  const s = String(addr || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
  const m = s.match(/\b(\d{1,6}[a-z]?)\s+(.+)/);
  if (!m) return "";
  const skip = new Set(["n", "s", "e", "w", "north", "south", "east", "west", "ne", "nw", "se", "sw"]);
  const word = (m[2].split(" ").find(w => w && !skip.has(w) && !/^\d+$/.test(w)) || "");
  if (!word) return "";
  const zip = (s.match(/\b(\d{5})\b(?!.*\b\d{5}\b)/) || [])[1] || "";
  return `${m[1]}|${word}|${zip}`;
}
function sameAddress(a, b) {
  const ka = addressKey(a), kb = addressKey(b);
  if (!ka || !kb) return false;
  const [na, wa, za] = ka.split("|"), [nb, wb, zb] = kb.split("|");
  return na === nb && wa === wb && (!za || !zb || za === zb);
}
// Same house is not enough: one address can have several real jobs (two
// Omnidian work orders, an FSD and a service call). Two jobs are only a
// suspected duplicate when nothing tells them apart — same partner, same kind
// of work, and no different work-order / FSD / SQ number — and nobody has
// marked the pair "not a duplicate".
const normWO = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
const notDupIds = (p) => (p?.refDocs || []).filter(d => d && d.kind === "notdup").map(d => d.url);
function looksLikeSameJob(a, b) {
  if (!a || !b || a.id === b.id) return false;
  if (notDupIds(a).includes(b.id) || notDupIds(b).includes(a.id)) return false;
  const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
  const sameHouse = (a.address && b.address) ? sameAddress(a.address, b.address)
    : (norm(a.client) && norm(a.client).length > 3 && norm(a.client) === norm(b.client));
  if (!sameHouse) return false;
  const wa = normWO(a.workOrder), wb = normWO(b.workOrder);
  if (wa && wb && wa !== wb) return false;                       // different WO / FSD / SQ
  const pa = norm(a.partner), pb = norm(b.partner);
  if (pa && pb && pa !== pb) return false;                       // different partner
  if (a.serviceSubtype && b.serviceSubtype && a.serviceSubtype !== b.serviceSubtype) return false;
  if (a.projectType && b.projectType && a.projectType !== b.projectType) return false;
  return true;
}
function findOpenProject(list, { address, client, excludeId, workOrder, partner, serviceSubtype, projectType } = {}) {
  const probe = { id: excludeId || "__new__", address, client, workOrder, partner, serviceSubtype, projectType };
  return (list || []).find(p => p.id !== excludeId && p.status !== "completed" && looksLikeSameJob(probe, p)) || null;
}
// Open jobs that look like the same house — for the Today queue and the editor banner
function duplicateGroups(list) {
  const open = (list || []).filter(p => p.status !== "completed" && addressKey(p.address));
  const groups = [];
  const used = new Set();
  open.forEach(p => {
    if (used.has(p.id)) return;
    // A group only grows with jobs that look like the same job as EVERY member,
    // so two different work orders never land in one group via a third job.
    const g = [p];
    open.forEach(q => { if (q.id !== p.id && !used.has(q.id) && g.every(m => looksLikeSameJob(m, q))) g.push(q); });
    if (g.length > 1) { g.forEach(q => used.add(q.id)); groups.push(g); }
  });
  return groups;
}

// The job's Proton Drive folder lives in the job documents list as a
// "folder" entry, so no schema change and it travels with the job.
const folderUrlOf = (p) => ((p?.refDocs || []).find(d => d && d.kind === "folder") || {}).url || "";
const withFolderUrl = (p, url) => {
  const rest = (p.refDocs || []).filter(d => !(d && d.kind === "folder"));
  const clean = (url || "").trim();
  return clean ? [{ id: "folder", kind: "folder", name: "Proton Drive job folder", url: clean, label: "Proton Drive job folder", at: new Date().toISOString() }, ...rest] : rest;
};
// Everything the office should have done before the truck rolls
// ─── Permits ───────────────────────────────────────────────────────────────
// p.permit = { required: true|false|null, kind, jurisdiction, type, number,
//   portalUrl, appliedOn, issuedOn, closedOn, docs:[{id,name,done,note}],
//   inspections:[{id,type,scheduledOn,result:""|passed|failed|partial,notes}], notes }
// The stage is DERIVED from what's filled in — the coordinator only records
// facts (dates, results), never has to remember to flip a status.
const PERMIT_PRESETS = {
  solar: { label: "Solar installation", type: "Building + Electrical",
    docs: ["Permit application (Chicago Easy Permit / village building dept)", "Plan set — roof layout, attachments, racking", "One-line electrical diagram", "Structural letter / rafter calc (if required)", "Spec sheets — modules, inverter, racking", "Contractor license (ICC) + certificate of insurance", "Homeowner authorization / signed contract", "ComEd interconnection application (Part 1)", "ComEd Part 2 / PTO request (after final inspection)"],
    inspections: ["Rough / structural (if required)", "Electrical final", "ComEd PTO (permission to operate)"] },
  rnr: { label: "Removal & reinstall (re-roof)", type: "Electrical",
    docs: ["Electrical permit application", "Original plan set / existing system info", "One-line (only if wiring changes)", "Contractor license (ICC) + certificate of insurance", "Homeowner authorization", "ComEd notice — system off / back on (if required)"],
    inspections: ["Electrical final"] },
  electrical: { label: "Electrical work", type: "Electrical",
    docs: ["Electrical permit application", "Load calculation", "One-line / panel schedule (service upgrade)", "Contractor license (ICC) + certificate of insurance", "Homeowner authorization", "ComEd service request / meter release (service upgrade)"],
    inspections: ["Electrical rough (if walls open)", "Electrical final", "ComEd meter release (service upgrade)"] },
};
const newPermitId = () => `pm${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
function permitFromPreset(kind, prev) {
  const ps = PERMIT_PRESETS[kind] || PERMIT_PRESETS.electrical;
  return { required: true, kind, type: ps.type, jurisdiction: "", number: "", portalUrl: "", appliedOn: "", issuedOn: "", closedOn: "", notes: "", ...(prev || {}),
    docs: ps.docs.map(name => ({ id: newPermitId(), name, done: false, note: "" })),
    inspections: ps.inspections.map(type => ({ id: newPermitId(), type, scheduledOn: "", result: "", notes: "" })) };
}
// → { key, label, tone, order } — order lets lists sort "most behind first"
function permitStage(p) {
  const pm = p?.permit;
  if (!pm || pm.required === null || pm.required === undefined) return { key: "undecided", label: "Permit: not decided", tone: C.muted, order: 0 };
  if (pm.required === false) return { key: "none", label: "No permit needed", tone: C.muted, order: 99 };
  if (pm.closedOn) return { key: "closed", label: "Permit closed", tone: C.success, order: 90 };
  const insp = (pm.inspections || []).filter(i => i.result !== "failed-reinspect"); // superseded by a re-inspection row
  const failed = insp.find(i => i.result === "failed");
  if (failed) return { key: "failed", label: `Inspection failed — ${failed.type}`, tone: C.danger, order: 10 };
  const docsLeft = (pm.docs || []).filter(d => !d.done).length;
  if (!pm.appliedOn && !pm.number && !pm.issuedOn) return { key: "to_apply", label: docsLeft ? `Permit not applied · ${docsLeft} doc${docsLeft > 1 ? "s" : ""} to gather` : "Permit ready to apply", tone: C.warning, order: 20 };
  if (!pm.issuedOn) return { key: "applied", label: `Applied${pm.appliedOn ? ` ${pm.appliedOn}` : ""} — waiting on ${pm.jurisdiction || "the city"}`, tone: C.info, order: 30 };
  const scheduled = insp.filter(i => i.scheduledOn && !i.result).sort((a, b) => a.scheduledOn.localeCompare(b.scheduledOn))[0];
  if (scheduled) return { key: "inspection", label: `Inspection ${scheduled.scheduledOn} — ${scheduled.type}`, tone: C.info, order: 50 };
  const allPassed = insp.length > 0 && insp.every(i => i.result === "passed");
  if (allPassed) return { key: "passed", label: "Inspections passed — close out permit", tone: C.success, order: 80 };
  const passedAny = insp.some(i => i.result === "passed");
  return { key: "issued", label: `Permit issued${pm.number ? ` #${pm.number}` : ""} — ${passedAny ? "next inspection" : "inspection"} not scheduled`, tone: C.warning, order: 40 };
}
const permitRequired = p => p?.permit?.required === true;
const permitLine = p => permitRequired(p) ? `${p.permit.number ? `#${p.permit.number}` : "pending"}${p.permit.jurisdiction ? ` · ${p.permit.jurisdiction}` : ""}${p.permit.issuedOn ? "" : " · NOT ISSUED"}` : "";

function preRollChecks(p) {
  const live = (p.visits || []).filter(v => !v.cancelled);
  const st = permitStage(p);
  return [
    { key: "permit", label: st.key === "undecided" ? "Permit: decide if this job needs one" : st.key === "none" ? "No permit needed" : `Permit issued (${st.label})`, ok: st.key === "none" || ["inspection", "issued", "passed", "closed"].includes(st.key) },
    { key: "folder", label: "Proton Drive folder created & shared with the crew", ok: !!folderUrlOf(p) },
    { key: "crew", label: "Crew on the job (by email)", ok: (p.members || []).some(m => (m.name || "").includes("@")) },
    { key: "invite", label: "Visit scheduled and invite sent", ok: live.some(v => v.scheduled_at && (v.invited || []).length > 0) },
    { key: "docs", label: "Planset / work order attached", ok: (p.refDocs || []).some(d => d && !d.kind), optional: true },
    { key: "jha", label: "Site JHA sheet prepared", ok: !!(p.jhaSheet && (p.jhaSheet.imageUrl || (p.jhaSheet.markers || []).length)), optional: true },
  ];
}

// Homeowner text templates. Short, plain, from the company — never a crew
// member's personal number. Keep under 2 SMS segments (~300 chars).
function smsTemplates(p, visit, user) {
  const when = visit?.scheduled_at ? `${fmtJobDate(visit.scheduled_at, { weekday: "short", month: "short", day: "numeric" })} at ${fmtJobTime(visit.scheduled_at)}` : "the scheduled time";
  const dur = parseFloat(visit?.durationHours) || 2;
  const first = (user?.name || "").split(" ")[0] || "Our crew";
  const addr = p.address ? ` at ${p.address.split(",")[0]}` : "";
  return [
    { id: "confirmation", label: "Confirmation", text: `SolarAbility: your service is scheduled ${when}${addr} (about ${dur} hr). Please make sure we can reach the electrical panel and the roof. Reply here with any questions. (312) 820-9027` },
    { id: "reminder", label: "Day-before reminder", text: `SolarAbility reminder: we'll be there tomorrow, ${when}${addr}. An adult (18+) needs to be home. Reply if anything changed.` },
    { id: "onmyway", label: "On my way", text: `${first} from SolarAbility is on the way${addr} — arriving in about 20–30 minutes.` },
    { id: "late", label: "Running late", text: `SolarAbility: we're running about 30 minutes behind for your appointment ${when}. Sorry for the delay — reply if that no longer works.` },
    { id: "done", label: "Work complete", text: `SolarAbility: today's work${addr} is complete. You'll get the report by email. Thank you! Questions: (312) 820-9027` },
  ];
}

// ─── Supabase Client (mock when not configured) ──────────────────────────────
let supabase = null;
if (USE_SUPABASE) {
  supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
}

// Mock Supabase for demo mode
const mockDB = { jha: [], surveys: [], users: [] };
const db = {
  async signIn(email, password) {
    if (USE_SUPABASE && supabase) {
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      return data.user;
    }
    return { id: "demo-user", email, user_metadata: { full_name: email.split("@")[0], role: email.includes("admin") ? "admin" : "employee" } };
  },
  async signUp(email, password, name) {
    if (USE_SUPABASE && supabase) {
      const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: name } } });
      if (error) throw error;
      return data.user;
    }
    return { id: "demo-user", email, user_metadata: { full_name: name, role: "employee" } };
  },
  // Role comes from the profiles table (server-controlled), never from client metadata
  // Each person's private calendar-feed token (their own profile row only)
  async getCalendarToken(userId) {
    if (!USE_SUPABASE || !supabase || !userId) return null;
    try {
      const { data } = await supabase.from("profiles").select("calendar_token").eq("id", userId).single();
      return data?.calendar_token || null;
    } catch(e) { console.warn("Calendar token lookup failed:", e); return null; }
  },
  async getRole(userId) {
    if (USE_SUPABASE && supabase && userId) {
      try {
        const { data } = await supabase.from("profiles").select("role, full_name").eq("id", userId).single();
        if (data) return { role: data.role || "employee", name: data.full_name || null };
      } catch(e) { console.warn("Profile lookup failed:", e); }
    }
    return { role: "employee", name: null };
  },
  async signOut() {
    if (USE_SUPABASE && supabase) await supabase.auth.signOut();
    localCache.save("user", null);
  },
  async resetPassword(email) {
    if (USE_SUPABASE && supabase) {
      const { error } = await supabase.auth.resetPasswordForEmail(email);
      if (error) throw error;
    }
  },
  // Convert HEIC (iPhone photos) to JPEG for browser compatibility
  // Decode HEIC if needed, downscale to MAX_DIM, and re-encode as JPEG.
  // iPhone photos are often 3-5MB; a 2000px JPEG of a meter or panel label is
  // visually identical for documentation but ~10x smaller, which is the
  // difference between a fast upload and a stalled one on cell service.
  heicHelpText(fileName) {
    return `Couldn't read ${fileName || "that photo"} — it's an iPhone HEIC file and this phone can't convert it.\n\n` +
      `Fix it once on the iPhone: Settings → Camera → Formats → Most Compatible. New photos will be JPEG.\n\n` +
      `For this one: take it again with the camera button in the Hub, or mark the item "Uploaded to folder".`;
  },
  async convertToJpeg(file) {
    const MAX_DIM = 2000;
    const QUALITY = 0.82;

    // Draw any decodable source onto a downscaled canvas and return a JPEG File
    const toResizedJpeg = async (source, width, height, name) => {
      let w = width, h = height;
      if (w > MAX_DIM || h > MAX_DIM) {
        if (w > h) { h = Math.round(h * MAX_DIM / w); w = MAX_DIM; }
        else { w = Math.round(w * MAX_DIM / h); h = MAX_DIM; }
      }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingQuality = "high";
      ctx.drawImage(source, 0, 0, w, h);
      const blob = await new Promise(r => canvas.toBlob(r, "image/jpeg", QUALITY));
      if (!blob) return null;
      return new File([blob], name.replace(/\.[^.]+$/, ".jpg"), { type: "image/jpeg" });
    };

    // Load a Blob/File into an <img> so it can be drawn to canvas
    const loadImage = (blob) => new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();
      img.onload = () => { URL.revokeObjectURL(url); resolve(img); };
      img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
      img.src = url;
    });

    // Sniff the actual bytes. Some pickers hand over a HEIC file named .jpg
    // with an empty MIME type, which used to slip past the name/type check,
    // fail to draw, and get uploaded as-is — invisible in the PDF later.
    const sniffHeic = async (f) => {
      try {
        const head = new Uint8Array(await f.slice(0, 16).arrayBuffer());
        if (head.length < 12) return false;
        const tag = String.fromCharCode(head[4], head[5], head[6], head[7]);
        if (tag !== "ftyp") return false;
        const brand = String.fromCharCode(head[8], head[9], head[10], head[11]).toLowerCase();
        return ["heic", "heix", "heim", "heis", "hevc", "hevx", "mif1", "msf1"].includes(brand);
      } catch (e) { return false; }
    };

    const ext = (file.name || "").toLowerCase().split(".").pop();
    const isHeic = file.type === "image/heic" || file.type === "image/heif"
      || ext === "heic" || ext === "heif" || await sniffHeic(file);

    if (isHeic) {
      // Method 1: heic2any, then resize the JPEG it produces
      try {
        const { default: heic2any } = await import("heic2any"); // only iPhones pay for this chunk
        const blob = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.9 });
        const jpegBlob = Array.isArray(blob) ? blob[0] : blob;
        const img = await loadImage(jpegBlob);
        const out = await toResizedJpeg(img, img.width, img.height, file.name);
        if (out) return out;
      } catch (err) { console.warn("heic2any path failed:", err); }

      // Method 2: createImageBitmap — Safari decodes HEIC natively
      try {
        const bitmap = await createImageBitmap(file);
        const out = await toResizedJpeg(bitmap, bitmap.width, bitmap.height, file.name);
        if (out) return out;
      } catch (err) { console.warn("createImageBitmap path failed:", err); }

      // Method 3: some browsers will load HEIC straight into an <img>
      try {
        const img = await loadImage(file);
        const out = await toResizedJpeg(img, img.width, img.height, file.name);
        if (out) return out;
      } catch (err) { console.warn("Image() path failed:", err); }

      // Every path failed. Uploading the original would look fine on the
      // phone and then show as a blank in the PDF, so fail loudly instead.
      const err = new Error("HEIC_CONVERSION_FAILED");
      err.code = "HEIC_CONVERSION_FAILED";
      throw err;
    }

    // Already a normal web image: still downscale if it's oversized
    try {
      const img = await loadImage(file);
      if (img.width <= MAX_DIM && img.height <= MAX_DIM && file.size < 1200000) {
        return file; // small enough already, don't recompress
      }
      const out = await toResizedJpeg(img, img.width, img.height, file.name);
      if (out) return out;
    } catch (err) { console.warn("Resize failed, uploading original:", err); }

    return file;
  },
  // Documents (PDF manuals, training material). Uploaded as-is — no image
  // conversion. Stored under docs/ in the same public bucket as photos, so
  // anyone with the link can open it: don't upload anything confidential.
  async uploadDocument(file) {
    if (!USE_SUPABASE || !supabase) return URL.createObjectURL(file);
    const safe = (file.name || "document").replace(/[^a-zA-Z0-9._-]/g, "_");
    const path = `docs/${Date.now()}-${safe}`;
    const { data, error } = await supabase.storage.from("safety-photos").upload(path, file, {
      contentType: file.type || "application/octet-stream",
    });
    if (error) throw error;
    return supabase.storage.from("safety-photos").getPublicUrl(data.path).data.publicUrl;
  },
  async uploadPhoto(file, path) {
    if (USE_SUPABASE && supabase) {
      // Callers usually convert first; this covers direct uploads. A HEIC
      // that cannot be converted throws rather than uploading unviewable.
      // Phone JPEGs are 3-8 MB each; that is what fills the storage quota.
      // Resize everything over ~700 KB down to 2000 px / q0.82 (~400 KB) —
      // plenty for a report photo, and 10x cheaper to keep.
      const converted = (file.type === "image/jpeg" && file.size < 700 * 1024) ? file : await this.convertToJpeg(file);
      const uploadPath = converted !== file ? path.replace(/\.[^.]+$/, ".jpg") : path;
      const { data, error } = await supabase.storage.from("safety-photos").upload(uploadPath, converted, {
        contentType: converted.type || "image/jpeg",
      });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from("safety-photos").getPublicUrl(data.path);
      return urlData.publicUrl;
    }
    return URL.createObjectURL(file);
  },

  // Internal online-only submit methods
  // A JHA is submitted in the morning so Anai can review the hazards before
  // work starts, but the water log keeps accruing all day. These two let a
  // crew append to that log without touching anything already reviewed.
  async getOpenJHAToday(user) {
    if (!USE_SUPABASE || !supabase || !user) return null;
    try {
      const todayKey = isoToJobWall(new Date().toISOString()).slice(0, 10);
      const { data } = await supabase.from("jha_submissions")
        .select("*").order("created_at", { ascending: false }).limit(25);
      const mine = (data || []).find(j => {
        if (!j.created_at || jobDayKey(j.created_at) !== todayKey) return false;
        const owner = (j.supervisor || "").toLowerCase();
        const me = (user.name || "").toLowerCase();
        if (j.user_id && user.id && j.user_id !== user.id && owner !== me) return false;
        let h = j.hazards;
        if (typeof h === "string") { try { h = JSON.parse(h); } catch (e) { h = null; } }
        if (!h || Array.isArray(h)) return false;
        return h.heatWatch && !h.waterLogClosedAt;
      });
      if (!mine) return null;
      let h = mine.hazards;
      if (typeof h === "string") { try { h = JSON.parse(h); } catch (e) { h = {}; } }
      return { id: mine.id, title: mine.title, date: mine.date, hazards: h };
    } catch (e) { console.warn("Open JHA lookup failed:", e); return null; }
  },
  async appendWaterLog(jhaId, hazards) {
    if (!USE_SUPABASE || !supabase) return false;
    const { error } = await supabase.from("jha_submissions")
      .update({ hazards: JSON.stringify(hazards) }).eq("id", jhaId);
    if (error) throw error;
    return true;
  },

  async _submitJHAOnline(data, userId) {
    if (USE_SUPABASE && supabase) {
      const { error } = await supabase.from("jha_submissions").insert({ ...data, user_id: userId });
      if (error) throw error;
    } else {
      mockDB.jha.push({ ...data, id: `jha-${Date.now()}` });
    }
  },
  async _submitSurveyOnline(data, userId) {
    if (USE_SUPABASE && supabase) {
      const { error } = await supabase.from("survey_submissions").insert({ ...data, user_id: userId });
      if (error) throw error;
    } else {
      mockDB.surveys.push({ ...data, id: `survey-${Date.now()}` });
    }
  },

  // Public submit methods with offline fallback
  async submitJHA(data, userId) {
    try {
      await this._submitJHAOnline(data, userId);
      return { saved: "online" };
    } catch (e) {
      console.warn("Online save failed, saving offline:", e);
      await offlineQueue.save({ formType: "jha", dbData: data, displayData: data });
      return { saved: "offline", error: e.message };
    }
  },
  async submitSurvey(data, userId) {
    try {
      await this._submitSurveyOnline(data, userId);
      return { saved: "online" };
    } catch (e) {
      console.warn("Online save failed, saving offline:", e);
      await offlineQueue.save({ formType: "survey", dbData: data, displayData: data });
      return { saved: "offline", error: e.message };
    }
  },

  async getJHAs(opts = {}) {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from("jha_submissions").select("*").order("created_at", { ascending: false }).limit(opts.limit || 300);
        if (error) throw error;
        return data || [];
      } catch (e) { console.warn("Failed to load JHAs:", e); return []; }
    }
    return mockDB.jha;
  },
  // Surveys / assessments carry a big checklist JSON (every photo, every
  // field). The list only needs a handful of those fields, so `light` pulls
  // just them (JSON path selects) and the full row is fetched when a report
  // is opened — see hydrateSubmission. Cuts login payload by ~10x.
  SURVEY_LIGHT: "id, site_name, address, date, inspector, weather, system_size, status, created_at, project_id, user_id, photo_urls, "
    + "formType:checklist->>formType, serviceType:checklist->>serviceType, partnerName:checklist->>partnerName, submitDue:checklist->>submitDue, "
    + "visitId:checklist->>visitId, scopeTitle:checklist->>scopeTitle, issue:checklist->>issue, client:checklist->client, missingPhotos:checklist->missingPhotos, timeOnSite:checklist->timeOnSite, fee:checklist->>fee",
  async getSurveys(opts = {}) {
    if (USE_SUPABASE && supabase) {
      try {
        if (opts.light) {
          const { data, error } = await supabase.from("survey_submissions").select(this.SURVEY_LIGHT).order("created_at", { ascending: false }).limit(opts.limit || 300);
          if (error) throw error;
          return (data || []).map(r => {
            const { formType, serviceType, partnerName, submitDue, visitId, scopeTitle, issue, client, missingPhotos, timeOnSite, fee, ...rest } = r;
            return { ...rest, _light: true, checklist: { formType, serviceType, partnerName, submitDue, visitId, scopeTitle, issue, client, missingPhotos: missingPhotos || [], timeOnSite, fee, photoChecklist: [], checklistPhotos: [] } };
          });
        }
        const { data, error } = await supabase.from("survey_submissions").select("*").order("created_at", { ascending: false }).limit(opts.limit || 300);
        if (error) throw error;
        return data || [];
      } catch (e) {
        // Older PostgREST or a typo in the JSON path: fall back to full rows
        if (opts.light) { console.warn("light survey select failed, falling back:", e); return this.getSurveys({ ...opts, light: false }); }
        console.warn("Failed to load surveys:", e); return [];
      }
    }
    return mockDB.surveys;
  },
  // Full row for a light list entry (opens a report, exports its PDF).
  async hydrateSubmission(s) {
    if (!s || !s._light || !s.id || !USE_SUPABASE || !supabase) return s;
    try {
      const { data, error } = await supabase.from("survey_submissions").select("*").eq("id", s.id).single();
      if (error || !data) return s;
      return { ...mapSurveyRow(data), status: s.status || data.status };
    } catch(e) { return s; }
  },
  async updateStatus(table, id, status) {
    if (USE_SUPABASE && supabase) {
      const { error } = await supabase.from(table).update({ status }).eq("id", id);
      if (error) throw error;
    }
  },
  // Admin correction of a submitted survey/assessment. Merges changes into the
  // existing checklist JSONB rather than overwriting it, so untouched fields
  // (photos, meter, panel, etc.) survive the edit.
  async updateSurveyFields(id, changes) {
    if (!USE_SUPABASE || !supabase) return false;
    try {
      const { data, error: readErr } = await supabase
        .from("survey_submissions").select("checklist, notes").eq("id", id).single();
      if (readErr) throw readErr;

      const existing = typeof data.checklist === "string"
        ? JSON.parse(data.checklist)
        : (data.checklist || {});
      const merged = { ...existing, ...changes };

      const update = { checklist: JSON.stringify(merged) };
      if (merged.formType === "Service Assessment") {
        if ("issue" in changes || "serviceType" in changes) update.notes = `${merged.serviceType || ""}: ${merged.issue || ""}`;
        if (changes.client?.name) update.site_name = changes.client.name;
        if (changes.client?.address) update.address = changes.client.address;
        if (changes.system?.size !== undefined) update.system_size = changes.system.size;
      } else if ("electricalNotes" in changes || "roofNotes" in changes) {
        update.notes = (changes.electricalNotes ?? existing.electricalNotes ?? "") + " " + (changes.roofNotes ?? existing.roofNotes ?? "");
      }
      // Homeowner name/address live in their own columns too — those drive the
      // submissions list and the PDF header, so keep them in sync with the edit.
      if (changes.homeowner?.name) update.site_name = changes.homeowner.name;
      if (changes.homeowner?.address) update.address = changes.homeowner.address;

      const { error } = await supabase.from("survey_submissions")
        .update(update)
        .eq("id", id);
      if (error) throw error;
      return true;
    } catch(e) { console.warn("updateSurveyFields failed:", e); return false; }
  },

  // ─── Projects ───────────────────────────────────────────────────────────
  async getProjects(userId, opts = {}) {
    if (USE_SUPABASE && supabase) {
      const ckey = `projects:${(localCache.loadUser() || {}).email || ""}`;
      if (!opts.fresh) { const hit = memoCache.get(ckey); if (hit) return hit; }
      try {
        const { data, error } = await supabase.from("projects").select("*").order("created_at", { ascending: false });
        if (error) throw error;
        // Invoice and costing live in project_finance, a table only the two
        // money logins can read. Everyone else gets jobs with no money on them.
        const fin = new Map();
        if (canSeeMoney(localCache.loadUser())) {
          try {
            const { data: rows } = await supabase.from("project_finance").select("project_id, invoice, costing, estimate");
            (rows || []).forEach(r => fin.set(r.project_id, r));
          } catch(e) { console.warn("project_finance read failed:", e); }
        }
        const pj = (v) => { try { return typeof v === "string" ? JSON.parse(v) : (v || null); } catch(e) { return null; } };
        const mapped = (data || []).map(p => ({
          ...p,
          systemSize: p.system_size || "",
          tasks: typeof p.tasks === "string" ? JSON.parse(p.tasks) : (p.tasks || []),
          materials: typeof p.materials === "string" ? JSON.parse(p.materials) : (p.materials || []),
          notes: typeof p.notes === "string" ? JSON.parse(p.notes) : (p.notes || []),
          members: typeof p.members === "string" ? JSON.parse(p.members) : (p.members || []),
          costing: fin.has(p.id) ? pj(fin.get(p.id).costing) : null,
          invoice: fin.has(p.id) ? pj(fin.get(p.id).invoice) : null,
          estimate: fin.has(p.id) ? pj(fin.get(p.id).estimate) : null,
          projectType: p.project_type || "",
          serviceSubtype: p.service_subtype || "",
          playbookId: p.playbook_id || "",
          referenceIds: typeof p.reference_ids === "string" ? JSON.parse(p.reference_ids) : (p.reference_ids || []),
          refPhotos: typeof p.ref_photos === "string" ? JSON.parse(p.ref_photos) : (p.ref_photos || []),
          jhaSheet: typeof p.jha_sheet === "string" ? JSON.parse(p.jha_sheet) : (p.jha_sheet || null),
          refDocs: typeof p.ref_docs === "string" ? JSON.parse(p.ref_docs) : (p.ref_docs || []),
          truckList: p.truck_list || "",
          partner: p.partner || "",
          clientEmail: p.client_email || "",
          clientPhone: p.client_phone || "",
          visits: typeof p.visits === "string" ? JSON.parse(p.visits) : (p.visits || []),
          inviteUidCrew: p.invite_uid_crew || "",
          inviteUidOwner: p.invite_uid_owner || "",
          inviteSeq: p.invite_seq || 0,
          scheduled_at: p.scheduled_at || "",
          acuityAppointmentId: p.acuity_appointment_id || "",
          scope: p.scope_of_work || "",
          workOrder: p.work_order || "",
          permit: typeof p.permit === "string" ? JSON.parse(p.permit) : (p.permit || null),
        }));
        return memoCache.set(ckey, mapped);
      } catch(e) { console.warn("Failed to load projects:", e); return localCache.load("projects") || []; }
    }
    return localCache.load("projects") || [];
  },
  async saveProject(project, opts = {}) {
    memoCache.invalidate("projects");
    project = { ...project, status: autoStatus(project) };
    // Always save locally first
    const projects = localCache.load("projects") || [];
    const idx = projects.findIndex(p => p.id === project.id);
    if (idx >= 0) projects[idx] = project; else projects.unshift(project);
    localCache.save("projects", projects);
    // Save to Supabase
    if (USE_SUPABASE && supabase) {
      try {
        const row = {
          id: project.id,
          name: project.name || "",
          client: project.client || "",
          address: project.address || "",
          system_size: project.systemSize || "",
          status: project.status || "not_started",
          completion: project.completion || 0,
          tasks: JSON.stringify(project.tasks || []),
          materials: JSON.stringify(project.materials || []),
          notes: JSON.stringify(project.notes || []),
          // A real JSON array, NOT JSON.stringify'd text. The database's
          // access policy matches crew by looking inside this list; when it
          // was saved as text, the policy matched nobody and crew couldn't
          // see a single job they were assigned to.
          members: Array.isArray(project.members) ? project.members : [],
          project_type: project.projectType || "",
          service_subtype: project.serviceSubtype || "",
          playbook_id: project.playbookId || "",
          reference_ids: JSON.stringify(project.referenceIds || []),
          ref_photos: JSON.stringify(project.refPhotos || []),
          jha_sheet: project.jhaSheet ? JSON.stringify(project.jhaSheet) : null,
          ref_docs: JSON.stringify(project.refDocs || []),
          truck_list: project.truckList || "",
          partner: project.partner || "",
          client_email: project.clientEmail || "",
          client_phone: project.clientPhone || "",
          visits: JSON.stringify(project.visits || []),
          invite_uid_crew: project.inviteUidCrew || "",
          invite_uid_owner: project.inviteUidOwner || "",
          invite_seq: project.inviteSeq || 0,
          scheduled_at: project.scheduled_at || null,
          acuity_appointment_id: project.acuityAppointmentId || null,
          created_by: project.created_by || "",
          scope_of_work: project.scope || "",
          work_order: project.workOrder || "",
          permit: project.permit || null,
        };
        // Optimistic lock: the update only lands if nobody changed the row
        // since this copy was loaded (updated_at still matches). Otherwise we
        // re-read, merge their change into ours, and try once more — so a
        // coordinator saving a job no longer wipes the tasks/notes the crew's
        // form submit added while it was open.
        const { data: existing } = await supabase.from("projects").select("id, updated_at").eq("id", project.id).maybeSingle();
        let error = null;
        const stamp = new Date().toISOString();
        if (existing) {
          const loaded = project.updated_at || null;
          let q = supabase.from("projects").update({ ...row, updated_at: stamp }).eq("id", project.id);
          if (loaded && existing.updated_at) q = q.eq("updated_at", loaded);
          let { data: upd, error: updateErr } = await q.select("id");
          if (updateErr && /scope_of_work|work_order|permit/.test(updateErr.message || "")) {
            // SCOPE_OF_WORK_SQL.md / WORK_ORDER_SQL.md / PERMITS_SQL.md not run yet — save everything else
            if (/work_order/.test(updateErr.message || "")) delete row.work_order; else if (/permit/.test(updateErr.message || "")) delete row.permit; else delete row.scope_of_work;
            ({ data: upd, error: updateErr } = await supabase.from("projects").update({ ...row, updated_at: stamp }).eq("id", project.id).select("id"));
          }
          if (updateErr && /updated_at/.test(updateErr.message || "")) {
            // Column not added yet (UPDATED_AT_LOCK_SQL.md) — save without the lock
            ({ error: updateErr } = await supabase.from("projects").update(row).eq("id", project.id));
            upd = [{ id: project.id }];
          }
          error = updateErr;
          if (!error && (!upd || upd.length === 0) && !opts._retried) {
            const fresh = (await this.getProjects(undefined, { fresh: true })).find(p => p.id === project.id);
            if (fresh) {
              const merged = mergeProjectEdits(fresh, project, opts.base);
              const res = await this.saveProject(merged, { ...opts, _retried: true });
              if (!opts.quiet) alert("Someone else changed this job while you had it open (a form was filed or another edit was saved). Their changes were merged with yours.");
              return { ok: true, merged: true, project: res?.project || merged };
            }
          }
          if (!error) project = { ...project, updated_at: stamp };
        } else {
          row.created_at = project.created_at || new Date().toISOString();
          row.updated_at = stamp;
          let { error: insertErr } = await supabase.from("projects").insert(row);
          if (insertErr && /work_order/.test(insertErr.message || "")) { delete row.work_order; ({ error: insertErr } = await supabase.from("projects").insert(row)); }
          if (insertErr && /scope_of_work/.test(insertErr.message || "")) { delete row.scope_of_work; ({ error: insertErr } = await supabase.from("projects").insert(row)); }
          if (insertErr && /permit/.test(insertErr.message || "")) { delete row.permit; ({ error: insertErr } = await supabase.from("projects").insert(row)); }
          if (insertErr && /updated_at/.test(insertErr.message || "")) { delete row.updated_at; ({ error: insertErr } = await supabase.from("projects").insert(row)); }
          error = insertErr;
          if (!error) project = { ...project, updated_at: stamp };
        }
        if (error) { console.error("Project save error:", error.message, error.details, error.hint); alert("Save failed: " + error.message); return { ok: false, error, project }; }
        // Money fields go to their own locked table — only the two money
        // logins can write it, and only they ever hold these values.
        if (canSeeMoney(localCache.loadUser()) && (project.invoice || project.costing)) {
          const { error: finErr } = await supabase.from("project_finance").upsert({
            project_id: project.id, invoice: project.invoice || null, costing: project.costing || null,
            ...(project.estimate ? { estimate: project.estimate } : {}),
            updated_by: (localCache.loadUser() || {}).email || "", updated_at: new Date().toISOString(),
          }, { onConflict: "project_id" });
          if (finErr) console.warn("project_finance save failed:", finErr.message);
        }
        return { ok: true, project };
      } catch(e) { console.error("Project save exception:", e); return { ok: false, error: e, project }; }
    }
    return { ok: true, project };
  },
  // Change a few columns of a job without touching the rest. Used by the
  // crew's form submit, the JHA sheet and job costing so they never overwrite
  // tasks/visits/notes someone else is editing. Re-reads and retries on a
  // lock miss; `fn(fresh)` returns the fields to write given the latest row.
  async patchProject(projectId, fn, { tries = 3 } = {}) {
    for (let i = 0; i < tries; i++) {
      const fresh = (await this.getProjects(undefined, { fresh: true })).find(p => p.id === projectId);
      if (!fresh) return null;
      const fields = fn(fresh);
      if (!fields) return fresh;
      const next = { ...fresh, ...fields };
      if (!USE_SUPABASE || !supabase) { await this.saveProject(next, { quiet: true }); return next; }
      const row = {};
      const map = { tasks: v => JSON.stringify(v || []), notes: v => JSON.stringify(v || []), visits: v => JSON.stringify(v || []), jhaSheet: v => v ? JSON.stringify(v) : null, members: v => Array.isArray(v) ? v : [], refPhotos: v => JSON.stringify(v || []), refDocs: v => JSON.stringify(v || []) };
      const col = { tasks: "tasks", notes: "notes", visits: "visits", jhaSheet: "jha_sheet", members: "members", refPhotos: "ref_photos", refDocs: "ref_docs", partner: "partner", status: "status", scheduled_at: "scheduled_at", completion: "completion", playbookId: "playbook_id", truckList: "truck_list", scope: "scope_of_work", workOrder: "work_order", permit: "permit" };
      Object.entries(fields).forEach(([k, v]) => { if (col[k]) row[col[k]] = map[k] ? map[k](v) : v; });
      const stamp = new Date().toISOString();
      let q = supabase.from("projects").update({ ...row, updated_at: stamp }).eq("id", projectId);
      if (fresh.updated_at) q = q.eq("updated_at", fresh.updated_at);
      let { data, error } = await q.select("id");
      if (error && /updated_at/.test(error.message || "")) { ({ error } = await supabase.from("projects").update(row).eq("id", projectId)); data = [{ id: projectId }]; }
      if (error) { console.warn("patchProject failed:", error.message); return null; }
      memoCache.invalidate("projects");
      if (data && data.length > 0) return { ...next, updated_at: stamp };
      // lock miss — someone saved in between; loop re-reads and reapplies
    }
    return null;
  },
  // Fold job `fromId` into job `intoId`: lists are unioned, blanks on the kept
  // job are filled from the other, forms/runs/texts/quotes/estimates are
  // re-pointed, then the duplicate is deleted.
  async mergeProjects(intoId, fromId) {
    const all = await this.getProjects(undefined, { fresh: true });
    const into = all.find(p => p.id === intoId), from = all.find(p => p.id === fromId);
    if (!into || !from) throw new Error("job not found");
    const merged = mergeProjectEdits(from, into);
    ["client", "address", "clientEmail", "clientPhone", "systemSize", "projectType", "serviceSubtype", "partner", "playbookId", "truckList", "scope"].forEach(k => {
      if (!merged[k] && from[k]) merged[k] = from[k];
    });
    if (!merged.jhaSheet && from.jhaSheet) merged.jhaSheet = from.jhaSheet;
    if (!merged.invoice && from.invoice) merged.invoice = from.invoice;
    if (!merged.costing && from.costing) merged.costing = from.costing;
    merged.notes = [...(merged.notes || []), { id: Date.now(), text: `Merged duplicate job "${from.name}" (${from.address || ""}) into this one.`, by: (localCache.loadUser() || {}).name || "", at: new Date().toISOString() }];
    merged.updated_at = into.updated_at;
    const res = await this.saveProject(merged, { quiet: true });
    if (res && res.ok === false) throw new Error("could not save the kept job");
    if (USE_SUPABASE && supabase) {
      const repoint = async (table, col = "project_id") => { try { await supabase.from(table).update({ [col]: intoId }).eq(col, fromId); } catch(e) { console.warn(`repoint ${table} failed`, e); } };
      await Promise.all(["jha_submissions", "survey_submissions", "procedure_runs", "comms_log", "service_quotes", "rr_estimates"].map(t => repoint(t)));
    }
    await this.deleteProject(fromId);
    return merged;
  },
  async deleteProject(id) {
    memoCache.invalidate("projects");
    if (USE_SUPABASE && supabase) { try { await supabase.from("project_finance").delete().eq("project_id", id); } catch(e) {} }
    const projects = (localCache.load("projects") || []).filter(p => p.id !== id);
    localCache.save("projects", projects);
    if (USE_SUPABASE && supabase) {
      try { await supabase.from("projects").delete().eq("id", id); } catch(e) {}
    }
  },

  // ─── Safety: incidents, inspections, corrective actions ───────────────────
  async getSafetyRecords(table) {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from(table).select("*").order("created_at", { ascending: false });
        if (error) throw error;
        return (data || []).map(r => ({
          ...r,
          details: typeof r.details === "string" ? JSON.parse(r.details) : (r.details || {}),
          photo_urls: typeof r.photo_urls === "string" ? JSON.parse(r.photo_urls) : (r.photo_urls || []),
        }));
      } catch(e) { console.warn("getSafetyRecords(" + table + ") failed:", e); }
    }
    return localCache.load(table) || [];
  },
  async saveSafetyRecord(table, record) {
    const row = {
      ...record,
      details: JSON.stringify(record.details || {}),
      photo_urls: JSON.stringify(record.photo_urls || []),
    };
    if (USE_SUPABASE && supabase) {
      try {
        const { data: existing } = await supabase.from(table).select("id").eq("id", record.id).maybeSingle();
        if (existing) { await supabase.from(table).update(row).eq("id", record.id); }
        else { row.created_at = row.created_at || new Date().toISOString(); await supabase.from(table).insert(row); }
        return true;
      } catch(e) { console.warn("saveSafetyRecord failed:", e); return false; }
    }
    const list = localCache.load(table) || [];
    const idx = list.findIndex(r => r.id === record.id);
    if (idx >= 0) list[idx] = record; else list.unshift(record);
    localCache.save(table, list);
    return true;
  },
  async deleteSafetyRecord(table, id) {
    if (USE_SUPABASE && supabase) {
      try { await supabase.from(table).delete().eq("id", id); } catch(e) {}
    }
    localCache.save(table, (localCache.load(table) || []).filter(r => r.id !== id));
  },

  // Draft a Spanish version of a procedure. Returns the translated fields for
  // review — nothing is shown to the crew until a person approves it.
  async translateProcedure(pb) {
    if (!USE_SUPABASE || !supabase) throw new Error("Supabase not configured");
    const { data, error } = await supabase.functions.invoke("translate-procedure", {
      body: {
        title: pb.title || "", summary: pb.summary || "",
        steps: pb.steps || [], safety: pb.safety || [],
        partnerNotes: (pb.partnerNotes || []).map(n => n.note || ""),
        links: (pb.links || []).map(l => l.title || ""),
      },
    });
    if (error) throw error;
    if (data?.error) throw new Error(data.error);
    return data;
  },

  // ─── Procedure Runs ───────────────────────────────────────────────────────
  // A completed walk-through of a procedure on a specific job. This is the
  // record that proves the closeout actually happened, not just that we had
  // a document saying it should.
  async getProcedureRuns(projectId) {
    if (USE_SUPABASE && supabase) {
      try {
        let q = supabase.from("procedure_runs").select("*").order("started_at", { ascending: false });
        if (projectId) q = q.eq("project_id", projectId);
        const { data, error } = await q;
        if (error) throw error;
        return (data || []).map(r => ({
          ...r,
          projectId: r.project_id, playbookId: r.playbook_id, playbookTitle: r.playbook_title,
          startedBy: r.started_by, startedAt: r.started_at, completedAt: r.completed_at,
          items: typeof r.items === "string" ? JSON.parse(r.items) : (r.items || []),
        }));
      } catch(e) { console.warn("getProcedureRuns failed:", e); }
    }
    const all = localCache.load("procedure_runs") || [];
    return projectId ? all.filter(r => r.projectId === projectId) : all;
  },
  async saveProcedureRun(run) {
    const row = {
      id: run.id, project_id: run.projectId || null, project_name: run.projectName || "",
      playbook_id: run.playbookId || "", playbook_title: run.playbookTitle || "",
      started_by: run.startedBy || "", started_at: run.startedAt || new Date().toISOString(),
      completed_at: run.completedAt || null,
      items: JSON.stringify(run.items || []),
    };
    if (USE_SUPABASE && supabase) {
      try {
        const { data: existing } = await supabase.from("procedure_runs").select("id").eq("id", run.id).maybeSingle();
        if (existing) await supabase.from("procedure_runs").update(row).eq("id", run.id);
        else await supabase.from("procedure_runs").insert(row);
        return true;
      } catch(e) { console.warn("saveProcedureRun failed:", e); return false; }
    }
    const list = localCache.load("procedure_runs") || [];
    const idx = list.findIndex(x => x.id === run.id);
    if (idx >= 0) list[idx] = run; else list.unshift(run);
    localCache.save("procedure_runs", list);
    return true;
  },

  // ─── Procedures / Playbooks ───────────────────────────────────────────────
  async getPlaybooks(opts = {}) {
    if (USE_SUPABASE && supabase) {
      const ckey = `playbooks:${(localCache.loadUser() || {}).email || ""}`;
      if (!opts.fresh) { const hit = memoCache.get(ckey); if (hit) return hit; }
      try {
        const { data, error } = await supabase.from("playbooks").select("*").order("title");
        if (error) throw error;
        // Partner pay per scope sits in playbook_pay (money logins only)
        const pay = new Map();
        if (canSeeMoney(localCache.loadUser())) {
          try {
            const { data: rows } = await supabase.from("playbook_pay").select("playbook_id, pay_rate, pay_nte");
            (rows || []).forEach(r => pay.set(r.playbook_id, r));
          } catch(e) { console.warn("playbook_pay read failed:", e); }
        }
        return memoCache.set(ckey, (data || []).filter(p => canSeePlaybook({ allowedEmails: typeof p.allowed_emails === "string" ? JSON.parse(p.allowed_emails) : (p.allowed_emails || []) }, localCache.loadUser())).map(p => ({
          ...p,
          serviceType: p.service_type || "",
          steps: typeof p.steps === "string" ? JSON.parse(p.steps) : (p.steps || []),
          safety: typeof p.safety === "string" ? JSON.parse(p.safety) : (p.safety || []),
          links: typeof p.links === "string" ? JSON.parse(p.links) : (p.links || []),
          partnerNotes: typeof p.partner_notes === "string" ? JSON.parse(p.partner_notes) : (p.partner_notes || []),
          partner: p.partner || "",
          timeBudget: p.time_budget || null,
          timeCrew: p.time_crew || null,
          payRate: pay.has(p.id) ? pay.get(p.id).pay_rate : null,
          payNte: pay.has(p.id) ? pay.get(p.id).pay_nte : null,
          reportFields: typeof p.report_fields === "string" ? JSON.parse(p.report_fields) : (p.report_fields || []),
          requiredPhotos: typeof p.required_photos === "string" ? JSON.parse(p.required_photos) : (p.required_photos || []),
          suggests: typeof p.suggests === "string" ? JSON.parse(p.suggests) : (p.suggests || []),
          es: typeof p.es === "string" ? (p.es ? JSON.parse(p.es) : null) : (p.es || null),
          esStatus: p.es_status || "none",
          allowedEmails: typeof p.allowed_emails === "string" ? JSON.parse(p.allowed_emails) : (p.allowed_emails || []),
        })));
      } catch(e) { console.warn("getPlaybooks failed:", e); }
    }
    return localCache.load("playbooks") || [];
  },
  async savePlaybook(pb) {
    memoCache.invalidate("playbooks");
    const row = {
      id: pb.id, title: pb.title || "", service_type: pb.serviceType || "",
      summary: pb.summary || "",
      steps: JSON.stringify(pb.steps || []),
      safety: JSON.stringify(pb.safety || []),
      links: JSON.stringify(pb.links || []),
      partner_notes: JSON.stringify(pb.partnerNotes || []),
      // Scope-of-work requirements: the report fields and photos this specific
      // kind of visit needs. Lives on the procedure because one partner can
      // have several scopes (inspection, removal, reinstall) with different lists.
      partner: pb.partner || "",
      // What the partner pays for this scope, in man-hours, and how many techs
      // it realistically takes. Together these give the crew an on-site clock.
      time_budget: pb.timeBudget || null,
      time_crew: pb.timeCrew || null,
      report_fields: JSON.stringify(pb.reportFields || []),
      required_photos: JSON.stringify(pb.requiredPhotos || []),
      // Spanish version, drafted by machine and approved by a person. Kept
      // beside the English rather than replacing it, so both stay available.
      suggests: JSON.stringify(pb.suggests || []),
      es: JSON.stringify(pb.es || null),
      es_status: pb.esStatus || "none", // none | draft | approved
      allowed_emails: JSON.stringify((pb.allowedEmails || []).map(e => String(e).trim().toLowerCase()).filter(Boolean)),
    };
    if (USE_SUPABASE && supabase) {
      try {
        const { data: existing } = await supabase.from("playbooks").select("id").eq("id", pb.id).maybeSingle();
        if (existing) await supabase.from("playbooks").update(row).eq("id", pb.id);
        else { row.created_at = new Date().toISOString(); await supabase.from("playbooks").insert(row); }
        if (canSeeMoney(localCache.loadUser()) && (pb.payRate != null || pb.payNte != null)) {
          const { error: payErr } = await supabase.from("playbook_pay").upsert({ playbook_id: pb.id, pay_rate: pb.payRate ?? null, pay_nte: pb.payNte ?? null, updated_at: new Date().toISOString() });
          if (payErr) console.warn("playbook_pay save failed:", payErr.message);
        }
        return true;
      } catch(e) { console.warn("savePlaybook failed:", e); return false; }
    }
    const list = localCache.load("playbooks") || [];
    const idx = list.findIndex(x => x.id === pb.id);
    if (idx >= 0) list[idx] = pb; else list.unshift(pb);
    localCache.save("playbooks", list);
    return true;
  },
  async deletePlaybook(id) {
    memoCache.invalidate("playbooks");
    if (USE_SUPABASE && supabase) {
      try { await supabase.from("playbooks").delete().eq("id", id); } catch(e) {}
    }
    localCache.save("playbooks", (localCache.load("playbooks") || []).filter(x => x.id !== id));
  },

  // ─── Partner Directory ────────────────────────────────────────────────────
  async getPartners(opts = {}) {
    if (USE_SUPABASE && supabase) {
      if (!opts.fresh) { const hit = memoCache.get("partners"); if (hit) return hit; }
      try {
        const { data, error } = await supabase.from("partners").select("*").order("name");
        if (error) throw error;
        // Safety net against duplicate rows ("Omnidian" / "Omnidian "): keep
        // one per trimmed, case-insensitive name — the one with the most
        // configuration on it — so the forms never show the same partner twice.
        const jl = (v) => { try { const a = typeof v === "string" ? JSON.parse(v) : v; return Array.isArray(a) ? a.length : 0; } catch(e) { return 0; } };
        const score = (p) => jl(p.custom_fields) + jl(p.required_photos) + jl(p.default_tasks) + (p.submit_days ? 1 : 0) + ((p.notes || "").length > 0 ? 1 : 0) + ((p.homeowner_note || "").length > 0 ? 1 : 0);
        const byName = new Map();
        for (const p of (data || [])) {
          const k = (p.name || "").trim().toLowerCase();
          if (!k) continue;
          const cur = byName.get(k);
          if (!cur || score(p) > score(cur)) byName.set(k, p);
        }
        return memoCache.set("partners", Array.from(byName.values()).map(p => ({
          ...p,
          customFields: typeof p.custom_fields === "string" ? JSON.parse(p.custom_fields) : (p.custom_fields || []),
          defaultTasks: typeof p.default_tasks === "string" ? JSON.parse(p.default_tasks) : (p.default_tasks || []),
          requiredPhotos: typeof p.required_photos === "string" ? JSON.parse(p.required_photos) : (p.required_photos || []),
          submitDays: p.submit_days || null,
          homeownerNote: p.homeowner_note || "",
          crewNote: p.crew_note || "",
          submissionMethod: p.submission_method || "email",
          contactName: p.contact_name || "", contactEmail: p.contact_email || "",
          contactPhone: p.contact_phone || "", portalUrl: p.portal_url || "",
        })));
      } catch(e) { console.warn("getPartners failed:", e); }
    }
    return localCache.load("partners") || [];
  },
  async savePartner(partner) {
    memoCache.invalidate("partners");
    const row = {
      id: partner.id, name: partner.name || "",
      submission_method: partner.submissionMethod || "email",
      contact_name: partner.contactName || "", contact_email: partner.contactEmail || "",
      contact_phone: partner.contactPhone || "", portal_url: partner.portalUrl || "",
      notes: partner.notes || "",
      custom_fields: JSON.stringify(partner.customFields || []),
      default_tasks: JSON.stringify(partner.defaultTasks || []),
      required_photos: JSON.stringify(partner.requiredPhotos || []),
      submit_days: partner.submitDays ? parseInt(partner.submitDays, 10) || null : null,
      homeowner_note: partner.homeownerNote || "",
      crew_note: partner.crewNote || "",
    };
    if (USE_SUPABASE && supabase) {
      try {
        const { data: existing } = await supabase.from("partners").select("id").eq("id", partner.id).single();
        if (existing) { await supabase.from("partners").update(row).eq("id", partner.id); }
        else { row.created_at = new Date().toISOString(); await supabase.from("partners").insert(row); }
        return;
      } catch(e) { console.warn("savePartner failed:", e); }
    }
    const list = localCache.load("partners") || [];
    const idx = list.findIndex(p => p.id === partner.id);
    if (idx >= 0) list[idx] = partner; else list.unshift(partner);
    localCache.save("partners", list);
  },
  async deletePartner(id) {
    memoCache.invalidate("partners");
    if (USE_SUPABASE && supabase) {
      try { await supabase.from("partners").delete().eq("id", id); } catch(e) {}
    }
    localCache.save("partners", (localCache.load("partners") || []).filter(p => p.id !== id));
  },
  // Forms filed against a job: by project_id, with a fallback on the address
  // for forms filed before project_id existed.
  async getProjectForms(project) {
    if (!project) return [];
    const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
    const addr = norm(project.address);
    const match = (row, addrField) => row.project_id === project.id || (addr && addr.length > 6 && norm(row[addrField]) === addr);
    try {
      let jhas = [], surveys = [];
      if (USE_SUPABASE && supabase) {
        // Only this job's rows: by project_id, plus a street-level address
        // match for forms filed before the job link existed.
        const street = String(project.address || "").split(",")[0].trim();
        const pull = async (table, addrField) => {
          const byId = await supabase.from(table).select("*").eq("project_id", project.id).limit(100);
          const byAddr = street.length > 5 ? await supabase.from(table).select("*").ilike(addrField, `%${street}%`).limit(50) : { data: [] };
          const seen = new Set();
          return [...(byId.data || []), ...(byAddr.data || [])].filter(r => { if (seen.has(r.id)) return false; seen.add(r.id); return true; });
        };
        [jhas, surveys] = await Promise.all([pull("jha_submissions", "location"), pull("survey_submissions", "address")]);
      } else {
        [jhas, surveys] = await Promise.all([this.getJHAs(), this.getSurveys()]);
      }
      const out = [
        ...(jhas || []).filter(j => match(j, "location")).map(mapJHARow),
        ...(surveys || []).filter(s => match(s, "address")).map(mapSurveyRow),
      ];
      return out.sort((a, b) => new Date(b.submittedAt) - new Date(a.submittedAt));
    } catch(e) { console.warn("getProjectForms failed:", e); return []; }
  },

  // ─── Quo (phone/SMS) — comms_log + the `quo` Edge Function ────────────────
  async getComms(projectId, { limit = 60 } = {}) {
    if (!USE_SUPABASE || !supabase || !projectId) return [];
    try {
      const { data, error } = await supabase.from("comms_log").select("*").eq("project_id", projectId).order("at", { ascending: false }).limit(limit);
      if (error) throw error;
      return data || [];
    } catch(e) { console.warn("getComms failed:", e); return []; }
  },
  // Everything the office still has to deal with: unanswered texts, missed
  // calls, voicemails, summaries with next steps, and unmatched numbers.
  async getCommsQueue() {
    if (!USE_SUPABASE || !supabase) return [];
    try {
      const { data, error } = await supabase.from("comms_log").select("*").eq("handled", false).order("at", { ascending: false }).limit(100);
      if (error) throw error;
      return data || [];
    } catch(e) { console.warn("getCommsQueue failed:", e); return []; }
  },
  async markCommHandled(id, handled = true) {
    if (!USE_SUPABASE || !supabase) return true;
    const { error } = await supabase.from("comms_log").update({ handled }).eq("id", id);
    return !error;
  },
  async attachComm(id, projectId) {
    if (!USE_SUPABASE || !supabase) return true;
    const { error } = await supabase.from("comms_log").update({ project_id: projectId }).eq("id", id);
    return !error;
  },
  // ── Office board: the coordinator's daily to-do + handoff ──────────────
  // Open items from earlier days carry over by simply still being open.
  async getOfficeTodos(day) {
    if (!USE_SUPABASE || !supabase) return localCache.load("office_todos") || [];
    try {
      const { data, error } = await supabase.from("office_todos").select("*")
        .or(`day.eq.${day},and(status.eq.open,day.lt.${day}),kind.eq.standing,kind.eq.recurring`)
        .order("day", { ascending: true }).order("seq", { ascending: true });
      if (error) throw error;
      return data || [];
    } catch(e) { console.warn("getOfficeTodos failed:", e); return []; }
  },
  async getOfficeDay(day) {
    if (!USE_SUPABASE || !supabase) return (localCache.load("office_todos") || []).filter(x => x.day === day);
    try {
      const { data, error } = await supabase.from("office_todos").select("*").eq("day", day).order("seq");
      if (error) throw error;
      return data || [];
    } catch(e) { return []; }
  },
  async addOfficeTodos(rows) {
    if (!USE_SUPABASE || !supabase) { const all = localCache.load("office_todos") || []; const withIds = rows.map((r, i) => ({ id: `ot${Date.now()}${i}`, status: "open", note: "", ...r })); localCache.save("office_todos", [...all, ...withIds]); return withIds; }
    const { data, error } = await supabase.from("office_todos").insert(rows).select("*");
    if (error) throw error;
    return data || [];
  },
  // Daily copies: the unique index (recurring_id, day) makes a double seed a
  // no-op, so two tabs or a double render can't produce duplicates.
  async seedOfficeDaily(rows) {
    if (!USE_SUPABASE || !supabase) return this.addOfficeTodos(rows);
    const { error } = await supabase.from("office_todos").upsert(rows, { onConflict: "recurring_id,day", ignoreDuplicates: true });
    if (error && !/recurring_id|no unique|constraint/i.test(error.message || "")) throw error;
    if (error) { // index not created yet — plain insert, still guarded by the in-memory check
      const { data, error: e2 } = await supabase.from("office_todos").insert(rows).select("*"); if (e2) throw e2; return data || [];
    }
    const day = rows[0]?.day;
    const { data } = await supabase.from("office_todos").select("*").eq("day", day).not("recurring_id", "is", null);
    return data || [];
  },
  async updateOfficeTodo(id, patch) {
    if (!USE_SUPABASE || !supabase) { const all = localCache.load("office_todos") || []; localCache.save("office_todos", all.map(x => x.id === id ? { ...x, ...patch } : x)); return true; }
    const { error } = await supabase.from("office_todos").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
    if (error) { console.warn("updateOfficeTodo failed:", error.message); return false; }
    return true;
  },
  async deleteOfficeTodo(id) {
    if (!USE_SUPABASE || !supabase) { localCache.save("office_todos", (localCache.load("office_todos") || []).filter(x => x.id !== id)); return true; }
    const { error } = await supabase.from("office_todos").delete().eq("id", id);
    return !error;
  },
  async getOfficeHandoff(day) {
    if (!USE_SUPABASE || !supabase) return (localCache.load("office_handoff") || {})[day] || null;
    try { const { data } = await supabase.from("office_handoff").select("*").eq("day", day).maybeSingle(); return data || null; } catch(e) { return null; }
  },
  async getRecentHandoffs(limit = 7) {
    if (!USE_SUPABASE || !supabase) return Object.values(localCache.load("office_handoff") || {});
    try { const { data } = await supabase.from("office_handoff").select("*").order("day", { ascending: false }).limit(limit); return data || []; } catch(e) { return []; }
  },
  async saveOfficeHandoff(day, note, by) {
    if (!USE_SUPABASE || !supabase) { const all = localCache.load("office_handoff") || {}; all[day] = { day, note, updated_by: by, updated_at: new Date().toISOString() }; localCache.save("office_handoff", all); return true; }
    const { error } = await supabase.from("office_handoff").upsert({ day, note, updated_by: by || "", updated_at: new Date().toISOString() }, { onConflict: "day" });
    if (error) { console.warn("saveOfficeHandoff failed:", error.message); return false; }
    return true;
  },
  async getCrewDirectory() {
    if (!USE_SUPABASE || !supabase) return [];
    try { const { data } = await supabase.from("profiles").select("email, full_name, role"); return (data || []).filter(x => x.email).map(x => ({ name: x.full_name || x.email, email: x.email, role: x.role || "" })); } catch(e) { return []; }
  },
  // Rough notes → structured items, matched to Hub jobs and crew (office-ai Edge Function)
  async officeAI(text, projects, crew) {
    if (!USE_SUPABASE || !supabase) throw new Error("Supabase not configured");
    const { data, error } = await supabase.functions.invoke("office-ai", { body: { text, today: jobDayKey(new Date().toISOString()), projects, crew } });
    if (error) {
      let msg = error.message || "AI call failed";
      try { const ctx = error.context; if (ctx && typeof ctx.json === "function") { const j = await ctx.json(); if (j?.error) msg = j.error; } } catch(_) {}
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);
    return data?.items || [];
  },
  async emailOfficeList(to, day, items, from) {
    if (!USE_SUPABASE || !supabase || !to) return false;
    const lines = items.map((it, i) => `${i + 1}. [${it.category}] ${it.title}\n   Do: ${it.do_text}\n   Before you start: ${it.before_text || "Nothing."}\n   Result goes: ${it.result_text || "—"}`);
    const { error } = await supabase.functions.invoke("send-notification", {
      body: { type: "Office To-Do", to, submittedBy: from || "", title: `To Do — ${fmtJobDate(`${day}T12:00:00`, { weekday: "long", month: "long", day: "numeric" })} (${items.length} items)`,
        message: `${lines.join("\n\n")}\n\nWork the list in the Hub → Office: ${(window.location.origin || "https://hub.solarability.com")}/#office` }
    });
    return !error;
  },
  // Send a text from the company number. Throws with a readable message.
  async quoSend({ to, content, projectId, kind }) {
    if (!USE_SUPABASE || !supabase) throw new Error("Supabase not configured");
    const { data, error } = await supabase.functions.invoke("quo", { body: { action: "send", to, content, projectId: projectId || null, kind: kind || "custom" } });
    if (error) {
      let msg = error.message || "send failed";
      try { const ctx = await error.context?.json?.(); if (ctx?.error) msg = ctx.error; } catch(e) {}
      throw new Error(msg);
    }
    if (data?.error) throw new Error(data.error);
    return data;
  },
  // Invoice / costing only (project_finance), without touching the job row
  async saveProjectFinance(projectId, { invoice, costing } = {}) {
    if (USE_SUPABASE && supabase) {
      try {
        const { data: ex } = await supabase.from("project_finance").select("project_id, invoice, costing").eq("project_id", projectId).maybeSingle();
        const row = { project_id: projectId, invoice: invoice !== undefined ? invoice : (ex?.invoice ?? null), costing: costing !== undefined ? costing : (ex?.costing ?? null),
          updated_by: (localCache.loadUser() || {}).email || "", updated_at: new Date().toISOString() };
        const { error } = await supabase.from("project_finance").upsert(row, { onConflict: "project_id" });
        if (error) throw error;
        memoCache.invalidate("projects");
        return true;
      } catch(e) { console.warn("saveProjectFinance failed:", e); return false; }
    }
    const list = localCache.load("projects") || [];
    const i = list.findIndex(p => p.id === projectId);
    if (i >= 0) { list[i] = { ...list[i], ...(invoice !== undefined ? { invoice } : {}), ...(costing !== undefined ? { costing } : {}) }; localCache.save("projects", list); }
    return true;
  },
  // ─── R&R estimate on a job (project_finance.estimate, money logins only)
  async saveProjectEstimate(projectId, estimate) {
    if (USE_SUPABASE && supabase) {
      try {
        const { data: existing } = await supabase.from("project_finance").select("project_id").eq("project_id", projectId).maybeSingle();
        const who = (localCache.loadUser() || {}).email || "";
        if (existing) { const { error } = await supabase.from("project_finance").update({ estimate, updated_by: who, updated_at: new Date().toISOString() }).eq("project_id", projectId); if (error) throw error; }
        else { const { error } = await supabase.from("project_finance").insert({ project_id: projectId, estimate, updated_by: who }); if (error) throw error; }
        return true;
      } catch(e) { console.warn("saveProjectEstimate failed:", e); return false; }
    }
    const list = localCache.load("projects") || [];
    const idx = list.findIndex(p => p.id === projectId);
    if (idx >= 0) { list[idx] = { ...list[idx], estimate }; localCache.save("projects", list); }
    return true;
  },

  // ─── Standalone R&R estimates (rr_estimates, money logins only). Most
  // estimates wait days or weeks for the customer to approve, so they live
  // here until there's a job to attach them to.
  async getRREstimates() {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from("rr_estimates").select("*").order("created_at", { ascending: false });
        if (error) throw error;
        return (data || []).map(r => ({
          id: r.id, client: r.client || "", address: r.address || "", status: r.status || "pending",
          price: Number(r.price || 0), cost: Number(r.cost || 0), margin: Number(r.margin || 0),
          inputs: typeof r.inputs === "string" ? JSON.parse(r.inputs) : (r.inputs || {}),
          estimate: typeof r.estimate === "string" ? JSON.parse(r.estimate) : (r.estimate || {}),
          notes: r.notes || "", projectId: r.project_id || "", createdBy: r.created_by || "", createdAt: r.created_at, updatedAt: r.updated_at,
        }));
      } catch(e) { console.warn("getRREstimates failed:", e); return []; }
    }
    return localCache.load("rr_estimates") || [];
  },
  async saveRREstimate(est) {
    const who = (localCache.loadUser() || {}).email || "";
    const row = {
      id: est.id, client: est.client || "", address: est.address || "", status: est.status || "pending",
      price: est.price || 0, cost: est.cost || 0, margin: est.margin || 0,
      inputs: JSON.stringify(est.inputs || {}), estimate: JSON.stringify(est.estimate || {}),
      notes: est.notes || "", project_id: est.projectId || null, created_by: est.createdBy || who, updated_at: new Date().toISOString(),
    };
    if (USE_SUPABASE && supabase) {
      const { error } = await supabase.from("rr_estimates").upsert(row, { onConflict: "id" });
      if (error) throw error;
      return true;
    }
    const list = localCache.load("rr_estimates") || [];
    const idx = list.findIndex(x => x.id === est.id);
    const next = { ...est, updatedAt: row.updated_at, createdAt: est.createdAt || row.updated_at, createdBy: est.createdBy || who };
    if (idx >= 0) list[idx] = next; else list.unshift(next);
    localCache.save("rr_estimates", list);
    return true;
  },
  async deleteRREstimate(id) {
    if (USE_SUPABASE && supabase) { const { error } = await supabase.from("rr_estimates").delete().eq("id", id); if (error) throw error; return true; }
    localCache.save("rr_estimates", (localCache.load("rr_estimates") || []).filter(x => x.id !== id));
    return true;
  },

  // ─── Service quotes (partner SQs) ────────────────────────────────────
  async getServiceQuotes() {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from("service_quotes").select("*").order("received_at", { ascending: false });
        if (error) throw error;
        return (data || []).map(r => ({ id: r.id, partner: r.partner || "", sqNumber: r.sq_number || "", client: r.client || "", address: r.address || "",
          serviceId: r.service_id || "", serviceName: r.service_name || "", offered: r.offered ?? "", nte: r.nte ?? "", status: r.status || "pending",
          reason: r.reason || "", estimateAmount: r.estimate_amount ?? "", receivedAt: r.received_at || "", decidedAt: r.decided_at || "", by: r.decided_by || "",
          notes: r.notes || "", projectId: r.project_id || "", createdAt: r.created_at }));
      } catch(e) { console.warn("getServiceQuotes failed:", e); }
    }
    return localCache.load("service_quotes") || [];
  },
  async saveServiceQuote(q) {
    const num = (v) => (v === "" || v == null || isNaN(Number(v))) ? null : Number(v);
    const row = { id: q.id, partner: q.partner || "", sq_number: q.sqNumber || "", client: q.client || "", address: q.address || "",
      service_id: q.serviceId || "", service_name: q.serviceName || "", offered: num(q.offered), nte: num(q.nte), status: q.status || "pending",
      reason: q.reason || "", estimate_amount: num(q.estimateAmount), received_at: q.receivedAt || null, decided_at: q.decidedAt || null, decided_by: q.by || "",
      notes: q.notes || "", project_id: q.projectId || null, updated_at: new Date().toISOString() };
    if (USE_SUPABASE && supabase) {
      try { const { error } = await supabase.from("service_quotes").upsert(row); if (error) throw error; return true; }
      catch(e) { console.warn("saveServiceQuote failed:", e); alert("Could not save the quote — is the service_quotes table created?"); return false; }
    }
    const list = localCache.load("service_quotes") || [];
    const idx = list.findIndex(r => r.id === q.id);
    if (idx >= 0) list[idx] = q; else list.unshift(q);
    localCache.save("service_quotes", list); return true;
  },

  // ─── Company contacts ────────────────────────────────────────────────
  async getContacts() {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from("company_contacts").select("*").order("company");
        if (error) throw error;
        return (data || []).map(r => ({ id: r.id, category: r.category || "other", company: r.company || "", name: r.name || "", role: r.role || "",
          email: r.email || "", phone: r.phone || "", whatFor: r.what_for || "", notes: r.notes || "" }));
      } catch(e) { console.warn("getContacts failed:", e); }
    }
    return localCache.load("company_contacts") || [];
  },
  async saveContact(c) {
    const row = { id: c.id, category: c.category || "other", company: c.company || "", name: c.name || "", role: c.role || "",
      email: (c.email || "").trim(), phone: (c.phone || "").trim(), what_for: c.whatFor || "", notes: c.notes || "", updated_by: c.updatedBy || "", updated_at: new Date().toISOString() };
    if (USE_SUPABASE && supabase) {
      try { const { error } = await supabase.from("company_contacts").upsert(row); if (error) throw error; return true; }
      catch(e) { console.warn("saveContact failed:", e); return false; }
    }
    const list = localCache.load("company_contacts") || [];
    const idx = list.findIndex(r => r.id === c.id);
    if (idx >= 0) list[idx] = c; else list.push(c);
    localCache.save("company_contacts", list); return true;
  },
  async deleteContact(id) {
    if (USE_SUPABASE && supabase) { try { await supabase.from("company_contacts").delete().eq("id", id); } catch(e) {} }
    localCache.save("company_contacts", (localCache.load("company_contacts") || []).filter(r => r.id !== id));
  },

  // ─── Crew capabilities & service catalog ─────────────────────────────
  async getCrewSkills() {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from("crew_skills").select("*").order("name");
        if (error) throw error;
        return (data || []).map(r => ({ id: r.id, name: r.name || "", role: r.role || "", email: r.email || "", notes: r.notes || "", updatedBy: r.updated_by || "",
          skills: typeof r.skills === "string" ? JSON.parse(r.skills) : (r.skills || {}) }));
      } catch(e) { console.warn("getCrewSkills failed:", e); }
    }
    return localCache.load("crew_skills") || [];
  },
  async saveCrewSkill(p) {
    const row = { id: p.id, name: p.name || "", role: p.role || "", email: (p.email || "").trim().toLowerCase(), notes: p.notes || "", skills: JSON.stringify(p.skills || {}), updated_by: p.updatedBy || "", updated_at: new Date().toISOString() };
    if (USE_SUPABASE && supabase) {
      try { const { error } = await supabase.from("crew_skills").upsert(row); if (error) throw error; return true; }
      catch(e) {
        console.warn("saveCrewSkill failed:", e);
        const msg = String(e?.message || e);
        alert(/email/i.test(msg) && /column/i.test(msg)
          ? "Not saved — the crew_skills table has no email column yet. Run in Supabase:\n\nALTER TABLE crew_skills ADD COLUMN IF NOT EXISTS email TEXT DEFAULT '';"
          : /policy|permission|row-level/i.test(msg)
            ? "Not saved — your login isn't allowed to write crew_skills. Run MONEY_ACCESS_SQL.md (or sign in as admin@ / customerservice@)."
            : "Not saved: " + msg);
        return false;
      }
    }
    const list = localCache.load("crew_skills") || [];
    const idx = list.findIndex(r => r.id === p.id);
    if (idx >= 0) list[idx] = p; else list.push(p);
    localCache.save("crew_skills", list); return true;
  },
  async deleteCrewSkill(id) {
    if (USE_SUPABASE && supabase) { try { await supabase.from("crew_skills").delete().eq("id", id); } catch(e) {} }
    localCache.save("crew_skills", (localCache.load("crew_skills") || []).filter(r => r.id !== id));
  },
  async getServiceCatalog() {
    if (USE_SUPABASE && supabase) {
      try {
        const { data, error } = await supabase.from("service_catalog").select("*").order("sort_order");
        if (error) throw error;
        return (data || []).map(r => ({ id: r.id, service: r.service || "", capability: r.capability || "", minLevel: r.min_level ?? 2, minCrew: r.min_crew ?? 1,
          estHours: Number(r.est_hours || 0), leadRequired: !!r.lead_required, projectType: r.project_type || "", serviceSubtype: r.service_subtype || "", notes: r.notes || "", sortOrder: r.sort_order ?? 0,
          partner: r.partner || "", minCharge: r.min_charge ?? "", nteDefault: r.nte_default ?? "", needsEstimate: !!r.needs_estimate, estimateWhen: r.estimate_when || "" }));
      } catch(e) { console.warn("getServiceCatalog failed:", e); }
    }
    return localCache.load("service_catalog") || [];
  },
  async saveService(s) {
    const row = { id: s.id, service: s.service || "", capability: s.capability || "", min_level: s.minLevel ?? 2, min_crew: s.minCrew ?? 1, est_hours: s.estHours ?? 0,
      lead_required: !!s.leadRequired, project_type: s.projectType || "", service_subtype: s.serviceSubtype || "", notes: s.notes || "", sort_order: s.sortOrder ?? 0,
      partner: s.partner || "", min_charge: s.minCharge === "" || s.minCharge == null ? null : Number(s.minCharge), nte_default: s.nteDefault === "" || s.nteDefault == null ? null : Number(s.nteDefault),
      needs_estimate: !!s.needsEstimate, estimate_when: s.estimateWhen || "" };
    if (USE_SUPABASE && supabase) {
      try { const { error } = await supabase.from("service_catalog").upsert(row); if (error) throw error; return true; }
      catch(e) { console.warn("saveService failed:", e); return false; }
    }
    const list = localCache.load("service_catalog") || [];
    const idx = list.findIndex(r => r.id === s.id);
    if (idx >= 0) list[idx] = s; else list.push(s);
    localCache.save("service_catalog", list); return true;
  },
  async deleteService(id) {
    if (USE_SUPABASE && supabase) { try { await supabase.from("service_catalog").delete().eq("id", id); } catch(e) {} }
    localCache.save("service_catalog", (localCache.load("service_catalog") || []).filter(r => r.id !== id));
  },
  // Auto-create a post-submission workflow project (called after TSA Survey / Assessment submit)
  // Update an existing project (e.g. one auto-created from an Acuity booking)
  // instead of creating a duplicate when a TSA Survey / Assessment is
  // submitted for a job that was already scheduled.
  async linkFormToProject(projectId, { formType, userName, partnerName, partnerTasks, submitDue, visitId }) {
    try {
      // Patch only tasks/notes/partner/status/visits on the latest row, so a
      // coordinator with the job open doesn't lose this (or wipe it on Save).
      const res = await this.patchProject(projectId, (existing) => {
      const partnerSteps = (partnerTasks || []).map(name => ({ name, due: "" }));
      const WORKFLOW = [
        { name: "Upload photos & report to the Proton Drive job folder", due: "" },
        { name: "Upload any drawings needed to the job folder", due: "" },
        ...partnerSteps,
        { name: partnerName ? `Submit to ${partnerName}` : "Create invoice", due: (partnerName && submitDue) || "" },
        ...(partnerName ? [{ name: "Create invoice", due: "" }] : []),
        { name: "Follow up by email", due: "" },
      ];

      // Only add tasks that aren't already on the project. Matching is loose
      // (ignores case/punctuation, and "Invoice Omnidian — $450" counts as
      // "Create invoice") so a job set up by hand or from a service quote
      // doesn't get a second copy of its checklist when the crew files the
      // assessment.
      const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9 ]+/g, " ").replace(/\s+/g, " ").trim();
      const existingNames = (existing.tasks || []).map(t => norm(t.name));
      const covered = (name) => {
        const n = norm(name);
        if (!n) return true;
        if (existingNames.some(e => e === n || e.includes(n) || n.includes(e))) return true;
        if (n === "create invoice" && existingNames.some(e => e.includes("invoice"))) return true;
        if (n.startsWith("submit to ") && existingNames.some(e => e.startsWith("submit to "))) return true;
        if (n.startsWith("upload photos") && existingNames.some(e => e.startsWith("upload photos"))) return true;
        if (n.startsWith("upload any drawings") && existingNames.some(e => e.startsWith("upload any drawings"))) return true;
        return false;
      };
      const newTasks = WORKFLOW
        .filter(w => !covered(w.name))
        .map((w, i) => ({ id: Date.now() + i, name: w.name, assigned: userName || "", due: w.due, priority: "medium", done: false }));

      let mergedTasks = [...(existing.tasks || []), ...newTasks];
      if (partnerName && submitDue) {
        const want = `submit to ${partnerName}`.toLowerCase();
        mergedTasks = mergedTasks.map(tk => (tk.name || "").trim().toLowerCase() === want && !tk.done
          ? { ...tk, due: submitDue, priority: "high" } : tk);
      }

      // A Service Assessment / Install Closeout closes out the visit it was
      // filed for. That "done" state drives the Schedule chips, the project
      // status and the coordinator's queue.
      const closesVisit = /Service Assessment|Closeout|Survey/i.test(formType || "") && !/JHA/i.test(formType || "");
      let visits = existing.visits || [];
      let doneVisitId = visitId;
      if (closesVisit && !doneVisitId) {
        // No visit id on the form (older link or deep link without it): close
        // the live visit nearest to now, if it is today or in the past.
        const today = jobDayKey(new Date().toISOString());
        const cand = visits.filter(v => !v.cancelled && v.scheduled_at && v.status !== "done" && jobDayKey(v.scheduled_at) <= today)
          .sort((a, b) => new Date(b.scheduled_at) - new Date(a.scheduled_at))[0];
        doneVisitId = cand?.id || "";
      }
      if (closesVisit && doneVisitId) {
        visits = visits.map(v => v.id === doneVisitId ? { ...v, status: "done", doneAt: new Date().toISOString(), doneBy: userName || "", doneForm: formType } : v);
      }
      const patched = {
        partner: partnerName || existing.partner || "",
        tasks: mergedTasks,
        visits,
        notes: [
          ...(existing.notes || []),
          { id: Date.now() + 999, text: `Linked from ${formType} submission` + (partnerName ? ` — Partner: ${partnerName}` : "") + (doneVisitId ? ` — visit ${(visits.find(v => v.id === doneVisitId) || {}).label || ""} done` : ""), by: userName || "", at: new Date().toISOString() },
        ],
      };
      patched.status = autoStatus({ ...existing, ...patched, status: existing.status === "not_started" ? "in_progress" : existing.status });
      return patched;
      });
      return !!res;
    } catch(e) { console.warn("linkFormToProject failed:", e); return false; }
  },
  async createWorkflowProject({ clientName, address, formType, userName, partnerName, partnerTasks, submitDue }) {
    try {
      let name = (clientName || "Unnamed Client").trim();
      const existing = await this.getProjects(undefined, { fresh: true });
      // If an open job already exists for this address or client (set up by
      // the office or from a service quote), attach the form to it instead of
      // spawning a second project with the same checklist.
      const normA = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
      const addrKey = normA(address);
      const nameKey = normA(clientName);
      const match = findOpenProject(existing, { address, client: clientName, partner: partnerName });
      if (match) {
        return this.linkFormToProject(match.id, { formType, userName, partnerName, partnerTasks, submitDue });
      }
      if (existing.some(p => (p.name || "").toLowerCase() === name.toLowerCase())) {
        name = `${name} — ${localDate()}`;
      }
      // Partner-specific steps (e.g. "Upload to Omnidian Workforce app") get inserted
      // right before invoicing, since they're part of "getting the work delivered"
      // rather than "getting paid."
      const partnerSteps = (partnerTasks || []).map(name => ({ name, due: "" }));
      const WORKFLOW = [
        { name: "Upload photos & report to the Proton Drive job folder", due: "" },
        { name: "Upload any drawings needed to the job folder", due: "" },
        ...partnerSteps,
        { name: partnerName ? `Submit to ${partnerName}` : "Create invoice", due: (partnerName && submitDue) || "" },
        ...(partnerName ? [{ name: "Create invoice", due: "" }] : []),
        { name: "Follow up by email", due: "" },
      ];
      const project = {
        id: `proj-${Date.now()}`,
        name, client: clientName || "", address: address || "",
        systemSize: "", status: "in_progress", completion: 0,
        partner: partnerName || "",
        tasks: WORKFLOW.map((w, i) => ({ id: Date.now() + i, name: w.name, assigned: userName || "", due: w.due, priority: w.due && partnerName && w.name.startsWith("Submit to") ? "high" : "medium", done: false })),
        materials: [], members: [],
        notes: [{ id: Date.now() + 100, text: `Auto-created from ${formType} submission` + (partnerName ? ` — Partner: ${partnerName}` : ""), by: userName || "", at: new Date().toISOString() }],
        created_by: userName || "",
        created_at: new Date().toISOString(),
      };
      await this.saveProject(project);
    } catch(e) { console.warn("Workflow project creation failed:", e); }
  },
  // Email someone the tasks just assigned to them on a job (one email per
  // person per save, not one per task). Same Edge Function as the member
  // notice, so nothing new to deploy.
  async notifyTaskAssigned(email, project, tasks, assignedBy) {
    if (!email || !email.includes("@") || !tasks?.length) return;
    if (USE_SUPABASE && supabase) {
      try {
        const lines = tasks.map(tk => `• ${tk.name || "(untitled task)"}${tk.due ? ` — due ${tk.due}` : ""}${tk.priority && tk.priority !== "medium" ? ` [${tk.priority}]` : ""}`);
        const job = [project.name, project.address].filter(Boolean).join(" · ");
        await supabase.functions.invoke("send-notification", {
          body: {
            type: "Task Assigned", to: email, submittedBy: assignedBy,
            title: `${tasks.length === 1 ? "New task" : `${tasks.length} new tasks`} on ${project.name || "a job"}: ${tasks.map(tk => tk.name).filter(Boolean).join("; ").slice(0, 120)}`,
            message: `${assignedBy || "The office"} assigned you on ${job}:\n\n${lines.join("\n")}\n\nOpen the job in the Hub: ${(window.location.origin || "https://hub.solarability.com")}/#projects/id/${encodeURIComponent(project.id)}`,
          }
        });
      } catch(e) { console.warn("Task notification failed (Edge Function not deployed?):", e); }
    }
  },
  // Email a member when added to a project (requires send-notification Edge Function)
  async notifyProjectMember(email, projectName, addedBy) {
    if (!email || !email.includes("@")) return; // only works with real emails
    if (USE_SUPABASE && supabase) {
      try {
        await supabase.functions.invoke("send-notification", {
          body: { type: "Project Invitation", title: `You've been added to project: ${projectName}`, submittedBy: addedBy, to: email }
        });
      } catch(e) { console.warn("Member notification failed (Edge Function not deployed?):", e); }
    }
  },
  async getUsers() {
    if (USE_SUPABASE && supabase) {
      try {
        // Get all users from auth - this requires admin access or a custom view
        // Fallback: use a simple users lookup from project members
        const { data, error } = await supabase.from("projects").select("created_by, members");
        if (error) throw error;
        const names = new Set();
        (data || []).forEach(p => {
          if (p.created_by) names.add(p.created_by);
          const members = typeof p.members === "string" ? JSON.parse(p.members) : (p.members || []);
          members.forEach(m => { if (m.name) names.add(m.name); });
        });
        return Array.from(names);
      } catch(e) { return []; }
    }
    return [];
  },

  // ─── Email Notification (disabled until Edge Function is deployed) ─────
  async sendNotification(type, title, submittedBy) {
    if (USE_SUPABASE && supabase) {
      try {
        // JHAs go to the safety officer as well as the admin. The Edge
        // Function resolves role emails server-side — crew can't read other
        // people's profiles under RLS, so it can't be done from the client.
        // Everything else also reaches the coordinator, who submits to partners.
        const notifyRoles = String(type).toUpperCase().includes("JHA")
          ? ["admin", "safety"]
          : ["coordinator"];
        await supabase.functions.invoke("send-notification", {
          body: { type, title, submittedBy, to: ADMIN_EMAIL, notifyRoles }
        });
      } catch(e) { console.warn("Email notification failed:", e); }
    }
  },
  // Send a calendar invite (.ics) to one or more recipients via the
  // send-notification Edge Function's attachment support.
  async sendCalendarInvite({ to, subject, icsBase64, audience, jobLine, ownerNote }) {
    if (!USE_SUPABASE || !supabase) throw new Error("Supabase not configured");
    const { error } = await supabase.functions.invoke("send-notification", {
      body: {
        mode: "calendar_invite", to, subject,
        audience: audience || "crew", // "crew" | "homeowner" — changes the email body
        jobLine: jobLine || "",
        ownerNote: ownerNote || "",
        attachments: [{ filename: "invite.ics", content: icsBase64 }],
      }
    });
    if (error) throw error;
  },
};

// ─── Brand Palette ───────────────────────────────────────────────────────────
// Palette matches solarability.com: deep forest backgrounds, lime accent,
// cream text. `gold` keeps its name so nothing else has to change — it is
// the brand accent (lime), not a color.
// Money and staffing views — rate card, service quotes, crew capabilities,
// contacts, costing, invoices — are limited to these two logins regardless
// of role. The crew login (jonathanz@) sees none of it.
// Money / staffing views. customercare@ stays during the switch-over to
// customerservice@ — remove it once the old login is gone.
const MONEY_USERS = ["admin@solarability.com", "customerservice@solarability.com", "customercare@solarability.com"];
const OWNER_EMAIL = "admin@solarability.com";
const isOwner = (user) => String(user?.email || "").trim().toLowerCase() === OWNER_EMAIL;
// A procedure can be restricted to named logins (training material, internal
// playbooks). Empty list = everyone. admin@ always sees everything.
const canSeePlaybook = (pb, user) => {
  const list = (pb?.allowedEmails || []).map(e => String(e).trim().toLowerCase()).filter(Boolean);
  if (list.length === 0 || isOwner(user)) return true;
  return list.includes(String(user?.email || "").trim().toLowerCase());
};
const canSeeMoney = (user) => MONEY_USERS.includes(String(user?.email || "").trim().toLowerCase());

const C = {
  gold: "#B4F53C", goldDark: "#8FD42A", goldLight: "#D4FF6E",
  green: "#2A5C38", greenLight: "#5FB36F",
  bg: "#071510", card: "#0D2B1A", surface: "#123622", border: "#1E4A30",
  muted: "#7A9E87", light: "#F5F0E8", white: "#FFFFFF",
  danger: "#F26D6D", dangerBg: "#2C1414", warning: "#F5C542", warnBg: "#2B2410",
  success: "#5FD38A", successBg: "#0F3320", info: "#6CB8F5", infoBg: "#10283A",
};

// ─── Icons ───────────────────────────────────────────────────────────────────
const I = {
  sun: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="5" stroke="currentColor" strokeWidth="2"/><path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  book: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M4 19.5A2.5 2.5 0 016.5 17H20V3H6.5A2.5 2.5 0 004 5.5v14z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M4 19.5A2.5 2.5 0 016.5 22H20v-5" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>,
  calendar: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  shield: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>,
  hazard: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M12 2L1 21h22L12 2z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M12 9v5M12 17h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  clip: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="3" y="3" width="18" height="18" rx="3" stroke="currentColor" strokeWidth="2"/><path d="M8 10h8M8 14h5" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  cam: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="13" r="4" stroke="currentColor" strokeWidth="2"/></svg>,
  dash: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="9" rx="2" stroke="currentColor" strokeWidth="2"/><rect x="14" y="3" width="7" height="5" rx="2" stroke="currentColor" strokeWidth="2"/><rect x="14" y="12" width="7" height="9" rx="2" stroke="currentColor" strokeWidth="2"/><rect x="3" y="16" width="7" height="5" rx="2" stroke="currentColor" strokeWidth="2"/></svg>,
  admin: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/><circle cx="9" cy="7" r="4" stroke="currentColor" strokeWidth="2"/><path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  pdf: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2"/><path d="M14 2v6h6M9 15v-2M12 15v-4M15 15v-1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  plus: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"/></svg>,
  trash: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  back: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M19 12H5M12 19l-7-7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  send: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>,
  logout: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4M16 17l5-5-5-5M21 12H9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  user: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="2"/><path d="M20 21a8 8 0 10-16 0" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  lock: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="3" y="11" width="18" height="11" rx="2" stroke="currentColor" strokeWidth="2"/><path d="M7 11V7a5 5 0 0110 0v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  mail: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><rect x="2" y="4" width="20" height="16" rx="3" stroke="currentColor" strokeWidth="2"/><path d="M22 7l-10 7L2 7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  menu: <svg width="24" height="24" fill="none" viewBox="0 0 24 24"><path d="M3 12h18M3 6h18M3 18h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  x: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  check: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  checkCircle: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  flag: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  eye: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8S1 12 1 12z" stroke="currentColor" strokeWidth="2"/><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2"/></svg>,
  download: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  chart: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M3 3v18h18M7 16V10M12 16V6M17 16v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  truck: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M1 3h13v13H1zM14 8h4l3 3v5h-7V8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="6" cy="18" r="2" stroke="currentColor" strokeWidth="2"/><circle cx="18" cy="18" r="2" stroke="currentColor" strokeWidth="2"/></svg>,
  shield: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M12 2l8 4v6c0 5-3.4 8.9-8 10-4.6-1.1-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><path d="M9 12l2 2 4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  handshake: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M11 12l2 2 4-4M2 12l4-4 3 1 3-2 3 2 3-1 4 4-4 4-3-1-3 2-3-2-3 1z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round"/></svg>,
  mappin: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M12 22s7-6.2 7-12a7 7 0 10-14 0c0 5.8 7 12 7 12z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/><circle cx="12" cy="10" r="2.5" stroke="currentColor" strokeWidth="2"/></svg>,
  wrench: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M14.7 6.3a1 1 0 000 1.4l1.6 1.6a1 1 0 001.4 0l3.77-3.77a6 6 0 01-7.94 7.94l-6.91 6.91a2.12 2.12 0 01-3-3l6.91-6.91a6 6 0 017.94-7.94l-3.76 3.76z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>,
  bolt: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>,
  folder: <svg width="20" height="20" fill="none" viewBox="0 0 24 24"><path d="M22 19a2 2 0 01-2 2H4a2 2 0 01-2-2V5a2 2 0 012-2h5l2 3h9a2 2 0 012 2z" stroke="currentColor" strokeWidth="2"/></svg>,
  globe: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M2 12h20M12 2a15.3 15.3 0 014 10 15.3 15.3 0 01-4 10 15.3 15.3 0 01-4-10A15.3 15.3 0 0112 2z" stroke="currentColor" strokeWidth="2"/></svg>,
  list: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  box: <svg width="18" height="18" fill="none" viewBox="0 0 24 24"><path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z" stroke="currentColor" strokeWidth="2"/><path d="M3.27 6.96L12 12.01l8.73-5.05M12 22.08V12" stroke="currentColor" strokeWidth="2"/></svg>,
  note: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" stroke="currentColor" strokeWidth="2"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  filter: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><path d="M22 3H2l8 9.46V19l4 2v-8.54L22 3z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round"/></svg>,
  clock: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/><path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
  search: <svg width="16" height="16" fill="none" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2"/><path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>,
};

// ─── Risk ────────────────────────────────────────────────────────────────────
const RISK_LEVELS = ["Low", "Medium", "High", "Critical"];
const RISK_COLORS = { Low: C.success, Medium: C.warning, High: "#F97316", Critical: C.danger };
const STATUS_COLORS = { submitted: C.info, approved: C.success, flagged: C.warning, rejected: C.danger };
const STATUS_LABELS = { submitted: "Pending Review", approved: "Approved", flagged: "Flagged", rejected: "Rejected" };

const PHOTO_CHECKLIST = [
  { label: "Utility Meter Location", desc: "Wide shot showing where the meter is mounted on the building", descEs: "Foto amplia mostrando dónde está montado el medidor en el edificio" },
  { label: "Utility Meter Close-up", desc: "Clear photo of meter face showing make, model, and reading", descEs: "Foto clara de la cara del medidor mostrando marca, modelo y lectura" },
  { label: "Utility Meter 10 ft Pull-back", desc: "Step back 10 feet to capture meter and surrounding area", descEs: "Retroceda 10 pies para capturar el medidor y el área circundante" },
  { label: "Main Service Panel Location", desc: "Wide shot showing panel location relative to the home", descEs: "Foto amplia mostrando la ubicación del panel en relación a la casa" },
  { label: "Main Service Panel 10 ft Pull-back", desc: "Step back 10 feet to capture full panel area and access", descEs: "Retroceda 10 pies para capturar el área completa del panel y acceso" },
  { label: "Main Service Panel Label", desc: "Close-up of the panel label showing manufacturer and ratings", descEs: "Primer plano de la etiqueta del panel mostrando fabricante y clasificaciones" },
  { label: "Inside Panel Label", desc: "Photo of the label inside the panel door showing breaker layout and specs", descEs: "Foto de la etiqueta dentro de la puerta del panel mostrando disposición de interruptores y especificaciones" },
  { label: "Main Breaker (Rating)", desc: "Close-up of the main breaker showing amp rating clearly", descEs: "Primer plano del interruptor principal mostrando el amperaje claramente" },
  { label: "Modem / Router Close-up", desc: "Photo of internet modem/router showing make, model, and ports", descEs: "Foto del módem/router de internet mostrando marca, modelo y puertos" },
  { label: "Proposed Inverter Location", desc: "Photo of the wall/area where the inverter will be installed", descEs: "Foto de la pared/área donde se instalará el inversor" },
  { label: "Proposed Conduit Runs", desc: "Photos showing the planned path for conduit from panels to inverter", descEs: "Fotos mostrando la ruta planificada del conducto desde los paneles al inversor" },
  { label: "360° from Inside the Attic", desc: "Panoramic or multiple photos capturing all angles inside the attic space", descEs: "Fotos panorámicas o múltiples capturando todos los ángulos dentro del ático" },
  { label: "Roof with Obstructions", desc: "Overhead or angled shot showing vents, pipes, skylights on the roof", descEs: "Foto aérea o en ángulo mostrando ventilaciones, tuberías y tragaluces en el techo" },
  { label: "Rafter / Truss Size", desc: "Photo from attic showing rafter/truss dimensions (use measuring tape)", descEs: "Foto del ático mostrando dimensiones de vigas (use cinta métrica)" },
  { label: "Rafter / Truss Spacing", desc: "Photo from attic showing distance between rafters/trusses", descEs: "Foto del ático mostrando la distancia entre vigas" },
  { label: "Existing Service Grounding", desc: "Photo of grounding rod, clamp, or water pipe connection", descEs: "Foto de la varilla de tierra, abrazadera o conexión de tubería de agua" },
  { label: "Service Grounding & Neutral Bonding", desc: "Photo showing the grounding electrode conductor and neutral-to-ground bond point", descEs: "Foto mostrando el conductor del electrodo de tierra y punto de unión neutro a tierra" },
  { label: "Generator / Switch Gear", desc: "If present, photo of existing generator or transfer switch", descEs: "Si existe, foto del generador existente o interruptor de transferencia" },
  { label: "Proposed Battery Location", desc: "Photo of the wall/area where battery storage will be installed", descEs: "Foto de la pared/área donde se instalará la batería de almacenamiento" },
  { label: "Proposed Subpanel Location", desc: "Photo of where the new subpanel will be mounted, if applicable", descEs: "Foto de dónde se montará el nuevo subpanel, si aplica" },
  { label: "360° from Around the House", desc: "All 4 sides of the house from the street/yard — panoramic or multiple photos covering every elevation", descEs: "Los 4 lados de la casa desde la calle/jardín — panorámica o varias fotos cubriendo cada elevación" },
];

const GROUNDING_OPTIONS = ["Ground Rod", "Cold Water Pipe", "Water Meter", "Building Steel", "Other"];

const INTERCONNECTION_OPTIONS = [
  "Existing main electrical panel", "Existing main panel w/ main breaker de-rate",
  "Existing electrical subpanel", "Existing meter socket", "Existing meter cabinet",
  "New tap box", "Automatic transfer switch", "Upgraded main electrical panel",
  "New electrical subpanel", "Renewable meter adapter at existing meter",
];

const ROOFING_TYPES = ["Asphalt Shingle", "Standing Seam Metal", "Wood Shake", "Clay S-Tile", "Rubber Membrane", "Slate Tile", "Corrugated Metal"];
const STRUCTURE_TYPES = ["Single Span Wood Rafter", "Truss (Wood)", "Steel Beam"];
const STRUCTURE_DETAILS = ["Existing Load Bearing Wall/Support", "Existing Purlins", "Existing Knee Wall", "Existing Collar Ties"];
const RAFTER_SIZES = ['2x4"', '2x6"', '2x8"', '2x10"', '2x12"'];
const RAFTER_SPACINGS = ['12" OC', '14" OC', '16" OC', '19.2" OC', '24" OC', '48" OC'];

// ─── PDF Generator ───────────────────────────────────────────────────────────
function esc(v) { return String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;"); }
// Shared print path. Installed PWAs (standalone mode) block window.open, so
// fall back to a hidden in-page iframe, which prints without a popup.
function printHTML(printContent) {
  const isStandalone = window.matchMedia?.("(display-mode: standalone)")?.matches
    || window.navigator.standalone === true;

  const printViaIframe = () => {
    const existing = document.getElementById("sa-print-frame");
    if (existing) existing.remove();
    const frame = document.createElement("iframe");
    frame.id = "sa-print-frame";
    frame.setAttribute("aria-hidden", "true");
    Object.assign(frame.style, { position: "fixed", right: "0", bottom: "0", width: "0", height: "0", border: "0", visibility: "hidden" });
    document.body.appendChild(frame);
    const fdoc = frame.contentWindow.document;
    fdoc.open();
    fdoc.write(printContent);
    fdoc.close();
    const go = () => {
      try { frame.contentWindow.focus(); frame.contentWindow.print(); }
      catch (e) { console.warn("Print failed:", e); alert("Couldn't open the print dialog. Try opening the Hub in your browser instead of the installed app."); }
      setTimeout(() => frame.remove(), 60000);
    };
    const imgs = Array.from(fdoc.images || []);
    if (imgs.length === 0) { setTimeout(go, 300); return; }
    let done = 0;
    const tick = () => { if (++done >= imgs.length) go(); };
    imgs.forEach(img => { if (img.complete) tick(); else { img.addEventListener("load", tick); img.addEventListener("error", tick); } });
    setTimeout(go, 6000);
  };

  if (isStandalone) { printViaIframe(); return; }
  const printWindow = window.open("", "_blank");
  if (!printWindow) { printViaIframe(); return; }
  printWindow.document.write(printContent);
  printWindow.document.close();
  setTimeout(() => printWindow.print(), 500);
}

// DB rows → the shape the dashboard, PDFs and project pages all use.
function mapJHARow(j) {
  return {
        ...j, type: "JHA", title: j.title, location: j.location, submittedAt: j.created_at, submittedBy: j.supervisor,
        photos: (j.photo_urls || []).map(u => ({ url: u, name: u.split("/").pop() })),
        ...(() => {
          const raw = typeof j.hazards === "string" ? JSON.parse(j.hazards) : (j.hazards || []);
          return Array.isArray(raw)
            ? { hazards: raw, emergency: null }
            : { hazards: raw.items || [], emergency: raw.emergency || null,
                siteHazards: raw.siteHazards || [], crewAck: raw.crewAck || [], waterLog: raw.waterLog || [],
                waterLogClosedAt: raw.waterLogClosedAt || "", heatWatch: !!raw.heatWatch, visitId: raw.visitId || "" };
        })(),
  };
}
function mapSurveyRow(s) {
        const checklist = typeof s.checklist === "string" ? JSON.parse(s.checklist) : (s.checklist || {});
        const checklistPhotos = checklist.checklistPhotos || [];
        // Parse photo_urls - might be a JSON string, array, or null
        let photoUrls = s.photo_urls || [];
        if (typeof photoUrls === "string") { try { photoUrls = JSON.parse(photoUrls); } catch(e) { photoUrls = []; } }
        if (!Array.isArray(photoUrls)) photoUrls = [];
        // Also extract photos from photoChecklist items that have photoUrl
        const itemPhotos = (checklist.photoChecklist || [])
          .filter(p => p.photoUrl && !p.photoUrl.startsWith("data:"))
          .map(p => ({ url: p.photoUrl, name: p.photoName || p.label, label: p.label }));
        // Build photos array - combine all sources, deduplicate by URL
        const seenUrls = new Set();
        const photos = [];
        // First: labeled checklist photos from checklistPhotos array
        for (const cp of checklistPhotos) {
          if (cp.url && !seenUrls.has(cp.url)) { seenUrls.add(cp.url); photos.push({ url: cp.url, name: cp.name || cp.label, label: cp.label }); }
        }
        // Second: photos from individual photoChecklist items
        for (const ip of itemPhotos) {
          if (ip.url && !seenUrls.has(ip.url)) { seenUrls.add(ip.url); photos.push(ip); }
        }
        // Third: any remaining photo_urls not already included
        for (const u of photoUrls) {
          if (u && !seenUrls.has(u)) { seenUrls.add(u); photos.push({ url: u, name: u.split("/").pop() }); }
        }
        const isAssessment = checklist.formType === "Service Assessment";
        return {
          ...s, type: isAssessment ? "Service Assessment" : "Site Survey",
          siteName: s.site_name, submittedAt: s.created_at, submittedBy: s.inspector,
          photos, photo_urls: photoUrls, checklist: checklist.photoChecklist || [], checklistData: checklist,
          homeowner: checklist.homeowner || { name: s.site_name, address: s.address },
          meter: checklist.meter, panel: checklist.panel, subpanel: checklist.subpanel,
          inverterLocation: checklist.inverterLocation, comms: checklist.comms,
          interconnection: checklist.interconnection, roofing: checklist.roofing, roof: checklist.roof,
          adders: checklist.adders, grounding: checklist.grounding, arrays: checklist.arrays || [],
          electricalNotes: checklist.electricalNotes, roofNotes: checklist.roofNotes,
          structureDetails: checklist.structureDetails, photoChecklist: checklist.photoChecklist || [],
          // Service Assessment fields
          ...(isAssessment ? {
            serviceType: checklist.serviceType, client: checklist.client, referral: checklist.referral,
            system: checklist.system, issue: checklist.issue, findings: checklist.findings,
            rootCause: checklist.rootCause, recommendations: checklist.recommendations, fee: checklist.fee,
            partnerName: checklist.partnerName || "", partnerFields: checklist.partnerFields || [],
            partnerPhotos: checklist.partnerPhotos || [], missingPhotos: checklist.missingPhotos || [],
            scopeTitle: checklist.scopeTitle || "",
            timeOnSite: checklist.timeOnSite || null, timeBudget: checklist.timeBudget || null,
            submitDue: checklist.submitDue || "",
          } : {}),
          visitId: checklist.visitId || s.visitId || "",
        };
}

function generatePDF(submission, opts = {}) {
  const isJHA = submission.type === "JHA";
  const isAssessment = submission.type === "Service Assessment";
  const title = isJHA ? "Job Hazard Analysis Report" : isAssessment ? `Service Assessment — ${submission.serviceType || ""}` : "TSA Site Survey Report";

  // Build PDF content as printable HTML
  const printContent = `
<!DOCTYPE html>
<html><head><title>${title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;600;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'DM Sans', sans-serif; color: #1a1a2e; padding: 40px; max-width: 800px; margin: 0 auto; font-size: 13px; line-height: 1.6; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #2A5C38; padding-bottom: 20px; margin-bottom: 24px; }
  .logo { display: flex; align-items: center; gap: 12px; }
  .logo-icon { width: 44px; height: 44px; background: linear-gradient(135deg, #B4F53C, #2A5C38); border-radius: 10px; display: flex; align-items: center; justify-content: center; color: #fff; font-size: 22px; font-weight: 900; }
  .logo-text h1 { font-size: 20px; font-weight: 800; color: #1a1a2e; }
  .logo-text p { font-size: 11px; color: #2A5C38; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; }
  .meta { text-align: right; font-size: 12px; color: #666; }
  .meta strong { color: #1a1a2e; }
  h2 { font-size: 16px; font-weight: 800; margin: 24px 0 12px; color: #071510; border-left: 4px solid #B4F53C; padding-left: 12px; }
  .field-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 24px; margin-bottom: 16px; }
  .field label { font-size: 11px; font-weight: 700; color: #888; text-transform: uppercase; letter-spacing: 0.5px; }
  .field p { font-size: 13px; color: #1a1a2e; font-weight: 500; margin-top: 2px; }
  table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; }
  th { background: #2A5C38; color: #F5F0E8; padding: 10px 12px; text-align: left; font-weight: 700; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 10px 12px; border-bottom: 1px solid #eee; vertical-align: top; }
  tr:nth-child(even) { background: #fafafa; }
  .risk { display: inline-block; padding: 2px 10px; border-radius: 12px; font-size: 11px; font-weight: 700; }
  .risk-low { background: #D1FAE5; color: #065F46; }
  .risk-medium { background: #FEF3C7; color: #92400E; }
  .risk-high { background: #FED7AA; color: #9A3412; }
  .risk-critical { background: #FEE2E2; color: #991B1B; }
  .check-item { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 12px; }
  .check-box { width: 16px; height: 16px; border: 2px solid #ddd; border-radius: 4px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
  .check-box.checked { background: #2A5C38; border-color: #2A5C38; color: #F5F0E8; }
  .status-badge { display: inline-block; padding: 4px 14px; border-radius: 16px; font-size: 12px; font-weight: 700; }
  .footer { margin-top: 32px; padding-top: 16px; border-top: 2px solid #eee; font-size: 11px; color: #888; display: flex; justify-content: space-between; }
  .notes { background: #f8f9fa; border-radius: 8px; padding: 14px; margin-top: 8px; font-size: 12px; color: #333; }
  @media print { body { padding: 20px; } }
</style></head><body>
  <div class="header">
    <div class="logo">
      <div class="logo-icon">☀</div>
      <div class="logo-text">
        <h1>SolarAbility LLC</h1>
      </div>
    </div>
    <div class="meta">
      <div><strong>${title}</strong></div>
      <div>ID: ${submission.id || "SA-" + Date.now().toString(36).toUpperCase()}</div>
      <div>Date: ${new Date(submission.submittedAt || submission.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</div>
      <div>Status: <span class="status-badge" style="background:${STATUS_COLORS[submission.status || "submitted"]}22;color:${STATUS_COLORS[submission.status || "submitted"]}">${STATUS_LABELS[submission.status || "submitted"]}</span></div>
    </div>
  </div>

  ${isAssessment && submission.partnerName ? `
    <div style="background:#B4F53C;color:#071510;padding:10px 16px;border-radius:8px;margin-bottom:16px;font-weight:800;font-size:13px;letter-spacing:0.04em;text-transform:uppercase;">
      Prepared for ${esc(submission.partnerName)}${submission.scopeTitle ? ` · ${esc(submission.scopeTitle.replace(/^[^—]*—\s*/, ""))}` : ""}
    </div>
  ` : ""}

  <h2>General Information</h2>
  <div class="field-grid">
    ${isJHA ? `
      <div class="field"><label>Job / Task Title</label><p>${esc(submission.title) || "—"}</p></div>
      <div class="field"><label>Location</label><p>${esc(submission.location) || "—"}</p></div>
      <div class="field"><label>Date</label><p>${submission.date || "—"}</p></div>
      <div class="field"><label>Supervisor</label><p>${esc(submission.supervisor) || "—"}</p></div>
      <div class="field"><label>Crew Members</label><p>${esc(submission.crew) || "—"}</p></div>
    ` : isAssessment ? `
      <div class="field"><label>Service Type</label><p style="color:#2A5C38;font-weight:700">${submission.serviceType || "—"}</p></div>
      <div class="field"><label>Inspector</label><p>${submission.submittedBy || "—"}</p></div>
      <div class="field"><label>Client / Owner</label><p>${esc(submission.client?.name || submission.siteName) || "—"}</p></div>
      <div class="field"><label>Address</label><p>${esc(submission.client?.address || submission.address) || "—"}</p></div>
      <div class="field"><label>Phone</label><p>${submission.client?.phone || "—"}</p></div>
      <div class="field"><label>Email</label><p>${submission.client?.email || "—"}</p></div>
      ${submission.referral?.name ? `<div class="field"><label>Referred By</label><p>${submission.referral.name}${submission.referral.source ? " (" + submission.referral.source + ")" : ""}</p></div>` : ""}
      <div class="field"><label>Date</label><p>${submission.date || new Date(submission.submittedAt).toLocaleDateString() || "—"}</p></div>
    ` : `
      <div class="field"><label>Homeowner Name</label><p>${esc(submission.homeowner?.name || submission.siteName) || "—"}</p></div>
      <div class="field"><label>Address</label><p>${esc(submission.homeowner?.address || submission.address) || "—"}</p></div>
      <div class="field"><label>Inspector</label><p>${submission.inspector || submission.submittedBy || "—"}</p></div>
      <div class="field"><label>Date</label><p>${submission.date || "—"}</p></div>
      <div class="field"><label>Drawings Available</label><p>${submission.homeowner?.drawingsAvailable || "—"}</p></div>
    `}
  </div>
  ${submission.description ? `<div class="notes"><strong>Description:</strong> ${esc(submission.description)}</div>` : ""}

  ${isAssessment && submission.system ? `
    <h2>System Information</h2>
    <div class="field-grid">
      ${submission.system.size ? `<div class="field"><label>System Size</label><p>${submission.system.size} kW</p></div>` : ""}
      ${submission.system.numPanels ? `<div class="field"><label>Number of Panels</label><p>${submission.system.numPanels}</p></div>` : ""}
      ${submission.system.installer ? `<div class="field"><label>Original Installer</label><p>${submission.system.installer}</p></div>` : ""}
      ${submission.system.installDate ? `<div class="field"><label>Install Date / Age</label><p>${submission.system.installDate}</p></div>` : ""}
      ${submission.system.panelMake ? `<div class="field"><label>Panel Make / Model</label><p>${submission.system.panelMake}</p></div>` : ""}
      ${submission.system.inverterMake ? `<div class="field"><label>Inverter Make / Model</label><p>${submission.system.inverterMake}</p></div>` : ""}
    </div>
  ` : ""}

  ${isAssessment && submission.issue ? `
    <h2>Reported Issue</h2>
    <div class="notes">${esc(submission.issue)}</div>
  ` : ""}

  ${isAssessment && submission.rootCause ? `
    <h2>Root Cause / Diagnosis</h2>
    <div class="notes">${esc(submission.rootCause)}</div>
  ` : ""}

  ${isAssessment && submission.recommendations ? `
    <h2>Recommendations</h2>
    <div class="notes">${esc(submission.recommendations)}</div>
  ` : ""}

  ${isAssessment && submission.findings ? `
    <h2>Additional Findings</h2>
    <div class="notes">${esc(submission.findings)}</div>
  ` : ""}

  ${isAssessment && !opts.hideMissing && (submission.missingPhotos || []).length > 0 ? `
    <div style="border:2px solid #c00;background:#fff6f6;border-radius:6px;padding:11px 13px;margin:12px 0;font-size:12px;line-height:1.6">
      <strong style="color:#c00">Required photos not captured:</strong>
      ${submission.missingPhotos.map(m => esc(m)).join(" · ")}
      <div style="color:#555;font-size:11px;margin-top:4px">See Findings for the reason.</div>
    </div>
  ` : ""}

  ${isAssessment && submission.partnerFields && submission.partnerFields.length > 0 ? `
    <h2>${esc(submission.partnerName)} — Required Fields</h2>
    <div class="field-grid">
      ${submission.partnerFields.map(pf => `<div class="field"><label>${esc(pf.label)}</label><p>${esc(pf.value) || "—"}</p></div>`).join("")}
    </div>
  ` : ""}

  ${isJHA && submission.emergency && (submission.emergency.hospitalName || submission.emergency.hospitalAddress) ? `
    <h2>Emergency — Nearest Hospital</h2>
    <div style="border:2px solid #c00;background:#fff6f6;border-radius:6px;padding:11px 13px;margin:8px 0;font-size:12px;line-height:1.6">
      ${submission.emergency.hospitalName ? `<div><strong>${esc(submission.emergency.hospitalName)}</strong></div>` : ""}
      ${submission.emergency.hospitalAddress ? `<div>${esc(submission.emergency.hospitalAddress)}</div>` : ""}
      ${submission.emergency.hospitalPhone ? `<div>Phone: ${esc(submission.emergency.hospitalPhone)}</div>` : ""}
      ${submission.emergency.clinicName ? `<div style="margin-top:5px"><strong>Clinic (non-emergency):</strong> ${esc(submission.emergency.clinicName)}${submission.emergency.clinicAddress ? " — " + esc(submission.emergency.clinicAddress) : ""}</div>` : ""}
      ${submission.emergency.coachName ? `<div><strong>Safety contact:</strong> ${esc(submission.emergency.coachName)}${submission.emergency.coachPhone ? " — " + esc(submission.emergency.coachPhone) : ""}</div>` : ""}
      <div style="margin-top:6px;color:#555;font-size:11px">Life-threatening emergency: call 911 first, then the office at (312) 312-5337.</div>
    </div>
  ` : ""}

  ${isJHA && (submission.siteHazards || []).length > 0 ? `
    <h2>Site Conditions</h2>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin:6px 0">
      ${submission.siteHazards.map(h => `<tr>
        <td style="border:1px solid #ddd;padding:5px;width:45%"><strong>${esc(h.label)}</strong></td>
        <td style="border:1px solid #ddd;padding:5px">${esc(h.note) || "—"}</td>
      </tr>`).join("")}
    </table>
  ` : ""}

  ${isJHA && (submission.waterLog || []).length > 0 ? `
    <h2>Water &amp; Break Log (over 90&deg;F)</h2>
    <div style="font-size:10.5px;color:${submission.waterLogClosedAt ? "#555" : "#b26b00"};margin-bottom:5px">
      ${submission.waterLogClosedAt
        ? `Day closed out at ${esc(fmtJobTime(submission.waterLogClosedAt))}.`
        : "Day was not closed out — this log may be incomplete."}
    </div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin:6px 0">
      ${submission.waterLog.map(w => `<tr>
        <td style="border:1px solid #ddd;padding:5px;width:90px"><strong>${esc(fmtJobTime(w.at))}</strong></td>
        <td style="border:1px solid #ddd;padding:5px">${esc(w.note) || "—"}</td>
      </tr>`).join("")}
    </table>
  ` : ""}

  ${isJHA && (submission.crewAck || []).length > 0 ? `
    <h2>Crew Sign-Off</h2>
    <div style="font-size:10.5px;color:#555;margin-bottom:5px">Each person below confirmed the hazards and the plan were explained to them before work started.</div>
    <table style="width:100%;border-collapse:collapse;font-size:11px;margin:6px 0">
      <tr style="background:#f4f4f4">
        <th style="border:1px solid #ddd;padding:5px;text-align:left">Name</th>
        <th style="border:1px solid #ddd;padding:5px;text-align:left;width:90px">Initials</th>
        <th style="border:1px solid #ddd;padding:5px;text-align:left;width:110px">Acknowledged</th>
      </tr>
      ${submission.crewAck.map(p => `<tr>
        <td style="border:1px solid #ddd;padding:5px">${esc(p.name) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px;text-transform:uppercase">${esc(p.initials) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px">${p.at ? esc(fmtJobDateTime(p.at)) : "not signed"}</td>
      </tr>`).join("")}
    </table>
  ` : ""}

  ${isJHA && submission.hazards ? `
    <h2>Hazard Identification</h2>
    <table>
      <thead><tr><th>#</th><th>Job Step</th><th>Potential Hazard</th><th>Risk</th><th>Control Measure</th></tr></thead>
      <tbody>
        ${submission.hazards.map((h, i) => `
          <tr>
            <td style="font-weight:700;color:#2A5C38">${i + 1}</td>
            <td>${h.step || "—"}</td>
            <td>${h.hazard || "—"}</td>
            <td><span class="risk risk-${(h.risk || "medium").toLowerCase()}">${h.risk || "Medium"}</span></td>
            <td>${h.control || "—"}</td>
          </tr>
        `).join("")}
      </tbody>
    </table>
  ` : ""}

  ${!isJHA && submission.photoChecklist ? `
    <h2>Photo Checklist</h2>
    <table>
      <thead><tr><th>Status</th><th>Required Photo</th></tr></thead>
      <tbody>${submission.photoChecklist.map(p => {
        const shot = (p.photoUrls || []).length > 0 || p.photoUrl;
        const filed = !shot && p.uploaded && p.uploadedTo;
        const mark = shot ? "✅" : filed ? "📁" : (p.na && p.naReason ? "➖" : "⬜");
        const note = filed
          ? `<div style="color:#1d6fa5;font-size:10px;margin-top:2px"><strong>Uploaded to:</strong> ${esc(p.uploadedTo)}</div>`
          : (!shot && p.naReason ? `<div style="color:#b26b00;font-size:10px;margin-top:2px"><strong>No photo:</strong> ${esc(p.naReason)}</div>` : "");
        return `<tr><td style="text-align:center">${mark}</td><td>${esc(p.label)}${note}</td></tr>`;
      }).join("")}</tbody>
    </table>
  ` : ""}

  ${!isJHA && submission.meter ? `
    <h2>Electrical Meter / Service Entrance</h2>
    <div class="field-grid">
      <div class="field"><label>Meter Make/Model</label><p>${submission.meter.make || "—"}</p></div>
      <div class="field"><label>Location</label><p>${submission.meter.locationDesc || "—"}</p></div>
      <div class="field"><label>Utility Entrance</label><p>${submission.meter.utilityEntrance || "—"}</p></div>
      <div class="field"><label>Transformer Volt</label><p>${submission.meter.transformerVolt || "—"}</p></div>
      <div class="field"><label>Grounding</label><p>${(submission.grounding || []).join(", ") || "—"}${submission.groundingOther ? `, ${submission.groundingOther}` : ""}</p></div>
    </div>
  ` : ""}

  ${!isJHA && submission.panel ? `
    <h2>Electrical Panel</h2>
    <div class="field-grid">
      <div class="field"><label>Panel Brand</label><p>${submission.panel.brand || "—"}</p></div>
      <div class="field"><label>Main Breaker (A)</label><p>${submission.panel.mainBreakerRating || "—"}</p></div>
      <div class="field"><label>Bus Bar (A)</label><p>${submission.panel.busBarRating || "—"}</p></div>
      <div class="field"><label>Voltage</label><p>${submission.panel.voltageMeasured || "—"} ${submission.panel.voltageType || ""}</p></div>
      <div class="field"><label>Spaces Available</label><p>${submission.panel.spacesAvailable || "—"}</p></div>
      <div class="field"><label>Location</label><p>${submission.panel.locationDesc || "—"}</p></div>
    </div>
  ` : ""}

  ${!isJHA && submission.subpanel?.exists === "Yes" ? `
    <h2>Subpanel</h2>
    <div class="field-grid">
      <div class="field"><label>Location</label><p>${submission.subpanel.location || "—"}</p></div>
      <div class="field"><label>Make/Model</label><p>${submission.subpanel.makeModel || "—"}</p></div>
      <div class="field"><label>Main Breaker</label><p>${submission.subpanel.mainBreakerRating || "—"}</p></div>
      <div class="field"><label>Bus Bar</label><p>${submission.subpanel.busBarRating || "—"}</p></div>
      <div class="field"><label>Voltage</label><p>${submission.subpanel.voltageMeasured || "—"}</p></div>
      <div class="field"><label>Spaces</label><p>${submission.subpanel.spacesAvailable || "—"}</p></div>
    </div>
  ` : ""}

  ${!isJHA && submission.inverterLocation ? `
    <h2>Inverter</h2>
    <div class="field-grid"><div class="field"><label>Location</label><p>${submission.inverterLocation}</p></div></div>
  ` : ""}

  ${!isJHA && submission.comms ? `
    <h2>Communications & Monitoring</h2>
    <div class="field-grid">
      <div class="field"><label>Modem Present</label><p>${submission.comms.modemPresent || "—"}</p></div>
      <div class="field"><label>Location</label><p>${submission.comms.modemLocation || "—"}</p></div>
      <div class="field"><label>Open Ports</label><p>${submission.comms.openPorts || "—"}</p></div>
      <div class="field"><label>Hardwire Run</label><p>${submission.comms.hardwireRun || "—"}</p></div>
      <div class="field"><label>Wi-Fi Signal</label><p>${submission.comms.wifiSignal || "—"}</p></div>
      <div class="field"><label>Cellular</label><p>${submission.comms.cellularCoverage || "—"}</p></div>
    </div>
  ` : ""}

  ${!isJHA && submission.interconnection?.length ? `
    <h2>Interconnection Strategy</h2>
    <div class="notes">${submission.interconnection.join(", ")}${submission.interconnectionOther ? `, ${submission.interconnectionOther}` : ""}</div>
  ` : ""}

  ${!isJHA && (submission.roofing || submission.roof) ? (() => {
    const rf = submission.roofing || {}, ro = submission.roof || {};
    const v = (x, other) => x ? esc(x) + (other ? ` (${esc(other)})` : "") : "—";
    return `
    <h2>Roof — Whole House</h2>
    <div class="field-grid">
      <div class="field"><label>Roofing Type</label><p>${v(ro.roofingType, ro.roofingTypeOther)}</p></div>
      <div class="field"><label>Make / Manufacturer</label><p>${v(rf.make)}</p></div>
      <div class="field"><label>Condition</label><p>${v(rf.condition)}</p></div>
      <div class="field"><label>Roof Access</label><p>${v(rf.roofAccess)}</p></div>
      <div class="field"><label>Attic Access</label><p>${v(rf.atticAccess)}</p></div>
      <div class="field"><label>Building Height</label><p>${v(ro.buildingHeight)}</p></div>
      <div class="field"><label>Ground to Eave</label><p>${v(ro.heightToEave)}</p></div>
    </div>
    ${(submission.structureDetails || []).length || submission.structureDetailsOther ? `<div class="notes"><strong>Structure Details:</strong> ${esc([...(submission.structureDetails || []), submission.structureDetailsOther].filter(Boolean).join(", "))}</div>` : ""}`;
  })() : ""}

  ${!isJHA && !isAssessment && (submission.arrays || []).length > 0 ? `
    <h2>Arrays</h2>
    <table style="width:100%;border-collapse:collapse;font-size:10.5px;margin:8px 0">
      <tr style="background:#f4f4f4">
        ${["Array", "Roof Plane", "Pitch", "Azimuth", "Structure", "Size", "Spacing"].map(h => `<th style="border:1px solid #ddd;padding:5px;text-align:left">${h}</th>`).join("")}
      </tr>
      ${submission.arrays.map(a => `<tr>
        <td style="border:1px solid #ddd;padding:5px;font-weight:bold">Array ${esc(a.id)}</td>
        <td style="border:1px solid #ddd;padding:5px">${esc(a.roof) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px">${esc(a.angle) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px">${esc(a.azimuth) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px">${esc(a.structureType) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px">${esc(a.rafterSize) || "—"}</td>
        <td style="border:1px solid #ddd;padding:5px">${esc(a.rafterSpacing) || "—"}</td>
      </tr>${a.notes ? `<tr><td colspan="7" style="border:1px solid #ddd;padding:5px;color:#555;font-style:italic">${esc(a.notes)}</td></tr>` : ""}`).join("")}
    </table>
  ` : ""}

  ${!isJHA && !isAssessment && (submission.arrays || []).length === 0 && submission.roof &&
    ["pitch", "azimuth", "structureType", "rafterSize", "rafterSpacing"].some(k => submission.roof[k]) ? (() => {
    // Surveys filed before Arrays existed kept these on the roof itself
    const ro = submission.roof;
    const v = (x, other) => x ? esc(x) + (other ? ` (${esc(other)})` : "") : "—";
    return `
    <h2>Roof Plane &amp; Framing</h2>
    <div class="field-grid">
      <div class="field"><label>Pitch</label><p>${ro.pitch ? esc(ro.pitch) + (/°|\//.test(String(ro.pitch)) ? "" : "°") : "—"}</p></div>
      <div class="field"><label>Azimuth</label><p>${v(ro.azimuth)}</p></div>
      <div class="field"><label>Structure Type</label><p>${v(ro.structureType, ro.structureTypeOther)}</p></div>
      <div class="field"><label>Rafter / Truss Size</label><p>${v(ro.rafterSize, ro.rafterSizeOther)}</p></div>
      <div class="field"><label>Spacing</label><p>${v(ro.rafterSpacing, ro.rafterSpacingOther)}</p></div>
    </div>`;
  })() : ""}

  ${!isJHA && submission.adders ? `
    <h2>Adders Checklist</h2>
    <div class="field-grid">
      <div class="field"><label>Steep Roof Adder</label><p>${esc(submission.adders.roofPitch) || "—"}</p></div>
      <div class="field"><label>Trenching</label><p>${submission.adders.trenching || "—"}</p></div>
      <div class="field"><label>Conduit Run</label><p>${submission.adders.conduitRun || "—"}</p></div>
      <div class="field"><label>Battery</label><p>${submission.adders.battery || "—"}</p></div>
      <div class="field"><label>Rooftop Access</label><p>${submission.adders.rooftopAccess || "—"}</p></div>
      <div class="field"><label>Rentals</label><p>${submission.adders.rentals || "—"}</p></div>
      <div class="field"><label>Roof Attachments</label><p>${submission.adders.roofAttachments || "—"}</p></div>
      <div class="field"><label>Electrical Upgrades</label><p>${submission.adders.electricalUpgrades || "—"}</p></div>
      <div class="field"><label>Grounding/Violations</label><p>${submission.adders.groundingViolations || "—"}</p></div>
      ${submission.adders.other ? `<div class="field"><label>Other</label><p>${submission.adders.other}</p></div>` : ""}
    </div>
  ` : ""}

  ${(submission.electricalNotes || submission.roofNotes || submission.notes) ? `<h2>Additional Notes</h2><div class="notes">${esc(submission.electricalNotes) || ""}${submission.roofNotes ? `<br/>${esc(submission.roofNotes)}` : ""}${submission.notes ? `<br/>${esc(submission.notes)}` : ""}</div>` : ""}

  ${(() => {
    const ap = []; const seen = new Set();
    const add = (u, lb) => { if (!u || seen.has(u) || u.startsWith("data:")) return; seen.add(u); ap.push({ url: u, label: lb || "" }); };
    // ORDER MATTERS: duplicates are skipped, so whichever source adds a URL
    // first decides its label. Labeled sources go first; the flat photo_urls
    // list (which holds every photo, unlabeled) goes LAST, only to catch any
    // stragglers. Before this ordering, every survey photo printed as "Photo".
    (submission.partnerPhotos || []).forEach(pp => (pp.urls || []).forEach(u => add(u, pp.label)));
    const addFromItem = (ci) => {
      if (Array.isArray(ci.photoUrls)) ci.photoUrls.forEach(u => add(u, ci.label));
      add(ci.photoUrl, ci.label); // legacy single-photo surveys
    };
    if (Array.isArray(submission.checklist)) submission.checklist.forEach(addFromItem);
    if (Array.isArray(submission.photoChecklist)) submission.photoChecklist.forEach(addFromItem);
    if (submission.checklistData?.checklistPhotos) submission.checklistData.checklistPhotos.forEach(cp => add(cp.url, cp.label));
    (submission.arrays || []).forEach(a => (a.photoUrls || []).forEach(u => add(u, `Array ${a.id}${a.roof ? " — " + a.roof : ""}`)));
    if (Array.isArray(submission.photos)) submission.photos.forEach(p => add(typeof p === "string" ? p : p.url, p.label || p.name));
    let pu = submission.photo_urls || [];
    if (typeof pu === "string") { try { pu = JSON.parse(pu); } catch(e) { pu = []; } }
    if (Array.isArray(pu)) pu.forEach(u => add(u));
    if (ap.length === 0) return "";
    // Two per row at a larger size, and object-fit:contain so the whole frame
    // is visible — cover was cropping the top and bottom off every photo,
    // which is exactly where meter readings and panel labels tend to sit.
    return '<h2>Photo Documentation</h2><div style="display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin:12px 0">' +
      ap.map(p => {
        // HEIC uploaded before conversion was enforced: no browser outside
        // Safari can draw it, so say so with a link instead of leaving a gap.
        const heic = /\.hei[cf](\?|$)/i.test(p.url || "");
        const body = heic
          ? '<div style="height:260px;background:#fafafa;display:flex;align-items:center;justify-content:center;text-align:center;padding:14px;box-sizing:border-box">' +
            '<div style="font-size:10.5px;color:#777;line-height:1.5">iPhone HEIC photo — cannot be shown in this report.<br/>' +
            '<a href="' + p.url + '" style="color:#1d6fa5;word-break:break-all">Open the original</a></div></div>'
          : '<a href="' + p.url + '" target="_blank" rel="noopener" title="Open full size"><img src="' + p.url + '" alt="' + esc(p.label || "") + '" style="width:100%;height:260px;object-fit:contain;background:#fafafa;display:block" onerror="this.style.display=\'none\'" /></a>';
        return '<div style="border:1px solid #eee;border-radius:8px;overflow:hidden;break-inside:avoid;page-break-inside:avoid">' + body +
          '<div style="padding:7px 9px;font-size:10.5px;color:#444;font-weight:600;border-top:1px solid #eee">' + esc(p.label || "Photo") + '</div></div>';
      }).join("") + '</div>';
  })()}

  <div class="footer">
    <div>SolarAbility LLC — Illinois — NABCEP Certified — I.C.C #21-0505</div>
    <div>Generated: ${new Date().toLocaleString()}</div>
  </div>

</body></html>`;

  printHTML(printContent);
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const S = {
  input: { width: "100%", padding: "12px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.light, fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", transition: "border-color 0.2s" },
  textarea: { width: "100%", padding: "12px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.light, fontSize: 14, outline: "none", resize: "vertical", minHeight: 80, boxSizing: "border-box", fontFamily: "inherit" },
  select: { width: "100%", padding: "12px 16px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, color: C.light, fontSize: 14, outline: "none", boxSizing: "border-box", appearance: "none", cursor: "pointer", fontFamily: "inherit" },
  card: { background: C.card, borderRadius: 16, border: `1px solid ${C.border}`, padding: 24, marginBottom: 16 },
  btnP: { background: `linear-gradient(135deg, ${C.gold}, ${C.goldDark})`, color: C.bg, border: "none", borderRadius: 10, padding: "12px 28px", fontSize: 14, fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "inherit", transition: "all 0.2s", boxShadow: `0 2px 12px ${C.gold}44` },
  btnS: { background: "transparent", color: C.light, border: `1px solid ${C.border}`, borderRadius: 10, padding: "10px 20px", fontSize: 14, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8, fontFamily: "inherit", transition: "all 0.2s" },
  btnDanger: { background: `${C.danger}22`, color: C.danger, border: `1px solid ${C.danger}44`, borderRadius: 10, padding: "10px 20px", fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" },
  btnSuccess: { background: `${C.success}22`, color: C.success, border: `1px solid ${C.success}44`, borderRadius: 10, padding: "10px 20px", fontSize: 13, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" },
  label: { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8, display: "block" },
  badge: (color) => ({ display: "inline-block", padding: "3px 11px", borderRadius: 16, fontSize: 11, fontWeight: 700, background: `${color}22`, color }),
};

// ─── Shared Components ───────────────────────────────────────────────────────
// ─── Form drafts ─────────────────────────────────────────────────────────────
// A half-finished form survives a phone call, a dead battery, or switching
// tabs. Drafts live on this device (localStorage), autosave a few seconds
// after each change once the form has real content, and are removed when
// the form is submitted. Uploaded photos keep their URLs; previews that were
// still uploading or failed are dropped (they'd be unusable anyway).
const DRAFTS_KEY = "form_drafts";
const draftsAll = () => localCache.load(DRAFTS_KEY) || [];
const draftsFor = (form, projectId) => draftsAll().filter(d => d.form === form && (!projectId || d.projectId === projectId));
const putDraft = (rec) => { const list = draftsAll().filter(d => d.id !== rec.id); list.unshift(rec); localCache.save(DRAFTS_KEY, list.slice(0, 30)); };
const dropDraft = (id) => localCache.save(DRAFTS_KEY, draftsAll().filter(d => d.id !== id));
function sanitizeForDraft(v) {
  if (Array.isArray(v)) {
    return v
      .filter(x => !(x && typeof x === "object" && "url" in x && (!x.url || x.uploading || String(x.url).startsWith("data:"))))
      .map(sanitizeForDraft);
  }
  if (v && typeof v === "object") {
    const o = {};
    for (const k of Object.keys(v)) { if (k === "uploading" || k === "stage" || k === "uploadFailed") continue; o[k] = sanitizeForDraft(v[k]); }
    return o;
  }
  return v;
}
// fields: { name: [value, setter] }. isDirty(): whether there's anything worth keeping.
function useFormDraft({ form, user, fields, isDirty, projectId, projectName, initialProjectId }) {
  const idRef = useRef(null);
  const [savedAt, setSavedAt] = useState(null);
  const [pending, setPending] = useState(() => initialProjectId ? draftsFor(form, initialProjectId) : draftsFor(form));
  const [resumed, setResumed] = useState(false);
  const values = {}; for (const k of Object.keys(fields)) values[k] = fields[k][0];
  const json = JSON.stringify(sanitizeForDraft(values));
  const saveNow = useCallback(() => {
    if (!idRef.current) idRef.current = `draft-${form}-${Date.now()}`;
    const rec = { id: idRef.current, form, projectId: projectId || "", projectName: projectName || "", by: user?.name || "", savedAt: new Date().toISOString(), data: JSON.parse(json) };
    putDraft(rec); setSavedAt(rec.savedAt);
  }, [json, form, projectId, projectName, user]);
  // Autosave, debounced, only once the form has content and the user has
  // either started fresh or already resumed a draft (never over an un-resumed one).
  useEffect(() => {
    if (!isDirty()) return;
    if (pending.length > 0 && !resumed && !idRef.current) return;
    const t = setTimeout(saveNow, 2500);
    return () => clearTimeout(t);
  }, [json]); // eslint-disable-line react-hooks/exhaustive-deps
  const restore = (rec) => {
    for (const k of Object.keys(fields)) if (rec.data && k in rec.data) fields[k][1](rec.data[k]);
    idRef.current = rec.id; setSavedAt(rec.savedAt); setResumed(true); setPending([]);
  };
  const discard = (rec) => { dropDraft(rec.id); setPending(p => p.filter(d => d.id !== rec.id)); if (idRef.current === rec.id) { idRef.current = null; setSavedAt(null); } };
  const startFresh = () => { setPending([]); setResumed(true); };
  const clear = () => { if (idRef.current) dropDraft(idRef.current); idRef.current = null; setSavedAt(null); };
  return { savedAt, pending, saveNow, restore, discard, startFresh, clear };
}
function DraftBanner({ draft, lang }) {
  const es = lang === "es";
  if (draft.pending.length > 0) {
    return (
      <div style={{ background: `${C.warning}18`, border: `1px solid ${C.warning}55`, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.light, marginBottom: 8 }}>{es ? "Tienes un borrador sin terminar" : "You have an unfinished draft"}</div>
        {draft.pending.map(d => (
          <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "6px 0", borderTop: `1px solid ${C.border}` }}>
            <div style={{ flex: 1, minWidth: 160, fontSize: 12, color: C.muted }}>
              <span style={{ color: C.light, fontWeight: 600 }}>{d.projectName || (es ? "Sin trabajo vinculado" : "No job linked")}</span> · {new Date(d.savedAt).toLocaleString()}{d.by ? ` · ${d.by}` : ""}
            </div>
            <button type="button" style={{ ...S.btnP, padding: "6px 14px", fontSize: 12 }} onClick={() => draft.restore(d)}>{es ? "Continuar" : "Resume"}</button>
            <button type="button" style={{ ...S.btnS, padding: "6px 12px", fontSize: 12, color: C.danger }} onClick={() => { if (window.confirm(es ? "¿Borrar este borrador?" : "Delete this draft?")) draft.discard(d); }}>{es ? "Borrar" : "Delete"}</button>
          </div>
        ))}
        <button type="button" onClick={draft.startFresh} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, padding: "8px 0 0", fontFamily: "inherit", textDecoration: "underline" }}>{es ? "Empezar en blanco" : "Start a blank form instead"}</button>
      </div>
    );
  }
  return null;
}
function DraftStatus({ draft, lang }) {
  const es = lang === "es";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12, color: C.muted, marginTop: 8, flexWrap: "wrap" }}>
      <button type="button" onClick={draft.saveNow} style={{ ...S.btnS, padding: "6px 12px", fontSize: 12 }}>{es ? "Guardar borrador" : "Save draft"}</button>
      {draft.savedAt && <span>{es ? "Borrador guardado" : "Draft saved"} {new Date(draft.savedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })} · {es ? "solo en este dispositivo" : "on this device only"}</span>}
    </div>
  );
}

// Turn bare URLs in free text into tappable links that wrap instead of
// stretching the layout. Used for notes, partner notes, visit notes.
function Linkify({ text }) {
  const parts = String(text || "").split(/(https?:\/\/[^\s<>"']+)/g);
  return <>{parts.map((p, i) => /^https?:\/\//.test(p)
    ? <a key={i} href={p} target="_blank" rel="noopener noreferrer" style={{ color: C.gold, overflowWrap: "anywhere", wordBreak: "break-all" }}>{p.replace(/^https?:\/\//, "").replace(/\/$/, "")}</a>
    : <span key={i}>{p}</span>)}</>;
}

function Field({ label, required, children }) {
  return <div style={{ marginBottom: 18 }}><label style={S.label}>{label} {required && <span style={{ color: C.gold }}>*</span>}</label>{children}</div>;
}

function PhotoUploader({ photos, setPhotos, label }) {
  const ref = useRef(null);
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const tempId = Date.now() + Math.random();
      // Add placeholder immediately so submit-guards know an upload is in flight
      setPhotos(p => [...p, { id: tempId, name: file.name, url: "", uploading: true }]);
      try {
        const path = `uploads/${Date.now()}-${file.name}`;
        const url = await db.uploadPhoto(file, path);
        setPhotos(p => p.map(ph => ph.id === tempId ? { ...ph, url, path, uploading: false } : ph));
      } catch (err) {
        if (err?.code === "HEIC_CONVERSION_FAILED") {
          // A data-URL fallback would be unviewable AND dropped at submit,
          // so drop the placeholder and tell them how to fix it instead.
          setPhotos(p => p.filter(ph => ph.id !== tempId));
          alert(db.heicHelpText(file.name));
          continue;
        }
        const reader = new FileReader();
        reader.onload = (ev) => setPhotos(p => p.map(ph => ph.id === tempId ? { ...ph, url: ev.target.result, uploading: false, uploadFailed: true } : ph));
        reader.readAsDataURL(file);
      }
    }
    e.target.value = "";
  };
  return (
    <div>
      <label style={S.label}>{label || "Photos"}</label>
      <div onClick={() => ref.current?.click()} style={{ border: `2px dashed ${C.border}`, borderRadius: 12, padding: "24px 20px", textAlign: "center", cursor: "pointer", background: `${C.surface}66`, transition: "border-color 0.2s" }}
        onMouseEnter={e => e.currentTarget.style.borderColor = C.gold} onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
        <div style={{ color: C.gold, marginBottom: 6, display: "flex", justifyContent: "center" }}>{I.cam}</div>
        <div style={{ color: C.muted, fontSize: 14, fontWeight: 500 }}>Click to upload photos</div>
        <div style={{ color: `${C.muted}77`, fontSize: 12, marginTop: 4 }}>JPG, PNG — up to 10MB</div>
        <input ref={ref} type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: "none" }} />
      </div>
      {photos.length > 0 && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
          {photos.map(p => (
            <div key={p.id} style={{ position: "relative" }}>
              {p.uploading ? (
                <div style={{ width: 80, height: 80, borderRadius: 10, border: `1px dashed ${C.gold}`, display: "flex", alignItems: "center", justifyContent: "center", background: C.surface }}>
                  <div style={{ fontSize: 10, color: C.gold, fontWeight: 700, animation: "pulse 1.2s infinite" }}>⏳</div>
                </div>
              ) : (
                <img loading="lazy" decoding="async" src={p.url} alt="" onClick={() => openPhoto(p.url, photos.map(x => x.url))} style={{ width: 80, height: 80, objectFit: "cover", borderRadius: 10, border: `1px solid ${p.uploadFailed ? C.danger : C.border}`, cursor: "zoom-in" }} />
              )}
              {p.uploadFailed && <div style={{ position: "absolute", bottom: 2, left: 2, right: 2, fontSize: 8, color: "#fff", background: `${C.danger}dd`, borderRadius: 4, textAlign: "center", padding: "1px 0" }}>offline only</div>}
              <button onClick={e => { e.stopPropagation(); setPhotos(prev => prev.filter(x => x.id !== p.id)); }}
                style={{ position: "absolute", top: -6, right: -6, width: 22, height: 22, borderRadius: "50%", background: C.danger, border: "none", color: "#fff", cursor: "pointer", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Full-size photo viewer ──────────────────────────────────────────────────
// openPhoto(url, list) from anywhere; <PhotoViewer/> is mounted once in App.
// Arrow keys / buttons step through the list, Esc closes, "Original" opens
// the file itself in a new tab (pinch-zoom on a phone).
const photoBus = { fn: null };
function openPhoto(url, list) {
  const urls = (list && list.length ? list : [url]).filter(u => u && !String(u).startsWith("data:"));
  const i = Math.max(0, urls.indexOf(url));
  if (photoBus.fn && urls.length) photoBus.fn({ urls, i });
}
function PhotoViewer() {
  const [state, setState] = useState(null);
  useEffect(() => { photoBus.fn = setState; return () => { photoBus.fn = null; }; }, []);
  const step = useCallback((d) => setState(s => s ? { ...s, i: (s.i + d + s.urls.length) % s.urls.length } : s), []);
  useEffect(() => {
    if (!state) return;
    const onKey = (e) => { if (e.key === "Escape") setState(null); if (e.key === "ArrowRight") step(1); if (e.key === "ArrowLeft") step(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [state, step]);
  const touch = useRef(null);
  if (!state) return null;
  const url = state.urls[state.i];
  const btn = { background: "rgba(0,0,0,0.55)", color: "#fff", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 10, padding: "8px 14px", fontSize: 14, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", textDecoration: "none" };
  return (
    <div onClick={() => setState(null)}
      onTouchStart={(e) => { touch.current = e.touches[0].clientX; }}
      onTouchEnd={(e) => { const dx = e.changedTouches[0].clientX - (touch.current ?? e.changedTouches[0].clientX); if (Math.abs(dx) > 50 && state.urls.length > 1) step(dx < 0 ? 1 : -1); touch.current = null; }}
      style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.92)", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <img src={url} alt="" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "96vw", maxHeight: "86vh", objectFit: "contain", borderRadius: 6, background: "#111" }} />
      <div onClick={(e) => e.stopPropagation()} style={{ position: "fixed", top: 12, left: 12, right: 12, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <span style={{ color: "#ddd", fontSize: 13 }}>{state.urls.length > 1 ? `${state.i + 1} / ${state.urls.length}` : ""}</span>
        <span style={{ display: "flex", gap: 8 }}>
          <a href={url} target="_blank" rel="noopener noreferrer" style={btn}>Original ↗</a>
          <button type="button" onClick={() => setState(null)} style={btn}>✕</button>
        </span>
      </div>
      {state.urls.length > 1 && (<>
        <button type="button" onClick={(e) => { e.stopPropagation(); step(-1); }} style={{ ...btn, position: "fixed", left: 10, top: "50%", transform: "translateY(-50%)", fontSize: 22, padding: "10px 14px" }}>‹</button>
        <button type="button" onClick={(e) => { e.stopPropagation(); step(1); }} style={{ ...btn, position: "fixed", right: 10, top: "50%", transform: "translateY(-50%)", fontSize: 22, padding: "10px 14px" }}>›</button>
      </>)}
    </div>
  );
}

function Modal({ children, onClose }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", zIndex: 1000, backdropFilter: "blur(6px)", overflowY: "auto", WebkitOverflowScrolling: "touch" }} onClick={onClose}>
      <div style={{ minHeight: "100%", display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px" }}>
        <div style={{ ...S.card, maxWidth: 520, width: "100%", padding: 28, animation: "slideUp 0.25s ease", maxHeight: "none" }} onClick={e => e.stopPropagation()}>
          <button onClick={onClose} style={{ position: "sticky", top: 0, float: "right", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, width: 32, height: 32, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.muted, zIndex: 1 }}>{I.x}</button>
          {children}
        </div>
      </div>
    </div>
  );
}

// ─── Login ───────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [mode, setMode] = useState("login"); // login | signup | reset
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const handle = async () => {
    setError(""); setSuccess("");
    if (mode === "reset") {
      if (!email) return setError("Enter your email");
      setLoading(true);
      try { await db.resetPassword(email); setSuccess("Password reset email sent!"); } catch (e) { setError(e.message || "Failed to send reset email"); }
      setLoading(false); return;
    }
    if (!email || !password) return setError("Please fill in all fields.");
    if (mode === "signup" && !name) return setError("Please enter your name.");
    if (mode === "signup" && password.length < 6) return setError("Password must be at least 6 characters.");
    setLoading(true);
    try {
      if (mode === "signup") {
        const user = await db.signUp(email, password, name);
        // Supabase may require email confirmation — check if user is confirmed
        if (user && !user.confirmed_at && !user.email_confirmed_at) {
          setSuccess("Account created! Check your email to confirm, then sign in. (If email confirmation is disabled, you can sign in now.)");
          setMode("login");
          setLoading(false);
          return;
        }
        // If auto-confirmed, log in directly
        const prof = await db.getRole(user.id);
        onLogin({ id: user.id, email: user.email, name: prof.name || user?.user_metadata?.full_name || name, role: prof.role });
      } else {
        const user = await db.signIn(email, password);
        const prof = await db.getRole(user.id);
        onLogin({ id: user.id, email: user.email, name: prof.name || user?.user_metadata?.full_name || email.split("@")[0], role: prof.role });
      }
    } catch (e) { setError(e.message || "Authentication failed"); }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: `radial-gradient(ellipse at 30% 20%, ${C.card} 0%, ${C.bg} 70%)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 20 }}>
      <div style={{ width: "100%", maxWidth: 440 }}>
        <div style={{ textAlign: "center", marginBottom: 36 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
            <LogoMark size={52} />
            <div style={{ textAlign: "left" }}>
              <div style={{ fontWeight: 900, fontSize: 24, color: C.light, letterSpacing: -0.5 }}>SolarAbility</div>
              <div style={{ fontSize: 11, color: C.gold, fontWeight: 700, letterSpacing: 2.5, textTransform: "uppercase" }}>Safety Hub</div>
            </div>
          </div>
          <p style={{ color: C.muted, fontSize: 14 }}>Employee safety & compliance portal</p>
        </div>

        <div style={{ ...S.card, padding: 32 }}>
          <h2 style={{ margin: "0 0 20px", fontSize: 18, fontWeight: 700, color: C.light }}>
            {mode === "login" ? "Welcome Back" : mode === "signup" ? "Create Account" : "Reset Password"}
          </h2>

          {error && <div style={{ background: C.dangerBg, border: `1px solid ${C.danger}44`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, color: C.danger, fontSize: 13 }}>{error}</div>}
          {success && <div style={{ background: C.successBg, border: `1px solid ${C.success}44`, borderRadius: 10, padding: "10px 14px", marginBottom: 16, color: C.success, fontSize: 13 }}>{success}</div>}

          {mode === "signup" && (
            <Field label="Full Name"><div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.muted }}>{I.user}</div>
              <input style={{ ...S.input, paddingLeft: 42 }} placeholder="e.g. John Smith" value={name} onChange={e => setName(e.target.value)} />
            </div></Field>
          )}
          <Field label="Email"><div style={{ position: "relative" }}>
            <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.muted }}>{I.mail}</div>
            <input style={{ ...S.input, paddingLeft: 42 }} type="email" placeholder="you@solarability.com" value={email} onChange={e => setEmail(e.target.value)} />
          </div></Field>
          {mode !== "reset" && (
            <Field label="Password"><div style={{ position: "relative" }}>
              <div style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", color: C.muted }}>{I.lock}</div>
              <input style={{ ...S.input, paddingLeft: 42 }} type="password" placeholder="Enter password" value={password} onChange={e => setPassword(e.target.value)} />
            </div></Field>
          )}

          <button style={{ ...S.btnP, width: "100%", justifyContent: "center", padding: "14px", fontSize: 15, opacity: loading ? 0.7 : 1 }} onClick={handle} disabled={loading}>
            {loading ? "Please wait..." : mode === "login" ? "Sign In" : mode === "signup" ? "Create Account" : "Send Reset Link"}
          </button>

          <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 18 }}>
            {mode !== "login" && <button style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 600 }} onClick={() => { setMode("login"); setError(""); setSuccess(""); }}>Sign In</button>}
            {mode !== "signup" && <button style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", fontSize: 13, fontFamily: "inherit", fontWeight: 600 }} onClick={() => { setMode("signup"); setError(""); setSuccess(""); }}>Create Account</button>}
            {mode !== "reset" && <button style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 13, fontFamily: "inherit" }} onClick={() => { setMode("reset"); setError(""); setSuccess(""); }}>Forgot Password?</button>}
          </div>

          {!USE_SUPABASE && (
            <div style={{ background: C.warnBg, border: `1px solid ${C.warning}33`, borderRadius: 10, padding: "12px 14px", marginTop: 18, fontSize: 12, color: C.warning, lineHeight: 1.5 }}>
              <strong>Demo Mode</strong> — Supabase not configured. Data is stored in memory only. Use <strong>admin@</strong> in email for admin access.
            </div>
          )}
        </div>

        <p style={{ textAlign: "center", color: `${C.muted}55`, fontSize: 11, marginTop: 20 }}>SolarAbility LLC · Illinois · NABCEP Certified · I.C.C #21-0505</p>
      </div>
    </div>
  );
}

// ─── Nav ─────────────────────────────────────────────────────────────────────
function NavBtn({ icon, label, active, onClick, count }) {
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 18px", width: "100%", textAlign: "left", border: "none", fontFamily: "inherit", background: active ? `${C.gold}12` : "transparent", borderLeft: active ? `3px solid ${C.gold}` : "3px solid transparent", color: active ? C.gold : C.muted, cursor: "pointer", fontSize: 13, fontWeight: active ? 600 : 400, transition: "all 0.15s" }}>
      {icon} <span style={{ flex: 1 }}>{label}</span>
      {count > 0 && <span style={{ background: C.gold, color: C.bg, borderRadius: 10, padding: "1px 8px", fontSize: 10, fontWeight: 800 }}>{count}</span>}
    </button>
  );
}

function Sidebar({ page, setPage, user, onLogout, counts, mobileOpen, setMobileOpen, lang, setLang, t }) {
  const isAdmin = user?.role === "admin";
  const isSafety = user?.role === "safety" || isAdmin;
  const isCoordinator = user?.role === "coordinator";
  const moneyOk = canSeeMoney(user);
  const nav = [
    { id: "dashboard", icon: I.dash, label: t.dashboard, count: counts.total },
    { id: "jha", icon: I.hazard, label: t.newJHA },
    { id: "survey", icon: I.clip, label: t.tsaSurvey },
    { id: "install", icon: I.sun, label: t.installCloseout || "Install Closeout" },
    { id: "assessment", icon: I.wrench, label: t.serviceAssessment },
    { id: "procedures", icon: I.book, label: t.procedures },
    { id: "truckcheck", icon: I.truck, label: t.truckCheck },
    { id: "schedule", icon: I.calendar, label: t.schedule },
    ...(moneyOk ? [{ id: "jobmap", icon: I.mappin, label: t.jobMap || "Job Map" }] : []),
    { id: "projects", icon: I.folder, label: t.projects },
    { id: "inventory", icon: I.box, label: t.inventory },
    ...(isSafety ? [{ id: "safety", icon: I.shield, label: t.safety }] : []),
    ...(moneyOk ? [{ id: "crew", icon: I.user, label: t.crew || "Crew" }] : []),
    ...(moneyOk ? [{ id: "quotes", icon: I.note, label: t.quotes || "Service Quotes" }] : []),
    ...(moneyOk ? [{ id: "estimator", icon: I.chart, label: t.estimator || "R&R Estimator" }] : []),
    ...(moneyOk ? [{ id: "contacts", icon: I.mail, label: t.contacts || "Contacts" }] : []),
    ...(moneyOk ? [{ id: "costing", icon: I.chart, label: t.costing }] : []),
    ...(isAdmin ? [{ id: "partners", icon: I.handshake, label: t.partners }] : []),
    ...((isAdmin || isCoordinator) ? [{ id: "office", icon: I.list, label: t.office || "Office" }] : []),
    ...((isAdmin || isSafety || isCoordinator) ? [{ id: "admin", icon: I.admin, label: t.adminPanel, count: counts.pending }] : []),
  ];

  const [sunBreak, setSunBreak] = useState(false);

  const inner = (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {sunBreak && <SunBreak onClose={() => setSunBreak(false)} />}
      <div style={{ padding: "22px 18px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <LogoMark size={36} onClick={() => setSunBreak(true)} />
          <div><div style={{ fontWeight: 800, fontSize: 15, color: C.light }}>SolarAbility</div><div style={{ fontSize: 9, color: C.gold, fontWeight: 700, letterSpacing: 2, textTransform: "uppercase" }}>Safety Hub</div></div>
        </div>
      </div>
      <div style={{ padding: "14px 0", flex: 1, overflowY: "auto" }}>
        <div style={{ padding: "0 18px", fontSize: 10, color: `${C.muted}66`, fontWeight: 700, letterSpacing: 1.5, marginBottom: 8 }}>MENU</div>
        {nav.map(n => <NavBtn key={n.id} {...n} active={page === n.id} onClick={() => { setPage(n.id); setMobileOpen(false); }} />)}
      </div>
      {/* Language Toggle */}
      <div style={{ padding: "8px 14px", borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 12px", background: C.surface, borderRadius: 8 }}>
          <span style={{ color: C.muted }}>{I.globe}</span>
          <button onClick={() => { const nl = lang === "en" ? "es" : "en"; setLang(nl); setLangStorage(nl); }}
            style={{ flex: 1, background: "none", border: "none", color: C.light, cursor: "pointer", fontFamily: "inherit", fontSize: 12, fontWeight: 600, textAlign: "left" }}>
            {lang === "en" ? "🇺🇸 English" : "🇲🇽 Español"}
          </button>
          <span style={{ fontSize: 10, color: C.muted }}>{lang === "en" ? "ES" : "EN"} →</span>
        </div>
      </div>
      <div style={{ padding: 14, borderTop: `1px solid ${C.border}` }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: C.surface, borderRadius: 10 }}>
          <div style={{ width: 32, height: 32, borderRadius: 8, background: isAdmin ? `${C.gold}33` : `${C.green}33`, display: "flex", alignItems: "center", justifyContent: "center", color: isAdmin ? C.gold : C.greenLight, flexShrink: 0 }}>{I.user}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user?.name}</div>
            <div style={{ fontSize: 11, color: C.muted }}>{isAdmin ? "Supervisor" : isCoordinator ? "Coordinator" : isSafety ? "Safety" : "Field Crew"}</div>
          </div>
          <button onClick={onLogout} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 4 }} title="Sign Out">{I.logout}</button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      <div className="sb-desktop" style={{ width: 250, background: `linear-gradient(180deg, ${C.card} 0%, #111620 100%)`, borderRight: `1px solid ${C.border}`, position: "fixed", top: 0, left: 0, bottom: 0, zIndex: 100, display: "none" }}>{inner}</div>
      {mobileOpen && (
        <div style={{ position: "fixed", inset: 0, zIndex: 200 }} onClick={() => setMobileOpen(false)}>
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.5)", backdropFilter: "blur(4px)" }} />
          <div style={{ position: "relative", width: 270, height: "100%", background: `linear-gradient(180deg, ${C.card} 0%, #111620 100%)`, borderRight: `1px solid ${C.border}`, animation: "slideR 0.2s ease" }} onClick={e => e.stopPropagation()}>{inner}</div>
        </div>
      )}
    </>
  );
}

// ─── JHA Form ────────────────────────────────────────────────────────────────
// Site conditions a crew marks up before work, taken from the markup key on a
// site safety plan. Checking one asks where it is, because "power lines" with
// no location does not help anybody.
const SITE_HAZARDS = [
  { id: "power", label: "Power lines / service drop", danger: true },
  { id: "noladder", label: "No ladder access (steep grade or obstruction)", danger: true },
  { id: "restricted", label: "Restricted access area", danger: true },
  { id: "skylight", label: "Skylight(s)", danger: true },
  { id: "fragile", label: "Fragile or damaged roof area", danger: true },
  { id: "anchor_p", label: "Permanent anchor point" },
  { id: "anchor_t", label: "Temporary anchor point" },
  { id: "ladder", label: "Installer ladder set-up point" },
  { id: "jbox", label: "Junction / combiner box" },
  { id: "stubout", label: "Stub-out" },
  { id: "conduit", label: "Conduit run" },
  { id: "gas", label: "Gas shut-off" },
  { id: "water", label: "Water shut-off" },
  { id: "dog", label: "Dog or animal on site" },
  { id: "traffic", label: "Traffic / public exposure — barricades needed" },
];

function JHAForm({ onSubmit, onBack, user, initialProjectId, initialVisitId }) {
  const lang = getLang();
  const [form, setForm] = useState({
    title: "", location: "", date: localDate(), supervisor: "", crew: "", description: "",
    hospitalName: "", hospitalAddress: "", hospitalPhone: "",
    clinicName: "", clinicAddress: "", coachName: "", coachPhone: "",
    heatWatch: false,
  });
  // Site conditions marked before work starts — the things that decide where
  // ladders, anchors and bodies can go.
  const [siteHazards, setSiteHazards] = useState({});
  const [crewAck, setCrewAck] = useState([]);   // [{ name, initials, at }]
  const [waterLog, setWaterLog] = useState([]); // [{ at, note }]

  // Today's JHA, already submitted and reviewed, with its water log still open.
  // The hazard analysis is locked once submitted; only the log keeps growing.
  const [openJha, setOpenJha] = useState(null);
  const [savingLog, setSavingLog] = useState(false);
  const openRef = useRef(false);
  if (!openRef.current) {
    openRef.current = true;
    db.getOpenJHAToday(user).then(setOpenJha).catch(() => {});
  }

  const pushLog = async (entries, closedAt) => {
    setSavingLog(true);
    const hazards = { ...(openJha.hazards || {}), waterLog: entries };
    if (closedAt) hazards.waterLogClosedAt = closedAt;
    try {
      await db.appendWaterLog(openJha.id, hazards);
      setOpenJha(closedAt ? null : { ...openJha, hazards });
      if (closedAt) alert(lang === "es" ? "Día cerrado. El registro de agua quedó guardado." : "Day closed. The water log is saved to the JHA.");
    } catch (err) {
      alert(lang === "es" ? "No se pudo guardar. Revisa tu conexión." : "Couldn't save that — check your connection and try again.");
    }
    setSavingLog(false);
  };
  const [hazards, setHazards] = useState([{ id: 1, step: "", hazard: "", risk: "Medium", control: "" }]);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const u = (f, v) => setForm(p => ({ ...p, [f]: v }));

  // Link to an existing job so the JHA attaches instead of creating a duplicate
  const [openProjects, setOpenProjects] = useState([]);
  const [linkedProjectId, setLinkedProjectId] = useState("");
  const jhaProjRef = useRef(false);
  if (!jhaProjRef.current) {
    jhaProjRef.current = true;
    db.getProjects().then(list => setOpenProjects(list.filter(p => p.status !== "completed"))).catch(() => {});
  }
  // Everything the project already knows lands on the JHA: job, address,
  // crew, lead, the urgent care from the Site JHA sheet, and the numbered
  // hazards marked on the roof plan as the first hazard rows.
  const applyProject = (proj) => {
    if (!proj) return;
    const sheet = proj.jhaSheet || {};
    const visit = initialVisitId ? (proj.visits || []).find(v => v.id === initialVisitId) : null;
    const crewNames = (proj.members || []).map(m => m.displayName || (m.name || "").replace(/@.*$/, "")).filter(Boolean).join(", ");
    setForm(prev => ({
      ...prev,
      title: prev.title || proj.name || "",
      location: proj.address || prev.location,
      supervisor: prev.supervisor || sheet.crewLead || user?.name || "",
      crew: prev.crew || crewNames,
      description: prev.description || [proj.serviceSubtype, visit?.notes].filter(Boolean).join(" — "),
      hospitalName: prev.hospitalName || sheet.urgentCare || "",
      hospitalAddress: prev.hospitalAddress || sheet.urgentCareAddress || "",
      hospitalPhone: prev.hospitalPhone || sheet.urgentCarePhone || "",
    }));
    const marked = (sheet.markers || []).filter(m => m.type === "H");
    if (marked.length) {
      setHazards(prev => {
        const blank = prev.length === 1 && !prev[0].step && !prev[0].hazard;
        if (!blank) return prev;
        return marked.map((m, i) => ({ id: Date.now() + i, step: `Site hazard #${m.num || i + 1} (see JHA sheet)`, hazard: m.note || "Marked on roof plan", risk: "High", control: "" }));
      });
    }
  };
  const initLinkRef = useRef(false);
  useEffect(() => {
    if (initLinkRef.current || !initialProjectId || openProjects.length === 0) return;
    initLinkRef.current = true;
    const proj = openProjects.find(x => x.id === initialProjectId);
    if (proj) { setLinkedProjectId(proj.id); applyProject(proj); }
  }, [initialProjectId, openProjects]); // eslint-disable-line react-hooks/exhaustive-deps

  const draft = useFormDraft({
    form: "jha", user, initialProjectId,
    projectId: linkedProjectId, projectName: openProjects.find(x => x.id === linkedProjectId)?.name || form.title || "",
    fields: { form: [form, setForm], siteHazards: [siteHazards, setSiteHazards], crewAck: [crewAck, setCrewAck], hazards: [hazards, setHazards], photos: [photos, setPhotos], linkedProjectId: [linkedProjectId, setLinkedProjectId] },
    isDirty: () => !!(form.title || form.location || form.description || photos.length || hazards.some(h => h.step || h.hazard)),
  });
  const submit = async () => {
    if (!form.title || !form.location) return alert("Please fill Job Title and Location.");
    if (photos.some(p => p.uploading)) return alert("Please wait — photos are still uploading.");
    const failedJ = photos.filter(p => p.uploadFailed || (p.url || "").startsWith("data:"));
    if (failedJ.length && !window.confirm(`${failedJ.length} photo(s) did not upload (no signal?) and will NOT be on this JHA:\n${failedJ.map(p => "• " + (p.name || "photo")).join("\n")}\n\nTap Cancel, then remove and re-add them when you have signal. Submit without them?`)) return;
    setLoading(true);
    const cleanUrls = photos.map(p => p.url).filter(u => u && !u.startsWith("data:"));
    const data = { ...form, hazards, photos: photos.map(p => ({ name: p.name, url: p.url })), photo_urls: cleanUrls, type: "JHA", submittedAt: new Date().toISOString(), submittedBy: user?.name, status: "submitted" };
    try {
      await db.submitJHA({
        title: data.title, location: data.location, date: data.date,
        supervisor: data.supervisor, crew: data.crew, description: data.description,
        // Emergency info rides along with the hazards so it survives in one place
        hazards: {
          items: data.hazards,
          emergency: {
            hospitalName: form.hospitalName, hospitalAddress: form.hospitalAddress, hospitalPhone: form.hospitalPhone,
            clinicName: form.clinicName, clinicAddress: form.clinicAddress,
            coachName: form.coachName, coachPhone: form.coachPhone,
          },
          siteHazards: SITE_HAZARDS.filter(h => siteHazards[h.id]).map(h => ({ label: h.label, note: (siteHazards[h.id] || {}).note || "" })),
          crewAck, waterLog, heatWatch: !!form.heatWatch,
          visitId: initialVisitId || "",
        },
        photo_urls: cleanUrls, status: "submitted", project_id: linkedProjectId || null,
      }, user?.id);
    } catch (e) { console.warn(e); }
    db.sendNotification("JHA", data.title, user?.name);
    // A JHA never creates a project on its own — that made duplicate jobs
    // with office billing tasks whenever the crew forgot the job picker.
    if (linkedProjectId) {
      db.linkFormToProject(linkedProjectId, { formType: "JHA", userName: user?.name, visitId: initialVisitId || "" });
    }
    setLoading(false);
    draft.clear();
    onSubmit(data);
  };

  return (
    <div style={{ maxWidth: 800, animation: "fadeIn 0.25s ease" }}>
      <DraftBanner draft={draft} lang={lang} />
      <button onClick={onBack} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} Back</button>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: C.warnBg, display: "flex", alignItems: "center", justifyContent: "center", color: C.warning }}>{I.hazard}</div>
        <div><h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>Job Hazard Analysis</h2><p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Identify hazards and controls for each work step</p></div>
      </div>

      {openJha && (() => {
        const entries = openJha.hazards?.waterLog || [];
        return (
          <div style={{ ...S.card, border: `1px solid ${C.warning}66`, background: `${C.warning}0d` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
              <div>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 800, color: C.warning }}>
                  {lang === "es" ? "Registro de agua abierto — hoy" : "Water log still open — today"}
                </h3>
                <div style={{ fontSize: 12.5, color: C.light, marginTop: 4 }}>{openJha.title}</div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
                  {lang === "es"
                    ? "El JHA ya se envió y se revisó. Solo falta registrar los descansos de agua hasta cerrar el día."
                    : "The JHA is already submitted and reviewed. Only the water breaks are still being logged."}
                </div>
              </div>
              <span style={S.badge(C.warning)}>{entries.length} {lang === "es" ? "registrados" : "logged"}</span>
            </div>

            <button type="button" disabled={savingLog}
              onClick={() => pushLog([...entries, { at: new Date().toISOString(), note: user?.name || "" }])}
              style={{ ...S.btnP, width: "100%", justifyContent: "center", marginTop: 14, opacity: savingLog ? 0.6 : 1 }}>
              {savingLog ? "..." : <>{I.plus} {lang === "es" ? "Registrar descanso de agua (ahora)" : "Log a water break (now)"}</>}
            </button>

            {entries.length > 0 && (
              <div style={{ marginTop: 12 }}>
                {entries.map((w, i) => (
                  <div key={i} style={{ display: "flex", gap: 10, padding: "6px 0", borderBottom: i < entries.length - 1 ? `1px solid ${C.border}33` : "none", fontSize: 12.5 }}>
                    <span style={{ color: C.gold, fontWeight: 700, width: 70, flexShrink: 0 }}>{fmtJobTime(w.at)}</span>
                    <span style={{ color: C.muted }}>{w.note || "—"}</span>
                  </div>
                ))}
              </div>
            )}

            <button type="button" disabled={savingLog}
              onClick={() => {
                if (!window.confirm(lang === "es"
                  ? "¿Cerrar el día? Ya no se pueden agregar descansos a este JHA."
                  : "Close out the day? No more water breaks can be added to this JHA.")) return;
                pushLog(entries, new Date().toISOString());
              }}
              style={{ ...S.btnS, width: "100%", justifyContent: "center", marginTop: 10 }}>
              {I.check} {lang === "es" ? "Cerrar el día" : "Close out the day"}
            </button>

            <div style={{ fontSize: 11, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
              {lang === "es" ? "¿Es otro trabajo? Llena un JHA nuevo abajo." : "Different job today? Fill out a new JHA below."}
            </div>
          </div>
        );
      })()}

      <div style={S.card}>
        <h3 style={{ margin: "0 0 18px", fontSize: 14, fontWeight: 700, color: C.light }}>General Information</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          {openProjects.length > 0 && (
            <div style={{ gridColumn: "1/-1" }}>
              <Field label="Link to Existing Job">
                <JobPicker projects={openProjects} value={linkedProjectId} onPick={id => {
                  setLinkedProjectId(id);
                  applyProject(openProjects.find(x => x.id === id));
                }} />
              </Field>
              {linkedProjectId && <div style={{ color: C.muted, fontSize: 12, marginTop: -8 }}>This JHA attaches to that project instead of creating a new one.</div>}
            </div>
          )}
          <Field label="Job Title" required><input style={S.input} placeholder="e.g. Residential Panel Install" value={form.title} onChange={e => u("title", e.target.value)} /></Field>
          <Field label="Location" required><input style={S.input} placeholder="e.g. 123 Main St, Springfield IL" value={form.location} onChange={e => u("location", e.target.value)} /></Field>
          <Field label="Date"><input type="date" style={{ ...S.input, colorScheme: "dark" }} value={form.date} onChange={e => u("date", e.target.value)} /></Field>
          <Field label="Supervisor"><input style={S.input} placeholder="Supervisor name" value={form.supervisor} onChange={e => u("supervisor", e.target.value)} /></Field>
          <Field label="Crew"><input style={S.input} placeholder="Crew member names" value={form.crew} onChange={e => u("crew", e.target.value)} /></Field>
          <div />
          <div style={{ gridColumn: "1/-1" }}><Field label="Description"><textarea style={S.textarea} placeholder="Scope of work..." value={form.description} onChange={e => u("description", e.target.value)} /></Field></div>
        </div>
      </div>

      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
          <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.light }}>Hazard Identification</h3>
          <button style={S.btnS} onClick={() => setHazards(p => [...p, { id: Date.now(), step: "", hazard: "", risk: "Medium", control: "" }])}>{I.plus} Add Step</button>
        </div>
        {hazards.map((h, idx) => (
          <div key={h.id} style={{ background: C.surface, borderRadius: 12, padding: 18, marginBottom: 10, border: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ background: `${C.gold}22`, color: C.gold, borderRadius: 8, padding: "3px 12px", fontSize: 12, fontWeight: 800 }}>Step {idx + 1}</span>
              {hazards.length > 1 && <button onClick={() => setHazards(p => p.filter(x => x.id !== h.id))} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label="Job Step"><input style={S.input} placeholder="Describe the step" value={h.step} onChange={e => setHazards(p => p.map(x => x.id === h.id ? { ...x, step: e.target.value } : x))} /></Field>
              <Field label="Hazard"><input style={S.input} placeholder="What could go wrong?" value={h.hazard} onChange={e => setHazards(p => p.map(x => x.id === h.id ? { ...x, hazard: e.target.value } : x))} /></Field>
              <Field label="Risk Level"><div style={{ position: "relative" }}><select style={S.select} value={h.risk} onChange={e => setHazards(p => p.map(x => x.id === h.id ? { ...x, risk: e.target.value } : x))}>{RISK_LEVELS.map(r => <option key={r}>{r}</option>)}</select><div style={{ position: "absolute", right: 14, top: "50%", transform: "translateY(-50%)", width: 10, height: 10, borderRadius: "50%", background: RISK_COLORS[h.risk] }} /></div></Field>
              <Field label="Control Measure"><input style={S.input} placeholder="Mitigation" value={h.control} onChange={e => setHazards(p => p.map(x => x.id === h.id ? { ...x, control: e.target.value } : x))} /></Field>
            </div>
          </div>
        ))}
      </div>

      {/* Emergency info — the thing nobody looks up until they need it in a
          hurry, which is exactly why it belongs on the JHA. */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: C.light }}>Emergency — Nearest Hospital</h3>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>Look this up before work starts, not after someone's hurt. Everyone on the crew should know where it is.</p>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Hospital Name"><input style={S.input} value={form.hospitalName} onChange={e => u("hospitalName", e.target.value)} placeholder="e.g. Advocate Christ Medical Center" /></Field>
          <Field label="Hospital Phone"><input style={S.input} type="tel" value={form.hospitalPhone} onChange={e => u("hospitalPhone", e.target.value)} placeholder="(708) 555-0000" /></Field>
          <div style={{ gridColumn: "1/-1" }}>
            <Field label="Hospital Address"><input style={S.input} value={form.hospitalAddress} onChange={e => u("hospitalAddress", e.target.value)} placeholder="Street, city" /></Field>
          </div>
          <Field label="Nearest Occupational Clinic"><input style={S.input} value={form.clinicName} onChange={e => u("clinicName", e.target.value)} placeholder="For non-emergency injuries" /></Field>
          <Field label="Clinic Address"><input style={S.input} value={form.clinicAddress} onChange={e => u("clinicAddress", e.target.value)} /></Field>
          <Field label="Safety Contact"><input style={S.input} value={form.coachName} onChange={e => u("coachName", e.target.value)} placeholder="e.g. Anai" /></Field>
          <Field label="Safety Contact Phone"><input style={S.input} type="tel" value={form.coachPhone} onChange={e => u("coachPhone", e.target.value)} /></Field>
        </div>
        {form.location && (
          <a href={`https://www.google.com/maps/search/emergency+room+near+${encodeURIComponent(form.location)}`}
            target="_blank" rel="noopener noreferrer"
            style={{ ...S.btnS, textDecoration: "none", display: "inline-flex", borderColor: C.info, color: C.info }}>
            Find nearest ER from job site ↗
          </a>
        )}
        <div style={{ marginTop: 12, fontSize: 12, color: C.muted, background: C.surface, borderRadius: 8, padding: 10, lineHeight: 1.6 }}>
          Life-threatening emergency: call <strong style={{ color: C.light }}>911</strong> first.
          Then notify the office at <strong style={{ color: C.light }}>(312) 312-5337</strong>.
          A fatality must be reported to OSHA within 8 hours; hospitalization, amputation, or loss of an eye within 24.
        </div>
      </div>

      {/* ── Site conditions ─────────────────────────────────────────────── */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: C.light }}>
          {lang === "es" ? "Condiciones del Sitio" : "Site Conditions"}
        </h3>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
          {lang === "es"
            ? "Marca lo que hay en el sitio y di DÓNDE está. Toma una foto del plano marcado si lo tienes."
            : "Tick what is on site and say WHERE it is. Photograph the marked-up site plan below if you have one."}
        </p>
        {SITE_HAZARDS.map(h => {
          const on = !!siteHazards[h.id];
          return (
            <div key={h.id} style={{ marginBottom: on ? 8 : 2 }}>
              <div onClick={() => setSiteHazards(p => ({ ...p, [h.id]: on ? undefined : { note: "" } }))}
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "7px 0", cursor: "pointer" }}>
                <span style={{
                  width: 22, height: 22, flexShrink: 0, borderRadius: 6,
                  border: on ? `2px solid ${h.danger ? C.danger : C.gold}` : `2px solid ${C.border}`,
                  background: on ? (h.danger ? C.danger : C.gold) : "transparent",
                  display: "grid", placeItems: "center",
                }}>
                  {on && <svg viewBox="0 0 24 24" style={{ width: 13, height: 13 }}><path d="M20 6 9 17l-5-5" fill="none" stroke={C.bg} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </span>
                <span style={{ fontSize: 13, color: on ? C.light : C.muted, lineHeight: 1.4 }}>
                  {h.label}{h.danger && <span style={{ color: C.danger }}> *</span>}
                </span>
              </div>
              {on && (
                <input style={{ ...S.input, marginLeft: 32, width: "calc(100% - 32px)", fontSize: 12.5, padding: "7px 10px", boxSizing: "border-box" }}
                  value={(siteHazards[h.id] || {}).note || ""}
                  onChange={ev => setSiteHazards(p => ({ ...p, [h.id]: { note: ev.target.value } }))}
                  placeholder={lang === "es" ? "¿Dónde? ¿Qué se hace al respecto?" : "Where is it? What are we doing about it?"} />
              )}
            </div>
          );
        })}
        <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
          <span style={{ color: C.danger }}>*</span> {lang === "es" ? "Peligro — explica el control en la tabla de arriba." : "Hazard — put the control in the hazard table above."}
        </div>
      </div>

      {/* ── Heat: water and break log ───────────────────────────────────── */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.light }}>
              {lang === "es" ? "Registro de Agua y Descansos" : "Water & Break Log"}
            </h3>
            <p style={{ color: C.muted, fontSize: 12, margin: "4px 0 0", lineHeight: 1.5 }}>
              {lang === "es" ? "Requerido cuando pasa de 90°F. Registra cada descanso de agua." : "Required when it goes over 90°F. Log each water break."}
            </p>
          </div>
          <button type="button" onClick={() => u("heatWatch", !form.heatWatch)} style={{
            padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit",
            border: `1px solid ${form.heatWatch ? C.warning : C.border}`,
            background: form.heatWatch ? `${C.warning}22` : "transparent",
            color: form.heatWatch ? C.warning : C.muted, whiteSpace: "nowrap",
          }}>{form.heatWatch ? (lang === "es" ? "Sobre 90°F" : "Over 90°F") : (lang === "es" ? "¿Sobre 90°F?" : "Over 90°F?")}</button>
        </div>
        {form.heatWatch && (
          <div style={{ marginTop: 14 }}>
            <button type="button" onClick={() => setWaterLog(p => [...p, { at: new Date().toISOString(), note: "" }])}
              style={{ ...S.btnS, width: "100%", justifyContent: "center", borderColor: C.info, color: C.info }}>
              {I.plus} {lang === "es" ? "Registrar descanso de agua (ahora)" : "Log a water break (now)"}
            </button>
            {waterLog.length === 0 && (
              <div style={{ fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
                {lang === "es" ? "Un cuarto de agua por persona por hora. Registra cada vez." : "One quart of water per person per hour. Log it each time."}
              </div>
            )}
            {waterLog.map((w, i) => (
              <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 8 }}>
                <span style={{ fontSize: 12.5, color: C.gold, fontWeight: 700, width: 70, flexShrink: 0 }}>{fmtJobTime(w.at)}</span>
                <input style={{ ...S.input, flex: 1, fontSize: 12.5, padding: "7px 10px" }} value={w.note}
                  onChange={ev => setWaterLog(p => p.map((x, j) => j === i ? { ...x, note: ev.target.value } : x))}
                  placeholder={lang === "es" ? "Quién / notas" : "Who / notes"} />
                <button type="button" onClick={() => setWaterLog(p => p.filter((_, j) => j !== i))}
                  style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Crew sign-off ───────────────────────────────────────────────── */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 4px", fontSize: 14, fontWeight: 700, color: C.light }}>
          {lang === "es" ? "Firma de la Cuadrilla" : "Crew Sign-Off"}
        </h3>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
          {lang === "es"
            ? "Cada persona en el sitio confirma que se le explicaron los peligros y el plan."
            : "Everyone on site confirms the hazards and the plan were explained to them."}
        </p>
        {crewAck.map((p, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8, flexWrap: "wrap" }}>
            <input style={{ ...S.input, flex: 2, minWidth: 120, fontSize: 13 }} value={p.name}
              onChange={ev => setCrewAck(prev => prev.map((x, j) => j === i ? { ...x, name: ev.target.value } : x))}
              placeholder={lang === "es" ? "Nombre" : "Name"} />
            <input style={{ ...S.input, width: 86, fontSize: 13, textTransform: "uppercase" }} value={p.initials}
              onChange={ev => setCrewAck(prev => prev.map((x, j) => j === i ? { ...x, initials: ev.target.value, at: ev.target.value.trim() ? (x.at || new Date().toISOString()) : "" } : x))}
              placeholder={lang === "es" ? "Iniciales" : "Initials"} />
            <span style={{ fontSize: 11, color: p.at ? C.success : C.muted, width: 62, flexShrink: 0 }}>
              {p.at ? fmtJobTime(p.at) : "—"}
            </span>
            <button type="button" onClick={() => setCrewAck(prev => prev.filter((_, j) => j !== i))}
              style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
          </div>
        ))}
        <button type="button" onClick={() => setCrewAck(p => [...p, { name: "", initials: "", at: "" }])}
          style={{ ...S.btnS, width: "100%", justifyContent: "center" }}>
          {I.plus} {lang === "es" ? "Agregar persona" : "Add a person"}
        </button>
      </div>

      <div style={S.card}><PhotoUploader photos={photos} setPhotos={setPhotos} label="Supporting Photos" /></div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8 }}>
        <button style={S.btnS} onClick={onBack}>Cancel</button>
        <button style={{ ...S.btnP, opacity: loading ? 0.7 : 1 }} onClick={submit} disabled={loading}>{loading ? "Submitting..." : <>{I.send} Submit JHA</>}</button>
      </div>
      <DraftStatus draft={draft} lang={lang} />
    </div>
  );
}

// ─── Site Survey Form ────────────────────────────────────────────────────────
function SurveyForm({ onSubmit, onBack, user, initialProjectId, initialVisitId }) {
  const lang = getLang();
  const [loading, setLoading] = useState(false);
  // Link to an already-scheduled job (e.g. from an Acuity booking) instead
  // of creating a duplicate project on submit.
  const [openProjects, setOpenProjects] = useState([]);
  const [linkedProjectId, setLinkedProjectId] = useState("");
  const projectsLoadedRef = useRef(false);
  if (!projectsLoadedRef.current) {
    projectsLoadedRef.current = true;
    db.getProjects().then(list => setOpenProjects(list.filter(p => p.status !== "completed"))).catch(() => {});
  }
  // Homeowner
  const [homeowner, setHomeowner] = useState({ name: "", address: "", drawingsAvailable: "" });
  // Pull across what the project already knows so nothing is retyped
  const applyLinkedProject = (proj) => setHomeowner(prev => ({
    ...prev,
    name: proj.client || proj.name || prev.name,
    address: proj.address || prev.address,
  }));
  // Opened from a job's briefing: link and fill once the job list arrives
  const initLinkRef = useRef(false);
  useEffect(() => {
    if (initLinkRef.current || !initialProjectId || openProjects.length === 0) return;
    initLinkRef.current = true;
    const proj = openProjects.find(x => x.id === initialProjectId);
    if (!proj) return;
    setLinkedProjectId(proj.id);
    applyLinkedProject(proj);
  }, [initialProjectId, openProjects]);
  const h = (f, v) => setHomeowner(p => ({...p, [f]: v}));
  // Photo checklist with per-item photo
  const [photoChecklist, setPhotoChecklist] = useState(PHOTO_CHECKLIST.map((p, i) => ({ id: i, label: p.label, desc: p.desc, descEs: p.descEs, checked: false, photos: [], na: false, naReason: "", uploaded: false, uploadedTo: "" })));
  // Meter
  const [meter, setMeter] = useState({ make: "", locationDesc: "", utilityEntrance: "", transformerVolt: "" });
  const m = (f, v) => setMeter(p => ({...p, [f]: v}));
  // Grounding
  const [grounding, setGrounding] = useState([]);
  const [groundingOther, setGroundingOther] = useState("");
  // Panel
  const [panel, setPanel] = useState({ brand: "", mainBreakerRating: "", busBarRating: "", voltageMeasured: "", voltageType: "", spacesAvailable: "", locationDesc: "" });
  const pn = (f, v) => setPanel(p => ({...p, [f]: v}));
  // Subpanel
  const [subpanel, setSubpanel] = useState({ exists: "", location: "", makeModel: "", mainBreakerRating: "", busBarRating: "", voltageMeasured: "", spacesAvailable: "", locationDesc: "" });
  const sp = (f, v) => setSubpanel(p => ({...p, [f]: v}));
  // Inverter
  const [inverterLocation, setInverterLocation] = useState("");
  // Comms
  const [comms, setComms] = useState({ modemPresent: "", modemLocation: "", openPorts: "", hardwireRun: "", wifiSignal: "", cellularCoverage: "" });
  const cm = (f, v) => setComms(p => ({...p, [f]: v}));
  // Interconnection
  const [interconnection, setInterconnection] = useState([]);
  const [interconnectionOther, setInterconnectionOther] = useState("");
  const [electricalNotes, setElectricalNotes] = useState("");
  // Roofing
  const [roofing, setRoofing] = useState({ make: "", condition: "", roofAccess: "", atticAccess: "" });
  const rf = (f, v) => setRoofing(p => ({...p, [f]: v}));
  // Roof structure
  const [roof, setRoof] = useState({ pitch: "", azimuth: "", buildingHeight: "", heightToEave: "", roofingType: "", roofingTypeOther: "", structureType: "", structureTypeOther: "", rafterSize: "", rafterSizeOther: "", rafterSpacing: "", rafterSpacingOther: "" });
  const ro = (f, v) => setRoof(p => ({...p, [f]: v}));
  // Per-array roof details. Most jobs have one array; some have three or four
  // on different roof faces, each with its own pitch and framing.
  const blankArray = (id) => ({
    id, roof: "", angle: "", azimuth: "", structureType: "", structureTypeOther: "",
    rafterSize: "", rafterSizeOther: "", rafterSpacing: "", rafterSpacingOther: "", notes: "", photos: [],
  });
  const [arrays, setArrays] = useState([blankArray(1)]);
  const arr = (id, field, v) => setArrays(prev => prev.map(a => a.id === id ? { ...a, [field]: v } : a));
  const addArray = () => setArrays(prev => [...prev, blankArray((prev.length ? Math.max(...prev.map(a => a.id)) : 0) + 1)]);
  const removeArray = (id) => {
    if (arrays.length === 1) return;
    if (!window.confirm(`Remove Array ${id}?`)) return;
    setArrays(prev => prev.filter(a => a.id !== id));
  };

  const handleArrayPhoto = async (id, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = "";
    for (const file of files) {
      const pid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setArrays(prev => prev.map(a => a.id === id
        ? { ...a, photos: [...(a.photos || []), { pid, name: file.name, url: "", uploading: true, stage: "compressing" }] } : a));

      let converted = file;
      try {
        converted = await db.convertToJpeg(file);
      } catch (err) {
        console.warn("Convert error:", err);
        if (err?.code === "HEIC_CONVERSION_FAILED") {
          setArrays(prev => prev.map(a => a.id === id ? { ...a, photos: (a.photos || []).map(ph => ph.pid === pid ? { ...ph, uploading: false, uploadFailed: true, name: file.name } : ph) } : a));
          alert(db.heicHelpText(file.name));
          continue;
        }
      }

      const previewUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = () => resolve("");
        reader.readAsDataURL(converted);
      });
      setArrays(prev => prev.map(a => a.id === id
        ? { ...a, photos: (a.photos || []).map(p => p.pid === pid ? { ...p, name: converted.name, url: previewUrl, stage: "uploading" } : p) } : a));

      const path = `tsa-photos/${Date.now()}-array${id}-${converted.name}`;
      try {
        const url = await db.uploadPhoto(converted, path);
        setArrays(prev => prev.map(a => a.id === id
          ? { ...a, photos: (a.photos || []).map(p => p.pid === pid ? { ...p, url, uploading: false, storagePath: path } : p) } : a));
      } catch (err) {
        console.warn("Array photo upload failed:", err);
        setArrays(prev => prev.map(a => a.id === id
          ? { ...a, photos: (a.photos || []).map(p => p.pid === pid ? { ...p, uploading: false, uploadFailed: true } : p) } : a));
      }
    }
  };

  const removeArrayPhoto = (id, pid) => setArrays(prev => prev.map(a => a.id === id
    ? { ...a, photos: (a.photos || []).filter(p => p.pid !== pid) } : a));

  const [structureDetails, setStructureDetails] = useState([]);
  const [structureDetailsOther, setStructureDetailsOther] = useState("");
  const [roofNotes, setRoofNotes] = useState("");
  // Adders
  const [adders, setAdders] = useState({ roofPitch: "", trenching: "", conduitRun: "", battery: "", rooftopAccess: "", rentals: "", roofAttachments: "", electricalUpgrades: "", groundingViolations: "", other: "" });
  const ad = (f, v) => setAdders(p => ({...p, [f]: v}));
  // General photos
  const [photos, setPhotos] = useState([]);

  const toggleArr = (val) => setGrounding(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleInterconn = (val) => setInterconnection(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);
  const toggleStructDet = (val) => setStructureDetails(prev => prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]);

  // An item is handled when it has a saved photo, or an explanation of why
  // there isn't one. "Done, no photo, no reason" is what this replaces.
  const itemHandled = (p) =>
    (p.photos || []).some(ph => ph.url && !ph.uploading && !ph.uploadFailed)
    || (p.uploaded && (p.uploadedTo || "").trim().length > 0)
    || (p.na && (p.naReason || "").trim().length > 0);
  const photoDone = photoChecklist.filter(itemHandled).length;
  const photoPct = Math.round((photoDone / photoChecklist.length) * 100);
  // The three states are exclusive: taken here, filed elsewhere, or not possible
  const setNa = (id, on) => setPhotoChecklist(prev => prev.map(c => c.id === id
    ? { ...c, na: on, naReason: on ? c.naReason : "", uploaded: on ? false : c.uploaded, uploadedTo: on ? "" : c.uploadedTo, checked: on ? !!(c.naReason || "").trim() : (c.photos || []).length > 0 }
    : c));
  const setNaReason = (id, v) => setPhotoChecklist(prev => prev.map(c => c.id === id ? { ...c, naReason: v, checked: !!v.trim() } : c));
  const setUploaded = (id, on) => setPhotoChecklist(prev => prev.map(c => c.id === id
    ? { ...c, uploaded: on, uploadedTo: on ? c.uploadedTo : "", na: on ? false : c.na, naReason: on ? "" : c.naReason, checked: on ? !!(c.uploadedTo || "").trim() : (c.photos || []).length > 0 }
    : c));
  const setUploadedTo = (id, v) => setPhotoChecklist(prev => prev.map(c => c.id === id ? { ...c, uploadedTo: v, checked: !!v.trim() } : c));

  // Handle photo upload for a checklist item
  // Multiple photos per checklist item. Each photo carries its own upload
  // state so one slow file doesn't make the others look stuck.
  const handleChecklistPhoto = async (id, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const item = photoChecklist.find(p => p.id === id);
    const safeLabel = (item?.label || "photo").replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
    e.target.value = "";

    for (const file of files) {
      const pid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

      // Placeholder appears immediately — HEIC decode plus resize takes a
      // couple of seconds on a phone and silence looks like a frozen app.
      setPhotoChecklist(prev => prev.map(p => p.id === id
        ? { ...p, checked: true, photos: [...(p.photos || []), { pid, name: file.name, url: "", uploading: true, stage: "compressing", label: item?.label }] }
        : p));

      let converted = file;
      try {
        converted = await db.convertToJpeg(file);
      } catch (err) {
        console.warn("Convert error:", err);
        if (err?.code === "HEIC_CONVERSION_FAILED") {
          setPhotoChecklist(prev => prev.map(p => p.id === id ? { ...p, photos: (p.photos || []).map(ph => ph.pid === pid ? { ...ph, uploading: false, uploadFailed: true, name: file.name } : ph) } : p));
          alert(db.heicHelpText(file.name));
          continue;
        }
      }

      // Local preview while the upload runs
      const previewUrl = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target.result);
        reader.onerror = () => resolve("");
        reader.readAsDataURL(converted);
      });
      setPhotoChecklist(prev => prev.map(p => p.id === id
        ? { ...p, photos: (p.photos || []).map(ph => ph.pid === pid ? { ...ph, name: converted.name, url: previewUrl, stage: "uploading" } : ph) }
        : p));

      const path = `tsa-photos/${Date.now()}-${safeLabel}-${converted.name}`;
      try {
        const uploadedUrl = await db.uploadPhoto(converted, path);
        setPhotoChecklist(prev => prev.map(p => p.id === id
          ? { ...p, photos: (p.photos || []).map(ph => ph.pid === pid ? { ...ph, url: uploadedUrl, uploading: false, storagePath: path } : ph) }
          : p));
      } catch (err) {
        console.warn("Photo upload failed:", err);
        setPhotoChecklist(prev => prev.map(p => p.id === id
          ? { ...p, photos: (p.photos || []).map(ph => ph.pid === pid ? { ...ph, uploading: false, uploadFailed: true } : ph) }
          : p));
      }
    }
  };

  const removeChecklistPhoto = (id, pid) => {
    setPhotoChecklist(prev => prev.map(p => {
      if (p.id !== id) return p;
      const photos = (p.photos || []).filter(ph => ph.pid !== pid);
      return { ...p, photos, checked: photos.length > 0 };
    }));
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────
  const Desc = ({ children }) => <div style={{ fontSize: 12, color: `${C.muted}aa`, marginBottom: 10, lineHeight: 1.5, fontStyle: "italic" }}>{children}</div>;
  const SectionTitle = ({ icon, title, subtitle }) => (
    <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 18 }}>
      <div style={{ width: 32, height: 32, borderRadius: 8, background: `${C.gold}18`, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, flexShrink: 0 }}>{icon}</div>
      <div><div style={{ fontSize: 15, fontWeight: 700, color: C.light }}>{title}</div>{subtitle && <div style={{ fontSize: 12, color: C.muted }}>{subtitle}</div>}</div>
    </div>
  );
  const RadioRow = ({ options, value, onChange }) => (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {options.map(opt => (
        <button key={opt} type="button" onClick={() => onChange(opt)} style={{ padding: "8px 16px", fontSize: 13, borderRadius: 8, border: `1px solid ${value === opt ? C.gold : C.border}`, background: value === opt ? `${C.gold}18` : "transparent", color: value === opt ? C.gold : C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: value === opt ? 600 : 400, transition: "all 0.15s" }}>{opt}</button>
      ))}
    </div>
  );
  const CheckOption = ({ label, checked, onChange }) => (
    <label style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", borderRadius: 8, cursor: "pointer", background: checked ? `${C.gold}08` : "transparent", border: `1px solid ${checked ? `${C.gold}30` : "transparent"}`, transition: "all 0.15s" }} onClick={onChange}>
      <div style={{ width: 20, height: 20, borderRadius: 5, border: checked ? `2px solid ${C.gold}` : `2px solid ${C.muted}44`, background: checked ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all 0.15s" }}>
        {checked && <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={C.bg} strokeWidth="3" strokeLinecap="round"/></svg>}
      </div>
      <span style={{ fontSize: 13, color: checked ? C.light : C.muted }}>{label}</span>
    </label>
  );

  const draft = useFormDraft({
    form: "survey", user, initialProjectId,
    projectId: linkedProjectId, projectName: openProjects.find(x => x.id === linkedProjectId)?.name || homeowner.name || "",
    fields: {
      homeowner: [homeowner, setHomeowner], photoChecklist: [photoChecklist, setPhotoChecklist], meter: [meter, setMeter],
      grounding: [grounding, setGrounding], groundingOther: [groundingOther, setGroundingOther], panel: [panel, setPanel], subpanel: [subpanel, setSubpanel],
      inverterLocation: [inverterLocation, setInverterLocation], comms: [comms, setComms], interconnection: [interconnection, setInterconnection],
      interconnectionOther: [interconnectionOther, setInterconnectionOther], electricalNotes: [electricalNotes, setElectricalNotes],
      roofing: [roofing, setRoofing], roof: [roof, setRoof], arrays: [arrays, setArrays], structureDetails: [structureDetails, setStructureDetails],
      structureDetailsOther: [structureDetailsOther, setStructureDetailsOther], roofNotes: [roofNotes, setRoofNotes], adders: [adders, setAdders],
      photos: [photos, setPhotos], linkedProjectId: [linkedProjectId, setLinkedProjectId],
    },
    isDirty: () => !!(homeowner.name || homeowner.address || photos.length || photoChecklist.some(p => (p.photos || []).length || p.na || p.uploaded)),
  });
  const submit = async () => {
    if (!homeowner.name || !homeowner.address) return alert("Please fill Homeowner Name and Address.");
    
    // Check if any photos are still uploading
    // Every checklist item needs a photo or a written reason. This is the
    // whole point of the change: "done" with neither tells you nothing later.
    const unexplained = photoChecklist.filter(p => !itemHandled(p));
    if (unexplained.length > 0) {
      const naNoReason = unexplained.filter(p => p.na);
      const upNoWhere = unexplained.filter(p => p.uploaded);
      const untouched = unexplained.filter(p => !p.na && !p.uploaded);
      return alert(
        (naNoReason.length > 0 ? `Add a reason for:\n\n• ${naNoReason.map(p => p.label).join("\n• ")}\n\n` : "") +
        (upNoWhere.length > 0 ? `Say where these were uploaded:\n\n• ${upNoWhere.map(p => p.label).join("\n• ")}\n\n` : "") +
        (untouched.length > 0 ? `These still need a photo, or "Uploaded to folder", or "Can't get this photo":\n\n• ${untouched.map(p => p.label).join("\n• ")}` : "")
      );
    }

    const stillUploading = photoChecklist.some(p => (p.photos || []).some(ph => ph.uploading))
      || arrays.some(a => (a.photos || []).some(ph => ph.uploading));
    if (stillUploading) return alert("Please wait - photos are still uploading.");
    const failedS = [...photoChecklist.flatMap(p => p.photos || []), ...arrays.flatMap(a => a.photos || [])].filter(ph => ph.uploadFailed || (ph.url || "").startsWith("data:"));
    if (failedS.length && !window.confirm(`${failedS.length} photo(s) did not upload (no signal?) and will NOT be on this survey:\n${failedS.map(p => "• " + (p.name || "photo")).join("\n")}\n\nTap Cancel, then remove and re-add them when you have signal. Submit without them?`)) return;
    
    setLoading(true);
    
    // Collect ALL photo URLs from checklist items and general uploader
    // Flatten every photo from every checklist item, keeping the item label
    // on each so the admin panel and PDF can group them.
    const checklistPhotos = [
      ...photoChecklist.flatMap(p =>
        (p.photos || [])
          .filter(ph => ph.url)
          .map(ph => ({ label: p.label, url: ph.url, name: ph.name, storagePath: ph.storagePath || null }))
      ),
      ...arrays.flatMap(a =>
        (a.photos || [])
          .filter(ph => ph.url)
          .map(ph => ({ label: `Array ${a.id}${a.roof ? ` — ${a.roof}` : ""}`, url: ph.url, name: ph.name, storagePath: ph.storagePath || null }))
      ),
    ];
    
    const uploadedChecklistPhotos = checklistPhotos.filter(p => !p.url.startsWith("data:"));
    const generalPhotoUrls = photos.map(p => p.url).filter(u => u && !u.startsWith("data:"));
    const allPhotoUrls = [...uploadedChecklistPhotos.map(p => p.url), ...generalPhotoUrls];

    // Build display photos array - this goes into local submissions for admin panel
    const allPhotosForDisplay = [
      ...checklistPhotos.map(p => ({ url: p.url, name: p.name, label: p.label })),
      ...photos.filter(p => p.url).map(p => ({ url: p.url, name: p.name })),
    ];

    const data = {
      type: "Site Survey", submittedAt: new Date().toISOString(), submittedBy: user?.name, status: "submitted",
      siteName: homeowner.name, address: homeowner.address, date: localDate(), inspector: user?.name,
      homeowner, photos: allPhotosForDisplay, photo_urls: allPhotoUrls,
      // photoUrl/photoName kept alongside photoUrls[] so older saved surveys
      // (single photo per item) still render in the admin panel and PDF.
      photoChecklist: photoChecklist.map(p => {
        const ph = (p.photos || []).filter(x => x.url && !String(x.url).startsWith("data:") && !x.uploadFailed);
        return {
          id: p.id, label: p.label, checked: p.checked,
          na: !!p.na, naReason: (p.naReason || "").trim(),
          uploaded: !!p.uploaded, uploadedTo: (p.uploadedTo || "").trim(),
          photoUrls: ph.map(x => x.url),
          photoUrl: ph[0]?.url || null, photoName: ph[0]?.name || null, storagePath: ph[0]?.storagePath || null,
        };
      }),
      checklist: photoChecklist.map(p => {
        const ph = (p.photos || []).filter(x => x.url && !String(x.url).startsWith("data:") && !x.uploadFailed);
        return {
          id: p.id, label: p.label, checked: p.checked,
          na: !!p.na, naReason: (p.naReason || "").trim(),
          uploaded: !!p.uploaded, uploadedTo: (p.uploadedTo || "").trim(),
          photoUrls: ph.map(x => x.url),
          photoUrl: ph[0]?.url || null, photoName: ph[0]?.name || null,
        };
      }),
      checklistData: { checklistPhotos: uploadedChecklistPhotos },
      arrays: arrays.map(a => ({
        id: a.id, roof: a.roof, angle: a.angle, azimuth: a.azimuth || "",
        structureType: a.structureType === "Other" ? (a.structureTypeOther || "Other") : (a.structureType || ""),
        rafterSize: a.rafterSize === "Other" ? (a.rafterSizeOther || "Other") : a.rafterSize,
        rafterSpacing: a.rafterSpacing === "Other" ? (a.rafterSpacingOther || "Other") : a.rafterSpacing,
        notes: a.notes,
        photoUrls: (a.photos || []).map(p => p.url).filter(u => u && !u.startsWith("data:")),
      })),
      meter, grounding, groundingOther, panel, subpanel, inverterLocation, comms,
      interconnection, interconnectionOther, electricalNotes, roofing, roof, structureDetails,
      structureDetailsOther, roofNotes, adders,
    };
    try {
      await db.submitSurvey({
        site_name: homeowner.name, address: homeowner.address, date: data.date, inspector: user?.name, weather: "", system_size: "",
        checklist: { 
          photoChecklist: data.photoChecklist, checklistPhotos: uploadedChecklistPhotos,
          meter, grounding, groundingOther, panel, subpanel, inverterLocation, comms, 
          interconnection, interconnectionOther, electricalNotes, roofing, roof, structureDetails, 
          structureDetailsOther, roofNotes, adders, arrays: data.arrays,
          visitId: initialVisitId || "",
        },
        notes: (electricalNotes || "") + " " + (roofNotes || ""), 
        photo_urls: allPhotoUrls,
        status: "submitted", project_id: linkedProjectId || null,
      }, user?.id);
    } catch (e) { console.warn(e); }
    db.sendNotification("TSA Survey", homeowner.name, user?.name);
    if (linkedProjectId) {
      db.linkFormToProject(linkedProjectId, { formType: "TSA Survey", userName: user?.name, visitId: initialVisitId || "" });
    } else {
      db.createWorkflowProject({ clientName: homeowner.name, address: homeowner.address, formType: "TSA Survey", userName: user?.name });
    }
    setLoading(false);
    draft.clear();
    onSubmit(data);
  };
  return (
    <div style={{ maxWidth: 800, animation: "fadeIn 0.25s ease" }}>
      <DraftBanner draft={draft} lang={lang} />
      <button onClick={onBack} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} Back</button>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: C.infoBg, display: "flex", alignItems: "center", justifyContent: "center", color: C.info }}>{I.clip}</div>
        <div><h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>TSA Site Survey</h2><p style={{ margin: 0, color: C.muted, fontSize: 13 }}>Complete solar site assessment — SolarAbility LLC</p></div>
      </div>

      {/* Link to an already-scheduled job so this doesn't create a duplicate project */}
      {openProjects.length > 0 && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <Field label="Link to Existing Job">
            <JobPicker projects={openProjects} value={linkedProjectId} lang={lang} onPick={id => {
              setLinkedProjectId(id);
              const proj = openProjects.find(x => x.id === id);
              if (proj) applyLinkedProject(proj);
            }} />
          </Field>
          {linkedProjectId && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
              Details filled in from this job. Submitting updates it instead of creating a new project.
            </div>
          )}
        </div>
      )}

      {/* ── 1. Homeowner ──────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.user} title="Homeowner / Site Information" subtitle="Basic info about the customer and property" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Homeowner Name" required><Desc>Full legal name of the property owner</Desc><input style={S.input} placeholder="e.g. John Smith" value={homeowner.name} onChange={e => h("name", e.target.value)} /></Field>
          <Field label="Address" required><Desc>Full street address including city, state, zip</Desc><input style={S.input} placeholder="e.g. 123 Main St, Springfield, IL 62701" value={homeowner.address} onChange={e => h("address", e.target.value)} /></Field>
        </div>
        <Field label="Drawings Available?"><Desc>Are architectural or structural drawings available for this property?</Desc>
          <RadioRow options={["Yes", "No"]} value={homeowner.drawingsAvailable} onChange={v => h("drawingsAvailable", v)} />
        </Field>
      </div>

      {/* ── 2. Photo Checklist ────────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.cam} title="Photo Checklist" subtitle="Take or upload a photo for each item" />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>{photoDone}/{photoChecklist.length} photos</span>
          <div style={{ flex: 1, height: 6, background: C.surface, borderRadius: 3, overflow: "hidden" }}>
            <div style={{ width: `${photoPct}%`, height: "100%", background: photoPct === 100 ? C.success : C.gold, borderRadius: 3, transition: "width 0.3s" }} />
          </div>
          <span style={{ fontSize: 12, color: photoPct === 100 ? C.success : C.gold, fontWeight: 700 }}>{photoPct}%</span>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {photoChecklist.map(item => (
            <div key={item.id} style={{ background: item.checked ? `${C.gold}08` : C.surface, borderRadius: 10, border: `1px solid ${item.checked ? `${C.gold}30` : C.border}`, padding: "12px 14px", transition: "all 0.15s" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, border: item.checked ? `2px solid ${C.gold}` : `2px solid ${C.muted}44`, background: item.checked ? C.gold : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, transition: "all 0.15s" }}>
                  {item.checked && <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={C.bg} strokeWidth="3" strokeLinecap="round"/></svg>}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: item.checked ? C.light : C.muted }}>{item.label}</div>
                  <div style={{ fontSize: 11, color: `${C.muted}99`, marginTop: 2, lineHeight: 1.4 }}>{lang === "es" && item.descEs ? item.descEs : item.desc}</div>
                  {(() => {
                    const photos = item.photos || [];
                    const busy = photos.some(ph => ph.uploading);
                    const savedCount = photos.filter(ph => !ph.uploading && !ph.uploadFailed && !(ph.url || "").startsWith("data:")).length;
                    const failedCount = photos.filter(ph => ph.uploadFailed).length;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
                        <label style={{ ...S.btnS, padding: "6px 14px", fontSize: 12, cursor: busy ? "wait" : "pointer", borderColor: photos.length > 0 && !busy ? C.success : C.border, color: photos.length > 0 && !busy ? C.success : C.muted, opacity: busy ? 0.6 : 1 }}>
                          {busy
                            ? (photos.some(ph => ph.stage === "compressing") ? "⏳ Compressing..." : "⏳ Uploading...")
                            : <>{I.cam} {photos.length > 0 ? "Add Another" : "Upload Photo"}</>}
                          <input type="file" accept="image/*" multiple onChange={(e) => handleChecklistPhoto(item.id, e)} style={{ display: "none" }} disabled={busy} />
                        </label>
                        {savedCount > 0 && (
                          <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>✓ {savedCount} saved</span>
                        )}
                        {failedCount > 0 && (
                          <span style={{ fontSize: 11, color: C.warning, fontWeight: 600 }}>⚠ {failedCount} failed to upload</span>
                        )}
                        {photos.length === 0 && !item.na && !item.uploaded && (
                          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                            <button type="button" style={{ background: "none", border: "none", color: C.info, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: 0 }}
                              onClick={() => setUploaded(item.id, true)}>{lang === "es" ? "Ya subida a carpeta" : "Uploaded to folder"}</button>
                            <button type="button" style={{ background: "none", border: "none", color: C.muted, fontSize: 12, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline", padding: 0 }}
                              onClick={() => setNa(item.id, true)}>{lang === "es" ? "No se pudo tomar" : "Can't get this photo"}</button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
              {item.uploaded && (
                <div style={{ marginTop: 10, marginLeft: 34, padding: 10, background: `${C.info}10`, border: `1px solid ${(item.uploadedTo || "").trim() ? `${C.info}55` : C.danger}`, borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 7 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.info }}>
                      {lang === "es" ? "¿A dónde se subió?" : "Uploaded where?"} <span style={{ color: C.danger }}>*</span>
                    </div>
                    <button type="button" onClick={() => setUploaded(item.id, false)}
                      style={{ background: "none", border: "none", color: C.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                      {lang === "es" ? "Cancelar" : "Cancel"}
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 7 }}>
                    {["Scanifly", "Google Drive", lang === "es" ? "Portal del socio" : "Partner portal", lang === "es" ? "Carpeta compartida" : "Shared folder"].map(r => (
                      <button key={r} type="button" onClick={() => setUploadedTo(item.id, r)} style={{
                        padding: "5px 10px", borderRadius: 14, cursor: "pointer", fontSize: 11, fontFamily: "inherit",
                        border: `1px solid ${item.uploadedTo === r ? C.info : C.border}`,
                        background: item.uploadedTo === r ? `${C.info}22` : "transparent",
                        color: item.uploadedTo === r ? C.info : C.muted,
                      }}>{r}</button>
                    ))}
                  </div>
                  <input style={{ ...S.input, fontSize: 13 }} value={item.uploadedTo || ""} onChange={e => setUploadedTo(item.id, e.target.value)}
                    placeholder={lang === "es" ? "Nombre de la carpeta o sistema..." : "Folder or system name..."} />
                </div>
              )}

              {item.na && (
                <div style={{ marginTop: 10, marginLeft: 34, padding: 10, background: `${C.warning}10`, border: `1px solid ${(item.naReason || "").trim() ? `${C.warning}55` : C.danger}`, borderRadius: 8 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 7 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: C.warning }}>
                      {lang === "es" ? "¿Por qué no hay foto?" : "Why is there no photo?"} <span style={{ color: C.danger }}>*</span>
                    </div>
                    <button type="button" onClick={() => setNa(item.id, false)}
                      style={{ background: "none", border: "none", color: C.muted, fontSize: 11, cursor: "pointer", fontFamily: "inherit", textDecoration: "underline" }}>
                      {lang === "es" ? "Cancelar" : "Cancel"}
                    </button>
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 7 }}>
                    {(lang === "es"
                      ? ["No existe en el sitio", "Sin acceso", "No es seguro subir", "Bloqueado / con llave", "Cliente no permitió"]
                      : ["Not present on site", "No access", "Unsafe to reach", "Locked or blocked", "Homeowner declined"]
                    ).map(r => (
                      <button key={r} type="button" onClick={() => setNaReason(item.id, r)} style={{
                        padding: "5px 10px", borderRadius: 14, cursor: "pointer", fontSize: 11, fontFamily: "inherit",
                        border: `1px solid ${item.naReason === r ? C.warning : C.border}`,
                        background: item.naReason === r ? `${C.warning}22` : "transparent",
                        color: item.naReason === r ? C.warning : C.muted,
                      }}>{r}</button>
                    ))}
                  </div>
                  <textarea style={{ ...S.textarea, minHeight: 40, fontSize: 13 }} rows={2}
                    value={item.naReason || ""} onChange={e => setNaReason(item.id, e.target.value)}
                    placeholder={lang === "es" ? "Explica por qué, o da los detalles que tengas..." : "Explain why, or give the details you do have..."} />
                </div>
              )}

              {(item.photos || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10, paddingLeft: 34 }}>
                  {(item.photos || []).map(ph => (
                    <div key={ph.pid} style={{ position: "relative", flexShrink: 0 }}>
                      {ph.url
                        ? <img loading="lazy" decoding="async" src={ph.url} alt={item.label} onClick={() => openPhoto(ph.url)} style={{ cursor: "zoom-in",  width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${ph.uploadFailed ? C.danger : C.border}`, opacity: ph.uploading ? 0.5 : 1 }} />
                        : <div style={{ width: 64, height: 64, borderRadius: 8, border: `1px dashed ${C.gold}`, display: "flex", alignItems: "center", justifyContent: "center", background: C.surface, fontSize: 14 }}>⏳</div>}
                      <button onClick={() => removeChecklistPhoto(item.id, ph.pid)}
                        style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: C.danger, border: "none", color: "#fff", cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── 3. Meter / Service Entrance ───────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.bolt} title="Electrical Meter / Service Entrance" subtitle="Document the existing utility meter and service entrance" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Meter Make / Model"><Desc>Found on the face of the meter (e.g. Itron C1S)</Desc><input style={S.input} value={meter.make} onChange={e => m("make", e.target.value)} /></Field>
          <Field label="Location Description"><Desc>Where on the property the meter is mounted</Desc><input style={S.input} placeholder="e.g. East side of garage" value={meter.locationDesc} onChange={e => m("locationDesc", e.target.value)} /></Field>
        </div>
        <Field label="Utility Entrance"><Desc>How the utility power enters the property</Desc>
          <RadioRow options={["Underground", "Overhead"]} value={meter.utilityEntrance} onChange={v => m("utilityEntrance", v)} />
        </Field>
        <Field label="Transformer Voltage / Voltage Required"><Desc>Check with the utility or measure at the service entrance</Desc>
          <input style={S.input} value={meter.transformerVolt} onChange={e => m("transformerVolt", e.target.value)} />
        </Field>
        <Field label="Existing Service Grounding"><Desc>Check all grounding methods visible at the service entrance</Desc>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {GROUNDING_OPTIONS.map(opt => <CheckOption key={opt} label={opt} checked={grounding.includes(opt)} onChange={() => toggleArr(opt)} />)}
            <input style={{ ...S.input, marginTop: 4, fontSize: 13 }} placeholder="If Other, describe..." value={groundingOther} onChange={e => setGroundingOther(e.target.value)} />
          </div>
        </Field>
      </div>

      {/* ── 4. Electrical Panel ───────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.bolt} title="Electrical Panel" subtitle="Main electrical panel specifications" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Main Panel Brand"><Desc>Brand name on the panel cover or label</Desc><input style={S.input} placeholder="e.g. Square D, Siemens, Eaton" value={panel.brand} onChange={e => pn("brand", e.target.value)} /></Field>
          <Field label="Main Breaker Rating (A)"><Desc>Amperage printed on the main breaker handle</Desc><input style={S.input} placeholder="e.g. 200" value={panel.mainBreakerRating} onChange={e => pn("mainBreakerRating", e.target.value)} /></Field>
          <Field label="Bus Bar Rating (A)"><Desc>Found on the panel label — may differ from breaker</Desc><input style={S.input} placeholder="e.g. 200" value={panel.busBarRating} onChange={e => pn("busBarRating", e.target.value)} /></Field>
          <Field label="Voltage (Measured)"><Desc>Measure with a multimeter at the bus bar</Desc><input style={S.input} placeholder="e.g. 242V" value={panel.voltageMeasured} onChange={e => pn("voltageMeasured", e.target.value)} /></Field>
          <Field label="Voltage Type"><RadioRow options={["240V Single", "240V 3-Phase", "208V 3-Phase"]} value={panel.voltageType} onChange={v => pn("voltageType", v)} /></Field>
          <Field label="Spaces Available"><Desc>Count empty breaker slots in the panel</Desc><input style={S.input} placeholder="e.g. 4" value={panel.spacesAvailable} onChange={e => pn("spacesAvailable", e.target.value)} /></Field>
          <Field label="Location Description"><Desc>Where the panel is located</Desc><input style={S.input} placeholder="e.g. Basement, west wall" value={panel.locationDesc} onChange={e => pn("locationDesc", e.target.value)} /></Field>
        </div>
      </div>

      {/* ── 5. Subpanel ──────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.bolt} title="Subpanel" subtitle="If a subpanel exists on the property" />
        <Field label="Subpanel Exists?"><RadioRow options={["Yes", "No"]} value={subpanel.exists} onChange={v => sp("exists", v)} /></Field>
        {subpanel.exists === "Yes" && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginTop: 14 }}>
            <Field label="Location"><input style={S.input} value={subpanel.location} onChange={e => sp("location", e.target.value)} /></Field>
            <Field label="Make / Model"><input style={S.input} value={subpanel.makeModel} onChange={e => sp("makeModel", e.target.value)} /></Field>
            <Field label="Main Breaker Rating"><input style={S.input} value={subpanel.mainBreakerRating} onChange={e => sp("mainBreakerRating", e.target.value)} /></Field>
            <Field label="Bus Bar Rating"><input style={S.input} value={subpanel.busBarRating} onChange={e => sp("busBarRating", e.target.value)} /></Field>
            <Field label="Voltage (Measured)"><input style={S.input} value={subpanel.voltageMeasured} onChange={e => sp("voltageMeasured", e.target.value)} /></Field>
            <Field label="Spaces Available"><input style={S.input} value={subpanel.spacesAvailable} onChange={e => sp("spacesAvailable", e.target.value)} /></Field>
            <div style={{ gridColumn: "1/-1" }}><Field label="Location Description"><input style={S.input} value={subpanel.locationDesc} onChange={e => sp("locationDesc", e.target.value)} /></Field></div>
          </div>
        )}
      </div>

      {/* ── 6. Inverter & Communications ──────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.sun} title="Inverter & Communications" subtitle="Inverter placement and internet connectivity for monitoring" />
        <Field label="Proposed Inverter Location"><Desc>Where the inverter will be mounted — ideally near the panel, shaded, and accessible</Desc>
          <input style={S.input} placeholder="e.g. North wall of garage, next to panel" value={inverterLocation} onChange={e => setInverterLocation(e.target.value)} />
        </Field>
        <Field label="Modem/Router Present?"><RadioRow options={["Yes", "No"]} value={comms.modemPresent} onChange={v => cm("modemPresent", v)} /></Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Modem/Router Location"><Desc>Where in the home the internet equipment is</Desc><input style={S.input} value={comms.modemLocation} onChange={e => cm("modemLocation", e.target.value)} /></Field>
          <Field label="Open Ports on Router?"><Desc>Available ethernet ports for hardwire connection</Desc><input style={S.input} value={comms.openPorts} onChange={e => cm("openPorts", e.target.value)} /></Field>
          <Field label="Hardwire: Proposed Cable Run"><Desc>Estimated distance from router to inverter for ethernet</Desc><input style={S.input} value={comms.hardwireRun} onChange={e => cm("hardwireRun", e.target.value)} /></Field>
          <Field label="Wi-Fi Signal at Inverter"><Desc>Test Wi-Fi signal strength at proposed inverter location</Desc><input style={S.input} placeholder="e.g. Strong / Weak / None" value={comms.wifiSignal} onChange={e => cm("wifiSignal", e.target.value)} /></Field>
          <Field label="Cellular: Verified Coverage?"><Desc>Can a cell signal reach the inverter location?</Desc><input style={S.input} placeholder="e.g. Yes - 3 bars LTE" value={comms.cellularCoverage} onChange={e => cm("cellularCoverage", e.target.value)} /></Field>
        </div>
      </div>

      {/* ── 7. Interconnection ────────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.bolt} title="Interconnection Strategy" subtitle="How the solar system will connect to the existing electrical" />
        <Desc>Select all methods that apply to this installation</Desc>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, marginBottom: 14 }}>
          {INTERCONNECTION_OPTIONS.map(opt => <CheckOption key={opt} label={opt} checked={interconnection.includes(opt)} onChange={() => toggleInterconn(opt)} />)}
          <input style={{ ...S.input, marginTop: 4, fontSize: 13 }} placeholder="Other method..." value={interconnectionOther} onChange={e => setInterconnectionOther(e.target.value)} />
        </div>
        <Field label="Electrical Notes"><Desc>Any additional observations about the electrical system, code concerns, or recommendations</Desc>
          <textarea style={{ ...S.textarea, minHeight: 90 }} value={electricalNotes} onChange={e => setElectricalNotes(e.target.value)} placeholder="e.g. Panel is outdated, recommend upgrade to 200A..." />
        </Field>
      </div>

      {/* ── 8. Roof — whole house. Per-plane details (pitch, azimuth, framing)
          live in Arrays below; they were asked in both places before. ──── */}
      <div style={S.card}>
        <SectionTitle icon={I.shield} title="Roof — Whole House" subtitle="Material, condition, access, and height" />
        <Field label="Roofing Type"><Desc>Main roofing material. If a plane differs (e.g. metal garage), note it on that array.</Desc>
          <RadioRow options={ROOFING_TYPES} value={roof.roofingType} onChange={v => ro("roofingType", v)} />
          <input style={{ ...S.input, marginTop: 8 }} placeholder="Other type..." value={roof.roofingTypeOther} onChange={e => ro("roofingTypeOther", e.target.value)} />
        </Field>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Roofing Make / Manufacturer"><Desc>Brand of shingles or roofing material if identifiable</Desc><input style={S.input} value={roofing.make} onChange={e => rf("make", e.target.value)} /></Field>
          <Field label="Roof Condition"><Desc>Good, Fair, Poor — note any damage or wear</Desc><input style={S.input} placeholder="e.g. Good - 5 years old" value={roofing.condition} onChange={e => rf("condition", e.target.value)} /></Field>
          <Field label="Roof Access?"><RadioRow options={["Yes", "No"]} value={roofing.roofAccess} onChange={v => rf("roofAccess", v)} /></Field>
          <Field label="Attic Access?"><RadioRow options={["Yes", "No"]} value={roofing.atticAccess} onChange={v => rf("atticAccess", v)} /></Field>
          <Field label="Building Height"><Desc>Ground to roof peak</Desc><input style={S.input} value={roof.buildingHeight} onChange={e => ro("buildingHeight", e.target.value)} /></Field>
          <Field label="Height (Ground to Eave)"><Desc>Ground to the bottom edge of the roof</Desc><input style={S.input} value={roof.heightToEave} onChange={e => ro("heightToEave", e.target.value)} /></Field>
        </div>
        <Field label="Additional Structure Details"><Desc>Check all that apply</Desc>
          <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
            {STRUCTURE_DETAILS.map(opt => <CheckOption key={opt} label={opt} checked={structureDetails.includes(opt)} onChange={() => toggleStructDet(opt)} />)}
            <input style={{ ...S.input, marginTop: 4, fontSize: 13 }} placeholder="Other details..." value={structureDetailsOther} onChange={e => setStructureDetailsOther(e.target.value)} />
          </div>
        </Field>
        <Field label="Additional Roof Notes"><textarea style={S.textarea} value={roofNotes} onChange={e => setRoofNotes(e.target.value)} placeholder="Any other roof or structural observations..." /></Field>
      </div>

      {/* ── Arrays ───────────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.clip} title="Arrays" subtitle="One per roof plane — pitch, direction, and framing can differ on each" />
        {arrays.map((a, idx) => (
          <div key={a.id} style={{ padding: 14, background: C.surface, borderRadius: 10, marginBottom: 10, border: `1px solid ${C.border}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: C.gold }}>Array {a.id}</div>
              {arrays.length > 1 && (
                <button onClick={() => removeArray(a.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 4 }}>{I.trash}</button>
              )}
            </div>

            <Field label="Roof Plane">
              <Desc>Which part of the roof — e.g. South face main house, rear garage</Desc>
              <input style={S.input} value={a.roof} onChange={e => arr(a.id, "roof", e.target.value)} placeholder="e.g. South face, main house" />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Pitch">
                <Desc>Angle finder or pitch app</Desc>
                <input style={S.input} value={a.angle} onChange={e => arr(a.id, "angle", e.target.value)} placeholder="e.g. 22° or 5/12" />
              </Field>
              <Field label="Azimuth">
                <Desc>Direction it faces (180° = South)</Desc>
                <input style={S.input} value={a.azimuth || ""} onChange={e => arr(a.id, "azimuth", e.target.value)} placeholder="e.g. 185°" />
              </Field>
            </div>
            <Field label="Structure Type">
              <Desc>Seen from the attic — rafters or trusses. Can differ between house and garage.</Desc>
              <select style={S.select} value={a.structureType || ""} onChange={e => arr(a.id, "structureType", e.target.value)}>
                <option value="">—</option>
                {STRUCTURE_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
                <option value="Other">Other</option>
              </select>
              {a.structureType === "Other" && <input style={{ ...S.input, marginTop: 8 }} placeholder="Describe..." value={a.structureTypeOther || ""} onChange={e => arr(a.id, "structureTypeOther", e.target.value)} />}
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Rafter / Truss Size">
                <Desc>Typical residential: 2x6 or 2x8</Desc>
                <select style={S.select} value={a.rafterSize} onChange={e => arr(a.id, "rafterSize", e.target.value)}>
                  <option value="">—</option>
                  {RAFTER_SIZES.map(o => <option key={o} value={o}>{o}</option>)}
                  <option value="Other">Other</option>
                </select>
                {a.rafterSize === "Other" && <input style={{ ...S.input, marginTop: 8 }} placeholder="Size..." value={a.rafterSizeOther || ""} onChange={e => arr(a.id, "rafterSizeOther", e.target.value)} />}
              </Field>
              <Field label="Spacing">
                <Desc>Measure across 3 rafters, divide by 2</Desc>
                <select style={S.select} value={a.rafterSpacing} onChange={e => arr(a.id, "rafterSpacing", e.target.value)}>
                  <option value="">—</option>
                  {RAFTER_SPACINGS.map(o => <option key={o} value={o}>{o}</option>)}
                  <option value="Other">Other</option>
                </select>
                {a.rafterSpacing === "Other" && <input style={{ ...S.input, marginTop: 8 }} placeholder="Spacing..." value={a.rafterSpacingOther || ""} onChange={e => arr(a.id, "rafterSpacingOther", e.target.value)} />}
              </Field>
            </div>

            <Field label="Notes">
              <input style={S.input} value={a.notes} onChange={e => arr(a.id, "notes", e.target.value)} placeholder="Obstructions, shading, anything unusual on this plane" />
            </Field>

            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <label style={{ ...S.btnS, padding: "6px 14px", fontSize: 12, cursor: (a.photos || []).some(p => p.uploading) ? "wait" : "pointer", borderColor: (a.photos || []).length > 0 ? C.success : C.border, color: (a.photos || []).length > 0 ? C.success : C.muted }}>
                {(a.photos || []).some(p => p.uploading)
                  ? ((a.photos || []).some(p => p.stage === "compressing") ? "⏳ Compressing..." : "⏳ Uploading...")
                  : <>{I.cam} {(a.photos || []).length > 0 ? "Add Another" : "Array Photos"}</>}
                <input type="file" accept="image/*" multiple onChange={(e) => handleArrayPhoto(a.id, e)} style={{ display: "none" }} disabled={(a.photos || []).some(p => p.uploading)} />
              </label>
              {(a.photos || []).filter(p => !p.uploading && !p.uploadFailed && p.url && !p.url.startsWith("data:")).length > 0 && (
                <span style={{ fontSize: 11, color: C.success, fontWeight: 600 }}>
                  ✓ {(a.photos || []).filter(p => !p.uploading && !p.uploadFailed && p.url && !p.url.startsWith("data:")).length} saved
                </span>
              )}
            </div>

            {(a.photos || []).length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 }}>
                {(a.photos || []).map(p => (
                  <div key={p.pid} style={{ position: "relative", flexShrink: 0 }}>
                    {p.url
                      ? <img loading="lazy" decoding="async" src={p.url} alt={`Array ${a.id}`} onClick={() => openPhoto(p.url)} style={{ cursor: "zoom-in",  width: 64, height: 64, objectFit: "cover", borderRadius: 8, border: `1px solid ${p.uploadFailed ? C.danger : C.border}`, opacity: p.uploading ? 0.5 : 1 }} />
                      : <div style={{ width: 64, height: 64, borderRadius: 8, border: `1px dashed ${C.gold}`, display: "flex", alignItems: "center", justifyContent: "center", background: C.card, fontSize: 14 }}>⏳</div>}
                    <button onClick={() => removeArrayPhoto(a.id, p.pid)}
                      style={{ position: "absolute", top: -6, right: -6, width: 20, height: 20, borderRadius: "50%", background: C.danger, border: "none", color: "#fff", cursor: "pointer", fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <button onClick={addArray} style={{ ...S.btnS, width: "100%", justifyContent: "center", borderColor: C.gold, color: C.gold }}>
          {I.plus} Add Array {arrays.length + 1}
        </button>
      </div>

      {/* ── 10. Adders ────────────────────────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.plus} title="Adders Checklist" subtitle="Additional factors that may affect installation cost" />
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Steep Roof Adder"><Desc>Any array over 30° needing extra safety gear or labor? Which one — pitch is measured per array above</Desc><input style={S.input} placeholder="e.g. Yes — Array 2 (35°)" value={adders.roofPitch} onChange={e => ad("roofPitch", e.target.value)} /></Field>
          <Field label="Trenching & Distance"><Desc>If underground conduit is needed, estimate total distance</Desc><input style={S.input} placeholder="e.g. 50 ft to detached garage" value={adders.trenching} onChange={e => ad("trenching", e.target.value)} /></Field>
          <Field label="Conduit Run Distance"><Desc>Total length of conduit from array to interconnection point</Desc><input style={S.input} placeholder="e.g. 75 ft" value={adders.conduitRun} onChange={e => ad("conduitRun", e.target.value)} /></Field>
          <Field label="Battery Integrations"><Desc>Will batteries be added? Type and quantity</Desc><input style={S.input} placeholder="e.g. 2x Tesla Powerwall" value={adders.battery} onChange={e => ad("battery", e.target.value)} /></Field>
          <Field label="Access to Rooftop or Attic"><Desc>Any access restrictions or challenges?</Desc><input style={S.input} placeholder="e.g. Easy, ladder access from driveway" value={adders.rooftopAccess} onChange={e => ad("rooftopAccess", e.target.value)} /></Field>
          <Field label="Rentals?"><Desc>Equipment rentals needed? (boom lift, scaffolding, etc.)</Desc><input style={S.input} placeholder="e.g. Boom lift needed" value={adders.rentals} onChange={e => ad("rentals", e.target.value)} /></Field>
          <Field label="Roof Attachments"><Desc>Type: flat roof, rolled shingle, metal/plastic</Desc><input style={S.input} value={adders.roofAttachments} onChange={e => ad("roofAttachments", e.target.value)} /></Field>
          <Field label="Electrical Upgrades Apparent?"><Desc>Does the existing system need upgrading for solar?</Desc><input style={S.input} placeholder="e.g. Panel upgrade to 200A needed" value={adders.electricalUpgrades} onChange={e => ad("electricalUpgrades", e.target.value)} /></Field>
          <Field label="Grounding / Code Violations"><Desc>Note any code issues discovered during survey</Desc><input style={S.input} value={adders.groundingViolations} onChange={e => ad("groundingViolations", e.target.value)} /></Field>
          <Field label="Other(s)"><Desc>Any other cost factors not listed above</Desc><input style={S.input} value={adders.other} onChange={e => ad("other", e.target.value)} /></Field>
        </div>
      </div>

      {/* ── 11. Additional Photos & Submit ────────────────────────────────────── */}
      <div style={S.card}>
        <SectionTitle icon={I.cam} title="Additional Site Photos" subtitle="Any extra photos not covered by the checklist above" />
        <PhotoUploader photos={photos} setPhotos={setPhotos} label="Upload additional photos" />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 8, marginBottom: 40 }}>
        <button style={S.btnS} onClick={onBack}>Cancel</button>
        <button style={{ ...S.btnP, opacity: loading ? 0.7 : 1 }} onClick={submit} disabled={loading}>{loading ? "Submitting..." : <>{I.send} Submit TSA Survey</>}</button>
      </div>
      <DraftStatus draft={draft} lang={lang} />
    </div>
  );
}

// ─── Service Assessment Form ─────────────────────────────────────────────────
const SERVICE_TYPES = [
  { id: "leak", label: "Leak Assessment", labelEs: "Evaluación de Fuga" },
  { id: "production", label: "Production Issue", labelEs: "Problema de Producción" },
  { id: "inspection", label: "System Inspection", labelEs: "Inspección del Sistema" },
  { id: "electrical", label: "Electrical Issue", labelEs: "Problema Eléctrico" },
  { id: "om", label: "General O&M", labelEs: "O&M General" },
];

function ServiceAssessment({ onSubmit, onBack, user, initialProjectId, initialVisitId, mode = "service" }) {
  // mode="install": the same form, but locked to the Solar Installation
  // closeout photo checklist and without the service-call sections.
  const isInstall = mode === "install";
  const lang = getLang();
  const t = T[lang] || T.en;
  const [loading, setLoading] = useState(false);
  const [serviceType, setServiceType] = useState(isInstall ? "install" : "leak");
  const [client, setClient] = useState({ name: "", address: "", phone: "", email: "" });
  // Actual time on site. Recorded every visit so budget vs reality is evidence,
  // not an impression, when a scope's paid hours need renegotiating.
  const [timeOnSite, setTimeOnSite] = useState({ arrived: "", departed: "", techs: "" });
  const actualHours = (() => {
    const { arrived, departed, techs } = timeOnSite;
    if (!arrived || !departed) return null;
    const [ah, am] = arrived.split(":").map(Number);
    const [dh, dm] = departed.split(":").map(Number);
    if ([ah, am, dh, dm].some(n => isNaN(n))) return null;
    let mins = (dh * 60 + dm) - (ah * 60 + am);
    if (mins < 0) mins += 24 * 60; // crossed midnight
    const crew = Math.max(1, parseInt(techs, 10) || 1);
    return { mins, crew, manHours: Math.round((mins / 60) * crew * 100) / 100 };
  })();
  const [referral, setReferral] = useState({ source: "", name: "" });
  const [system, setSystem] = useState({ size: "", installer: "", installDate: "", panelMake: "", inverterMake: "", numPanels: "" });
  const [issue, setIssue] = useState("");
  const [findings, setFindings] = useState("");
  const [rootCause, setRootCause] = useState("");
  const [recommendations, setRecommendations] = useState("");
  const [fee, setFee] = useState("200");
  const [photos, setPhotos] = useState([]);
  const [partners, setPartners] = useState([]);
  const [partnerId, setPartnerId] = useState("");
  const [partnerFieldValues, setPartnerFieldValues] = useState({});
  // Labeled photo slots for partners that specify required photos.
  // { [slotId]: [{ pid, url, name, uploading, stage, uploadFailed }] }
  const [slotPhotos, setSlotPhotos] = useState({});

  const handleSlotPhoto = async (slotId, slotLabel, e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = "";
    const safe = (slotLabel || "photo").replace(/[^a-zA-Z0-9]/g, "_").toLowerCase();
    for (const file of files) {
      const pid = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const put = (patch) => setSlotPhotos(prev => ({
        ...prev, [slotId]: (prev[slotId] || []).map(p => p.pid === pid ? { ...p, ...patch } : p),
      }));
      setSlotPhotos(prev => ({ ...prev, [slotId]: [...(prev[slotId] || []), { pid, name: file.name, url: "", uploading: true, stage: "compressing" }] }));
      let converted = file;
      try {
        converted = await db.convertToJpeg(file);
      } catch (err) {
        console.warn("Convert error:", err);
        if (err?.code === "HEIC_CONVERSION_FAILED") {
          put({ uploading: false, uploadFailed: true, name: file.name });
          alert(db.heicHelpText(file.name));
          continue;
        }
      }
      const preview = await new Promise(res => { const r = new FileReader(); r.onload = ev => res(ev.target.result); r.onerror = () => res(""); r.readAsDataURL(converted); });
      put({ name: converted.name, url: preview, stage: "uploading" });
      try {
        const url = await db.uploadPhoto(converted, `assessment-photos/${Date.now()}-${safe}-${converted.name}`);
        put({ url, uploading: false });
      } catch (err) {
        console.warn("Slot photo upload failed:", err);
        put({ uploading: false, uploadFailed: true });
      }
    }
  };
  const removeSlotPhoto = (slotId, pid) => setSlotPhotos(prev => ({ ...prev, [slotId]: (prev[slotId] || []).filter(p => p.pid !== pid) }));

  const partnersLoadedRef = useRef(false);
  if (!partnersLoadedRef.current) {
    partnersLoadedRef.current = true;
    db.getPartners().then(setPartners).catch(() => {});
  }
  // A job's partner may not be in the list the crew can see (partner
  // directory is admin-only, or the name was typed with different casing).
  // Carry the job's partner anyway as a name-only entry so the form never
  // falls back to "direct" on a partner job.
  const sameName = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  const partnerFor = (name) => partners.find(x => sameName(x.name, name)) || null;
  const selectedPartner = partners.find(p => p.id === partnerId)
    || (partnerId.startsWith("name:") ? { id: partnerId, name: partnerId.slice(5), customFields: [], requiredPhotos: [], defaultTasks: [] } : null);
  const partnerButtons = selectedPartner && !partners.some(p => p.id === selectedPartner.id) ? [...partners, selectedPartner] : partners;

  // Scope of work: which procedure this report is for. A partner can have
  // several (Omnidian: inspection, removal visit, reinstall visit), each with
  // its own required report fields and photos.
  const [scopes, setScopes] = useState([]);
  const [scopeId, setScopeId] = useState("");
  const scopesRef = useRef(false);
  if (!scopesRef.current) {
    scopesRef.current = true;
    db.getPlaybooks().then(list => {
      const withReqs = (list || []).filter(b => (b.reportFields || []).length > 0 || (b.requiredPhotos || []).length > 0);
      setScopes(withReqs);
      // Install closeout opens straight on its checklist — no scope to pick
      if (isInstall) {
        const inst = withReqs.find(b => b.id === "pb-install-closeout") || withReqs.find(b => b.serviceType === "New Installation");
        if (inst) setScopeId(inst.id);
      }
    }).catch(() => {});
  }
  const scopeOptions = scopes
    .filter(b => !b.partner || (selectedPartner && sameName(b.partner, selectedPartner.name)))
    .filter(b => isInstall ? b.serviceType === "New Installation" : b.serviceType !== "New Installation");
  const selectedScope = scopes.find(b => b.id === scopeId) || null;
  // Partner-wide requirements first, then the scope's own
  const reqFields = [...(selectedPartner?.customFields || []), ...(selectedScope?.reportFields || [])];
  const reqPhotos = [...(selectedPartner?.requiredPhotos || []), ...(selectedScope?.requiredPhotos || [])];
  const reqSource = [selectedPartner?.name, selectedScope?.title].filter(Boolean).join(" — ");


  // Link to an already-scheduled job instead of creating a duplicate project
  const [openProjects, setOpenProjects] = useState([]);
  const [linkedProjectId, setLinkedProjectId] = useState("");
  const projectsLoadedRef = useRef(false);
  if (!projectsLoadedRef.current) {
    projectsLoadedRef.current = true;
    db.getProjects().then(list => setOpenProjects(list.filter(p => p.status !== "completed"))).catch(() => {});
  }
  // Partners load asynchronously. If a project was linked before they arrived,
  // match its partner once the list is in.
  useEffect(() => {
    if ((partnerId && !partnerId.startsWith("name:")) || partners.length === 0 || !linkedProjectId) return;
    const proj = openProjects.find(x => x.id === linkedProjectId);
    if (!proj?.partner) return;
    const match = partnerFor(proj.partner);
    if (match) setPartnerId(match.id);
  }, [partners, linkedProjectId, partnerId, openProjects]);

  const applyLinkedProject = (proj) => {
    // Carry across everything the project already holds
    setClient(prev => ({
      name: proj.client || proj.name || prev.name,
      address: proj.address || prev.address,
      phone: proj.clientPhone || prev.phone,
      email: proj.clientEmail || prev.email,
    }));
    if (proj.systemSize) setSystem(prev => ({ ...prev, size: proj.systemSize }));
    // What the office wrote on the visit (or the quote) is the reported issue
    const visit = (initialVisitId && (proj.visits || []).find(v => v.id === initialVisitId))
      || (proj.visits || []).filter(v => !v.cancelled && v.scheduled_at).sort((a, b) => Math.abs(new Date(a.scheduled_at) - Date.now()) - Math.abs(new Date(b.scheduled_at) - Date.now()))[0];
    const quoteNote = (proj.notes || []).map(n => n.text || "").find(t => /^Scope from quote:|service quote/i.test(t));
    const issueText = (visit?.notes || "").trim() || (quoteNote ? quoteNote.replace(/^.*Scope from quote:\s*/s, "").trim() : "") || (proj.scope || "").trim();
    if (issueText && !isInstall) setIssue(prev => prev || issueText);
    // System details from the last assessment filed on this job — visit 2
    // shouldn't retype what visit 1 recorded.
    db.getProjectForms(proj).then(forms => {
      const last = (forms || []).find(f => f.type === "Service Assessment" && f.system && (f.system.inverterMake || f.system.panelMake || f.system.numPanels || f.system.installer));
      if (!last) return;
      setSystem(prev => ({
        size: prev.size || last.system.size || "", installer: prev.installer || last.system.installer || "", installDate: prev.installDate || last.system.installDate || "",
        panelMake: prev.panelMake || last.system.panelMake || "", inverterMake: prev.inverterMake || last.system.inverterMake || "", numPanels: prev.numPanels || last.system.numPanels || "",
      }));
    }).catch(() => {});
    // Match the project's partner so their required fields appear
    if (proj.partner) {
      const match = partnerFor(proj.partner);
      setPartnerId(match ? match.id : `name:${proj.partner.trim()}`); setPartnerFieldValues({});
    }
    // Map the project's service subtype to this form's service type
    if (proj.serviceSubtype) {
      const map = {
        "Roof Leak": "leak", "Inverter Issue": "electrical",
        "Production Issue": "production", "System Inspection": "inspection",
        "Electrical Issue": "electrical", "General O&M": "om",
        "Monitoring / Comms": "om", "Panel Cleaning": "om",
      };
      const st = map[proj.serviceSubtype];
      if (st) setServiceType(st);
    }
  };

  // Opened from a job's briefing: link and fill once the job list arrives
  const initLinkRef = useRef(false);
  useEffect(() => {
    if (initLinkRef.current || !initialProjectId || openProjects.length === 0) return;
    initLinkRef.current = true;
    const proj = openProjects.find(x => x.id === initialProjectId);
    if (!proj) return;
    setLinkedProjectId(proj.id);
    applyLinkedProject(proj);
  }, [initialProjectId, openProjects]);

  // Default the scope from the job. On a multi-visit job (removal, then
  // reinstall), the visit happening today decides it, so the reinstall crew
  // gets the reinstall checklist without having to know which one to pick.
  useEffect(() => {
    if (scopeId || !linkedProjectId || scopes.length === 0) return;
    const proj = openProjects.find(x => x.id === linkedProjectId);
    if (!proj) return;
    const today = isoToJobWall(new Date().toISOString()).slice(0, 10);
    const todays = (proj.visits || []).find(v => !v.cancelled && v.scheduled_at && jobDayKey(v.scheduled_at) === today);
    const want = (todays && todays.playbookId) || proj.playbookId;
    if (want && scopes.some(b => b.id === want)) setScopeId(want);
  }, [scopes, linkedProjectId, openProjects, scopeId]);

  const hasSlotPhotos = Object.values(slotPhotos).some(l => l.length > 0);
  const switchPartner = (id) => {
    if (id === partnerId) return;
    if (hasSlotPhotos && !window.confirm("You've taken photos for this partner's checklist. Switching partners will discard them. Continue?")) return;
    setPartnerId(id); setPartnerFieldValues({}); setSlotPhotos({}); setScopeId("");
  };
  const switchScope = (id) => {
    if (id === scopeId) return;
    if (hasSlotPhotos && !window.confirm("Changing the scope of work will discard the photos you've taken for its checklist. Continue?")) return;
    setScopeId(id); setPartnerFieldValues({}); setSlotPhotos({});
  };
  const draft = useFormDraft({
    form: isInstall ? "install" : "assessment", user, initialProjectId,
    projectId: linkedProjectId, projectName: openProjects.find(x => x.id === linkedProjectId)?.name || client.name || "",
    fields: {
      serviceType: [serviceType, setServiceType], client: [client, setClient], timeOnSite: [timeOnSite, setTimeOnSite], referral: [referral, setReferral],
      system: [system, setSystem], issue: [issue, setIssue], findings: [findings, setFindings], rootCause: [rootCause, setRootCause],
      recommendations: [recommendations, setRecommendations], fee: [fee, setFee], photos: [photos, setPhotos], partnerId: [partnerId, setPartnerId],
      partnerFieldValues: [partnerFieldValues, setPartnerFieldValues], slotPhotos: [slotPhotos, setSlotPhotos], scopeId: [scopeId, setScopeId],
      linkedProjectId: [linkedProjectId, setLinkedProjectId],
    },
    isDirty: () => !!(client.name || client.address || issue || photos.length || Object.values(slotPhotos).some(l => l.length)),
  });
  const submit = async () => {
    if (!client.name || !client.address) return alert("Please fill Client Name and Address.");
    if (photos.some(p => p.uploading) || Object.values(slotPhotos).some(list => list.some(p => p.uploading))) {
      return alert("Please wait — photos are still uploading.");
    }
    const failedA = [...photos, ...Object.values(slotPhotos).flat()].filter(p => p.uploadFailed || (p.url || "").startsWith("data:"));
    if (failedA.length) return alert(`${failedA.length} photo(s) did not upload (no signal?):\n${failedA.map(p => "• " + (p.name || "photo")).join("\n")}\n\nThe partner needs these for payment. Remove and re-add them once you have signal, then submit.`);
    // Enforce this partner's required fields before submitting
    if (reqFields.length > 0) {
      const missing = reqFields.filter(cf => cf.required && !(partnerFieldValues[cf.id] || "").trim());
      if (missing.length > 0) return alert(`Please fill the required report field(s) for ${reqSource}:\n\n• ` + missing.map(m => m.label).join("\n• "));
    }

    // Required photos. Not a hard block — a roof can be unsafe to walk, and
    // the partner may accept that — but the gap is written into the record and
    // the PDF so it can't be missed at submission time.
    const slots = reqPhotos;
    const savedIn = (id) => (slotPhotos[id] || []).filter(p => p.url && !p.url.startsWith("data:") && !p.uploadFailed);
    const missingPhotos = slots.filter(sl => sl.required && savedIn(sl.id).length === 0).map(sl => sl.label);
    if (missingPhotos.length > 0) {
      const go = window.confirm(
        `${reqSource} requires these photos for payment and they're missing:\n\n• ` +
        missingPhotos.join("\n• ") +
        `\n\nSubmit anyway? The missing photos will be listed on the report. Explain why in Findings.`
      );
      if (!go) return;
    }
    setLoading(true);

    const typeLabel = isInstall ? "Installation Closeout" : (SERVICE_TYPES.find(s => s.id === serviceType)?.label || serviceType);
    const partnerPhotosOut = slots.map(sl => ({ label: sl.label, required: !!sl.required, urls: savedIn(sl.id).map(p => p.url) }));
    const photoUrls = [
      ...partnerPhotosOut.flatMap(pp => pp.urls),
      ...photos.map(p => p.url).filter(u => u && !u.startsWith("data:")),
    ];
    // Partner submission window, counted in business days from today's visit
    const submitDue = selectedPartner?.submitDays
      ? addBusinessDays(new Date().toISOString(), parseInt(selectedPartner.submitDays, 10))
      : "";
    const partnerFieldsOut = reqFields.map(cf => ({ label: cf.label, value: partnerFieldValues[cf.id] || "" }));

    const data = {
      type: "Service Assessment", submittedAt: new Date().toISOString(), submittedBy: user?.name,
      status: "submitted", siteName: client.name, address: client.address,
      serviceType: typeLabel, client, referral, system, issue, findings, rootCause,
      recommendations, fee, photos: photos.map(p => ({ url: p.url, name: p.name })),
      photo_urls: photoUrls,
      partnerName: selectedPartner?.name || "", partnerFields: partnerFieldsOut,
      partnerPhotos: partnerPhotosOut, missingPhotos, submitDue,
      scopeTitle: selectedScope?.title || "",
      timeOnSite: { ...timeOnSite, manHours: actualHours?.manHours ?? null },
      timeBudget: selectedScope?.timeBudget ?? null,
    };

    try {
      await db.submitSurvey({
        site_name: client.name, address: client.address,
        date: localDate(), inspector: user?.name,
        weather: "", system_size: system.size,
        checklist: JSON.stringify({
          formType: "Service Assessment", serviceType: typeLabel, visitId: initialVisitId || "",
          client, referral, system, issue, findings, rootCause, recommendations, fee,
          partnerName: selectedPartner?.name || "", partnerFields: partnerFieldsOut,
          partnerPhotos: partnerPhotosOut, missingPhotos, submitDue,
          scopeTitle: selectedScope?.title || "",
          timeOnSite: { ...timeOnSite, manHours: actualHours?.manHours ?? null },
          timeBudget: selectedScope?.timeBudget ?? null,
        }),
        notes: `${typeLabel}: ${issue}`,
        photo_urls: photoUrls,
        status: "submitted", project_id: linkedProjectId || null,
      }, user?.id);
    } catch(e) { console.warn(e); }

    db.sendNotification("Service Assessment", `${typeLabel} — ${client.name}`, user?.name);
    if (linkedProjectId) {
      db.linkFormToProject(linkedProjectId, {
        formType: `Service Assessment (${typeLabel})`, userName: user?.name,
        partnerName: selectedPartner?.name || "", partnerTasks: selectedPartner?.defaultTasks || [],
        submitDue, visitId: initialVisitId || "",
      });
    } else {
      db.createWorkflowProject({
        clientName: client.name, address: client.address,
        formType: `Service Assessment (${typeLabel})`, userName: user?.name,
        partnerName: selectedPartner?.name || "", partnerTasks: selectedPartner?.defaultTasks || [],
        submitDue,
      });
    }
    setLoading(false);
    draft.clear();
    onSubmit(data);
  };

  return (
    <div style={{ maxWidth: 800, animation: "fadeIn 0.25s ease" }}>
      <DraftBanner draft={draft} lang={lang} />
      <button onClick={onBack} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
        <div style={{ width: 44, height: 44, borderRadius: 12, background: `${isInstall ? C.gold : C.success}22`, display: "flex", alignItems: "center", justifyContent: "center", color: isInstall ? C.gold : C.success }}>{isInstall ? I.sun : I.wrench}</div>
        <div><h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>{isInstall ? t.installCloseout : t.serviceAssessment}</h2><p style={{ margin: 0, color: C.muted, fontSize: 13 }}>{isInstall ? (lang === "es" ? "Lista de fotos de cierre — toma las fotos mientras instalas, no al final" : "Closeout photo checklist — take the photos as you go, not at the end") : "On-site system evaluation — SolarAbility LLC"}</p></div>
      </div>

      {/* Link to an already-scheduled job so this doesn't create a duplicate project */}
      {openProjects.length > 0 && (
        <div style={{ ...S.card, marginBottom: 20 }}>
          <Field label="Link to Existing Job">
            <JobPicker projects={openProjects} value={linkedProjectId} lang={lang} onPick={id => {
              setLinkedProjectId(id);
              const proj = openProjects.find(x => x.id === id);
              if (proj) applyLinkedProject(proj);
            }} />
          </Field>
          {linkedProjectId && (
            <div style={{ fontSize: 12, color: C.muted, marginTop: 8 }}>
              Client, partner, and scope filled in from this job. Submitting updates it instead of creating a new project.
            </div>
          )}
        </div>
      )}

      {/* Service Type */}
      {!isInstall && <div style={S.card}>
        <div style={S.label}>{t.serviceType}</div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {SERVICE_TYPES.map(st => (
            <button key={st.id} onClick={() => setServiceType(st.id)}
              style={{ padding: "10px 18px", borderRadius: 10, border: `1px solid ${serviceType === st.id ? C.gold : C.border}`,
                background: serviceType === st.id ? `${C.gold}22` : "transparent",
                color: serviceType === st.id ? C.gold : C.muted, fontSize: 13, fontWeight: 600,
                cursor: "pointer", fontFamily: "inherit", transition: "all 0.15s" }}>
              {lang === "es" ? st.labelEs : st.label}
            </button>
          ))}
        </div>
      </div>}

      {/* Client Info */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.clientInfo}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t.clientName2} required><input style={S.input} value={client.name} onChange={e => setClient(p => ({ ...p, name: e.target.value }))} placeholder="Property owner or company" /></Field>
          <Field label={t.address} required><input style={S.input} value={client.address} onChange={e => setClient(p => ({ ...p, address: e.target.value }))} placeholder="Full address" /></Field>
          <Field label={t.clientPhone}><input style={S.input} type="tel" value={client.phone} onChange={e => setClient(p => ({ ...p, phone: e.target.value }))} placeholder="(312) 555-0000" /></Field>
          <Field label={t.clientEmail}><input style={S.input} type="email" value={client.email} onChange={e => setClient(p => ({ ...p, email: e.target.value }))} placeholder="client@email.com" /></Field>
        </div>
      </div>

      {/* Referral */}
      {!isInstall && <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.referralSource}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t.referralName}><input style={S.input} value={referral.name} onChange={e => setReferral(p => ({ ...p, name: e.target.value }))} placeholder="e.g. StraightUp Solar, Nelnet" /></Field>
          <Field label={t.referralSource}><input style={S.input} value={referral.source} onChange={e => setReferral(p => ({ ...p, source: e.target.value }))} placeholder="e.g. Email, Phone, Website" /></Field>
        </div>
      </div>}

      {!isInstall && <div style={{ marginBottom: 16 }}><OemSupport compact /></div>}

      {/* Partner / Source — drives which extra fields are required and which
          submission steps land on the auto-created project */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.selectPartner}</h3>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          <button type="button" onClick={() => switchPartner("")} style={{
            padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
            border: `1px solid ${partnerId === "" ? C.gold : C.border}`,
            background: partnerId === "" ? `${C.gold}22` : "transparent",
            color: partnerId === "" ? C.gold : C.muted,
          }}>{t.noPartnerOption}</button>
          {partnerButtons.map(p => (
            <button key={p.id} type="button" onClick={() => switchPartner(p.id)} style={{
              padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
              border: `1px solid ${partnerId === p.id ? C.gold : C.border}`,
              background: partnerId === p.id ? `${C.gold}22` : "transparent",
              color: partnerId === p.id ? C.gold : C.muted,
            }}>{p.name}</button>
          ))}
        </div>
        {scopeOptions.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ ...S.label, marginBottom: 8 }}>Scope of Work</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {scopeOptions.map(b => (
                <button key={b.id} type="button" onClick={() => switchScope(b.id)} style={{
                  padding: "9px 14px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                  border: `1px solid ${scopeId === b.id ? C.info : C.border}`,
                  background: scopeId === b.id ? `${C.info}22` : "transparent",
                  color: scopeId === b.id ? C.info : C.muted,
                }}>{b.title.replace(/^[^—]*—\s*/, "")}</button>
              ))}
            </div>
            {!scopeId && <div style={{ fontSize: 12, color: C.warning, marginTop: 8 }}>{isInstall ? "The installation checklist procedure isn't loaded yet — run GENERAL_RR_AND_INSTALL_PHOTOS.md in Supabase." : "Pick which visit this report is for — each has its own required items."}</div>}
          </div>
        )}
        {(() => {
          const budget = parseFloat(selectedScope?.timeBudget) || null;
          const over = budget && actualHours && actualHours.manHours > budget;
          return (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <div style={{ ...S.label, marginBottom: 8 }}>Time On Site</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="Arrived"><input style={S.input} type="time" value={timeOnSite.arrived} onChange={e => setTimeOnSite(p => ({ ...p, arrived: e.target.value }))} /></Field>
                <Field label="Departed"><input style={S.input} type="time" value={timeOnSite.departed} onChange={e => setTimeOnSite(p => ({ ...p, departed: e.target.value }))} /></Field>
                <Field label="# Techs"><input style={S.input} type="number" min="1" max="6" value={timeOnSite.techs} onChange={e => setTimeOnSite(p => ({ ...p, techs: e.target.value }))} placeholder="1" /></Field>
              </div>
              {actualHours && (
                <div style={{ fontSize: 12.5, color: over ? C.warning : C.muted, lineHeight: 1.6 }}>
                  {Math.floor(actualHours.mins / 60)}h {actualHours.mins % 60}m × {actualHours.crew} tech{actualHours.crew > 1 ? "s" : ""} = <strong>{actualHours.manHours} man-hours</strong>
                  {budget ? (over
                    ? ` — over the ${budget} paid for this scope by ${Math.round((actualHours.manHours - budget) * 100) / 100}.`
                    : ` — within the ${budget} paid.`) : ""}
                </div>
              )}
            </div>
          );
        })()}

        {reqFields.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
            <div style={{ ...S.label, marginBottom: 10 }}>{reqSource} — Report</div>
            <div>
              {reqFields.map(cf => (
                <Field key={cf.id} label={cf.label} required={cf.required}>
                  <textarea style={{ ...S.textarea, minHeight: 44 }} rows={2} value={partnerFieldValues[cf.id] || ""}
                    onChange={e => setPartnerFieldValues(p => ({ ...p, [cf.id]: e.target.value }))} />
                </Field>
              ))}
            </div>
          </div>
        )}
        {reqPhotos.length > 0 && (() => {
          const slots = reqPhotos;
          const done = slots.filter(sl => (slotPhotos[sl.id] || []).some(p => p.url && !p.uploading && !p.uploadFailed)).length;
          const mustHave = slots.filter(sl => sl.required);
          const mustDone = mustHave.filter(sl => (slotPhotos[sl.id] || []).some(p => p.url && !p.uploading && !p.uploadFailed)).length;
          return (
            <div style={{ marginTop: 16, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4, gap: 8, flexWrap: "wrap" }}>
                <div style={S.label}>{reqSource} — Required Photos</div>
                <span style={{ fontSize: 12, fontWeight: 700, color: mustDone === mustHave.length ? C.success : C.warning }}>
                  {mustDone}/{mustHave.length} required · {done}/{slots.length} total
                </span>
              </div>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                Missing photos can cost the payment. If you fix something, add a photo <strong>before and after</strong> in the same slot.
              </div>
              {slots.map(sl => {
                const list = slotPhotos[sl.id] || [];
                const busy = list.some(p => p.uploading);
                const has = list.some(p => p.url && !p.uploading && !p.uploadFailed);
                return (
                  <div key={sl.id} style={{ padding: 11, background: C.surface, borderRadius: 8, marginBottom: 8, border: `1px solid ${has ? `${C.success}55` : sl.required ? `${C.warning}44` : C.border}` }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                      <span style={{ fontSize: 14, color: has ? C.success : C.muted, width: 18 }}>{has ? "✓" : "○"}</span>
                      <div style={{ flex: 1, minWidth: 150 }}>
                        <div style={{ fontSize: 13, color: C.light, fontWeight: 600 }}>
                          {sl.label}{!sl.required && <span style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}> · if applicable</span>}
                        </div>
                        {sl.hint && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sl.hint}</div>}
                      </div>
                      <label style={{ ...S.btnS, padding: "6px 12px", fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1 }}>
                        {busy ? (list.some(p => p.stage === "compressing") ? "⏳ Compressing..." : "⏳ Uploading...") : <>{I.cam} {has ? "Add" : "Photo"}</>}
                        <input type="file" accept="image/*" multiple style={{ display: "none" }} disabled={busy}
                          onChange={(e) => handleSlotPhoto(sl.id, sl.label, e)} />
                      </label>
                    </div>
                    {list.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 9, paddingLeft: 28 }}>
                        {list.map(p => (
                          <div key={p.pid} style={{ position: "relative" }}>
                            {p.url
                              ? <img loading="lazy" decoding="async" src={p.url} alt="" onClick={() => openPhoto(p.url)} style={{ cursor: "zoom-in",  width: 56, height: 56, objectFit: "cover", borderRadius: 6, border: `1px solid ${p.uploadFailed ? C.danger : C.border}`, opacity: p.uploading ? 0.5 : 1 }} />
                              : <div style={{ width: 56, height: 56, borderRadius: 6, border: `1px dashed ${C.gold}`, display: "flex", alignItems: "center", justifyContent: "center" }}>⏳</div>}
                            <button onClick={() => removeSlotPhoto(sl.id, p.pid)}
                              style={{ position: "absolute", top: -6, right: -6, width: 18, height: 18, borderRadius: "50%", background: C.danger, border: "none", color: "#fff", cursor: "pointer", fontSize: 10 }}>×</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })()}
        {selectedPartner?.submitDays && (
          <div style={{ marginTop: 12, fontSize: 12, color: C.light, background: `${C.warning}15`, border: `1px solid ${C.warning}44`, borderRadius: 8, padding: 10 }}>
            ⏱ Report and photos must reach {selectedPartner.name} within <strong>{selectedPartner.submitDays} business day{String(selectedPartner.submitDays) === "1" ? "" : "s"}</strong> — due <strong>{addBusinessDays(new Date().toISOString(), parseInt(selectedPartner.submitDays, 10))}</strong> if you file today.
          </div>
        )}
        {selectedPartner?.notes && (
          <div style={{ marginTop: 12, fontSize: 12, color: C.muted, background: C.surface, borderRadius: 8, padding: 10 }}>
            <strong style={{ color: C.light }}>Submission notes:</strong> {selectedPartner.notes}
          </div>
        )}
      </div>

      {/* System Info */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.systemInfo}</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <Field label={t.systemSizeKw}><input style={S.input} value={system.size} onChange={e => setSystem(p => ({ ...p, size: e.target.value }))} placeholder="e.g. 8.4" /></Field>
          <Field label={t.numPanels}><input style={S.input} value={system.numPanels} onChange={e => setSystem(p => ({ ...p, numPanels: e.target.value }))} placeholder="e.g. 24" /></Field>
          <Field label={t.originalInstaller}><input style={S.input} value={system.installer} onChange={e => setSystem(p => ({ ...p, installer: e.target.value }))} placeholder="Who installed the system" /></Field>
          <Field label={t.installDate}><input style={S.input} value={system.installDate} onChange={e => setSystem(p => ({ ...p, installDate: e.target.value }))} placeholder="e.g. 2021 or 3 years" /></Field>
          <Field label={t.panelMake}><input style={S.input} value={system.panelMake} onChange={e => setSystem(p => ({ ...p, panelMake: e.target.value }))} placeholder="e.g. REC Alpha 400W" /></Field>
          <Field label={t.inverterMake}><input style={S.input} value={system.inverterMake} onChange={e => setSystem(p => ({ ...p, inverterMake: e.target.value }))} placeholder="e.g. Enphase IQ8+" /></Field>
        </div>
      </div>

      {/* Issue Description */}
      {!isInstall && <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.issueDescription}</h3>
        <Field label={t.reportedIssue} required>
          <textarea style={S.textarea} value={issue} onChange={e => setIssue(e.target.value)} rows={3}
            placeholder="Describe the reported issue — what the homeowner/client is experiencing" />
        </Field>
      </div>}

      {/* Findings (can fill on-site) */}
      {!isInstall && <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.findings}</h3>
        <Field label={t.rootCause}>
          <textarea style={S.textarea} value={rootCause} onChange={e => setRootCause(e.target.value)} rows={3}
            placeholder="What you found on-site — root cause analysis" />
        </Field>
        <Field label={t.recommendations}>
          <textarea style={S.textarea} value={recommendations} onChange={e => setRecommendations(e.target.value)} rows={3}
            placeholder="Recommended repairs, next steps, or actions" />
        </Field>
        <Field label={t.findings}>
          <textarea style={S.textarea} value={findings} onChange={e => setFindings(e.target.value)} rows={2}
            placeholder="Additional observations, notes" />
        </Field>
      </div>}

      {/* Photos */}
      <div style={S.card}>
        <h3 style={{ margin: "0 0 16px", fontSize: 16, fontWeight: 700, color: C.light }}>{t.assessmentPhotos}</h3>
        <PhotoUploader photos={photos} setPhotos={setPhotos} label={t.assessmentPhotos} />
      </div>

      {/* Submit */}
      <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
        <button onClick={onBack} style={{ ...S.btnS, flex: 1, justifyContent: "center" }}>{t.cancel}</button>
        <button onClick={submit} disabled={loading} style={{ ...S.btnP, flex: 2, justifyContent: "center", opacity: loading ? 0.6 : 1 }}>
          {loading ? t.submitting : <>{I.send} {t.submitAssessment}</>}
        </button>
      </div>
      <DraftStatus draft={draft} lang={lang} />
    </div>
  );
}

// ─── Project Tracker ─────────────────────────────────────────────────────────
const PROJECT_TYPES = [
  { id: "survey", label: "Site Survey" },
  { id: "new_install", label: "New Solar Installation" },
  { id: "electrical", label: "Electrical Services" },
  { id: "removal_reinstall", label: "Removal & Reinstallation" },
  { id: "service", label: "Service Call / O&M" },
];

// Service calls need a second level — the workflow is the same but what you're
// diagnosing isn't, and it drives which procedure and checklist apply.
const SERVICE_SUBTYPES = [
  "Roof Leak", "Inverter Issue", "Production Issue", "System Inspection",
  "Electrical Issue", "Panel Cleaning", "Monitoring / Comms", "General O&M",
];

// Material templates per project type. Quantities are left blank on purpose —
// they depend on the array, and a wrong default is worse than an empty box the
// crew has to fill in.
// ─── Materials Catalog ──────────────────────────────────────────────────────
// Real part numbers from Greentech Renewables quotes. `price` is the last
// quoted unit price for reference only — quoted prices change, so it is never
// treated as authoritative. `per` follows the supplier convention: E = each,
// C = per hundred.

// ─── Wire & Conduit (Amperage Electrical Supply) ────────────────────────────
// Sheet dated 09/08/2026, prices subject to change. Stored at the supplier's
// own put-up (wire per 1,000 ft, conduit per 100 ft) and converted to a
// per-foot figure for estimating, since that's how runs actually get measured.
const WIRE_PRICE_SHEET_DATE = "09/08/2026";

// [label, price per 1,000 ft]
const THHN_COPPER = [
  ["18 STR TFFN", 113.14], ["16 STR TFFN", 158.30],
  ["14 SOL", 153.58], ["14 STR", 168.91],
  ["12 SOL", 235.63], ["12 STR", 244.17], ["12 STR STRIPE", 274.17], ["12 STR PULLPRO", 314.98],
  ["10 STR", 381.78], ["10 STR STRIPE", 411.78], ["10 STR PULLPRO", 489.26],
  ["8 STR", 708.62], ["6 STR", 1090.26], ["4 STR", 1614.59], ["3 STR", 2036.51],
  ["2 STR", 2549.06], ["1 STR", 2921.60], ["1/0 STR", 3577.03], ["2/0 STR", 4406.19],
  ["3/0 STR", 5560.78], ["4/0 STR", 6941.30], ["250MCM", 7966.14], ["300MCM", 9553.38],
  ["350MCM", 11186.11], ["400MCM", 12724.78], ["500MCM", 16078.68], ["600MCM", 20035.57],
];

// [label, coil price per 1,000 ft, reel price or null]
const NMB_WIRE = [
  ["14/2", 461.14, 496.66], ["14/3", 614.06, 649.58], ["14/2/2", 913.06, null],
  ["12/2", 680.44, 715.83], ["12/3", 933.53, 968.92],
  ["10/2", 1187.28, 1222.66], ["10/3", 1478.39, 1513.76],
  ["8/2", 2023.73, null], ["8/3", 2849.02, null],
  ["6/2", 2858.16, null], ["6/3", 4163.61, null],
];

// [label, price per 1,000 ft]
const ALUM_XHHW = [
  ["6 STR", 322.90], ["4 STR", 398.88], ["3 STR", 426.40], ["2 STR", 506.06],
  ["1 STR", 697.44], ["1/0 STR", 783.30], ["2/0 STR", 925.69], ["3/0 STR", 1149.30],
  ["4/0 STR", 1277.44], ["250MCM", 1559.42], ["300MCM", 2154.73], ["350MCM", 2190.31],
  ["400MCM", 2560.59], ["500MCM", 2824.04], ["600MCM", 3578.85], ["750MCM", 4174.14],
];

// size -> [EMT, Color EMT, IMC, Rigid, PVC40, PVC80, Aluminum] per 100 ft
const CONDUIT_TYPES = ["EMT", "Color EMT", "IMC", "Rigid", "PVC 40", "PVC 80", "Aluminum"];
const CONDUIT_PRICES = [
  ['1/2"', [56.78, 88.06, 184.92, 377.18, 31.06, 49.76, 171.29]],
  ['3/4"', [103.69, 151.47, 218.18, 387.10, 37.30, 68.74, 227.97]],
  ['1"', [178.37, 258.68, 334.81, 616.75, 54.43, 93.94, 332.21]],
  ['1-1/4"', [289.93, 505.97, 469.69, 908.19, 78.39, 123.93, 436.45]],
  ['1-1/2"', [354.69, 626.57, 584.96, 1021.99, 90.89, 147.31, 540.69]],
  ['2"', [415.18, 787.68, 732.82, 1253.80, 113.32, 190.78, 722.34]],
  ['2-1/2"', [629.99, null, 1451.29, 2362.83, 177.62, 283.45, 1142.94]],
  ['3"', [798.85, null, 1898.70, 2678.63, 216.96, 341.18, 1501.36]],
  ['3-1/2"', [1054.83, null, 2232.37, 3510.72, 275.38, 507.08, 1801.87]],
  ['4"', [1080.45, null, 2508.00, 3711.95, 311.11, 544.02, 2134.70]],
  ['5"', [null, null, null, 6977.47, 439.43, 794.82, 2919.21]],
  ['6"', [null, null, null, 9101.33, 574.76, 1122.62, null]],
];

function buildWireConduitCatalog() {
  const out = [];
  const r4 = (n) => Math.round(n * 10000) / 10000;

  THHN_COPPER.forEach(([label, per1000]) => {
    out.push({
      sku: `THHN ${label}`, name: `THHN Copper ${label}`, cat: "Wire",
      unit: "ft", per: "FT", price: r4(per1000 / 1000),
      putUp: `$${per1000.toFixed(2)} / 1,000 ft`,
    });
  });

  NMB_WIRE.forEach(([label, coil, reel]) => {
    out.push({
      sku: `NM-B ${label}`, name: `NM-B w/ Ground ${label} (coil)`, cat: "Wire",
      unit: "ft", per: "FT", price: r4(coil / 1000),
      putUp: `$${coil.toFixed(2)} / 1,000 ft coil`,
    });
    if (reel) {
      out.push({
        sku: `NM-B ${label} REEL`, name: `NM-B w/ Ground ${label} (reel)`, cat: "Wire",
        unit: "ft", per: "FT", price: r4(reel / 1000),
        putUp: `$${reel.toFixed(2)} / 1,000 ft reel`,
      });
    }
  });

  ALUM_XHHW.forEach(([label, per1000]) => {
    out.push({
      sku: `XHHW-AL ${label}`, name: `Aluminum XHHW ${label}`, cat: "Wire",
      unit: "ft", per: "FT", price: r4(per1000 / 1000),
      putUp: `$${per1000.toFixed(2)} / 1,000 ft · may carry $25 cut charge per leg`,
    });
  });

  CONDUIT_PRICES.forEach(([size, prices]) => {
    prices.forEach((p, i) => {
      if (p == null) return;
      const type = CONDUIT_TYPES[i];
      out.push({
        sku: `${type.toUpperCase().replace(/\s/g, "")} ${size}`,
        name: `${type} Conduit ${size}`, cat: "Conduit",
        unit: "ft", per: "FT", price: r4(p / 100),
        putUp: `$${p.toFixed(2)} / 100 ft`,
      });
    });
  });

  return out;
}

const MATERIALS_CATALOG = [
  // Sunmodo racking
  { sku: "SUNMO NANOMOUNT-SEAL-B", name: "NanoMount Black w/ Butyl", cat: "Racking", unit: "ea", per: "E", price: 14.04 },
  { sku: "SUNMO SCREW-B", name: "Nano Decking Screw Kit Black/SQ40", cat: "Racking", unit: "ea", per: "E", price: 1.13 },
  { sku: "SUNMO LFA-SMR100-B", name: "L-Foot Adaptor SMR100 Black", cat: "Racking", unit: "ea", per: "E", price: 4.47 },
  { sku: "SUNMO SMR100-168-M", name: 'SMR100 Rail Mill Finish 168"', cat: "Racking", unit: "ea", per: "E", price: 39.22 },
  { sku: "SUNMO CLMP-MID-SMR-S", name: "Mid Clamp SMR Pop-On Clear", cat: "Racking", unit: "ea", per: "E", price: 6.42 },
  { sku: "SUNMO CLMP-END-SMR-S", name: "End Clamp SMR Pop-On Clear", cat: "Racking", unit: "ea", per: "E", price: 5.07 },
  { sku: "SUNMO GRND-SMR", name: "SMR Grounding Lug - M8 Hex", cat: "Racking", unit: "ea", per: "E", price: 11.96 },

  // Unirac
  { sku: "UNIRC 004270D", name: "FlashKit SFM Slider Comp Dark", cat: "Racking", unit: "ea", per: "E", price: 14.62 },
  { sku: "UNIRC 250010U", name: 'SFM Splice 6.5"', cat: "Racking", unit: "ea", per: "E", price: 10.03 },
  { sku: "UNIRC 250020U", name: 'SFM MicroRail 2"', cat: "Racking", unit: "ea", per: "E", price: 8.66 },

  // IronRidge
  { sku: "IRIDG UFO-CL-01-B1", name: "Universal Module Clamp Black", cat: "Racking", unit: "ea", per: "E", price: 4.20 },
  { sku: "IRIDG QM-HUG-01-B1", name: "Halo Ultra Grip Black", cat: "Racking", unit: "ea", per: "E", price: 9.98 },
  { sku: "IRIDG HW-RD1430-01-M1", name: "RD Structural Screw 3.0L (SQ 120)", cat: "Racking", unit: "ea", per: "E", price: 0.95 },
  { sku: "IRIDG BHW-MI-01-A1", name: "Micro Inverter Bonding T-Bolt", cat: "Racking", unit: "ea", per: "E", price: 1.85 },

  // Storage & power electronics
  { sku: "SOLED UBAT-10K1PS0B-03", name: "Domestic 10 kWh Home Battery", cat: "Storage", unit: "ea", per: "E", price: 7621.95 },
  { sku: "SOLED BI-EUSGN-03", name: "Backup Interface Service Side w/ Main Breaker", cat: "Storage", unit: "ea", per: "E", price: 1344.18 },
  { sku: "SOLED S500B", name: "Power Optimizer 500W / 125V", cat: "Electronics", unit: "ea", per: "E", price: 202.07 },
  { sku: "ENP IQBATTERY-5P-1P-NA-DOM", name: "Domestic IQ Battery 5P", cat: "Storage", unit: "ea", per: "E", price: 3195.12 },
  { sku: "ENP SC200D111C240US01", name: "IQ System Controller 3", cat: "Electronics", unit: "ea", per: "E", price: 1925.61 },
  { sku: "ENP ENV2-IQ-AM1-240", name: "IQ Envoy IEEE 1547:2018", cat: "Electronics", unit: "ea", per: "E", price: 504.40 },
  { sku: "ENP COMMS-KIT-02", name: "Communications Kit", cat: "Electronics", unit: "ea", per: "E", price: 335.37 },

  // Wire management / connectors
  { sku: "MULTI 32.0016P0001-UR", name: "MC4 Female Connector 10pc/bag", cat: "Electrical", unit: "bag", per: "E", price: 1.98 },
  { sku: "MULTI 32.0017P0001-UR", name: "MC4 Male Connector 10pc/bag", cat: "Electrical", unit: "bag", per: "E", price: 1.98 },

  // Critter guard / edge screen
  { sku: "SNRAC 232-04077", name: 'Edge Screen 6" Black 100ft Roll', cat: "Critter Guard", unit: "roll", per: "E", price: 83.34 },
  { sku: "SNRAC 242-04105", name: "Array Edge Screen Fastener (100)", cat: "Critter Guard", unit: "ea", per: "E", price: 2.31 },
  { sku: "BIRDX SOLAR MESH-P-6", name: 'Premium Solar Mesh 6" x 100\'', cat: "Critter Guard", unit: "roll", per: "E", price: 69.03 },
  { sku: "BIRDX SOLAR CLIP", name: "Nylon Clips 100pk", cat: "Critter Guard", unit: "pk", per: "C", price: 70.47 },
  ...buildWireConduitCatalog(),
];

// Searchable picker used anywhere a material name is entered.
// Replaces <datalist>, which Android Chrome handles inconsistently and iOS
// Safari largely ignores — meaning suggestions never appeared on the phones
// the crew actually uses. Defined at module scope so it isn't remounted on
// every parent render (that would drop focus on each keystroke).
function MaterialPicker({ value, onChange, onPick, options, placeholder, style }) {
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  const query = (value || "").trim().toLowerCase();
  const matches = query.length === 0
    ? options.slice(0, 12)
    : options.filter(o =>
        o.label.toLowerCase().includes(query) ||
        (o.sub || "").toLowerCase().includes(query)
      ).slice(0, 25);

  // Close when tapping outside
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const choose = (opt) => { onPick(opt); setOpen(false); };

  return (
    <div ref={wrapRef} style={{ position: "relative", ...(style || {}) }}>
      <input
        style={{ ...S.input, width: "100%", boxSizing: "border-box" }}
        value={value || ""}
        placeholder={placeholder}
        autoComplete="off"
        onFocus={() => { setOpen(true); setHighlight(0); }}
        onChange={(e) => { onChange(e.target.value); setOpen(true); setHighlight(0); }}
        onKeyDown={(e) => {
          if (!open) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHighlight(h => Math.min(h + 1, matches.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight(h => Math.max(h - 1, 0)); }
          else if (e.key === "Enter" && matches[highlight]) { e.preventDefault(); choose(matches[highlight]); }
          else if (e.key === "Escape") { setOpen(false); }
        }}
      />
      {open && matches.length > 0 && (
        <div style={{
          position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50, marginTop: 4,
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
          maxHeight: 260, overflowY: "auto", WebkitOverflowScrolling: "touch",
          boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
        }}>
          {matches.map((o, i) => (
            <div
              key={o.key}
              // onMouseDown fires before the input's blur, so the tap registers
              onMouseDown={(e) => { e.preventDefault(); choose(o); }}
              onMouseEnter={() => setHighlight(i)}
              style={{
                padding: "11px 13px", cursor: "pointer",
                borderBottom: i === matches.length - 1 ? "none" : `1px solid ${C.border}44`,
                background: i === highlight ? `${C.gold}18` : "transparent",
              }}>
              <div style={{ fontSize: 13, color: C.light, fontWeight: 600, lineHeight: 1.3 }}>{o.label}</div>
              {o.sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{o.sub}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function catalogLookup(nameOrSku) {
  const q = (nameOrSku || "").trim().toLowerCase();
  if (!q) return null;
  return MATERIALS_CATALOG.find(m =>
    m.name.toLowerCase() === q || m.sku.toLowerCase() === q ||
    `${m.sku} — ${m.name}`.toLowerCase() === q
  ) || null;
}

const REMOVAL_REINSTALL_MATERIALS = [
  "Mounting rail", "Roof attachment", "L-foot adaptor", "Rail splice",
  "End clamp", "Mid clamp", "Grounding lug", "Rail end cap", "Optimizer mount",
];

const NEW_INSTALL_MATERIALS = [
  "PV module", "String inverter", "DC power optimizer",
  "Mounting rail", "Roof attachment", "L-foot adaptor", "Rail splice",
  "End clamp", "Mid clamp", "Grounding lug", "Rail end cap", "Optimizer mount",
  "AC disconnect", "Roof junction box", "Current transformer", "Fuses",
];

const PROJECT_MATERIALS = {
  removal_reinstall: REMOVAL_REINSTALL_MATERIALS,
  new_install: NEW_INSTALL_MATERIALS,
};

function materialsForProjectType(typeId, userName) {
  const list = PROJECT_MATERIALS[typeId];
  if (!list) return [];
  return list.map((name, i) => {
    // If the generic template name matches something in the catalog, carry the
    // part number through so a quote request is usable without extra typing.
    const hit = catalogLookup(name);
    return {
      id: Date.now() + 5000 + i,
      name: hit ? hit.name : name,
      sku: hit ? hit.sku : "",
      unit: hit ? hit.unit : "",
      catPrice: hit ? hit.price : undefined,
      qty: "", ordered: false, received: false,
      createdBy: userName || "",
    };
  });
}

function tasksForProjectType(typeId, userName) {
  if (typeId === "survey") {
    return [
      "Confirm appointment with homeowner",
      "Assign crew and send calendar invite",
      "Site survey visit",
      "Complete TSA Survey in Hub",
      "Review survey photos and measurements",
      "Prepare proposal / quote",
      "Send proposal",
      "Closed",
    ].map((name, i) => ({
      id: Date.now() + i, name, assigned: "", due: "", priority: "medium", done: false, createdBy: userName || "",
    }));
  }
  if (typeId === "service") {
    return [
      "Confirm appointment with homeowner",
      "Assign crew and send calendar invite",
      "Site visit / diagnosis",
      "Complete Service Assessment in Hub",
      "Upload photos to partner system",
      "Submit findings to partner",
      "Create invoice",
      "Send invoice",
      "Paid",
      "Closed",
    ].map((name, i) => ({
      id: Date.now() + i, name, assigned: "", due: "", priority: "medium", done: false, createdBy: userName || "",
    }));
  }
  const base = ["Survey", "Quote", "Deposit", "Drawings and SL", "Permit"];
  const afterPermit = typeId === "removal_reinstall" ? ["Removal"] : [];
  const rest = ["Material sourcing", "Installation", "Create folder and upload documents", "Send invoice", "Paid", "Closed"];
  return [...base, ...afterPermit, ...rest].map((name, i) => ({
    id: Date.now() + i, name, assigned: "", due: "", priority: "medium", done: false, createdBy: userName || "",
  }));
}


// NOTE: ProjectEditor is defined at MODULE SCOPE (not nested inside
// ProjectTracker) on purpose. A component defined inside another
// component's render body gets a brand-new function identity every
// parent render, which forces React to remount it — this caused a
// hooks-mismatch crash (React error #300) when background data finished
// loading while the New Project form was open. Keep it hoisted.
// Printable material list for sending to a supplier for pricing. Deliberately
// omits our own cost figures — this goes out to a vendor, so it carries part
// numbers and quantities only.
// Warehouse pull sheet for the crew on install/reinstall day. Deliberately
// carries no pricing — this goes in someone's hand in a warehouse, not to a
// vendor. Shows current stock so they know whether to pull from the shelf or
// flag a shortage before leaving.
async function generateMaterialPullSheet(project) {
  const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const mats = (project.materials || []).filter(m => (m.name || "").trim());
  if (mats.length === 0) { alert("No materials on this project yet."); return; }

  // Match each line against live stock so the sheet shows where to find it
  let inventory = [];
  try {
    if (USE_SUPABASE && supabase) {
      const { data } = await supabase.from("inventory").select("name, sku, unit, warehouse_qty, truck_qty");
      inventory = data || [];
    } else {
      inventory = localCache.load("inventory") || [];
    }
  } catch (e) { console.warn("Stock lookup failed for pull sheet:", e); }

  const findStock = (m) => {
    const bySku = m.sku && inventory.find(i => (i.sku || "").toLowerCase() === m.sku.toLowerCase());
    if (bySku) return bySku;
    return inventory.find(i => (i.name || "").toLowerCase() === (m.name || "").toLowerCase()) || null;
  };

  let shortages = 0;
  const rows = mats.map((m, i) => {
    const stock = findStock(m);
    const need = parseFloat(m.qty) || 0;
    const wh = stock ? (Number(stock.warehouse_qty) || 0) : null;
    const trk = stock ? (Number(stock.truck_qty) || 0) : null;
    const onHand = stock ? wh + trk : null;
    const short = stock && need > 0 && onHand < need;
    if (short) shortages++;

    const stockCell = stock
      ? `<span style="white-space:nowrap">WH ${wh} &nbsp;·&nbsp; TRK ${trk}</span>` +
        (short ? `<div style="color:#c00;font-weight:bold;font-size:10px;margin-top:2px">SHORT ${(need - onHand).toFixed(0)}</div>` : "")
      : '<span style="color:#999">not stocked</span>';

    return `<tr${short ? ' style="background:#fff6f6"' : ''}>
      <td style="border:1px solid #ccc;padding:9px;text-align:center;font-size:20px;line-height:1">☐</td>
      <td style="border:1px solid #ccc;padding:9px;text-align:center;color:#666">${i + 1}</td>
      <td style="border:1px solid #ccc;padding:9px">
        <div style="font-weight:bold;font-size:13px">${esc(m.name)}</div>
        ${m.sku ? `<div style="font-family:monospace;font-size:10px;color:#666;margin-top:2px">${esc(m.sku)}</div>` : ""}
      </td>
      <td style="border:1px solid #ccc;padding:9px;text-align:center;font-size:17px;font-weight:bold;white-space:nowrap">
        ${esc(m.qty || "—")} <span style="font-size:11px;font-weight:normal;color:#666">${esc(m.unit || "")}</span>
      </td>
      <td style="border:1px solid #ccc;padding:9px;font-size:11px;text-align:center">${stockCell}</td>
    </tr>`;
  }).join("");

  const when = project.scheduled_at
    ? new Date(project.scheduled_at).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
    : "Not scheduled";

  const printContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Pull Sheet — ${esc(project.name)}</title>
  <style>
    body { font-family: Helvetica, Arial, sans-serif; color:#000; padding:30px; font-size:12px; }
    h1 { font-size:17px; margin:0; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:3px solid #2A5C38; padding-bottom:8px; margin-bottom:12px; }
    .job { background:#f6f6f6; border:1px solid #ddd; border-radius:6px; padding:10px 12px; margin-bottom:12px; }
    .job div { font-size:12px; margin:2px 0; }
    .job strong { font-size:15px; }
    table { width:100%; border-collapse:collapse; }
    th { background:#333; color:#fff; padding:8px 9px; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.07em; }
    .warn { background:#fff3f3; border:2px solid #c00; border-radius:6px; padding:10px 12px; margin-bottom:12px; font-size:12px; }
    .foot { margin-top:22px; border-top:1px solid #ccc; padding-top:9px; font-size:10px; color:#555; }
    .sign { margin-top:18px; display:flex; gap:30px; font-size:11px; }
    .sign div { flex:1; border-top:1px solid #666; padding-top:5px; }
    @media print { body { padding:16px; } tr { break-inside:avoid; page-break-inside:avoid; } }
  </style></head><body>
    <div class="hdr">
      <div>
        <h1>SolarAbility LLC</h1>
        <div style="font-size:10px;color:#555;margin-top:2px">Material Pull Sheet</div>
      </div>
      <div style="text-align:right;font-size:11px;color:#555">
        Printed ${new Date().toLocaleDateString()}
      </div>
    </div>

    <div class="job">
      <div><strong>${esc(project.name)}</strong></div>
      <div>${esc(project.address) || "Address not set"}</div>
      <div><span style="color:#666">Scheduled:</span> <strong>${esc(when)}</strong>${project.systemSize ? ` &nbsp;·&nbsp; <span style="color:#666">System:</span> ${esc(project.systemSize)} kW` : ""}</div>
    </div>

    ${shortages > 0 ? `<div class="warn"><strong>⚠ ${shortages} item${shortages === 1 ? "" : "s"} short on stock.</strong> Check the highlighted rows before loading — order or source these first.</div>` : ""}

    <table>
      <tr>
        <th style="width:38px;text-align:center">Got</th>
        <th style="width:28px;text-align:center">#</th>
        <th>Item</th>
        <th style="width:90px;text-align:center">Qty</th>
        <th style="width:130px;text-align:center">On Hand</th>
      </tr>
      ${rows}
    </table>

    <div class="sign">
      <div>Pulled by</div>
      <div>Date / Time</div>
      <div>Loaded on truck (initials)</div>
    </div>

    <div class="foot">
      ${mats.length} line item${mats.length === 1 ? "" : "s"} · WH = warehouse stock, TRK = already on truck ·
      Remember to run Scan Out in the Hub so inventory stays accurate.
    </div>
  </body></html>`;

  printHTML(printContent);
}

function generateMaterialRFQ(project) {
  const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const mats = (project.materials || []).filter(m => (m.name || "").trim());
  if (mats.length === 0) { alert("No materials on this project yet."); return; }

  const rows = mats.map((m, i) => `<tr>
    <td style="border:1px solid #ddd;padding:6px;text-align:center">${i + 1}</td>
    <td style="border:1px solid #ddd;padding:6px;font-family:monospace;font-size:10.5px">${esc(m.sku || "—")}</td>
    <td style="border:1px solid #ddd;padding:6px">${esc(m.name)}</td>
    <td style="border:1px solid #ddd;padding:6px;text-align:center;font-weight:bold">${esc(m.qty || "")}</td>
    <td style="border:1px solid #ddd;padding:6px;text-align:center">${esc(m.unit || "")}</td>
    <td style="border:1px solid #ddd;padding:6px;width:90px"></td>
  </tr>`).join("");

  const printContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Material List — ${esc(project.name)}</title>
  <style>
    body { font-family: Helvetica, Arial, sans-serif; color:#000; padding:36px; font-size:12px; }
    h1 { font-size:18px; margin:0; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #2A5C38; padding-bottom:8px; margin-bottom:14px; }
    table { width:100%; border-collapse:collapse; margin-top:10px; }
    th { background:#f4f4f4; border:1px solid #ddd; padding:6px; text-align:left; font-size:10px; text-transform:uppercase; letter-spacing:.06em; }
    .meta { display:grid; grid-template-columns:1fr 1fr; gap:4px 18px; margin-bottom:6px; }
    .meta div { font-size:11px; }
    .meta span { color:#666; }
    .foot { margin-top:26px; border-top:1px solid #ccc; padding-top:8px; font-size:9px; color:#555; display:flex; justify-content:space-between; }
    @media print { body { padding:20px; } }
  </style></head><body>
    <div class="hdr">
      <div>
        <h1>SolarAbility LLC</h1>
        <div style="font-size:10px;color:#555;margin-top:3px">10933 S. Avenue O, Chicago, IL 60617 &nbsp;·&nbsp; (312) 312-5337</div>
      </div>
      <div style="text-align:right">
        <div style="font-weight:bold;font-size:14px">Material List / Request for Quote</div>
        <div style="font-size:10px;color:#555;margin-top:3px">${new Date().toLocaleDateString()}</div>
      </div>
    </div>

    <div class="meta">
      <div><span>Job:</span> <strong>${esc(project.name)}</strong></div>
      <div><span>Address:</span> ${esc(project.address) || "—"}</div>
      ${project.systemSize ? `<div><span>System Size:</span> ${esc(project.systemSize)} kW</div>` : ""}
      ${project.scheduled_at ? `<div><span>Scheduled:</span> ${new Date(project.scheduled_at).toLocaleDateString()}</div>` : ""}
    </div>

    <table>
      <tr>
        <th style="width:28px">#</th>
        <th style="width:150px">Part Number</th>
        <th>Description</th>
        <th style="width:50px;text-align:center">Qty</th>
        <th style="width:55px;text-align:center">Unit</th>
        <th style="width:90px">Unit Price</th>
      </tr>
      ${rows}
    </table>

    <div style="margin-top:14px;font-size:11px;color:#444">
      Please quote pricing and availability for the items above. Blank part
      numbers indicate an item we have not previously ordered — substitutions
      welcome if you carry an equivalent.
    </div>

    <div class="foot">
      <span>SolarAbility LLC — NABCEP Certified — ICC #21-0505</span>
      <span>${mats.length} line item${mats.length === 1 ? "" : "s"}</span>
    </div>
  </body></html>`;

  printHTML(printContent);
}

// ─── Calendar Invite (.ics) helpers ────────────────────────────────────────
// Derived from Truck Check itself, so adding or renaming a list there shows up
// here automatically instead of the two silently drifting apart.
const INVITE_CHECKLISTS = [...TRUCK_LIST_NAMES, "None"];

// Sensible starting points from the job type. These are only defaults — the
// project stores whatever you actually choose, and that choice wins everywhere.
function suggestTruckList(proj) {
  if (!proj) return "";
  if (proj.serviceSubtype === "Panel Cleaning") return "Panel Cleaning";
  const byType = {
    survey: "Survey w/ Poke Test",
    new_install: "Solar Install Day",
    removal_reinstall: "Removal & Reinstall",
    service: "O&M / Service Call",
    electrical: "O&M / Service Call",
  };
  const name = byType[proj.projectType] || "";
  return TRUCK_LIST_NAMES.includes(name) ? name : "";
}

// Which reference documents go with a given scope of work. Explicit pairings
// set on the procedure come first; after that, equipment and job-type words
// shared between the two. Each suggestion carries the reason it appeared, so
// nobody has to guess why the Hub is proposing it.
const EQUIPMENT_WORDS = ["solaredge", "enphase", "locus", "also energy", "setapp", "iq gateway", "envoy", "scanifly"];
const TOPIC_WORDS = [
  "inverter", "optimizer", "string", "cellular", "modem", "rs485", "communication", "comms",
  "production", "underperformance", "roof leak", "leak", "removal", "reinstall", "arc fault",
  "error code", "monitoring", "meter",
];

// Paid man-hours against crew size gives real time on site, and a point at
// which to stop troubleshooting and start documenting. An unfinished diagnosis
// with a complete report still gets paid; a finished one without photos does not.
function timeBudgetFor(pb, crewCount) {
  const budget = parseFloat(pb?.timeBudget);
  if (!budget || budget <= 0) return null;
  const crew = Math.max(1, parseInt(crewCount, 10) || parseInt(pb?.timeCrew, 10) || 1);
  const onSiteHours = budget / crew;
  const docMinutes = Math.max(20, Math.round(onSiteHours * 60 * 0.3));  // leave ~30% for the report
  const troubleshootMinutes = Math.max(0, Math.round(onSiteHours * 60) - docMinutes);
  const fmt = (m) => m >= 60 ? `${Math.floor(m / 60)}h ${m % 60 ? `${m % 60}m` : ""}`.trim() : `${m}m`;
  // The NTE expressed as time: at the contract rate, how many man-hours the
  // ceiling actually buys. Turns "can we stay longer" into a number.
  const rate = parseFloat(pb?.payRate) || null;
  const nte = parseFloat(pb?.payNte) || null;
  const perManHour = rate ? rate / budget : null;
  const nteManHours = (perManHour && nte) ? Math.round((nte / perManHour) * 100) / 100 : null;

  return {
    budget, crew,
    onSite: fmt(Math.round(onSiteHours * 60)),
    troubleshoot: fmt(troubleshootMinutes),
    documenting: fmt(docMinutes),
    tight: onSiteHours * 60 < 120,
    rate, nte, perManHour: perManHour ? Math.round(perManHour * 100) / 100 : null,
    nteManHours,
    nteOnSite: nteManHours ? fmt(Math.round((nteManHours / crew) * 60)) : null,
  };
}

function suggestedReferences(mainPb, proj, allPlaybooks) {
  if (!mainPb) return [];
  const refs = (allPlaybooks || []).filter(b => b.id !== mainPb.id && isReferenceDoc(b));
  if (refs.length === 0) return [];

  const textOf = (b) => [b.title, b.summary, b.serviceType, ...(b.steps || []).slice(0, 6)].join(" ").toLowerCase();
  const mainText = [textOf(mainPb), proj?.serviceSubtype || "", proj?.partner || "", proj?.name || ""].join(" ").toLowerCase();
  const explicit = new Set(mainPb.suggests || []);

  return refs.map(r => {
    if (explicit.has(r.id)) return { ref: r, score: 100, why: "Paired with this scope" };
    const rt = textOf(r);
    const equip = EQUIPMENT_WORDS.filter(w => mainText.includes(w) && rt.includes(w));
    const topic = TOPIC_WORDS.filter(w => mainText.includes(w) && rt.includes(w));
    if (equip.length === 0 && topic.length === 0) return null;
    const label = (equip[0] || topic[0] || "");
    return {
      ref: r,
      score: equip.length * 10 + topic.length,
      why: label ? label.replace(/\b\w/g, ch => ch.toUpperCase()) : "Related",
    };
  }).filter(Boolean).sort((a, b) => b.score - a.score).slice(0, 4);
}

function suggestPlaybook(proj, playbooks) {
  if (!proj || !playbooks?.length) return null;
  const svc = (proj.serviceSubtype || "").toLowerCase();
  const partner = (proj.partner || "").toLowerCase();
  const firstWord = svc.split(" ")[0];
  const typeMatch = (b) => firstWord && (b.serviceType || "").toLowerCase().includes(firstWord);
  const partnerMatch = (b) => partner && (b.partnerNotes || []).some(n => (n.partner || "").toLowerCase() === partner);
  const pool = playbooks.filter(b => !isReferenceDoc(b)); // never suggest a manual as the job's procedure
  return pool.find(b => typeMatch(b) && partnerMatch(b))
    || pool.find(typeMatch)
    || null;
}

function icsEscape(s) {
  return String(s || "").replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");
}
function icsDate(d) {
  return d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
}
function foldIcsLine(line) {
  // RFC 5545 line folding: max 75 octets per line, continuation lines start with a space
  if (line.length <= 75) return line;
  let out = line.slice(0, 75);
  let rest = line.slice(75);
  while (rest.length > 0) {
    out += "\r\n " + rest.slice(0, 74);
    rest = rest.slice(74);
  }
  return out;
}
// Rescheduling only works if the invite keeps the SAME uid and bumps
// SEQUENCE. A fresh uid makes the calendar treat it as a brand-new event, so
// the crew ends up with the old time still sitting there alongside the new one.
function buildICS({ title, start, end, location, description, organizerEmail, attendees, uid, sequence, cancel }) {
  const eventUid = uid || `sa-${Date.now()}-${Math.random().toString(36).slice(2)}@solarability.com`;
  const lines = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//SolarAbility//Safety Hub//EN",
    "CALSCALE:GREGORIAN", cancel ? "METHOD:CANCEL" : "METHOD:REQUEST", "BEGIN:VEVENT",
    `UID:${eventUid}`, `DTSTAMP:${icsDate(new Date())}`,
    `DTSTART:${icsDate(start)}`, `DTEND:${icsDate(end)}`,
    foldIcsLine(`SUMMARY:${icsEscape(title)}`),
    foldIcsLine(`LOCATION:${icsEscape(location)}`),
    foldIcsLine(`DESCRIPTION:${icsEscape(description)}`),
    `ORGANIZER;CN=SolarAbility LLC:mailto:${organizerEmail}`,
    ...attendees.map(a => `ATTENDEE;CN=${icsEscape(a.name)};RSVP=TRUE:mailto:${a.email}`),
    cancel ? "STATUS:CANCELLED" : "STATUS:CONFIRMED",
    `SEQUENCE:${sequence || 0}`,
    "END:VEVENT", "END:VCALENDAR",
  ];
  return lines.join("\r\n");
}
function base64Utf8(str) {
  return btoa(unescape(encodeURIComponent(str)));
}

// ─── Calls & texts on a job (Quo) ───────────────────────────────────────────
function commIcon(c) { return c.kind === "sms" ? (c.direction === "outgoing" ? "💬→" : "💬") : c.kind === "voicemail" ? "📨" : c.status === "missed" ? "📵" : "📞"; }
function commTitle(c) {
  if (c.kind === "sms") return c.direction === "outgoing" ? `Text sent${c.sent_by ? ` by ${c.sent_by.split("@")[0]}` : ""}` : "Text from homeowner";
  if (c.kind === "voicemail") return `Voicemail${c.duration ? ` · ${c.duration}s` : ""}`;
  if (c.status === "missed") return "Missed call";
  return `${c.direction === "outgoing" ? "Outgoing" : "Incoming"} call${c.duration ? ` · ${Math.round(c.duration / 60)} min` : ""}`;
}
function CommRow({ c, onHandled, onAddTask, compact }) {
  const [open, setOpen] = useState(false);
  const next = Array.isArray(c.next_steps) ? c.next_steps : [];
  const summary = Array.isArray(c.summary) ? c.summary : [];
  return (
    <div style={{ padding: compact ? "8px 0" : "10px 12px", borderTop: `1px solid ${C.border}`, background: compact ? "transparent" : C.surface, borderRadius: compact ? 0 : 8, marginBottom: compact ? 0 : 6 }}>
      <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
        <span style={{ fontSize: 15, flexShrink: 0 }}>{commIcon(c)}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.light }}>{commTitle(c)}{c.partner ? <span style={S.badge(C.gold)}>{c.partner}</span> : null}</span>
            <span style={{ fontSize: 11, color: C.muted }}>{fmtJobDateTime(c.at)}</span>
          </div>
          {c.body ? <div style={{ fontSize: 13, color: C.light, marginTop: 3, whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{c.body}</div> : null}
          {summary.length > 0 && (
            <div style={{ marginTop: 4 }}>
              {summary.map((s, i) => <div key={i} style={{ fontSize: 12.5, color: C.light, lineHeight: 1.5 }}>• {s}</div>)}
            </div>
          )}
          {next.length > 0 && (
            <div style={{ marginTop: 6, padding: "6px 8px", background: `${C.warning}14`, border: `1px solid ${C.warning}44`, borderRadius: 6 }}>
              <div style={{ fontSize: 11, color: C.warning, fontWeight: 800, letterSpacing: 0.4 }}>NEXT STEPS</div>
              {next.map((s, i) => (
                <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 12.5, color: C.light, marginTop: 3 }}>
                  <span style={{ flex: 1 }}>→ {s}</span>
                  {onAddTask && <button type="button" onClick={() => onAddTask(s)} style={{ ...S.btnS, fontSize: 11, padding: "3px 8px" }}>+ task</button>}
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 10, marginTop: 6, flexWrap: "wrap", alignItems: "center", fontSize: 11 }}>
            {c.external_number && <span style={{ color: C.muted }}>{c.external_number}</span>}
            {c.recording_url && <a href={c.recording_url} target="_blank" rel="noopener noreferrer" style={{ color: C.gold }}>Recording</a>}
            {c.quo_link && <a href={c.quo_link} target="_blank" rel="noopener noreferrer" style={{ color: C.gold }}>Open in Quo</a>}
            {Array.isArray(c.transcript) && c.transcript.length > 0 && <button type="button" onClick={() => setOpen(o => !o)} style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", padding: 0, fontSize: 11 }}>{open ? "Hide transcript" : "Transcript"}</button>}
            {c.status && c.kind === "sms" && c.direction === "outgoing" && <span style={{ color: c.status === "failed" || c.status === "undelivered" ? C.danger : C.muted }}>{c.status}</span>}
            {onHandled && !c.handled && <button type="button" onClick={() => onHandled(c)} style={{ ...S.btnS, fontSize: 11, padding: "3px 8px", marginLeft: "auto" }}>✓ Handled</button>}
          </div>
          {open && (
            <div style={{ marginTop: 6, maxHeight: 220, overflowY: "auto", fontSize: 12, color: C.muted, lineHeight: 1.5 }}>
              {c.transcript.map((d, i) => <div key={i}><strong style={{ color: d.who === "us" ? C.gold : C.light }}>{d.who === "us" ? "Us" : "Them"}:</strong> {d.text}</div>)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CommsCard({ p, user, visit, onAddTask }) {
  const [rows, setRows] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const load = useCallback(async () => { setRows(await db.getComms(p.id)); setLoaded(true); }, [p.id]);
  useEffect(() => { let live = true; db.getComms(p.id).then(r => { if (live) { setRows(r); setLoaded(true); } }); return () => { live = false; }; }, [p.id]);
  const phone = (p.clientPhone || "").trim();
  const templates = smsTemplates(p, visit, user);
  const send = async () => {
    const content = text.trim();
    if (!content) return;
    if (!phone) return alert("Add the homeowner's phone number on the job first.");
    if (!window.confirm(`Text ${p.client || "the homeowner"} at ${phone}?\n\n${content}`)) return;
    setSending(true);
    try {
      await db.quoSend({ to: phone, content, projectId: p.id, kind: (templates.find(tp => tp.text === content) || {}).id || "custom" });
      setText("");
      await load();
    } catch(e) { alert(`Text not sent: ${e.message}`); }
    setSending(false);
  };
  return (
    <div style={S.card}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>📞 Calls & texts</h3>
        <span style={{ fontSize: 11, color: C.muted }}>{phone ? `Homeowner ${phone} · company line (312) 820-9027` : "No homeowner phone on this job"}</span>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
        {templates.map(tp => <button key={tp.id} type="button" onClick={() => setText(tp.text)} style={{ ...S.btnS, fontSize: 11.5, padding: "5px 10px", borderColor: text === tp.text ? C.gold : C.border, color: text === tp.text ? C.gold : C.muted }}>{tp.label}</button>)}
      </div>
      <textarea style={{ ...S.textarea, minHeight: 64 }} rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="Pick a template or type a text to the homeowner…" />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 11, color: text.length > 300 ? C.warning : C.muted }}>{text.length} chars{text.length > 160 ? ` · ${Math.ceil(text.length / 153)} segments` : ""}</span>
        <button type="button" onClick={send} disabled={sending || !text.trim() || !phone} style={{ ...S.btnP, fontSize: 12.5, padding: "8px 14px", opacity: !text.trim() || !phone ? 0.5 : 1 }}>{sending ? "Sending…" : "Send text"}</button>
      </div>
      {!loaded && <div style={{ fontSize: 12, color: C.muted }}>Loading…</div>}
      {loaded && rows.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>Nothing yet. Calls and texts on the company number with this homeowner's phone show up here automatically (needs the Quo webhook — see QUO_SETUP.md).</div>}
      {rows.map(c => <CommRow key={c.id} c={c} onAddTask={onAddTask} onHandled={!c.handled ? async (row) => { await db.markCommHandled(row.id); setRows(rs => rs.map(x => x.id === row.id ? { ...x, handled: true } : x)); } : null} />)}
    </div>
  );
}

function PermitCard({ p, u, canManage }) {
  const pm = p.permit || null;
  const st = permitStage(p);
  const set = (patch) => u("permit", { ...(pm || {}), ...patch });
  const setDoc = (id, patch) => set({ docs: (pm.docs || []).map(d => d.id === id ? { ...d, ...patch } : d) });
  const setInsp = (id, patch) => set({ inspections: (pm.inspections || []).map(i => i.id === id ? { ...i, ...patch } : i) });
  const ro = !canManage;
  const kindGuess = p.projectType === "rnr" || /remov|reinstall|re-roof|r&r/i.test(`${p.serviceSubtype || ""} ${p.scope || ""} ${p.name || ""}`) ? "rnr" : (p.projectType === "install" || /install/i.test(p.projectType || "")) ? "solar" : "electrical";
  const docsLeft = (pm?.docs || []).filter(d => !d.done).length;
  const smallIn = { ...S.input, padding: "8px 10px", fontSize: 13 };
  return (
    <div style={{ ...S.card, border: `1px solid ${st.tone}66` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>📋 Permit & inspections</h3>
        <span style={{ fontSize: 12, color: st.tone, fontWeight: 700 }}>{st.label}</span>
      </div>
      {(!pm || pm.required === null || pm.required === undefined) && (
        <div>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 8 }}>Does this job need a permit? Pick the kind of work and the required-docs checklist and inspections are filled in — edit them to match the village.</div>
          {!ro && <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {Object.entries(PERMIT_PRESETS).map(([k, ps]) => (
              <button key={k} type="button" onClick={() => u("permit", permitFromPreset(k))} style={{ ...S.btnS, fontSize: 12.5, padding: "8px 12px", border: `1px solid ${k === kindGuess ? C.gold : C.border}` }}>Yes — {ps.label}</button>
            ))}
            <button type="button" onClick={() => u("permit", { required: false })} style={{ ...S.btnS, fontSize: 12.5, padding: "8px 12px" }}>No permit needed</button>
          </div>}
        </div>
      )}
      {pm && pm.required === false && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", fontSize: 13, color: C.muted }}>
          <span>No permit needed for this job.</span>
          {!ro && <button type="button" onClick={() => u("permit", null)} style={{ ...S.btnS, fontSize: 12, padding: "5px 10px" }}>Change</button>}
        </div>
      )}
      {pm && pm.required === true && (
        <div style={{ display: "grid", gap: 12 }}>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10 }}>
            <Field label="Kind of work"><select style={{ ...S.select, padding: "8px 10px", fontSize: 13 }} disabled={ro} value={pm.kind || ""} onChange={e => { if (window.confirm("Reload the required-docs and inspection lists for this kind of work? Anything ticked is kept by name.")) { const next = permitFromPreset(e.target.value, pm); next.docs = next.docs.map(d => ({ ...d, done: !!(pm.docs || []).find(x => x.name === d.name && x.done) })); u("permit", next); } else set({ kind: e.target.value }); }}>{Object.entries(PERMIT_PRESETS).map(([k, ps]) => <option key={k} value={k}>{ps.label}</option>)}</select></Field>
            <Field label="Jurisdiction (city / village)"><input style={smallIn} readOnly={ro} value={pm.jurisdiction || ""} onChange={e => set({ jurisdiction: e.target.value })} placeholder="City of Chicago, Oak Lawn…" /></Field>
            <Field label="Permit type"><input style={smallIn} readOnly={ro} value={pm.type || ""} onChange={e => set({ type: e.target.value })} /></Field>
            <Field label="Permit #"><input style={smallIn} readOnly={ro} value={pm.number || ""} onChange={e => set({ number: e.target.value })} placeholder="once issued" /></Field>
            <Field label="Applied on"><input style={smallIn} type="date" readOnly={ro} value={pm.appliedOn || ""} onChange={e => set({ appliedOn: e.target.value })} /></Field>
            <Field label="Issued on"><input style={smallIn} type="date" readOnly={ro} value={pm.issuedOn || ""} onChange={e => set({ issuedOn: e.target.value })} /></Field>
            <Field label="Closed / finaled on"><input style={smallIn} type="date" readOnly={ro} value={pm.closedOn || ""} onChange={e => set({ closedOn: e.target.value })} /></Field>
            <Field label="Portal / application link"><div style={{ display: "flex", gap: 6 }}><input style={smallIn} readOnly={ro} value={pm.portalUrl || ""} onChange={e => set({ portalUrl: e.target.value })} placeholder="https://…" />{pm.portalUrl && <a href={pm.portalUrl} target="_blank" rel="noopener noreferrer" style={{ ...S.btnS, textDecoration: "none", padding: "8px 10px", fontSize: 12 }}>Open</a>}</div></Field>
          </div>

          <div>
            <div style={{ ...S.label, marginBottom: 6, display: "flex", justifyContent: "space-between" }}><span>Required documents</span><span style={{ color: docsLeft ? C.warning : C.success, fontWeight: 700 }}>{(pm.docs || []).length - docsLeft}/{(pm.docs || []).length} ready</span></div>
            <div style={{ display: "grid", gap: 4 }}>
              {(pm.docs || []).map(d => (
                <div key={d.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, padding: "6px 8px", background: C.surface, borderRadius: 8 }}>
                  <input type="checkbox" disabled={ro} checked={!!d.done} onChange={e => setDoc(d.id, { done: e.target.checked, doneAt: e.target.checked ? new Date().toISOString() : "" })} />
                  <span style={{ flex: 1, color: d.done ? C.muted : C.light, textDecoration: d.done ? "line-through" : "none" }}>{d.name}</span>
                  <input style={{ ...smallIn, width: 160, padding: "4px 8px", fontSize: 12 }} readOnly={ro} value={d.note || ""} onChange={e => setDoc(d.id, { note: e.target.value })} placeholder="note / where it is" />
                  {!ro && <button type="button" onClick={() => set({ docs: pm.docs.filter(x => x.id !== d.id) })} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 2 }}>{I.trash || "✕"}</button>}
                </div>
              ))}
            </div>
            {!ro && <button type="button" onClick={() => { const name = window.prompt("Document name:"); if (name && name.trim()) set({ docs: [...(pm.docs || []), { id: newPermitId(), name: name.trim(), done: false, note: "" }] }); }} style={{ ...S.btnS, fontSize: 12, padding: "5px 10px", marginTop: 6 }}>+ Add document</button>}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Upload the actual files under Job Documents below; tick here once they're in the application.</div>
          </div>

          <div>
            <div style={{ ...S.label, marginBottom: 6 }}>Inspections</div>
            <div style={{ display: "grid", gap: 4 }}>
              {(pm.inspections || []).map(i => (
                <div key={i.id} style={{ display: "grid", gridTemplateColumns: "minmax(140px, 1.4fr) 140px 120px minmax(120px, 1fr) auto", gap: 8, alignItems: "center", fontSize: 13, padding: "6px 8px", background: C.surface, borderRadius: 8, borderLeft: `3px solid ${i.result === "passed" ? C.success : i.result === "failed" ? C.danger : i.scheduledOn ? C.info : C.border}` }}>
                  <input style={{ ...smallIn, padding: "4px 8px", fontSize: 12.5 }} readOnly={ro} value={i.type} onChange={e => setInsp(i.id, { type: e.target.value })} />
                  <input style={{ ...smallIn, padding: "4px 8px", fontSize: 12.5 }} type="date" readOnly={ro} value={i.scheduledOn || ""} onChange={e => setInsp(i.id, { scheduledOn: e.target.value })} title="Scheduled" />
                  <select style={{ ...S.select, padding: "4px 8px", fontSize: 12.5 }} disabled={ro} value={i.result || ""} onChange={e => setInsp(i.id, { result: e.target.value, resultAt: e.target.value ? new Date().toISOString() : "" })}>
                    <option value="">— result —</option><option value="passed">Passed</option><option value="partial">Partial / corrections</option><option value="failed">Failed</option>
                  </select>
                  <input style={{ ...smallIn, padding: "4px 8px", fontSize: 12 }} readOnly={ro} value={i.notes || ""} onChange={e => setInsp(i.id, { notes: e.target.value })} placeholder="inspector, corrections…" />
                  {!ro ? <button type="button" onClick={() => set({ inspections: pm.inspections.filter(x => x.id !== i.id) })} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 2 }}>{I.trash || "✕"}</button> : <span />}
                </div>
              ))}
            </div>
            {!ro && <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
              <button type="button" onClick={() => { const type = window.prompt("Inspection:", "Re-inspection"); if (type && type.trim()) set({ inspections: [...(pm.inspections || []), { id: newPermitId(), type: type.trim(), scheduledOn: "", result: "", notes: "" }] }); }} style={{ ...S.btnS, fontSize: 12, padding: "5px 10px" }}>+ Add inspection</button>
              {(pm.inspections || []).some(i => i.result === "failed") && <button type="button" onClick={() => { const f = pm.inspections.find(i => i.result === "failed"); set({ inspections: [...pm.inspections.map(i => i.id === f.id ? { ...i, result: "failed-reinspect" } : i), { id: newPermitId(), type: `Re-inspection — ${f.type}`, scheduledOn: "", result: "", notes: f.notes || "" }] }); }} style={{ ...S.btnS, fontSize: 12, padding: "5px 10px", color: C.warning }}>Schedule re-inspection</button>}
            </div>}
          </div>

          <Field label="Permit notes"><textarea style={{ ...S.textarea, minHeight: 48 }} rows={2} readOnly={ro} value={pm.notes || ""} onChange={e => set({ notes: e.target.value })} placeholder="Reviewer comments, fee paid, who to call at the village…" /></Field>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 11, color: C.muted }}>
            <span>The permit # goes on the crew invite and Schedule card once issued; the Today queue chases whatever is next.</span>
            {!ro && <button type="button" onClick={() => { if (window.confirm("Mark this job as NOT needing a permit? The checklist is removed.")) u("permit", { required: false }); }} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>no permit needed after all</button>}
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectEditor({ project, onSave, onCancel, onDelete, user, isAdmin, canManage = isAdmin, t, onOpenMap }) {
  const isNew = !project;
  // Snapshot at open: Cancel warns if anything changed and wasn't saved
  const openedRef = useRef(null);
  const [p, setP] = useState(project || {
    id: `proj-${Date.now()}`, name: "", client: "", clientEmail: "", clientPhone: "", address: "", systemSize: "",
    status: "not_started", completion: 0, tasks: [], materials: [], notes: [], members: [],
    projectType: "", serviceSubtype: "", partner: "", playbookId: "", referenceIds: [], truckList: "", scheduled_at: "", created_at: new Date().toISOString(), created_by: user?.name,
  });
  const [newMember, setNewMember] = useState("");
  const u = (f, v) => setP(prev => ({ ...prev, [f]: v }));
  // Other open jobs for the same house — shown as a banner with a Merge button
  const [twins, setTwins] = useState([]);
  const [merging, setMerging] = useState(false);
  useEffect(() => {
    if (!project?.id || !project.address) return;
    let live = true;
    db.getProjects().then(list => { if (live) setTwins((list || []).filter(x => x.status !== "completed" && looksLikeSameJob(project, x))); }).catch(() => {});
    return () => { live = false; };
  }, [project?.id, project?.address]);
  if (openedRef.current === null) openedRef.current = JSON.stringify(p);
  const isDirty = () => JSON.stringify(p) !== openedRef.current;
  const cancelEdit = () => {
    if (isDirty() && !window.confirm("You have unsaved changes on this job. Leave without saving?")) return;
    onCancel();
  };
  const confirmDelete = () => {
    const liveInvited = (p.visits || []).filter(v => !v.cancelled && (v.invited || []).length > 0);
    if (liveInvited.length) return alert(`This job still has ${liveInvited.length} visit(s) on people's calendars (${liveInvited.map(v => v.label).join(", ")}). Cancel those visits first so the invites come off their calendars, then delete.`);
    const forms = (projForms || []).length;
    const lines = [
      `Delete "${p.name}"?`,
      forms ? `• ${forms} form(s) filed on it stay in the database but lose their job link` : "• No forms are linked to it",
      (p.visits || []).length ? `• ${(p.visits || []).length} visit(s) and their invite history go` : "",
      p.invoice?.number ? `• Invoice #${p.invoice.number} record goes with it` : "",
      "", "This cannot be undone. Mark it Completed or On Hold instead if you just want it out of the way.",
    ].filter(l => l !== "");
    if (window.confirm(lines.join("\n"))) onDelete(p.id);
  };

  // ─── Calendar Invite ──────────────────────────────────────────────────────
  // ─── Invoice tracking ─────────────────────────────────────────────────────
  const inv = p.invoice || {};
  const setInv = (k, v) => setP(prev => ({ ...prev, invoice: { ...(prev.invoice || {}), [k]: v } }));

  const invoiceTotalFromCosting = (() => {
    const cst = p.costing;
    if (!cst) return null;
    return typeof cst.grand === "number" ? cst.grand : null;
  })();

  // Net 30 from the date sent
  const dueDate = inv.sentDate ? (() => {
    const d = new Date(inv.sentDate + "T00:00:00");
    d.setDate(d.getDate() + 30);
    return d;
  })() : null;

  const invStatus = inv.paidDate ? "paid" : inv.sentDate ? "sent" : (inv.number || inv.amount) ? "draft" : "none";
  const daysOutstanding = inv.sentDate && !inv.paidDate
    ? Math.floor((Date.now() - new Date(inv.sentDate + "T00:00:00").getTime()) / 86400000)
    : null;
  const isOverdue = invStatus === "sent" && dueDate && Date.now() > dueDate.getTime();

  const copyForQuickBooks = () => {
    const cst = p.costing;
    if (!cst) { alert("No Job Costing data on this project yet — add costs in the Job Costing tab first."); return; }
    const money = (v) => "$" + (Number(v) || 0).toFixed(2);
    const lines = [];
    lines.push(`${p.name || p.client}${p.address ? " — " + p.address : ""}`);
    lines.push("");
    (cst.labor || []).forEach(l => {
      const hrs = Number(l.hours) || 0, rate = Number(l.rate) || 0;
      if (hrs || rate) lines.push(`Labor: ${l.who || "Labor"} — ${hrs} hrs @ ${money(rate)}/hr = ${money(hrs * rate)}`);
    });
    (cst.materials || []).forEach(m => {
      const qty = Number(m.qty) || 0, cost = Number(m.cost) || 0;
      if (qty || cost) lines.push(`Material: ${m.name || "Material"} — ${qty} ${m.unit || ""} @ ${money(cost)} = ${money(qty * cost)}`);
    });
    (cst.other || []).forEach(o => {
      if (o.amount) lines.push(`${o.name || "Other"}: ${money(o.amount)}`);
    });
    lines.push("");
    lines.push(`Subtotal: ${money(cst.subtotal || 0)}`);
    if (cst.margin) lines.push(`Margin (${cst.margin}%): ${money((cst.subtotal || 0) * (cst.margin / 100))}`);
    lines.push(`TOTAL: ${money(cst.grand || 0)}`);
    const text = lines.join("\n");
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => alert("Line items copied — paste into QuickBooks."),
        () => window.prompt("Copy these line items:", text)
      );
    } else {
      window.prompt("Copy these line items:", text);
    }
  };

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteDuration, setInviteDuration] = useState("2"); // kept as string; parsed only when actually used
  const [inviteChecklist, setInviteChecklist] = useState(INVITE_CHECKLISTS[0]);
  const [inviteNotes, setInviteNotes] = useState("");
  const [inviteRecipients, setInviteRecipients] = useState({}); // { memberIndex: true/false }
  const [inviteHomeowner, setInviteHomeowner] = useState(true);
  const [inviteText, setInviteText] = useState(true); // also text the homeowner the confirmation (Quo)
  const [inviteMode, setInviteMode] = useState("update"); // "update" | "add"
  const [activeVisit, setActiveVisit] = useState(null); // visit id being invited

  // A job can need more than one trip. Visits hold their own date and invite
  // thread; the project's scheduled_at mirrors the next open visit so the
  // Schedule tab and everything built on it keep working unchanged.
  const visits = useMemo(() => {
    const v = p.visits || [];
    if (v.length > 0) {
      // Guard against two visits sharing an id (would make the date picker,
      // invite target and calendar uid of one visit land on the other).
      const seen = new Set();
      return v.map((x, i) => {
        let id = x.id || `v-${i + 1}`;
        if (seen.has(id)) id = `${id}-${i + 1}`;
        seen.add(id);
        return id === x.id ? x : { ...x, id };
      });
    }
    // Legacy projects: synthesise a first visit from scheduled_at. If an
    // invite already went out (seq > 0) the crew with emails were on it.
    if (p.scheduled_at) {
      const legacyInvited = (p.inviteSeq || 0) > 0
        ? [...(p.members || []).map(m => m.name || "").filter(n => n.includes("@")), ...((p.clientEmail || "").includes("@") && p.inviteUidOwner ? [p.clientEmail.trim()] : [])]
        : [];
      return [{
        id: "v1", label: "Visit 1", scheduled_at: p.scheduled_at,
        uidCrew: p.inviteUidCrew || "", uidOwner: p.inviteUidOwner || "",
        seq: p.inviteSeq || 0, invited: legacyInvited, cancelled: false,
      }];
    }
    return [];
  }, [p.visits, p.scheduled_at, p.inviteUidCrew, p.inviteUidOwner, p.inviteSeq, p.members, p.clientEmail]);

  const writeVisits = (next) => {
    // Keep scheduled_at pointing at the soonest upcoming, non-cancelled visit
    const live = next.filter(v => !v.cancelled && v.scheduled_at);
    const soonest = live.slice().sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
    setP(prev => ({ ...prev, visits: next, scheduled_at: soonest?.scheduled_at || "" }));
  };

  const addVisit = () => {
    const n = visits.length + 1;
    writeVisits([...visits, {
      id: `v${Date.now()}`, label: `Visit ${n}`, scheduled_at: "",
      uidCrew: "", uidOwner: "", seq: 0, invited: [], cancelled: false,
    }]);
  };
  const updVisit = (id, field, val) => writeVisits(visits.map(v => v.id === id ? { ...v, [field]: val } : v));
  const removeVisit = (id) => {
    if (!window.confirm("Remove this visit? If invites went out, cancel it first so it comes off their calendars.")) return;
    writeVisits(visits.filter(v => v.id !== id));
  };
  const [playbooks, setPlaybooks] = useState([]);
  const [invitePlaybook, setInvitePlaybook] = useState("");
  const [procRuns, setProcRuns] = useState([]);
  const [projForms, setProjForms] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const refDataRef = useRef(false);
  if (!refDataRef.current) {
    refDataRef.current = true;
    db.getPlaybooks().then(setPlaybooks).catch(() => {});
    // If the partners table comes back empty for this login (database
    // permission), still offer the partners already used on jobs so the
    // coordinator can pick one.
    db.getPartners({ fresh: true }).then(async (list) => {
      if ((list || []).length > 0) { setPartnersList(list); return; }
      const names = [...new Set((await db.getProjects().catch(() => [])).map(x => (x.partner || "").trim()).filter(Boolean))].sort();
      setPartnersList(names.map(n => ({ id: `name:${n}`, name: n, fromJobs: true, customFields: [], requiredPhotos: [], defaultTasks: [] })));
    }).catch(() => {});
    if (project?.id) db.getProcedureRuns(project.id).then(setProcRuns).catch(() => {});
    if (project?.id) db.getProjectForms(project).then(setProjForms).catch(() => {});
  }
  const [inviteSending, setInviteSending] = useState(false);

  const emailMembers = (p.members || []).map((m, i) => ({ ...m, idx: i })).filter(m => (m.name || "").includes("@"));

  // Sending an invite changes real state: the calendar uid, the sequence, who
  // was invited, and the date the Schedule tab reads. Relying on the user to
  // hit Save afterwards meant the Schedule showed a stale time and the next
  // send created a duplicate event. So the invite persists itself.
  const persist = async (nextVisits, extra = {}) => {
    const live = (nextVisits || []).filter(v => !v.cancelled && v.scheduled_at);
    const soonest = live.slice().sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
    const next = {
      ...p, ...extra,
      visits: nextVisits,
      scheduled_at: soonest?.scheduled_at || "",
      completion: taskCompletion,
    };
    setP(next);
    try {
      const res = await db.saveProject(next, { base: JSON.parse(openedRef.current || "null") });
      const final = res?.project || next;
      setP(final);
      openedRef.current = JSON.stringify(final);
      onSave(final, { stay: true });
      return res ? res.ok !== false : true;
    } catch (err) {
      console.warn("Auto-save after invite failed:", err);
      return false;
    }
  };

  // Take ONE person off a visit: a cancel for the same event uid goes only to
  // them, so it disappears from their calendar and nobody else is touched.
  const uninvite = async (visit, email) => {
    const homeownerEmail = (p.clientEmail || "").trim().toLowerCase();
    const isOwner = email.toLowerCase() === homeownerEmail;
    const alsoMember = !isOwner && (p.members || []).some(m => (m.name || "").toLowerCase() === email.toLowerCase());
    if (!window.confirm(`Remove ${email} from this visit? They'll get a cancellation for it.${alsoMember ? "\n\nThey will also be taken off the job's team so it leaves their Hub schedule." : ""}`)) return;
    const start = new Date(visit.scheduled_at);
    const end = new Date(start.getTime() + (parseFloat(visit.durationHours) || 2) * 3600000);
    const seq = (visit.seq || 0) + 1;
    const uid = isOwner ? visit.uidOwner : visit.uidCrew;
    try {
      if (uid) {
        const ics = buildICS({
          title: `${p.name || p.client} — REMOVED FROM YOUR SCHEDULE`,
          start, end, location: p.address || "",
          description: "You are no longer assigned to this visit.",
          organizerEmail: ADMIN_EMAIL,
          attendees: [{ name: email, email }],
          uid, sequence: seq, cancel: true,
        });
        await db.sendCalendarInvite({ to: [email], subject: `REMOVED: ${p.name || p.client} — ${fmtJobDate(start.toISOString())}`, icsBase64: base64Utf8(ics), audience: isOwner ? "homeowner" : "crew" });
      }
      const nextVisits = visits.map(v => v.id === visit.id ? {
        ...v, seq, invited: (v.invited || []).filter(e => e.toLowerCase() !== email.toLowerCase()),
        inviteLog: [...(v.inviteLog || []), { at: new Date().toISOString(), by: user?.name || "", mode: "uninvite", to: [email], seq, when: v.scheduled_at }],
      } : v);
      const extra = alsoMember ? { members: (p.members || []).filter(m => (m.name || "").toLowerCase() !== email.toLowerCase()) } : {};
      const saved = await persist(nextVisits, extra);
      alert(saved ? `${email} removed ✓` : "Cancellation sent, but the job did not save — hit Save Project.");
    } catch(e) { alert("Could not send the cancellation — check the send-notification function."); }
  };

  // Pull a visit off everyone's calendar. METHOD:CANCEL with the same uid and
  // a bumped sequence is what tells a calendar to remove the event — simply
  // deleting it here would leave it sitting on the crew's phones.
  const cancelVisit = async (visit) => {
    const recipients = visit.invited || [];
    if (recipients.length === 0) {
      if (!window.confirm("No invites were sent for this visit. Mark it cancelled?")) return;
      writeVisits(visits.map(v => v.id === visit.id ? { ...v, cancelled: true } : v));
      return;
    }
    if (!window.confirm(`Cancel this visit and remove it from ${recipients.length} calendar(s)?`)) return;

    const start = new Date(visit.scheduled_at);
    const end = new Date(start.getTime() + 2 * 60 * 60 * 1000);
    const seq = (visit.seq || 0) + 1;
    const homeownerEmail = (p.clientEmail || "").trim();
    const crewList = recipients.filter(r => r !== homeownerEmail);

    try {
      if (crewList.length > 0 && visit.uidCrew) {
        const ics = buildICS({
          title: `${p.name || p.client} — CANCELLED`,
          start, end, location: p.address || "",
          description: "This appointment has been cancelled.",
          organizerEmail: ADMIN_EMAIL,
          attendees: crewList.map(e => ({ name: e, email: e })),
          uid: visit.uidCrew, sequence: seq, cancel: true,
        });
        await db.sendCalendarInvite({
          to: crewList,
          subject: `CANCELLED: ${p.name || p.client} — ${fmtJobDate(start.toISOString())}`,
          icsBase64: base64Utf8(ics), audience: "crew",
        });
      }
      if (recipients.includes(homeownerEmail) && visit.uidOwner) {
        const ics = buildICS({
          title: "SolarAbility — Appointment Cancelled",
          start, end, location: p.address || "",
          description: "Your appointment has been cancelled. We'll be in touch to reschedule.",
          organizerEmail: ADMIN_EMAIL,
          attendees: [{ name: p.client || "Homeowner", email: homeownerEmail }],
          uid: visit.uidOwner, sequence: seq, cancel: true,
        });
        await db.sendCalendarInvite({
          to: [homeownerEmail],
          subject: `Cancelled: your SolarAbility appointment — ${fmtJobDate(start.toISOString())}`,
          icsBase64: base64Utf8(ics), audience: "homeowner",
          jobLine: `${fmtJobDate(start.toISOString(), { weekday: "short", month: "short", day: "numeric" })} at ${fmtJobTime(start.toISOString())} (Central)`,
        });
      }
      const saved = await persist(visits.map(v => v.id === visit.id ? {
        ...v, cancelled: true, seq,
        inviteLog: [...(v.inviteLog || []), { at: new Date().toISOString(), by: user?.name || "", action: "cancelled", to: recipients, seq, when: visit.scheduled_at }],
      } : v));
      alert(`Cancellation sent ✓\n\nA call or text to the homeowner is still worth doing — calendar cancellations are easy to miss.\n\n${saved
        ? "Saved."
        : "WARNING: the cancellation went out but the job did not save. Hit Save Project."}`);
    } catch(e) {
      alert("Failed to send the cancellation — check your connection and try again.");
    }
  };

  const sendInvite = async () => {
    const visit = visits.find(v => v.id === activeVisit) || visits[0];
    if (!visit?.scheduled_at) return alert("Set a date and time for this visit first.");
    const chosen = emailMembers.filter(m => inviteRecipients[m.idx]);
    const homeownerEmail = (p.clientEmail || "").trim();
    const includeHomeowner = inviteHomeowner && homeownerEmail.includes("@");
    if (chosen.length === 0 && !includeHomeowner) {
      return alert("Select at least one recipient — a team member with an email, or the homeowner.");
    }
    if (chosen.length > 0 && !folderUrlOf(p) && !window.confirm("No Proton Drive folder link on this job yet (see \"Before truck roll\"). The crew won't have a folder to upload to.\n\nSend the invite anyway?")) return;
    setInviteSending(true);
    const start = new Date(visit.scheduled_at);
    const durationHours = parseFloat(inviteDuration) || 2;
    const end = new Date(start.getTime() + durationHours * 60 * 60 * 1000);
    // Chicago time, stated explicitly — this text lands in customers' inboxes
    const timeStr = `${fmtJobDate(start.toISOString(), { weekday: "short", month: "short", day: "numeric" })} at ${fmtJobTime(start.toISOString())} (Central)`;

    // Crew invite — the full briefing, so nobody has to ask what to bring,
    // what the partner needs, or how to approach the issue.
    const pb = playbooks.find(x => x.id === invitePlaybook) || null;
    const partnerRec = partnersList.find(x => x.name === p.partner) || null;

    const sec = (title, lines) => {
      const body = (lines || []).filter(Boolean);
      return body.length ? [`── ${title} ──`, ...body, ""] : [];
    };

    const crewDesc = [
      ...sec("JOB", [
        `Client: ${p.client || p.name}`,
        `Address: ${p.address || "—"}`,
        p.clientPhone ? `Homeowner phone: ${p.clientPhone}` : "",
        p.systemSize ? `System: ${p.systemSize} kW` : "",
      ]),
      ...sec("SCOPE OF WORK", p.scope ? [p.scope] : []),
      ...sec("PERMIT", permitRequired(p) ? [`Permit ${permitLine(p)}${p.permit.issuedOn ? " — post the permit card on site before starting" : " — do not start permitted work until the office confirms it's issued"}`] : []),
      ...sec("REPORTED ISSUE", [inviteNotes || "See project notes in the Hub."]),
      ...sec("REFERENCE PHOTOS", (p.refPhotos || []).length
        ? (p.refPhotos || []).map((ph, i) => `• ${ph.label || `Photo ${i + 1}`}: ${ph.url}`)
        : []),
      ...sec("JOB FOLDER (Proton Drive) — upload photos & report here", folderUrlOf(p) ? [`• ${folderUrlOf(p)}`] : []),
      ...sec("JOB DOCUMENTS", (p.refDocs || []).filter(d => !d.kind).length
        ? (p.refDocs || []).filter(d => !d.kind).map(d => `• ${d.label || d.name}: ${d.url}`)
        : []),
      ...sec(p.partner ? `PARTNER — ${p.partner.toUpperCase()}` : "", [
        partnerRec?.submissionMethod ? `Submit via: ${partnerRec.submissionMethod}` : "",
        // Crew-facing note only. Office notes (pricing, portal logins, closeout
        // tasks) stay with the coordinator and never go out on an invite.
        partnerRec?.crewNote || "",
        ...(pb?.partnerNotes || []).filter(n => !p.partner || n.partner.toLowerCase() === p.partner.toLowerCase()).map(n => n.note),
      ]),
      // Same 1–4 order as the job briefing in the Hub
      ...sec("1 · BEFORE YOU LEAVE THE SHOP", [
        inviteChecklist !== "None" ? `• Truck Check list: ${inviteChecklist}` : "• Load per the job",
      ]),
      ...sec("2 · SAFETY — BEFORE YOU START", [
        ...(pb?.safety || []).map(s => `• ${s}`),
        "• Complete the JHA in the Hub before starting work",
      ]),
      ...sec("3 · ON SITE — HUB FORM", [
        `• ${p.projectType === "new_install" ? "Install Closeout (photo checklist)" : p.projectType === "survey" ? "TSA Survey" : "Service Assessment"} — opens linked to this job:`,
        `  https://hub.solarability.com/#form/${p.projectType === "new_install" ? "install" : p.projectType === "survey" ? "survey" : "assessment"}/${p.id}/${visit.id}`,
        `• JHA for this visit (pre-filled): https://hub.solarability.com/#form/jha/${p.id}/${visit.id}`,
        "• Photos go in the Hub first — then upload to the partner's system",
      ]),
      ...sec("TIME ON SITE", (() => {
        const isOmnidian = [p.partner, pb?.partner].some(x => (x || "").trim().toLowerCase() === "omnidian");
        if (!isOmnidian) return [];
        const tb = timeBudgetFor(pb, chosen.length || undefined);
        return tb ? [
          `• ${tb.onSite} on site (${tb.budget} man-hr paid)`,
          `• ~${tb.troubleshoot} to diagnose, then ~${tb.documenting} to document`,
          `• Call OEM support early — hold time comes out of the same clock`,
        ] : [];
      })()),
      ...sec("4 · PROCEDURE & REFERENCE", [
        ...(pb ? [pb.title] : []),
        ...(pb?.steps || []).slice(0, 8).map((s, i) => `${i + 1}. ${s}`),
        (pb?.steps || []).length > 8 ? `...full procedure in the Hub` : "",
        ...((p.referenceIds || []).map(id => playbooks.find(b => b.id === id)).filter(Boolean)
            .map(r => `• Reference: ${r.title} — https://hub.solarability.com/#procedures/${r.id}`)),
      ]),
      ...sec("OEM SUPPORT", p.projectType === "service"
        ? OEM_SUPPORT.map(o => `• ${o.name}: ${o.display}`) : []),
      ...sec("LINKS", [
        pb ? `Full procedure: https://hub.solarability.com/#procedures/${pb.id}` : "",
        ...(pb?.links || []).map(l => `${l.title || "Reference"}: ${l.url}`),
        "Hub: https://hub.solarability.com",
      ]),
    ].filter(Boolean).join("\n");

    // Homeowner invite — deliberately excludes crew notes, the checklist, and
    // the Hub link. They just need to know we're coming and when.
    // Partner-specific homeowner instructions (e.g. an adult must be present)
    const ownerNote = (partnersList.find(x => x.name === p.partner)?.homeownerNote || "").trim();
    const ownerDesc = [
      `SolarAbility LLC will be on site for your scheduled service.`,
      `Address: ${p.address || "—"}`,
      ...(ownerNote ? [ownerNote] : []),
      `Please make sure we have clear access to your solar equipment.`,
      "", `Questions? Call (312) 312-5337.`,
    ].join("\n");

    try {
      // Stable per-visit UIDs so a resend updates that visit's event.
      // "add" mode keeps the sequence where it is: adding a person to an
      // unchanged appointment shouldn't re-notify everyone else.
      const crewUid = visit.uidCrew || `sa-crew-${p.id}-${visit.id}@solarability.com`;
      const ownerUid = visit.uidOwner || `sa-owner-${p.id}-${visit.id}@solarability.com`;
      const alreadySent = (visit.seq || 0) > 0;
      const nextSeq = (alreadySent && inviteMode === "add")
        ? visit.seq
        : (visit.seq || 0) + 1;

      if (chosen.length > 0) {
        const crewIcs = buildICS({
          title: `${p.name || p.client} — ${inviteChecklist !== "None" ? inviteChecklist : "Job"}`,
          start, end, location: p.address || "",
          description: crewDesc,
          organizerEmail: ADMIN_EMAIL,
          attendees: chosen.map(m => ({ name: m.name, email: m.name })),
          uid: crewUid, sequence: nextSeq,
        });
        await db.sendCalendarInvite({
          to: chosen.map(m => m.name),
          subject: `Job Scheduled: ${p.name || p.client} — ${timeStr}`,
          icsBase64: base64Utf8(crewIcs),
          audience: "crew",
        });
      }
      if (includeHomeowner) {
        const ownerIcs = buildICS({
          title: `SolarAbility — Scheduled Service`,
          start, end, location: p.address || "",
          description: ownerDesc,
          organizerEmail: ADMIN_EMAIL,
          attendees: [{ name: p.client || "Homeowner", email: homeownerEmail }],
          uid: ownerUid, sequence: nextSeq,
        });
        await db.sendCalendarInvite({
          to: [homeownerEmail],
          subject: `Your SolarAbility appointment — ${timeStr}`,
          icsBase64: base64Utf8(ownerIcs),
          audience: "homeowner",
          jobLine: `${timeStr}${p.address ? ` · ${p.address}` : ""}`,
          ownerNote,
        });
      }
      // Track uid/sequence, who was invited, and a dated log of every send
      const invitedNow = [...chosen.map(m => m.name), ...(includeHomeowner ? [homeownerEmail] : [])];
      const entry = {
        at: new Date().toISOString(),
        by: user?.name || "",
        action: !alreadySent ? "sent" : (inviteMode === "add" ? "added" : "updated"),
        to: invitedNow,
        seq: nextSeq,
        when: visit.scheduled_at,
      };
      const nextVisits = visits.map(v => v.id === visit.id ? {
        ...v, uidCrew: crewUid, uidOwner: ownerUid, seq: nextSeq,
        playbookId: invitePlaybook || v.playbookId || "",
        // Keep what was written on the invite with the visit, so the Schedule
        // tab and the next re-send show the same notes the crew received.
        notes: inviteNotes,
        durationHours,
        invited: [...new Set([...(v.invited || []), ...invitedNow])],
        inviteLog: [...(v.inviteLog || []), entry],
      } : v);
      const saved = await persist(nextVisits, {
        playbookId: p.playbookId || invitePlaybook || "",
        truckList: inviteChecklist && inviteChecklist !== "None" ? inviteChecklist : (p.truckList || ""),
      });

      const sentTo = [chosen.length > 0 ? `${chosen.length} crew` : "", includeHomeowner ? "homeowner" : ""].filter(Boolean).join(" and ");
      const note = !alreadySent
        ? ""
        : inviteMode === "add"
          ? "\n\nSent only to the people you selected. Nobody else was re-notified."
          : "\n\nExisting calendar entries will move to the new time.";
      // Confirmation text to the homeowner, from the company number
      let textNote = "";
      if (inviteText && (p.clientPhone || "").replace(/\D/g, "").length >= 10) {
        try {
          const tpl = smsTemplates({ ...p }, { ...visit, durationHours }, user).find(x => x.id === "confirmation");
          await db.quoSend({ to: p.clientPhone, content: tpl.text, projectId: p.id, kind: "confirmation" });
          textNote = "\nHomeowner texted the confirmation ✓";
        } catch(e) { textNote = `\nHomeowner text NOT sent: ${e.message}`; }
      }
      alert(`Invite sent to ${sentTo} ✓${textNote}${note}\n\n${saved
        ? "Saved — the Schedule tab now shows this time."
        : "WARNING: the invite went out but the job did not save. Check your connection and hit Save Project, or the Schedule will show the old time."}`);
      setInviteOpen(false);
    } catch(e) { alert("Failed to send invite — check that the send-notification function is deployed and updated."); }
    setInviteSending(false);
  };

  const addTask = () => u("tasks", [...p.tasks, { id: Date.now(), name: "", assigned: "", due: "", priority: "medium", done: false, createdBy: user?.name || "" }]);
  const moveTask = (idx, dir) => {
    const to = idx + dir;
    if (to < 0 || to >= p.tasks.length) return;
    const arr = [...p.tasks];
    [arr[idx], arr[to]] = [arr[to], arr[idx]];
    u("tasks", arr);
  };
  const updTask = (id, f, v) => u("tasks", p.tasks.map(t => t.id === id ? { ...t, [f]: v, ...(f === "done" ? { doneAt: v ? new Date().toISOString() : "", doneBy: v ? (user?.name || "") : "" } : {}) } : t));
  const canDelete = (item) => isAdmin || (!!item.createdBy && item.createdBy === user?.name);
  const delTask = (id) => u("tasks", p.tasks.filter(t => t.id !== id));

  const addMaterial = () => u("materials", [...p.materials, { id: Date.now(), name: "", qty: "", unit: "", ordered: false, received: false, createdBy: user?.name || "" }]);
  const updMat = (id, f, v) => u("materials", p.materials.map(m => m.id === id ? { ...m, [f]: v } : m));
  const delMat = (id) => u("materials", p.materials.filter(m => m.id !== id));

  const addNote = () => {
    const text = prompt(t.noteText);
    if (!text) return;
    u("notes", [...p.notes, { id: Date.now(), text, by: user?.name, at: new Date().toISOString() }]);
  };

  // Auto-calculate completion from tasks
  const taskCompletion = p.tasks.length > 0 ? Math.round((p.tasks.filter(t => t.done).length / p.tasks.length) * 100) : p.completion;

  const save = async () => {
    if (!p.name) return alert("Project name is required");
    if (isNew) {
      // Same house already has an open job?
      const dup = findOpenProject(await db.getProjects(undefined, { fresh: true }), { address: p.address, client: p.client, excludeId: p.id, workOrder: p.workOrder, partner: p.partner, serviceSubtype: p.serviceSubtype, projectType: p.projectType });
      if (dup) {
        const openIt = window.confirm(`There is already an open job for this house:\n\n${dup.name}${dup.workOrder ? ` (WO ${dup.workOrder})` : ""}\n${dup.address || ""}\n(${(dup.status || "").replace(/_/g, " ")})\n\nOK = open that job instead (nothing new is created)\nCancel = this is a different work order / FSD / service`);
        if (openIt) { window.location.hash = `projects/id/${encodeURIComponent(dup.id)}`; onCancel(); return; }
        if (!p.workOrder) {
          const wo = window.prompt(`What tells this job apart from "${dup.name}"?\nWork order / FSD / SQ number (or a short label like "battery service"):`, "");
          if (!wo || !wo.trim()) return;
          p.workOrder = wo.trim();
        }
      }
    }
    const toSave = { ...p, completion: taskCompletion };
    const base = JSON.parse(openedRef.current || "null");
    const res = await db.saveProject(toSave, { base });
    if (res && res.ok === false) return; // saveProject already alerted
    // Newly assigned tasks → one email per person (members are keyed by email)
    if (!isNew) {
      const before = new Map(((base && base.tasks) || []).map(tk => [tk.id, tk.assigned || ""]));
      const emailOf = (who) => { if (!who) return ""; if (who.includes("@")) return who; const m = (p.members || []).find(x => (x.displayName || "") === who && (x.name || "").includes("@")); return m ? m.name : ""; };
      const byPerson = {};
      (toSave.tasks || []).forEach(tk => {
        if (!tk.assigned || tk.done || before.get(tk.id) === tk.assigned) return;
        const em = emailOf(tk.assigned);
        if (!em || em.toLowerCase() === (user?.email || "").toLowerCase()) return; // not yourself
        (byPerson[em] = byPerson[em] || []).push(tk);
      });
      Object.entries(byPerson).forEach(([em, tks]) => db.notifyTaskAssigned(em, toSave, tks, user?.name));
    }
    setP(res?.project || toSave);
    openedRef.current = JSON.stringify(res?.project || toSave);
    // A brand-new job stays open so the visit/invite can be sent right away
    if (isNew) alert("Saved. Set the visit date below and send the invite — or hit Back.");
    onSave(res?.project || toSave, { stay: isNew });
  };

  const statuses = [
    { val: "not_started", label: t.notStarted, color: C.muted },
    { val: "in_progress", label: t.inProgress, color: C.info },
    { val: "pending_submission", label: t.pendingSubmission, color: C.gold },
    { val: "completed", label: t.completed, color: C.success },
    { val: "on_hold", label: t.onHold, color: C.warning },
  ];

  return (
    <div style={{ maxWidth: 800, animation: "fadeIn 0.25s ease" }}>
      <button onClick={cancelEdit} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>

      {/* Header */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 18, flexWrap: "wrap" }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{isNew ? t.newProject : t.editProject}</h3>
          {!isNew && onOpenMap && p.address && <button style={{ ...S.btnS, padding: "8px 14px", fontSize: 13 }} onClick={() => onOpenMap(p.id)}>{I.mappin} {t.openInJobMap || "Open in Job Map"}</button>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label={t.projectName} required><input style={S.input} placeholder="e.g. Johnson Solar Install" value={p.name} onChange={e => u("name", e.target.value)} /></Field>
          <Field label={t.clientName}><input style={S.input} value={p.client} onChange={e => u("client", e.target.value)} /></Field>
          <Field label={t.address}><input style={S.input} value={p.address} onChange={e => u("address", e.target.value)} /></Field>
          <Field label={t.systemSize}><input style={S.input} placeholder="e.g. 8.4" value={p.systemSize} onChange={e => u("systemSize", e.target.value)} /></Field>
          <Field label="Homeowner Email"><input style={S.input} type="email" value={p.clientEmail || ""} onChange={e => u("clientEmail", e.target.value)} placeholder="For sending the appointment confirmation" /></Field>
          <Field label="Homeowner Phone"><input style={S.input} type="tel" value={p.clientPhone || ""} onChange={e => u("clientPhone", e.target.value)} placeholder="(312) 555-0000" /></Field>
        </div>
        <Field label="Work order / FSD / SQ #">
          <input style={{ ...S.input, maxWidth: 320 }} value={p.workOrder || ""} onChange={e => u("workOrder", e.target.value)} placeholder="e.g. WO-48213, FSD 1182, SQ-9931" />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Tells apart separate jobs at the same address — different numbers are never flagged as duplicates.</div>
        </Field>
        <Field label="Scope of work">
          <textarea style={{ ...S.textarea, minHeight: 64 }} rows={3} value={p.scope || ""} onChange={e => u("scope", e.target.value)}
            placeholder="What we're doing on this job, in one or two lines — e.g. Remove 22 panels for re-roof, store on site, reinstall on new rail after roofer finishes. 2 trips." />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Shows on the crew's Schedule card, in the invite, and on the Job Map so scheduling can be done at a glance.</div>
        </Field>
        {isNew && (
          <Field label="Project Type">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {PROJECT_TYPES.map(pt => (
                <button key={pt.id} type="button" onClick={() => {
                  if ((p.tasks.length > 0 || p.materials.length > 0) && p.projectType && p.projectType !== pt.id) {
                    if (!window.confirm("Switching project type will replace the pre-loaded task and material lists. Continue?")) return;
                  }
                  setP(prev => ({
                    ...prev,
                    projectType: pt.id,
                    tasks: tasksForProjectType(pt.id, user?.name),
                    materials: materialsForProjectType(pt.id, user?.name),
                  }));
                }} style={{
                  padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  border: `1px solid ${p.projectType === pt.id ? C.gold : C.border}`,
                  background: p.projectType === pt.id ? `${C.gold}22` : "transparent",
                  color: p.projectType === pt.id ? C.gold : C.muted,
                }}>{pt.label}</button>
              ))}
            </div>
            {p.projectType && <div style={{ color: C.muted, fontSize: 12, marginTop: 8 }}>
              {p.tasks.length} tasks{p.materials.length > 0 ? ` and ${p.materials.length} materials` : ""} pre-loaded — you can add, reorder, or remove any below.
            </div>}
          </Field>
        )}

        {/* Service subtype — only relevant for service calls */}
        {p.projectType === "service" && (
          <Field label="Type of Service">
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SERVICE_SUBTYPES.map(st => (
                <button key={st} type="button" onClick={() => u("serviceSubtype", st)} style={{
                  padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                  border: `1px solid ${p.serviceSubtype === st ? C.info : C.border}`,
                  background: p.serviceSubtype === st ? `${C.info}22` : "transparent",
                  color: p.serviceSubtype === st ? C.info : C.muted,
                }}>{st}</button>
              ))}
            </div>
          </Field>
        )}

        {/* Partner / source — who the ticket came from */}
        <Field label="Partner / Source">
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={() => u("partner", "")} style={{
              padding: "9px 15px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
              border: `1px solid ${!p.partner ? C.gold : C.border}`,
              background: !p.partner ? `${C.gold}22` : "transparent",
              color: !p.partner ? C.gold : C.muted,
            }}>Direct / internal</button>
            {partnersList.map(pt => (
              <button key={pt.id} type="button" onClick={() => u("partner", pt.name)} style={{
                padding: "9px 15px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                border: `1px solid ${p.partner === pt.name ? C.gold : C.border}`,
                background: p.partner === pt.name ? `${C.gold}22` : "transparent",
                color: p.partner === pt.name ? C.gold : C.muted,
              }}>{pt.name}</button>
            ))}
          </div>
          {partnersList.length === 0 && (
            <div style={{ color: C.muted, fontSize: 11, marginTop: 6 }}>No partners set up yet — add them in the Partners tab so they appear here.</div>
          )}
          {partnersList.length > 0 && partnersList.every(x => x.fromJobs) && (
            <div style={{ color: C.warning, fontSize: 11, marginTop: 6 }}>Showing partners used on other jobs — this login can't read the Partners list (run PARTNERS_READ_FIX_SQL.md).</div>
          )}
          {(() => {
            const rec = partnersList.find(x => x.name === p.partner);
            if (!rec) return null;
            return (
              <div style={{ marginTop: 10, background: C.surface, borderRadius: 8, padding: 10, fontSize: 12, color: C.light, lineHeight: 1.5 }}>
                <strong style={{ color: C.gold }}>{rec.name}</strong>
                {rec.submissionMethod && <span style={{ color: C.muted }}> · submit via {rec.submissionMethod}</span>}
                {rec.notes && <div style={{ color: C.muted, marginTop: 4 }}>{rec.notes}</div>}
              </div>
            );
          })()}
        </Field>

        {/* What the crew follows on site. Set once here; the invite, the
            briefing, and the calendar link all read from these fields. */}
        <Field label="Field Kit — procedure & truck check">
          {(() => {
            const sugPb = suggestPlaybook(p, playbooks);
            const sugTruck = suggestTruckList(p);
            return (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Main procedure (partner scope)</div>
                  <select style={S.select} value={p.playbookId || ""} onChange={e => u("playbookId", e.target.value)}>
                    <option value="">— none —</option>
                    {playbooks.filter(b => !isReferenceDoc(b)).map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                  </select>
                  {!p.playbookId && sugPb && (
                    <button type="button" onClick={() => u("playbookId", sugPb.id)}
                      style={{ background: "none", border: "none", color: C.gold, fontSize: 11, cursor: "pointer", padding: "5px 0 0", fontFamily: "inherit", textAlign: "left" }}>
                      Use suggested: {sugPb.title}
                    </button>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Truck Check list</div>
                  <select style={S.select} value={p.truckList || ""} onChange={e => u("truckList", e.target.value)}>
                    <option value="">— none —</option>
                    {TRUCK_LIST_NAMES.map(n => <option key={n} value={n}>{n}</option>)}
                  </select>
                  {!p.truckList && sugTruck && (
                    <button type="button" onClick={() => u("truckList", sugTruck)}
                      style={{ background: "none", border: "none", color: C.gold, fontSize: 11, cursor: "pointer", padding: "5px 0 0", fontFamily: "inherit", textAlign: "left" }}>
                      Use suggested: {sugTruck}
                    </button>
                  )}
                </div>
              </div>
            );
          })()}
          {playbooks.length === 0 && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>No procedures written yet — add them in the Procedures tab.</div>
          )}

          {/* Reference documents: troubleshooting guides, training, manuals.
              Read-only on site — as many as the job needs. */}
          {playbooks.length > 0 && (() => {
            const refs = (p.referenceIds || []).map(id => playbooks.find(b => b.id === id)).filter(Boolean);
            const available = playbooks.filter(b => b.id !== p.playbookId && !(p.referenceIds || []).includes(b.id));
            return (
              <div style={{ marginTop: 12 }}>
                <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Reference documents (troubleshooting, training, manuals)</div>
                {refs.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {refs.map(b => (
                      <span key={b.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 6px 5px 11px", borderRadius: 16, background: C.surface, border: `1px solid ${C.border}`, fontSize: 12, color: C.light }}>
                        {I.book} {b.title}
                        <button type="button" onClick={() => u("referenceIds", (p.referenceIds || []).filter(x => x !== b.id))}
                          style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 4px" }}>×</button>
                      </span>
                    ))}
                  </div>
                )}
                {(() => {
                  // Suggested from the scope you just picked — one tap to attach
                  const mainPb = playbooks.find(b => b.id === p.playbookId);
                  const sug = suggestedReferences(mainPb, p, playbooks)
                    .filter(x => !(p.referenceIds || []).includes(x.ref.id));
                  if (sug.length === 0) return null;
                  return (
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 5 }}>Suggested for this scope</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                        {sug.map(({ ref, why }) => (
                          <button key={ref.id} type="button"
                            onClick={() => u("referenceIds", [...(p.referenceIds || []), ref.id])}
                            style={{
                              display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 11px", borderRadius: 16,
                              border: `1px dashed ${C.info}77`, background: `${C.info}10`, color: C.info,
                              fontSize: 12, fontFamily: "inherit", cursor: "pointer", textAlign: "left",
                            }}>
                            {I.plus}
                            <span>
                              {ref.title}
                              <span style={{ color: C.muted, fontSize: 10.5, display: "block" }}>{why}</span>
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
                {available.length > 0 && (
                  <select style={{ ...S.select, fontSize: 13 }} value="" onChange={e => { if (e.target.value) u("referenceIds", [...(p.referenceIds || []), e.target.value]); }}>
                    <option value="">+ Add a reference document…</option>
                    {available.filter(isReferenceDoc).length > 0 && (
                      <optgroup label="Troubleshooting, training & manuals">
                        {available.filter(isReferenceDoc).map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                      </optgroup>
                    )}
                    {available.filter(b => !isReferenceDoc(b)).length > 0 && (
                      <optgroup label="Other procedures">
                        {available.filter(b => !isReferenceDoc(b)).map(b => <option key={b.id} value={b.id}>{b.title}</option>)}
                      </optgroup>
                    )}
                  </select>
                )}
              </div>
            );
          })()}
        </Field>

        <Field label={t.projectStatus}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {statuses.map(s => (
              <button key={s.val} onClick={() => u("status", s.val)} style={{ padding: "8px 16px", fontSize: 13, borderRadius: 8, border: `1px solid ${p.status === s.val ? s.color : C.border}`, background: p.status === s.val ? `${s.color}18` : "transparent", color: p.status === s.val ? s.color : C.muted, cursor: "pointer", fontFamily: "inherit", fontWeight: p.status === s.val ? 600 : 400 }}>{s.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>Moves on its own: a visit scheduled → In Progress · every visit done (assessment filed) → Pending Submission · "Submit to / Share with partner" task ticked → Completed (direct jobs: invoice sent) · invoice paid → Completed. Completed jobs stay in Awaiting Payment until the paid date is entered. On Hold set by hand stays put.</div>
        </Field>
        {(
          <Field label="Visits — Central Time (job site)">
            {visits.length === 0 && (
              <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>No visit scheduled yet.</div>
            )}
            {visits.map((v, i) => {
              const sent = (v.invited || []).length > 0;
              return (
                <div key={v.id} style={{
                  padding: 12, background: C.surface, borderRadius: 8, marginBottom: 8,
                  border: `1px solid ${v.cancelled ? `${C.danger}44` : C.border}`,
                  opacity: v.cancelled ? 0.6 : 1,
                }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 10, flexWrap: "wrap" }}>
                    <input style={{ ...S.input, width: 130, fontSize: 12, padding: "6px 10px" }}
                      value={v.label} onChange={e => updVisit(v.id, "label", e.target.value)} />
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      {v.cancelled && <span style={S.badge(C.danger)}>CANCELLED</span>}
                      {!v.cancelled && sent && <span style={S.badge(C.success)}>{(v.invited || []).length} invited</span>}
                      {!v.cancelled && canManage && (
                        <button type="button" title={v.status === "done" ? "Reopen this visit" : "Mark this visit done (normally set when the crew files the assessment)"}
                          onClick={async () => {
                            const next = visits.map(x => x.id === v.id ? (x.status === "done" ? { ...x, status: "scheduled", doneAt: "", doneBy: "" } : { ...x, status: "done", doneAt: new Date().toISOString(), doneBy: user?.name || "" }) : x);
                            await persist(next);
                          }}
                          style={{ ...S.btnS, fontSize: 11, padding: "4px 9px", borderColor: v.status === "done" ? C.success : C.border, color: v.status === "done" ? C.success : C.muted }}>
                          {v.status === "done" ? "✓ Done — reopen" : "Mark done"}
                        </button>
                      )}
                      {visits.length > 1 && (
                        <button onClick={() => removeVisit(v.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 2 }}>{I.trash}</button>
                      )}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                    <input type="datetime-local" style={{ ...S.input, maxWidth: 230, fontSize: 13 }}
                      disabled={v.cancelled}
                      value={isoToJobWall(v.scheduled_at)}
                      onChange={e => updVisit(v.id, "scheduled_at", e.target.value ? jobWallToISO(e.target.value) : "")} />
                    {!v.cancelled && (
                      <>
                        <button type="button" onClick={() => {
                          // Pre-tick the crew only for the job's first invite. Once any visit
                          // has gone out, nothing is pre-ticked — visit 2 often has a different
                          // crew, and pre-ticking put visit-1 people on visit 2's calendar.
                          const otherVisitSent = visits.some(x => x.id !== v.id && !x.cancelled && (x.invited || []).length > 0);
                          setInviteRecipients(sent || otherVisitSent ? {} : Object.fromEntries(emailMembers.map(m => [m.idx, true])));
                          setActiveVisit(v.id); setInviteMode(sent ? "add" : "update");
                          setInvitePlaybook(v.playbookId || p.playbookId || suggestPlaybook(p, playbooks)?.id || "");
                          setInviteChecklist(p.truckList || suggestTruckList(p) || "None");
                          setInviteNotes(v.notes || "");
                          setInviteDuration(v.durationHours ? String(v.durationHours) : "2");
                          setInviteOpen(true);
                        }}
                          style={{ ...S.btnS, borderColor: C.gold, color: C.gold, fontSize: 12, padding: "7px 12px" }}>
                          {I.send} {sent ? "Send / Update" : "Send Invite"}
                        </button>
                        {sent && (
                          <button type="button" onClick={() => cancelVisit(v)}
                            style={{ ...S.btnS, borderColor: C.danger, color: C.danger, fontSize: 12, padding: "7px 12px" }}>Cancel</button>
                        )}
                      </>
                    )}
                  </div>

                  {!v.cancelled && playbooks.length > 0 && (
                    <select style={{ ...S.select, marginTop: 8, fontSize: 12, padding: "7px 10px" }}
                      value={v.playbookId || ""} onChange={e => updVisit(v.id, "playbookId", e.target.value)}>
                      <option value="">Procedure: same as job{p.playbookId ? ` (${(playbooks.find(b => b.id === p.playbookId) || {}).title || "—"})` : ""}</option>
                      {playbooks.filter(b => !isReferenceDoc(b)).map(b => <option key={b.id} value={b.id}>Procedure: {b.title}</option>)}
                    </select>
                  )}
                  {sent && (
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                      Invited: {(v.invited || []).join(", ")}
                    </div>
                  )}

                  {!v.cancelled && !isNew && (
                    <div style={{ marginTop: 8 }}>
                      <VisitChips progress={visitProgress(p, v, projForms, procRuns)} />
                    </div>
                  )}
                  {(v.inviteLog || []).length > 0 && (
                    <details style={{ marginTop: 8 }}>
                      <summary style={{ fontSize: 11.5, color: C.gold, cursor: "pointer" }}>
                        Invite history ({v.inviteLog.length})
                      </summary>
                      <div style={{ marginTop: 7 }}>
                        {[...v.inviteLog].reverse().map((lg, i) => {
                          const tone = lg.action === "cancelled" ? C.danger : lg.action === "updated" ? C.info : C.muted;
                          const label = { sent: "Sent", updated: "Time changed", added: "Added someone", cancelled: "Cancelled" }[lg.action] || lg.action;
                          return (
                            <div key={i} style={{ fontSize: 11, color: C.muted, padding: "5px 0", borderBottom: i < v.inviteLog.length - 1 ? `1px solid ${C.border}33` : "none", lineHeight: 1.55 }}>
                              <span style={{ color: tone, fontWeight: 700 }}>{label}</span>
                              {" · "}{fmtJobDateTime(lg.at)}
                              {lg.by ? ` · by ${lg.by}` : ""}
                              {lg.when ? <div>for {fmtJobDateTime(lg.when)}</div> : null}
                              {(lg.to || []).length > 0 && <div>to {lg.to.join(", ")}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </details>
                  )}
                  {!v.cancelled && (v.invited || []).length > 0 && (
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
                      <span style={{ fontSize: 11, color: C.muted }}>Invited:</span>
                      {(v.invited || []).map(e => (
                        <span key={e} style={{ ...S.badge(C.success), display: "inline-flex", alignItems: "center", gap: 6, fontWeight: 600, textTransform: "none" }}>
                          {e}
                          {canManage && <button type="button" title="Remove from this visit" onClick={() => uninvite(v, e)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>}
                        </span>
                      ))}
                    </div>
                  )}
                  {(v.notes || v.durationHours) && <div style={{ fontSize: 12, color: C.muted, marginTop: 8, whiteSpace: "pre-wrap" }}>
                    {v.durationHours ? <span style={{ marginRight: 10 }}>⏱ {v.durationHours} hr</span> : null}
                    {v.notes ? <><span style={{ color: C.warning, fontWeight: 700 }}>Notes on invite:</span> <Linkify text={v.notes} /></> : null}
                  </div>}
                </div>
              );
            })}
            <button type="button" onClick={addVisit} style={{ ...S.btnS, width: "100%", justifyContent: "center", borderColor: C.gold, color: C.gold }}>
              {I.plus} Add {visits.length === 0 ? "Visit" : `Visit ${visits.length + 1}`}
            </button>
            {emailMembers.length === 0 && <div style={{ color: C.muted, fontSize: 11, marginTop: 8 }}>Add team members with an email address to send them an invite.</div>}
          </Field>
        )}
        {/* Completion bar */}
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
            <span style={{ fontSize: 12, color: C.muted, fontWeight: 600 }}>{t.completion}</span>
            <span style={{ fontSize: 14, fontWeight: 800, color: taskCompletion === 100 ? C.success : C.gold }}>{taskCompletion}%</span>
          </div>
          <div style={{ width: "100%", height: 8, background: C.surface, borderRadius: 4, overflow: "hidden" }}>
            <div style={{ width: `${taskCompletion}%`, height: "100%", background: taskCompletion === 100 ? C.success : `linear-gradient(90deg, ${C.gold}, ${C.goldDark})`, borderRadius: 4, transition: "width 0.3s" }} />
          </div>
        </div>
      </div>

      {/* Tasks */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{t.tasks} ({p.tasks.filter(t => t.done).length}/{p.tasks.length})</h3>
          <button style={S.btnS} onClick={addTask}>{I.plus} {t.addTask}</button>
        </div>
        {p.tasks.length === 0 && <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>{t.addTask}...</div>}
        {p.tasks.map((task, idx) => (
          <div key={task.id} style={{ background: C.surface, borderRadius: 10, padding: 14, marginBottom: 8, border: `1px solid ${task.done ? `${C.success}30` : C.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
              <div onClick={() => updTask(task.id, "done", !task.done)} style={{ width: 22, height: 22, borderRadius: 6, border: task.done ? `2px solid ${C.success}` : `2px solid ${C.muted}44`, background: task.done ? C.success : "transparent", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }}>
                {task.done && <svg width="12" height="12" viewBox="0 0 24 24" fill="none"><path d="M5 13l4 4L19 7" stroke={C.bg} strokeWidth="3" strokeLinecap="round"/></svg>}
              </div>
              <input style={{ ...S.input, flex: 1, textDecoration: task.done ? "line-through" : "none", opacity: task.done ? 0.6 : 1 }} placeholder={t.taskName} value={task.name} onChange={e => updTask(task.id, "name", e.target.value)} />
              <div style={{ display: "flex", flexDirection: "column", gap: 2, flexShrink: 0 }}>
                <button onClick={() => moveTask(idx, -1)} disabled={idx === 0} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, color: idx === 0 ? `${C.muted}44` : C.muted, cursor: idx === 0 ? "default" : "pointer", padding: "1px 7px", fontSize: 11, lineHeight: 1.4, minHeight: 0 }}>▲</button>
                <button onClick={() => moveTask(idx, 1)} disabled={idx === p.tasks.length - 1} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, color: idx === p.tasks.length - 1 ? `${C.muted}44` : C.muted, cursor: idx === p.tasks.length - 1 ? "default" : "pointer", padding: "1px 7px", fontSize: 11, lineHeight: 1.4, minHeight: 0 }}>▼</button>
              </div>
              {canDelete(task)
                ? <button onClick={() => delTask(task.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 4 }}>{I.trash}</button>
                : <span title={`Added by ${task.createdBy || "admin"}`} style={{ color: `${C.muted}55`, padding: 4, fontSize: 13, cursor: "default" }}>🔒</span>}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              <select style={{ ...S.select, fontSize: 12, padding: "8px 10px" }} value={task.assigned || ""} onChange={e => updTask(task.id, "assigned", e.target.value)}>
                <option value="">{t.assigned}...</option>
                {(() => {
                  const people = new Set();
                  if (user?.name) people.add(user.name);
                  if (p.created_by) people.add(p.created_by);
                  (p.members || []).forEach(m => { if (m.name) people.add(m.name); });
                  if (task.assigned) people.add(task.assigned); // keep legacy values visible
                  return [...people].sort().map(n => <option key={n} value={n}>{n}</option>);
                })()}
              </select>
              <input type="date" style={{ ...S.input, fontSize: 12, padding: "8px 10px", colorScheme: "dark" }} value={task.due} onChange={e => updTask(task.id, "due", e.target.value)} />
              <select style={{ ...S.select, fontSize: 12, padding: "8px 10px" }} value={task.priority} onChange={e => updTask(task.id, "priority", e.target.value)}>
                <option value="low">{t.low}</option><option value="medium">{t.medium}</option><option value="high">{t.high}</option><option value="critical">{t.critical}</option>
              </select>
            </div>
          </div>
        ))}
      </div>

      {/* Materials */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{t.materialsList}</h3>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {p.materials.length > 0 && (
              <>
                <button style={{ ...S.btnS, borderColor: C.gold, color: C.gold }} onClick={() => generateMaterialPullSheet(p)} title="Print a checklist for the crew to pull and load materials">
                  {I.box} Pull Sheet
                </button>
                <button style={S.btnS} onClick={() => generateMaterialRFQ(p)} title="Print a list to send to a supplier for pricing">
                  {I.pdf} Quote Request
                </button>
              </>
            )}
            <button style={S.btnS} onClick={addMaterial}>{I.plus} {t.addMaterial}</button>
          </div>
        </div>
        {p.materials.length === 0 && <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>{t.addMaterial}...</div>}
        {p.materials.map(mat => (
          <div key={mat.id} style={{ padding: "12px", background: C.surface, borderRadius: 8, marginBottom: 6, border: `1px solid ${C.border}` }}>
            <MaterialPicker
              value={mat.name}
              placeholder={t.materialName}
              style={{ marginBottom: 4 }}
              options={MATERIALS_CATALOG.map(m => ({ key: m.sku, label: m.name, sub: `${m.sku}${m.putUp ? " · " + m.putUp : " · " + m.cat}`, raw: m }))}
              onChange={(v) => updMat(mat.id, "name", v)}
              onPick={(o) => setP(prev => ({ ...prev, materials: prev.materials.map(m => m.id === mat.id
                ? { ...m, name: o.raw.name, sku: o.raw.sku, unit: m.unit || o.raw.unit, catPrice: o.raw.price } : m) }))}
            />
            {mat.sku && <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>
              {mat.sku}{mat.catPrice ? ` · last quoted $${Number(mat.catPrice).toFixed(2)}` : ""}
            </div>}
            <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
              <input style={{ ...S.input, width: 64, fontSize: 13, padding: "8px 10px", textAlign: "center" }} placeholder={t.quantity} value={mat.qty} onChange={e => updMat(mat.id, "qty", e.target.value)} />
              <input style={{ ...S.input, width: 76, fontSize: 13, padding: "8px 10px" }} placeholder={t.unit} value={mat.unit} onChange={e => updMat(mat.id, "unit", e.target.value)} />
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: mat.ordered ? C.success : C.muted, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={mat.ordered} onChange={e => updMat(mat.id, "ordered", e.target.checked)} /> Ordered
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: mat.received ? C.success : C.muted, cursor: "pointer", whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={mat.received} onChange={e => updMat(mat.id, "received", e.target.checked)} /> Received
              </label>
              {canDelete(mat)
                ? <button onClick={() => delMat(mat.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", marginLeft: "auto" }}>{I.trash}</button>
                : <span title={`Added by ${mat.createdBy || "admin"}`} style={{ color: `${C.muted}55`, marginLeft: "auto", fontSize: 13, cursor: "default" }}>🔒</span>}
            </div>
          </div>
        ))}
      </div>

      {/* Notes */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{t.projectNotes}</h3>
          <button style={S.btnS} onClick={addNote}>{I.plus} {t.addNote}</button>
        </div>
        {p.notes.length === 0 && <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 20 }}>{t.addNote}...</div>}
        {p.notes.slice().reverse().map(note => (
          <div key={note.id} style={{ background: C.surface, borderRadius: 8, padding: 12, marginBottom: 6, border: `1px solid ${C.border}` }}>
            <div style={{ fontSize: 13, color: C.light, lineHeight: 1.5, marginBottom: 6, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}><Linkify text={note.text} /></div>
            <div style={{ fontSize: 11, color: C.muted }}>{t.createdBy} {note.by} · {new Date(note.at).toLocaleString()}</div>
          </div>
        ))}
      </div>

      {/* Reference photos — what the crew should see before they arrive */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{I.cam} Reference Photos</h3>
          <span style={{ fontSize: 11, color: C.muted }}>{(p.refPhotos || []).length ? `${(p.refPhotos || []).length} on this job` : ""}</span>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Shown on the Schedule card and linked in the crew invite. Roof from the street, inverter location, gate, the damage the homeowner sent, a layout sketch.</div>
        <RefPhotos photos={p.refPhotos || []} onChange={list => u("refPhotos", list)} canEdit={canManage} user={user} />
        {(p.refPhotos || []).length === 0 && !canManage && <div style={{ fontSize: 12, color: C.muted }}>None yet.</div>}
      </div>

      {/* Same house, another open job — merge it in */}
      {!isNew && canManage && twins.length > 0 && (
        <div style={{ ...S.card, border: `1px solid ${C.warning}88`, background: `${C.warning}10` }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: C.warning, marginBottom: 2 }}>⚠ Possible duplicate — {twins.length === 1 ? "another open job" : `${twins.length} other open jobs`} at this address with nothing telling them apart</div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>If it's a separate job (different work order, FSD or service), put its number in "Work order / FSD / SQ #" on each job, or tap "Not a duplicate".</div>
          {twins.map(tw => (
            <div key={tw.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "6px 0", borderTop: `1px solid ${C.border}` }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.light }}>{tw.name}</div>
                <div style={{ fontSize: 11.5, color: C.muted }}>{tw.address} · {(tw.status || "").replace(/_/g, " ")}{tw.scheduled_at ? ` · ${fmtJobDateTime(tw.scheduled_at)}` : ""} · {(tw.tasks || []).length} tasks · {(tw.visits || []).filter(v => !v.cancelled).length} visits</div>
              </div>
              <button type="button" onClick={() => { window.location.hash = `projects/id/${encodeURIComponent(tw.id)}`; onCancel(); }} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }}>Open it</button>
              <button type="button" onClick={async () => {
                const mark = (other) => (fresh) => ({ refDocs: [...(fresh.refDocs || []).filter(d => !(d.kind === "notdup" && d.url === other)), { id: `notdup-${other}`, kind: "notdup", url: other, at: new Date().toISOString() }] });
                const a = await db.patchProject(p.id, mark(tw.id));
                await db.patchProject(tw.id, mark(p.id));
                if (a) { setP(prev => ({ ...prev, refDocs: a.refDocs, updated_at: a.updated_at })); openedRef.current = JSON.stringify({ ...p, refDocs: a.refDocs, updated_at: a.updated_at }); }
                setTwins(ts => ts.filter(x => x.id !== tw.id));
              }} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }}>Not a duplicate</button>
              <button type="button" disabled={merging} onClick={async () => {
                if (isDirty()) return alert("Save this job first (Save button at the bottom), then merge.");
                if (!window.confirm(`Merge "${tw.name}" INTO this job?\n\n• Its visits, tasks, notes, crew, photos and documents are added here\n• Forms, procedure runs, calls/texts, quotes and estimates on it are moved here\n• Then "${tw.name}" is deleted\n\nThis can't be undone.`)) return;
                setMerging(true);
                try { const merged = await db.mergeProjects(p.id, tw.id); setP(merged); openedRef.current = JSON.stringify(merged); setTwins(ts => ts.filter(x => x.id !== tw.id)); onSave(merged, { stay: true }); alert("Merged ✓"); }
                catch(e) { alert(`Merge failed: ${e.message}`); }
                setMerging(false);
              }} style={{ ...S.btnP, fontSize: 12, padding: "6px 10px" }}>{merging ? "Merging…" : "Merge into this job"}</button>
            </div>
          ))}
        </div>
      )}

      {/* Permit — required docs, application, inspections. Stage is derived from the facts. */}
      {!isNew && <PermitCard p={p} u={u} canManage={canManage} />}

      {/* Before truck roll — the office checklist, with the Proton Drive folder front and center */}
      {!isNew && (() => {
        const checks = preRollChecks(p);
        const must = checks.filter(c => !c.optional);
        const ready = must.every(c => c.ok);
        const folder = folderUrlOf(p);
        const crewEmails = (p.members || []).map(m => m.name || "").filter(n => n.includes("@"));
        return (
          <div style={{ ...S.card, border: `1px solid ${ready ? C.success : C.warning}66` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{ready ? "✓" : "⚠"} Before truck roll</h3>
              <span style={{ fontSize: 11, color: ready ? C.success : C.warning, fontWeight: 700 }}>{must.filter(c => c.ok).length}/{must.length} required done</span>
            </div>
            <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
              {checks.map(c => (
                <div key={c.key} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, color: c.ok ? C.light : (c.optional ? C.muted : C.warning) }}>
                  <span style={{ width: 18, textAlign: "center", fontWeight: 800, color: c.ok ? C.success : C.muted }}>{c.ok ? "✓" : "○"}</span>
                  <span>{c.label}{c.optional ? <span style={{ color: C.muted }}> (if applicable)</span> : null}</span>
                </div>
              ))}
            </div>
            <Field label="Proton Drive job folder — share link">
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <input style={{ ...S.input, flex: 1, minWidth: 220 }} placeholder="https://drive.proton.me/urls/…" value={folder}
                  readOnly={!canManage}
                  onChange={e => u("refDocs", withFolderUrl(p, e.target.value))} />
                {folder && <a href={folder} target="_blank" rel="noopener noreferrer" style={{ ...S.btnS, textDecoration: "none", padding: "8px 12px", fontSize: 12 }}>{I.eye} Open</a>}
                {folder && <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(folder); alert("Folder link copied."); } catch(e) { window.prompt("Copy:", folder); } }} style={{ ...S.btnS, padding: "8px 12px", fontSize: 12 }}>Copy</button>}
                {!folder && canManage && <a href="https://drive.proton.me/" target="_blank" rel="noopener noreferrer" style={{ ...S.btnP, textDecoration: "none", padding: "8px 12px", fontSize: 12 }}>Open Proton Drive</a>}
              </div>
            </Field>
            <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
              In Proton Drive: New folder named <strong style={{ color: C.light }}>{[p.client || p.name, p.address].filter(Boolean).join(" — ") || "client — address"}</strong> → Share → add the crew by email → copy the link and paste it above.
              {crewEmails.length > 0 && (
                <div style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span>Share with:</span>
                  <code style={{ color: C.light, fontSize: 12, overflowWrap: "anywhere" }}>{crewEmails.join(", ")}</code>
                  <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(crewEmails.join(", ")); alert("Crew emails copied."); } catch(e) { window.prompt("Copy:", crewEmails.join(", ")); } }} style={{ ...S.btnS, padding: "4px 9px", fontSize: 11 }}>Copy emails</button>
                </div>
              )}
              {crewEmails.length === 0 && <div style={{ marginTop: 6, color: C.warning }}>Add the crew (by email) under Team Members first, so you know who to share the folder with.</div>}
              <div style={{ marginTop: 6 }}>The link goes on the crew's invite and their Schedule card. Hit Save after pasting.</div>
            </div>
          </div>
        );
      })()}

      {/* Job documents — planset, work order, drawings */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{I.pdf} Job Documents</h3>
          <span style={{ fontSize: 11, color: C.muted }}>{(p.refDocs || []).filter(d => !d.kind).length ? `${(p.refDocs || []).filter(d => !d.kind).length} on this job` : ""}</span>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>Planset, partner work order, SOW, drawings. Shown on the Schedule card and linked in the crew invite.</div>
        <RefDocs docs={p.refDocs || []} onChange={list => u("refDocs", list)} canEdit={canManage} user={user} />
        {(p.refDocs || []).filter(d => !d.kind).length === 0 && !canManage && <div style={{ fontSize: 12, color: C.muted }}>None yet.</div>}
      </div>

      {/* Calls & texts with the homeowner — Quo */}
      {!isNew && canManage && USE_SUPABASE && (
        <CommsCard p={p} user={user}
          visit={visits.filter(v => !v.cancelled && v.scheduled_at).sort((a, b) => Math.abs(new Date(a.scheduled_at) - Date.now()) - Math.abs(new Date(b.scheduled_at) - Date.now()))[0]}
          onAddTask={(txt) => { u("tasks", [...(p.tasks || []), { id: Date.now(), name: txt, assigned: user?.name || "", due: "", priority: "high", done: false, createdBy: user?.name || "" }]); alert("Added to tasks — hit Save."); }} />
      )}

      {/* Site JHA sheet — roof plan with hazards marked, printed for the truck */}
      {!isNew && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{I.hazard} Site JHA Sheet</h3>
            {p.jhaSheet?.imageUrl && <span style={{ fontSize: 11, color: C.success, fontWeight: 700 }}>{(p.jhaSheet.markers || []).filter(m => m.type === "H").length} hazards marked</span>}
          </div>
          <SiteJhaSheet project={p} user={user} canEdit={canManage}
            onSave={async (sheet) => {
              // Only the sheet column is written, so an open editor elsewhere can't be clobbered
              const saved = await db.patchProject(p.id, () => ({ jhaSheet: sheet }));
              if (!saved) { alert("Could not save the JHA sheet — check your connection."); return; }
              const next = { ...p, jhaSheet: sheet, updated_at: saved.updated_at || p.updated_at };
              setP(next); onSave(next);
            }} />
        </div>
      )}

      {/* Team Members */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{I.admin} Team Members</h3>
        </div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
          Add employees by name or email so they can see this project in their portal. The project creator ({p.created_by || user?.name}) always has access.
        </div>
        {/* Current members */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
          {/* Creator - always shown */}
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: `${C.gold}08`, borderRadius: 8, border: `1px solid ${C.gold}30` }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `${C.gold}33`, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, flexShrink: 0, fontSize: 12 }}>{I.user}</div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.light }}>{p.created_by || user?.name}</div>
              <div style={{ fontSize: 11, color: C.gold }}>Project Creator</div>
            </div>
          </div>
          {/* Added members */}
          {(p.members || []).map((member, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: C.surface, borderRadius: 8, border: `1px solid ${C.border}` }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: `${C.green}33`, display: "flex", alignItems: "center", justifyContent: "center", color: C.greenLight, flexShrink: 0, fontSize: 12 }}>{I.user}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.light }}>{member.displayName && member.displayName !== member.name ? `${member.displayName} · ${member.name}` : member.name}</div>
                <div style={{ fontSize: 11, color: C.muted }}>{member.role || "Team Member"} {member.addedAt ? `· Added ${new Date(member.addedAt).toLocaleDateString()}` : ""}</div>
              </div>
              {canManage && <button onClick={() => u("members", p.members.filter((_, i) => i !== idx))} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 4 }}>{I.trash}</button>}
            </div>
          ))}
        </div>
        {/* Add member input — admin only */}
        {!canManage && <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>Only a supervisor or coordinator can add or remove team members.</div>}
        {canManage && <div style={{ display: "flex", gap: 8 }}>
          <input style={{ ...S.input, flex: 1 }} placeholder="Employee name or email" value={newMember} onChange={e => setNewMember(e.target.value)}
            onKeyDown={e => {
              if (e.key === "Enter" && newMember.trim()) {
                const nm = newMember.trim();
                u("members", [...(p.members || []), { name: nm, role: "Team Member", addedAt: new Date().toISOString(), addedBy: user?.name }]);
                db.notifyProjectMember(nm, p.name || "Untitled Project", user?.name);
                setNewMember("");
              }
            }} />
          <button style={{ ...S.btnP, padding: "10px 18px", whiteSpace: "nowrap" }} onClick={() => {
            if (newMember.trim()) {
              const nm = newMember.trim();
              u("members", [...(p.members || []), { name: nm, role: "Team Member", addedAt: new Date().toISOString(), addedBy: user?.name }]);
              db.notifyProjectMember(nm, p.name || "Untitled Project", user?.name);
              setNewMember("");
            }
          }}>{I.plus} Add</button>
        </div>}
        {canSeeMoney(user) && <CrewSuggest project={p} members={p.members} onAdd={(nm, role, displayName) => {
          u("members", [...(p.members || []), { name: nm, displayName: displayName || nm, role, addedAt: new Date().toISOString(), addedBy: user?.name }]);
          db.notifyProjectMember(nm, p.name || "Untitled Project", user?.name);
        }} />}
      </div>

      {/* Procedure verification records — proof the closeout was performed */}
      {/* Forms filed on this job — JHA, TSA Survey, Service Assessment, Install Closeout */}
      {!isNew && projForms.length > 0 && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: C.light }}>Forms on this job</h3>
          {projForms.map(f => {
            const label = f.type === "JHA" ? "JHA" : f.type === "Service Assessment" ? (f.serviceType === "Installation Closeout" ? "Install Closeout" : `Service Assessment — ${f.serviceType || ""}`) : "TSA Survey";
            const icon = f.type === "JHA" ? I.hazard : f.type === "Service Assessment" ? (f.serviceType === "Installation Closeout" ? I.sun : I.wrench) : I.clip;
            const color = f.type === "JHA" ? C.warning : f.type === "Service Assessment" ? C.success : C.info;
            const photoCount = (f.photos || []).length;
            return (
              <div key={f.id || f.submittedAt} style={{ display: "flex", alignItems: "center", gap: 10, background: C.surface, borderRadius: 8, padding: 11, marginBottom: 6, borderLeft: `3px solid ${color}` }}>
                <div style={{ color, flexShrink: 0 }}>{icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.light }}>{label}{f.scopeTitle ? <span style={{ color: C.muted, fontWeight: 400 }}> · {f.scopeTitle.replace(/^[^—]*—\s*/, "")}</span> : null}</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    {f.submittedBy || "—"} · {f.submittedAt ? new Date(f.submittedAt).toLocaleString() : ""}{photoCount ? ` · ${photoCount} photo${photoCount === 1 ? "" : "s"}` : ""}
                    {f.project_id !== project.id && <span style={{ color: C.warning }}> · matched by address</span>}
                  </div>
                </div>
                <button type="button" onClick={() => generatePDF(f)} style={{ ...S.btnS, padding: "6px 10px", fontSize: 12, flexShrink: 0 }}>{I.eye} View</button>
              </div>
            );
          })}
        </div>
      )}

      {!isNew && procRuns.length > 0 && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 12px", fontSize: 16, fontWeight: 700, color: C.light }}>Procedure Verifications</h3>
          {procRuns.map(r => {
            const done = (r.items || []).filter(i => i.checked).length;
            const total = (r.items || []).length;
            const exceptions = total - done;
            return (
              <div key={r.id} style={{ background: C.surface, borderRadius: 8, padding: 11, marginBottom: 6, borderLeft: `3px solid ${r.completedAt ? (exceptions > 0 ? C.warning : C.success) : C.muted}` }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <div style={{ fontWeight: 700, fontSize: 13, color: C.light }}>{r.playbookTitle}</div>
                  <div style={{ display: "flex", gap: 10 }}>
                    {!r.completedAt && <a href={`#procedures/${encodeURIComponent(r.playbookId)}/resume/${encodeURIComponent(r.id)}`} onClick={(ev) => goHash(ev, `procedures/${encodeURIComponent(r.playbookId)}/resume/${encodeURIComponent(r.id)}`)} style={{ color: C.gold, fontSize: 12, fontWeight: 700, textDecoration: "none" }}>Resume ›</a>}
                    <button onClick={() => generateProcedureRunPDF(r)} style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Print</button>
                  </div>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  {r.startedBy} · {r.completedAt ? new Date(r.completedAt).toLocaleString() : "in progress"}
                </div>
                <div style={{ fontSize: 12, marginTop: 5, color: exceptions > 0 ? C.warning : C.success, fontWeight: 600 }}>
                  {done}/{total} verified{exceptions > 0 ? ` · ${exceptions} exception${exceptions === 1 ? "" : "s"}` : ""}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Invoice — admin only. Crew added as project members must not see
          amounts or payment status. */}
      {!isNew && canSeeMoney(user) && (
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.light }}>Invoice</h3>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              {invStatus === "paid" && <span style={S.badge(C.success)}>PAID</span>}
              {invStatus === "sent" && !isOverdue && <span style={S.badge(C.info)}>SENT — {daysOutstanding}d</span>}
              {isOverdue && <span style={S.badge(C.danger)}>OVERDUE — {daysOutstanding}d</span>}
              {invStatus === "draft" && <span style={S.badge(C.warning)}>DRAFT</span>}
              {invStatus === "none" && <span style={S.badge(C.muted)}>NOT STARTED</span>}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Invoice #">
              <input style={S.input} value={inv.number || ""} onChange={e => setInv("number", e.target.value)} placeholder="QuickBooks invoice number" />
            </Field>
            <Field label="Amount">
              <div style={{ display: "flex", gap: 8 }}>
                <input style={{ ...S.input, flex: 1 }} type="number" step="0.01" value={inv.amount ?? ""} onChange={e => setInv("amount", e.target.value)} placeholder="0.00" />
                {invoiceTotalFromCosting != null && (
                  <button type="button" onClick={() => setInv("amount", invoiceTotalFromCosting.toFixed(2))}
                    title="Fill from Job Costing total"
                    style={{ ...S.btnS, whiteSpace: "nowrap", padding: "8px 12px", fontSize: 12 }}>
                    Use ${invoiceTotalFromCosting.toFixed(2)}
                  </button>
                )}
                {p.estimate?.price > 0 && (
                  <button type="button" onClick={() => setInv("amount", Number(p.estimate.price).toFixed(2))}
                    title={`R&R estimate — customer price (internal cost $${Math.round(p.estimate.cost || 0)}, ${Math.round((p.estimate.margin || 0) * 100)}% margin)`}
                    style={{ ...S.btnS, whiteSpace: "nowrap", padding: "8px 12px", fontSize: 12, borderColor: C.gold, color: C.gold }}>
                    Use estimate ${Number(p.estimate.price).toFixed(2)}
                  </button>
                )}
              </div>
            </Field>
            <Field label="Date Sent">
              <input style={S.input} type="date" value={inv.sentDate || ""} onChange={e => setInv("sentDate", e.target.value)} />
            </Field>
            <Field label="Date Paid">
              <input style={S.input} type="date" value={inv.paidDate || ""} onChange={e => setInv("paidDate", e.target.value)} />
            </Field>
          </div>

          {inv.sentDate && !inv.paidDate && dueDate && (
            <div style={{ fontSize: 12, color: isOverdue ? C.danger : C.muted, marginBottom: 12 }}>
              Net 30 — due {dueDate.toLocaleDateString()}{isOverdue ? ` (${daysOutstanding - 30} days past due)` : ""}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Submitted Via">
              <select style={S.select} value={inv.method || ""} onChange={e => setInv("method", e.target.value)}>
                <option value="">— select —</option>
                <option value="quickbooks">QuickBooks (sent direct)</option>
                <option value="portal">Partner portal upload</option>
                <option value="email">Email</option>
                <option value="other">Other</option>
              </select>
            </Field>
            <Field label="Payment Method">
              <select style={S.select} value={inv.paymentMethod || ""} onChange={e => setInv("paymentMethod", e.target.value)}>
                <option value="">— select —</option>
                <option value="check">Check</option>
                <option value="ach">ACH / Transfer</option>
                <option value="card">Card</option>
                <option value="cash">Cash</option>
                <option value="other">Other</option>
              </select>
            </Field>
          </div>

          <Field label="QuickBooks / Portal Link">
            <input style={S.input} value={inv.link || ""} onChange={e => setInv("link", e.target.value)} placeholder="Paste the QuickBooks invoice link or portal submission URL" />
          </Field>
          <Field label="Invoice Notes">
            <textarea style={S.textarea} rows={2} value={inv.notes || ""} onChange={e => setInv("notes", e.target.value)} placeholder="PO number, partner reference, payment terms exceptions..." />
          </Field>

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button type="button" onClick={copyForQuickBooks} style={S.btnS}>{I.clip} Copy Line Items for QuickBooks</button>
            {inv.link && <a href={inv.link} target="_blank" rel="noopener noreferrer" style={{ ...S.btnS, textDecoration: "none", display: "inline-flex" }}>Open Invoice ↗</a>}
            {!inv.paidDate && (
              <button type="button" onClick={() => setInv("paidDate", localDate())} style={{ ...S.btnS, borderColor: C.success, color: C.success }}>
                {I.check} Mark Paid Today
              </button>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>Remember to hit Save Project below after updating the invoice.</div>
        </div>
      )}

      {/* Actions */}
      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, marginTop: 8, marginBottom: 40 }}>
        <div>{!isNew && isAdmin && <button style={S.btnDanger} onClick={confirmDelete}>{I.trash} {t.deleteProject}</button>}</div>
        <div style={{ display: "flex", gap: 10 }}>
          <button style={S.btnS} onClick={cancelEdit}>{t.cancel}</button>
          <button style={S.btnP} onClick={save}>{I.check} {t.saveProject}</button>
        </div>
      </div>

      {inviteOpen && (
        <Modal onClose={() => setInviteOpen(false)}>
          <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: C.light }}>
            {(p.inviteSeq || 0) > 0 ? "📅 Send Updated Invite" : "📅 Send Calendar Invite"}
          </h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>{p.name || p.client}</p>
          {(() => {
            const v = visits.find(x => x.id === activeVisit) || visits[0];
            if (!v || (v.invited || []).length === 0) return null;
            return (
              <div style={{ marginBottom: 16 }}>
                <div style={{ ...S.label, marginBottom: 8 }}>This send is…</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" onClick={() => setInviteMode("add")} style={{
                    flex: 1, minWidth: 150, padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", textAlign: "left",
                    border: `1px solid ${inviteMode === "add" ? C.gold : C.border}`,
                    background: inviteMode === "add" ? `${C.gold}22` : "transparent",
                    color: inviteMode === "add" ? C.gold : C.muted,
                  }}>
                    Adding someone
                    <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400, marginTop: 2 }}>Only the people you tick get an email</div>
                  </button>
                  <button type="button" onClick={() => setInviteMode("update")} style={{
                    flex: 1, minWidth: 150, padding: "10px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", textAlign: "left",
                    border: `1px solid ${inviteMode === "update" ? C.info : C.border}`,
                    background: inviteMode === "update" ? `${C.info}22` : "transparent",
                    color: inviteMode === "update" ? C.info : C.muted,
                  }}>
                    Time changed
                    <div style={{ fontSize: 11, opacity: 0.8, fontWeight: 400, marginTop: 2 }}>Moves the entry on everyone's calendar</div>
                  </button>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 8, lineHeight: 1.5 }}>
                  Already invited: {(v.invited || []).join(", ")}
                </div>
              </div>
            );
          })()}

          <Field label="Duration (hours)">
            <input type="number" min="0.5" step="0.5" style={S.input} value={inviteDuration} onChange={e => setInviteDuration(e.target.value)} />
          </Field>
          <Field label="Checklist to reference">
            <select style={S.select} value={inviteChecklist} onChange={e => setInviteChecklist(e.target.value)}>
              {INVITE_CHECKLISTS.map(cl => <option key={cl} value={cl}>{cl}</option>)}
            </select>
          </Field>
          <Field label="Procedure to reference">
            <select style={S.select} value={invitePlaybook} onChange={e => setInvitePlaybook(e.target.value)}>
              <option value="">— none —</option>
              {playbooks.filter(pb => !isReferenceDoc(pb)).map(pb => <option key={pb.id} value={pb.id}>{pb.title} ({pb.serviceType})</option>)}
            </select>
            {invitePlaybook && <div style={{ color: C.muted, fontSize: 11, marginTop: 6 }}>Safety steps, on-site steps, and manual links are included in the invite, with a link to the full procedure.</div>}
          </Field>
          <Field label="Reported issue / notes">
            <textarea style={S.textarea} rows={3} value={inviteNotes} onChange={e => setInviteNotes(e.target.value)} placeholder="What the homeowner or partner reported — gate codes, access notes, anything the crew needs" />
          </Field>
          <Field label="Send to">
            {(p.clientEmail || "").includes("@") ? (
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", fontSize: 13, color: C.gold, cursor: "pointer", fontWeight: 600, borderBottom: `1px solid ${C.border}`, marginBottom: 4 }}>
                <input type="checkbox" checked={inviteHomeowner} onChange={e => setInviteHomeowner(e.target.checked)} />
                Homeowner — {p.clientEmail}
              </label>
            ) : (
              <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>Add a homeowner email above to send them a confirmation too.</div>
            )}
            {(p.clientPhone || "").replace(/\D/g, "").length >= 10 && USE_SUPABASE && (
              <label style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", fontSize: 13, color: C.gold, cursor: "pointer", fontWeight: 600, borderBottom: `1px solid ${C.border}`, marginBottom: 6 }}>
                <input type="checkbox" checked={inviteText} onChange={e => setInviteText(e.target.checked)} />
                Also text the homeowner the confirmation — {p.clientPhone} <span style={{ color: C.muted, fontWeight: 400 }}>(from (312) 820-9027)</span>
              </label>
            )}
            {emailMembers.length === 0 && <div style={{ color: C.muted, fontSize: 12 }}>No team members with an email on this project yet.</div>}
            {emailMembers.map(m => (
              <label key={m.idx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 0", fontSize: 13, color: C.light, cursor: "pointer" }}>
                <input type="checkbox" checked={!!inviteRecipients[m.idx]} onChange={e => setInviteRecipients(prev => ({ ...prev, [m.idx]: e.target.checked }))} />
                {m.name}
              </label>
            ))}
          </Field>
          <button onClick={sendInvite} disabled={inviteSending} style={{ ...S.btnP, width: "100%", justifyContent: "center", opacity: inviteSending ? 0.6 : 1 }}>
            {inviteSending ? "Sending..." : <>{I.send} Send Invite</>}
          </button>
        </Modal>
      )}
    </div>
  );
}

function ProjectTracker({ user, t, isAdmin, canManage = isAdmin, initialPartner, onPartnerHandled, onOpenMap, initialProjectId, onProjectOpened }) {
  const [projects, setProjects] = useState([]);
  const [editing, setEditing] = useState(null); // null = list, "new" or project id
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [invFilter, setInvFilter] = useState("all"); // all | unpaid | overdue
  const [partnerFilter, setPartnerFilter] = useState("all"); // "all" | "direct" | partner name
  useEffect(() => {
    if (!initialPartner) return;
    setPartnerFilter(initialPartner);
    onPartnerHandled?.();
  }, [initialPartner, onPartnerHandled]);
  // Deep link straight into one job (#projects/id/<id>) once the list is here
  useEffect(() => {
    if (!initialProjectId || projects.length === 0) return;
    if (projects.some(p => p.id === initialProjectId)) setEditing(initialProjectId);
    onProjectOpened?.();
  }, [initialProjectId, projects]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load projects
  const load = useCallback(async () => {
    setLoading(true);
    const data = await db.getProjects();
    // Non-admins only see projects they created or were added to as a member
    const visible = canManage ? data : data.filter(p => {
      if (p.created_by && user?.name && p.created_by === user.name) return true;
      return (p.members || []).some(m =>
        (m.name && user?.name && m.name.toLowerCase() === user.name.toLowerCase()) ||
        (m.name && user?.email && m.name.toLowerCase() === user.email.toLowerCase())
      );
    });
    setProjects(visible);
    setLoading(false);
  }, []);
  // Load on mount
  const loaded = useRef(false);
  if (!loaded.current) { loaded.current = true; setTimeout(load, 50); }

  // ─── Project Editor ────────────────────────────────────────────────────

  // ─── Project List ──────────────────────────────────────────────────────
  const statusColors = { not_started: C.muted, in_progress: C.info, pending_submission: C.gold, completed: C.success, on_hold: C.warning };
  const statusLabels = { not_started: t.notStarted, in_progress: t.inProgress, pending_submission: t.pendingSubmission, completed: t.completed, on_hold: t.onHold };

  // NOTE: this hook must run on every render, before any early return below —
  // otherwise React sees a different number of hooks between renders and
  // crashes to a blank screen the moment `editing` changes.
  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = projects;
    if (q) {
      list = list.filter(p =>
        (p.name || "").toLowerCase().includes(q) ||
        (p.client || "").toLowerCase().includes(q) ||
        (p.address || "").toLowerCase().includes(q) ||
        (p.partner || "").toLowerCase().includes(q) ||
        (p.serviceSubtype || "").toLowerCase().includes(q) ||
        (p.scope || "").toLowerCase().includes(q) ||
        (p.permit?.number || "").toLowerCase().includes(q) ||
        (p.workOrder || "").toLowerCase().includes(q) ||
        // Invoice / SQ / work-order numbers — only present for money logins
        (p.invoice?.number || "").toLowerCase().includes(q) ||
        (p.clientEmail || "").toLowerCase().includes(q) ||
        (p.clientPhone || "").replace(/\D/g, "").includes(q.replace(/\D/g, "") || "~")
      );
    }
    // Invoice filters are an admin-only affordance; ignore for everyone else
    if (partnerFilter === "direct") list = list.filter(p => !(p.partner || "").trim());
    else if (partnerFilter !== "all") {
      const want = partnerFilter.trim().toLowerCase();
      list = list.filter(p => (p.partner || "").trim().toLowerCase() === want);
    }
    if (canManage && invFilter === "unpaid") {
      // Sent and unpaid, or work completed and not paid yet (invoice may not be out)
      list = list.filter(p => !p.invoice?.paidDate && (p.invoice?.sentDate || p.status === "completed"));
    } else if (canManage && invFilter === "overdue") {
      list = list.filter(p => {
        const iv = p.invoice;
        if (!iv?.sentDate || iv.paidDate) return false;
        return Math.floor((Date.now() - new Date(iv.sentDate + "T00:00:00").getTime()) / 86400000) > 30;
      });
    }
    // Non-completed projects first; within each group, most recently active first
    return [...list].sort((a, b) => {
      const aDone = a.status === "completed" ? 1 : 0;
      const bDone = b.status === "completed" ? 1 : 0;
      if (aDone !== bDone) return aDone - bDone;
      const at = new Date(a.updated_at || a.created_at || 0).getTime();
      const bt = new Date(b.updated_at || b.created_at || 0).getTime();
      return bt - at;
    });
  }, [projects, search, invFilter, partnerFilter, isAdmin, canManage]);

  if (editing !== null) {
    const project = editing === "new" ? null : projects.find(p => p.id === editing);
    return <ProjectEditor project={project} onCancel={() => setEditing(null)} user={user} isAdmin={isAdmin} canManage={canManage} t={t} onOpenMap={onOpenMap}
      onSave={(p, opts = {}) => { setProjects(prev => { const idx = prev.findIndex(x => x.id === p.id); if (idx >= 0) { const next = [...prev]; next[idx] = p; return next; } return [p, ...prev]; }); setEditing(opts.stay ? p.id : null); }}
      onDelete={async (id) => { await db.deleteProject(id); setProjects(prev => prev.filter(p => p.id !== id)); setEditing(null); }} />;
  }

  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{t.projectTracker}</h2>
          <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13 }}>{projects.length} {t.projects.toLowerCase()}</p>
        </div>
        <button style={S.btnP} onClick={() => setEditing("new")}>{I.plus} {t.newProject}</button>
      </div>

      {canManage && (() => {
        // Money rollup — what's owed and what's late, across every project.
        // Admin and coordinator only: crew members shouldn't see billing figures.
        let outstanding = 0, overdue = 0, overdueCount = 0;
        projects.forEach(p => {
          const iv = p.invoice;
          if (!iv || iv.paidDate || !iv.sentDate) return;
          const amt = Number(iv.amount) || 0;
          outstanding += amt;
          const days = Math.floor((Date.now() - new Date(iv.sentDate + "T00:00:00").getTime()) / 86400000);
          if (days > 30) { overdue += amt; overdueCount++; }
        });
        if (outstanding === 0) return null;
        const fmt = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return (
          <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
            <div style={{ ...S.card, margin: 0, flex: 1, minWidth: 150, padding: "14px 18px" }}>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Outstanding</div>
              <div style={{ fontSize: 22, fontWeight: 900, color: C.info, marginTop: 4 }}>{fmt(outstanding)}</div>
            </div>
            {overdue > 0 && (
              <div style={{ ...S.card, margin: 0, flex: 1, minWidth: 150, padding: "14px 18px", border: `1px solid ${C.danger}44` }}>
                <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>Overdue ({overdueCount})</div>
                <div style={{ fontSize: 22, fontWeight: 900, color: C.danger, marginTop: 4 }}>{fmt(overdue)}</div>
              </div>
            )}
          </div>
        );
      })()}

      {projects.length > 0 && (
        <>
          <input style={{ ...S.input, marginBottom: 10 }} value={search} onChange={e => setSearch(e.target.value)}
            placeholder={canSeeMoney(user) ? "Search by name, client, address, invoice #, phone..." : "Search by project name, client, or address..."} />
          {(() => {
            // One chip per partner even if the name was typed with different
            // casing or stray spaces on different jobs.
            const seen = new Map();
            projects.forEach(p => {
              const raw = (p.partner || "").trim();
              if (raw && !seen.has(raw.toLowerCase())) seen.set(raw.toLowerCase(), raw);
            });
            const names = [...seen.values()].sort((a, b) => a.localeCompare(b));
            const anyDirect = projects.some(p => !(p.partner || "").trim());
            if (names.length === 0) return null;
            const chips = [
              { id: "all", label: "All Partners", n: projects.length },
              ...names.map(n => ({ id: n, label: n, n: projects.filter(p => (p.partner || "").trim().toLowerCase() === n.toLowerCase()).length })),
              ...(anyDirect ? [{ id: "direct", label: "Direct / internal", n: projects.filter(p => !(p.partner || "").trim()).length }] : []),
            ];
            return (
              <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
                {chips.map(f => (
                  <button key={f.id} onClick={() => setPartnerFilter(f.id)} style={{
                    padding: "6px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                    border: `1px solid ${partnerFilter.toLowerCase() === f.id.toLowerCase() ? C.gold : C.border}`,
                    background: partnerFilter.toLowerCase() === f.id.toLowerCase() ? `${C.gold}22` : "transparent",
                    color: partnerFilter.toLowerCase() === f.id.toLowerCase() ? C.gold : C.muted,
                  }}>{f.label} <span style={{ opacity: 0.7 }}>({f.n})</span></button>
                ))}
              </div>
            );
          })()}
          {canSeeMoney(user) && <div style={{ display: "flex", gap: 6, marginBottom: 18, flexWrap: "wrap" }}>
            {[{ id: "all", label: "All" }, { id: "unpaid", label: "Awaiting Payment" }, { id: "overdue", label: "⚠ Overdue" }].map(f => (
              <button key={f.id} onClick={() => setInvFilter(f.id)} style={{
                padding: "6px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                border: `1px solid ${invFilter === f.id ? C.gold : C.border}`,
                background: invFilter === f.id ? `${C.gold}22` : "transparent",
                color: invFilter === f.id ? C.gold : C.muted,
              }}>{f.label}</button>
            ))}
          </div>}
        </>
      )}

      {loading ? <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading...</div> :
       projects.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 50 }}>
          <div style={{ width: 50, height: 50, borderRadius: 14, background: `${C.gold}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", color: C.gold }}>{I.folder}</div>
          <div style={{ color: C.muted, fontSize: 14, fontWeight: 600 }}>{t.noProjects}</div>
          <div style={{ color: `${C.muted}66`, fontSize: 12, marginTop: 4 }}>{t.noProjectsDesc}</div>
        </div>
      ) : visible.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No projects match "{search}"</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {visible.map(p => (
            <div key={p.id} style={{ ...S.card, cursor: "pointer", transition: "border-color 0.15s" }} onClick={() => setEditing(p.id)}
              onMouseEnter={e => e.currentTarget.style.borderColor = C.gold} onMouseLeave={e => e.currentTarget.style.borderColor = C.border}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 700, color: C.light }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{p.client}{p.address ? ` · ${p.address}` : ""}{p.systemSize ? ` · ${p.systemSize} kW` : ""}</div>
                  {p.scope && <div style={{ fontSize: 12.5, color: C.light, marginTop: 4, lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>🛠 {p.scope}</div>}
                  {permitRequired(p) && (() => { const st = permitStage(p); return <div style={{ fontSize: 11.5, color: st.tone, marginTop: 3, fontWeight: 600 }}>📋 {st.label}</div>; })()}
                </div>
                <span style={S.badge(statusColors[p.status] || C.muted)}>{statusLabels[p.status] || p.status}</span>
                {p.partner && <span style={S.badge(C.gold)}>{p.partner}</span>}
                {p.workOrder && <span style={S.badge(C.muted)}>#{p.workOrder}</span>}
                {p.serviceSubtype && <span style={S.badge(C.info)}>{p.serviceSubtype}</span>}
              </div>
              {(() => {
                // Partner submission deadline — the one date that decides payment
                const tk = (p.tasks || []).find(x => !x.done && x.due && isShareTask(x));
                if (!tk) return null;
                const today = isoToJobWall(new Date().toISOString()).slice(0, 10);
                const late = tk.due < today, isToday = tk.due === today;
                const color = late ? C.danger : isToday ? C.warning : C.info;
                const [y, m, d] = tk.due.split("-").map(Number);
                const label = new Date(Date.UTC(y, m - 1, d)).toLocaleDateString("en-US", { timeZone: "UTC", weekday: "short", month: "short", day: "numeric" });
                return (
                  <div style={{ fontSize: 12, fontWeight: 700, color, marginBottom: 10 }}>
                    ⏱ {tk.name} {late ? `— OVERDUE (was ${label})` : isToday ? "— due TODAY" : `by ${label}`}
                  </div>
                );
              })()}
              {p.scheduled_at && (
                <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, fontSize: 12, color: C.gold, fontWeight: 600 }}>
                  📅 {fmtJobDateTime(p.scheduled_at)}
                </div>
              )}
              {canSeeMoney(user) && (() => {
                const iv = p.invoice;
                if (!iv || (!iv.amount && !iv.number)) return null;
                const amt = Number(iv.amount) || 0;
                const amtStr = amt ? "$" + amt.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : "";
                if (iv.paidDate) return <div style={{ fontSize: 12, color: C.success, fontWeight: 600, marginBottom: 10 }}>✓ Paid {amtStr}</div>;
                if (iv.sentDate) {
                  const days = Math.floor((Date.now() - new Date(iv.sentDate + "T00:00:00").getTime()) / 86400000);
                  const over = days > 30;
                  return <div style={{ fontSize: 12, color: over ? C.danger : C.info, fontWeight: 600, marginBottom: 10 }}>
                    {over ? "⚠ Overdue" : "Sent"} {amtStr} — {days}d
                  </div>;
                }
                return <div style={{ fontSize: 12, color: C.warning, fontWeight: 600, marginBottom: 10 }}>Invoice draft {amtStr}</div>;
              })()}
              {/* Progress bar */}
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
                <div style={{ flex: 1, height: 6, background: C.surface, borderRadius: 3, overflow: "hidden" }}>
                  <div style={{ width: `${p.completion || 0}%`, height: "100%", background: (p.completion || 0) === 100 ? C.success : C.gold, borderRadius: 3 }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, color: (p.completion || 0) === 100 ? C.success : C.gold }}>{p.completion || 0}%</span>
              </div>
              {/* Summary */}
              <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.muted }}>
                <span>{I.list} {(p.tasks || []).filter(t => t.done).length}/{(p.tasks || []).length} {t.tasks.toLowerCase()}</span>
                <span>{I.box} {(p.materials || []).length} {t.materials.toLowerCase()}</span>
                <span>{I.note} {(p.notes || []).length} {t.notes.toLowerCase()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Inventory Tracker ──────────────────────────────────────────────────────
const INVENTORY_CATEGORIES = ["Electrical", "Tools", "Critter Guard", "General"];
const CAT_COLORS = { Electrical: C.warning, Tools: C.info, "Critter Guard": C.success, General: C.muted };

function InventoryTracker({ user, t }) {
  const [items, setItems] = useState([]);
  const [logs, setLogs] = useState([]);
  const [view, setView] = useState("list"); // list, add, detail, logs
  const [editItem, setEditItem] = useState(null);
  const [search, setSearch] = useState("");
  const [catFilter, setCatFilter] = useState("all");
  const [locFilter, setLocFilter] = useState("all"); // all, warehouse, truck, low
  const [modal, setModal] = useState(null);
  const [moveQty, setMoveQty] = useState(1);
  const [moveFrom, setMoveFrom] = useState("warehouse");
  const [jobInput, setJobInput] = useState("");
  const emptyForm = { name: "", category: "Electrical", sku: "", warehouse_qty: 0, truck_qty: 0, min_stock: 0, unit: "pcs", notes: "", scan_hints: "", group_name: "", unit_cost: null };
  const [form, setForm] = useState(emptyForm);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  // Scan Out state
  const [scanImage, setScanImage] = useState(null);      // { dataUrl, base64, mediaType }
  const [scanning, setScanning] = useState(false);
  const [scanResults, setScanResults] = useState(null);  // [{ itemId, name, qty, matched, unit }]
  const [scanJob, setScanJob] = useState("");
  const [scanError, setScanError] = useState("");
  const scanCamRef = useRef(null);
  const [expandedGroups, setExpandedGroups] = useState({});

  // Load inventory from Supabase or localStorage
  const loadInventory = useCallback(async () => {
    if (USE_SUPABASE && supabase) {
      try {
        const { data } = await supabase.from("inventory").select("*").order("name");
        if (data) setItems(data);
        const { data: logData } = await supabase.from("inventory_logs").select("*").order("created_at", { ascending: false }).limit(100);
        if (logData) setLogs(logData);
      } catch(e) { console.warn("Failed to load inventory:", e); }
    } else {
      setItems(localCache.load("inventory") || []);
      setLogs(localCache.load("inventory_logs") || []);
    }
    setLoaded(true);
  }, []);

  const loadedRef = useRef(false);
  if (!loadedRef.current) { loadedRef.current = true; loadInventory(); }

  const saveItem = async (item, { navigate = true } = {}) => {
    const isNew = !item.id;
    const record = {
      ...item,
      id: item.id || `inv-${Date.now()}`,
      updated_at: new Date().toISOString(),
      updated_by: user?.name,
    };
    if (USE_SUPABASE && supabase) {
      try {
        if (isNew) {
          record.created_at = new Date().toISOString();
          record.created_by = user?.name;
          const { data, error } = await supabase.from("inventory").insert(record).select().single();
          if (error) throw error;
          setItems(p => [...p, data]);
        } else {
          const { error } = await supabase.from("inventory").update(record).eq("id", item.id);
          if (error) throw error;
          setItems(p => p.map(i => i.id === item.id ? record : i));
        }
      } catch(e) { console.warn("Save inventory error:", e); alert("Failed to save — check your connection."); }
    } else {
      if (isNew) { record.created_at = new Date().toISOString(); setItems(p => { const n = [...p, record]; localCache.save("inventory", n); return n; }); }
      else { setItems(p => { const n = p.map(i => i.id === item.id ? record : i); localCache.save("inventory", n); return n; }); }
    }
    if (navigate) { setView("list"); setEditItem(null); }
    return record;
  };

  const addLog = async (entry) => {
    const record = { ...entry, id: `log-${Date.now()}`, created_at: new Date().toISOString(), user_name: user?.name };
    if (USE_SUPABASE && supabase) {
      try {
        const { data } = await supabase.from("inventory_logs").insert(record).select().single();
        if (data) setLogs(p => [data, ...p]);
      } catch(e) { console.warn("Log error:", e); }
    } else {
      setLogs(p => { const n = [record, ...p]; localCache.save("inventory_logs", n); return n; });
    }
  };

  const transfer = async (item, from, to, qty) => {
    if (!qty || qty <= 0) { alert("Enter a quantity greater than 0."); return null; }
    if (qty > (item[`${from}_qty`] || 0)) { alert(`Only ${item[`${from}_qty`] || 0} available at ${from}.`); return null; }
    const updated = { ...item, [`${from}_qty`]: (item[`${from}_qty`] || 0) - qty, [`${to}_qty`]: (item[`${to}_qty`] || 0) + qty };
    const saved = await saveItem(updated, { navigate: false });
    await addLog({ item_id: item.id, item_name: item.name, action: "transfer", from_location: from, to_location: to, quantity: qty });
    return saved;
  };

  const useOnJob = async (item, location, qty, jobName) => {
    if (!qty || qty <= 0) { alert("Enter a quantity greater than 0."); return null; }
    if (qty > (item[`${location}_qty`] || 0)) { alert(`Only ${item[`${location}_qty`] || 0} available at ${location}.`); return null; }
    const updated = { ...item, [`${location}_qty`]: (item[`${location}_qty`] || 0) - qty };
    const saved = await saveItem(updated, { navigate: false });
    await addLog({ item_id: item.id, item_name: item.name, action: "used", from_location: location, quantity: qty, job_name: jobName });
    return saved;
  };

  // ─── Scan Out (camera + AI detection) ─────────────────────────────────────
  const handleScanPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setScanError(""); setScanResults(null);
    let converted = file;
    try {
      converted = await db.convertToJpeg(file);
    } catch (err) {
      if (err?.code === "HEIC_CONVERSION_FAILED") { setScanError(db.heicHelpText(file.name)); return; }
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const dataUrl = ev.target.result;
      const base64 = dataUrl.split(",")[1];
      setScanImage({ dataUrl, base64, mediaType: converted.type || "image/jpeg" });
    };
    reader.readAsDataURL(converted);
    e.target.value = "";
  };

  const runScan = async () => {
    if (!scanImage || !supabase) return;
    setScanning(true); setScanError("");
    try {
      const inventoryList = items.map(i => ({ id: i.id, name: i.name, sku: i.sku || "", category: i.category, unit: i.unit || "pcs", truck_qty: i.truck_qty || 0, hints: i.scan_hints || "" }));
      const { data, error } = await supabase.functions.invoke("scan-inventory", {
        body: { image: scanImage.base64, mediaType: scanImage.mediaType, inventory: inventoryList }
      });
      if (error) throw error;
      const detected = data?.items || [];
      if (detected.length === 0) { setScanError("No inventory items recognized in the photo. Try a clearer shot or add items manually below."); }
      setScanResults(detected.map(d => ({ ...d, qty: Math.max(1, d.qty || 1) })));
    } catch (err) {
      console.warn("Scan error:", err);
      setScanError("Scan failed — check that the scan-inventory Edge Function is deployed. You can still build the list manually below.");
      setScanResults([]);
    }
    setScanning(false);
  };

  const approveScan = async () => {
    const valid = (scanResults || []).filter(r => r.itemId && r.qty > 0);
    if (valid.length === 0) return alert("Nothing to approve — add at least one item with quantity.");
    for (const r of valid) {
      const item = items.find(i => i.id === r.itemId);
      if (!item) continue;
      const avail = item.truck_qty || 0;
      const qty = Math.min(r.qty, avail);
      if (qty <= 0) continue;
      const updated = { ...item, truck_qty: avail - qty };
      await saveItem(updated, { navigate: false });
      await addLog({ item_id: item.id, item_name: item.name, action: "used", from_location: "truck", quantity: qty, job_name: scanJob || "Scan-out" });
    }
    setScanImage(null); setScanResults(null); setScanJob("");
    setView("list");
    alert("Inventory updated ✓");
  };

  const deleteItem = async (id) => {
    if (USE_SUPABASE && supabase) {
      try { await supabase.from("inventory").delete().eq("id", id); } catch(e) { console.warn(e); }
    }
    setItems(p => { const n = p.filter(i => i.id !== id); localCache.save("inventory", n); return n; });
    setView("list");
    setEditItem(null);
  };

  const filtered = useMemo(() => {
    let list = items;
    if (catFilter !== "all") list = list.filter(i => i.category === catFilter);
    if (locFilter === "warehouse") list = list.filter(i => (i.warehouse_qty || 0) > 0);
    if (locFilter === "truck") list = list.filter(i => (i.truck_qty || 0) > 0);
    if (locFilter === "low") list = list.filter(i => ((i.warehouse_qty || 0) + (i.truck_qty || 0)) <= (i.min_stock || 0) && (i.min_stock || 0) > 0);
    if (search) list = list.filter(i => (i.name + " " + (i.sku || "") + " " + (i.category || "")).toLowerCase().includes(search.toLowerCase()));
    return list;
  }, [items, catFilter, locFilter, search]);

  const lowStockCount = useMemo(() => items.filter(i => ((i.warehouse_qty || 0) + (i.truck_qty || 0)) <= (i.min_stock || 0) && (i.min_stock || 0) > 0).length, [items]);

  // ─── Add/Edit Form ────────────────────────────────────────────────────────
  if (view === "add" || view === "edit") {
    const isEdit = view === "edit" && editItem;
    return (
      <div style={{ maxWidth: 600, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => { setView("list"); setEditItem(null); }} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: "0 0 20px" }}>{isEdit ? t.itemUpdated : t.addItem}</h2>
        <div style={S.card}>
          <Field label={t.itemName} required>
            <MaterialPicker
              value={form.name}
              placeholder="Start typing — suggestions from the supplier catalog"
              options={MATERIALS_CATALOG.map(m => ({ key: m.sku, label: m.name, sub: `${m.sku}${m.putUp ? " · " + m.putUp : " · " + m.cat}`, raw: m }))}
              onChange={(v) => f("name", v)}
              onPick={(o) => setForm(p => ({ ...p, name: o.raw.name, sku: o.raw.sku, unit: o.raw.unit || p.unit, unit_cost: o.raw.price ?? p.unit_cost }))}
            />
            {(() => {
              const hit = catalogLookup(form.name);
              if (!hit) return null;
              return <div style={{ fontSize: 11, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                {hit.sku}{hit.putUp ? ` · ${hit.putUp}` : ""}
                {hit.cat === "Wire" || hit.cat === "Conduit" ? ` · sheet dated ${WIRE_PRICE_SHEET_DATE}` : ""}
              </div>;
            })()}
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label={t.category}>
              <select style={S.select} value={form.category} onChange={e => f("category", e.target.value)}>
                {INVENTORY_CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
            <Field label={t.sku}><input style={S.input} value={form.sku || ""} onChange={e => f("sku", e.target.value)} placeholder="Optional" /></Field>
          </div>
          <Field label="Group (optional — groups collapse together in the list)">
            <MaterialPicker
              value={form.group_name || ""}
              placeholder='e.g. "THHN 10 STR", "3/4&quot; Conduit &amp; Fittings"'
              options={[...new Set(items.map(i => i.group_name).filter(Boolean))].sort().map(g => ({
                key: g, label: g,
                sub: `${items.filter(i => i.group_name === g).length} item(s)`,
                raw: { name: g },
              }))}
              onChange={(v) => f("group_name", v)}
              onPick={(o) => f("group_name", o.raw.name)}
            />
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Field label={`${t.warehouseQty}`}><input style={S.input} type="number" min="0" value={form.warehouse_qty} onChange={e => f("warehouse_qty", parseInt(e.target.value) || 0)} /></Field>
            <Field label={`${t.truckQty}`}><input style={S.input} type="number" min="0" value={form.truck_qty} onChange={e => f("truck_qty", parseInt(e.target.value) || 0)} /></Field>
            <Field label={t.unit}><input style={S.input} value={form.unit || "pcs"} onChange={e => f("unit", e.target.value)} placeholder="pcs, ft, rolls" /></Field>
            <Field label="Unit Cost ($)"><input style={S.input} type="number" min="0" step="0.01" value={form.unit_cost ?? ""} onChange={e => f("unit_cost", e.target.value === "" ? null : parseFloat(e.target.value))} placeholder="Optional — used in Job Costing" /></Field>
          </div>
          <Field label={t.minStock + " (alert threshold)"}><input style={S.input} type="number" min="0" value={form.min_stock} onChange={e => f("min_stock", parseInt(e.target.value) || 0)} /></Field>
          <Field label={t.notes}><textarea style={S.textarea} value={form.notes || ""} onChange={e => f("notes", e.target.value)} rows={2} placeholder="Optional notes about this item" /></Field>
          <Field label="Scan Hints (helps AI identify this item)"><textarea style={S.textarea} value={form.scan_hints || ""} onChange={e => f("scan_hints", e.target.value)} rows={2} placeholder='e.g. "1/2 inch LB conduit body — hub ~0.84in OD on ruler, cover opening faces back"' /></Field>
          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button onClick={() => { if (!form.name) return alert("Item name is required"); saveItem(form); }} style={{ ...S.btnP, flex: 1, justifyContent: "center" }}>{I.check} {isEdit ? t.itemUpdated : t.addItem}</button>
            {isEdit && <button onClick={() => { if (window.confirm("Delete this item?")) deleteItem(form.id); }} style={S.btnDanger}>{I.trash}</button>}
          </div>
        </div>
      </div>
    );
  }

  // ─── Scan Out View ────────────────────────────────────────────────────────
  if (view === "scan") {
    const totalDetected = (scanResults || []).reduce((s, r) => s + (r.qty || 0), 0);
    return (
      <div style={{ maxWidth: 600, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => { setView("list"); setScanImage(null); setScanResults(null); setScanError(""); }} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: "0 0 4px" }}>📸 Scan Out</h2>
        <p style={{ color: C.muted, fontSize: 13, margin: "0 0 20px" }}>Photograph items as you take them off the truck. AI builds the list — you review and approve.</p>

        {/* Step 1: Capture or go manual */}
        {!scanImage && !scanResults && (
          <>
            <div onClick={() => scanCamRef.current?.click()}
              style={{ border: `2px dashed ${C.gold}`, borderRadius: 16, padding: "48px 20px", textAlign: "center", cursor: "pointer", background: `${C.gold}08` }}>
              <div style={{ color: C.gold, marginBottom: 10, display: "flex", justifyContent: "center", transform: "scale(1.6)" }}>{I.cam}</div>
              <div style={{ color: C.light, fontSize: 15, fontWeight: 700 }}>Take Photo of Items</div>
              <div style={{ color: C.muted, fontSize: 12, marginTop: 6 }}>Lay items out visibly — tailgate or ground works great</div>
              <input ref={scanCamRef} type="file" accept="image/*" capture="environment" onChange={handleScanPhoto} style={{ display: "none" }} />
            </div>
            <button onClick={() => { setScanResults([{ itemId: "", qty: 1 }]); setScanError(""); }}
              style={{ ...S.btnS, width: "100%", justifyContent: "center", marginTop: 12, padding: "14px 20px" }}>
              ✏️ Skip photo — enter items manually
            </button>
            <div style={{ color: `${C.muted}88`, fontSize: 11, textAlign: "center", marginTop: 8 }}>
              Use manual entry for wire cuts, conduit lengths, and anything hard to see in a photo
            </div>
          </>
        )}

        {/* Step 2: Preview + Analyze */}
        {scanImage && !scanResults && (
          <div style={S.card}>
            <img loading="lazy" decoding="async" src={scanImage.dataUrl} alt="items" style={{ width: "100%", borderRadius: 12, maxHeight: 340, objectFit: "contain", background: C.surface }} />
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              <button onClick={() => { setScanImage(null); setScanError(""); }} style={{ ...S.btnS, flex: 1, justifyContent: "center" }}>Retake</button>
              <button onClick={runScan} disabled={scanning} style={{ ...S.btnP, flex: 2, justifyContent: "center", opacity: scanning ? 0.6 : 1 }}>
                {scanning ? "🔍 Analyzing..." : "🔍 Detect Items"}
              </button>
            </div>
            {scanError && <div style={{ marginTop: 10, color: C.danger, fontSize: 12 }}>{scanError}</div>}
          </div>
        )}

        {/* Step 3: Review, Edit, Approve */}
        {scanResults && (
          <>
            <div style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <div style={S.label}>{scanImage ? "Detected Items — review & edit" : "Checkout List — items being taken out"}</div>
                <button onClick={() => { setScanImage(null); setScanResults(null); setScanError(""); }} style={{ ...S.btnS, padding: "6px 12px", fontSize: 12 }}>{scanImage ? "↺ Rescan" : "↺ Start Over"}</button>
              </div>
              {scanError && <div style={{ color: C.warning, fontSize: 12, marginBottom: 10 }}>{scanError}</div>}
              {scanResults.map((r, idx) => {
                const item = items.find(i => i.id === r.itemId);
                const avail = item?.truck_qty || 0;
                const over = r.qty > avail;
                return (
                  <div key={idx} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.border}33` }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <select style={{ ...S.select, padding: "8px 12px", fontSize: 13 }} value={r.itemId || ""}
                        onChange={e => setScanResults(p => p.map((x, i) => i === idx ? { ...x, itemId: e.target.value } : x))}>
                        <option value="">— select item —</option>
                        {(() => {
                          const gs = new Map(); const singles = [];
                          items.forEach(i => { const g = (i.group_name || "").trim(); if (g) { if (!gs.has(g)) gs.set(g, []); gs.get(g).push(i); } else singles.push(i); });
                          const out = [];
                          [...gs.entries()].sort((a, b) => a[0].localeCompare(b[0])).forEach(([g, mem]) => {
                            out.push(<optgroup key={"g" + g} label={g}>{mem.sort((a, b) => a.name.localeCompare(b.name)).map(i => <option key={i.id} value={i.id}>{i.name} (truck: {i.truck_qty || 0})</option>)}</optgroup>);
                          });
                          singles.sort((a, b) => a.name.localeCompare(b.name)).forEach(i => out.push(<option key={i.id} value={i.id}>{i.name} (truck: {i.truck_qty || 0})</option>));
                          return out;
                        })()}
                      </select>
                      {r.detectedAs && !item && <div style={{ fontSize: 10, color: C.warning, marginTop: 3 }}>AI saw: "{r.detectedAs}" — pick the matching item</div>}
                      {over && <div style={{ fontSize: 10, color: C.danger, marginTop: 3 }}>Only {avail} on truck — will deduct {avail}</div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                      <input type="number" min="1" value={r.qty} onChange={e => setScanResults(p => p.map((x, i) => i === idx ? { ...x, qty: parseInt(e.target.value) || 1 } : x))}
                        style={{ ...S.input, width: 68, padding: "8px 10px", textAlign: "center", borderColor: over ? C.danger : C.border }} />
                      <div style={{ fontSize: 9, color: C.muted, fontWeight: 700 }}>{item?.unit || ""}</div>
                    </div>
                    <button onClick={() => setScanResults(p => p.filter((_, i) => i !== idx))} style={{ ...S.btnDanger, padding: "8px 10px" }}>{I.trash}</button>
                  </div>
                );
              })}
              <button onClick={() => setScanResults(p => [...p, { itemId: "", qty: 1 }])} style={{ ...S.btnS, marginTop: 12, fontSize: 12, padding: "8px 14px" }}>{I.plus} Add another item</button>
            </div>
            <div style={S.card}>
              <Field label="Job / Site (optional)"><input style={S.input} value={scanJob} onChange={e => setScanJob(e.target.value)} placeholder="e.g. Smith Residence" /></Field>
              <button onClick={approveScan} style={{ ...S.btnP, width: "100%", justifyContent: "center", marginTop: 4 }}>
                {I.check} Approve — Deduct {(scanResults || []).filter(r => r.itemId && r.qty > 0).length} item{(scanResults || []).filter(r => r.itemId && r.qty > 0).length === 1 ? "" : "s"} from Truck
              </button>
            </div>
          </>
        )}
      </div>
    );
  }

  // ─── Detail View ──────────────────────────────────────────────────────────
  if (view === "detail" && editItem) {
    const item = items.find(i => i.id === editItem.id) || editItem;
    const total = (item.warehouse_qty || 0) + (item.truck_qty || 0);
    const isLow = total <= (item.min_stock || 0) && (item.min_stock || 0) > 0;
    const itemLogs = logs.filter(l => l.item_id === item.id).slice(0, 20);
    return (
      <div style={{ maxWidth: 600, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => { setView("list"); setEditItem(null); }} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: 0 }}>{item.name}</h2>
            <div style={{ display: "flex", gap: 8, marginTop: 6 }}>
              <span style={S.badge(CAT_COLORS[item.category] || C.muted)}>{item.category}</span>
              {item.sku && <span style={{ color: C.muted, fontSize: 12 }}>SKU: {item.sku}</span>}
              {isLow && <span style={S.badge(C.danger)}>⚠ {t.lowStock}</span>}
            </div>
          </div>
          <button onClick={() => { setForm(item); setEditItem(item); setView("edit"); }} style={S.btnS}>{I.clip} Edit</button>
        </div>
        {/* Stock bars */}
        <div style={S.card}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, textAlign: "center" }}>
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>{t.warehouseQty}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: C.info }}>{item.warehouse_qty || 0}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{item.unit || "pcs"}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>{t.truckQty}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: C.warning }}>{item.truck_qty || 0}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{item.unit || "pcs"}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: C.muted, fontWeight: 700, marginBottom: 4 }}>{t.totalQty}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: isLow ? C.danger : C.success }}>{total}</div>
              <div style={{ fontSize: 10, color: C.muted }}>{item.min_stock ? `min: ${item.min_stock}` : ""}</div>
            </div>
          </div>
        </div>
        {/* Actions */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 16 }}>
          <button onClick={() => { setModal({ item, action: "transfer" }); setMoveQty(1); setMoveFrom("warehouse"); }} style={{ ...S.btnS, justifyContent: "center", borderColor: C.info, color: C.info }}>↔ {t.transferTo}</button>
          <button onClick={() => { setModal({ item, action: "use" }); setMoveQty(1); setMoveFrom("truck"); setJobInput(""); }} style={{ ...S.btnS, justifyContent: "center", borderColor: C.warning, color: C.warning }}>⚡ {t.useOnJob}</button>
        </div>
        {item.notes && <div style={{ ...S.card, fontSize: 13, color: C.light }}>{item.notes}</div>}
        {/* Item log */}
        {itemLogs.length > 0 && (
          <div style={S.card}>
            <div style={S.label}>{t.transferHistory}</div>
            {itemLogs.map((l, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "8px 0", borderBottom: i < itemLogs.length - 1 ? `1px solid ${C.border}22` : "none", fontSize: 12 }}>
                <div>
                  <span style={{ color: l.action === "transfer" ? C.info : C.warning, fontWeight: 700 }}>{l.action === "transfer" ? "↔" : "⚡"}</span>
                  <span style={{ color: C.light, marginLeft: 6 }}>
                    {l.action === "transfer" ? `${l.from_location} → ${l.to_location}` : `Used from ${l.from_location}`}
                    {l.job_name ? ` — ${l.job_name}` : ""}
                  </span>
                </div>
                <div style={{ color: C.muted, flexShrink: 0 }}>
                  <span style={{ color: C.light, fontWeight: 600 }}>{l.quantity} {item.unit || "pcs"}</span>
                  <span style={{ marginLeft: 8 }}>{new Date(l.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Modal for transfer/use from detail view */}
        {modal && (
          <Modal onClose={() => setModal(null)}>
            <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: C.light }}>
              {modal.action === "transfer" ? `↔ ${t.transferTo}` : `⚡ ${t.useOnJob}`}
            </h3>
            <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>{modal.item.name}</p>
            {modal.action === "transfer" ? (
              <>
                <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", justifyContent: "center" }}>
                  <button onClick={() => { setMoveFrom("warehouse"); setMoveQty(q => Math.min(q, modal.item.warehouse_qty || 0) || 1); }} style={{ ...S.btnS, padding: "8px 16px", fontSize: 13, borderColor: moveFrom === "warehouse" ? C.info : C.border, color: moveFrom === "warehouse" ? C.info : C.muted, background: moveFrom === "warehouse" ? `${C.info}22` : "transparent" }}>
                    {t.warehouse} ({modal.item.warehouse_qty || 0})
                  </button>
                  <span style={{ color: C.gold, fontWeight: 800, fontSize: 18 }}>→</span>
                  <button onClick={() => { setMoveFrom("truck"); setMoveQty(q => Math.min(q, modal.item.truck_qty || 0) || 1); }} style={{ ...S.btnS, padding: "8px 16px", fontSize: 13, borderColor: moveFrom === "truck" ? C.warning : C.border, color: moveFrom === "truck" ? C.warning : C.muted, background: moveFrom === "truck" ? `${C.warning}22` : "transparent" }}>
                    {t.truck} ({modal.item.truck_qty || 0})
                  </button>
                </div>
                <div style={{ textAlign: "center", color: C.muted, fontSize: 12, marginBottom: 12 }}>
                  {moveFrom === "warehouse" ? `${t.warehouse} → ${t.truck}` : `${t.truck} → ${t.warehouse}`}
                </div>
              </>
            ) : (
              <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
                <button onClick={() => { setMoveFrom("truck"); setMoveQty(q => Math.min(q, modal.item.truck_qty || 0) || 1); }} style={{ ...S.btnS, flex: 1, justifyContent: "center", fontSize: 12, borderColor: moveFrom === "truck" ? C.warning : C.border, color: moveFrom === "truck" ? C.warning : C.muted, background: moveFrom === "truck" ? `${C.warning}22` : "transparent" }}>
                  {t.truck} ({modal.item.truck_qty || 0})
                </button>
                <button onClick={() => { setMoveFrom("warehouse"); setMoveQty(q => Math.min(q, modal.item.warehouse_qty || 0) || 1); }} style={{ ...S.btnS, flex: 1, justifyContent: "center", fontSize: 12, borderColor: moveFrom === "warehouse" ? C.info : C.border, color: moveFrom === "warehouse" ? C.info : C.muted, background: moveFrom === "warehouse" ? `${C.info}22` : "transparent" }}>
                  {t.warehouse} ({modal.item.warehouse_qty || 0})
                </button>
              </div>
            )}
            <Field label={t.moveQty}>
              <input style={S.input} type="number" min="1" max={modal.item[`${moveFrom}_qty`] || 0} value={moveQty} onChange={e => setMoveQty(parseInt(e.target.value) || 0)} />
            </Field>
            {modal.action === "use" && (
              <Field label={t.jobName}><input style={S.input} value={jobInput} onChange={e => setJobInput(e.target.value)} placeholder="e.g. Smith Residence" /></Field>
            )}
            <button onClick={async () => {
              let saved = null;
              if (modal.action === "transfer") {
                const to = moveFrom === "warehouse" ? "truck" : "warehouse";
                saved = await transfer(modal.item, moveFrom, to, moveQty);
              } else {
                saved = await useOnJob(modal.item, moveFrom, moveQty, jobInput);
              }
              if (!saved) return; // validation failed — keep modal open
              setEditItem(saved);
              setModal(null);
            }} style={{ ...S.btnP, width: "100%", justifyContent: "center" }}>
              {I.check} {modal.action === "transfer" ? t.transferred : t.usedOnJob}
            </button>
          </Modal>
        )}
      </div>
    );
  }

  // ─── List View ────────────────────────────────────────────────────────────
  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{t.inventory}</h2>
          <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13 }}>{items.length} items · {lowStockCount > 0 ? <span style={{ color: C.danger }}>⚠ {lowStockCount} {t.lowStock.toLowerCase()}</span> : "All stocked"}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={() => { setScanImage(null); setScanResults(null); setScanError(""); setView("scan"); }} style={{ ...S.btnS, borderColor: C.gold, color: C.gold }}>📸 Scan Out</button>
          <button onClick={() => { setForm(emptyForm); setView("add"); }} style={S.btnP}>{I.plus} {t.addItem}</button>
        </div>
      </div>
      {/* Filters */}
      <div style={{ display: "flex", gap: 6, marginBottom: 12, flexWrap: "wrap" }}>
        {[{ id: "all", label: t.allItems }, { id: "warehouse", label: t.warehouse }, { id: "truck", label: t.truck }, { id: "low", label: "⚠ " + t.lowStock }].map(f => (
          <button key={f.id} onClick={() => setLocFilter(f.id)}
            style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${locFilter === f.id ? C.gold : C.border}`, background: locFilter === f.id ? `${C.gold}22` : "transparent", color: locFilter === f.id ? C.gold : C.muted, fontSize: 12, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            {f.label} {f.id === "low" && lowStockCount > 0 ? `(${lowStockCount})` : ""}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {["all", ...INVENTORY_CATEGORIES].map(c => (
          <button key={c} onClick={() => setCatFilter(c)}
            style={{ padding: "4px 12px", borderRadius: 16, border: `1px solid ${catFilter === c ? (CAT_COLORS[c] || C.gold) : C.border}`, background: catFilter === c ? `${CAT_COLORS[c] || C.gold}22` : "transparent", color: catFilter === c ? (CAT_COLORS[c] || C.gold) : C.muted, fontSize: 11, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            {c === "all" ? t.allItems : c}
          </button>
        ))}
      </div>
      {/* Search */}
      <input style={{ ...S.input, marginBottom: 16 }} value={search} onChange={e => setSearch(e.target.value)} placeholder={t.searchInventory} />
      {/* Items list */}
      {filtered.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 50 }}>
          <div style={{ width: 50, height: 50, borderRadius: 14, background: `${C.gold}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", color: C.gold }}>{I.box}</div>
          <div style={{ color: C.muted, fontSize: 14, fontWeight: 600 }}>{t.noItems}</div>
          <div style={{ color: `${C.muted}66`, fontSize: 12, marginTop: 4 }}>{t.noItemsDesc}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          {(() => {
            // Build display entries: grouped items collapse under a header; ungrouped render flat
            const groups = new Map();
            const singles = [];
            filtered.forEach(item => {
              const g = (item.group_name || "").trim();
              if (g) { if (!groups.has(g)) groups.set(g, []); groups.get(g).push(item); }
              else singles.push(item);
            });
            // Groups with only 1 member render flat too
            const entries = [];
            groups.forEach((members, name) => {
              if (members.length > 1) entries.push({ type: "group", name, members });
              else singles.push(members[0]);
            });
            singles.forEach(item => entries.push({ type: "item", item }));
            entries.sort((a, b) => (a.type === "group" ? a.name : a.item.name).localeCompare(b.type === "group" ? b.name : b.item.name));

            const ItemRow = ({ item, indent }) => {
              const total = (item.warehouse_qty || 0) + (item.truck_qty || 0);
              const isLow = total <= (item.min_stock || 0) && (item.min_stock || 0) > 0;
              return (
                <div onClick={() => { setEditItem(item); setView("detail"); }}
                  style={{ display: "flex", alignItems: "center", padding: indent ? "11px 14px" : "14px 16px", marginLeft: indent ? 22 : 0, background: indent ? `${C.card}cc` : C.card, borderRadius: 12, border: `1px solid ${isLow ? `${C.danger}44` : C.border}`, cursor: "pointer", gap: 12, transition: "all 0.15s" }}>
                  <div style={{ width: indent ? 32 : 40, height: indent ? 32 : 40, borderRadius: 10, background: `${CAT_COLORS[item.category] || C.muted}22`, display: "flex", alignItems: "center", justifyContent: "center", color: CAT_COLORS[item.category] || C.muted, flexShrink: 0 }}>{I.box}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: indent ? 13 : 14, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{item.name}</div>
                    <div style={{ display: "flex", gap: 10, marginTop: 3, fontSize: 11, color: C.muted }}>
                      {!indent && <span style={S.badge(CAT_COLORS[item.category] || C.muted)}>{item.category}</span>}
                      {item.sku && <span>#{item.sku}</span>}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 16, alignItems: "center", flexShrink: 0 }}>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>WH</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.info }}>{item.warehouse_qty || 0}</div>
                    </div>
                    <div style={{ textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>TRK</div>
                      <div style={{ fontSize: 16, fontWeight: 800, color: C.warning }}>{item.truck_qty || 0}</div>
                    </div>
                    {isLow && <span style={{ color: C.danger, fontSize: 16 }}>⚠</span>}
                  </div>
                </div>
              );
            };

            return entries.map((e, idx) => {
              if (e.type === "item") return <ItemRow key={e.item.id} item={e.item} />;
              const open = !!expandedGroups[e.name] || !!search; // searches auto-expand
              const whSum = e.members.reduce((s, m) => s + (m.warehouse_qty || 0), 0);
              const trkSum = e.members.reduce((s, m) => s + (m.truck_qty || 0), 0);
              const anyLow = e.members.some(m => ((m.warehouse_qty || 0) + (m.truck_qty || 0)) <= (m.min_stock || 0) && (m.min_stock || 0) > 0);
              const cat = e.members[0]?.category;
              return (
                <div key={"g-" + e.name} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  <div onClick={() => setExpandedGroups(p => ({ ...p, [e.name]: !p[e.name] }))}
                    style={{ display: "flex", alignItems: "center", padding: "14px 16px", background: `${C.gold}0d`, borderRadius: 12, border: `1px solid ${anyLow ? `${C.danger}44` : `${C.gold}44`}`, cursor: "pointer", gap: 12 }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C.gold}22`, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, flexShrink: 0, fontSize: 14, fontWeight: 900, transition: "transform 0.15s", transform: open ? "rotate(90deg)" : "none" }}>▸</div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 800, fontSize: 14, color: C.light }}>{e.name}</div>
                      <div style={{ display: "flex", gap: 10, marginTop: 3, fontSize: 11, color: C.muted }}>
                        <span style={S.badge(CAT_COLORS[cat] || C.muted)}>{cat}</span>
                        <span>{e.members.length} types</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 16, alignItems: "center", flexShrink: 0 }}>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>WH</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: C.info }}>{whSum}</div>
                      </div>
                      <div style={{ textAlign: "center" }}>
                        <div style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>TRK</div>
                        <div style={{ fontSize: 16, fontWeight: 800, color: C.warning }}>{trkSum}</div>
                      </div>
                      {anyLow && <span style={{ color: C.danger, fontSize: 16 }}>⚠</span>}
                    </div>
                  </div>
                  {open && e.members.sort((a, b) => a.name.localeCompare(b.name)).map(m => <ItemRow key={m.id} item={m} indent />)}
                </div>
              );
            });
          })()}
        </div>
      )}
      {/* Transfer / Use Modal */}
      {modal && (
        <Modal onClose={() => setModal(null)}>
          <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: C.light }}>
            {modal.action === "transfer" ? `↔ ${t.transferTo}` : `⚡ ${t.useOnJob}`}
          </h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px" }}>{modal.item.name}</p>
          {modal.action === "transfer" ? (
            <>
              <div style={{ display: "flex", gap: 8, marginBottom: 12, alignItems: "center", justifyContent: "center" }}>
                <button onClick={() => { setMoveFrom("warehouse"); setMoveQty(q => Math.min(q, modal.item.warehouse_qty || 0) || 1); }} style={{ ...S.btnS, padding: "8px 16px", fontSize: 13, borderColor: moveFrom === "warehouse" ? C.info : C.border, color: moveFrom === "warehouse" ? C.info : C.muted, background: moveFrom === "warehouse" ? `${C.info}22` : "transparent" }}>
                  {t.warehouse} ({modal.item.warehouse_qty || 0})
                </button>
                <span style={{ color: C.gold, fontWeight: 800, fontSize: 18 }}>→</span>
                <button onClick={() => { setMoveFrom("truck"); setMoveQty(q => Math.min(q, modal.item.truck_qty || 0) || 1); }} style={{ ...S.btnS, padding: "8px 16px", fontSize: 13, borderColor: moveFrom === "truck" ? C.warning : C.border, color: moveFrom === "truck" ? C.warning : C.muted, background: moveFrom === "truck" ? `${C.warning}22` : "transparent" }}>
                  {t.truck} ({modal.item.truck_qty || 0})
                </button>
              </div>
              <div style={{ textAlign: "center", color: C.muted, fontSize: 12, marginBottom: 12 }}>
                {moveFrom === "warehouse" ? `${t.warehouse} → ${t.truck}` : `${t.truck} → ${t.warehouse}`}
              </div>
            </>
          ) : (
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              <button onClick={() => { setMoveFrom("truck"); setMoveQty(q => Math.min(q, modal.item.truck_qty || 0) || 1); }} style={{ ...S.btnS, flex: 1, justifyContent: "center", fontSize: 12, borderColor: moveFrom === "truck" ? C.warning : C.border, color: moveFrom === "truck" ? C.warning : C.muted, background: moveFrom === "truck" ? `${C.warning}22` : "transparent" }}>
                {t.truck} ({modal.item.truck_qty || 0})
              </button>
              <button onClick={() => { setMoveFrom("warehouse"); setMoveQty(q => Math.min(q, modal.item.warehouse_qty || 0) || 1); }} style={{ ...S.btnS, flex: 1, justifyContent: "center", fontSize: 12, borderColor: moveFrom === "warehouse" ? C.info : C.border, color: moveFrom === "warehouse" ? C.info : C.muted, background: moveFrom === "warehouse" ? `${C.info}22` : "transparent" }}>
                {t.warehouse} ({modal.item.warehouse_qty || 0})
              </button>
            </div>
          )}
          <Field label={t.moveQty}>
            <input style={S.input} type="number" min="1" max={modal.item[`${moveFrom}_qty`] || 0} value={moveQty} onChange={e => setMoveQty(parseInt(e.target.value) || 0)} />
          </Field>
          {modal.action === "use" && (
            <Field label={t.jobName}><input style={S.input} value={jobInput} onChange={e => setJobInput(e.target.value)} placeholder="e.g. Smith Residence" /></Field>
          )}
          <button onClick={async () => {
            let saved = null;
            if (modal.action === "transfer") {
              const to = moveFrom === "warehouse" ? "truck" : "warehouse";
              saved = await transfer(modal.item, moveFrom, to, moveQty);
            } else {
              saved = await useOnJob(modal.item, moveFrom, moveQty, jobInput);
            }
            if (!saved) return;
            setModal(null);
          }} style={{ ...S.btnP, width: "100%", justifyContent: "center" }}>
            {I.check} {modal.action === "transfer" ? t.transferred : t.usedOnJob}
          </button>
        </Modal>
      )}
    </div>
  );
}

// ─── Job Costing ─────────────────────────────────────────────────────────────
// NOTE: defined at module scope — defining it inside CostTracker would remount
// every row on each keystroke and drop input focus.
const CostRow = ({ children }) => <div style={{ padding: 12, background: C.surface, borderRadius: 8, marginBottom: 6, border: `1px solid ${C.border}` }}>{children}</div>;

// ─── Safety Center ──────────────────────────────────────────────────────────
// NOTE ON OSHA: SolarAbility is under 10 employees, so it is partially exempt
// from routinely keeping the OSHA 300 log, 300A summary, and 301 form
// (29 CFR 1904.1). That exemption does NOT cover severe-event reporting —
// every employer, regardless of size, must report a fatality within 8 hours
// and an in-patient hospitalization, amputation, or loss of an eye within
// 24 hours (29 CFR 1904.39). The incident form surfaces that trigger.

const INCIDENT_TYPES = [
  { id: "near_miss", label: "Near Miss", severity: "low" },
  { id: "first_aid", label: "First Aid Only", severity: "low" },
  { id: "medical", label: "Medical Treatment", severity: "med" },
  { id: "lost_time", label: "Lost Time Injury", severity: "med" },
  { id: "hospitalization", label: "Hospitalization", severity: "high" },
  { id: "amputation", label: "Amputation", severity: "high" },
  { id: "eye_loss", label: "Loss of an Eye", severity: "high" },
  { id: "fatality", label: "Fatality", severity: "critical" },
  { id: "property", label: "Property Damage Only", severity: "low" },
];

// Each inspection type carries its own checkpoints so the inspector isn't
// working from memory. `mode` decides what the checklist means:
//   "condition" = inspect each point, any failure can fail the item
//   "present"   = confirm the item is stocked/in the truck
const INSPECTION_TYPES = [
  {
    name: "Harness / Fall Protection", mode: "condition",
    points: [
      "Webbing — no cuts, fraying, burns, or chemical damage",
      "Stitching intact, no pulled or broken threads",
      "D-rings — not bent, cracked, or corroded; move freely",
      "Buckles and adjusters work, no distortion",
      "Grommets/eyelets not elongated or torn",
      "Labels legible, manufacture date within service life",
      "Lanyard / SRL — no cuts, retracts and locks properly",
      "Snap hooks and carabiners latch and lock",
      "No sign of prior fall arrest (deployed indicator)",
    ],
  },
  {
    name: "Ladders", mode: "condition",
    points: [
      "Rails — no cracks, bends, or splits",
      "Rungs/steps tight, not bent or missing",
      "Feet/shoes present, not worn through",
      "Spreader bars lock fully (stepladder)",
      "Rope and pulley intact (extension)",
      "Rung locks engage properly (extension)",
      "Labels and duty rating legible",
      "No paint or buildup hiding damage",
      "Clean of grease, mud, or ice",
    ],
  },
  {
    name: "Power Tools", mode: "condition",
    points: [
      "Cord and plug — no cuts, exposed conductors, or missing ground pin",
      "Housing not cracked; no missing screws",
      "Guards present and functioning",
      "Trigger/switch operates and returns, no sticking",
      "No excessive play in chuck or blade mount",
      "Blades/bits sharp and correctly seated",
      "Battery and charger in good condition (cordless)",
      "Air fittings and hoses secure (pneumatic)",
    ],
  },
  {
    name: "Extension Cords", mode: "condition",
    points: [
      "Jacket intact — no nicks, cuts, or taped repairs",
      "Plug and receptacle ends not cracked or loose",
      "Ground pin present and straight",
      "No exposed copper anywhere in the run",
      "Correct gauge for the load and length",
      "Rated for outdoor/wet use if used outside",
      "GFCI protection in place",
    ],
  },
  {
    name: "PPE Bin (per truck)", mode: "present",
    points: [
      "Gloves — stocked, correct sizes, no holes or dry rot",
      "Hard hats — stocked, no cracks, suspension intact, within date",
      "Safety glasses — stocked, lenses not scratched through",
      "Face masks — stocked and sealed",
    ],
  },
  {
    name: "Vehicle / Truck", mode: "condition",
    points: [
      "Tires — tread depth and pressure, no visible damage",
      "Lights — headlights, brake, turn signals, hazards",
      "Brakes and parking brake",
      "Fluids — oil, coolant, washer",
      "Mirrors and glass clear, no cracks",
      "Horn works",
      "Load secured, nothing loose in the bed",
      "Registration and insurance current and in vehicle",
    ],
  },
  {
    name: "Fire Extinguisher", mode: "condition",
    points: [
      "Gauge reads in the green (charged)",
      "Pin in place and seal unbroken",
      "Hose/nozzle clear, no cracks",
      "Body not dented or corroded",
      "Inspection tag current",
      "Mounted securely and accessible",
    ],
  },
  {
    name: "First Aid Kit", mode: "present",
    points: [
      "Bandages and gauze — stocked",
      "Antiseptic wipes / solution",
      "Burn gel or burn dressing",
      "Eye wash",
      "Gloves (nitrile)",
      "Trauma shears",
      "Tape",
      "Nothing expired",
      "Kit sealed and accessible",
    ],
  },
  {
    name: "Electrical Test Equipment", mode: "present",
    points: [
      "Multimeter",
      "Clamp meter",
      "Non-contact voltage tester",
      "Insulated test leads — no cracked insulation",
      "Receptacle tester",
      "Insulation resistance tester (megger)",
      "Irradiance meter",
      "Torque screwdriver / wrench",
      "Lockout-tagout kit",
      "Spare batteries",
      "Meters within calibration date",
    ],
  },
  { name: "Other", mode: "condition", points: [] },
];

// Look up a type by name, tolerating the older plain-string values that
// existing saved inspections may still carry.
function inspectionTypeDef(name) {
  return INSPECTION_TYPES.find(t => t.name === name)
    || { name: name || "Other", mode: "condition", points: [] };
}

const INSPECTION_RESULTS = [
  { id: "pass", label: "Pass", color: "success" },
  { id: "pass_notes", label: "Pass w/ Notes", color: "warning" },
  { id: "fail", label: "Fail — Remove from Service", color: "danger" },
];

function oshaReportingRequirement(typeId) {
  if (typeId === "fatality") {
    return { urgent: true, window: "8 hours", text: "Fatality — must be reported to OSHA within 8 hours." };
  }
  if (typeId === "hospitalization" || typeId === "amputation" || typeId === "eye_loss") {
    return { urgent: true, window: "24 hours", text: "Must be reported to OSHA within 24 hours." };
  }
  return null;
}

// Printable hard copy of a safety record, including its full edit history.
// Safety records are the one place where "what did this say originally" can
// matter later, so the history prints with the record rather than being
// hidden in the app.
function generateSafetyPDF(kind, rec) {
  const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const d = rec.details || {};
  const fmtDateTime = (iso) => { try { return new Date(iso).toLocaleString(); } catch(e) { return iso || ""; } };

  let title, rows, bodyBlocks = "";

  if (kind === "incident") {
    const type = INCIDENT_TYPES.find(it => it.id === rec.type);
    const osha = oshaReportingRequirement(rec.type);
    title = "Incident Report";
    rows = [
      ["Date", rec.date], ["Time", d.time], ["Incident Type", type?.label || rec.type],
      ["Person Involved", d.person], ["Job / Site", d.site],
      ["Status", rec.status === "open" ? "Open" : "Closed"],
      ["Reported to OSHA", d.oshaReported === "yes" ? "Yes" : d.oshaReported === "pending" ? "REQUIRED — NOT YET REPORTED" : "Not required"],
      ["Recorded By", rec.created_by], ["Recorded At", fmtDateTime(rec.created_at)],
    ];
    if (osha) {
      bodyBlocks += `<div style="border:2px solid #c00;background:#fff3f3;padding:12px;margin:14px 0;border-radius:6px">
        <div style="font-weight:bold;color:#c00;font-size:13px">OSHA REPORTING REQUIRED — ${esc(osha.window)}</div>
        <div style="font-size:11px;margin-top:4px">${esc(osha.text)} Applies to all employers regardless of size. OSHA: 1-800-321-6742.</div>
      </div>`;
    }
    [["What Happened", d.description], ["Injury / Damage", d.injury],
     ["Immediate Action Taken", d.immediate], ["Root Cause", d.rootCause]].forEach(([h, v]) => {
      if (v) bodyBlocks += `<h2>${esc(h)}</h2><div class="notes">${esc(v)}</div>`;
    });
  } else if (kind === "inspection") {
    const def = inspectionTypeDef(rec.type);
    const res = INSPECTION_RESULTS.find(r => r.id === rec.result);
    title = "Equipment Inspection";
    rows = [
      ["Date", rec.date], ["Equipment Type", rec.type], ["Item / ID", d.item],
      ["Result", res?.label || rec.result], ["Inspector", d.inspector],
      ["Recorded At", fmtDateTime(rec.created_at)],
    ];
    const pts = d.points || {};
    if (def.points.length > 0) {
      const isPresent = def.mode === "present";
      bodyBlocks += `<h2>${isPresent ? "Stocked Check" : "Inspection Points"}</h2>
        <table style="width:100%;border-collapse:collapse;font-size:11px">
        ${def.points.map(pt => {
          const v = pts[pt];
          const mark = v === "ok" ? (isPresent ? "IN TRUCK" : "OK") : v === "bad" ? (isPresent ? "MISSING" : "PROBLEM") : "—";
          const color = v === "bad" ? "#c00" : v === "ok" ? "#0a7" : "#999";
          return `<tr>
            <td style="border-bottom:1px solid #eee;padding:5px 0">${esc(pt)}</td>
            <td style="border-bottom:1px solid #eee;padding:5px 0;text-align:right;font-weight:bold;color:${color};white-space:nowrap">${mark}</td>
          </tr>`;
        }).join("")}
        </table>`;
    }
    if (d.notes) bodyBlocks += `<h2>Findings / Notes</h2><div class="notes">${esc(d.notes)}</div>`;
  } else {
    title = "Corrective Action";
    rows = [
      ["Source", d.source], ["Assigned To", d.assigned], ["Due Date", rec.due],
      ["Priority", rec.priority], ["Status", rec.status],
      ["Recorded At", fmtDateTime(rec.created_at)],
    ];
    if (d.what) bodyBlocks += `<h2>Action Required</h2><div class="notes">${esc(d.what)}</div>`;
    if (d.verification) bodyBlocks += `<h2>Verification / Close-out</h2><div class="notes">${esc(d.verification)}</div>`;
  }

  const photoUrls = (rec.photo_urls || []).filter(u => u && !u.startsWith("data:"));
  if (photoUrls.length > 0) {
    bodyBlocks += '<h2>Photos</h2><div style="display:grid;grid-template-columns:repeat(2,1fr);gap:14px;margin:10px 0">' +
      photoUrls.map(u => '<div style="border:1px solid #eee;border-radius:8px;overflow:hidden;break-inside:avoid;page-break-inside:avoid">' +
        '<a href="' + esc(u) + '" target="_blank" rel="noopener" title="Open full size"><img src="' + esc(u) + '" style="width:100%;height:260px;object-fit:contain;background:#fafafa;display:block" onerror="this.style.display=\'none\'" /></a>' +
        '</div>').join("") + '</div>';
  }

  const history = d.history || [];
  let historyBlock = "";
  if (history.length > 0) {
    historyBlock = `<h2>Edit History</h2>
      <table style="width:100%;border-collapse:collapse;font-size:10.5px">
        <tr style="background:#f4f4f4">
          <th style="text-align:left;padding:5px;border:1px solid #ddd">When</th>
          <th style="text-align:left;padding:5px;border:1px solid #ddd">Who</th>
          <th style="text-align:left;padding:5px;border:1px solid #ddd">Changed</th>
        </tr>
        ${history.map(h => `<tr>
          <td style="padding:5px;border:1px solid #ddd;white-space:nowrap">${esc(fmtDateTime(h.at))}</td>
          <td style="padding:5px;border:1px solid #ddd">${esc(h.by)}</td>
          <td style="padding:5px;border:1px solid #ddd">${(h.changes || []).map(ch => esc(ch)).join("<br>")}</td>
        </tr>`).join("")}
      </table>`;
  }

  const printContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(title)} — SolarAbility</title>
  <style>
    body { font-family: Helvetica, Arial, sans-serif; color:#000; padding:36px; font-size:12px; }
    h1 { font-size:18px; margin:0; }
    h2 { font-size:12px; text-transform:uppercase; letter-spacing:.08em; border-bottom:1px solid #bbb; padding-bottom:4px; margin:18px 0 8px; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #2A5C38; padding-bottom:8px; margin-bottom:4px; }
    .field-grid { display:grid; grid-template-columns:1fr 1fr; gap:6px 18px; margin:10px 0; }
    .field label { font-size:9px; text-transform:uppercase; letter-spacing:.08em; color:#666; display:block; }
    .field p { margin:1px 0 0; font-size:12px; }
    .notes { background:#f7f7f7; border-left:3px solid #B4F53C; padding:9px 11px; margin:4px 0; white-space:pre-wrap; }
    .foot { margin-top:24px; border-top:1px solid #ccc; padding-top:7px; font-size:9px; color:#555; display:flex; justify-content:space-between; }
    @media print { body { padding:20px; } }
  </style></head><body>
    <div class="hdr">
      <div><h1>SolarAbility LLC</h1></div>
      <div style="text-align:right"><div style="font-weight:bold;font-size:13px">${esc(title)}</div></div>
    </div>
    <div class="field-grid">
      ${rows.filter(([, v]) => v).map(([k, v]) => `<div class="field"><label>${esc(k)}</label><p>${esc(v)}</p></div>`).join("")}
    </div>
    ${bodyBlocks}
    ${historyBlock}
    <div class="foot">
      <span>SolarAbility LLC — NABCEP Certified — ICC #21-0505</span>
      <span>Printed ${new Date().toLocaleString()}</span>
    </div>
  </body></html>`;

  printHTML(printContent);
}

const CREDENTIAL_TYPES = [
  "OSHA 30", "OSHA 10", "NABCEP", "ICC License", "Electrical License",
  "First Aid / CPR", "Fall Protection", "Driver's License", "Other",
];

// Days until a date string (YYYY-MM-DD); null when unset
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  return Math.round((new Date(dateStr + "T00:00:00") - today) / 86400000);
}

// Expiry banding — matches the 60-day warning window in the safety schedule
function credentialStatus(dateStr) {
  const d = daysUntil(dateStr);
  if (d === null) return { key: "none", label: "No expiry", color: C.muted };
  if (d < 0)      return { key: "expired",  label: `Expired ${Math.abs(d)}d ago`, color: C.danger };
  if (d <= 30)    return { key: "critical", label: `${d}d left`, color: C.danger };
  if (d <= 60)    return { key: "warning",  label: `${d}d left`, color: C.warning };
  return { key: "valid", label: `${d}d left`, color: C.success };
}

function SafetyCenter({ user, t, isAdmin }) {
  const [tab, setTab] = useState("incidents");
  const [incidents, setIncidents] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [actions, setActions] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [jhas, setJhas] = useState([]);
  const [jhaDetail, setJhaDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // { kind, record } or null
  const [form, setForm] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [inc, insp, act, cred] = await Promise.all([
        db.getSafetyRecords("safety_incidents"),
        db.getSafetyRecords("safety_inspections"),
        db.getSafetyRecords("safety_actions"),
        db.getSafetyRecords("safety_credentials"),
      ]);
      setIncidents(inc); setInspections(insp); setActions(act); setCredentials(cred);
      // JHAs live in their own table — the safety officer reviews them here
      if (USE_SUPABASE && supabase) {
        try {
          const { data } = await supabase.from("jha_submissions")
            .select("*").order("created_at", { ascending: false }).limit(100);
          setJhas((data || []).map(j => ({
            ...j, type: "JHA", submittedAt: j.created_at, submittedBy: j.supervisor,
            ...(() => {
              const raw = typeof j.hazards === "string" ? JSON.parse(j.hazards) : (j.hazards || []);
              // Newer JHAs nest hazards under .items with emergency info alongside
              return Array.isArray(raw)
                ? { hazards: raw, emergency: null }
                : { hazards: raw.items || [], emergency: raw.emergency || null,
                    siteHazards: raw.siteHazards || [], crewAck: raw.crewAck || [], waterLog: raw.waterLog || [],
                waterLogClosedAt: raw.waterLogClosedAt || "", heatWatch: !!raw.heatWatch, visitId: raw.visitId || "" };
            })(),
            photo_urls: typeof j.photo_urls === "string" ? JSON.parse(j.photo_urls) : (j.photo_urls || []),
          })));
        } catch(e) { console.warn("JHA load failed:", e); }
      }
    } catch(e) { console.warn(e); }
    setLoading(false);
  }, []);
  const loadedRef = useRef(false);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const d = (k, v) => setForm(p => ({ ...p, details: { ...(p.details || {}), [k]: v } }));

  // PhotoUploader works on a plain array, so bridge it to form.photo_urls
  const [photos, setPhotos] = useState([]);
  const photosToUrls = (list) => list.map(p => p.url).filter(u => u && !u.startsWith("data:"));

  const openNew = (kind) => {
    const base = { id: `${kind}-${Date.now()}`, created_at: new Date().toISOString(), created_by: user?.name || "", details: {} };
    setPhotos([]);
    if (kind === "incident") setForm({ ...base, date: localDate(), type: "near_miss", status: "open" });
    if (kind === "inspection") setForm({ ...base, date: localDate(), type: INSPECTION_TYPES[0].name, result: "pass", details: { inspector: user?.name || "" } });
    if (kind === "action") setForm({ ...base, status: "open", priority: "medium", due: "" });
    if (kind === "credential") setForm({ ...base, date: "", type: CREDENTIAL_TYPES[0], status: "active" });
    setEditing({ kind, record: null });
  };
  const openEdit = (kind, record) => {
    setForm({ ...record, details: record.details || {} });
    setPhotos((record.photo_urls || []).map((u, i) => ({ id: `ex-${i}`, name: u.split("/").pop(), url: u })));
    setEditing({ kind, record });
  };

  const tableFor = (kind) => kind === "incident" ? "safety_incidents" : kind === "inspection" ? "safety_inspections" : kind === "credential" ? "safety_credentials" : "safety_actions";

  const save = async () => {
    const kind = editing.kind;
    if (kind === "incident" && !form.details?.description) return alert("Describe what happened.");
    if (kind === "inspection" && !form.details?.item) return alert("Enter which item was inspected.");
    if (kind === "action" && !form.details?.what) return alert("Describe the corrective action.");
    if (photos.some(p => p.uploading)) return alert("Please wait — photos are still uploading.");

    // Safety records keep an edit trail. On an edit, diff against the original
    // and append a history entry naming the fields that changed — enough to
    // answer "what did this say before" without storing full snapshots.
    let toSave = form;
    if (editing.record) {
      const before = editing.record;
      const bd = before.details || {}, fd = form.details || {};
      const changed = [];
      const labelFor = {
        description: "What happened", injury: "Injury/damage", immediate: "Immediate action",
        rootCause: "Root cause", person: "Person involved", site: "Job/site", time: "Time",
        oshaReported: "OSHA reported", item: "Item/ID", inspector: "Inspector", notes: "Findings",
        what: "Action required", source: "Source", assigned: "Assigned to", verification: "Verification",
      };
      const topLabel = {
        date: "Date", type: "Type", status: "Status", result: "Result",
        due: "Due date", priority: "Priority",
      };
      Object.keys(topLabel).forEach(k => {
        if ((before[k] || "") !== (form[k] || "")) {
          changed.push(`${topLabel[k]}: "${before[k] || "—"}" → "${form[k] || "—"}"`);
        }
      });

      // Short text fields record their actual before/after. Long narrative
      // fields would bloat the record, so those log old/new length instead —
      // enough to show something substantive changed and roughly how much.
      const LONG_FIELDS = ["description", "injury", "immediate", "rootCause", "notes", "what", "verification"];
      Object.keys(labelFor).forEach(k => {
        const bv = (bd[k] || "").toString(), fv = (fd[k] || "").toString();
        if (bv === fv) return;
        if (LONG_FIELDS.includes(k) && (bv.length > 60 || fv.length > 60)) {
          changed.push(`${labelFor[k]}: rewritten (${bv.length} → ${fv.length} chars)`);
        } else {
          changed.push(`${labelFor[k]}: "${bv || "—"}" → "${fv || "—"}"`);
        }
      });

      // Inspection checkpoints: name exactly which point changed and how.
      // "Harness webbing: OK → Problem" is the kind of thing that matters if
      // an inspection is ever questioned.
      const beforeCount = (before.photo_urls || []).length;
      const afterCount = photosToUrls(photos).length;
      if (beforeCount !== afterCount) changed.push(`Photos: ${beforeCount} → ${afterCount}`);

      const bPts = bd.points || {}, fPts = fd.points || {};
      const readable = (v) => v === "ok" ? "OK" : v === "bad" ? "Problem" : "unanswered";
      const allPointKeys = [...new Set([...Object.keys(bPts), ...Object.keys(fPts)])];
      allPointKeys.forEach(pt => {
        const bv = bPts[pt] || "", fv = fPts[pt] || "";
        if (bv !== fv) {
          const short = pt.length > 55 ? pt.slice(0, 52) + "..." : pt;
          changed.push(`${short}: ${readable(bv)} → ${readable(fv)}`);
        }
      });

      if (changed.length > 0) {
        const history = [...(bd.history || []), { at: new Date().toISOString(), by: user?.name || "Unknown", changes: changed }];
        toSave = { ...form, details: { ...fd, history } };
      }
    }

    toSave = { ...toSave, photo_urls: photosToUrls(photos) };
    const ok = await db.saveSafetyRecord(tableFor(kind), toSave);
    if (!ok) return alert("Failed to save — check your connection.");
    await load();
    setEditing(null);
  };
  const remove = async () => {
    if (!window.confirm("Delete this record? Safety records are usually kept for reference.")) return;
    await db.deleteSafetyRecord(tableFor(editing.kind), form.id);
    await load();
    setEditing(null);
  };

  const sevColor = (sev) => sev === "critical" ? C.danger : sev === "high" ? C.danger : sev === "med" ? C.warning : C.muted;

  // ── Editors ───────────────────────────────────────────────────────────────
  if (editing) {
    const kind = editing.kind;
    const osha = kind === "incident" ? oshaReportingRequirement(form.type) : null;
    return (
      <div style={{ maxWidth: 640, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => setEditing(null)} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: "0 0 20px" }}>
          {kind === "incident" ? t.reportIncident : kind === "inspection" ? t.newInspection
            : kind === "credential" ? "Credential" : t.newAction}
        </h2>

        {kind === "incident" && (
          <>
            {osha && (
              <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, borderRadius: 12, padding: 16, marginBottom: 16 }}>
                <div style={{ color: C.danger, fontWeight: 800, fontSize: 14, marginBottom: 6 }}>⚠ OSHA REPORTING REQUIRED — {osha.window}</div>
                <div style={{ color: C.light, fontSize: 13, lineHeight: 1.5 }}>
                  {osha.text} This applies to every employer regardless of size.
                  Report by phone to <strong>1-800-321-6742</strong>, your local OSHA area office,
                  or OSHA's online Serious Event Reporting form. Logging it here does not satisfy that requirement.
                </div>
              </div>
            )}
            <div style={S.card}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Date of Incident" required><input style={S.input} type="date" value={form.date || ""} onChange={e => f("date", e.target.value)} /></Field>
                <Field label="Time"><input style={S.input} type="time" value={form.details?.time || ""} onChange={e => d("time", e.target.value)} /></Field>
              </div>
              <Field label="Incident Type" required>
                <select style={S.select} value={form.type || "near_miss"} onChange={e => f("type", e.target.value)}>
                  {INCIDENT_TYPES.map(it => <option key={it.id} value={it.id}>{it.label}</option>)}
                </select>
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Person Involved"><input style={S.input} value={form.details?.person || ""} onChange={e => d("person", e.target.value)} placeholder="Name" /></Field>
                <Field label="Job / Site"><input style={S.input} value={form.details?.site || ""} onChange={e => d("site", e.target.value)} placeholder="Address or project" /></Field>
              </div>
              <Field label="What Happened" required>
                <textarea style={S.textarea} rows={4} value={form.details?.description || ""} onChange={e => d("description", e.target.value)}
                  placeholder="Describe the sequence of events plainly — what was being done, what went wrong, what the outcome was." />
              </Field>
              <Field label="Injury / Damage Detail">
                <textarea style={S.textarea} rows={2} value={form.details?.injury || ""} onChange={e => d("injury", e.target.value)}
                  placeholder="Body part, nature of injury, or property damaged" />
              </Field>
              <Field label="Immediate Action Taken">
                <textarea style={S.textarea} rows={2} value={form.details?.immediate || ""} onChange={e => d("immediate", e.target.value)}
                  placeholder="First aid given, area secured, equipment tagged out..." />
              </Field>
              <Field label="Root Cause">
                <textarea style={S.textarea} rows={2} value={form.details?.rootCause || ""} onChange={e => d("rootCause", e.target.value)}
                  placeholder="Why did this happen — not who is at fault" />
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Status">
                  <select style={S.select} value={form.status || "open"} onChange={e => f("status", e.target.value)}>
                    <option value="open">Open — under review</option>
                    <option value="closed">Closed</option>
                  </select>
                </Field>
                <Field label="Reported to OSHA?">
                  <select style={S.select} value={form.details?.oshaReported || "n/a"} onChange={e => d("oshaReported", e.target.value)}>
                    <option value="n/a">Not required</option>
                    <option value="yes">Yes — reported</option>
                    <option value="pending">Required — NOT yet reported</option>
                  </select>
                </Field>
              </div>
            </div>
          </>
        )}

        {kind === "credential" && (
          <div style={S.card}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Employee" required>
                <input style={S.input} value={form.details?.employee || ""} onChange={e => d("employee", e.target.value)} placeholder="Name" />
              </Field>
              <Field label="Credential Type" required>
                <select style={S.select} value={form.type || CREDENTIAL_TYPES[0]} onChange={e => f("type", e.target.value)}>
                  {CREDENTIAL_TYPES.map(ct => <option key={ct} value={ct}>{ct}</option>)}
                </select>
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Card / Certificate Number">
                <input style={S.input} value={form.details?.number || ""} onChange={e => d("number", e.target.value)} />
              </Field>
              <Field label="Issuing Body / Provider">
                <input style={S.input} value={form.details?.issuer || ""} onChange={e => d("issuer", e.target.value)} placeholder="Training provider or authority" />
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Issue Date">
                <input style={S.input} type="date" value={form.details?.issueDate || ""} onChange={e => d("issueDate", e.target.value)} />
              </Field>
              <Field label="Expiry Date" required>
                <input style={S.input} type="date" value={form.date || ""} onChange={e => f("date", e.target.value)} />
              </Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Renewal Cost ($)">
                <input style={S.input} type="number" step="0.01" value={form.details?.cost || ""} onChange={e => d("cost", e.target.value)} />
              </Field>
              <Field label="Paid By">
                <select style={S.select} value={form.details?.paidBy || ""} onChange={e => d("paidBy", e.target.value)}>
                  <option value="">—</option>
                  <option value="company">Company</option>
                  <option value="employee">Employee</option>
                  <option value="reimbursed">Employee — reimbursed</option>
                </select>
              </Field>
            </div>
            <Field label="Status">
              <select style={S.select} value={form.status || "active"} onChange={e => f("status", e.target.value)}>
                <option value="active">Active</option>
                <option value="superseded">Superseded — renewed under a new record</option>
              </select>
            </Field>
            <Field label="Notes">
              <textarea style={S.textarea} rows={2} value={form.details?.notes || ""} onChange={e => d("notes", e.target.value)}
                placeholder="Renewal requirements, continuing education hours, anything to remember at renewal time" />
            </Field>
            <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
              Attach the card or certificate below — photo or scan. That image is what a client asks for during prequalification.
            </div>
          </div>
        )}

        {kind === "inspection" && (
          <div style={S.card}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Date" required><input style={S.input} type="date" value={form.date || ""} onChange={e => f("date", e.target.value)} /></Field>
              <Field label="Equipment Type" required>
                <select style={S.select} value={form.type || INSPECTION_TYPES[0].name}
                  onChange={e => setForm(p => ({ ...p, type: e.target.value, details: { ...(p.details || {}), points: {} } }))}>
                  {INSPECTION_TYPES.map(it => <option key={it.name} value={it.name}>{it.name}</option>)}
                </select>
              </Field>
            </div>
            <Field label="Item / ID" required>
              <input style={S.input} value={form.details?.item || ""} onChange={e => d("item", e.target.value)} placeholder="e.g. Harness #3, 24ft ladder, Truck 2" />
            </Field>
            <Field label="Result" required>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {INSPECTION_RESULTS.map(r => (
                  <button key={r.id} type="button" onClick={() => f("result", r.id)} style={{
                    padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                    border: `1px solid ${form.result === r.id ? C[r.color] : C.border}`,
                    background: form.result === r.id ? `${C[r.color]}22` : "transparent",
                    color: form.result === r.id ? C[r.color] : C.muted,
                  }}>{r.label}</button>
                ))}
              </div>
            </Field>
            {(() => {
              const def = inspectionTypeDef(form.type);
              if (def.points.length === 0) return null;
              const answers = form.details?.points || {};
              const setPoint = (pt, val) => setForm(p => ({
                ...p,
                details: { ...(p.details || {}), points: { ...((p.details || {}).points || {}), [pt]: val } },
              }));
              const isPresentMode = def.mode === "present";
              const okLabel = isPresentMode ? "In truck" : "OK";
              const badLabel = isPresentMode ? "Missing" : "Problem";
              const checked = def.points.filter(pt => answers[pt] === "ok").length;
              const flagged = def.points.filter(pt => answers[pt] === "bad").length;
              return (
                <Field label={isPresentMode ? `Confirm Stocked (${checked}/${def.points.length})` : `Inspection Points (${checked}/${def.points.length})`}>
                  <div style={{ fontSize: 12, color: `${C.muted}aa`, marginBottom: 10, lineHeight: 1.5, fontStyle: "italic" }}>
                    {isPresentMode
                      ? "Confirm each item is present and usable. Mark anything missing or expired."
                      : "Check each point. Anything marked Problem should push the result to Fail."}
                  </div>
                  <div style={{ border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
                    {def.points.map((pt, i) => {
                      const val = answers[pt];
                      return (
                        <div key={pt} style={{
                          display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                          borderBottom: i === def.points.length - 1 ? "none" : `1px solid ${C.border}`,
                          background: val === "bad" ? `${C.danger}12` : "transparent",
                        }}>
                          <span style={{ flex: 1, fontSize: 13, color: C.light, lineHeight: 1.4 }}>{pt}</span>
                          <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                            <button type="button" onClick={() => setPoint(pt, val === "ok" ? "" : "ok")} style={{
                              padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                              border: `1px solid ${val === "ok" ? C.success : C.border}`,
                              background: val === "ok" ? `${C.success}22` : "transparent",
                              color: val === "ok" ? C.success : C.muted,
                            }}>{okLabel}</button>
                            <button type="button" onClick={() => setPoint(pt, val === "bad" ? "" : "bad")} style={{
                              padding: "6px 12px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit",
                              border: `1px solid ${val === "bad" ? C.danger : C.border}`,
                              background: val === "bad" ? `${C.danger}22` : "transparent",
                              color: val === "bad" ? C.danger : C.muted,
                            }}>{badLabel}</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {flagged > 0 && form.result !== "fail" && (
                    <div style={{ marginTop: 10, background: `${C.warning}18`, border: `1px solid ${C.warning}`, borderRadius: 8, padding: 10, fontSize: 12, color: C.light }}>
                      {flagged} point{flagged === 1 ? "" : "s"} flagged but the result above isn't set to Fail. Set it to Fail if this item shouldn't be used, or note why it still passes.
                    </div>
                  )}
                </Field>
              );
            })()}
            <Field label="Inspector"><input style={S.input} value={form.details?.inspector ?? ""} onChange={e => d("inspector", e.target.value)} placeholder="Who performed this inspection" /></Field>
            <Field label="Findings / Notes">
              <textarea style={S.textarea} rows={3} value={form.details?.notes || ""} onChange={e => d("notes", e.target.value)}
                placeholder="Wear, damage, missing parts, next inspection due..." />
            </Field>
            {form.result === "fail" && (
              <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, borderRadius: 10, padding: 12, fontSize: 13, color: C.light }}>
                Tag this item out of service now. Consider adding a Corrective Action to track the repair or replacement.
              </div>
            )}
          </div>
        )}

        {kind === "action" && (
          <div style={S.card}>
            <Field label="Corrective Action" required>
              <textarea style={S.textarea} rows={3} value={form.details?.what || ""} onChange={e => d("what", e.target.value)}
                placeholder="What needs to change to prevent this from happening again" />
            </Field>
            <Field label="Source">
              <input style={S.input} value={form.details?.source || ""} onChange={e => d("source", e.target.value)}
                placeholder="e.g. Incident 3/14, Harness #3 inspection, JHA review" />
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Field label="Assigned To"><input style={S.input} value={form.details?.assigned || ""} onChange={e => d("assigned", e.target.value)} /></Field>
              <Field label="Due Date"><input style={S.input} type="date" value={form.due || ""} onChange={e => f("due", e.target.value)} /></Field>
              <Field label="Priority">
                <select style={S.select} value={form.priority || "medium"} onChange={e => f("priority", e.target.value)}>
                  <option value="low">Low</option><option value="medium">Medium</option>
                  <option value="high">High</option><option value="critical">Critical</option>
                </select>
              </Field>
              <Field label="Status">
                <select style={S.select} value={form.status || "open"} onChange={e => f("status", e.target.value)}>
                  <option value="open">Open</option>
                  <option value="in_progress">In Progress</option>
                  <option value="pending_submission">Pending Submission</option>
                  <option value="done">Complete</option>
                </select>
              </Field>
            </div>
            <Field label="Verification / Close-out Notes">
              <textarea style={S.textarea} rows={2} value={form.details?.verification || ""} onChange={e => d("verification", e.target.value)}
                placeholder="How was it confirmed this actually got fixed" />
            </Field>
          </div>
        )}

        {/* Photos — one uploader shared by all three record types */}
        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.light }}>Photos</h3>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
            {kind === "incident" && "The scene, the equipment involved, and any injury or damage — photographed before anything is moved or cleaned up, where that's safe to do."}
            {kind === "inspection" && "Any wear, damage, or missing parts you flagged above. A dated photo of a failed harness or ladder is worth more than a description."}
            {kind === "action" && "Before and after — showing the problem and showing it fixed."}
          </p>
          <PhotoUploader photos={photos} setPhotos={setPhotos} label="Add photos" />
        </div>

        {/* Edit history — visible in-app, and prints with the record */}
        {editing.record && (form.details?.history || []).length > 0 && (
          <div style={S.card}>
            <div style={S.label}>Edit History</div>
            {(form.details.history || []).slice().reverse().map((h, i) => (
              <div key={i} style={{ padding: "8px 0", borderBottom: i < form.details.history.length - 1 ? `1px solid ${C.border}33` : "none" }}>
                <div style={{ fontSize: 11, color: C.muted }}>
                  {new Date(h.at).toLocaleString()} · {h.by}
                </div>
                <div style={{ marginTop: 5 }}>
                  {(h.changes || []).map((ch, j) => (
                    <div key={j} style={{ fontSize: 12, color: C.light, lineHeight: 1.6, paddingLeft: 10, borderLeft: `2px solid ${C.border}`, marginBottom: 3 }}>
                      {ch}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, marginBottom: 40, flexWrap: "wrap" }}>
          <button onClick={save} style={{ ...S.btnP, flex: 2, justifyContent: "center", minWidth: 140 }}>{I.check} Save</button>
          {editing.record && (
            <button onClick={() => generateSafetyPDF(editing.kind, form)} style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 130 }}>
              {I.pdf} Print / Save PDF
            </button>
          )}
          {editing.record && isAdmin && <button onClick={remove} style={S.btnDanger}>{I.trash}</button>}
        </div>
      </div>
    );
  }

  // ── Lists ─────────────────────────────────────────────────────────────────
  const openIncidents = incidents.filter(i => i.status === "open").length;
  const failedInspections = inspections.filter(i => i.result === "fail").length;
  const openActions = actions.filter(a => a.status !== "done").length;
  const overdueActions = actions.filter(a => a.status !== "done" && a.due && new Date(a.due) < new Date()).length;
  const oshaPending = incidents.filter(i => i.details?.oshaReported === "pending").length;
  const activeCreds = credentials.filter(cr => cr.status !== "superseded");
  const credExpired = activeCreds.filter(cr => credentialStatus(cr.date).key === "expired").length;
  const credSoon = activeCreds.filter(cr => ["critical", "warning"].includes(credentialStatus(cr.date).key)).length;

  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{t.safety}</h2>
        <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13 }}>Incidents, equipment inspections, corrective actions, and credentials</p>
      </div>

      {oshaPending > 0 && (
        <div style={{ background: `${C.danger}18`, border: `1px solid ${C.danger}`, borderRadius: 12, padding: 14, marginBottom: 16 }}>
          <div style={{ color: C.danger, fontWeight: 800, fontSize: 14 }}>⚠ {oshaPending} incident{oshaPending === 1 ? "" : "s"} flagged as requiring OSHA reporting, not yet reported</div>
          <div style={{ color: C.light, fontSize: 12, marginTop: 4 }}>OSHA hotline: 1-800-321-6742</div>
        </div>
      )}

      {/* Rollup */}
      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { label: "New JHAs", val: jhas.filter(j => j.status !== "approved").length, color: jhas.filter(j => j.status !== "approved").length ? C.warning : C.success },
          { label: "Open Incidents", val: openIncidents, color: openIncidents ? C.warning : C.success },
          { label: "Failed Equipment", val: failedInspections, color: failedInspections ? C.danger : C.success },
          { label: "Open Actions", val: openActions, color: openActions ? C.info : C.success },
          { label: "Overdue Actions", val: overdueActions, color: overdueActions ? C.danger : C.success },
          { label: "Credentials Expiring", val: credExpired + credSoon, color: credExpired ? C.danger : credSoon ? C.warning : C.success },
        ].map(s => (
          <div key={s.label} style={{ ...S.card, margin: 0, flex: 1, minWidth: 130, padding: "14px 18px" }}>
            <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{s.label}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: s.color, marginTop: 4 }}>{s.val}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { id: "jhas", label: `JHAs${jhas.filter(j => j.status !== "approved").length > 0 ? ` (${jhas.filter(j => j.status !== "approved").length})` : ""}` },
          { id: "incidents", label: t.incidents }, { id: "inspections", label: t.inspections }, { id: "actions", label: t.correctiveActions },
          { id: "credentials", label: `Credentials${credExpired + credSoon > 0 ? ` (${credExpired + credSoon})` : ""}` },
        ].map(tb => (
          <button key={tb.id} onClick={() => setTab(tb.id)} style={{
            padding: "8px 18px", borderRadius: 20, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
            border: `1px solid ${tab === tb.id ? C.gold : C.border}`,
            background: tab === tb.id ? `${C.gold}22` : "transparent",
            color: tab === tb.id ? C.gold : C.muted,
          }}>{tb.label}</button>
        ))}
        <div style={{ flex: 1 }} />
        {tab !== "jhas" && (
          <button style={S.btnP} onClick={() => openNew(tab === "incidents" ? "incident" : tab === "inspections" ? "inspection" : tab === "credentials" ? "credential" : "action")}>
            {I.plus} {tab === "incidents" ? t.reportIncident : tab === "inspections" ? t.newInspection : tab === "credentials" ? "Add Credential" : t.newAction}
          </button>
        )}
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading...</div> : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {tab === "jhas" && (jhas.length === 0
            ? <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No JHAs submitted yet. Crew submit these from the New JHA tab before starting work.</div>
            : jhas.map(j => {
              const hazardCount = (j.hazards || []).filter(h => h.selected || h.checked).length || (j.hazards || []).length;
              const approved = j.status === "approved";
              return (
                <div key={j.id} onClick={() => setJhaDetail(j)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${approved ? C.border : `${C.warning}44`}`, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C.warning}22`, display: "flex", alignItems: "center", justifyContent: "center", color: C.warning, flexShrink: 0 }}>{I.hazard}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.title || "Untitled JHA"}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {j.location || "—"} · {j.supervisor || "—"} · {j.date || (j.created_at ? new Date(j.created_at).toLocaleDateString() : "")}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {(j.photo_urls || []).length > 0 && <span style={{ fontSize: 11, color: C.muted }}>📷 {j.photo_urls.length}</span>}
                    <span style={S.badge(approved ? C.success : C.warning)}>{approved ? "REVIEWED" : "NEW"}</span>
                  </div>
                </div>
              );
            }))}

          {tab === "incidents" && (incidents.length === 0
            ? <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No incidents logged. Near misses are worth logging too — they're the cheapest lesson you'll get.</div>
            : incidents.map(inc => {
              const type = INCIDENT_TYPES.find(it => it.id === inc.type);
              return (
                <div key={inc.id} onClick={() => openEdit("incident", inc)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${type?.severity === "high" || type?.severity === "critical" ? `${C.danger}66` : C.border}`, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${sevColor(type?.severity)}22`, display: "flex", alignItems: "center", justifyContent: "center", color: sevColor(type?.severity), flexShrink: 0 }}>{I.hazard}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.light }}>{type?.label || inc.type}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {inc.date} · {inc.details?.person || "—"} · {inc.details?.site || "—"}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {(inc.photo_urls || []).length > 0 && <span style={{ fontSize: 11, color: C.muted }}>📷 {inc.photo_urls.length}</span>}
                    {inc.details?.oshaReported === "pending" && <span style={S.badge(C.danger)}>OSHA</span>}
                    <span style={S.badge(inc.status === "open" ? C.warning : C.success)}>{inc.status === "open" ? "OPEN" : "CLOSED"}</span>
                  </div>
                </div>
              );
            }))}

          {tab === "inspections" && (inspections.length === 0
            ? <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No inspections logged yet.</div>
            : inspections.map(insp => {
              const res = INSPECTION_RESULTS.find(r => r.id === insp.result);
              return (
                <div key={insp.id} onClick={() => openEdit("inspection", insp)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${insp.result === "fail" ? `${C.danger}66` : C.border}`, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C[res?.color || "muted"]}22`, display: "flex", alignItems: "center", justifyContent: "center", color: C[res?.color || "muted"], flexShrink: 0 }}>{I.clip}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.light }}>{insp.details?.item || insp.type}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {insp.type} · {insp.date} · {insp.details?.inspector || "—"}
                      {(() => {
                        const pts = insp.details?.points || {};
                        const bad = Object.values(pts).filter(v => v === "bad").length;
                        return bad > 0 ? <span style={{ color: C.danger, fontWeight: 700 }}> · {bad} flagged</span> : null;
                      })()}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {(insp.photo_urls || []).length > 0 && <span style={{ fontSize: 11, color: C.muted }}>📷 {insp.photo_urls.length}</span>}
                    <span style={S.badge(C[res?.color || "muted"])}>{res?.label || insp.result}</span>
                  </div>
                </div>
              );
            }))}

          {tab === "actions" && (actions.length === 0
            ? <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>No corrective actions yet.</div>
            : actions.map(a => {
              const overdue = a.status !== "done" && a.due && new Date(a.due) < new Date();
              return (
                <div key={a.id} onClick={() => openEdit("action", a)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${overdue ? `${C.danger}66` : C.border}`, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${a.status === "done" ? C.success : C.info}22`, display: "flex", alignItems: "center", justifyContent: "center", color: a.status === "done" ? C.success : C.info, flexShrink: 0 }}>{I.check}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.details?.what}</div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {a.details?.assigned || "Unassigned"}{a.due ? ` · due ${a.due}` : ""}{a.details?.source ? ` · from ${a.details.source}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {(a.photo_urls || []).length > 0 && <span style={{ fontSize: 11, color: C.muted }}>📷 {a.photo_urls.length}</span>}
                    {overdue && <span style={S.badge(C.danger)}>OVERDUE</span>}
                    <span style={S.badge(a.status === "done" ? C.success : a.status === "in_progress" ? C.info : C.warning)}>
                      {a.status === "done" ? "DONE" : a.status === "in_progress" ? "IN PROGRESS" : "OPEN"}
                    </span>
                  </div>
                </div>
              );
            }))}

          {tab === "credentials" && (() => {
            const sorted = [...credentials].sort((a, b) => {
              // Superseded records sink; everything else sorts soonest-expiring first
              if ((a.status === "superseded") !== (b.status === "superseded")) return a.status === "superseded" ? 1 : -1;
              if (!a.date) return 1;
              if (!b.date) return -1;
              return a.date.localeCompare(b.date);
            });
            if (sorted.length === 0) return (
              <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
                No credentials tracked yet. Add OSHA 30 cards, NABCEP, ICC, first aid, and fall protection here —
                expiry warnings start 60 days out.
              </div>
            );
            return sorted.map(cr => {
              const st = cr.status === "superseded"
                ? { key: "superseded", label: "SUPERSEDED", color: C.muted }
                : credentialStatus(cr.date);
              const flagged = ["expired", "critical"].includes(st.key);
              return (
                <div key={cr.id} onClick={() => openEdit("credential", cr)} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${flagged ? `${C.danger}66` : C.border}`, cursor: "pointer" }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: `${st.color}22`, display: "flex", alignItems: "center", justifyContent: "center", color: st.color, flexShrink: 0 }}>{I.shield}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {cr.details?.employee || "—"} · {cr.type}
                    </div>
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
                      {cr.details?.number ? `#${cr.details.number}` : "No number"}
                      {cr.date ? ` · expires ${cr.date}` : " · no expiry"}
                      {cr.details?.issuer ? ` · ${cr.details.issuer}` : ""}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0 }}>
                    {(cr.photo_urls || []).length > 0 && <span style={{ fontSize: 11, color: C.muted }}>📷 {cr.photo_urls.length}</span>}
                    <span style={S.badge(st.color)}>{st.label}</span>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      )}

      {jhaDetail && (
        <Modal onClose={() => setJhaDetail(null)}>
          <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: C.light }}>{jhaDetail.title || "JHA"}</h3>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>
            {jhaDetail.location || "—"} · {jhaDetail.date || ""} · Supervisor: {jhaDetail.supervisor || "—"}
          </p>

          {jhaDetail.crew && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Crew</div>
              <div style={{ fontSize: 13, color: C.light }}>{jhaDetail.crew}</div>
            </div>
          )}

          {jhaDetail.description && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Job Description</div>
              <div style={{ fontSize: 13, color: C.light, background: C.surface, borderRadius: 8, padding: 10, lineHeight: 1.5 }}>{jhaDetail.description}</div>
            </div>
          )}

          {jhaDetail.emergency && (jhaDetail.emergency.hospitalName || jhaDetail.emergency.hospitalAddress) && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Nearest Hospital</div>
              <div style={{ fontSize: 13, color: C.light, background: `${C.danger}12`, border: `1px solid ${C.danger}44`, borderRadius: 8, padding: 10, lineHeight: 1.6 }}>
                {jhaDetail.emergency.hospitalName && <div style={{ fontWeight: 700 }}>{jhaDetail.emergency.hospitalName}</div>}
                {jhaDetail.emergency.hospitalAddress && <div>{jhaDetail.emergency.hospitalAddress}</div>}
                {jhaDetail.emergency.hospitalPhone && <div style={{ color: C.muted }}>{jhaDetail.emergency.hospitalPhone}</div>}
              </div>
            </div>
          )}

          {(jhaDetail.siteHazards || []).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Site Conditions</div>
              {jhaDetail.siteHazards.map((h, i) => (
                <div key={i} style={{ fontSize: 12.5, color: C.light, padding: "4px 0", lineHeight: 1.5 }}>
                  • <strong>{h.label}</strong>{h.note ? ` — ${h.note}` : ""}
                </div>
              ))}
            </div>
          )}

          {(jhaDetail.crewAck || []).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Crew Sign-Off ({jhaDetail.crewAck.filter(p => p.at).length}/{jhaDetail.crewAck.length})</div>
              {jhaDetail.crewAck.map((p, i) => (
                <div key={i} style={{ fontSize: 12.5, color: p.at ? C.light : C.warning, padding: "3px 0" }}>
                  {p.name || "—"} {p.initials ? `(${p.initials})` : ""} · {p.at ? fmtJobTime(p.at) : "not signed"}
                </div>
              ))}
            </div>
          )}

          {(jhaDetail.waterLog || []).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>
                Water Breaks ({jhaDetail.waterLog.length}){jhaDetail.waterLogClosedAt ? "" : " — day still open"}
              </div>
              <div style={{ fontSize: 12.5, color: C.light }}>
                {jhaDetail.waterLog.map(w => fmtJobTime(w.at)).join(" · ")}
              </div>
            </div>
          )}

          {jhaDetail.heatWatch && !jhaDetail.waterLogClosedAt && (
            <div style={{ background: `${C.warning}15`, border: `1px solid ${C.warning}44`, borderRadius: 8, padding: 10, fontSize: 12, color: C.light, marginBottom: 12 }}>
              Over-90°F day that was never closed out. The water log may be missing entries — worth asking the crew.
            </div>
          )}

          {(jhaDetail.hazards || []).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Hazards & Controls</div>
              <div style={{ maxHeight: 260, overflowY: "auto" }}>
                {jhaDetail.hazards.map((h, i) => (
                  <div key={i} style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}33`, fontSize: 12 }}>
                    <div style={{ color: C.light, fontWeight: 600 }}>{h.hazard || h.name || h.label || `Hazard ${i + 1}`}</div>
                    {(h.control || h.controls) && <div style={{ color: C.muted, marginTop: 2 }}>{h.control || h.controls}</div>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {(jhaDetail.photo_urls || []).length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Photos ({jhaDetail.photo_urls.length})</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(110px, 1fr))", gap: 8 }}>
                {jhaDetail.photo_urls.map((u, i) => (
                  <img loading="lazy" decoding="async" key={i} src={u} alt="" onClick={() => openPhoto(u, jhaDetail.photo_urls)} style={{ width: "100%", height: 90, objectFit: "cover", borderRadius: 8, border: `1px solid ${C.border}`, cursor: "zoom-in" }}
                    onError={(e) => { e.target.style.display = "none"; }} />
                ))}
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <button onClick={() => generatePDF(jhaDetail)} style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 120 }}>{I.pdf} Export PDF</button>
            {jhaDetail.status !== "approved" && (
              <button onClick={async () => {
                try {
                  await db.updateStatus("jha_submissions", jhaDetail.id, "approved");
                  setJhas(prev => prev.map(j => j.id === jhaDetail.id ? { ...j, status: "approved" } : j));
                  setJhaDetail(null);
                } catch(e) { alert("Failed to update — check your connection."); }
              }} style={{ ...S.btnSuccess, flex: 1, justifyContent: "center", minWidth: 120 }}>{I.checkCircle} Mark Reviewed</button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Schedule ───────────────────────────────────────────────────────────────
// Month view of every scheduled job with the crew assigned to each, so you can
// see real capacity before committing a date in a partner's portal.
function ScheduleView({ user, t, isAdmin, canManage = isAdmin }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cursor, setCursor] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); return d; });
  const [selectedDay, setSelectedDay] = useState(null);
  const [person, setPerson] = useState(""); // "" = everyone — holds a canonical identity key
  const [calToken, setCalToken] = useState(null);
  const [showSync, setShowSync] = useState(false);
  const [jobDetail, setJobDetail] = useState(null);
  const [jobPhotos, setJobPhotos] = useState([]);
  const [jobRefs, setJobRefs] = useState({ playbooks: [], partners: [] });

  const tokenRef = useRef(false);
  if (!tokenRef.current && user?.id) {
    tokenRef.current = true;
    db.getCalendarToken(user.id).then(setCalToken).catch(() => {});
    Promise.all([db.getPlaybooks(), db.getPartners()])
      .then(([pb, pt]) => setJobRefs({ playbooks: pb || [], partners: pt || [] }))
      .catch(() => {});
  }

  const feedUrl = calToken && SUPABASE_URL && !SUPABASE_URL.includes("YOUR_")
    ? `${SUPABASE_URL}/functions/v1/calendar-feed?token=${calToken}`
    : null;

  // Open a job's full briefing, pulling photos from forms filed at the same address
  const [jobProgress, setJobProgress] = useState({ forms: [], runs: [] });
  const [textBusy, setTextBusy] = useState(false);
  const openJob = async (j) => {
    setJobDetail(j);
    setJobPhotos([]);
    setJobProgress({ forms: [], runs: [] });
    // What's already filed for this job — drives the per-visit chips
    Promise.all([db.getProjectForms(j).catch(() => []), db.getProcedureRuns(j.id).catch(() => [])])
      .then(([forms, runs]) => setJobProgress({ forms: forms || [], runs: runs || [] }));
    if (!j.address || !USE_SUPABASE || !supabase) return;
    try {
      const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
      const target = norm(j.address);
      // Only this address's rows (street-level match server side, exact
      // match client side) — no checklist JSON, just the photo list.
      const street = String(j.address || "").split(",")[0].trim();
      const q = supabase.from("survey_submissions").select("id, site_name, address, created_at, photo_urls, project_id").order("created_at", { ascending: false }).limit(40);
      const { data } = street.length > 5 ? await q.or(`project_id.eq.${j.id},address.ilike.*${street.replace(/[,()]/g, " ")}*`) : await q.eq("project_id", j.id);
      const photos = [];
      (data || []).filter(s => s.project_id === j.id || norm(s.address) === target).forEach(s => {
        let urls = s.photo_urls;
        if (typeof urls === "string") { try { urls = JSON.parse(urls); } catch(e) { urls = []; } }
        (urls || []).forEach(u => { if (u && !u.startsWith("data:")) photos.push({ url: u, when: s.created_at }); });
      });
      setJobPhotos(photos);
    } catch(e) { console.warn("Photo lookup failed:", e); }
  };

  // Deep link from the calendar feed: #schedule/<projectId>
  useEffect(() => {
    const h = (window.location.hash || "").replace(/^#/, "");
    const [route, id] = h.split("/");
    if (route !== "schedule" || !id || projects.length === 0) return;
    // A job with several visits has several entries — open the next upcoming one
    const mine = projects.filter(p => p.id === id).sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
    const hit = mine.find(p => new Date(p.scheduled_at) >= new Date(Date.now() - 6 * 3600000)) || mine[mine.length - 1];
    if (hit) openJob(hit);
  }, [projects]);
  const [profilesList, setProfilesList] = useState([]);
  const profRef = useRef(false);
  if (!profRef.current && canManage && USE_SUPABASE && supabase) {
    profRef.current = true;
    supabase.from("profiles").select("email, full_name").then(({ data }) => setProfilesList(data || [])).catch(() => {});
  }

  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await db.getProjects();
      const visible = canManage ? all : all.filter(p => {
        if (p.created_by && user?.name && p.created_by === user.name) return true;
        return (p.members || []).some(m =>
          (m.name && user?.name && m.name.toLowerCase() === user.name.toLowerCase()) ||
          (m.name && user?.email && m.name.toLowerCase() === user.email.toLowerCase())
        );
      });
      // One schedule entry per VISIT, not per job. A removal + reinstall + a
      // return visit are three dates on the calendar even though they are one
      // project. Jobs without a visits list fall back to their single date.
      const entries = [];
      visible.forEach(p => {
        const live = (p.visits || []).filter(v => !v.cancelled && v.scheduled_at);
        if (live.length === 0) { if (p.scheduled_at) entries.push({ ...p, key: p.id }); return; }
        const me = [user?.email, user?.name].filter(Boolean).map(s => s.toLowerCase());
        const owner = (p.clientEmail || "").toLowerCase();
        live.forEach(v => {
          // Once a visit's invite has gone out, it belongs to the people on that
          // invite — a second crew member on the job doesn't get visits they
          // weren't sent. Visits not invited yet still show to everyone on the job.
          const crewInvited = (v.invited || []).filter(e => e && e.toLowerCase() !== owner);
          if (!canManage && crewInvited.length > 0 && !crewInvited.some(e => me.includes(e.toLowerCase()))) return;
          entries.push({ ...p, scheduled_at: v.scheduled_at, visitId: v.id, visitLabel: v.label, visitCrew: crewInvited, key: `${p.id}:${v.id}` });
        });
      });
      setProjects(entries);
    } catch(e) { console.warn(e); }
    setLoading(false);
  }, [canManage, user]);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  const dayKey = (d) => `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;

  // A visit that has been invited shows the people on THAT invite; otherwise the job's crew
  const crewFor = (p) => (p.visitCrew && p.visitCrew.length ? p.visitCrew : (p.members || []).map(m => m.name).filter(Boolean));

  // The same person gets recorded two ways: created_by stores a display name,
  // members store an email (because that's what invites need). Resolve both
  // to one canonical key — the email where we can find it — so one person
  // doesn't show up as two, and filtering by them catches every job.
  const identityOf = useCallback((raw) => {
    const v = String(raw || "").trim();
    if (!v) return null;
    const low = v.toLowerCase();
    const byEmail = profilesList.find(pr => (pr.email || "").toLowerCase() === low);
    if (byEmail) return { key: low, label: byEmail.full_name || byEmail.email };
    const byName = profilesList.find(pr =>
      (pr.full_name || "").toLowerCase() === low ||
      (pr.email || "").toLowerCase().split("@")[0] === low
    );
    if (byName) return { key: byName.email.toLowerCase(), label: byName.full_name || byName.email };
    // Unknown to profiles: if it looks like an email, key on it; otherwise
    // fall back to the email-style local part so "jonathanz" and
    // "jonathanz@..." still collapse when no profile row exists.
    if (low.includes("@")) return { key: low, label: v };
    return { key: low, label: v };
  }, [profilesList]);

  // Everyone ASSIGNED to a job, deduplicated by canonical identity.
  // Deliberately not the creator: whoever typed the job in (often the office
  // "admin" login) isn't the person going out, and counting them inflated the
  // workload bars and put "admin" on the roster as if it were crew.
  const peopleOn = useCallback((p) => {
    const out = new Map();
    crewFor(p).forEach(raw => {
      const id = identityOf(raw);
      if (id && !out.has(id.key)) out.set(id.key, id);
    });
    return [...out.values()];
  }, [identityOf]);

  const roster = useMemo(() => {
    const map = new Map();
    projects.forEach(p => peopleOn(p).forEach(id => { if (!map.has(id.key)) map.set(id.key, id); }));
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  }, [projects, peopleOn]);

  const onJob = (p, personKey) => !personKey || !canManage
    || peopleOn(p).some(id => id.key === personKey);

  const personLabel = (roster.find(id => id.key === person) || {}).label || person;

  const byDay = useMemo(() => {
    const map = {};
    projects.filter(p => onJob(p, person)).forEach(p => {
      const k = jobDayKey(p.scheduled_at);
      (map[k] = map[k] || []).push(p);
    });
    Object.values(map).forEach(list => list.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at)));
    return map;
  }, [projects, person]);

  // Build the month grid, padded to whole weeks
  const grid = useMemo(() => {
    const first = new Date(cursor);
    const startPad = first.getDay();
    const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startPad; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(new Date(cursor.getFullYear(), cursor.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [cursor]);

  const partnerColor = (name) => {
    if (!name) return C.info;
    // Stable colour per partner so the same one reads the same every month
    const palette = [C.gold, C.success, C.info, C.warning, "#A78BFA", "#F472B6"];
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) % 997;
    return palette[h % palette.length];
  };

  const today = new Date(); today.setHours(0,0,0,0);
  const todayKey = jobDayKey(new Date().toISOString()); // Chicago's today, not the viewer's
  const monthLabel = cursor.toLocaleDateString(undefined, { month: "long", year: "numeric" });
  const shiftMonth = (n) => setCursor(prev => new Date(prev.getFullYear(), prev.getMonth() + n, 1));

  const selectedJobs = selectedDay ? (byDay[selectedDay] || []) : [];
  // Everyone assigned to something on the selected day
  const busyThatDay = selectedDay
    ? [...new Map(selectedJobs.flatMap(peopleOn).map(id => [id.key, id.label])).values()]
    : [];

  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      {/* Row 1: title + Sync. Row 2: month navigation at full width.
          One row didn't fit a phone — Sync got cut off and the page
          scrolled sideways, exposing a white strip down the right edge. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{t.schedule}</h2>
          <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13 }}>
            {!canManage
              ? `Your schedule — ${projects.length} job${projects.length === 1 ? "" : "s"}`
              : person
                ? `${projects.filter(p => onJob(p, person)).length} job${projects.filter(p => onJob(p, person)).length === 1 ? "" : "s"} for ${personLabel}`
                : `${projects.length} scheduled job${projects.length === 1 ? "" : "s"}`}
          </p>
        </div>
        <button style={{ ...S.btnS, borderColor: C.gold, color: C.gold, flexShrink: 0, padding: "8px 14px" }} onClick={() => setShowSync(true)}>{I.calendar} Sync</button>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 16 }}>
        <button style={{ ...S.btnS, padding: "8px 14px", flexShrink: 0 }} onClick={() => shiftMonth(-1)} aria-label="Previous month">‹</button>
        <div style={{ flex: 1, minWidth: 0, textAlign: "center", fontWeight: 700, color: C.light, fontSize: 15, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{monthLabel}</div>
        <button style={{ ...S.btnS, padding: "8px 14px", flexShrink: 0 }} onClick={() => shiftMonth(1)} aria-label="Next month">›</button>
        <button style={{ ...S.btnS, padding: "8px 12px", flexShrink: 0 }} onClick={() => { const d = new Date(); d.setDate(1); d.setHours(0,0,0,0); setCursor(d); setSelectedDay(todayKey); }}>Today</button>
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading...</div> : (
        <>
          {/* Whose schedule — filters the calendar and both lists below */}
          {/* Whose-schedule filter is admin only. Crew see their own jobs and
              nothing about who else is booked when. */}
          {canManage && roster.length > 0 && (
            <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
              <button onClick={() => setPerson("")} style={{
                padding: "7px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12.5, fontWeight: 700, fontFamily: "inherit",
                border: `1px solid ${!person ? C.gold : C.border}`,
                background: !person ? `${C.gold}22` : "transparent",
                color: !person ? C.gold : C.muted,
              }}>Everyone</button>
              {roster.map(id => {
                const count = projects.filter(p => onJob(p, id.key)).length;
                const active = person === id.key;
                return (
                  <button key={id.key} onClick={() => setPerson(active ? "" : id.key)} style={{
                    padding: "7px 14px", borderRadius: 20, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit",
                    border: `1px solid ${active ? C.gold : C.border}`,
                    background: active ? `${C.gold}22` : "transparent",
                    color: active ? C.gold : C.muted,
                  }}>{id.label} <span style={{ opacity: 0.7 }}>({count})</span></button>
                );
              })}
            </div>
          )}

          {canManage && person && (
            <div style={{ ...S.card, padding: "12px 16px", borderColor: `${C.gold}44` }}>
              {(() => {
                const theirs = projects.filter(p => onJob(p, person));
                const now = new Date(); now.setHours(0, 0, 0, 0);
                const upcoming = theirs.filter(p => new Date(p.scheduled_at) >= now);
                const next = upcoming.sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at))[0];
                const week = upcoming.filter(p => {
                  const d = new Date(p.scheduled_at);
                  return d <= new Date(now.getTime() + 7 * 86400000);
                }).length;
                return (
                  <div style={{ fontSize: 13, color: C.light, lineHeight: 1.7 }}>
                    <strong style={{ color: C.gold }}>{personLabel}</strong> — {upcoming.length} upcoming, {week} in the next 7 days
                    {next && (
                      <div style={{ color: C.muted, fontSize: 12 }}>
                        Next: {next.name} · {fmtJobDate(next.scheduled_at, { weekday: "short", month: "short", day: "numeric" })} at {fmtJobTime(next.scheduled_at)}
                      </div>
                    )}
                    {upcoming.length === 0 && <div style={{ color: C.success, fontSize: 12 }}>Nothing scheduled — fully open.</div>}
                  </div>
                );
              })()}
            </div>
          )}

          <div style={{ ...S.card, padding: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(7, minmax(0, 1fr))", gap: 3 }}>
              {["S","M","T","W","T","F","S"].map((d, i) => (
                <div key={i} style={{ textAlign: "center", fontSize: 10, fontWeight: 800, color: C.muted, letterSpacing: 1, padding: "4px 0" }}>{d}</div>
              ))}
              {grid.map((d, i) => {
                if (!d) return <div key={i} />;
                const k = dayKey(d);
                const jobs = byDay[k] || [];
                const isToday = k === todayKey;
                const isSel = selectedDay === k;
                return (
                  <div key={i} onClick={() => setSelectedDay(isSel ? null : k)}
                    style={{
                      minHeight: 62, borderRadius: 8, padding: "5px 5px 4px", cursor: "pointer",
                      background: isSel ? `${C.gold}22` : jobs.length > 0 ? C.surface : "transparent",
                      border: `1px solid ${isSel ? C.gold : isToday ? C.info : C.border}`,
                    }}>
                    <div style={{ fontSize: 11, fontWeight: isToday ? 800 : 600, color: isToday ? C.info : C.light, marginBottom: 3 }}>{d.getDate()}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {jobs.slice(0, 3).map(j => (
                        <div key={j.key || j.id} style={{ height: 4, borderRadius: 2, background: partnerColor(j.partner) }} title={j.name} />
                      ))}
                      {jobs.length > 3 && <div style={{ fontSize: 9, color: C.muted }}>+{jobs.length - 3}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {selectedDay && (
            <div style={S.card}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>
                  {new Date(selectedDay + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
                </h3>
                <span style={{ fontSize: 12, color: C.muted }}>{selectedJobs.length} job{selectedJobs.length === 1 ? "" : "s"}</span>
              </div>

              {selectedJobs.length === 0 ? (
                <div style={{ color: C.success, fontSize: 13, textAlign: "center", padding: 16 }}>Nothing scheduled — everyone's open.</div>
              ) : (
                <>
                  {selectedJobs.map(j => (
                    <div key={j.key || j.id} onClick={() => openJob(j)} style={{ padding: 12, background: C.surface, borderRadius: 8, marginBottom: 6, borderLeft: `3px solid ${partnerColor(j.partner)}`, cursor: "pointer" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 14, color: C.light }}>{j.name}{j.visitLabel && (j.visits || []).filter(v => !v.cancelled).length > 1 && <span style={{ color: C.gold, fontWeight: 600, fontSize: 12 }}> · {j.visitLabel}</span>}{((j.visits || []).find(v => v.id === j.visitId) || {}).status === "done" && <span style={{ ...S.badge(C.success), marginLeft: 8 }}>✓ done</span>}</div>
                        <div style={{ fontSize: 13, color: C.gold, fontWeight: 700 }}>
                          {fmtJobTime(j.scheduled_at)}
                        </div>
                      </div>
                      {j.address && <div style={{ fontSize: 12, color: C.muted, marginTop: 3 }}>{j.address}</div>}
                      {j.scope && <div style={{ fontSize: 12.5, color: C.light, marginTop: 4, lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>🛠 {j.scope}</div>}
                      <div style={{ display: "flex", gap: 8, marginTop: 7, flexWrap: "wrap", alignItems: "center" }}>
                        {j.partner && <span style={S.badge(partnerColor(j.partner))}>{j.partner}</span>}
                        {crewFor(j).length > 0
                          ? [...new Map(crewFor(j).map(raw => { const id = identityOf(raw); return [id.key, id]; })).values()]
                              .map(id => <span key={id.key} style={{ fontSize: 11, color: C.light, background: C.card, borderRadius: 12, padding: "3px 9px" }}>{id.label}</span>)
                          : <span style={{ fontSize: 11, color: C.warning }}>⚠ No crew assigned</span>}
                      </div>
                    </div>
                  ))}
                  {canManage && busyThatDay.length > 0 && (
                    <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted }}>
                      <strong style={{ color: C.light }}>Booked this day:</strong> {busyThatDay.join(", ")}
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Who's carrying what — only useful when looking at everyone */}
          {canManage && !person && roster.length > 0 && (
            <div style={S.card}>
              <div style={S.label}>Workload — Next 14 Days</div>
              {(() => {
                const now = new Date(); now.setHours(0, 0, 0, 0);
                const horizon = new Date(now.getTime() + 14 * 86400000);
                const rows = roster.map(id => {
                  const jobs = projects.filter(p => onJob(p, id.key))
                    .filter(p => { const d = new Date(p.scheduled_at); return d >= now && d <= horizon; });
                  return { key: id.key, name: id.label, count: jobs.length };
                }).sort((a, b) => b.count - a.count);
                const max = Math.max(1, ...rows.map(r => r.count));
                return rows.map(r => (
                  <div key={r.key} onClick={() => setPerson(r.key)}
                    style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 0", cursor: "pointer" }}>
                    <div style={{ width: 110, fontSize: 13, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</div>
                    <div style={{ flex: 1, height: 8, background: C.surface, borderRadius: 99, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(r.count / max) * 100}%`, background: r.count === 0 ? C.success : C.gold }} />
                    </div>
                    <div style={{ width: 54, textAlign: "right", fontSize: 12, fontWeight: 700, color: r.count === 0 ? C.success : C.light }}>
                      {r.count === 0 ? "open" : `${r.count} job${r.count === 1 ? "" : "s"}`}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}

          {/* Upcoming list — the practical view on a phone */}
          <div style={S.card}>
            <div style={S.label}>Next 14 Days{!canManage ? " — Yours" : person ? ` — ${personLabel}` : ""}</div>
            {(() => {
              const now = new Date(); now.setHours(0,0,0,0);
              const horizon = new Date(now.getTime() + 14 * 86400000);
              const upcoming = projects
                .filter(p => onJob(p, person))
                .filter(p => { const d = new Date(p.scheduled_at); return d >= now && d <= horizon; })
                .sort((a, b) => new Date(a.scheduled_at) - new Date(b.scheduled_at));
              if (upcoming.length === 0) return <div style={{ color: C.muted, fontSize: 13, textAlign: "center", padding: 14 }}>Nothing scheduled in the next two weeks.</div>;
              return upcoming.map(j => (
                <div key={j.key || j.id} onClick={() => openJob(j)} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${C.border}33`, cursor: "pointer" }}>
                  <div style={{ width: 3, alignSelf: "stretch", borderRadius: 2, background: partnerColor(j.partner) }} />
                  <div style={{ minWidth: 92, fontSize: 12, color: C.gold, fontWeight: 700 }}>
                    {fmtJobDate(j.scheduled_at, { weekday: "short", month: "short", day: "numeric" })}
                    <div style={{ color: C.muted, fontWeight: 500 }}>{fmtJobTime(j.scheduled_at)}</div>
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{j.name}{j.visitLabel && (j.visits || []).filter(v => !v.cancelled).length > 1 && <span style={{ color: C.gold, fontWeight: 600, fontSize: 12 }}> · {j.visitLabel}</span>}{((j.visits || []).find(v => v.id === j.visitId) || {}).status === "done" && <span style={{ ...S.badge(C.success), marginLeft: 8 }}>✓ done</span>}</div>
                    {j.scope && <div style={{ fontSize: 11.5, color: C.light, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>🛠 {j.scope}</div>}
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      {crewFor(j).length > 0
                        ? [...new Map(crewFor(j).map(raw => { const id = identityOf(raw); return [id.key, id]; })).values()].map(id => id.label).join(", ")
                        : <span style={{ color: C.warning }}>No crew assigned</span>}
                    </div>
                  </div>
                  {j.partner && <span style={S.badge(partnerColor(j.partner))}>{j.partner}</span>}
                </div>
              ));
            })()}
          </div>
        </>
      )}

      {/* ── Calendar sync ─────────────────────────────────────────────── */}
      {showSync && (
        <Modal onClose={() => setShowSync(false)}>
          <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: C.light }}>📅 Sync to your calendar</h3>
          <p style={{ color: C.muted, fontSize: 13, margin: "0 0 16px", lineHeight: 1.5 }}>
            Set this up once. After that your jobs appear in your calendar on their own —
            reschedules move and cancellations disappear automatically.
          </p>

          {!feedUrl ? (
            <div style={{ background: `${C.warning}15`, border: `1px solid ${C.warning}44`, borderRadius: 8, padding: 12, fontSize: 13, color: C.light }}>
              Your calendar link isn't set up yet. Ask Ivan to run the calendar setup.
            </div>
          ) : (
            <>
              <div style={S.label}>Your private link</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
                <input readOnly value={feedUrl} style={{ ...S.input, fontSize: 11, fontFamily: "monospace" }} onFocus={e => e.target.select()} />
                <button style={{ ...S.btnP, whiteSpace: "nowrap" }} onClick={() => {
                  if (navigator.clipboard?.writeText) navigator.clipboard.writeText(feedUrl).then(() => alert("Link copied ✓"), () => window.prompt("Copy this link:", feedUrl));
                  else window.prompt("Copy this link:", feedUrl);
                }}>Copy</button>
              </div>
              <div style={{ fontSize: 11, color: C.warning, marginBottom: 16, lineHeight: 1.5 }}>
                ⚠ Treat this like a password — anyone with it can see your job schedule. Don't share it.
              </div>

              <div style={S.label}>Proton Calendar</div>
              <ol style={{ margin: "0 0 14px", paddingLeft: 20, fontSize: 13, color: C.light, lineHeight: 1.8 }}>
                <li>Copy the link above</li>
                <li>Open <strong>calendar.proton.me</strong> on a computer</li>
                <li>Settings ⚙️ → All settings → <strong>Calendars</strong></li>
                <li>Under <strong>Other calendars</strong>, click <strong>Add calendar from URL</strong></li>
                <li>Paste the link → <strong>Add calendar</strong></li>
              </ol>
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 14, lineHeight: 1.5 }}>
                It shows up on the Proton phone app too once added. The first sync can take a few minutes.
              </div>

              <div style={S.label}>Español</div>
              <ol style={{ margin: "0 0 14px", paddingLeft: 20, fontSize: 13, color: C.light, lineHeight: 1.8 }}>
                <li>Copien el enlace de arriba</li>
                <li>Abran <strong>calendar.proton.me</strong> en una computadora</li>
                <li>Configuración ⚙️ → Todos los ajustes → <strong>Calendarios</strong></li>
                <li>En <strong>Otros calendarios</strong>, den clic en <strong>Agregar calendario desde URL</strong></li>
                <li>Peguen el enlace → <strong>Agregar calendario</strong></li>
              </ol>

              <div style={{ background: C.surface, borderRadius: 8, padding: 11, fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                <strong style={{ color: C.light }}>How fast does it update?</strong> Your calendar app checks for changes on its own
                schedule — usually within an hour or a few. For a same-day change, you'll still get a
                text or email. The Hub is always current.
              </div>
            </>
          )}
        </Modal>
      )}

      {/* ── Full job briefing ─────────────────────────────────────────── */}
      {jobDetail && (() => {
        const j = jobDetail;
        const partnerRec = jobRefs.partners.find(x => x.name === j.partner);
        // The job's own Field Kit wins. A suggestion is only shown when nothing
        // was set, and it's labeled as one — never presented as the assignment.
        // The upcoming visit's own procedure wins (removal vs reinstall), then the job's
        const nextVisit = (j.visits || []).find(v => j.visitId ? v.id === j.visitId : (!v.cancelled && v.scheduled_at === j.scheduled_at));
        const pbId = (nextVisit && nextVisit.playbookId) || j.playbookId;
        const assignedPb = pbId ? jobRefs.playbooks.find(b => b.id === pbId) : null;
        const pb = assignedPb || suggestPlaybook(j, jobRefs.playbooks);
        const pbIsSuggestion = !assignedPb && !!pb;
        const truckName = j.truckList || suggestTruckList(j);
        const truckIsSuggestion = !j.truckList && !!truckName;
        const truckKey = truckListKey(truckName);
        const crew = crewFor(j);
        return (
          <Modal onClose={() => setJobDetail(null)}>
            <h3 style={{ margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: C.light }}>{j.name}</h3>
            <div style={{ fontSize: 14, color: C.gold, fontWeight: 700, marginBottom: 14 }}>
              {(j.visits || []).filter(v => !v.cancelled).length > 1 && nextVisit ? `${nextVisit.label} · ` : ""}
              {fmtJobDate(j.scheduled_at, { weekday: "long", month: "long", day: "numeric" })} · {fmtJobTime(j.scheduled_at)}
              {(() => { const d = parseFloat(nextVisit?.durationHours); if (!d) return null; const endIso = new Date(new Date(j.scheduled_at).getTime() + d * 3600000).toISOString(); return <span style={{ color: C.muted, fontWeight: 500 }}> – {fmtJobTime(endIso)} ({d} hr)</span>; })()}
            </div>
            {nextVisit && (
              <div style={{ marginBottom: 12 }}>
                <VisitChips progress={visitProgress(j, nextVisit, jobProgress.forms, jobProgress.runs)} size={11.5} />
              </div>
            )}

            <div style={{ background: C.surface, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, color: C.light, lineHeight: 1.7 }}>
              {j.address && (
                <div>
                  📍 <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(j.address)}`} target="_blank" rel="noopener noreferrer" style={{ color: C.light }}>{j.address}</a>
                </div>
              )}
              {j.clientPhone && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span>📞 <a href={`tel:${j.clientPhone}`} style={{ color: C.light }}>{j.clientPhone}</a></span>
                  {USE_SUPABASE && (
                    <button type="button" disabled={textBusy} onClick={async () => {
                      const tpl = smsTemplates(j, nextVisit, user).find(x => x.id === "onmyway");
                      if (!window.confirm(`Text the homeowner from the company number?\n\n${tpl.text}`)) return;
                      setTextBusy(true);
                      try { await db.quoSend({ to: j.clientPhone, content: tpl.text, projectId: j.id, kind: "onmyway" }); alert("Sent ✓"); }
                      catch(e) { alert(`Not sent: ${e.message}`); }
                      setTextBusy(false);
                    }} style={{ ...S.btnS, fontSize: 12, padding: "5px 10px", borderColor: C.gold, color: C.gold }}>{textBusy ? "…" : "💬 Text: on my way"}</button>
                  )}
                </div>
              )}
              {j.scope && <div style={{ marginTop: 6, padding: "8px 10px", background: `${C.gold}14`, border: `1px solid ${C.gold}44`, borderRadius: 8, whiteSpace: "pre-wrap", lineHeight: 1.5 }}><strong style={{ color: C.gold }}>Scope:</strong> {j.scope}</div>}
              {permitRequired(j) && <div style={{ marginTop: 6, color: j.permit.issuedOn ? C.light : C.warning }}>📋 Permit {permitLine(j)}{j.permit.issuedOn ? " — post on site" : ""}</div>}
              {j.serviceSubtype && <div>🔧 {j.serviceSubtype}</div>}
              {j.systemSize && <div>☀️ {j.systemSize} kW</div>}
              {crew.length > 0 && <div>👷 {crew.join(", ")}</div>}
            </div>

            {(() => {
              const notes = ((nextVisit && nextVisit.notes) || (j.visits || []).filter(v => !v.cancelled && v.notes).map(v => v.notes).pop() || "").trim();
              if (!notes) return null;
              return (
                <div style={{ marginBottom: 12 }}>
                  <div style={S.label}>Reported issue / notes</div>
                  <div style={{ fontSize: 13, color: C.light, background: `${C.warning}12`, border: `1px solid ${C.warning}44`, borderRadius: 8, padding: 10, lineHeight: 1.6, whiteSpace: "pre-wrap" }}><Linkify text={notes} /></div>
                </div>
              );
            })()}

            {j.jhaSheet?.imageUrl && (
              <div style={{ marginBottom: 12 }}>
                <div style={S.label}>Site JHA sheet</div>
                <div style={{ display: "flex", gap: 10, alignItems: "center", background: C.surface, borderRadius: 8, padding: 10, border: `1px solid ${C.border}` }}>
                  <img loading="lazy" decoding="async" src={j.jhaSheet.imageUrl} alt="" style={{ width: 64, height: 48, objectFit: "cover", borderRadius: 6, background: "#fff" }} />
                  <div style={{ flex: 1, fontSize: 12, color: C.muted }}>{(j.jhaSheet.markers || []).filter(m => m.type === "H").length} hazards marked{j.jhaSheet.urgentCare ? ` · urgent care: ${j.jhaSheet.urgentCare}` : ""}</div>
                  <button type="button" onClick={() => { const html = jhaSheetPrintHtml(j.jhaSheet, j); const w = window.open("", "_blank"); if (w) { w.document.write(html); w.document.close(); } }} style={{ ...S.btnP, fontSize: 12, padding: "7px 12px" }}>{I.pdf} Print</button>
                </div>
              </div>
            )}

            {folderUrlOf(j) && (
              <div style={{ marginBottom: 12 }}>
                <a href={folderUrlOf(j)} target="_blank" rel="noopener noreferrer" style={{ ...S.btnP, textDecoration: "none", display: "inline-flex", fontSize: 13, padding: "9px 14px" }}>📁 Open job folder (Proton Drive)</a>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Photos and the report go here as well as in the Hub.</div>
              </div>
            )}
            {(j.refDocs || []).filter(d => !d.kind).length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={S.label}>Job documents</div>
                <div style={{ background: C.surface, borderRadius: 8, padding: "2px 10px", border: `1px solid ${C.border}` }}>
                  <RefDocs docs={j.refDocs} onChange={() => {}} canEdit={false} compact />
                </div>
              </div>
            )}

            {(j.refPhotos || []).length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={S.label}>Reference photos</div>
                <RefPhotos photos={j.refPhotos} onChange={() => {}} canEdit={false} compact />
              </div>
            )}

            {j.partner && (
              <div style={{ marginBottom: 12 }}>
                <div style={S.label}>Partner — {j.partner}</div>
                <div style={{ fontSize: 13, color: C.light, background: `${C.gold}12`, border: `1px solid ${C.gold}33`, borderRadius: 8, padding: 10, lineHeight: 1.6 }}>
                  {partnerRec?.submissionMethod && <div style={{ color: C.muted }}>Submit via {partnerRec.submissionMethod}</div>}
                  {partnerRec?.crewNote && <div style={{ marginTop: 4, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}><Linkify text={partnerRec.crewNote} /></div>}
                  {canSeeMoney(user) && partnerRec?.notes && <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.border}`, fontSize: 12, color: C.muted, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}><span style={{ fontWeight: 700 }}>Office notes (not sent to crew):</span> <Linkify text={partnerRec.notes} /></div>}
                  {!partnerRec?.crewNote && !partnerRec?.notes && <div style={{ color: C.muted }}>No partner notes on file.</div>}
                </div>
              </div>
            )}

            {/* ── Field Kit: what to load, how to stay safe, what to follow ── */}
            <div style={{ ...S.label, marginTop: 4 }}>Field Kit</div>

            {/* 1. Truck Check — first, because it happens before leaving the shop */}
            <div style={{ background: C.surface, borderRadius: 8, padding: 11, marginBottom: 8, border: `1px solid ${C.border}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <span style={{ color: C.gold, flexShrink: 0 }}>{I.truck}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: C.muted }}>1 · Before you leave the shop</div>
                  <div style={{ fontSize: 13, color: C.light, fontWeight: 600 }}>
                    {truckName ? `Truck Check: ${truckName}` : "No Truck Check list set"}
                    {truckIsSuggestion && <span style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}> (suggested)</span>}
                  </div>
                </div>
                {truckKey && (
                  <a href={`#truckcheck/${encodeURIComponent(truckKey)}/${encodeURIComponent(j.name || "")}`}
                    onClick={(ev) => { setJobDetail(null); goHash(ev, `truckcheck/${encodeURIComponent(truckKey)}/${encodeURIComponent(j.name || "")}`); }}
                    style={{ ...S.btnS, textDecoration: "none", fontSize: 12, padding: "6px 12px", borderColor: C.gold, color: C.gold, flexShrink: 0 }}>Open</a>
                )}
              </div>
            </div>

            {/* 2. Safety — shown in full, not hidden behind a link */}
            <div style={{ background: `${C.danger}0d`, borderRadius: 8, padding: 11, marginBottom: 8, border: `1px solid ${C.danger}44` }}>
              <div style={{ fontSize: 11, color: C.muted }}>2 · Before you start work</div>
              <div style={{ fontSize: 13, color: C.danger, fontWeight: 700, marginBottom: 6 }}>⚠ Safety</div>
              {pb && (pb.safety || []).length > 0
                ? ((getLang() === "es" && pb.esStatus === "approved" && (pb.es?.safety || []).length === pb.safety.length) ? pb.es.safety : pb.safety)
                    .map((s, i) => <div key={i} style={{ fontSize: 12.5, color: C.light, lineHeight: 1.55, padding: "2px 0" }}>• {s}</div>)
                : <div style={{ fontSize: 12, color: C.muted }}>No procedure-specific safety steps. Standard PV safety applies — verify AC and DC disconnects before opening any enclosure.</div>}
              <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 11.5, color: C.light, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.danger}33` }}>
                <span>Complete the <strong>JHA</strong> before starting.</span>
                <a href={`#form/jha/${encodeURIComponent(j.id)}/${encodeURIComponent(j.visitId || "")}`}
                  onClick={(ev) => { setJobDetail(null); goHash(ev, `form/jha/${encodeURIComponent(j.id)}/${encodeURIComponent(j.visitId || "")}`); }}
                  style={{ ...S.btnP, textDecoration: "none", fontSize: 12, padding: "6px 12px" }}>{I.hazard} Fill JHA</a>
                <span style={{ color: C.muted }}>Job, address, crew, urgent care and marked hazards fill in.</span>
              </div>
            </div>

            {/* 3. The Hub form — filled on site, pre-linked to this job */}
            {(() => {
              const primary = j.projectType === "new_install" ? "install" : j.projectType === "survey" ? "survey" : "assessment";
              const other = primary === "install" ? "survey" : primary === "survey" ? "assessment" : "survey";
              const label = (k) => k === "survey" ? "TSA Survey" : k === "install" ? "Install Closeout" : "Service Assessment";
              return (
                <div style={{ background: C.surface, borderRadius: 8, padding: 11, marginBottom: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 11, color: C.muted }}>3 · On site</div>
                  <div style={{ fontSize: 13, color: C.light, fontWeight: 600, marginBottom: 8 }}>
                    Fill out the {label(primary)} — photos, findings, and anything the partner requires
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <a href={`#form/${primary}/${encodeURIComponent(j.id)}/${encodeURIComponent(j.visitId || "")}`}
                      onClick={(ev) => { setJobDetail(null); goHash(ev, `form/${primary}/${encodeURIComponent(j.id)}/${encodeURIComponent(j.visitId || "")}`); }}
                      style={{ ...S.btnP, textDecoration: "none", fontSize: 12, padding: "7px 14px" }}>{I.clip} Open {label(primary)}</a>
                    <a href={`#form/${other}/${encodeURIComponent(j.id)}/${encodeURIComponent(j.visitId || "")}`}
                      onClick={(ev) => { setJobDetail(null); goHash(ev, `form/${other}/${encodeURIComponent(j.id)}/${encodeURIComponent(j.visitId || "")}`); }}
                      style={{ fontSize: 12, color: C.muted, textDecoration: "underline" }}>or {label(other)}</a>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 7 }}>Opens already linked to this job — client, partner, and required items fill in.</div>
                </div>
              );
            })()}

            {/* Time budget — what the partner pays, and when to stop
                troubleshooting and start documenting. */}
            {(() => {
              // Omnidian only — their contract is the one priced per visit
              const isOmnidian = [j.partner, pb?.partner].some(x => (x || "").trim().toLowerCase() === "omnidian");
              if (!isOmnidian) return null;
              const tb = timeBudgetFor(pb, crew.length || undefined);
              if (!tb) return null;
              return (
                <div style={{ background: tb.tight ? `${C.warning}12` : C.surface, borderRadius: 8, padding: 11, marginBottom: 8,
                  border: `1px solid ${tb.tight ? `${C.warning}55` : C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, flexWrap: "wrap" }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: tb.tight ? C.warning : C.light }}>
                      ⏱ {tb.onSite} on site
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {tb.budget} man-hr paid{crew.length ? ` · ${crew.length} assigned` : ` · assumes ${tb.crew}`}
                    </div>
                  </div>
                  <div style={{ fontSize: 12, color: C.light, marginTop: 7, lineHeight: 1.6 }}>
                    <strong>~{tb.troubleshoot}</strong> to diagnose, then <strong>~{tb.documenting}</strong> for photos and the report.
                  </div>
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 7, lineHeight: 1.5 }}>
                    Call OEM support early — hold time comes out of the same clock. When the diagnose time is up,
                    stop and document what you have. An unfinished diagnosis with a complete report still gets paid;
                    a solved one without photos does not. Put what is left in Return Visit Required.
                  </div>

                </div>
              );
            })()}

            {/* 4. Partner procedure + reference material */}
            {(() => {
              const refs = (j.referenceIds || []).map(id => jobRefs.playbooks.find(b => b.id === id)).filter(Boolean);
              return (
                <div style={{ background: C.surface, borderRadius: 8, padding: 11, marginBottom: 12, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 11, color: C.muted }}>4 · Procedure & reference</div>
                  {pb ? (
                    <>
                      <div style={{ fontSize: 13, color: C.light, fontWeight: 600, margin: "2px 0 8px" }}>
                        {(getLang() === "es" && pb.esStatus === "approved" && pb.es?.title) ? pb.es.title : pb.title}
                        {pbIsSuggestion && <span style={{ color: C.muted, fontWeight: 400, fontSize: 11 }}> (suggested — not assigned)</span>}
                      </div>
                      {(pb.steps || []).length > 0 && (
                        <div style={{ marginBottom: 10 }}>
                          {((getLang() === "es" && pb.esStatus === "approved" && (pb.es?.steps || []).length === pb.steps.length) ? pb.es.steps : pb.steps).slice(0, 4).map((st, i) => (
                            <div key={i} style={{ fontSize: 12, color: C.light, lineHeight: 1.5, padding: "2px 0" }}>{i + 1}. {st}</div>
                          ))}
                          {pb.steps.length > 4 && <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>+{pb.steps.length - 4} more steps</div>}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <a href={`#procedures/${encodeURIComponent(pb.id)}`}
                          onClick={(ev) => { setJobDetail(null); goHash(ev, `procedures/${encodeURIComponent(pb.id)}`); }}
                          style={{ ...S.btnS, textDecoration: "none", fontSize: 12, padding: "6px 12px", flex: 1, justifyContent: "center" }}>Full procedure</a>
                        {(pb.steps || []).length > 0 && (
                          <a href={`#procedures/${encodeURIComponent(pb.id)}/run/${encodeURIComponent(j.id)}`}
                            onClick={(ev) => { setJobDetail(null); goHash(ev, `procedures/${encodeURIComponent(pb.id)}/run/${encodeURIComponent(j.id)}`); }}
                            style={{ ...S.btnP, textDecoration: "none", fontSize: 12, padding: "6px 12px", flex: 1, justifyContent: "center" }}>{I.check} Start on this job</a>
                        )}
                      </div>
                    </>
                  ) : (
                    <div style={{ fontSize: 12, color: C.muted, margin: "2px 0 0" }}>No procedure assigned to this job.</div>
                  )}

                  {refs.length > 0 && (
                    <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 6 }}>Reference</div>
                      {refs.map(r => (
                        <a key={r.id} href={`#procedures/${encodeURIComponent(r.id)}`}
                          onClick={(ev) => { setJobDetail(null); goHash(ev, `procedures/${encodeURIComponent(r.id)}`); }}
                          style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 0", textDecoration: "none", borderBottom: `1px solid ${C.border}33` }}>
                          <span style={{ color: C.gold, flexShrink: 0 }}>{I.book}</span>
                          <span style={{ flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 13, color: C.light, display: "block" }}>{r.title}</span>
                            <span style={{ fontSize: 11, color: C.muted }}>{r.serviceType}{(r.links || []).length > 0 ? ` · ${r.links.length} file${r.links.length === 1 ? "" : "s"}` : ""}</span>
                          </span>
                          <span style={{ color: C.muted, fontSize: 12 }}>→</span>
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              );
            })()}

            {j.projectType === "service" && <div style={{ marginBottom: 12 }}><OemSupport /></div>}

            {jobPhotos.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={S.label}>Photos ({jobPhotos.length})</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 6 }}>
                  {jobPhotos.map((ph, i) => (
                    <a key={i} href={ph.url} target="_blank" rel="noopener noreferrer">
                      <img loading="lazy" decoding="async" src={ph.url} alt="" style={{ width: "100%", height: 88, objectFit: "cover", borderRadius: 6, border: `1px solid ${C.border}` }}
                        onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                    </a>
                  ))}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>From forms filed at this address</div>
              </div>
            )}


          </Modal>
        );
      })()}
    </div>
  );
}

// Printable record of a completed procedure run — the evidence that the
// closeout was actually performed, by whom, and what was flagged.
function generateProcedureRunPDF(run) {
  const esc = (v) => String(v ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
  const items = run.items || [];
  const done = items.filter(i => i.checked).length;
  const exceptions = items.filter(i => !i.checked);

  const rows = items.map((it, i) => `<tr${!it.checked ? ' style="background:#fff6f6"' : ''}>
    <td style="border:1px solid #ccc;padding:7px;text-align:center;font-size:15px;line-height:1">${it.checked ? "☑" : "☐"}</td>
    <td style="border:1px solid #ccc;padding:7px;text-align:center;color:#666;font-size:10px">${i + 1}</td>
    <td style="border:1px solid #ccc;padding:7px;font-size:11px;line-height:1.5">
      ${esc(it.step)}
      ${it.note ? `<div style="margin-top:4px;color:#c00;font-size:10.5px"><strong>Exception:</strong> ${esc(it.note)}</div>` : ""}
    </td>
  </tr>`).join("");

  const printContent = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${esc(run.playbookTitle)} — ${esc(run.projectName)}</title>
  <style>
    body { font-family: Helvetica, Arial, sans-serif; color:#000; padding:32px; font-size:12px; }
    h1 { font-size:17px; margin:0; }
    h2 { font-size:11px; text-transform:uppercase; letter-spacing:.08em; border-bottom:1px solid #bbb; padding-bottom:4px; margin:18px 0 8px; }
    .hdr { display:flex; justify-content:space-between; align-items:flex-end; border-bottom:2px solid #2A5C38; padding-bottom:8px; margin-bottom:12px; }
    .meta { border:1px solid #ddd; background:#f7f7f7; border-radius:6px; padding:10px 12px; margin-bottom:12px; }
    .meta div { font-size:11.5px; margin:2px 0; }
    table { width:100%; border-collapse:collapse; }
    .warn { background:#fff3f3; border:2px solid #c00; border-radius:6px; padding:10px 12px; margin:12px 0; font-size:11.5px; }
    .foot { margin-top:22px; border-top:1px solid #ccc; padding-top:8px; font-size:9px; color:#555; display:flex; justify-content:space-between; }
    @media print { body { padding:18px; } tr { break-inside:avoid; page-break-inside:avoid; } }
  </style></head><body>
    <div class="hdr">
      <div><h1>SolarAbility LLC</h1><div style="font-size:10px;color:#555;margin-top:2px">Procedure Verification Record</div></div>
      <div style="text-align:right"><div style="font-weight:bold;font-size:12.5px">${esc(run.playbookTitle)}</div></div>
    </div>

    <div class="meta">
      <div><strong>${esc(run.projectName || "—")}</strong></div>
      <div><span style="color:#666">Performed by:</span> ${esc(run.startedBy || "—")}</div>
      <div><span style="color:#666">Started:</span> ${run.startedAt ? new Date(run.startedAt).toLocaleString() : "—"}</div>
      <div><span style="color:#666">Completed:</span> ${run.completedAt ? new Date(run.completedAt).toLocaleString() : "Not marked complete"}</div>
      <div><span style="color:#666">Verified:</span> <strong>${done} of ${items.length}</strong> items</div>
    </div>

    ${exceptions.length > 0 ? `<div class="warn">
      <strong>${exceptions.length} item${exceptions.length === 1 ? "" : "s"} not verified.</strong>
      Any item left unchecked is an exception and must be documented with the partner before the site is considered closed.
    </div>` : ""}

    <h2>Verification Items</h2>
    <table>${rows}</table>

    <div style="margin-top:20px;display:flex;gap:30px;font-size:11px">
      <div style="flex:1;border-top:1px solid #666;padding-top:5px">Technician signature</div>
      <div style="flex:1;border-top:1px solid #666;padding-top:5px">Date</div>
    </div>

    <div class="foot">
      <span>SolarAbility LLC — NABCEP Certified — ICC #21-0505</span>
      <span>Printed ${new Date().toLocaleString()}</span>
    </div>
  </body></html>`;

  printHTML(printContent);
}

// ─── Procedures / Playbooks ─────────────────────────────────────────────────
// Field reference the crew opens on site: what to do for this kind of call,
// the safety steps, partner-specific requirements, and links to manuals.
const PLAYBOOK_SERVICE_TYPES = [
  "Leak Assessment", "Production Issue", "Inverter Issue", "System Inspection",
  "Electrical Issue", "Panel Cleaning", "New Installation", "Removal & Reinstall",
  "EV Charger", "General O&M",
  // Reference material — read on site, not worked through as a checklist
  "Troubleshooting Guide", "Training", "Manual / Reference",
];
const REFERENCE_TYPES = ["Troubleshooting Guide", "Training", "Manual / Reference"];
const isReferenceDoc = (b) => REFERENCE_TYPES.includes(b?.serviceType);

// Pick a Hub login to grant access to. Lists profiles when available,
// otherwise accepts a typed email.
function AccessPicker({ value, onAdd }) {
  const [profiles, setProfiles] = useState([]);
  const [typed, setTyped] = useState("");
  const ref = useRef(false);
  if (!ref.current) { ref.current = true; if (USE_SUPABASE && supabase) supabase.from("profiles").select("email, full_name").then(({ data }) => setProfiles((data || []).filter(x => x.email))).catch(() => {}); }
  const remaining = profiles.filter(p => !(value || []).includes(p.email.toLowerCase()));
  return (
    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
      {remaining.length > 0 && (
        <select style={{ ...S.select, flex: 1, minWidth: 220 }} value="" onChange={e => { if (e.target.value) onAdd(e.target.value); }}>
          <option value="">— add a login —</option>
          {remaining.map(p => <option key={p.email} value={p.email.toLowerCase()}>{p.full_name ? `${p.full_name} · ${p.email}` : p.email}</option>)}
        </select>
      )}
      <input style={{ ...S.input, flex: 1, minWidth: 200 }} type="email" placeholder="or type an email" value={typed} onChange={e => setTyped(e.target.value)}
        onKeyDown={e => { if (e.key === "Enter" && typed.includes("@")) { onAdd(typed); setTyped(""); } }} />
      <button type="button" style={S.btnS} onClick={() => { if (typed.includes("@")) { onAdd(typed); setTyped(""); } }}>{I.plus} Add</button>
    </div>
  );
}

function Procedures({ user, t, isAdmin, focusId, runForProject, onFocusHandled }) {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list"); // list | read | edit | run
  const [current, setCurrent] = useState(null);
  const [form, setForm] = useState(null);
  const [search, setSearch] = useState("");
  const [partnerNames, setPartnerNames] = useState([]);
  const [docUploading, setDocUploading] = useState(false);
  const [showEs, setShowEs] = useState(getLang() === "es"); // read view language
  const [translating, setTranslating] = useState(false);
  const [category, setCategory] = useState("all"); // list filter
  const pnRef = useRef(false);
  if (!pnRef.current) { pnRef.current = true; db.getPartners().then(l => setPartnerNames((l || []).map(x => x.name))).catch(() => {}); }

  // ── Running a procedure against a job ──────────────────────────────────
  const [runProjects, setRunProjects] = useState([]);
  const [run, setRun] = useState(null);
  const [runSaving, setRunSaving] = useState(false);
  // This user's saved-but-unfinished runs, for the "pick up where you left off" strip
  const [openRuns, setOpenRuns] = useState([]);
  const openRunsRef = useRef(false);
  const refreshOpenRuns = useCallback(() => {
    db.getProcedureRuns().then(list => setOpenRuns((list || []).filter(r => !r.completedAt && (!r.startedBy || !user?.name || r.startedBy === user.name)))).catch(() => {});
  }, [user]);
  if (!openRunsRef.current) { openRunsRef.current = true; refreshOpenRuns(); }
  const runProjRef = useRef(false);
  if (!runProjRef.current) {
    runProjRef.current = true;
    db.getProjects().then(list => setRunProjects(list.filter(p => p.status !== "completed"))).catch(() => {});
  }

  const startRun = (pb) => {
    setRun({
      id: `run-${Date.now()}`,
      projectId: "", projectName: "",
      playbookId: pb.id, playbookTitle: pb.title,
      startedBy: user?.name || "", startedAt: new Date().toISOString(),
      completedAt: null,
      // English is the record (partners read it); Spanish rides along for the
      // tech working through it on site.
      items: (pb.steps || []).map((s, i) => ({
        step: s,
        stepEs: (pb.esStatus === "approved" && (pb.es?.steps || []).length === (pb.steps || []).length) ? pb.es.steps[i] : "",
        checked: false, note: "",
      })),
    });
    setCurrent(pb);
    setView("run");
  };

  const toggleRunItem = (i) => setRun(p => ({
    ...p, items: p.items.map((it, j) => j === i ? { ...it, checked: !it.checked } : it),
  }));
  const setRunNote = (i, v) => setRun(p => ({
    ...p, items: p.items.map((it, j) => j === i ? { ...it, note: v } : it),
  }));

  const saveRun = async (markComplete) => {
    if (!run.projectId) return alert("Pick which job this is for.");
    const unchecked = run.items.filter(it => !it.checked);
    const missingNotes = unchecked.filter(it => !(it.note || "").trim());
    if (markComplete && missingNotes.length > 0) {
      return alert(`${missingNotes.length} item(s) aren't verified and have no exception noted. Add a reason for each, or check them off.`);
    }
    setRunSaving(true);
    const payload = { ...run, completedAt: markComplete ? new Date().toISOString() : null };
    const ok = await db.saveProcedureRun(payload);
    setRunSaving(false);
    if (!ok) return alert("Failed to save — check your connection.");
    setRun(payload);
    refreshOpenRuns();
    alert(markComplete ? "Verification record saved ✓" : "Progress saved — finish it later. You'll find it under Procedures and on the job.");
    if (markComplete) setView("read");
  };

  const load = useCallback(async () => {
    setLoading(true);
    try { setBooks(await db.getPlaybooks()); } catch(e) {}
    setLoading(false);
  }, []);
  const loadedRef = useRef(false);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  // Deep link: #procedures/<id> opens it; #procedures/<id>/run/<projectId>
  // goes straight into a verification run with the job already picked.
  useEffect(() => {
    if (!focusId || books.length === 0) return;
    // For a run we also need the job list loaded, or we can't preselect it
    if (runForProject && runProjects.length === 0) return;
    const hit = books.find(b => b.id === focusId);
    if (hit) {
      if (runForProject && String(runForProject).startsWith("resume:")) {
        // Reopen a saved, unfinished run exactly where it was left
        const runId = String(runForProject).slice(7);
        db.getProcedureRuns().then(list => {
          const saved = (list || []).find(r => r.id === runId);
          if (saved) { setRun({ ...saved }); setCurrent(hit); setView("run"); }
          else { alert("That saved run couldn't be found."); setCurrent(hit); setView("read"); }
        }).catch(() => { setCurrent(hit); setView("read"); });
      } else if (runForProject) {
        startRun(hit);
        const proj = runProjects.find(x => x.id === runForProject);
        if (proj) setRun(prev => prev ? { ...prev, projectId: proj.id, projectName: proj.name } : prev);
      } else {
        setCurrent(hit); setView("read");
      }
    }
    onFocusHandled?.();
  }, [focusId, runForProject, books, runProjects, onFocusHandled]);

  // Show Spanish only when it has been approved. A draft is visible to whoever
  // is reviewing it in the editor, never to the crew on site.
  const viewOf = (b, wantEs) => {
    if (!b) return b;
    if (!wantEs || b.esStatus !== "approved" || !b.es) return b;
    const es = b.es;
    return {
      ...b,
      title: es.title || b.title,
      summary: es.summary || b.summary,
      steps: (es.steps || []).length === (b.steps || []).length ? es.steps : b.steps,
      safety: (es.safety || []).length === (b.safety || []).length ? es.safety : b.safety,
      partnerNotes: (b.partnerNotes || []).map((n, i) => ({ ...n, note: (es.partnerNotes || [])[i] || n.note })),
      links: (b.links || []).map((l, i) => ({ ...l, title: (es.links || [])[i] || l.title })),
    };
  };

  const emptyBook = () => ({
    id: `pb-${Date.now()}`, title: "", serviceType: PLAYBOOK_SERVICE_TYPES[0], summary: "",
    steps: [""], safety: [""], links: [], partnerNotes: [],
    allowedEmails: [], partner: "", reportFields: [], requiredPhotos: [], suggests: [], timeBudget: "", timeCrew: "", payRate: "", payNte: "", es: null, esStatus: "none",
  });

  const startNew = () => { setForm(emptyBook()); setView("edit"); };
  const startEdit = (b) => { setForm({ ...emptyBook(), ...b }); setView("edit"); };

  const save = async () => {
    if (!form.title.trim()) return alert("Give the procedure a title.");
    const clean = {
      ...form,
      steps: form.steps.filter(s => s.trim()),
      safety: form.safety.filter(s => s.trim()),
      links: form.links.filter(l => (l.title || "").trim() || (l.url || "").trim()),
      partnerNotes: form.partnerNotes.filter(n => (n.partner || "").trim() || (n.note || "").trim()),
      suggests: form.suggests || [],
      timeBudget: form.timeBudget === "" ? null : Number(form.timeBudget),
      timeCrew: form.timeCrew === "" ? null : Number(form.timeCrew),
      payRate: form.payRate === "" ? null : Number(form.payRate),
      payNte: form.payNte === "" ? null : Number(form.payNte),
      reportFields: (form.reportFields || []).filter(f => (f.label || "").trim()),
      requiredPhotos: (form.requiredPhotos || []).filter(p => (p.label || "").trim()),
      es: form.es || null,
      esStatus: form.es ? (form.esStatus || "draft") : "none",
      allowedEmails: form.allowedEmails || [],
    };
    const ok = await db.savePlaybook(clean);
    if (!ok) return alert("Failed to save — check your connection.");
    await load();
    setCurrent(clean);
    setView("read");
  };

  const remove = async () => {
    if (!window.confirm("Delete this procedure?")) return;
    await db.deletePlaybook(form.id);
    await load();
    setView("list");
  };

  // Simple list editors
  const setListItem = (key, i, v) => setForm(p => ({ ...p, [key]: p[key].map((x, j) => j === i ? v : x) }));
  const addListItem = (key, blank) => setForm(p => ({ ...p, [key]: [...p[key], blank] }));
  const delListItem = (key, i) => setForm(p => ({ ...p, [key]: p[key].filter((_, j) => j !== i) }));

  const CATEGORIES = [
    { id: "all", label: "All", test: () => true },
    { id: "proc", label: "Procedures", test: (b) => !isReferenceDoc(b) },
    { id: "trouble", label: "Troubleshooting", test: (b) => b.serviceType === "Troubleshooting Guide" },
    { id: "training", label: "Training", test: (b) => b.serviceType === "Training" },
    { id: "ref", label: "Manuals", test: (b) => b.serviceType === "Manual / Reference" },
  ];
  const catTest = (CATEGORIES.find(x => x.id === category) || CATEGORIES[0]).test;
  const filtered = books.filter(b => {
    if (!catTest(b)) return false;
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (b.title + " " + b.serviceType + " " + (b.summary || "")).toLowerCase().includes(q);
  });

  // ── Run view — the crew ticks items on site ───────────────────────────────
  if (view === "run" && run) {
    const done = run.items.filter(i => i.checked).length;
    const pct = run.items.length ? Math.round((done / run.items.length) * 100) : 0;
    const allDone = done === run.items.length;
    return (
      <div style={{ maxWidth: 680, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => { if (window.confirm("Leave without saving this run?")) { setRun(null); setView("read"); } }}
          style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>

        <h2 style={{ fontSize: 21, fontWeight: 800, color: C.light, margin: "0 0 4px" }}>{run.playbookTitle}</h2>
        <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>Tick each item as you verify it. Anything you can't verify needs a written reason.</p>

        <div style={S.card}>
          <Field label="Which job?" required>
            <select style={S.select} value={run.projectId} onChange={e => {
              const proj = runProjects.find(x => x.id === e.target.value);
              setRun(p => ({ ...p, projectId: e.target.value, projectName: proj?.name || "" }));
            }}>
              <option value="">— select a job —</option>
              {runProjects.map(p => (
                <option key={p.id} value={p.id}>{p.name}{p.partner ? ` (${p.partner})` : ""}</option>
              ))}
            </select>
          </Field>
          <div style={{ fontSize: 12, color: C.muted }}>Performed by {run.startedBy || "—"} · started {new Date(run.startedAt).toLocaleString()}</div>
        </div>

        <div style={{ ...S.card, position: "sticky", top: 8, zIndex: 5 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: allDone ? C.success : C.light }}>{done} of {run.items.length} verified</span>
            <span style={{ fontSize: 12, color: C.muted }}>{pct}%</span>
          </div>
          <div style={{ height: 6, background: C.surface, borderRadius: 99, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${pct}%`, background: allDone ? C.success : C.gold, transition: "width .25s" }} />
          </div>
        </div>

        {(current?.safety || []).length > 0 && (
          <div style={{ ...S.card, border: `1px solid ${C.danger}44` }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.danger, marginBottom: 8 }}>⚠ Safety</div>
            {((getLang() === "es" && current.esStatus === "approved" && (current.es?.safety || []).length === (current.safety || []).length) ? current.es.safety : current.safety).map((s, i) => (
              <div key={i} style={{ fontSize: 12, color: C.light, lineHeight: 1.5, padding: "3px 0" }}>• {s}</div>
            ))}
          </div>
        )}

        <div style={S.card}>
          {run.items.map((it, i) => (
            <div key={i} style={{
              padding: "12px 0", borderBottom: i < run.items.length - 1 ? `1px solid ${C.border}33` : "none",
              background: !it.checked && (it.note || "").trim() ? `${C.warning}0d` : "transparent",
            }}>
              <div onClick={() => toggleRunItem(i)} style={{ display: "flex", gap: 12, cursor: "pointer", alignItems: "flex-start" }}>
                <span style={{
                  width: 26, height: 26, flexShrink: 0, borderRadius: 7, marginTop: 1,
                  border: it.checked ? `2px solid ${C.success}` : `2px solid ${C.border}`,
                  background: it.checked ? C.success : "transparent",
                  display: "grid", placeItems: "center",
                }}>
                  {it.checked && (
                    <svg viewBox="0 0 24 24" style={{ width: 15, height: 15 }}>
                      <path d="M20 6 9 17l-5-5" fill="none" stroke={C.bg} strokeWidth="3.4" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </span>
                <span style={{ fontSize: 13, lineHeight: 1.55, color: it.checked ? C.muted : C.light, flex: 1 }}>
                  {(getLang() === "es" && it.stepEs) ? it.stepEs : it.step}
                </span>
              </div>
              {!it.checked && (
                <input style={{ ...S.input, marginTop: 8, marginLeft: 38, width: "calc(100% - 38px)", fontSize: 12, padding: "8px 10px", boxSizing: "border-box" }}
                  value={it.note} onChange={e => setRunNote(i, e.target.value)}
                  placeholder="If you can't verify this, say why — goes in the record" />
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 40, flexWrap: "wrap" }}>
          <button onClick={() => saveRun(false)} disabled={runSaving} style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 120 }}>Save progress</button>
          <button onClick={() => saveRun(true)} disabled={runSaving} style={{ ...S.btnP, flex: 2, justifyContent: "center", minWidth: 160, opacity: runSaving ? 0.6 : 1 }}>
            {runSaving ? "Saving..." : <>{I.check} Complete Verification</>}
          </button>
          {run.completedAt && (
            <button onClick={() => generateProcedureRunPDF(run)} style={{ ...S.btnS, justifyContent: "center" }}>{I.pdf} Print</button>
          )}
        </div>
      </div>
    );
  }

  // ── Read view — what the crew sees on site ────────────────────────────────
  if (view === "read" && current) {
    const hasEs = current.esStatus === "approved" && current.es;
    const v = viewOf(current, showEs && hasEs);
    return (
      <div style={{ maxWidth: 680, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => { setView("list"); setCurrent(null); }} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: 0 }}>{v.title}</h2>
            <span style={{ ...S.badge(C.gold), marginTop: 8, display: "inline-block" }}>{current.serviceType}</span>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {hasEs && (
              <div style={{ display: "flex", borderRadius: 8, overflow: "hidden", border: `1px solid ${C.border}` }}>
                {[{ id: false, l: "EN" }, { id: true, l: "ES" }].map(o => (
                  <button key={o.l} type="button" onClick={() => setShowEs(o.id)} style={{
                    padding: "6px 12px", cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", border: "none",
                    background: showEs === o.id ? `${C.gold}22` : "transparent",
                    color: showEs === o.id ? C.gold : C.muted,
                  }}>{o.l}</button>
                ))}
              </div>
            )}
            {(current.steps || []).length > 0 && !isReferenceDoc(current) && (
              <button style={{ ...S.btnP, whiteSpace: "nowrap" }} onClick={() => startRun(current)}>{I.check} Run on a Job</button>
            )}
            {isAdmin && <button style={S.btnS} onClick={() => startEdit(current)}>{I.clip} Edit</button>}
          </div>
        </div>

        {getLang() === "es" && !hasEs && (
          <div style={{ ...S.card, background: `${C.info}12`, border: `1px solid ${C.info}44`, fontSize: 12.5, color: C.light }}>
            Este procedimiento todavía solo está en inglés. Si algo no queda claro, pregunta antes de empezar.
          </div>
        )}
        {v.summary && (
          <div style={{ ...S.card, background: C.surface, fontSize: 13, color: C.light, lineHeight: 1.6 }}>{v.summary}</div>
        )}

        {(v.safety || []).length > 0 && (
          <div style={{ ...S.card, border: `1px solid ${C.danger}44` }}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: C.danger }}>⚠ Safety — before you start</h3>
            {v.safety.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "7px 0", fontSize: 13, color: C.light, lineHeight: 1.5 }}>
                <span style={{ color: C.danger, flexShrink: 0 }}>•</span><span>{s}</span>
              </div>
            ))}
          </div>
        )}

        {(v.steps || []).length > 0 && (
          <div style={S.card}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: C.light }}>Steps</h3>
            {v.steps.map((s, i) => (
              <div key={i} style={{ display: "flex", gap: 12, padding: "9px 0", borderBottom: i < v.steps.length - 1 ? `1px solid ${C.border}33` : "none" }}>
                <span style={{ width: 24, height: 24, borderRadius: "50%", background: `${C.gold}22`, color: C.gold, fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
                <span style={{ fontSize: 13, color: C.light, lineHeight: 1.55 }}>{s}</span>
              </div>
            ))}
          </div>
        )}

        {(v.partnerNotes || []).length > 0 && (
          <div style={S.card}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: C.light }}>Partner Requirements</h3>
            {v.partnerNotes.map((n, i) => (
              <div key={i} style={{ background: C.surface, borderRadius: 8, padding: 10, marginBottom: 6 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: C.gold, marginBottom: 3 }}>{n.partner}</div>
                <div style={{ fontSize: 13, color: C.light, lineHeight: 1.5 }}>{n.note}</div>
              </div>
            ))}
          </div>
        )}

        {(v.links || []).length > 0 && (
          <div style={S.card}>
            <h3 style={{ margin: "0 0 12px", fontSize: 15, fontWeight: 700, color: C.light }}>Manuals & Links</h3>
            {v.links.map((l, i) => (
              <a key={i} href={l.url} target="_blank" rel="noopener noreferrer"
                style={{ display: "flex", alignItems: "center", gap: 10, padding: "11px 12px", background: C.surface, borderRadius: 8, marginBottom: 6, textDecoration: "none", border: `1px solid ${C.border}` }}>
                <span style={{ color: C.gold, flexShrink: 0 }}>{I.pdf}</span>
                <span style={{ fontSize: 13, color: C.light, fontWeight: 600, flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis" }}>{l.title || l.url}</span>
                <span style={{ color: C.muted, fontSize: 12 }}>↗</span>
              </a>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ── Editor ────────────────────────────────────────────────────────────────
  if (view === "edit" && form) {
    return (
      <div style={{ maxWidth: 680, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => setView(current ? "read" : "list")} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: "0 0 20px" }}>{form.title || "New Procedure"}</h2>

        <div style={S.card}>
          <Field label="Title" required><input style={S.input} value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="e.g. Inverter Not Producing — Field Diagnostic" /></Field>
          <Field label="Service Type">
            <select style={S.select} value={form.serviceType} onChange={e => setForm(p => ({ ...p, serviceType: e.target.value }))}>
              {PLAYBOOK_SERVICE_TYPES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
          <Field label="Summary"><textarea style={S.textarea} rows={2} value={form.summary} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} placeholder="One or two lines — when this procedure applies" /></Field>
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.danger }}>Safety Steps</h3>
            <button style={S.btnS} onClick={() => addListItem("safety", "")}>{I.plus} Add</button>
          </div>
          {form.safety.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={{ ...S.input, flex: 1 }} value={s} onChange={e => setListItem("safety", i, e.target.value)} placeholder="e.g. Verify AC and DC disconnects are open before opening the inverter" />
              <button onClick={() => delListItem("safety", i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Steps</h3>
            <button style={S.btnS} onClick={() => addListItem("steps", "")}>{I.plus} Add</button>
          </div>
          {form.steps.map((s, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "flex-start" }}>
              <span style={{ width: 24, height: 24, marginTop: 9, borderRadius: "50%", background: `${C.gold}22`, color: C.gold, fontSize: 12, fontWeight: 800, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{i + 1}</span>
              <textarea style={{ ...S.textarea, flex: 1, minHeight: 44 }} rows={2} value={s} onChange={e => setListItem("steps", i, e.target.value)} placeholder="What to do" />
              <button onClick={() => delListItem("steps", i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", marginTop: 9 }}>{I.trash}</button>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Partner Requirements</h3>
            <button style={S.btnS} onClick={() => addListItem("partnerNotes", { partner: "", note: "" })}>{I.plus} Add</button>
          </div>
          {form.partnerNotes.length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 10 }}>e.g. "IGS — open Scanifly on arrival and check for a form; use it for all photos"</div>}
          {form.partnerNotes.map((n, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={{ ...S.input, width: 130 }} value={n.partner} onChange={e => setListItem("partnerNotes", i, { ...n, partner: e.target.value })} placeholder="Partner" />
              <input style={{ ...S.input, flex: 1 }} value={n.note} onChange={e => setListItem("partnerNotes", i, { ...n, note: e.target.value })} placeholder="What they require" />
              <button onClick={() => delListItem("partnerNotes", i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.light }}>Scope of Work — Report Requirements</h3>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
            What the partner needs in the report for <em>this kind</em> of visit. When a tech picks this scope on the
            Service Assessment, these become its required fields and photo slots.
          </p>
          {canSeeMoney(user) && <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Contract pay ($)">
              <input style={S.input} type="number" step="1" min="0" value={form.payRate ?? ""}
                onChange={e => setForm(p => ({ ...p, payRate: e.target.value }))} placeholder="e.g. 300" />
            </Field>
            <Field label="NTE ceiling ($)">
              <input style={S.input} type="number" step="1" min="0" value={form.payNte ?? ""}
                onChange={e => setForm(p => ({ ...p, payNte: e.target.value }))} placeholder="e.g. 500" />
            </Field>
          </div>}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label="Paid time (man-hours)">
              <input style={S.input} type="number" step="0.25" min="0" value={form.timeBudget ?? ""}
                onChange={e => setForm(p => ({ ...p, timeBudget: e.target.value }))} placeholder="e.g. 2.5" />
            </Field>
            <Field label="Techs this really needs">
              <input style={S.input} type="number" min="1" max="4" value={form.timeCrew ?? ""}
                onChange={e => setForm(p => ({ ...p, timeCrew: e.target.value }))} placeholder="e.g. 2" />
            </Field>
          </div>
          {(() => {
            const tb = timeBudgetFor({ timeBudget: form.timeBudget, timeCrew: form.timeCrew }, form.timeCrew);
            if (!tb) return null;
            return (
              <div style={{ fontSize: 12, color: C.muted, marginTop: -4, marginBottom: 14, lineHeight: 1.6 }}>
                {tb.budget} man-hours with {tb.crew} tech{tb.crew > 1 ? "s" : ""} = <strong style={{ color: C.light }}>{tb.onSite} on site</strong>.
                The crew sees roughly {tb.troubleshoot} to troubleshoot, then {tb.documenting} to document.

              </div>
            );
          })()}

          <Field label="Partner this scope belongs to">
            <select style={S.select} value={form.partner || ""} onChange={e => setForm(p => ({ ...p, partner: e.target.value }))}>
              <option value="">— any partner / internal —</option>
              {partnerNames.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </Field>
          {isOwner(user) && (
            <Field label="Who can see this procedure">
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>Leave empty for everyone. Pick logins to restrict it — training material, coordinator playbooks, anything not for the whole crew. admin@ always sees it.</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8 }}>
                {(form.allowedEmails || []).map(e => (
                  <span key={e} style={{ ...S.badge(C.gold), display: "inline-flex", alignItems: "center", gap: 6 }}>{e}
                    <button type="button" onClick={() => setForm(p => ({ ...p, allowedEmails: (p.allowedEmails || []).filter(x => x !== e) }))} style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", padding: 0, fontSize: 14, lineHeight: 1 }}>×</button>
                  </span>
                ))}
                {(form.allowedEmails || []).length === 0 && <span style={{ fontSize: 12, color: C.success, fontWeight: 600 }}>Visible to everyone</span>}
              </div>
              <AccessPicker value={form.allowedEmails || []} onAdd={e => setForm(p => ({ ...p, allowedEmails: [...new Set([...(p.allowedEmails || []), e.trim().toLowerCase()])] }))} />
            </Field>
          )}

          {!isReferenceDoc(form) && (() => {
            const refs = books.filter(b => b.id !== form.id && isReferenceDoc(b));
            if (refs.length === 0) return null;
            const chosen = form.suggests || [];
            return (
              <div style={{ marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${C.border}` }}>
                <div style={S.label}>Guides to suggest with this scope</div>
                <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 8, lineHeight: 1.5 }}>
                  These are offered as one-tap additions when someone picks this scope on a job.
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {refs.map(r => {
                    const on = chosen.includes(r.id);
                    return (
                      <button key={r.id} type="button"
                        onClick={() => setForm(p => ({ ...p, suggests: on ? chosen.filter(x => x !== r.id) : [...chosen, r.id] }))}
                        style={{
                          padding: "6px 12px", borderRadius: 16, cursor: "pointer", fontSize: 12, fontFamily: "inherit",
                          border: `1px solid ${on ? C.info : C.border}`,
                          background: on ? `${C.info}22` : "transparent",
                          color: on ? C.info : C.muted,
                        }}>{on ? "✓ " : ""}{r.title}</button>
                    );
                  })}
                </div>
              </div>
            );
          })()}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 0 8px" }}>
            <div style={S.label}>Report fields</div>
            <button style={S.btnS} onClick={() => addListItem("reportFields", { id: `rf${Date.now()}`, label: "", required: false })}>{I.plus} Add</button>
          </div>
          {form.reportFields.length === 0 && <div style={{ color: C.muted, fontSize: 12, padding: "4px 0 10px" }}>None.</div>}
          {form.reportFields.map((rf, i) => (
            <div key={rf.id || i} style={{ display: "flex", gap: 8, marginBottom: 8, alignItems: "center" }}>
              <input style={{ ...S.input, flex: 1 }} value={rf.label} onChange={e => setListItem("reportFields", i, { ...rf, label: e.target.value })} placeholder="e.g. Was the system on at arrival?" />
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={!!rf.required} onChange={e => setListItem("reportFields", i, { ...rf, required: e.target.checked })} /> Required
              </label>
              <button onClick={() => delListItem("reportFields", i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
            </div>
          ))}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "14px 0 8px" }}>
            <div style={S.label}>Required photos</div>
            <button style={S.btnS} onClick={() => addListItem("requiredPhotos", { id: `rp${Date.now()}`, label: "", required: true, hint: "" })}>{I.plus} Add</button>
          </div>
          {form.requiredPhotos.length === 0 && <div style={{ color: C.muted, fontSize: 12, padding: "4px 0" }}>None.</div>}
          {form.requiredPhotos.map((ph, i) => (
            <div key={ph.id || i} style={{ padding: 9, background: C.surface, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={{ ...S.input, flex: 1 }} value={ph.label} onChange={e => setListItem("requiredPhotos", i, { ...ph, label: e.target.value })} placeholder="Photo label" />
                <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={!!ph.required} onChange={e => setListItem("requiredPhotos", i, { ...ph, required: e.target.checked })} /> Always
                </label>
                <button onClick={() => delListItem("requiredPhotos", i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
              </div>
              <input style={{ ...S.input, marginTop: 6, fontSize: 12 }} value={ph.hint || ""} onChange={e => setListItem("requiredPhotos", i, { ...ph, hint: e.target.value })} placeholder="Hint for the crew (optional)" />
            </div>
          ))}
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Manuals & Links</h3>
            <div style={{ display: "flex", gap: 6 }}>
              <label style={{ ...S.btnS, cursor: docUploading ? "wait" : "pointer", opacity: docUploading ? 0.6 : 1 }}>
                {docUploading ? "⏳ Uploading..." : <>{I.pdf} Upload file</>}
                <input type="file" accept=".pdf,image/*,.doc,.docx,.xls,.xlsx,.txt" multiple style={{ display: "none" }} disabled={docUploading}
                  onChange={async (e) => {
                    const files = Array.from(e.target.files || []);
                    e.target.value = "";
                    if (files.length === 0) return;
                    setDocUploading(true);
                    for (const file of files) {
                      try {
                        const url = await db.uploadDocument(file);
                        addListItem("links", { title: file.name.replace(/\.[^.]+$/, ""), url });
                      } catch (err) {
                        console.warn("Document upload failed:", err);
                        alert(`Couldn't upload ${file.name}. Check your connection and try again.`);
                      }
                    }
                    setDocUploading(false);
                  }} />
              </label>
              <button style={S.btnS} onClick={() => addListItem("links", { title: "", url: "" })}>{I.plus} Link</button>
            </div>
          </div>
          {form.links.length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 10 }}>Link to manufacturer manuals, spec sheets, or a Drive folder</div>}
          {form.links.map((l, i) => (
            <div key={i} style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <input style={{ ...S.input, width: 150 }} value={l.title} onChange={e => setListItem("links", i, { ...l, title: e.target.value })} placeholder="Label" />
              <input style={{ ...S.input, flex: 1 }} value={l.url} onChange={e => setListItem("links", i, { ...l, url: e.target.value })} placeholder="https://..." />
              <button onClick={() => delListItem("links", i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
            </div>
          ))}
        </div>

        {/* Spanish version. Machine-drafted, then read and approved by a
            person before the crew ever sees it. */}
        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Versión en Español</h3>
            <span style={S.badge(form.esStatus === "approved" ? C.success : form.esStatus === "draft" ? C.warning : C.muted)}>
              {form.esStatus === "approved" ? "APPROVED" : form.esStatus === "draft" ? "DRAFT — NOT SHOWN YET" : "NONE"}
            </span>
          </div>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
            Draft a translation, read it against the English, fix anything off, then approve.
            The crew only sees it once it's approved.
          </p>

          <button type="button" disabled={translating} onClick={async () => {
            const clean = { ...form, steps: form.steps.filter(x => x.trim()), safety: form.safety.filter(x => x.trim()) };
            if (!clean.title.trim() || clean.steps.length === 0) return alert("Give it a title and at least one step first.");
            setTranslating(true);
            try {
              const out = await db.translateProcedure(clean);
              setForm(p => ({
                ...p,
                steps: clean.steps, safety: clean.safety,
                es: out,
                esStatus: p.esStatus === "approved" ? "draft" : "draft",
              }));
            } catch (err) {
              alert("Translation failed: " + (err?.message || "check your connection and try again."));
            }
            setTranslating(false);
          }} style={{ ...S.btnS, width: "100%", justifyContent: "center", borderColor: C.gold, color: C.gold, marginBottom: 12, opacity: translating ? 0.6 : 1 }}>
            {translating ? "Translating..." : form.es ? "Re-draft translation" : "Draft Spanish translation"}
          </button>

          {form.es && (() => {
            const setEs = (k, v) => setForm(p => ({ ...p, es: { ...p.es, [k]: v }, esStatus: "draft" }));
            const setEsItem = (k, i, v) => setForm(p => ({ ...p, es: { ...p.es, [k]: (p.es[k] || []).map((x, j) => j === i ? v : x) }, esStatus: "draft" }));
            const Row = ({ en, es, onChange, num }) => (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.45, marginBottom: 4 }}>{num ? `${num}. ` : ""}{en}</div>
                <textarea style={{ ...S.textarea, minHeight: 40, fontSize: 13 }} rows={2} value={es || ""} onChange={e => onChange(e.target.value)} />
              </div>
            );
            return (
              <div>
                <div style={S.label}>Título</div>
                <Row en={form.title} es={form.es.title} onChange={v => setEs("title", v)} />
                {form.summary && (<><div style={S.label}>Resumen</div><Row en={form.summary} es={form.es.summary} onChange={v => setEs("summary", v)} /></>)}
                {(form.es.safety || []).length > 0 && <div style={{ ...S.label, color: C.danger }}>Seguridad</div>}
                {(form.es.safety || []).map((t, i) => (
                  <Row key={i} en={form.safety.filter(x => x.trim())[i]} es={t} onChange={v => setEsItem("safety", i, v)} />
                ))}
                {(form.es.steps || []).length > 0 && <div style={S.label}>Pasos</div>}
                {(form.es.steps || []).map((t, i) => (
                  <Row key={i} num={i + 1} en={form.steps.filter(x => x.trim())[i]} es={t} onChange={v => setEsItem("steps", i, v)} />
                ))}
                {(form.es.partnerNotes || []).length > 0 && <div style={S.label}>Notas del socio</div>}
                {(form.es.partnerNotes || []).map((t, i) => (
                  <Row key={i} en={(form.partnerNotes[i] || {}).note} es={t} onChange={v => setEsItem("partnerNotes", i, v)} />
                ))}

                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  {form.esStatus !== "approved" ? (
                    <button type="button" onClick={() => setForm(p => ({ ...p, esStatus: "approved" }))}
                      style={{ ...S.btnSuccess, flex: 1, justifyContent: "center", minWidth: 150 }}>{I.check} Approve Spanish</button>
                  ) : (
                    <button type="button" onClick={() => setForm(p => ({ ...p, esStatus: "draft" }))}
                      style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 150 }}>Unpublish Spanish</button>
                  )}
                  <button type="button" onClick={() => { if (window.confirm("Discard the Spanish version?")) setForm(p => ({ ...p, es: null, esStatus: "none" })); }}
                    style={{ ...S.btnS, borderColor: C.danger, color: C.danger }}>{I.trash}</button>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>
                  Approving doesn't save on its own — hit Save below.
                </div>
              </div>
            );
          })()}
        </div>

        <div style={{ display: "flex", gap: 8, marginBottom: 40 }}>
          <button onClick={save} style={{ ...S.btnP, flex: 1, justifyContent: "center" }}>{I.check} Save</button>
          {books.some(b => b.id === form.id) && <button onClick={remove} style={S.btnDanger}>{I.trash}</button>}
        </div>
      </div>
    );
  }

  // ── List ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{t.procedures}</h2>
          <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13 }}>What to do on site, by service type</p>
        </div>
        {isAdmin && <button style={S.btnP} onClick={startNew}>{I.plus} New Procedure</button>}
      </div>

      {/* Unfinished runs — saved with "Save progress", pick up where you left off */}
      {openRuns.length > 0 && (
        <div style={{ background: `${C.warning}14`, border: `1px solid ${C.warning}44`, borderRadius: 12, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.warning, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Unfinished — pick up where you left off</div>
          {openRuns.map(r => {
            const done = (r.items || []).filter(i => i.checked).length;
            return (
              <div key={r.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "6px 0", borderTop: `1px solid ${C.border}`, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 180 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.light }}>{r.playbookTitle}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>{r.projectName || "No job picked"} · {done}/{(r.items || []).length} done · {new Date(r.startedAt).toLocaleDateString()}</div>
                </div>
                <button type="button" style={{ ...S.btnP, padding: "6px 14px", fontSize: 12 }} onClick={() => { const pb = books.find(b => b.id === r.playbookId); if (!pb) return alert("That procedure no longer exists."); setRun({ ...r }); setCurrent(pb); setView("run"); }}>Resume</button>
              </div>
            );
          })}
        </div>
      )}

      {books.length > 0 && (
        <>
          <input style={{ ...S.input, marginBottom: 10 }} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search procedures and documents..." />
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
            {CATEGORIES.map(cat => {
              const n = books.filter(cat.test).length;
              if (cat.id !== "all" && n === 0) return null;
              const on = category === cat.id;
              return (
                <button key={cat.id} onClick={() => setCategory(cat.id)} style={{
                  padding: "6px 13px", borderRadius: 20, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
                  border: `1px solid ${on ? C.gold : C.border}`, background: on ? `${C.gold}22` : "transparent", color: on ? C.gold : C.muted,
                }}>{cat.label} ({n})</button>
              );
            })}
          </div>
        </>
      )}

      {loading ? <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading...</div> :
       books.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 50 }}>
          <div style={{ width: 50, height: 50, borderRadius: 14, background: `${C.gold}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", color: C.gold }}>{I.book}</div>
          <div style={{ color: C.muted, fontSize: 14, fontWeight: 600 }}>No procedures yet</div>
          <div style={{ color: `${C.muted}66`, fontSize: 12, marginTop: 4 }}>Add one per service type so the crew knows the drill before they arrive</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.map(b => (
            <div key={b.id} onClick={() => { setCurrent(b); setView("read"); }}
              style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, cursor: "pointer" }}>
              <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C.gold}22`, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, flexShrink: 0 }}>{I.book}</div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: 14, color: C.light }}>{b.title}</div>
                <div style={{ display: "flex", gap: 10, marginTop: 3, fontSize: 11, color: C.muted, flexWrap: "wrap" }}>
                  <span style={S.badge(C.gold)}>{b.serviceType}</span>
                  {(b.steps || []).length > 0 && <span>{b.steps.length} steps</span>}
                  {(b.links || []).length > 0 && <span>{b.links.length} link(s)</span>}
                  {b.esStatus === "approved" && <span style={S.badge(C.success)}>ES</span>}
                  {b.esStatus === "draft" && isAdmin && <span style={S.badge(C.warning)}>ES draft</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Partner Directory ──────────────────────────────────────────────────────
const SUBMISSION_METHODS = [
  { id: "app", labelKey: "methodApp" },
  { id: "portal", labelKey: "methodPortal" },
  { id: "email", labelKey: "methodEmail" },
  { id: "folder", labelKey: "methodFolder" },
];

// ─── Crew Capabilities & Service Catalog ─────────────────────────────────────
// Answers the coordinator's question: who can I send on this job?
// Scores: 0 = not trained, 1 = assist only, 2 = can perform, 3 = can lead/train.
// Certifications are NOT tracked here — they live in Safety > Credentials.
const money = (n) => (n === "" || n == null || isNaN(Number(n))) ? "—" : `$${Number(n).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const CAPABILITIES = [
  { id: "elec", label: "Licensed Electrical Work", es: "Trabajo eléctrico (licencia)" },
  { id: "pv", label: "Solar PV Install", es: "Instalación solar" },
  { id: "rr", label: "Panel R&R", es: "Quitar / reinstalar paneles" },
  { id: "inv", label: "Inverter Troubleshooting", es: "Diagnóstico de inversor" },
  { id: "ev", label: "EV Charger Install", es: "Cargador EV" },
  { id: "critter", label: "Critter Guard", es: "Malla anti-animales" },
  { id: "clean", label: "Panel Cleaning", es: "Limpieza de paneles" },
  { id: "survey", label: "Site Survey / Scanifly", es: "Levantamiento / Scanifly" },
  { id: "roof_std", label: "Roof — Standard Pitch", es: "Techo — pendiente normal" },
  { id: "roof_steep", label: "Roof — Steep / 3+ Stories", es: "Techo — empinado / 3+ pisos" },
  { id: "comm", label: "Commercial Rooftop", es: "Techo comercial" },
  { id: "lead", label: "Crew Lead", es: "Líder de cuadrilla" },
  { id: "vehicle", label: "Company Vehicle", es: "Vehículo de empresa" },
  { id: "es", label: "Bilingual (ES)", es: "Bilingüe (ES)" },
];
const SKILL_LEVELS = [
  { v: 0, label: "Not trained — do not assign", color: "#7A9E87" },
  { v: 1, label: "Assist only — pair with a 2 or 3", color: "#F5C542" },
  { v: 2, label: "Can perform unsupervised", color: "#5FD38A" },
  { v: 3, label: "Can lead the job and train others", color: "#B4F53C" },
];
const skillColor = (v) => (SKILL_LEVELS[v] || SKILL_LEVELS[0]).color;

// Starting guesses from job titles — Ivan is to verify every cell before the
// coordinator relies on this. Loaded by the "Load starter roster" button.
const SEED_CREW = [
  ["Ayan M.", "Electrician",              [3,3,3,3,3,2,1,2,3,2,2,3,2,0]],
  ["Jacob C.", "Apprentice Electrician",  [1,2,2,1,2,2,2,1,2,1,1,0,2,0]],
  ["Giancarlo P.", "Field Technician",    [0,2,3,1,1,3,3,2,3,2,2,2,2,0]],
  ["Juan G.", "Field Technician",         [0,2,3,1,1,3,3,2,3,2,1,2,2,0]],
  ["Josue", "Field Technician",           [0,2,2,0,1,2,3,1,2,1,1,0,2,0]],
  ["Diego F.", "Field Technician",        [0,2,2,0,1,2,3,1,2,1,1,0,2,0]],
  ["Cynthia", "Field Technician",         [0,1,2,0,1,2,3,2,2,0,1,0,2,0]],
  ["Jonathan Z.", "Owner",                [3,3,3,3,3,3,3,3,3,3,3,3,3,3]],
].map(([name, role, arr], i) => ({
  id: `crew-seed-${i}`, name, role,
  skills: Object.fromEntries(CAPABILITIES.map((c, k) => [c.id, arr[k]])),
  notes: "",
}));
// projectType / serviceSubtype tie each service to how jobs are typed in
// the Hub, so the job editor can suggest crew automatically.
const SEED_CATALOG = [
  ["Site Survey (TSA)", "survey", 2, 1, 2, false, "survey", "", "Photos + measurements. Can run solo."],
  ["Service Assessment", "inv", 2, 1, 1.5, false, "service", "", "Diagnostic visit. Add electrician if fault suspected."],
  ["Roof Leak Assessment", "roof_std", 2, 1, 2, false, "service", "Roof Leak", "Photo documentation is the deliverable."],
  ["Panel Removal (R&R leg 1)", "rr", 2, 2, 4, true, "removal_reinstall", "", "Coordinate with roofer. Confirm reinstall date before removal."],
  ["Panel Reinstall (R&R leg 2)", "rr", 2, 2, 5, true, "removal_reinstall", "", "New flashing + sealant. Verify production after."],
  ["Critter Guard Install", "critter", 2, 2, 4, false, "service", "General O&M", "$12/linear ft within pitch <35° and ≤3 stories."],
  ["Solar Panel Cleaning", "clean", 2, 1, 2.5, false, "service", "Panel Cleaning", "Tiered by system size — see website plans."],
  ["Solar PV Install — Residential", "pv", 2, 3, 16, true, "new_install", "", "Multi-day. Electrician required for final tie-in."],
  ["Solar PV Install — Commercial", "comm", 2, 4, 40, true, "new_install", "", "Prevailing wage may apply. Check county."],
  ["EV Charger Install — Residential", "ev", 2, 2, 4, true, "electrical", "", "Licensed electrician required. Permit + inspection."],
  ["EV Charger Install — Commercial", "ev", 3, 2, 8, true, "electrical", "", "Licensed electrician required. Load calc first."],
  ["Electrical Panel Upgrade", "elec", 3, 2, 8, true, "electrical", "", "Licensed electrician required. ComEd coordination."],
  ["Inverter Replacement", "inv", 3, 2, 4, true, "service", "Inverter Issue", "Confirm warranty/RMA before scheduling."],
  ["Commercial PM / O&M Visit", "comm", 2, 2, 8, true, "service", "System Inspection", "Partner SLA applies — check submission deadline."],
  ["Electrical Safety Inspection", "elec", 2, 1, 1, false, "service", "Electrical Issue", "Grounding, connections, conduit, breakers."],
].map(([service, capability, minLevel, minCrew, estHours, leadRequired, projectType, serviceSubtype, notes], i) => ({
  id: `svc-seed-${i}`, service, capability, minLevel, minCrew, estHours, leadRequired, projectType, serviceSubtype, notes, sortOrder: i,
}));

// Who qualifies for a catalog entry. Returns { eligible, leads, short, spof }.
function eligibleCrewFor(svc, crew) {
  if (!svc) return { eligible: [], leads: [], short: false, spof: false };
  const lvl = (p, cap) => Number((p.skills || {})[cap] || 0);
  const eligible = crew.filter(p => lvl(p, svc.capability) >= (svc.minLevel || 2));
  const leads = eligible.filter(p => lvl(p, "lead") >= 3);
  const need = svc.minCrew || 1;
  const short = eligible.length < need || (svc.leadRequired && leads.length === 0);
  const spof = !short && eligible.length === need;
  return { eligible, leads, short, spof };
}
// Best catalog match for a job: subtype match first, then project type.
function catalogMatchesFor(proj, catalog) {
  if (!proj) return [];
  const base = catalog.filter(s => !s.partner);
  const bySub = base.filter(s => s.projectType === proj.projectType && s.serviceSubtype && s.serviceSubtype === proj.serviceSubtype);
  if (bySub.length) return bySub;
  return base.filter(s => s.projectType === proj.projectType);
}

// Small module cache so the job editor doesn't refetch on every open.
const crewCache = { crew: null, catalog: null, at: 0 };
async function loadCrewData(force) {
  if (!force && crewCache.crew && Date.now() - crewCache.at < 60000) return crewCache;
  const [crew, catalog] = await Promise.all([db.getCrewSkills(), db.getServiceCatalog()]);
  Object.assign(crewCache, { crew, catalog, at: Date.now() });
  return crewCache;
}

// Shown in the job editor under "Add member": who qualifies for this job.
function CrewSuggest({ project, members, onAdd }) {
  const [data, setData] = useState(crewCache.crew ? crewCache : null);
  const [pick, setPick] = useState("");
  const loadedRef = useRef(false);
  if (!loadedRef.current) { loadedRef.current = true; loadCrewData().then(d => setData({ ...d })).catch(() => {}); }
  if (!data || !data.crew?.length || !data.catalog?.length) return null;
  const matches = catalogMatchesFor(project, data.catalog);
  if (!matches.length) return null;
  const svc = matches.find(s => s.id === pick) || matches[0];
  const { eligible, leads, short, spof } = eligibleCrewFor(svc, data.crew);
  // Members are stored by email: that is what invites go to and what the
  // access rules match. A person with no linked Hub account can still be
  // added by name, but they won't see the job until an email is set.
  const keyOf = (p) => (p.email || p.name || "").trim().toLowerCase();
  const has = (p) => (members || []).some(m => { const k = (m.name || "").trim().toLowerCase(); return k === keyOf(p) || k === (p.name || "").trim().toLowerCase(); });
  const lvl = (p) => Number((p.skills || {})[svc.capability] || 0);
  const unlinked = eligible.filter(p => !p.email);
  return (
    <div style={{ marginTop: 12, padding: "12px 14px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>Who can do this</span>
        {matches.length > 1
          ? <select value={svc.id} onChange={e => setPick(e.target.value)} style={{ ...S.select, width: "auto", padding: "4px 28px 4px 8px", fontSize: 12 }}>
              {matches.map(s => <option key={s.id} value={s.id}>{s.service}</option>)}
            </select>
          : <span style={{ fontSize: 12, color: C.light, fontWeight: 600 }}>{svc.service}</span>}
        <span style={{ fontSize: 11, color: C.muted }}>· crew of {svc.minCrew} · ~{svc.estHours}h{svc.leadRequired ? " · lead required" : ""}</span>
      </div>
      {short && <div style={{ fontSize: 12, color: C.danger, marginBottom: 6 }}>⚠ Not enough qualified people for this service type{svc.leadRequired && leads.length === 0 ? " — no one scores 3 in Crew Lead" : ""}.</div>}
      {spof && <div style={{ fontSize: 12, color: C.warning, marginBottom: 6 }}>Exactly enough people qualify — one person out and this job doesn't go.</div>}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {eligible.map(p => (
          <button key={p.id} type="button" disabled={has(p)} onClick={() => onAdd(p.email || p.name, lvl(p) >= 3 && svc.leadRequired ? "Lead" : "Team Member", p.name)}
            title={`${CAPABILITIES.find(c => c.id === svc.capability)?.label}: ${lvl(p)}${p.email ? ` · ${p.email}` : " · no Hub account linked"}`}
            style={{ padding: "6px 10px", borderRadius: 8, cursor: has(p) ? "default" : "pointer", fontSize: 12, fontFamily: "inherit", fontWeight: 600,
              border: `1px solid ${has(p) ? C.border : skillColor(lvl(p))}66`, background: has(p) ? C.card : `${skillColor(lvl(p))}18`, color: has(p) ? C.muted : C.light, opacity: has(p) ? 0.6 : 1 }}>
            {has(p) ? "✓ " : "+ "}{p.name}{!p.email && <span style={{ marginLeft: 4, color: C.warning }}>⚠</span>}
            <span style={{ marginLeft: 6, color: skillColor(lvl(p)), fontWeight: 800 }}>{lvl(p)}</span>
            {leads.includes(p) && <span style={{ marginLeft: 4, fontSize: 10, color: C.gold }}>LEAD</span>}
          </button>
        ))}
        {eligible.length === 0 && <span style={{ fontSize: 12, color: C.muted }}>No one is scored ≥{svc.minLevel} on {CAPABILITIES.find(c => c.id === svc.capability)?.label}. Update the Crew page.</span>}
      </div>
      {unlinked.length > 0 && <div style={{ fontSize: 11, color: C.warning, marginTop: 8 }}>⚠ {unlinked.map(p => p.name).join(", ")}: no Hub account linked on the Crew page — they can be added by name, but won't see the job or get an invite until their email is set.</div>}
    </div>
  );
}

function CrewView({ user, t, lang }) {
  const [tab, setTab] = useState("grid"); // grid | skills | catalog | howto
  const [crew, setCrew] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editSvc, setEditSvc] = useState(null);
  const [newName, setNewName] = useState("");
  const [newRole, setNewRole] = useState("Field Technician");
  const [profiles, setProfiles] = useState([]);
  const profRef = useRef(false);
  if (!profRef.current) {
    profRef.current = true;
    if (USE_SUPABASE && supabase) supabase.from("profiles").select("email, full_name").then(({ data }) => setProfiles((data || []).filter(x => x.email))).catch(() => {});
  }
  const es = lang === "es";
  const [savedFlash, setSavedFlash] = useState("");
  const setEmail = async (person, email) => {
    const next = { ...person, email: (email || "").trim().toLowerCase(), updatedBy: user?.name };
    setCrew(cs => cs.map(c => c.id === person.id ? next : c)); crewCache.at = 0;
    const ok = await db.saveCrewSkill(next);
    if (ok) { setSavedFlash(person.id); setTimeout(() => setSavedFlash(""), 1800); }
    else setCrew(cs => cs.map(c => c.id === person.id ? person : c)); // put it back so the screen matches the database
  };
  const capLabel = (c) => es ? c.es : c.label;

  const loadedRef = useRef(false);
  const load = useCallback(async (force) => {
    setLoading(true);
    try { const d = await loadCrewData(force); setCrew(d.crew || []); setCatalog(d.catalog || []); } catch(e) {}
    setLoading(false);
  }, []);
  if (!loadedRef.current) { loadedRef.current = true; load(true); }

  const sortedCatalog = [...catalog].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.service.localeCompare(b.service) || (a.partner || "").localeCompare(b.partner || ""));
  // Partner-specific rate rows duplicate a service; the capability grid only needs one row per service.
  const gridCatalog = sortedCatalog.filter(s => !s.partner);

  const setSkill = async (person, capId, v) => {
    const next = { ...person, skills: { ...(person.skills || {}), [capId]: v }, updatedBy: user?.name };
    setCrew(cs => cs.map(c => c.id === person.id ? next : c));
    crewCache.at = 0;
    await db.saveCrewSkill(next);
  };
  const cycle = (person, capId) => setSkill(person, capId, (Number((person.skills || {})[capId] || 0) + 1) % 4);
  const addPerson = async () => {
    const nm = newName.trim(); if (!nm) return;
    const p = { id: `crew-${Date.now()}`, name: nm, role: newRole, skills: Object.fromEntries(CAPABILITIES.map(c => [c.id, 0])), notes: "", updatedBy: user?.name };
    setCrew(cs => [...cs, p]); setNewName(""); crewCache.at = 0;
    await db.saveCrewSkill(p);
  };
  const removePerson = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the crew table? Credentials and past jobs are not affected.`)) return;
    setCrew(cs => cs.filter(c => c.id !== p.id)); crewCache.at = 0;
    await db.deleteCrewSkill(p.id);
  };
  const loadStarter = async () => {
    if (!window.confirm("Load the starter roster and service catalog? Scores are guesses from job titles — review every cell afterwards.")) return;
    setLoading(true);
    const haveCrew = new Set(crew.map(c => c.name.toLowerCase()));
    const haveSvc = new Set(catalog.map(s => s.service.toLowerCase()));
    const guessEmail = (name) => {
      const first = name.toLowerCase().split(" ")[0];
      const hit = profiles.find(pr => (pr.full_name || "").toLowerCase().startsWith(first) || (pr.email || "").toLowerCase().startsWith(first));
      return hit ? hit.email.toLowerCase() : "";
    };
    for (const p of SEED_CREW) if (!haveCrew.has(p.name.toLowerCase())) await db.saveCrewSkill({ ...p, email: guessEmail(p.name), updatedBy: user?.name });
    for (const s of SEED_CATALOG) if (!haveSvc.has(s.service.toLowerCase())) await db.saveService(s);
    await load(true);
  };
  const saveSvc = async () => {
    const s = editSvc; if (!s.service?.trim()) return alert("Service name is required.");
    const row = { ...s, minLevel: Number(s.minLevel) || 2, minCrew: Number(s.minCrew) || 1, estHours: Number(s.estHours) || 0, sortOrder: s.sortOrder ?? catalog.length };
    await db.saveService(row); crewCache.at = 0; setEditSvc(null); await load(true);
  };
  const removeSvc = async (s) => {
    if (!window.confirm(`Delete "${s.service}" from the catalog?`)) return;
    await db.deleteService(s.id); crewCache.at = 0; await load(true);
  };

  const tabs = [
    ["grid", es ? "Quién puede hacer qué" : "Who Can Do What"],
    ["skills", es ? "Capacidades" : "Crew Capabilities"],
    ["catalog", es ? "Catálogo de servicios" : "Service Catalog"],
    ["howto", es ? "Cómo programar" : "How to Schedule"],
  ];
  const cell = { padding: "8px 6px", textAlign: "center", borderBottom: `1px solid ${C.border}`, fontSize: 12 };
  const headCell = { ...cell, color: C.muted, fontSize: 10, fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase", whiteSpace: "nowrap", position: "sticky", top: 0, background: C.card, zIndex: 1 };
  const nameCell = { ...cell, textAlign: "left", whiteSpace: "nowrap", position: "sticky", left: 0, background: C.card, zIndex: 2, fontWeight: 600, color: C.light };

  const empty = !loading && crew.length === 0 && catalog.length === 0;

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>{es ? "Cuadrilla" : "Crew"}</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{es ? "¿A quién puedo mandar a este trabajo? Las certificaciones viven en Seguridad › Credenciales." : "Who can I send on this job? Certifications live in Safety › Credentials, not here."}</div>
        </div>
        <button style={S.btnS} onClick={loadStarter}>{es ? "Cargar lista inicial" : "Load starter roster"}</button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {tabs.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{ padding: "8px 14px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit",
            border: `1px solid ${tab === id ? C.gold : C.border}`, background: tab === id ? `${C.gold}22` : "transparent", color: tab === id ? C.gold : C.muted }}>{label}</button>
        ))}
      </div>

      {loading && <div style={{ color: C.muted, padding: 24 }}>Loading…</div>}
      {empty && <div style={{ ...S.card, textAlign: "center", color: C.muted }}>
        <div style={{ fontSize: 15, color: C.light, fontWeight: 600, marginBottom: 6 }}>{es ? "Todavía no hay datos de cuadrilla." : "No crew data yet."}</div>
        <div style={{ fontSize: 13, marginBottom: 14 }}>{es ? "Carga la lista inicial (8 personas, 15 servicios) y luego corrige cada puntaje." : "Load the starter roster (8 people, 15 services), then correct every score."}</div>
        <button style={S.btnP} onClick={loadStarter}>{es ? "Cargar lista inicial" : "Load starter roster"}</button>
      </div>}

      {/* Legend */}
      {!empty && tab !== "howto" && tab !== "catalog" && (
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 12, fontSize: 12, color: C.muted }}>
          {SKILL_LEVELS.map(l => <span key={l.v}><span style={{ display: "inline-block", width: 18, textAlign: "center", borderRadius: 5, background: `${l.color}22`, color: l.color, fontWeight: 800, marginRight: 6 }}>{l.v}</span>{l.label}</span>)}
        </div>
      )}

      {/* WHO CAN DO WHAT — computed */}
      {!empty && tab === "grid" && (
        <div style={{ ...S.card, padding: 0, overflow: "auto", maxHeight: "70vh" }}>
          <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 720 }}>
            <thead><tr>
              <th style={{ ...headCell, ...nameCell, zIndex: 3 }}>{es ? "Servicio" : "Service"}</th>
              <th style={headCell}>{es ? "Cuadrilla" : "Crew"}</th>
              <th style={headCell}>{es ? "Horas" : "Hours"}</th>
              {crew.map(p => <th key={p.id} style={headCell}>{p.name}</th>)}
              <th style={headCell}>{es ? "Elegibles" : "Eligible"}</th>
            </tr></thead>
            <tbody>
              {gridCatalog.map(s => {
                const { eligible, leads, short, spof } = eligibleCrewFor(s, crew);
                return (
                  <tr key={s.id}>
                    <td style={nameCell}>
                      {s.service}
                      {s.leadRequired && <span style={{ marginLeft: 6, fontSize: 9, color: C.gold, fontWeight: 800 }}>LEAD</span>}
                      <div style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>{capLabel(CAPABILITIES.find(c => c.id === s.capability) || {})} ≥{s.minLevel}</div>
                    </td>
                    <td style={cell}>{s.minCrew}</td>
                    <td style={cell}>{s.estHours}</td>
                    {crew.map(p => {
                      const v = Number((p.skills || {})[s.capability] || 0);
                      const ok = v >= s.minLevel;
                      const isLead = ok && Number((p.skills || {}).lead || 0) >= 3;
                      return <td key={p.id} style={{ ...cell, color: ok ? skillColor(v) : `${C.muted}55`, fontWeight: ok ? 800 : 400 }} title={`${p.name}: ${v}`}>{ok ? (isLead ? "★" : "✓") : "·"}</td>;
                    })}
                    <td style={{ ...cell, whiteSpace: "nowrap", color: short ? C.danger : spof ? C.warning : C.success, fontWeight: 700 }}>
                      {eligible.length}{s.leadRequired ? ` / ${leads.length} lead` : ""}{short ? " ⚠" : spof ? " !" : ""}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: "10px 14px", fontSize: 11, color: C.muted, borderTop: `1px solid ${C.border}` }}>
            ✓ {es ? "califica" : "qualifies"} · ★ {es ? "califica y puede liderar" : "qualifies and can lead"} · ⚠ {es ? "no hay suficiente gente calificada" : "not enough qualified people"} · ! {es ? "justo los necesarios — sin respaldo" : "exactly enough — no backup"}
          </div>
        </div>
      )}

      {/* CREW CAPABILITIES — editable */}
      {!empty && tab === "skills" && (
        <div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{es ? "Toca una celda para cambiar el puntaje (0 → 1 → 2 → 3). Se guarda solo. Vincula cada persona a su cuenta del Hub — así “Who can do this” los agrega al trabajo con acceso e invitación." : "Tap a cell to change the score (0 → 1 → 2 → 3). Saves automatically. Link each person to their Hub account — that is what lets “Who can do this” add them to a job with access and an invite."}</div>
          <div style={{ ...S.card, padding: 0, overflow: "auto", maxHeight: "70vh" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 900 }}>
              <thead><tr>
                <th style={{ ...headCell, ...nameCell, zIndex: 3 }}>{es ? "Nombre" : "Name"}</th>
                <th style={{ ...headCell, textAlign: "left" }}>{es ? "Puesto" : "Role"}</th>
                <th style={{ ...headCell, textAlign: "left" }}>{es ? "Cuenta del Hub" : "Hub account"}</th>
                {CAPABILITIES.map(c => <th key={c.id} style={{ ...headCell, maxWidth: 70, whiteSpace: "normal", fontSize: 9 }}>{capLabel(c)}</th>)}
                <th style={headCell}></th>
              </tr></thead>
              <tbody>
                {crew.map(p => (
                  <tr key={p.id}>
                    <td style={nameCell}>{p.name}</td>
                    <td style={{ ...cell, textAlign: "left", padding: "4px 6px" }}>
                      <input value={p.role || ""} onChange={e => { const next = { ...p, role: e.target.value }; setCrew(cs => cs.map(c => c.id === p.id ? next : c)); }}
                        onBlur={e => { crewCache.at = 0; db.saveCrewSkill({ ...p, role: e.target.value, updatedBy: user?.name }); }}
                        style={{ ...S.input, padding: "6px 8px", fontSize: 12, minWidth: 120 }} />
                    </td>
                    <td style={{ ...cell, textAlign: "left", padding: "4px 6px" }}>
                      {profiles.length > 0
                        ? <select value={p.email || ""} onChange={e => setEmail(p, e.target.value)} style={{ ...S.select, padding: "6px 28px 6px 8px", fontSize: 12, minWidth: 170, borderColor: p.email ? C.border : C.warning }}>
                            <option value="">{es ? "— sin vincular —" : "— not linked —"}</option>
                            {profiles.map(pr => <option key={pr.email} value={pr.email.toLowerCase()}>{pr.full_name ? `${pr.full_name} · ${pr.email}` : pr.email}</option>)}
                          </select>
                        : <input value={p.email || ""} placeholder="email@solarability.com" onChange={e => setCrew(cs => cs.map(c => c.id === p.id ? { ...c, email: e.target.value } : c))} onBlur={e => setEmail(p, e.target.value)}
                            style={{ ...S.input, padding: "6px 8px", fontSize: 12, minWidth: 170, borderColor: p.email ? C.border : C.warning }} />}
                      {savedFlash === p.id && <div style={{ fontSize: 10, color: C.success, fontWeight: 700, marginTop: 2 }}>Saved ✓</div>}
                    </td>
                    {CAPABILITIES.map(c => {
                      const v = Number((p.skills || {})[c.id] || 0);
                      return <td key={c.id} style={{ ...cell, padding: 3 }}>
                        <button type="button" onClick={() => cycle(p, c.id)} title={SKILL_LEVELS[v].label}
                          style={{ width: 32, height: 30, borderRadius: 7, border: `1px solid ${skillColor(v)}55`, background: `${skillColor(v)}22`, color: skillColor(v), fontWeight: 800, cursor: "pointer", fontFamily: "inherit", fontSize: 13 }}>{v}</button>
                      </td>;
                    })}
                    <td style={cell}><button onClick={() => removePerson(p)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            <input style={{ ...S.input, flex: 1, minWidth: 180 }} placeholder={es ? "Nombre (como aparece en los trabajos)" : "Name (as used on jobs)"} value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && addPerson()} />
            <input style={{ ...S.input, width: 200 }} placeholder={es ? "Puesto" : "Role"} value={newRole} onChange={e => setNewRole(e.target.value)} />
            <button style={S.btnP} onClick={addPerson}>{I.plus} {es ? "Agregar" : "Add person"}</button>
          </div>
        </div>
      )}

      {/* SERVICE CATALOG — editable */}
      {!empty && tab === "catalog" && (
        <div>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>{es ? "Horas = tiempo en sitio para la cuadrilla indicada, sin traslado." : "Hours = field time for the stated crew size, excluding travel. Project type / subtype tie each service to how jobs are typed so the job editor can suggest crew."}</div>
          <div style={{ ...S.card, padding: 0, overflow: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 820 }}>
              <thead><tr>
                {[es ? "Servicio" : "Service", es ? "Capacidad" : "Capability", es ? "Nivel" : "Min level", es ? "Cuadrilla" : "Crew", es ? "Horas" : "Hours", es ? "Líder" : "Lead", es ? "Mín $" : "Min $", "NTE", es ? "Tipo de trabajo" : "Job type", es ? "Notas" : "Notes", ""].map((h, i) => <th key={i} style={{ ...headCell, textAlign: "left" }}>{h}</th>)}
              </tr></thead>
              <tbody>
                {sortedCatalog.map(s => (
                  <tr key={s.id}>
                    <td style={{ ...cell, textAlign: "left", fontWeight: 600, color: C.light }}>{s.service}</td>
                    <td style={{ ...cell, textAlign: "left" }}>{capLabel(CAPABILITIES.find(c => c.id === s.capability) || {})}</td>
                    <td style={cell}><span style={{ color: skillColor(s.minLevel), fontWeight: 800 }}>{s.minLevel}</span></td>
                    <td style={cell}>{s.minCrew}</td>
                    <td style={cell}>{s.estHours}</td>
                    <td style={cell}>{s.leadRequired ? (es ? "Sí" : "Yes") : (es ? "No" : "No")}</td>
                    <td style={{ ...cell, color: s.needsEstimate ? C.warning : C.light, fontWeight: 700, whiteSpace: "nowrap" }}>{s.needsEstimate ? (es ? "Estimado" : "Estimate") : money(s.minCharge)}{s.partner ? <div style={{ fontSize: 9, color: C.gold, fontWeight: 600 }}>{s.partner}</div> : null}</td>
                    <td style={cell}>{money(s.nteDefault)}</td>
                    <td style={{ ...cell, textAlign: "left", color: C.muted }}>{PROJECT_TYPES.find(t => t.id === s.projectType)?.label || "—"}{s.serviceSubtype ? ` › ${s.serviceSubtype}` : ""}</td>
                    <td style={{ ...cell, textAlign: "left", color: C.muted, maxWidth: 260 }}>{s.notes}</td>
                    <td style={{ ...cell, whiteSpace: "nowrap" }}>
                      <button onClick={() => setEditSvc({ ...s })} style={{ ...S.btnS, padding: "4px 10px", fontSize: 12 }}>{es ? "Editar" : "Edit"}</button>
                      <button onClick={() => removeSvc(s)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", marginLeft: 4 }}>{I.trash}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <button style={{ ...S.btnP, marginTop: 12 }} onClick={() => setEditSvc({ id: `svc-${Date.now()}`, service: "", capability: CAPABILITIES[0].id, minLevel: 2, minCrew: 1, estHours: 2, leadRequired: false, projectType: "service", serviceSubtype: "", notes: "", sortOrder: catalog.length, partner: "", minCharge: "", nteDefault: "", needsEstimate: false, estimateWhen: "" })}>{I.plus} {es ? "Agregar servicio" : "Add service"}</button>
          {editSvc && (
            <Modal onClose={() => setEditSvc(null)}>
              <h3 style={{ margin: "0 0 16px", color: C.light }}>{editSvc.service ? (es ? "Editar servicio" : "Edit service") : (es ? "Nuevo servicio" : "New service")}</h3>
              <Field label={es ? "Servicio" : "Service type"} required><input style={S.input} value={editSvc.service} onChange={e => setEditSvc(s => ({ ...s, service: e.target.value }))} /></Field>
              <Field label={es ? "Capacidad requerida" : "Required capability"}>
                <select style={S.select} value={editSvc.capability} onChange={e => setEditSvc(s => ({ ...s, capability: e.target.value }))}>{CAPABILITIES.map(c => <option key={c.id} value={c.id}>{capLabel(c)}</option>)}</select>
              </Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label={es ? "Nivel mínimo" : "Min level"}><select style={S.select} value={editSvc.minLevel} onChange={e => setEditSvc(s => ({ ...s, minLevel: Number(e.target.value) }))}>{[1,2,3].map(v => <option key={v} value={v}>{v}</option>)}</select></Field>
                <Field label={es ? "Cuadrilla mín." : "Min crew"}><input type="number" min="1" style={S.input} value={editSvc.minCrew} onChange={e => setEditSvc(s => ({ ...s, minCrew: e.target.value }))} /></Field>
                <Field label={es ? "Horas" : "Est. hours"}><input type="number" min="0" step="0.5" style={S.input} value={editSvc.estHours} onChange={e => setEditSvc(s => ({ ...s, estHours: e.target.value }))} /></Field>
              </div>
              <Field label={es ? "Tipo de trabajo en el Hub" : "Hub job type"}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <select style={S.select} value={editSvc.projectType} onChange={e => setEditSvc(s => ({ ...s, projectType: e.target.value, serviceSubtype: e.target.value === "service" ? s.serviceSubtype : "" }))}>{PROJECT_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}</select>
                  {editSvc.projectType === "service" && <select style={S.select} value={editSvc.serviceSubtype} onChange={e => setEditSvc(s => ({ ...s, serviceSubtype: e.target.value }))}><option value="">(any service subtype)</option>{SERVICE_SUBTYPES.map(st => <option key={st} value={st}>{st}</option>)}</select>}
                </div>
              </Field>
              <label style={{ display: "flex", alignItems: "center", gap: 8, color: C.light, fontSize: 13, marginBottom: 16, cursor: "pointer" }}>
                <input type="checkbox" checked={!!editSvc.leadRequired} onChange={e => setEditSvc(s => ({ ...s, leadRequired: e.target.checked }))} /> {es ? "Requiere líder (alguien con 3 en Líder de cuadrilla)" : "Lead required (someone scoring 3 in Crew Lead)"}
              </label>
              <div style={{ borderTop: `1px solid ${C.border}`, margin: "4px 0 14px" }} />
              <div style={{ fontSize: 11, fontWeight: 700, color: C.gold, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>{es ? "Regla de cotización" : "Quote rule — what the coordinator can accept"}</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label={es ? "Socio (vacío = todos)" : "Partner (blank = default)"}><input style={S.input} value={editSvc.partner || ""} onChange={e => setEditSvc(s => ({ ...s, partner: e.target.value }))} placeholder="Omnidian" /></Field>
                <Field label={es ? "Mínimo para aceptar ($)" : "Min to accept ($)"}><input type="number" style={S.input} value={editSvc.minCharge ?? ""} onChange={e => setEditSvc(s => ({ ...s, minCharge: e.target.value }))} /></Field>
                <Field label={es ? "NTE usual ($)" : "Usual NTE ($)"}><input type="number" style={S.input} value={editSvc.nteDefault ?? ""} onChange={e => setEditSvc(s => ({ ...s, nteDefault: e.target.value }))} /></Field>
              </div>
              <label style={{ display: "flex", alignItems: "center", gap: 8, color: C.light, fontSize: 13, marginBottom: 12, cursor: "pointer" }}>
                <input type="checkbox" checked={!!editSvc.needsEstimate} onChange={e => setEditSvc(s => ({ ...s, needsEstimate: e.target.checked }))} /> {es ? "Siempre requiere estimado (nunca aceptar un NTE)" : "Always needs an estimate (never accept an NTE outright)"}
              </label>
              <Field label={es ? "Cotizar cuando…" : "Quote instead when…"}><input style={S.input} value={editSvc.estimateWhen || ""} onChange={e => setEditSvc(s => ({ ...s, estimateWhen: e.target.value }))} placeholder={es ? "más de 2 fugas, más de 3 pisos, más de 20 paneles…" : "more than 2 leaks, 3+ stories, over 20 panels, parts needed…"} /></Field>
              <Field label={es ? "Notas" : "Notes"}><textarea style={S.textarea} value={editSvc.notes || ""} onChange={e => setEditSvc(s => ({ ...s, notes: e.target.value }))} /></Field>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button style={S.btnS} onClick={() => setEditSvc(null)}>{es ? "Cancelar" : "Cancel"}</button>
                <button style={S.btnP} onClick={saveSvc}>{es ? "Guardar" : "Save"}</button>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* HOW TO SCHEDULE */}
      {tab === "howto" && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 12px", color: C.light }}>{es ? "Cómo programar un trabajo" : "How to schedule a job"}</h3>
          <ol style={{ color: C.light, fontSize: 14, lineHeight: 1.7, paddingLeft: 22, margin: 0 }}>
            <li>{es ? "Busca el tipo de servicio en el Catálogo — anota cuadrilla y horas." : "Find the service type in the Service Catalog — note crew size and hours."}</li>
            <li>{es ? "Abre Quién puede hacer qué — esa fila muestra a todos los elegibles." : "Open Who Can Do What — that row shows everyone eligible."}</li>
            <li>{es ? "Si el trabajo necesita líder, elige a alguien con ★ (3 en Líder de cuadrilla)." : "If the job needs a lead, pick someone marked ★ (3 in Crew Lead)."}</li>
            <li>{es ? "Revisa Seguridad › Credenciales — nadie sale con una tarjeta vencida." : "Check Safety › Credentials — nobody goes out on an expired card."}</li>
            <li>{es ? "Crea o abre el trabajo en Proyectos; en Miembros verás “Who can do this” con un botón por persona." : "Create or open the job in Projects; under Members you'll see “Who can do this” with a button per person."}</li>
            <li>{es ? "Asigna, guarda y manda la invitación desde Programación." : "Assign, save, and send the invite from Schedule."}</li>
          </ol>
          <div style={{ marginTop: 16, padding: 12, background: `${C.gold}11`, border: `1px solid ${C.gold}33`, borderRadius: 10, fontSize: 13, color: C.light }}>
            {es ? "Los puntajes iniciales son estimaciones por puesto. Ivan debe revisar cada celda antes de que la coordinadora dependa de esta tabla." : "Starter scores are guesses from job titles. Ivan is to review every cell before the coordinator relies on this table."}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Company Contacts ────────────────────────────────────────────────────────
// Who to call for what: insurance (COI / bonds), accounting, suppliers, OEM
// support, utilities, permits. Built so the coordinator can request a COI
// without hunting through email. Admin + coordinator only.
const CONTACT_CATEGORIES = [
  { id: "insurance", label: "Insurance & Bonds", es: "Seguros y fianzas" },
  { id: "accounting", label: "Accounting / Payroll", es: "Contabilidad / Nómina" },
  { id: "supplier", label: "Suppliers", es: "Proveedores" },
  { id: "oem", label: "OEM Support", es: "Soporte OEM" },
  { id: "utility", label: "Utilities", es: "Servicios públicos" },
  { id: "permits", label: "Permits / Inspections", es: "Permisos / Inspecciones" },
  { id: "partner", label: "Partner Contacts", es: "Contactos de socios" },
  { id: "other", label: "Other", es: "Otros" },
];
const SEED_CONTACTS = [
  { id: "ct-ins-jcins", category: "insurance", company: "JC Insurance Agency", name: "J. Cisneros", role: "Insurance agent", email: "jcisneros@jcinsagency.com", phone: "", whatFor: "Certificates of Insurance (COI), additional insured endorsements, bonds", notes: "Send the certificate holder's exact legal name + address and any required limits or endorsements. Ask for the bond quote separately." },
  { id: "ct-oem-enphase", category: "oem", company: "Enphase", name: "Installer Support", role: "", email: "", phone: "+18777974743", whatFor: "Microinverter / IQ Gateway troubleshooting, RMAs", notes: "Have the system serial and site ID ready." },
  { id: "ct-oem-solaredge", category: "oem", company: "SolarEdge", name: "Technical Support", role: "", email: "", phone: "+15104983200", whatFor: "Inverter / optimizer troubleshooting, RMAs", notes: "" },
  { id: "ct-oem-locus", category: "oem", company: "Locus / AlsoEnergy", name: "Support", role: "", email: "", phone: "+18775628736,2", whatFor: "Monitoring and communication issues", notes: "Press 2 for support." },
];

// Default COI request — the coordinator fills the holder details and sends.
function coiRequestMailto(contact, f) {
  const lines = [
    `Hi ${contact.name || contact.company || ""},`.trim(),
    "",
    "Please issue a Certificate of Insurance for SolarAbility LLC with the following:",
    "",
    `Certificate holder: ${f.holder || "[name]"}`,
    `Holder address: ${f.holderAddress || "[address]"}`,
    f.additionalInsured ? `Additional insured: ${f.additionalInsured}` : "Additional insured: [if required]",
    `Project / job: ${f.project || "[job name / address]"}`,
    f.requirements ? `Required coverage / endorsements: ${f.requirements}` : "Required coverage / endorsements: [per contract, if any]",
    f.needBy ? `Needed by: ${f.needBy}` : "",
    "",
    "Please send the certificate to " + (f.sendTo || "info@solarability.com") + (f.holderEmail ? ` and copy ${f.holderEmail}` : "") + ".",
    "",
    "Thank you,",
    f.signature || "SolarAbility LLC",
  ].filter(l => l !== "");
  const subject = `COI request — SolarAbility LLC — ${f.project || f.holder || ""}`.trim();
  return `mailto:${contact.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(lines.join("\n"))}`;
}

function CompanyContacts({ user, t, lang, canManage }) {
  const es = lang === "es";
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [cat, setCat] = useState("all");
  const [edit, setEdit] = useState(null);
  const [coiFor, setCoiFor] = useState(null);
  const [coi, setCoi] = useState({ holder: "", holderAddress: "", holderEmail: "", additionalInsured: "", project: "", requirements: "", needBy: "", sendTo: "info@solarability.com", signature: user?.name ? `${user.name}\nSolarAbility LLC` : "SolarAbility LLC" });

  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setContacts(await db.getContacts()); } catch(e) {}
    setLoading(false);
  }, []);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  const loadStarter = async () => {
    const have = new Set(contacts.map(c => (c.email || c.phone || c.company || "").toLowerCase()));
    for (const c of SEED_CONTACTS) if (!have.has((c.email || c.phone || c.company || "").toLowerCase())) await db.saveContact(c);
    await load();
  };
  const save = async () => {
    if (!edit.company?.trim() && !edit.name?.trim()) return alert(es ? "Pon al menos la empresa o el nombre." : "Enter at least a company or a name.");
    await db.saveContact({ ...edit, updatedBy: user?.name }); setEdit(null); await load();
  };
  const remove = async (c) => {
    if (!window.confirm(es ? `¿Eliminar a ${c.name || c.company}?` : `Remove ${c.name || c.company}?`)) return;
    await db.deleteContact(c.id); await load();
  };

  const q = search.trim().toLowerCase();
  const shown = contacts
    .filter(c => cat === "all" || c.category === cat)
    .filter(c => !q || [c.company, c.name, c.role, c.email, c.phone, c.whatFor, c.notes].join(" ").toLowerCase().includes(q));
  const grouped = CONTACT_CATEGORIES.map(k => ({ ...k, items: shown.filter(c => c.category === k.id) })).filter(g => g.items.length);
  const catLabel = (k) => es ? k.es : k.label;
  const telHref = (p) => `tel:${String(p || "").replace(/[^0-9+,]/g, "")}`;
  const fmtPhone = (p) => { const d = String(p || "").replace(/[^0-9]/g, "").replace(/^1/, ""); return d.length === 10 ? `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}` : p; };

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>{es ? "Contactos" : "Contacts"}</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{es ? "A quién llamar para qué — seguros, COI y fianzas, proveedores, soporte OEM." : "Who to call for what — insurance, COI and bonds, suppliers, OEM support."}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {canManage && contacts.length === 0 && !loading && <button style={S.btnS} onClick={loadStarter}>{es ? "Cargar contactos iniciales" : "Load starter contacts"}</button>}
          {canManage && <button style={S.btnP} onClick={() => setEdit({ id: `ct-${Date.now()}`, category: "insurance", company: "", name: "", role: "", email: "", phone: "", whatFor: "", notes: "" })}>{I.plus} {es ? "Agregar" : "Add contact"}</button>}
        </div>
      </div>

      <input style={{ ...S.input, marginBottom: 10 }} value={search} onChange={e => setSearch(e.target.value)} placeholder={es ? "Buscar — COI, fianza, Enphase, permisos…" : "Search — COI, bond, Enphase, permits…"} />
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 16 }}>
        {[{ id: "all", label: es ? "Todos" : "All" }, ...CONTACT_CATEGORIES.map(k => ({ id: k.id, label: catLabel(k) }))].map(k => (
          <button key={k.id} onClick={() => setCat(k.id)} style={{ padding: "6px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
            border: `1px solid ${cat === k.id ? C.gold : C.border}`, background: cat === k.id ? `${C.gold}22` : "transparent", color: cat === k.id ? C.gold : C.muted }}>{k.label}</button>
        ))}
      </div>

      {loading && <div style={{ color: C.muted, padding: 24 }}>Loading…</div>}
      {!loading && contacts.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", color: C.muted }}>
          <div style={{ fontSize: 15, color: C.light, fontWeight: 600, marginBottom: 6 }}>{es ? "Todavía no hay contactos." : "No contacts yet."}</div>
          <div style={{ fontSize: 13, marginBottom: 14 }}>{es ? "Carga los iniciales (agente de seguros, soporte OEM) y agrega el resto." : "Load the starter set (insurance agent, OEM support) and add the rest."}</div>
          {canManage && <button style={S.btnP} onClick={loadStarter}>{es ? "Cargar contactos iniciales" : "Load starter contacts"}</button>}
        </div>
      )}
      {!loading && contacts.length > 0 && grouped.length === 0 && <div style={{ color: C.muted, padding: 16 }}>{es ? "Nada coincide." : "Nothing matches."}</div>}

      {grouped.map(g => (
        <div key={g.id} style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 8 }}>{catLabel(g)}</div>
          {g.items.map(c => (
            <div key={c.id} style={{ ...S.card, padding: 16, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.light }}>{c.company || c.name}</div>
                  {(c.name || c.role) && c.company && <div style={{ fontSize: 13, color: C.muted, marginTop: 2 }}>{[c.name, c.role].filter(Boolean).join(" · ")}</div>}
                  {c.whatFor && <div style={{ fontSize: 13, color: C.gold, marginTop: 6 }}>{c.whatFor}</div>}
                  {c.notes && <div style={{ fontSize: 12, color: C.muted, marginTop: 6, whiteSpace: "pre-wrap" }}>{c.notes}</div>}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "stretch", minWidth: 200 }}>
                  {c.email && <a href={`mailto:${c.email}`} style={{ ...S.btnS, textDecoration: "none", fontSize: 12, padding: "7px 12px", justifyContent: "flex-start", gap: 8 }}>{I.mail} {c.email}</a>}
                  {c.phone && <a href={telHref(c.phone)} style={{ ...S.btnS, textDecoration: "none", fontSize: 12, padding: "7px 12px", justifyContent: "flex-start", gap: 8 }}>☎ {fmtPhone(c.phone)}</a>}
                  {c.category === "insurance" && c.email && (
                    <button type="button" onClick={() => setCoiFor(c)} style={{ ...S.btnP, fontSize: 12, padding: "7px 12px", justifyContent: "center" }}>{es ? "Pedir COI" : "Request COI"}</button>
                  )}
                  {canManage && (
                    <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                      <button onClick={() => setEdit({ ...c })} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>{es ? "Editar" : "Edit"}</button>
                      <button onClick={() => remove(c)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ))}

      {edit && (
        <Modal onClose={() => setEdit(null)}>
          <h3 style={{ margin: "0 0 16px", color: C.light }}>{es ? "Contacto" : "Contact"}</h3>
          <Field label={es ? "Categoría" : "Category"}>
            <select style={S.select} value={edit.category} onChange={e => setEdit(s => ({ ...s, category: e.target.value }))}>{CONTACT_CATEGORIES.map(k => <option key={k.id} value={k.id}>{catLabel(k)}</option>)}</select>
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label={es ? "Empresa" : "Company"}><input style={S.input} value={edit.company} onChange={e => setEdit(s => ({ ...s, company: e.target.value }))} /></Field>
            <Field label={es ? "Nombre" : "Contact name"}><input style={S.input} value={edit.name} onChange={e => setEdit(s => ({ ...s, name: e.target.value }))} /></Field>
            <Field label={es ? "Puesto" : "Role"}><input style={S.input} value={edit.role} onChange={e => setEdit(s => ({ ...s, role: e.target.value }))} placeholder="Agent, rep, inspector…" /></Field>
            <Field label={es ? "Teléfono" : "Phone"}><input style={S.input} type="tel" value={edit.phone} onChange={e => setEdit(s => ({ ...s, phone: e.target.value }))} /></Field>
          </div>
          <Field label="Email"><input style={S.input} type="email" value={edit.email} onChange={e => setEdit(s => ({ ...s, email: e.target.value }))} /></Field>
          <Field label={es ? "Para qué" : "What for"}><input style={S.input} value={edit.whatFor} onChange={e => setEdit(s => ({ ...s, whatFor: e.target.value }))} placeholder={es ? "COI, fianzas, RMAs, permisos…" : "COI, bonds, RMAs, permits…"} /></Field>
          <Field label={es ? "Notas" : "Notes"}><textarea style={S.textarea} value={edit.notes} onChange={e => setEdit(s => ({ ...s, notes: e.target.value }))} placeholder={es ? "Qué información tener lista, horarios, etc." : "What to have ready, hours, account numbers to quote, etc."} /></Field>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button style={S.btnS} onClick={() => setEdit(null)}>{es ? "Cancelar" : "Cancel"}</button>
            <button style={S.btnP} onClick={save}>{es ? "Guardar" : "Save"}</button>
          </div>
        </Modal>
      )}

      {coiFor && (
        <Modal onClose={() => setCoiFor(null)}>
          <h3 style={{ margin: "0 0 4px", color: C.light }}>{es ? "Pedir certificado de seguro" : "Request a Certificate of Insurance"}</h3>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>{es ? "Se abre un correo ya redactado para" : "Opens a pre-written email to"} <strong style={{ color: C.light }}>{coiFor.email}</strong>. {es ? "Llena lo que pide el cliente o socio." : "Fill in what the customer or partner asked for."}</div>
          <Field label={es ? "Titular del certificado (nombre legal exacto)" : "Certificate holder (exact legal name)"} required><input style={S.input} value={coi.holder} onChange={e => setCoi(s => ({ ...s, holder: e.target.value }))} placeholder="e.g. Omnidian, Inc." /></Field>
          <Field label={es ? "Dirección del titular" : "Holder address"}><input style={S.input} value={coi.holderAddress} onChange={e => setCoi(s => ({ ...s, holderAddress: e.target.value }))} /></Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label={es ? "Correo del titular (copia)" : "Holder email (cc)"}><input style={S.input} type="email" value={coi.holderEmail} onChange={e => setCoi(s => ({ ...s, holderEmail: e.target.value }))} /></Field>
            <Field label={es ? "Fecha límite" : "Needed by"}><input style={S.input} type="date" value={coi.needBy} onChange={e => setCoi(s => ({ ...s, needBy: e.target.value }))} /></Field>
          </div>
          <Field label={es ? "Trabajo / proyecto" : "Project / job"}><input style={S.input} value={coi.project} onChange={e => setCoi(s => ({ ...s, project: e.target.value }))} placeholder="Job name and address" /></Field>
          <Field label={es ? "Asegurado adicional (si lo piden)" : "Additional insured (if required)"}><input style={S.input} value={coi.additionalInsured} onChange={e => setCoi(s => ({ ...s, additionalInsured: e.target.value }))} /></Field>
          <Field label={es ? "Coberturas / endosos requeridos" : "Required coverage / endorsements"}><textarea style={{ ...S.textarea, minHeight: 60 }} value={coi.requirements} onChange={e => setCoi(s => ({ ...s, requirements: e.target.value }))} placeholder="e.g. GL $1M/$2M, auto $1M, WC statutory, waiver of subrogation, primary & non-contributory" /></Field>
          <Field label={es ? "Enviar certificado a" : "Send certificate to"}><input style={S.input} type="email" value={coi.sendTo} onChange={e => setCoi(s => ({ ...s, sendTo: e.target.value }))} /></Field>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button style={S.btnS} onClick={() => setCoiFor(null)}>{es ? "Cancelar" : "Cancel"}</button>
            <a href={coiRequestMailto(coiFor, coi)} style={{ ...S.btnP, textDecoration: "none", opacity: coi.holder ? 1 : 0.5, pointerEvents: coi.holder ? "auto" : "none" }}>{I.mail} {es ? "Abrir correo" : "Open email"}</a>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Job reference photos ────────────────────────────────────────────────────
// Pictures the coordinator attaches to a job so the crew sees them on the
// Schedule card before they arrive: the roof from the street, where the
// inverter is, the gate, the damage the homeowner sent, a sketch of the
// layout. Stored on the project (ref_photos). Separate from form photos.
function RefPhotos({ photos, onChange, canEdit, user, compact }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);
  const [open, setOpen] = useState(null);
  const list = photos || [];
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(b => b + files.length);
    let next = [...list];
    for (const file of files) {
      try {
        let f = file;
        try { f = await db.convertToJpeg(file); } catch(err) { if (err?.code === "HEIC_CONVERSION_FAILED") { alert(db.heicHelpText(file.name)); setBusy(b => b - 1); continue; } }
        const path = `job-refs/${Date.now()}-${(f.name || file.name).replace(/[^a-zA-Z0-9._-]/g, "_")}`;
        const url = await db.uploadPhoto(f, path);
        if (url && !String(url).startsWith("data:")) {
          next = [...next, { id: `rp-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, url, path, label: "", by: user?.name || "", at: new Date().toISOString() }];
          onChange(next);
        } else alert(`Upload failed for ${file.name}. Check your connection and try again.`);
      } catch(err) { console.warn("ref photo upload failed:", err); alert(`Upload failed for ${file.name}.`); }
      setBusy(b => b - 1);
    }
  };
  const setLabel = (id, label) => onChange(list.map(p => p.id === id ? { ...p, label } : p));
  const remove = (id) => { if (window.confirm("Remove this reference photo from the job?")) onChange(list.filter(p => p.id !== id)); };
  return (
    <div>
      {list.length === 0 && !canEdit && null}
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${compact ? 96 : 130}px, 1fr))`, gap: 8 }}>
        {list.map(p => (
          <div key={p.id} style={{ background: C.surface, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
            <div onClick={() => setOpen(p)} style={{ cursor: "zoom-in", height: compact ? 80 : 110, background: "#000" }}>
              <img loading="lazy" decoding="async" src={p.url} alt={p.label || ""} style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
            </div>
            {canEdit
              ? <div style={{ display: "flex", alignItems: "center", gap: 4, padding: 4 }}>
                  <input value={p.label || ""} placeholder="Label" onChange={e => setLabel(p.id, e.target.value)} style={{ ...S.input, padding: "4px 6px", fontSize: 11, flex: 1, minWidth: 0 }} />
                  <button type="button" onClick={() => remove(p.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 2, flexShrink: 0 }}>{I.trash}</button>
                </div>
              : (p.label ? <div style={{ padding: "5px 7px", fontSize: 11, color: C.light, fontWeight: 600 }}>{p.label}</div> : null)}
          </div>
        ))}
        {canEdit && (
          <div onClick={() => ref.current?.click()} style={{ border: `2px dashed ${C.border}`, borderRadius: 10, minHeight: compact ? 80 : 110, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.muted, fontSize: 12, gap: 4, padding: 8, textAlign: "center" }}>
            <span style={{ color: C.gold }}>{I.cam}</span>
            {busy > 0 ? `Uploading ${busy}…` : "Add photo"}
            <input ref={ref} type="file" accept="image/*,.heic,.heif" multiple style={{ display: "none" }} onChange={handleFiles} />
          </div>
        )}
      </div>
      {open && (
        <div onClick={() => setOpen(null)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 2000, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 16, cursor: "zoom-out" }}>
          <img loading="lazy" decoding="async" src={open.url} alt={open.label || ""} style={{ maxWidth: "100%", maxHeight: "85vh", objectFit: "contain", borderRadius: 8 }} />
          <div style={{ color: C.light, fontSize: 13, marginTop: 10, textAlign: "center" }}>{open.label || ""}{open.by ? <span style={{ color: C.muted }}> · {open.by}{open.at ? ` · ${new Date(open.at).toLocaleDateString()}` : ""}</span> : null}</div>
          <a href={open.url} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()} style={{ color: C.gold, fontSize: 12, marginTop: 6 }}>Open full size</a>
        </div>
      )}
    </div>
  );
}

// ─── Service Quotes (SQ) log ─────────────────────────────────────────────────
// Partner service quotes arrive by email or portal. The coordinator logs each
// one here, the rate rules on the Service Catalog say whether to accept it
// outright, decline it, or send an estimate, and an accepted SQ becomes a job
// with one tap. Nothing gets accepted below the minimum without a reason.
const SQ_STATUS = [
  { id: "pending", label: "Pending", color: "#F5C542" },
  { id: "accepted", label: "Accepted", color: "#5FD38A" },
  { id: "quote_sent", label: "Quote sent", color: "#6CB8F5" },
  { id: "declined", label: "Declined", color: "#F26D6D" },
];
const sqStatus = (id) => SQ_STATUS.find(s => s.id === id) || SQ_STATUS[0];

// Rate rule for a partner + service: partner-specific catalog row wins,
// then the default row for that service.
function rateRuleFor(catalog, partnerName, serviceId) {
  const same = (a, b) => (a || "").trim().toLowerCase() === (b || "").trim().toLowerCase();
  const rows = catalog.filter(s => s.id === serviceId || (serviceId && same(s.service, serviceId)));
  const svc = rows.find(s => s.id === serviceId) || rows[0];
  if (!svc) return null;
  const sameSvc = catalog.filter(s => same(s.service, svc.service));
  const specific = sameSvc.find(s => s.partner && same(s.partner, partnerName));
  const base = sameSvc.find(s => !s.partner) || svc;
  const r = specific || base;
  return { ...r, isPartnerRule: !!specific };
}
// What the rule says about an offered amount.
function sqAdvice(rule, offered) {
  if (!rule) return { verdict: "unknown", text: "No rate rule for this service yet — add one on the Service Catalog." };
  const min = Number(rule.minCharge || 0);
  const amt = Number(offered);
  if (rule.needsEstimate) return { verdict: "quote", text: `Needs an estimate${rule.estimateWhen ? ` — ${rule.estimateWhen}` : ""}. Do not accept an NTE; send a quote.${/removal|reinstall/i.test(rule.service || "") ? " Use the R&R Estimator." : ""}` };
  if (!min) return { verdict: "unknown", text: "No minimum set for this service — add one on the Service Catalog." };
  if (!offered && offered !== 0) return { verdict: "info", text: `Accept if the NTE / offered amount is at least ${money(min)}${rule.nteDefault ? ` (usual NTE ${money(rule.nteDefault)})` : ""}.${rule.estimateWhen ? ` Estimate instead when: ${rule.estimateWhen}.` : ""}` };
  if (amt >= min) return { verdict: "accept", text: `Accept — ${money(amt)} meets the ${money(min)} minimum.${rule.estimateWhen ? ` Still quote if: ${rule.estimateWhen}.` : ""}` };
  return { verdict: "decline", text: `Below minimum — ${money(amt)} offered, ${money(min)} required. Decline, or counter with a quote.` };
}

function ServiceQuotes({ user, t, lang, canManage }) {
  const es = lang === "es";
  const [quotes, setQuotes] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("pending");
  const [search, setSearch] = useState("");
  const [edit, setEdit] = useState(null);
  const [showRates, setShowRates] = useState(false);

  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [q, c, p] = await Promise.all([db.getServiceQuotes(), db.getServiceCatalog(), db.getPartners()]);
      setQuotes(q || []); setCatalog(c || []); setPartners(p || []);
    } catch(e) {}
    setLoading(false);
  }, []);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  const blank = () => ({ id: `sq-${Date.now()}`, partner: partners[0]?.name || "", sqNumber: "", client: "", address: "", serviceId: "", serviceName: "", offered: "", nte: "", status: "pending", reason: "", estimateAmount: "", receivedAt: new Date().toISOString().slice(0, 10), decidedAt: "", by: user?.name || "", notes: "", projectId: "" });

  const save = async (q) => {
    if (!q.partner || !(q.client || q.address)) return alert(es ? "Pon el socio y el cliente o la dirección." : "Enter the partner and the client or address.");
    const svc = catalog.find(s => s.id === q.serviceId);
    const row = { ...q, serviceName: svc ? svc.service : (q.serviceName || ""), by: q.by || user?.name || "" };
    await db.saveServiceQuote(row); setEdit(null); await load();
  };
  const decide = async (q, status, extra = {}) => {
    const row = { ...q, ...extra, status, decidedAt: new Date().toISOString(), by: user?.name || q.by };
    await db.saveServiceQuote(row); await load();
    return row;
  };
  const acceptAndCreate = async (q) => {
    const rule = rateRuleFor(catalog, q.partner, q.serviceId);
    const adv = sqAdvice(rule, q.offered);
    if (adv.verdict === "decline" && !window.confirm(`${adv.text}\n\nAccept anyway?`)) return;
    let reason = q.reason;
    if (adv.verdict === "decline") { reason = window.prompt("Reason for accepting below minimum (goes on the record):", q.reason || "") || ""; if (!reason) return; }
    const partnerRec = partners.find(p => (p.name || "").trim().toLowerCase() === (q.partner || "").trim().toLowerCase());
    const svc = catalog.find(s => s.id === q.serviceId);
    // Same house already has an open job? Attach the quote to it instead.
    const dup = findOpenProject(await db.getProjects(undefined, { fresh: true }), { address: q.address, client: q.client, workOrder: q.sqNumber, partner: q.partner, serviceSubtype: svc?.serviceSubtype || "", projectType: svc?.projectType || "" });
    if (dup && window.confirm(`There is already an open job at this address:\n\n${dup.name} (${(dup.status || "").replace(/_/g, " ")})\n\nOK = mark this quote accepted and open that job. Cancel = create a new job anyway.`)) {
      await db.patchProject(dup.id, (fresh) => ({ partner: fresh.partner || q.partner || "", notes: [...(fresh.notes || []), { id: Date.now(), text: `${q.partner} service quote${q.sqNumber ? ` ${q.sqNumber}` : ""} accepted at ${money(q.offered)}${q.nte ? `, NTE ${money(q.nte)}` : ""}.${q.notes ? ` Scope: ${q.notes}` : ""}`, by: user?.name || "", at: new Date().toISOString() }] }));
      await decide(q, "accepted", { reason, projectId: dup.id });
      window.location.hash = `projects/id/${encodeURIComponent(dup.id)}`;
      return;
    }
    // Create the job, typed from the catalog so the right procedure and
    // crew suggestions line up. Everything the quote knows rides along: the
    // scope notes become the first visit's crew note, the accepted amount
    // becomes the invoice amount, contact details land on the job.
    const project = {
      id: `proj-${Date.now()}`,
      name: `${q.client || q.address}${q.sqNumber ? ` — ${q.partner} ${q.sqNumber}` : ""}`.trim(),
      client: q.client || "", address: q.address || "",
      systemSize: "", status: "not_started", completion: 0,
      partner: q.partner || "",
      projectType: svc?.projectType || "service", serviceSubtype: svc?.serviceSubtype || "",
      playbookId: "", referenceIds: [], truckList: "", scheduled_at: "",
      tasks: [
        { id: Date.now() + 1, name: "Pull work order details from partner portal into this job", assigned: user?.name || "", due: "", priority: "high", done: false },
        { id: Date.now() + 2, name: "Check whether a permit is required", assigned: user?.name || "", due: "", priority: "medium", done: false },
        { id: Date.now() + 3, name: "Schedule with homeowner (Schedule tab availability + open crew)", assigned: user?.name || "", due: "", priority: "high", done: false },
        ...(partnerRec?.defaultTasks || []).map((nm, i) => ({ id: Date.now() + 10 + i, name: nm, assigned: user?.name || "", due: "", priority: "medium", done: false })),
        { id: Date.now() + 50, name: `Invoice ${q.partner}${q.offered ? ` — ${money(q.offered)}` : ""}`, assigned: user?.name || "", due: "", priority: "medium", done: false },
      ],
      materials: [], members: [],
      clientPhone: q.phone || q.clientPhone || "", clientEmail: q.email || q.clientEmail || "",
      notes: [{ id: Date.now() + 100, text: `Created from ${q.partner} service quote${q.sqNumber ? ` ${q.sqNumber}` : ""}. Accepted at ${money(q.offered)}${q.nte ? `, NTE ${money(q.nte)}` : ""}.${reason ? ` Note: ${reason}` : ""}${q.notes ? `\nScope from quote: ${q.notes}` : ""}`, by: user?.name || "", at: new Date().toISOString() }],
      visits: [{ id: `v${Date.now()}`, label: "Visit 1", scheduled_at: "", uidCrew: "", uidOwner: "", seq: 0, invited: [], cancelled: false,
        notes: [q.notes, q.sqNumber ? `${q.partner} ${q.sqNumber}` : ""].filter(Boolean).join(" — ") }],
      invoice: q.offered ? { amount: String(q.offered), number: "" } : null,
      scope: [q.serviceName || svc?.service || "", q.notes || ""].filter(Boolean).join(" — "),
      workOrder: q.sqNumber || "",
      created_by: user?.name || "", created_at: new Date().toISOString(),
    };
    await db.saveProject(project);
    await decide(q, "accepted", { reason, projectId: project.id });
    // Straight into the job to set the date and send the invite
    window.location.hash = `projects/id/${encodeURIComponent(project.id)}`;
  };

  const q = search.trim().toLowerCase();
  const shown = quotes
    .filter(x => filter === "all" || x.status === filter)
    .filter(x => !q || [x.partner, x.sqNumber, x.client, x.address, x.serviceName, x.notes].join(" ").toLowerCase().includes(q))
    .sort((a, b) => new Date(b.receivedAt || b.createdAt || 0) - new Date(a.receivedAt || a.createdAt || 0));
  const counts = SQ_STATUS.reduce((a, s) => { a[s.id] = quotes.filter(x => x.status === s.id).length; return a; }, {});
  const partnerNames = [...new Set([...partners.map(p => p.name), ...quotes.map(x => x.partner)].filter(Boolean))];
  const ratesForPartner = (name) => catalog.filter(s => (s.minCharge || s.needsEstimate || s.nteDefault)).filter(s => !s.partner || (s.partner || "").toLowerCase() === (name || "").toLowerCase());

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>{es ? "Cotizaciones de servicio" : "Service Quotes"}</h2>
          <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{es ? "Registra cada SQ, decide con la tarifa mínima, y conviértela en trabajo." : "Log every partner SQ, decide against the minimum rate, and turn accepted ones into jobs."}</div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button style={S.btnS} onClick={() => setShowRates(v => !v)}>{showRates ? (es ? "Ocultar tarifas" : "Hide rate card") : (es ? "Ver tarifas" : "Rate card")}</button>
          {canManage && <button style={S.btnP} onClick={() => setEdit(blank())}>{I.plus} {es ? "Nueva SQ" : "Log a quote"}</button>}
        </div>
      </div>

      {/* Rate card — the minimum to accept without an estimate */}
      {showRates && (
        <div style={{ ...S.card, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{es ? "Tarifa mínima por servicio" : "Minimum to accept, by service"}</h3>
            <a href="#crew" onClick={(ev) => goHash(ev, "crew")} style={{ fontSize: 12, color: C.gold }}>{es ? "Editar en Cuadrilla › Catálogo ›" : "Edit on Crew › Service Catalog ›"}</a>
          </div>
          {ratesForPartner("").length === 0 && catalog.every(s => !s.minCharge && !s.needsEstimate) && <div style={{ fontSize: 13, color: C.muted }}>{es ? "Todavía no hay tarifas. Agrega Mínimo, NTE y “Requiere estimado” en el Catálogo de servicios." : "No rates set yet. Add Min charge, NTE and “Needs estimate” to each service on the Service Catalog (Crew page)."}</div>}
          <div style={{ overflow: "auto" }}>
            <table style={{ borderCollapse: "collapse", width: "100%", minWidth: 640 }}>
              <thead><tr>{[es ? "Servicio" : "Service", es ? "Socio" : "Partner", es ? "Mínimo" : "Min to accept", "NTE", es ? "Estimado" : "Estimate?", es ? "Cuándo cotizar" : "Quote when"].map((h, i) => <th key={i} style={{ padding: "6px 8px", textAlign: "left", fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` }}>{h}</th>)}</tr></thead>
              <tbody>
                {catalog.filter(s => s.minCharge || s.needsEstimate || s.nteDefault).sort((a, b) => a.service.localeCompare(b.service) || (a.partner || "").localeCompare(b.partner || "")).map(s => (
                  <tr key={s.id}>
                    <td style={{ padding: "7px 8px", fontSize: 13, color: C.light, fontWeight: 600, borderBottom: `1px solid ${C.border}` }}>{s.service}</td>
                    <td style={{ padding: "7px 8px", fontSize: 12, color: s.partner ? C.gold : C.muted, borderBottom: `1px solid ${C.border}` }}>{s.partner || (es ? "todos" : "default")}</td>
                    <td style={{ padding: "7px 8px", fontSize: 13, color: C.light, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>{money(s.minCharge)}</td>
                    <td style={{ padding: "7px 8px", fontSize: 13, color: C.light, borderBottom: `1px solid ${C.border}` }}>{money(s.nteDefault)}</td>
                    <td style={{ padding: "7px 8px", fontSize: 12, color: s.needsEstimate ? C.warning : C.success, fontWeight: 700, borderBottom: `1px solid ${C.border}` }}>{s.needsEstimate ? (es ? "Siempre" : "Always") : (es ? "No" : "No")}</td>
                    <td style={{ padding: "7px 8px", fontSize: 12, color: C.muted, borderBottom: `1px solid ${C.border}`, maxWidth: 280 }}>{s.estimateWhen || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 10 }}>
        {[{ id: "pending", label: es ? "Pendientes" : "Pending" }, { id: "accepted", label: es ? "Aceptadas" : "Accepted" }, { id: "quote_sent", label: es ? "Cotización enviada" : "Quote sent" }, { id: "declined", label: es ? "Rechazadas" : "Declined" }, { id: "all", label: es ? "Todas" : "All" }].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{ padding: "6px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit",
            border: `1px solid ${filter === f.id ? C.gold : C.border}`, background: filter === f.id ? `${C.gold}22` : "transparent", color: filter === f.id ? C.gold : C.muted }}>
            {f.label}{f.id !== "all" && counts[f.id] ? ` · ${counts[f.id]}` : ""}
          </button>
        ))}
      </div>
      <input style={{ ...S.input, marginBottom: 14 }} value={search} onChange={e => setSearch(e.target.value)} placeholder={es ? "Buscar — SQ #, cliente, dirección…" : "Search — SQ #, client, address…"} />

      {loading && <div style={{ color: C.muted, padding: 24 }}>Loading…</div>}
      {!loading && shown.length === 0 && <div style={{ ...S.card, color: C.muted, textAlign: "center" }}>{filter === "pending" ? (es ? "Nada pendiente." : "Nothing pending.") : (es ? "Nada aquí." : "Nothing here.")}</div>}

      {shown.map(x => {
        const st = sqStatus(x.status);
        const rule = rateRuleFor(catalog, x.partner, x.serviceId);
        const adv = sqAdvice(rule, x.offered === "" ? null : x.offered);
        const advColor = adv.verdict === "accept" ? C.success : adv.verdict === "decline" ? C.danger : adv.verdict === "quote" ? C.warning : C.muted;
        return (
          <div key={x.id} style={{ ...S.card, padding: 16, marginBottom: 10, borderLeft: `3px solid ${st.color}` }}>
            <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
              <div style={{ flex: 1, minWidth: 240 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={S.badge(st.color)}>{st.label.toUpperCase()}</span>
                  <span style={{ fontSize: 15, fontWeight: 700, color: C.light }}>{x.client || x.address}</span>
                  <span style={{ fontSize: 12, color: C.gold, fontWeight: 600 }}>{x.partner}{x.sqNumber ? ` · ${x.sqNumber}` : ""}</span>
                </div>
                {x.client && x.address && <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>{x.address}</div>}
                <div style={{ fontSize: 13, color: C.light, marginTop: 6 }}>
                  {x.serviceName || (es ? "Servicio no indicado" : "Service not set")} · {es ? "Ofrecido" : "Offered"} <strong>{money(x.offered)}</strong>{x.nte ? ` · NTE ${money(x.nte)}` : ""}{x.estimateAmount ? ` · ${es ? "Cotizado" : "Quoted"} ${money(x.estimateAmount)}` : ""}
                </div>
                {x.status === "pending" && <div style={{ fontSize: 12, color: advColor, marginTop: 6, fontWeight: 600 }}>{adv.text}</div>}
                {x.reason && <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{es ? "Motivo" : "Reason"}: {x.reason}</div>}
                {x.notes && <div style={{ fontSize: 12, color: C.muted, marginTop: 4, whiteSpace: "pre-wrap" }}>{x.notes}</div>}
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
                  {es ? "Recibida" : "Received"} {x.receivedAt ? new Date(x.receivedAt + (x.receivedAt.length === 10 ? "T12:00:00" : "")).toLocaleDateString() : "—"}{x.decidedAt ? ` · ${es ? "decidida" : "decided"} ${new Date(x.decidedAt).toLocaleDateString()}` : ""}{x.by ? ` · ${x.by}` : ""}
                  {x.projectId && <> · <a href={`#schedule/${encodeURIComponent(x.projectId)}`} onClick={(ev) => goHash(ev, `schedule/${encodeURIComponent(x.projectId)}`)} style={{ color: C.gold }}>{es ? "ver trabajo" : "open job"} ›</a></>}
                </div>
              </div>
              {canManage && (
                <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 150 }}>
                  {x.status === "pending" && <>
                    <button style={{ ...S.btnP, fontSize: 12, padding: "7px 12px", justifyContent: "center" }} onClick={() => acceptAndCreate(x)}>{es ? "Aceptar → crear trabajo" : "Accept → create job"}</button>
                    <button style={{ ...S.btnS, fontSize: 12, padding: "7px 12px", justifyContent: "center", color: C.info, borderColor: `${C.info}66` }} onClick={async () => { const amt = window.prompt(es ? "Monto cotizado ($):" : "Quoted amount ($):", x.estimateAmount || ""); if (amt === null) return; await decide(x, "quote_sent", { estimateAmount: amt }); }}>{es ? "Cotización enviada" : "Quote sent"}</button>
                    <button style={{ ...S.btnS, fontSize: 12, padding: "7px 12px", justifyContent: "center", color: C.danger, borderColor: `${C.danger}66` }} onClick={async () => { const r = window.prompt(es ? "Motivo del rechazo:" : "Reason for declining:", adv.verdict === "decline" ? `Below ${money(rule?.minCharge)} minimum` : ""); if (r === null) return; await decide(x, "declined", { reason: r }); }}>{es ? "Rechazar" : "Decline"}</button>
                  </>}
                  {x.status === "quote_sent" && <>
                    <button style={{ ...S.btnP, fontSize: 12, padding: "7px 12px", justifyContent: "center" }} onClick={() => acceptAndCreate({ ...x, offered: x.estimateAmount || x.offered })}>{es ? "Aprobada → crear trabajo" : "Approved → create job"}</button>
                    <button style={{ ...S.btnS, fontSize: 12, padding: "7px 12px", justifyContent: "center", color: C.danger, borderColor: `${C.danger}66` }} onClick={async () => { const r = window.prompt(es ? "Motivo:" : "Reason:", "Customer / partner did not approve the quote"); if (r === null) return; await decide(x, "declined", { reason: r }); }}>{es ? "No aprobada" : "Not approved"}</button>
                  </>}
                  <button style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} onClick={() => setEdit({ ...x })}>{es ? "Editar" : "Edit"}</button>
                  {x.status !== "pending" && <button style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12, fontFamily: "inherit" }} onClick={() => decide(x, "pending", { decidedAt: "" })}>{es ? "Reabrir" : "Reopen"}</button>}
                </div>
              )}
            </div>
          </div>
        );
      })}

      {edit && (() => {
        const rule = rateRuleFor(catalog, edit.partner, edit.serviceId);
        const adv = sqAdvice(rule, edit.offered === "" ? null : edit.offered);
        const advColor = adv.verdict === "accept" ? C.success : adv.verdict === "decline" ? C.danger : adv.verdict === "quote" ? C.warning : C.muted;
        return (
          <Modal onClose={() => setEdit(null)}>
            <h3 style={{ margin: "0 0 16px", color: C.light }}>{es ? "Cotización de servicio" : "Service quote"}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Field label={es ? "Socio" : "Partner"} required>
                <input list="sq-partners" style={S.input} value={edit.partner} onChange={e => setEdit(s => ({ ...s, partner: e.target.value }))} />
                <datalist id="sq-partners">{partnerNames.map(n => <option key={n} value={n} />)}</datalist>
              </Field>
              <Field label={es ? "SQ / orden #" : "SQ / ticket #"}><input style={S.input} value={edit.sqNumber} onChange={e => setEdit(s => ({ ...s, sqNumber: e.target.value }))} /></Field>
              <Field label={es ? "Cliente" : "Client / homeowner"}><input style={S.input} value={edit.client} onChange={e => setEdit(s => ({ ...s, client: e.target.value }))} /></Field>
              <Field label={es ? "Dirección" : "Address"}><input style={S.input} value={edit.address} onChange={e => setEdit(s => ({ ...s, address: e.target.value }))} /></Field>
            </div>
            <Field label={es ? "Servicio" : "Service"}>
              <select style={S.select} value={edit.serviceId} onChange={e => setEdit(s => ({ ...s, serviceId: e.target.value }))}>
                <option value="">{es ? "— elegir —" : "— pick —"}</option>
                {[...new Map(catalog.filter(s => !s.partner).map(s => [s.service, s])).values()].map(s => <option key={s.id} value={s.id}>{s.service}</option>)}
              </select>
            </Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label={es ? "Ofrecido ($)" : "Offered / NTE ($)"}><input type="number" style={S.input} value={edit.offered} onChange={e => setEdit(s => ({ ...s, offered: e.target.value }))} /></Field>
              <Field label={es ? "NTE ($)" : "NTE cap ($)"}><input type="number" style={S.input} value={edit.nte} onChange={e => setEdit(s => ({ ...s, nte: e.target.value }))} /></Field>
              <Field label={es ? "Recibida" : "Received"}><input type="date" style={S.input} value={edit.receivedAt} onChange={e => setEdit(s => ({ ...s, receivedAt: e.target.value }))} /></Field>
            </div>
            <div style={{ fontSize: 13, color: advColor, fontWeight: 600, padding: "10px 12px", background: `${advColor}14`, border: `1px solid ${advColor}44`, borderRadius: 10, marginBottom: 16 }}>{adv.text}{rule?.isPartnerRule ? ` (${edit.partner} rate)` : ""}</div>
            <Field label={es ? "Notas" : "Notes"}><textarea style={{ ...S.textarea, minHeight: 60 }} value={edit.notes} onChange={e => setEdit(s => ({ ...s, notes: e.target.value }))} placeholder={es ? "Qué pide el socio, alcance, acceso…" : "What the partner asked for, scope, access…"} /></Field>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button style={S.btnS} onClick={() => setEdit(null)}>{es ? "Cancelar" : "Cancel"}</button>
              <button style={S.btnP} onClick={() => save(edit)}>{es ? "Guardar" : "Save"}</button>
            </div>
          </Modal>
        );
      })()}
    </div>
  );
}

// ─── R&R Estimator ───────────────────────────────────────────────────────────
// Solar panel removal & reinstallation pricing, per the R&R estimator spec
// (Oct 12, 2026). Customer price from the tier table; internal cost from the
// man-hour model; margin and validation flags. Money logins only.
const RR_TIERS = {
  standard:  { label: "Standard",  materials: 105, removal: 92, reinstall: 169, electrical: 30, note: "Insurance carriers, direct retail homeowners" },
  wholesale: { label: "Wholesale", materials: 84,  removal: 75, reinstall: 147, electrical: 24, note: "Roofing contractors and GCs who manage the customer" },
  reduced:   { label: "Reduced",   materials: 80,  removal: 66, reinstall: 132, electrical: 22, note: "Volume / repeat contractor relationships" },
  custom:    { label: "Custom",    materials: 89,  removal: 80, reinstall: 156, electrical: 25, note: "Any override rate — split proportionally across the four lines" },
};
const RR_LINE_KEYS = ["materials", "removal", "reinstall", "electrical"];
const RR_LINE_LABELS = { materials: "Materials", removal: "Removal", reinstall: "Reinstallation", electrical: "Electrical" };
function rrTierRates(tier, customPerPanel) {
  const base = RR_TIERS[tier] || RR_TIERS.standard;
  if (tier !== "custom") return { ...base };
  // Custom: split the target per-panel rate in the Standard proportions so the four lines sum to it
  const target = Number(customPerPanel) || 350;
  if (target === 350) return { ...RR_TIERS.custom, label: "Custom-350" }; // the split the spec publishes
  const std = RR_TIERS.standard; const sum = std.materials + std.removal + std.reinstall + std.electrical;
  const r = { label: `Custom-${target}` };
  let acc = 0;
  RR_LINE_KEYS.forEach((k, i) => {
    if (i < RR_LINE_KEYS.length - 1) { r[k] = Math.round(target * std[k] / sum); acc += r[k]; }
    else r[k] = Math.round((target - acc) * 100) / 100; // last line absorbs rounding
  });
  return r;
}
// All numbers the spec defines, in one place.
function rrEstimate(inp) {
  const p = Math.max(0, Number(inp.panels) || 0);
  const a = Math.max(1, Number(inp.arrays) || 1);
  const scope = inp.scope || "full"; // full | removal | reinstall
  const crew = Math.max(1, Number(inp.crew) || 4);
  const oneWayMin = Math.max(0, Number(inp.oneWayMin) || 0);
  const roundTripHrs = (oneWayMin * 2) / 60;
  const rates = rrTierRates(inp.tier, inp.customRate);
  const includes = { materials: scope !== "removal", removal: scope !== "reinstall", reinstall: scope !== "removal", electrical: true };

  // ── Price
  const lines = RR_LINE_KEYS.filter(k => includes[k]).map(k => ({ key: k, label: RR_LINE_LABELS[k], perPanel: rates[k], total: Math.round(rates[k] * p * 100) / 100 }));
  const linesTotal = lines.reduce((s, l) => s + l.total, 0);
  const steepPanels = Math.min(p, Math.max(0, Number(inp.steepPanels) || 0));
  const travelFee = oneWayMin <= 45 ? 0 : oneWayMin <= 90 ? 600 : 1400;
  const adders = [
    inp.permit ? { label: "Permitting & Inspection Coordination", total: 400 } : null,
    steepPanels > 0 ? { label: `Steep pitch adder (${steepPanels} panel${steepPanels === 1 ? "" : "s"} over 35° × $40)`, total: steepPanels * 40 } : null,
    travelFee > 0 ? { label: `Travel & Mobilization (${oneWayMin} min one way)`, total: travelFee } : null,
  ].filter(Boolean);
  const addersTotal = adders.reduce((s, x) => s + x.total, 0);
  let price = linesTotal + addersTotal;
  let floorApplied = false;
  if (scope === "removal" && p < 20 && price < 2400) { price = 2400; floorApplied = true; }
  const perPanelRate = RR_LINE_KEYS.filter(k => includes[k]).reduce((s, k) => s + rates[k], 0);

  // ── Cost
  // Spec writes the array premiums as 1.33 / 2.67 — they are 4/3 and 8/3 (1 and 2 clock hours on a 4-man crew)
  const removalMH = includes.removal ? 0.91 * p + (a - 1) * (4 / 3) : 0;
  const reinstallMH = includes.reinstall ? 1.00 * p + (a - 1) * (8 / 3) : 0;
  const workingMH = removalMH + reinstallMH;
  const trips = scope === "full" ? 2 : 1;
  const travelMH = roundTripHrs * crew * trips;
  const fieldMH = workingMH + travelMH;
  const electricalHrs = 4 + (a - 1) * (4 / 3);
  const permitCost = inp.permit ? 4 * 60 : 0;
  const materialRate = inp.materialRate !== "" && inp.materialRate != null ? Number(inp.materialRate) : (scope === "removal" ? 0 : (inp.reuseRail ? 33 : 85));
  const materialCost = materialRate * p;
  const vehicle = oneWayMin > 90 ? 500 : 350;
  const laborCost = fieldMH * 50;
  const electricalCost = electricalHrs * 60;
  const cost = laborCost + electricalCost + permitCost + materialCost + vehicle;
  const margin = price > 0 ? (price - cost) / price : 0;
  const priceAt = (m) => cost / (1 - m);

  // ── Validation
  const flags = [];
  const perPanelWorking = p ? workingMH / p : 0;
  if (p > 0) {
    if (scope === "full" && (perPanelWorking < 1.5 || perPanelWorking > 2.5)) flags.push(`Working labor ${perPanelWorking.toFixed(2)} MH/panel — benchmark 1.5–2.5`);
    if (scope === "reinstall" && (perPanelWorking < 1.0 || perPanelWorking > 1.1)) flags.push(`Reinstall labor ${perPanelWorking.toFixed(2)} MH/panel — benchmark 1.0–1.1`);
    if (scope === "removal" && (perPanelWorking < 0.4 || perPanelWorking > 0.65)) flags.push(`Removal labor ${perPanelWorking.toFixed(2)} MH/panel — benchmark 0.4–0.65`);
    const cpp = cost / p;
    if (scope === "full" && (cpp < 180 || cpp > 290)) flags.push(`Fully loaded cost $${Math.round(cpp)}/panel — benchmark $180–290`);
  }
  if (margin < 0.20) flags.push(`Margin ${(margin * 100).toFixed(0)}% is under 20% — do not quote at this tier without review`);
  if (a > 1 && a > p / 3) flags.push(`${a} arrays for ${p} panels — check the array count`);

  return { p, a, scope, crew, rates, lines, linesTotal, adders, addersTotal, price, floorApplied, perPanelRate,
    removalMH, reinstallMH, workingMH, travelMH, fieldMH, electricalHrs, trips, roundTripHrs,
    laborCost, electricalCost, permitCost, materialRate, materialCost, vehicle, cost, margin, priceAt, flags, perPanelWorking };
}

function RREstimator({ user, t, lang }) {
  const es = lang === "es";
  const [inp, setInp] = useState({ panels: "", arrays: "1", tier: "standard", customRate: "350", scope: "full", permit: true, steepPanels: "", oneWayMin: "30", crew: "4", reuseRail: false, materialRate: "", client: "", address: "" });
  const set = (k, v) => setInp(s => ({ ...s, [k]: v }));
  const r = rrEstimate(inp);
  const [projects, setProjects] = useState([]);
  const [projId, setProjId] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState([]);          // standalone estimates waiting on approval
  const [currentId, setCurrentId] = useState("");  // which saved estimate is loaded in the form
  const [estNotes, setEstNotes] = useState("");
  const [estFilter, setEstFilter] = useState("pending");
  const loadedRef = useRef(false);
  if (!loadedRef.current) {
    loadedRef.current = true;
    db.getProjects().then(list => setProjects((list || []).filter(p => p.status !== "completed"))).catch(() => {});
    db.getRREstimates().then(list => setSaved(list || [])).catch(() => {});
  }
  const fmt = (n) => "$" + (Math.round(Number(n) || 0)).toLocaleString();
  const fmt2 = (n) => "$" + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const pct = (m) => `${(m * 100).toFixed(0)}%`;
  const hasInput = r.p > 0;

  const quoteText = () => {
    const L = [];
    L.push(`SolarAbility LLC — Solar Panel ${r.scope === "full" ? "Removal & Reinstallation" : r.scope === "removal" ? "Removal" : "Reinstallation"} Estimate`);
    if (inp.client || inp.address) L.push([inp.client, inp.address].filter(Boolean).join(" — "));
    L.push(`${r.p} panels${r.a > 1 ? `, ${r.a} arrays` : ""}`);
    L.push("");
    r.lines.forEach(l => L.push(`${l.label.padEnd(36)} ${fmt2(l.total)}`));
    r.adders.forEach(x => L.push(`${x.label.padEnd(36)} ${fmt2(x.total)}`));
    if (r.floorApplied) L.push(`${"Minimum job charge".padEnd(36)} ${fmt2(2400)}`);
    L.push("".padEnd(48, "-"));
    L.push(`${"TOTAL".padEnd(36)} ${fmt2(r.price)}`);
    L.push("");
    L.push("20% deposit to schedule; balance due on completion.");
    return L.join("\n");
  };
  const copyQuote = async () => { try { await navigator.clipboard.writeText(quoteText()); alert("Quote copied — paste into QuickBooks or an email."); } catch(e) { window.prompt("Copy this:", quoteText()); } };
  const saveToJob = async () => {
    if (!projId) return alert(es ? "Elige un trabajo." : "Pick a job to save this estimate on.");
    setSaving(true);
    const estimate = { ...inp, panels: r.p, arrays: r.a, price: Math.round(r.price * 100) / 100, cost: Math.round(r.cost * 100) / 100, margin: Math.round(r.margin * 1000) / 1000,
      lines: r.lines, adders: r.adders, floorApplied: r.floorApplied, fieldMH: Math.round(r.fieldMH * 100) / 100, electricalHrs: Math.round(r.electricalHrs * 100) / 100,
      savedBy: user?.name || "", savedAt: new Date().toISOString(), quoteText: quoteText() };
    const ok = await db.saveProjectEstimate(projId, estimate);
    setSaving(false);
    alert(ok ? "Estimate saved on the job (money view only)." : "Could not save — is the project_finance.estimate column added?");
  };

  // Snapshot of the current form + numbers, the same shape saveToJob writes.
  const snapshot = () => ({ ...inp, panels: r.p, arrays: r.a, price: Math.round(r.price * 100) / 100, cost: Math.round(r.cost * 100) / 100, margin: Math.round(r.margin * 1000) / 1000,
    lines: r.lines, adders: r.adders, floorApplied: r.floorApplied, fieldMH: Math.round(r.fieldMH * 100) / 100, electricalHrs: Math.round(r.electricalHrs * 100) / 100,
    savedBy: user?.name || "", savedAt: new Date().toISOString(), quoteText: quoteText() });
  const saveEstimate = async () => {
    if (!hasInput) return alert(es ? "Pon el número de paneles." : "Enter the panel count first.");
    if (!inp.client && !inp.address) return alert(es ? "Pon cliente o dirección para poder encontrarla después." : "Add a client name or address so you can find it later.");
    setSaving(true);
    try {
      const existing = saved.find(x => x.id === currentId);
      const est = {
        id: currentId || `rre-${Date.now()}`, client: inp.client, address: inp.address,
        status: existing?.status || "pending", price: Math.round(r.price * 100) / 100, cost: Math.round(r.cost * 100) / 100, margin: Math.round(r.margin * 1000) / 1000,
        inputs: { ...inp }, estimate: snapshot(), notes: estNotes, projectId: existing?.projectId || "",
        createdBy: existing?.createdBy || user?.email || "", createdAt: existing?.createdAt,
      };
      await db.saveRREstimate(est);
      setCurrentId(est.id);
      setSaved(prev => { const i = prev.findIndex(x => x.id === est.id); const next = { ...est, updatedAt: new Date().toISOString(), createdAt: est.createdAt || new Date().toISOString() }; if (i >= 0) { const c = [...prev]; c[i] = next; return c; } return [next, ...prev]; });
      alert(es ? "Estimado guardado." : "Estimate saved — it's in the list below until the customer decides.");
    } catch(e) { alert(`Could not save: ${e.message || e}\n\nRun RR_ESTIMATES_SQL.md if the rr_estimates table isn't created yet.`); }
    setSaving(false);
  };
  const loadEstimate = (x) => { setInp({ ...inp, ...(x.inputs || {}) }); setCurrentId(x.id); setEstNotes(x.notes || ""); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const newEstimate = () => { setInp({ panels: "", arrays: "1", tier: "standard", customRate: "350", scope: "full", permit: true, steepPanels: "", oneWayMin: "30", crew: "4", reuseRail: false, materialRate: "", client: "", address: "" }); setCurrentId(""); setEstNotes(""); };
  const setEstStatus = async (x, status, extra = {}) => {
    const next = { ...x, status, ...extra };
    try { await db.saveRREstimate(next); setSaved(prev => prev.map(y => y.id === x.id ? next : y)); } catch(e) { alert(`Could not update: ${e.message || e}`); }
  };
  const removeEstimate = async (x) => {
    if (!window.confirm(es ? "¿Borrar este estimado?" : `Delete the estimate for ${x.client || x.address}?`)) return;
    try { await db.deleteRREstimate(x.id); setSaved(prev => prev.filter(y => y.id !== x.id)); if (currentId === x.id) setCurrentId(""); } catch(e) { alert(`Could not delete: ${e.message || e}`); }
  };
  // Customer said yes → make the job, carry the estimate (price + internal cost) onto it.
  const approveAndCreate = async (x) => {
    if (x.projectId) return alert(es ? "Ya tiene trabajo creado." : "A job was already created from this estimate.");
    const dup = findOpenProject(await db.getProjects(undefined, { fresh: true }), { address: x.address, client: x.client, projectType: "removal_reinstall" });
    if (dup && window.confirm(`There is already an open job for this address/client:\n\n${dup.name}\n\nOK = put this estimate on that job. Cancel = create a new R&R job.`)) {
      await db.saveProjectEstimate(dup.id, { ...(x.estimate || {}), approvedAt: new Date().toISOString() });
      await setEstStatus(x, "approved", { projectId: dup.id, approvedAt: new Date().toISOString() });
      window.location.hash = `projects/id/${encodeURIComponent(dup.id)}`;
      return;
    }
    const e = x.estimate || {};
    const scopeLabel = e.scope === "removal" ? "Removal" : e.scope === "reinstall" ? "Reinstallation" : "Removal & Reinstallation";
    const project = {
      id: `proj-${Date.now()}`,
      name: `${x.client || x.address} — ${scopeLabel}`.trim(),
      client: x.client || "", address: x.address || "",
      systemSize: "", status: "not_started", completion: 0, partner: "",
      projectType: "removal_reinstall", serviceSubtype: "", playbookId: "", referenceIds: [], truckList: "", scheduled_at: "",
      // Two trips by nature; dates get set when the roofer confirms
      visits: e.scope === "removal" || e.scope === "reinstall"
        ? [{ id: `v${Date.now()}`, label: e.scope === "removal" ? "Removal" : "Reinstall", scheduled_at: "", uidCrew: "", uidOwner: "", seq: 0, invited: [], cancelled: false, notes: `${e.panels || x.inputs?.panels || ""} panels${e.reuseRail ? " — rail stays, reuse attachments" : ""}` }]
        : [{ id: `v${Date.now()}a`, label: "Removal", scheduled_at: "", uidCrew: "", uidOwner: "", seq: 0, invited: [], cancelled: false, notes: `Remove ${e.panels || x.inputs?.panels || ""} panels; label and stage for reinstall` },
           { id: `v${Date.now()}b`, label: "Reinstall", scheduled_at: "", uidCrew: "", uidOwner: "", seq: 0, invited: [], cancelled: false, notes: `Reinstall ${e.panels || x.inputs?.panels || ""} panels${e.reuseRail ? " on existing rail" : " with new racking"}; commission and verify production` }],
      invoice: x.price ? { amount: String(Math.round(x.price * 100) / 100), number: "" } : null,
      scope: `${scopeLabel} — ${e.panels || x.inputs?.panels || "?"} panels${e.arrays > 1 ? `, ${e.arrays} arrays` : ""}${e.reuseRail ? ", existing rail reused" : ", new racking"}${e.permit ? ", permit required" : ""}${x.notes ? `. ${x.notes}` : ""}`,
      clientPhone: x.phone || "", clientEmail: x.email || "",
      tasks: [
        { id: Date.now() + 1, name: "Collect 20% deposit to schedule", assigned: user?.name || "", due: "", priority: "high", done: false },
        ...(e.permit ? [{ id: Date.now() + 2, name: "Pull permit / prepare drawing", assigned: user?.name || "", due: "", priority: "high", done: false }] : []),
        { id: Date.now() + 3, name: "Coordinate removal date with roofer / homeowner", assigned: user?.name || "", due: "", priority: "high", done: false },
        { id: Date.now() + 4, name: e.reuseRail ? "Order attachments, flashings, clamps" : "Order racking and attachments", assigned: user?.name || "", due: "", priority: "medium", done: false },
        { id: Date.now() + 5, name: "Schedule reinstall once roof is done", assigned: user?.name || "", due: "", priority: "medium", done: false },
        { id: Date.now() + 6, name: "Invoice balance on completion", assigned: user?.name || "", due: "", priority: "medium", done: false },
      ],
      materials: [], members: [],
      notes: [{ id: Date.now() + 100, text: `Created from approved R&R estimate — ${e.panels || x.inputs?.panels} panels, ${scopeLabel}, customer price ${fmt2(x.price)}.${x.notes ? ` Note: ${x.notes}` : ""}`, by: user?.name || "", at: new Date().toISOString() }],
      created_by: user?.name || "", created_at: new Date().toISOString(),
    };
    try {
      await db.saveProject(project);
      await db.saveProjectEstimate(project.id, { ...e, approvedAt: new Date().toISOString() });
      await setEstStatus(x, "approved", { projectId: project.id, approvedAt: new Date().toISOString() });
      setProjects(prev => [project, ...prev]);
      window.location.hash = `projects/id/${encodeURIComponent(project.id)}`;
    } catch(err) { alert(`Could not create the job: ${err.message || err}`); }
  };

  const num = (k, label, extra = {}) => (
    <Field label={label}><input type="number" style={S.input} value={inp[k]} onChange={e => set(k, e.target.value)} {...extra} /></Field>
  );
  const row = (label, val, sub, strong) => (
    <div style={{ display: "flex", justifyContent: "space-between", gap: 10, padding: "6px 0", borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
      <div style={{ color: strong ? C.light : C.muted, fontWeight: strong ? 700 : 400 }}>{label}{sub ? <div style={{ fontSize: 11, color: C.muted, fontWeight: 400 }}>{sub}</div> : null}</div>
      <div style={{ color: strong ? C.gold : C.light, fontWeight: strong ? 800 : 600, whiteSpace: "nowrap" }}>{val}</div>
    </div>
  );

  return (
    <div style={{ maxWidth: 980 }}>
      <div style={{ marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>{es ? "Estimador R&R" : "R&R Estimator"}</h2>
        <div style={{ color: C.muted, fontSize: 13, marginTop: 4 }}>{es ? "Precio al cliente por tarifa, costo interno por horas-hombre, margen y validación." : "Customer price from the tier table, internal cost from the man-hour model, margin and validation flags."}</div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) minmax(300px, 1.2fr)", gap: 16, alignItems: "start" }}>
        {/* INPUTS */}
        <div style={S.card}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <Field label={es ? "Cliente" : "Client"}><input style={S.input} value={inp.client} onChange={e => set("client", e.target.value)} /></Field>
            <Field label={es ? "Dirección" : "Address"}><input style={S.input} value={inp.address} onChange={e => set("address", e.target.value)} /></Field>
            {num("panels", es ? "Paneles" : "Panels", { min: 0 })}
            {num("arrays", es ? "Arreglos" : "Arrays", { min: 1 })}
          </div>
          <Field label={es ? "Alcance" : "Scope"}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {[["full", es ? "Quitar + reinstalar" : "Full R&R"], ["removal", es ? "Solo quitar" : "Removal only"], ["reinstall", es ? "Solo reinstalar" : "Reinstall only"]].map(([id, label]) => (
                <button key={id} type="button" onClick={() => set("scope", id)} style={{ padding: "8px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", border: `1px solid ${inp.scope === id ? C.gold : C.border}`, background: inp.scope === id ? `${C.gold}22` : "transparent", color: inp.scope === id ? C.gold : C.muted }}>{label}</button>
              ))}
            </div>
          </Field>
          <Field label={es ? "Tarifa" : "Rate tier"}>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.entries(RR_TIERS).map(([id, tr]) => (
                <button key={id} type="button" onClick={() => set("tier", id)} title={tr.note} style={{ padding: "8px 12px", borderRadius: 10, cursor: "pointer", fontSize: 12.5, fontWeight: 600, fontFamily: "inherit", border: `1px solid ${inp.tier === id ? C.gold : C.border}`, background: inp.tier === id ? `${C.gold}22` : "transparent", color: inp.tier === id ? C.gold : C.muted }}>
                  {tr.label}{id !== "custom" ? ` $${tr.materials + tr.removal + tr.reinstall + tr.electrical}` : ""}
                </button>
              ))}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{RR_TIERS[inp.tier]?.note}</div>
            {inp.tier === "custom" && <div style={{ marginTop: 8 }}>{num("customRate", es ? "Precio por panel ($)" : "Per-panel rate ($)", { min: 0 })}</div>}
          </Field>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {num("oneWayMin", es ? "Viaje ida (min)" : "Drive one way (min)", { min: 0, step: 5 })}
            {num("steepPanels", es ? "Paneles en techo >35°" : "Panels on roof over 35°", { min: 0 })}
            {num("crew", es ? "Cuadrilla" : "Crew size", { min: 1, max: 8 })}
            <Field label={es ? "Costo material/panel" : "Material cost / panel"}><input type="number" style={S.input} value={inp.materialRate} placeholder={String(r.materialRate)} onChange={e => set("materialRate", e.target.value)} /></Field>
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: C.light, fontSize: 13, marginBottom: 8, cursor: "pointer" }}>
            <input type="checkbox" checked={!!inp.permit} onChange={e => set("permit", e.target.checked)} /> {es ? "Requiere permiso (+$400 cliente, 4 h oficina)" : "Permit required (+$400 to customer, 4 office hrs)"}
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8, color: C.light, fontSize: 13, cursor: "pointer" }}>
            <input type="checkbox" checked={!!inp.reuseRail} onChange={e => set("reuseRail", e.target.checked)} /> {es ? "Se reutiliza el riel (material $33/panel)" : "Existing rail reused — attachments, flashings, clamps only ($33/panel)"}
          </label>
        </div>

        {/* OUTPUT */}
        <div>
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{es ? "Precio al cliente" : "Customer price"} · {r.rates.label}</h3>
              <span style={{ fontSize: 12, color: C.muted }}>{hasInput ? `$${r.perPanelRate}/panel` : ""}</span>
            </div>
            {!hasInput && <div style={{ color: C.muted, fontSize: 13 }}>{es ? "Pon el número de paneles." : "Enter the panel count."}</div>}
            {hasInput && <>
              {r.lines.map(l => row(l.label, fmt2(l.total), `${r.p} × $${l.perPanel}`))}
              {r.adders.map((x, i) => row(x.label, fmt2(x.total)))}
              {r.floorApplied && row(es ? "Mínimo por trabajo" : "Small-job minimum", fmt2(2400), es ? "Solo quitar, menos de 20 paneles" : "Removal only under 20 panels")}
              {row("TOTAL", fmt2(r.price), null, true)}
              <Field label={es ? "Nota interna (opcional)" : "Internal note (optional)"}><input style={S.input} value={estNotes} placeholder={es ? "Ej. esperando al techador" : "e.g. waiting on roofer's date, sent 10/21"} onChange={e => setEstNotes(e.target.value)} /></Field>
              <div style={{ display: "flex", gap: 8, marginTop: 4, flexWrap: "wrap", alignItems: "center" }}>
                <button style={S.btnP} onClick={saveEstimate} disabled={saving}>{saving ? "…" : (currentId ? (es ? "Actualizar estimado" : "Update saved estimate") : (es ? "Guardar estimado" : "Save estimate"))}</button>
                <button style={S.btnS} onClick={copyQuote}>{es ? "Copiar cotización" : "Copy quote"}</button>
                {currentId && <button style={{ ...S.btnS, border: "none" }} onClick={newEstimate}>{es ? "Nuevo" : "New estimate"}</button>}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{es ? "Guarda precio al cliente y costo interno. Cuando el cliente apruebe, crea el trabajo desde la lista de abajo." : "Saves customer price and internal cost together. When the customer approves, create the job from the saved list below."}</div>
              <details style={{ marginTop: 10 }}>
                <summary style={{ fontSize: 12, color: C.muted, cursor: "pointer" }}>{es ? "O guardar en un trabajo existente" : "Or attach to an existing job"}</summary>
                <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                  <JobPicker projects={projects} value={projId} onPick={setProjId} lang={lang} />
                  <button style={S.btnS} onClick={saveToJob} disabled={saving}>{saving ? "…" : (es ? "Guardar en trabajo" : "Save to job")}</button>
                </div>
              </details>
            </>}
          </div>

          {hasInput && (
            <div style={{ ...S.card, borderColor: r.margin < 0.2 ? C.danger : C.border }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{es ? "Costo interno" : "Internal cost"} — {fmt(r.cost)} <span style={{ color: C.muted, fontWeight: 400, fontSize: 12 }}>(${Math.round(r.cost / r.p)}/panel)</span></h3>
                <span style={{ fontSize: 18, fontWeight: 800, color: r.margin < 0.2 ? C.danger : r.margin < 0.3 ? C.warning : C.success }}>{pct(r.margin)} {es ? "margen" : "margin"}</span>
              </div>
              {row(es ? "Horas-hombre en campo" : "Field labor", fmt(r.laborCost), `${r.fieldMH.toFixed(1)} MH × $50 — removal ${r.removalMH.toFixed(1)} + reinstall ${r.reinstallMH.toFixed(1)} + travel ${r.travelMH.toFixed(1)} (${r.roundTripHrs.toFixed(2)} h RT × ${r.crew} × ${r.trips} trip${r.trips > 1 ? "s" : ""})`)}
              {row(es ? "Eléctrico" : "Electrical", fmt(r.electricalCost), `${r.electricalHrs.toFixed(2)} hrs × $60`)}
              {r.permitCost > 0 && row(es ? "Permiso y dibujo" : "Permit & drawing", fmt(r.permitCost), "4 hrs × $60")}
              {row(es ? "Material" : "Material", fmt(r.materialCost), `${r.p} × $${r.materialRate}`)}
              {row(es ? "Vehículo, combustible, consumibles" : "Vehicle, fuel, consumables, disposal", fmt(r.vehicle), r.vehicle === 500 ? "over 90 min one way" : "local")}
              {row(es ? "Costo total" : "Total cost", fmt2(r.cost), null, true)}
              <div style={{ marginTop: 10, fontSize: 12, color: C.muted }}>
                {es ? "Precio a margen objetivo" : "Price at target margin"}: 25% {fmt(r.priceAt(0.25))} · 30% {fmt(r.priceAt(0.30))} · 35% {fmt(r.priceAt(0.35))} · 40% {fmt(r.priceAt(0.40))}
              </div>
              {r.flags.length > 0 && (
                <div style={{ marginTop: 10, padding: "8px 10px", background: `${C.warning}14`, border: `1px solid ${C.warning}44`, borderRadius: 8 }}>
                  {r.flags.map((f, i) => <div key={i} style={{ fontSize: 12, color: C.warning, fontWeight: 600 }}>⚠ {f}</div>)}
                </div>
              )}
              {r.flags.length === 0 && <div style={{ marginTop: 10, fontSize: 12, color: C.success, fontWeight: 600 }}>✓ {es ? "Dentro de los parámetros" : "Within benchmarks"} — {r.perPanelWorking.toFixed(2)} MH/panel</div>}
            </div>
          )}
        </div>
      </div>

      {/* SAVED ESTIMATES — waiting on the customer */}
      <div style={{ ...S.card, marginTop: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{es ? "Estimados guardados" : "Saved estimates"} <span style={{ color: C.muted, fontWeight: 400, fontSize: 12 }}>({saved.filter(x => x.status === "pending").length} {es ? "pendientes" : "pending"})</span></h3>
          <div style={{ display: "flex", gap: 6 }}>
            {[["pending", es ? "Pendientes" : "Pending"], ["approved", es ? "Aprobados" : "Approved"], ["declined", es ? "Rechazados" : "Declined"], ["all", es ? "Todos" : "All"]].map(([id, label]) => (
              <button key={id} type="button" onClick={() => setEstFilter(id)} style={{ padding: "5px 10px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600, fontFamily: "inherit", border: `1px solid ${estFilter === id ? C.gold : C.border}`, background: estFilter === id ? `${C.gold}22` : "transparent", color: estFilter === id ? C.gold : C.muted }}>{label}</button>
            ))}
          </div>
        </div>
        {saved.filter(x => estFilter === "all" || x.status === estFilter).length === 0 && (
          <div style={{ color: C.muted, fontSize: 13 }}>{es ? "Nada aquí todavía." : estFilter === "pending" ? "Nothing waiting. Save an estimate above and it shows up here." : "Nothing here."}</div>
        )}
        {saved.filter(x => estFilter === "all" || x.status === estFilter).map(x => {
          const e = x.estimate || {};
          const sc = x.status === "approved" ? C.success : x.status === "declined" ? C.danger : C.warning;
          const days = x.createdAt ? Math.floor((Date.now() - new Date(x.createdAt)) / 86400000) : 0;
          return (
            <div key={x.id} style={{ padding: "10px 12px", background: currentId === x.id ? `${C.gold}0f` : C.surface, border: `1px solid ${currentId === x.id ? C.gold : C.border}`, borderRadius: 10, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{ color: C.light, fontWeight: 700, fontSize: 14, overflowWrap: "anywhere" }}>{x.client || "—"} <span style={S.badge(sc)}>{x.status.toUpperCase()}</span></div>
                  <div style={{ color: C.muted, fontSize: 12, overflowWrap: "anywhere" }}>{x.address}</div>
                  <div style={{ color: C.muted, fontSize: 12, marginTop: 4 }}>
                    {e.panels || x.inputs?.panels} {es ? "paneles" : "panels"} · {e.scope === "removal" ? (es ? "solo quitar" : "removal only") : e.scope === "reinstall" ? (es ? "solo reinstalar" : "reinstall only") : "full R&R"} · {RR_TIERS[x.inputs?.tier]?.label || x.inputs?.tier}{x.inputs?.permit ? ` · ${es ? "permiso" : "permit"}` : ""}
                    {" · "}{x.createdAt ? new Date(x.createdAt).toLocaleDateString() : ""}{x.status === "pending" && days >= 14 ? <span style={{ color: C.warning, fontWeight: 700 }}> · {days}d {es ? "esperando" : "waiting"}</span> : ""}
                  </div>
                  {x.notes && <div style={{ color: C.light, fontSize: 12, marginTop: 4 }}>{x.notes}</div>}
                  {x.projectId && <div style={{ color: C.success, fontSize: 12, marginTop: 4 }}>✓ {es ? "Trabajo creado" : "Job created"} — {projects.find(p => p.id === x.projectId)?.name || x.projectId}</div>}
                </div>
                <div style={{ textAlign: "right", flexShrink: 0 }}>
                  <div style={{ color: C.gold, fontWeight: 800, fontSize: 16 }}>{fmt2(x.price)}</div>
                  <div style={{ color: C.muted, fontSize: 12 }}>{es ? "costo" : "cost"} {fmt(x.cost)} · <span style={{ color: x.margin < 0.2 ? C.danger : x.margin < 0.3 ? C.warning : C.success, fontWeight: 700 }}>{pct(x.margin)}</span></div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <button style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }} onClick={() => loadEstimate(x)}>{es ? "Abrir" : "Open"}</button>
                <button style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }} onClick={async () => { try { await navigator.clipboard.writeText(e.quoteText || ""); alert("Quote copied."); } catch(err) { window.prompt("Copy this:", e.quoteText || ""); } }}>{es ? "Copiar cotización" : "Copy quote"}</button>
                {x.status === "pending" && <>
                  <button style={{ ...S.btnP, fontSize: 12, padding: "6px 10px" }} onClick={() => approveAndCreate(x)}>{es ? "Aprobado → crear trabajo" : "Approved → create job"}</button>
                  <button style={{ ...S.btnS, fontSize: 12, padding: "6px 10px", color: C.danger, borderColor: `${C.danger}66` }} onClick={() => { const rsn = window.prompt(es ? "Motivo:" : "Reason (optional):", ""); if (rsn === null) return; setEstStatus(x, "declined", { notes: [x.notes, rsn].filter(Boolean).join(" — ") }); }}>{es ? "Rechazado" : "Declined"}</button>
                </>}
                {x.status === "approved" && !x.projectId && <button style={{ ...S.btnP, fontSize: 12, padding: "6px 10px" }} onClick={() => approveAndCreate(x)}>{es ? "Crear trabajo" : "Create job"}</button>}
                {x.status === "declined" && <button style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }} onClick={() => setEstStatus(x, "pending")}>{es ? "Reabrir" : "Reopen"}</button>}
                <button style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: "4px 6px", marginLeft: "auto" }} onClick={() => removeEstimate(x)}>{I.trash}</button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Site JHA Sheet ──────────────────────────────────────────────────────────
// A printable one-page Job Hazard Analysis built on the roof plan from the
// permit set: legend, bilingual checklists, numbered hazards marked on the
// drawing, nearest urgent care, crew sign-off. Modeled on the PV9 sheet in a
// standard permit set so the crew sees the same thing at every job.
const JHA_MARKERS = [
  { id: "H", label: "Hazard (numbered)", es: "Riesgo numerado", color: "#F26D6D", numbered: true },
  { id: "A", label: "Anchor", es: "Anclaje", color: "#B4F53C" },
  { id: "W", label: "Anchor work angle", es: "Anclaje trabajado en ángulos", color: "#B4F53C" },
  { id: "L", label: "Ladder", es: "Escalera", color: "#6CB8F5" },
  { id: "X", label: "Exclusion zone", es: "Zona restringida", color: "#F5C542" },
  { id: "Z", label: "Electrical zone", es: "Zona eléctrica", color: "#F5C542" },
  { id: "E", label: "Home entrance", es: "Entrada de la casa", color: "#F5F0E8" },
  { id: "V", label: "Vehicle", es: "Vehículo", color: "#F5F0E8" },
  { id: "T", label: "Tree", es: "Árbol", color: "#5FD38A" },
];
const JHA_SHEET_CHECKS = {
  public: { label: "PUBLIC PROTECTION / PROTECCIÓN PÚBLICA", items: ["Exclusion zones / Zona restringida", "Marked entryways / Entrada marcada"] },
  fall: { label: "FALL PROTECTION / PROTECCIÓN DE CAÍDAS", items: ["Anchors / Anclaje", "Ropes / carabiners & rope grab / Sogas, carabinero y agarre de soga", "Harness / Arnés", "Lanyards / Soga"] },
  ppe: { label: "PPE / EPP", items: ["Safety glasses / Gafas de seguridad", "Gloves / Guantes", "Hard hats / Casco de seguridad", "Arc flash suit / hot gloves / Traje aislante / guantes para electricidad", "First aid kit / Kit de primeros auxilios", "Fire extinguisher on truck / Extintor en el camión"] },
};

// Render one PDF page to a JPEG data URL with pdf.js loaded on demand.
async function renderPdfPage(file, pageNum, scale = 2) {
  if (!window.pdfjsLib) {
    await new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js";
      s.onload = res; s.onerror = () => rej(new Error("Could not load the PDF reader. Check the connection and try again."));
      document.head.appendChild(s);
    });
    window.pdfjsLib.GlobalWorkerOptions.workerSrc = "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
  }
  const buf = await file.arrayBuffer();
  const pdf = await window.pdfjsLib.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(Math.min(Math.max(1, pageNum), pdf.numPages));
  const vp = page.getViewport({ scale });
  const canvas = document.createElement("canvas");
  canvas.width = vp.width; canvas.height = vp.height;
  await page.render({ canvasContext: canvas.getContext("2d"), viewport: vp }).promise;
  return { dataUrl: canvas.toDataURL("image/jpeg", 0.9), pages: pdf.numPages, width: vp.width, height: vp.height };
}
async function pdfPageCount(file) {
  const r = await renderPdfPage(file, 1, 0.3);
  return r.pages;
}
const dataUrlToFile = async (dataUrl, name) => { const res = await fetch(dataUrl); const blob = await res.blob(); return new File([blob], name, { type: "image/jpeg" }); };

function jhaSheetPrintHtml(sheet, proj) {
  const esc = (s) => String(s || "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
  const mk = (m) => JHA_MARKERS.find(x => x.id === m.type) || JHA_MARKERS[0];
  const markers = (sheet.markers || []).map(m => {
    const d = mk(m);
    const txt = d.numbered ? `H${m.num}` : d.id;
    return `<div style="position:absolute;left:${m.x}%;top:${m.y}%;transform:translate(-50%,-50%);width:26px;height:26px;border-radius:${d.numbered ? "50%" : "5px"};border:2.5px solid #111;background:${d.numbered ? "#fff" : "#fff"};font:800 11px/22px Arial;text-align:center;color:#111;box-shadow:0 0 0 2px #fff">${txt}</div>`;
  }).join("");
  const hazards = (sheet.markers || []).filter(m => m.type === "H").sort((a, b) => a.num - b.num);
  const chk = (label) => `<div style="display:flex;gap:6px;align-items:flex-start;margin:2px 0"><span style="display:inline-block;width:11px;height:11px;border:1.5px solid #111;flex-shrink:0;margin-top:1px"></span><span>${esc(label)}</span></div>`;
  const section = (title, items) => `<div style="margin-top:9px"><div style="font-weight:800;text-decoration:underline;font-size:10.5px">${esc(title)}</div>${items.map(chk).join("")}</div>`;
  const legend = JHA_MARKERS.map(d => `<div style="display:flex;gap:8px;align-items:center;margin:5px 0"><span style="display:inline-block;width:22px;height:22px;border-radius:${d.numbered ? "50%" : "4px"};border:2px solid #111;font:800 10px/19px Arial;text-align:center;flex-shrink:0">${d.numbered ? "H#" : d.id}</span><span style="font-size:9.5px;line-height:1.2"><b>${esc(d.label.toUpperCase())}</b><br>${esc(d.es.toUpperCase())}</span></div>`).join("");
  const today = new Date().toLocaleDateString();
  return `<!doctype html><html><head><meta charset="utf-8"><title>JHA — ${esc(proj?.name || "")}</title>
<style>
  @page { size: letter landscape; margin: 0.35in; }
  body { font-family: Arial, Helvetica, sans-serif; color: #111; margin: 0; }
  .sheet { border: 2px solid #111; display: grid; grid-template-columns: 1.45in 3.2in 1fr; grid-template-rows: 1fr auto; height: 7.4in; }
  .cell { border-right: 2px solid #111; padding: 8px 10px; font-size: 10.5px; overflow: hidden; }
  .title { font-weight: 900; font-size: 13px; letter-spacing: .3px; }
  .plan { position: relative; padding: 8px; }
  .tb { grid-column: 1 / -1; border-top: 2px solid #111; display: grid; grid-template-columns: 2.2in 1.4in 1fr 1.6in 1.1in; font-size: 9px; }
  .tb > div { border-right: 2px solid #111; padding: 6px 8px; }
  .tb > div:last-child { border-right: none; }
  .line { border-bottom: 1px solid #111; height: 14px; margin: 3px 0 7px; }
  @media print { .noprint { display: none } }
</style></head><body>
<div class="noprint" style="padding:8px;background:#eee;font-size:12px">Print → Landscape, Letter, fit to page. <button onclick="window.print()">Print</button></div>
<div class="sheet">
  <div class="cell"><div class="title">LEGEND /<br>LEYENDA</div><div style="margin-top:8px">${legend}</div></div>
  <div class="cell">
    <div class="title">JOB HAZARD ANALYSIS /<br>ANÁLISIS DE RIESGOS LABORALES</div>
    <div style="margin-top:8px"><div style="font-weight:800;text-decoration:underline;font-size:10.5px">SITE NOTES (HAZARDS NUMBERED AND EXPLAINED) / NOTAS DEL LUGAR</div>
      ${hazards.length ? hazards.map(h => `<div style="margin:3px 0"><b>H${h.num}</b> — ${esc(h.note || "")}</div>`).join("") : '<div class="line"></div><div class="line"></div>'}
      ${sheet.siteNotes ? `<div style="margin-top:4px;white-space:pre-wrap">${esc(sheet.siteNotes)}</div>` : ""}
    </div>
    <div style="margin-top:9px"><div style="font-weight:800;text-decoration:underline;font-size:10.5px">CLOSEST URGENT CARE / MEDICAL FACILITY / SALA DE EMERGENCIA MÁS CERCANA</div>
      <div>${esc(sheet.urgentCare || "")}${sheet.urgentCareAddress ? `<br>${esc(sheet.urgentCareAddress)}` : ""}${sheet.urgentCarePhone ? ` · ${esc(sheet.urgentCarePhone)}` : ""}</div>
      ${!sheet.urgentCare ? '<div class="line"></div>' : ""}
    </div>
    ${section(JHA_SHEET_CHECKS.public.label, JHA_SHEET_CHECKS.public.items)}
    ${section(JHA_SHEET_CHECKS.fall.label, JHA_SHEET_CHECKS.fall.items)}
    ${section(JHA_SHEET_CHECKS.ppe.label, JHA_SHEET_CHECKS.ppe.items)}
    <div style="margin-top:10px;font-size:10.5px"><b>DATE / FECHA:</b> ${esc(sheet.date || today)}</div>
    <div style="margin-top:6px;font-size:10.5px"><b>CREW LEAD / LÍDER DE GRUPO:</b> ${esc(sheet.crewLead || "")}${sheet.crewLead ? "" : '<div class="line"></div>'}</div>
    <div style="margin-top:6px;font-size:10.5px"><b>CREW MEMBERS / MIEMBROS DEL GRUPO:</b> ${esc(sheet.crewMembers || "")}${sheet.crewMembers ? "" : '<div class="line"></div><div class="line"></div>'}</div>
  </div>
  <div class="plan" style="display:flex;align-items:center;justify-content:center">${sheet.imageUrl ? `<div style="position:relative;display:inline-block;line-height:0"><img src="${esc(sheet.imageUrl)}" alt="Roof plan" style="display:block;max-width:100%;max-height:7.1in;width:auto;height:auto"><div style="position:absolute;inset:0">${markers}</div></div>` : '<div style="color:#999;text-align:center">No roof plan attached</div>'}</div>
  <div class="tb">
    <div><b style="font-size:12px">SolarAbility LLC</b><br>10933 S. Avenue O, Chicago, IL 60617<br>312.312.5337 · NABCEP · ICC</div>
    <div>SIGNATURE:<div class="line"></div>CONTRACTOR LICENSE:<div class="line"></div></div>
    <div>PROJECT: ${esc(proj?.name || "")}<br>ADDRESS: ${esc(proj?.address || "")}<br>DATE: ${esc(sheet.date || today)}${proj?.partner ? `<br>PARTNER: ${esc(proj.partner)}` : ""}</div>
    <div><b>${esc(proj?.client || proj?.name || "")}</b><br>${esc(proj?.address || "")}</div>
    <div style="text-align:center"><div style="font-size:9px">JOB HAZARD<br>ANALYSIS</div><div style="font-size:22px;font-weight:900;margin-top:4px">JHA</div></div>
  </div>
</div>
</body></html>`;
}

function SiteJhaSheet({ project, user, onSave, canEdit }) {
  const [sheet, setSheet] = useState(() => project?.jhaSheet || {
    imageUrl: "", markers: [], siteNotes: "", urgentCare: "", urgentCareAddress: "", urgentCarePhone: "", crewLead: "",
    crewMembers: (project?.members || []).map(m => m.displayName || m.name).filter(Boolean).join(", "),
    date: project?.scheduled_at ? String(project.scheduled_at).slice(0, 10) : "",
  });
  const [tool, setTool] = useState("H");
  const [busy, setBusy] = useState("");
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPages, setPdfPages] = useState(0);
  const [pdfPage, setPdfPage] = useState(1);
  const [preview, setPreview] = useState("");
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef(null);
  const imgRef = useRef(null);
  const upd = (patch) => { setSheet(s => ({ ...s, ...patch })); setDirty(true); };

  const onFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = ""; if (!f) return;
    try {
      if (/pdf$/i.test(f.name) || f.type === "application/pdf") {
        setBusy("Reading planset…");
        const n = await pdfPageCount(f);
        setPdfFile(f); setPdfPages(n);
        // Roof / JHA plan is usually near the end; start on the last page and let them step back
        const start = n; setPdfPage(start);
        const r = await renderPdfPage(f, start, 1.2); setPreview(r.dataUrl);
      } else {
        setBusy("Loading image…");
        const url = await db.uploadPhoto(f, `jha-sheets/${Date.now()}-${f.name.replace(/[^a-zA-Z0-9._-]/g, "_")}`);
        upd({ imageUrl: url, markers: [] }); setPdfFile(null); setPreview("");
      }
    } catch(err) { alert(err.message || "Could not read that file."); }
    setBusy("");
  };
  const showPage = async (n) => { if (!pdfFile) return; setBusy(`Page ${n}…`); setPdfPage(n); try { const r = await renderPdfPage(pdfFile, n, 1.2); setPreview(r.dataUrl); } catch(e) {} setBusy(""); };
  const usePage = async () => {
    if (!pdfFile) return;
    setBusy("Saving roof plan…");
    try {
      const r = await renderPdfPage(pdfFile, pdfPage, 2.5);
      const file = await dataUrlToFile(r.dataUrl, `${(project?.name || "plan").replace(/[^a-zA-Z0-9]/g, "_")}-p${pdfPage}.jpg`);
      const url = await db.uploadPhoto(file, `jha-sheets/${Date.now()}-p${pdfPage}.jpg`);
      if (!url || String(url).startsWith("data:")) throw new Error("Upload failed");
      upd({ imageUrl: url, markers: [], sourcePage: pdfPage, sourceFile: pdfFile.name });
      setPdfFile(null); setPreview("");
    } catch(e) { alert("Could not save the page: " + (e.message || e)); }
    setBusy("");
  };
  const place = (e) => {
    if (!canEdit || !sheet.imageUrl) return;
    const box = imgRef.current.getBoundingClientRect();
    const x = ((e.clientX - box.left) / box.width) * 100, y = ((e.clientY - box.top) / box.height) * 100;
    if (x < 0 || y < 0 || x > 100 || y > 100) return;
    const def = JHA_MARKERS.find(m => m.id === tool);
    const num = def.numbered ? (Math.max(0, ...sheet.markers.filter(m => m.type === "H").map(m => m.num || 0)) + 1) : null;
    upd({ markers: [...sheet.markers, { id: `m${Date.now()}`, type: tool, x: Math.round(x * 10) / 10, y: Math.round(y * 10) / 10, num, note: "" }] });
  };
  const removeMarker = (id) => {
    const rest = sheet.markers.filter(m => m.id !== id);
    let n = 0; upd({ markers: rest.map(m => m.type === "H" ? { ...m, num: ++n } : m) });
  };
  const setNote = (id, note) => upd({ markers: sheet.markers.map(m => m.id === id ? { ...m, note } : m) });
  const save = async () => { await onSave(sheet); setDirty(false); };
  const print = () => {
    const html = jhaSheetPrintHtml(sheet, project);
    const w = window.open("", "_blank");
    if (!w) { const blob = new Blob([html], { type: "text/html" }); const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `JHA-${(project?.name || "job").replace(/[^a-zA-Z0-9]/g, "_")}.html`; a.click(); return; }
    w.document.write(html); w.document.close();
  };
  const hazards = sheet.markers.filter(m => m.type === "H").sort((a, b) => a.num - b.num);

  return (
    <div>
      <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>
        Upload the permit set (PDF) or a photo of the roof plan, pick the roof page, then tap the drawing to mark hazards, anchors, ladder and exclusion zones. Print lands as a one-page landscape sheet for the truck.
      </div>

      {/* Step 1: source */}
      {!sheet.imageUrl && !pdfFile && canEdit && (
        <div onClick={() => fileRef.current?.click()} style={{ border: `2px dashed ${C.border}`, borderRadius: 12, padding: 24, textAlign: "center", cursor: "pointer", color: C.muted }}>
          <div style={{ color: C.gold, marginBottom: 6 }}>{I.pdf}</div>
          {busy || "Upload planset PDF or roof plan image"}
          <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={onFile} />
        </div>
      )}
      {pdfFile && (
        <div style={{ ...S.card, padding: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
            <span style={{ fontSize: 13, color: C.light, fontWeight: 600 }}>{pdfFile.name}</span>
            <span style={{ fontSize: 12, color: C.muted }}>page {pdfPage} of {pdfPages} — find the roof plan</span>
            <button style={{ ...S.btnS, padding: "4px 10px" }} disabled={pdfPage <= 1} onClick={() => showPage(pdfPage - 1)}>‹</button>
            <button style={{ ...S.btnS, padding: "4px 10px" }} disabled={pdfPage >= pdfPages} onClick={() => showPage(pdfPage + 1)}>›</button>
            <button style={S.btnP} onClick={usePage} disabled={!!busy}>{busy || "Use this page"}</button>
            <button style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 12 }} onClick={() => { setPdfFile(null); setPreview(""); }}>Cancel</button>
          </div>
          {preview && <img loading="lazy" decoding="async" src={preview} alt="" style={{ width: "100%", borderRadius: 8, background: "#fff" }} />}
        </div>
      )}

      {/* Step 2: mark up */}
      {sheet.imageUrl && (
        <>
          {canEdit && (
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 8, alignItems: "center" }}>
              <span style={{ fontSize: 11, color: C.muted, fontWeight: 700, letterSpacing: 1 }}>TAP TO PLACE:</span>
              {JHA_MARKERS.map(m => (
                <button key={m.id} type="button" onClick={() => setTool(m.id)} title={m.label} style={{ padding: "5px 9px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", border: `1px solid ${tool === m.id ? m.color : C.border}`, background: tool === m.id ? `${m.color}22` : "transparent", color: tool === m.id ? m.color : C.muted }}>
                  {m.numbered ? "H#" : m.id} <span style={{ fontWeight: 400 }}>{m.label}</span>
                </button>
              ))}
              <button type="button" style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 12, marginLeft: "auto" }} onClick={() => { if (window.confirm("Replace the roof plan? Markers will be cleared.")) { upd({ imageUrl: "", markers: [] }); } }}>Replace plan</button>
              <input ref={fileRef} type="file" accept="application/pdf,image/*" style={{ display: "none" }} onChange={onFile} />
            </div>
          )}
          <div style={{ position: "relative", background: "#fff", borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
            <img loading="lazy" decoding="async" ref={imgRef} src={sheet.imageUrl} alt="Roof plan" onClick={place} style={{ width: "100%", display: "block", cursor: canEdit ? "crosshair" : "default" }} />
            {sheet.markers.map(m => {
              const d = JHA_MARKERS.find(x => x.id === m.type) || JHA_MARKERS[0];
              return (
                <div key={m.id} onClick={(e) => { e.stopPropagation(); if (canEdit && window.confirm(`Remove ${d.numbered ? "H" + m.num : d.label}?`)) removeMarker(m.id); }}
                  style={{ position: "absolute", left: `${m.x}%`, top: `${m.y}%`, transform: "translate(-50%,-50%)", width: 28, height: 28, borderRadius: d.numbered ? "50%" : 6, border: `3px solid ${d.color}`, background: "#071510", color: d.color, font: "800 11px/22px Arial", textAlign: "center", cursor: canEdit ? "pointer" : "default", boxShadow: "0 0 0 2px #fff" }}>
                  {d.numbered ? `H${m.num}` : d.id}
                </div>
              );
            })}
          </div>

          {/* Hazard notes */}
          <div style={{ ...S.card, marginTop: 12 }}>
            <h4 style={{ margin: "0 0 8px", fontSize: 13, color: C.light }}>Site notes — hazards numbered and explained</h4>
            {hazards.length === 0 && <div style={{ fontSize: 12, color: C.muted }}>No numbered hazards yet. Pick <strong>H#</strong> above and tap the drawing where the hazard is (power lines, skylight, steep area, dog…).</div>}
            {hazards.map(h => (
              <div key={h.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 6 }}>
                <span style={{ width: 34, textAlign: "center", fontWeight: 800, color: "#F26D6D", flexShrink: 0 }}>H{h.num}</span>
                <input list="jha-hazard-suggest" style={{ ...S.input, flex: 1, padding: "8px 10px", fontSize: 13 }} value={h.note} placeholder="What it is and how to work around it" onChange={e => setNote(h.id, e.target.value)} readOnly={!canEdit} />
              </div>
            ))}
            <datalist id="jha-hazard-suggest">{SITE_HAZARDS.map(s => <option key={s.id} value={s.label} />)}</datalist>
            <Field label="Other site notes"><textarea style={{ ...S.textarea, minHeight: 56 }} value={sheet.siteNotes} onChange={e => upd({ siteNotes: e.target.value })} readOnly={!canEdit} placeholder="Access, gate code, where to park, homeowner instructions…" /></Field>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="Closest urgent care / hospital"><input style={S.input} value={sheet.urgentCare} onChange={e => upd({ urgentCare: e.target.value })} readOnly={!canEdit} /></Field>
              <Field label="Address"><input style={S.input} value={sheet.urgentCareAddress} onChange={e => upd({ urgentCareAddress: e.target.value })} readOnly={!canEdit} /></Field>
              <Field label="Phone"><input style={S.input} value={sheet.urgentCarePhone} onChange={e => upd({ urgentCarePhone: e.target.value })} readOnly={!canEdit} /></Field>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <Field label="Date"><input type="date" style={S.input} value={sheet.date} onChange={e => upd({ date: e.target.value })} readOnly={!canEdit} /></Field>
              <Field label="Crew lead"><input style={S.input} value={sheet.crewLead} onChange={e => upd({ crewLead: e.target.value })} readOnly={!canEdit} /></Field>
              <Field label="Crew members"><input style={S.input} value={sheet.crewMembers} onChange={e => upd({ crewMembers: e.target.value })} readOnly={!canEdit} /></Field>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {canEdit && <button style={{ ...S.btnP, opacity: dirty ? 1 : 0.7 }} onClick={save}>{dirty ? "Save JHA sheet" : "Saved ✓"}</button>}
              <button style={S.btnS} onClick={print}>{I.pdf} Print / PDF</button>
              {sheet.sourceFile && <span style={{ fontSize: 11, color: C.muted, alignSelf: "center" }}>From {sheet.sourceFile}, page {sheet.sourcePage}</span>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Job documents ───────────────────────────────────────────────────────────
// Plansets, partner work orders, SOWs, drawings — PDFs and other files the
// crew should be able to open from the job. Stored on the project (ref_docs).
const docIcon = (name) => /\.pdf$/i.test(name) ? "PDF" : /\.(png|jpe?g|heic|webp)$/i.test(name) ? "IMG" : /\.(xlsx?|csv)$/i.test(name) ? "XLS" : /\.(docx?)$/i.test(name) ? "DOC" : "FILE";
const fmtBytes = (n) => !n ? "" : n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
function RefDocs({ docs, onChange, canEdit, user, compact }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(0);
  const all = docs || [];
  const list = all.filter(d => d && !d.kind); // folder link and markers aren't documents
  const handleFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setBusy(b => b + files.length);
    let next = [...all];
    for (const file of files) {
      try {
        if (file.size > 40 * 1048576) { alert(`${file.name} is over 40 MB — too big to store on the job. Compress it or link the Drive copy in notes.`); setBusy(b => b - 1); continue; }
        const url = await db.uploadDocument(file);
        next = [...next, { id: `rd-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`, url, name: file.name, size: file.size, label: "", by: user?.name || "", at: new Date().toISOString() }];
        onChange(next);
      } catch(err) { console.warn("doc upload failed:", err); alert(`Upload failed for ${file.name}: ${err.message || err}`); }
      setBusy(b => b - 1);
    }
  };
  const setLabel = (id, label) => onChange(all.map(d => d.id === id ? { ...d, label } : d));
  const remove = (id) => { if (window.confirm("Remove this document from the job?")) onChange(all.filter(d => d.id !== id)); };
  return (
    <div>
      {list.map(d => (
        <div key={d.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: compact ? "6px 0" : "8px 10px", background: compact ? "transparent" : C.surface, borderRadius: 8, border: compact ? "none" : `1px solid ${C.border}`, borderTop: compact ? `1px solid ${C.border}` : undefined, marginBottom: compact ? 0 : 6 }}>
          <span style={{ fontSize: 10, fontWeight: 800, color: C.gold, border: `1px solid ${C.gold}66`, borderRadius: 5, padding: "3px 5px", flexShrink: 0 }}>{docIcon(d.name)}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            {canEdit
              ? <input value={d.label || ""} placeholder={d.name} onChange={e => setLabel(d.id, e.target.value)} style={{ ...S.input, padding: "4px 8px", fontSize: 12, width: "100%" }} />
              : <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ color: C.light, fontSize: 13, fontWeight: 600, textDecoration: "none", overflowWrap: "anywhere" }}>{d.label || d.name}</a>}
            <div style={{ fontSize: 11, color: C.muted, overflowWrap: "anywhere" }}>{d.label ? `${d.name} · ` : ""}{fmtBytes(d.size)}{d.by ? ` · ${d.by}` : ""}{d.at ? ` · ${new Date(d.at).toLocaleDateString()}` : ""}</div>
          </div>
          <a href={d.url} target="_blank" rel="noopener noreferrer" style={{ ...S.btnS, padding: "5px 10px", fontSize: 12, textDecoration: "none", flexShrink: 0 }}>{I.eye} Open</a>
          {canEdit && <button type="button" onClick={() => remove(d.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", padding: 2, flexShrink: 0 }}>{I.trash}</button>}
        </div>
      ))}
      {canEdit && (
        <div onClick={() => ref.current?.click()} style={{ border: `2px dashed ${C.border}`, borderRadius: 10, padding: "12px 10px", textAlign: "center", cursor: "pointer", color: C.muted, fontSize: 12, marginTop: list.length ? 6 : 0 }}>
          <span style={{ color: C.gold, marginRight: 6 }}>{I.pdf}</span>{busy > 0 ? `Uploading ${busy}…` : "Add planset, work order, drawing, SOW — PDF or any file"}
          <input ref={ref} type="file" accept="application/pdf,image/*,.doc,.docx,.xls,.xlsx,.csv,.txt" multiple style={{ display: "none" }} onChange={handleFiles} />
        </div>
      )}
    </div>
  );
}

function PartnerDirectory({ user, t }) {
  const [partners, setPartners] = useState([]);
  const [view, setView] = useState("list"); // list | detail | edit
  const [detail, setDetail] = useState(null);
  const [allProjects, setAllProjects] = useState([]);
  const projRef = useRef(false);
  if (!projRef.current) { projRef.current = true; db.getProjects().then(setAllProjects).catch(() => {}); }
  // Match on trimmed, case-insensitive name: a job typed "windfree" should
  // still count as Windfree.
  const jobsFor = (name) => {
    const n = (name || "").trim().toLowerCase();
    return allProjects.filter(p => (p.partner || "").trim().toLowerCase() === n);
  };
  const [editing, setEditing] = useState(null); // null = new, else partner object
  const [loading, setLoading] = useState(true);

  const loadedRef = useRef(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { setPartners(await db.getPartners()); } catch(e) {}
    setLoading(false);
  }, []);
  if (!loadedRef.current) { loadedRef.current = true; load(); }

  const emptyPartner = { id: "", name: "", submissionMethod: "email", contactName: "", contactEmail: "", contactPhone: "", portalUrl: "", notes: "", crewNote: "", customFields: [], defaultTasks: [], requiredPhotos: [], submitDays: "", homeownerNote: "" };
  const [form, setForm] = useState(emptyPartner);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const startNew = () => { setForm({ ...emptyPartner, id: `partner-${Date.now()}` }); setEditing(null); setView("edit"); };
  const startEdit = (p) => { setForm({ ...emptyPartner, ...p }); setEditing(p); setView("edit"); };

  const save = async () => {
    if (!form.name.trim()) return alert("Partner name is required.");
    await db.savePartner(form);
    await load();
    setView("list");
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this partner? Projects already created won't be affected.")) return;
    await db.deletePartner(id);
    await load();
    setView("list");
  };

  const addField = () => f("customFields", [...form.customFields, { id: `f${Date.now()}`, label: "", required: false }]);
  const updField = (id, k, v) => f("customFields", form.customFields.map(x => x.id === id ? { ...x, [k]: v } : x));
  const delField = (id) => f("customFields", form.customFields.filter(x => x.id !== id));

  const addStep = () => f("defaultTasks", [...form.defaultTasks, ""]);
  const addPhoto = () => f("requiredPhotos", [...(form.requiredPhotos || []), { id: `ph${Date.now()}`, label: "", required: true, hint: "" }]);
  const updPhoto = (id, k, v) => f("requiredPhotos", (form.requiredPhotos || []).map(x => x.id === id ? { ...x, [k]: v } : x));
  const delPhoto = (id) => f("requiredPhotos", (form.requiredPhotos || []).filter(x => x.id !== id));
  const updStep = (i, v) => f("defaultTasks", form.defaultTasks.map((x, idx) => idx === i ? v : x));
  const delStep = (i) => f("defaultTasks", form.defaultTasks.filter((_, idx) => idx !== i));

  if (view === "detail" && detail) {
    const jobs = jobsFor(detail.name);
    const open = jobs.filter(j => j.status !== "completed");
    return (
      <div style={{ maxWidth: 680, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => { setView("list"); setDetail(null); }} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: 0 }}>{detail.name}</h2>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              {open.length} open · {jobs.length} total job{jobs.length === 1 ? "" : "s"}
              {detail.submissionMethod ? ` · submit via ${detail.submissionMethod}` : ""}
              {detail.submitDays ? ` · ${detail.submitDays} business day deadline` : ""}
            </div>
          </div>
          <button style={S.btnS} onClick={() => startEdit(detail)}>{I.clip} Edit</button>
        </div>

        {detail.notes && (
          <div style={{ ...S.card, fontSize: 13, color: C.light, lineHeight: 1.6 }}>{detail.notes}</div>
        )}

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, gap: 8, flexWrap: "wrap" }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Jobs</h3>
            {jobs.length > 0 && (
              <a href={`#projects/${encodeURIComponent(detail.name)}`} onClick={(ev) => goHash(ev, `projects/${encodeURIComponent(detail.name)}`)}
                style={{ fontSize: 12, color: C.gold, textDecoration: "underline" }}>Open in Projects →</a>
            )}
          </div>
          {jobs.length === 0 ? (
            <div style={{ color: C.muted, fontSize: 12.5, lineHeight: 1.6 }}>
              No jobs are assigned to {detail.name} yet. On a project, set <strong>Partner / Source</strong> to {detail.name} and it will show here.
            </div>
          ) : (
            [...jobs].sort((a, b) => {
              if (!!a.scheduled_at !== !!b.scheduled_at) return a.scheduled_at ? -1 : 1;
              if (a.scheduled_at) return new Date(b.scheduled_at) - new Date(a.scheduled_at);
              return (a.name || "").localeCompare(b.name || "");
            }).slice(0, 25).map(j => (
              <div key={j.id} style={{ padding: "10px 0", borderBottom: `1px solid ${C.border}33` }}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13.5, color: C.light, fontWeight: 600 }}>{j.name}</span>
                  <span style={S.badge(j.status === "completed" ? C.success : C.info)}>{(j.status || "").replace(/_/g, " ")}</span>
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 2, display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {j.address && <span>{j.address}</span>}
                  {j.scheduled_at && <span style={{ color: C.gold }}>{fmtJobDate(j.scheduled_at, { month: "short", day: "numeric" })}</span>}
                  {j.serviceSubtype && <span>{j.serviceSubtype}</span>}
                </div>
              </div>
            ))
          )}
        </div>

        {(detail.requiredPhotos || []).length > 0 && (
          <div style={S.card}>
            <h3 style={{ margin: "0 0 8px", fontSize: 15, fontWeight: 700, color: C.light }}>Required Photos</h3>
            {detail.requiredPhotos.map(ph => (
              <div key={ph.id} style={{ fontSize: 12.5, color: C.light, padding: "3px 0" }}>
                {ph.required ? "•" : "◦"} {ph.label}{!ph.required && <span style={{ color: C.muted }}> · if applicable</span>}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (view === "edit") {
    return (
      <div style={{ maxWidth: 640, animation: "fadeIn 0.25s ease" }}>
        <button onClick={() => setView(detail ? "detail" : "list")} style={{ ...S.btnS, border: "none", padding: "6px 0", marginBottom: 10 }}>{I.back} {t.back}</button>
        <h2 style={{ fontSize: 22, fontWeight: 800, color: C.light, margin: "0 0 20px" }}>{editing ? form.name : t.addPartner}</h2>

        <div style={S.card}>
          <Field label={t.partnerName} required><input style={S.input} value={form.name} onChange={e => f("name", e.target.value)} placeholder="e.g. Omnidian, Windfree, Sunrun" /></Field>
          <Field label={t.submissionMethod}>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {SUBMISSION_METHODS.map(m => (
                <button key={m.id} type="button" onClick={() => f("submissionMethod", m.id)} style={{
                  padding: "10px 16px", borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700, fontFamily: "inherit",
                  border: `1px solid ${form.submissionMethod === m.id ? C.gold : C.border}`,
                  background: form.submissionMethod === m.id ? `${C.gold}22` : "transparent",
                  color: form.submissionMethod === m.id ? C.gold : C.muted,
                }}>{t[m.labelKey]}</button>
              ))}
            </div>
          </Field>
          <Field label={t.portalLink}><input style={S.input} value={form.portalUrl} onChange={e => f("portalUrl", e.target.value)} placeholder="https://..." /></Field>
        </div>

        <div style={S.card}>
          <h3 style={{ margin: "0 0 16px", fontSize: 15, fontWeight: 700, color: C.light }}>{t.contactInfo}</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Field label={t.createdBy || "Contact Name"}><input style={S.input} value={form.contactName} onChange={e => f("contactName", e.target.value)} /></Field>
            <Field label={t.clientPhone}><input style={S.input} value={form.contactPhone} onChange={e => f("contactPhone", e.target.value)} /></Field>
            <Field label={t.clientEmail}><input style={S.input} value={form.contactEmail} onChange={e => f("contactEmail", e.target.value)} /></Field>
          </div>
          <Field label="Crew note — goes on every invite and the job card">
            <textarea style={S.textarea} rows={3} value={form.crewNote || ""} onChange={e => f("crewNote", e.target.value)}
              placeholder="What the crew must know on site: don't discuss findings, adult 18+ home, photo expectations, call the PM before…" />
          </Field>
          <Field label="Office notes — coordinator only, never sent to crew">
            <textarea style={S.textarea} rows={3} value={form.notes} onChange={e => f("notes", e.target.value)}
              placeholder="Pricing tier, portal, submission deadlines, invoicing — anything with money or logins" />
          </Field>
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{t.customFields}</h3>
            <button style={S.btnS} onClick={addField}>{I.plus} {t.addField}</button>
          </div>
          {form.customFields.length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 10 }}>None yet — add fields this partner specifically asks for (Ticket #, System ID, etc.)</div>}
          {form.customFields.map(cf => (
            <div key={cf.id} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder={t.fieldLabel} value={cf.label} onChange={e => updField(cf.id, "label", e.target.value)} />
              <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>
                <input type="checkbox" checked={cf.required} onChange={e => updField(cf.id, "required", e.target.checked)} /> Required
              </label>
              <button onClick={() => delField(cf.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
            </div>
          ))}
        </div>

        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.light }}>Payment Requirements</h3>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 14px", lineHeight: 1.5 }}>
            What this partner needs before they'll pay. The Service Assessment enforces these.
          </p>
          <Field label="Submit report & photos within (business days)">
            <input style={{ ...S.input, maxWidth: 120 }} type="number" min="0" value={form.submitDays || ""}
              onChange={e => f("submitDays", e.target.value)} placeholder="e.g. 2" />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 5 }}>Creates a dated task on the project when the assessment is filed. Leave blank for no deadline.</div>
          </Field>
          <Field label="Note for the homeowner's confirmation">
            <textarea style={S.textarea} rows={2} value={form.homeownerNote || ""} onChange={e => f("homeownerNote", e.target.value)}
              placeholder="e.g. An adult 18 or older must be home for the visit." />
            <div style={{ fontSize: 11, color: C.muted, marginTop: 5 }}>Added to the homeowner's appointment email and calendar entry for this partner's jobs.</div>
          </Field>
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Required Photos</h3>
            <button style={S.btnS} onClick={addPhoto}>{I.plus} Add</button>
          </div>
          <p style={{ color: C.muted, fontSize: 12, margin: "0 0 12px" }}>Each becomes its own labeled photo slot on the Service Assessment.</p>
          {(form.requiredPhotos || []).length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 10 }}>None — the assessment uses a general photo upload.</div>}
          {(form.requiredPhotos || []).map(ph => (
            <div key={ph.id} style={{ padding: 10, background: C.surface, borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <input style={{ ...S.input, flex: 1 }} placeholder="Photo label (e.g. Production Status from Inverter)" value={ph.label} onChange={e => updPhoto(ph.id, "label", e.target.value)} />
                <label style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12, color: C.muted, whiteSpace: "nowrap" }}>
                  <input type="checkbox" checked={!!ph.required} onChange={e => updPhoto(ph.id, "required", e.target.checked)} /> Always
                </label>
                <button onClick={() => delPhoto(ph.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
              </div>
              <input style={{ ...S.input, marginTop: 6, fontSize: 12 }} placeholder="Hint for the crew (optional)" value={ph.hint || ""} onChange={e => updPhoto(ph.id, "hint", e.target.value)} />
            </div>
          ))}
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}><strong>Always</strong> = required on every visit. Unticked = only if it applies.</div>
        </div>

        <div style={S.card}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>{t.partnerTasks}</h3>
            <button style={S.btnS} onClick={addStep}>{I.plus} {t.addStep}</button>
          </div>
          {form.defaultTasks.length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 10 }}>None yet — steps unique to this partner get added to the project workflow automatically</div>}
          {form.defaultTasks.map((step, i) => (
            <div key={i} style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 8 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder={t.stepName} value={step} onChange={e => updStep(i, e.target.value)} />
              <button onClick={() => delStep(i)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={save} style={{ ...S.btnP, flex: 1, justifyContent: "center" }}>{I.check} {t.itemUpdated || "Save"}</button>
          {editing && <button onClick={() => remove(form.id)} style={S.btnDanger}>{I.trash}</button>}
        </div>
      </div>
    );
  }

  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <div>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: 0 }}>{t.partnerDirectory}</h2>
          <p style={{ color: C.muted, margin: "4px 0 0", fontSize: 13 }}>{partners.length} {t.partners.toLowerCase()}</p>
        </div>
        <button style={S.btnP} onClick={startNew}>{I.plus} {t.addPartner}</button>
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 40, color: C.muted }}>Loading...</div> :
       partners.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 50 }}>
          <div style={{ width: 50, height: 50, borderRadius: 14, background: `${C.gold}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", color: C.gold }}>{I.handshake}</div>
          <div style={{ color: C.muted, fontSize: 14, fontWeight: 600 }}>{t.noPartners}</div>
          <div style={{ color: `${C.muted}66`, fontSize: 12, marginTop: 4 }}>{t.noPartnersDesc}</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {partners.map(p => {
            const methodLabel = t[SUBMISSION_METHODS.find(m => m.id === p.submissionMethod)?.labelKey] || p.submissionMethod;
            return (
              <div key={p.id} onClick={() => { setDetail(p); setView("detail"); }}
                style={{ display: "flex", alignItems: "center", padding: "14px 16px", background: C.card, borderRadius: 12, border: `1px solid ${C.border}`, cursor: "pointer", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: `${C.gold}22`, display: "flex", alignItems: "center", justifyContent: "center", color: C.gold, flexShrink: 0 }}>{I.handshake}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: C.light }}>{p.name}</div>
                  <div style={{ display: "flex", gap: 10, marginTop: 3, fontSize: 11, color: C.muted }}>
                    <span style={S.badge(C.gold)}>{methodLabel}</span>
                    {p.customFields?.length > 0 && <span>{p.customFields.length} custom field{p.customFields.length === 1 ? "" : "s"}</span>}
                    {p.defaultTasks?.length > 0 && <span>{p.defaultTasks.length} extra step{p.defaultTasks.length === 1 ? "" : "s"}</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CostTracker({ user, t }) {
  const [projects, setProjects] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [selId, setSelId] = useState("");
  const [labor, setLabor] = useState([]);
  const [mats, setMats] = useState([]);
  const [other, setOther] = useState([]);
  const [margin, setMargin] = useState("");
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const loadedRef = useRef(false);
  if (!loadedRef.current) {
    loadedRef.current = true;
    (async () => {
      try { setProjects(await db.getProjects()); } catch(e) {}
      if (USE_SUPABASE && supabase) {
        try { const { data } = await supabase.from("inventory").select("*").order("name"); if (data) setInventory(data); } catch(e) {}
      } else { setInventory(localCache.load("inventory") || []); }
    })();
  }

  const selected = projects.find(p => p.id === selId);

  // Actual time on site, from the Service Assessments filed on this job.
  // This is the budget-vs-reality number: what the partner paid for versus
  // what the crew actually spent.
  const [siteTimes, setSiteTimes] = useState([]);
  useEffect(() => {
    if (!selected) { setSiteTimes([]); return; }
    let live = true;
    db.getProjectForms(selected).then(forms => {
      if (!live) return;
      setSiteTimes((forms || []).filter(f => f.type === "Service Assessment" && f.timeOnSite && (f.timeOnSite.arrived || f.timeOnSite.manHours))
        .map(f => ({ id: f.id || f.submittedAt, when: f.submittedAt, by: f.submittedBy, scope: f.scopeTitle || f.serviceType || "", ...f.timeOnSite, budget: f.timeBudget })));
    }).catch(() => {});
    return () => { live = false; };
  }, [selId]); // eslint-disable-line react-hooks/exhaustive-deps
  const siteManHours = siteTimes.reduce((a, s) => a + (Number(s.manHours) || 0), 0);
  const siteBudget = siteTimes.reduce((a, s) => a + (Number(s.budget) || 0), 0);
  const fmtClock = (hhmm) => { if (!hhmm) return "—"; const [h, m] = hhmm.split(":").map(Number); if (isNaN(h)) return hhmm; const d = new Date(); d.setHours(h, m || 0); return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); };

  // Build costing lines from a project's material list, pricing each from your
  // own stock first (what you actually paid), then the supplier catalog.
  const materialsFromProject = (proj) => (proj?.materials || [])
    .filter(m => (m.name || "").trim())
    .map((m, i) => {
      const inv = inventory.find(x => (m.sku && (x.sku || "").toLowerCase() === m.sku.toLowerCase())
        || (x.name || "").toLowerCase() === (m.name || "").toLowerCase());
      const cat = catalogLookup(m.name);
      const price = inv?.unit_cost ?? cat?.price ?? "";
      return {
        id: Date.now() + 9000 + i,
        name: m.name,
        sku: m.sku || cat?.sku || "",
        qty: m.qty ?? "",
        unit: m.unit || inv?.unit || cat?.unit || "",
        cost: price === "" ? "" : String(price),
        fromProject: true,
      };
    });

  const pick = (id) => {
    setSelId(id);
    const p = projects.find(x => x.id === id);
    const cst = p?.costing || {};
    setLabor(cst.labor || []);
    // First time costing this project, seed materials from its material list
    // so you're not retyping what the crew already entered.
    const saved = cst.materials || [];
    setMats(saved.length === 0 ? materialsFromProject(p) : saved);
    setOther(cst.other || []);
    setMargin(cst.margin != null ? String(cst.margin) : "");
    setDirty(saved.length === 0 && (p?.materials || []).length > 0);
  };

  // Re-pull from the project without clobbering prices already entered:
  // updates qty on matching lines, appends anything new, leaves the rest alone.
  const syncFromProject = () => {
    const proj = projects.find(x => x.id === selId);
    if (!proj) return;
    const incoming = materialsFromProject(proj);
    if (incoming.length === 0) { alert("This project has no materials listed yet."); return; }
    setMats(prev => {
      const next = [...prev];
      let added = 0, updated = 0;
      incoming.forEach(inc => {
        const key = (inc.sku || inc.name).toLowerCase();
        const idx = next.findIndex(x => ((x.sku || x.name) || "").toLowerCase() === key);
        if (idx >= 0) {
          if ((next[idx].qty ?? "") !== (inc.qty ?? "")) { next[idx] = { ...next[idx], qty: inc.qty }; updated++; }
        } else { next.push(inc); added++; }
      });
      setTimeout(() => alert(`Synced — ${added} added, ${updated} quantity update${updated === 1 ? "" : "s"}. Prices you entered were kept.`), 0);
      return next;
    });
    setDirty(true);
  };

  const n = (v) => { const x = parseFloat(v); return isNaN(x) ? 0 : x; };
  const money = (v) => "$" + v.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const laborTotal = labor.reduce((s, l) => s + n(l.hours) * n(l.rate), 0);
  const matTotal = mats.reduce((s, m) => s + n(m.qty) * n(m.cost), 0);
  const otherTotal = other.reduce((s, o) => s + n(o.amount), 0);
  const subtotal = laborTotal + matTotal + otherTotal;
  const marginAmt = subtotal * (n(margin) / 100);
  const grand = subtotal + marginAmt;

  const mark = (fn) => (...a) => { fn(...a); setDirty(true); };
  const addLabor = mark(() => setLabor(p => [...p, { id: Date.now(), who: "", hours: "", rate: "" }]));
  const updLabor = mark((id, f, v) => setLabor(p => p.map(x => x.id === id ? { ...x, [f]: v } : x)));
  const delLabor = mark((id) => setLabor(p => p.filter(x => x.id !== id)));
  const addMat = mark(() => setMats(p => [...p, { id: Date.now(), name: "", qty: "", unit: "", cost: "" }]));
  const delMat = mark((id) => setMats(p => p.filter(x => x.id !== id)));
  const addOther = mark(() => setOther(p => [...p, { id: Date.now(), name: "", amount: "" }]));
  const updOther = mark((id, f, v) => setOther(p => p.map(x => x.id === id ? { ...x, [f]: v } : x)));
  const delOther = mark((id) => setOther(p => p.filter(x => x.id !== id)));

  // Material name change: autofill unit/cost when it matches an inventory item
  const updMat = mark((id, f, v) => setMats(p => p.map(x => {
    if (x.id !== id) return x;
    const next = { ...x, [f]: v };
    if (f === "name") {
      // Prefer your own stocked item (it reflects what you actually paid),
      // then fall back to the supplier catalog.
      const inv = inventory.find(i => (i.name || "").toLowerCase() === v.toLowerCase());
      const cat = catalogLookup(v);
      const src = inv || cat;
      if (src) {
        next.unit = next.unit || src.unit || "";
        const price = inv ? inv.unit_cost : cat?.price;
        if (price != null && !x.cost) next.cost = String(price);
        if (cat?.sku) next.sku = cat.sku;
      }
    }
    return next;
  })));

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    const updated = { ...selected, costing: { labor, materials: mats, other, margin: n(margin), subtotal, grand, updatedAt: new Date().toISOString(), updatedBy: user?.name } };
    try {
      // Costing lives in project_finance; write just that, never the whole job row
      const ok = await db.saveProjectFinance(updated.id, { costing: updated.costing });
      if (!ok) throw new Error("finance save failed");
      setProjects(p => p.map(x => x.id === updated.id ? updated : x));
      setDirty(false);
    } catch(e) { alert("Failed to save — check your connection."); }
    setSaving(false);
  };

  return (
    <div style={{ maxWidth: 760, animation: "fadeIn 0.25s ease" }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: "0 0 4px" }}>{t.costing}</h2>
      <p style={{ color: C.muted, fontSize: 13, margin: "0 0 20px" }}>Track labor, materials, and expenses per project.</p>

      <div style={S.card}>
        <Field label="Project">
          <JobPicker projects={projects} value={selId} onPick={pick} lang={getLang()} />
        </Field>
        {projects.length === 0 && <div style={{ color: C.muted, fontSize: 12 }}>No projects yet — submit a survey or assessment to create one.</div>}
      </div>

      {selected && (
        <>
          {/* Time on site — from the assessments filed on this job */}
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>⏱ Time on site{siteTimes.length ? ` — ${Math.round(siteManHours * 100) / 100} man-hr` : ""}</h3>
              {siteTimes.length > 0 && (
                <button style={S.btnS} onClick={mark(() => setLabor(p => [...p, ...siteTimes.filter(s => Number(s.manHours) > 0).map((s, i) => ({ id: Date.now() + i, who: `Crew on site${s.scope ? ` — ${s.scope.replace(/^[^—]*—\s*/, "")}` : ""} (${s.techs || 1} tech${Number(s.techs) > 1 ? "s" : ""}, ${fmtClock(s.arrived)}–${fmtClock(s.departed)})`, hours: String(s.manHours), rate: "60" }))]))}>
                  {I.plus} Add to labor @ $60/hr
                </button>
              )}
            </div>
            {siteTimes.length === 0 && <div style={{ color: C.muted, fontSize: 12 }}>No arrival / departure recorded yet — the crew enters it on the Service Assessment.</div>}
            {siteTimes.map(s => {
              const over = s.budget && Number(s.manHours) > Number(s.budget);
              return (
                <div key={s.id} style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", padding: "8px 0", borderTop: `1px solid ${C.border}`, fontSize: 13 }}>
                  <div style={{ flex: 1, minWidth: 200 }}>
                    <div style={{ color: C.light, fontWeight: 600 }}>{s.scope ? s.scope.replace(/^[^—]*—\s*/, "") : "Service Assessment"}</div>
                    <div style={{ color: C.muted, fontSize: 12 }}>{s.when ? new Date(s.when).toLocaleDateString() : ""}{s.by ? ` · ${s.by}` : ""} · {fmtClock(s.arrived)} → {fmtClock(s.departed)} · {s.techs || 1} tech{Number(s.techs) > 1 ? "s" : ""}</div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ color: over ? C.danger : C.gold, fontWeight: 800 }}>{s.manHours != null ? `${s.manHours} man-hr` : "—"}</div>
                    {s.budget ? <div style={{ fontSize: 11, color: over ? C.danger : C.muted }}>{over ? `over by ${Math.round((Number(s.manHours) - Number(s.budget)) * 100) / 100}` : `paid ${s.budget}`}</div> : null}
                  </div>
                </div>
              );
            })}
            {siteTimes.length > 1 && siteBudget > 0 && (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, fontSize: 12, color: siteManHours > siteBudget ? C.danger : C.success, fontWeight: 600 }}>
                Total {Math.round(siteManHours * 100) / 100} man-hr actual vs {siteBudget} paid{siteManHours > siteBudget ? ` — ${Math.round((siteManHours - siteBudget) * 100) / 100} over` : ""}
              </div>
            )}
          </div>

          {/* Labor */}
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Labor — {money(laborTotal)}</h3>
              <button style={S.btnS} onClick={addLabor}>{I.plus} Add</button>
            </div>
            {labor.length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 14 }}>No labor entries yet</div>}
            {labor.map(l => (
              <CostRow key={l.id}>
                <input style={{ ...S.input, width: "100%", fontSize: 13, padding: "9px 10px", marginBottom: 8, boxSizing: "border-box" }} placeholder="Who / task description" value={l.who} onChange={e => updLabor(l.id, "who", e.target.value)} />
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <input style={{ ...S.input, width: 78, fontSize: 13, padding: "8px 10px", textAlign: "center" }} type="number" min="0" step="0.25" placeholder="Hours" value={l.hours} onChange={e => updLabor(l.id, "hours", e.target.value)} />
                  <span style={{ color: C.muted, fontSize: 12 }}>hrs ×</span>
                  <input style={{ ...S.input, width: 88, fontSize: 13, padding: "8px 10px", textAlign: "center" }} type="number" min="0" step="0.01" placeholder="Rate" value={l.rate} onChange={e => updLabor(l.id, "rate", e.target.value)} />
                  <span style={{ color: C.muted, fontSize: 12 }}>/hr =</span>
                  <span style={{ color: C.gold, fontWeight: 800, fontSize: 14 }}>{money(n(l.hours) * n(l.rate))}</span>
                  <button onClick={() => delLabor(l.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", marginLeft: "auto" }}>{I.trash}</button>
                </div>
              </CostRow>
            ))}
          </div>

          {/* Materials */}
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Materials — {money(matTotal)}</h3>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {(selected?.materials || []).length > 0 && (
                  <button style={{ ...S.btnS, borderColor: C.gold, color: C.gold }} onClick={syncFromProject}
                    title="Pull the project's material list in — keeps any prices you've already entered">
                    ↻ Sync from Project
                  </button>
                )}
                <button style={S.btnS} onClick={addMat}>{I.plus} Add</button>
              </div>
            </div>
            {mats.length === 0 && (
              <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 14 }}>
                No materials yet
                {(selected?.materials || []).length > 0 && " — use Sync from Project to pull in the project's list"}
              </div>
            )}
            {mats.map(m => (
              <CostRow key={m.id}>
                <MaterialPicker
                  value={m.name}
                  placeholder="Material name — stock and supplier catalog"
                  style={{ marginBottom: 4 }}
                  options={(() => {
                    const seen = new Set();
                    const opts = [];
                    inventory.forEach(i => {
                      const k = (i.name || "").toLowerCase();
                      if (!k || seen.has(k)) return;
                      seen.add(k);
                      opts.push({ key: "inv-" + i.id, label: i.name, sub: `In stock${i.unit_cost != null ? ` · $${Number(i.unit_cost).toFixed(2)}/${i.unit || "ea"}` : ""}`, raw: { name: i.name, sku: i.sku || "", unit: i.unit, price: i.unit_cost } });
                    });
                    MATERIALS_CATALOG.forEach(mc => {
                      const k = mc.name.toLowerCase();
                      if (seen.has(k)) return;
                      seen.add(k);
                      opts.push({ key: "cat-" + mc.sku, label: mc.name, sub: `${mc.sku}${mc.putUp ? " · " + mc.putUp : ""}`, raw: mc });
                    });
                    return opts;
                  })()}
                  onChange={(v) => updMat(m.id, "name", v)}
                  onPick={(o) => { setDirty(true); setMats(prev => prev.map(x => x.id === m.id ? {
                    ...x, name: o.raw.name, sku: o.raw.sku || x.sku || "",
                    unit: x.unit || o.raw.unit || "",
                    cost: x.cost || (o.raw.price != null ? String(o.raw.price) : ""),
                  } : x)); }}
                />
                {(m.sku || m.fromProject) && (
                  <div style={{ fontSize: 11, color: C.muted, marginBottom: 6, display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {m.sku && <span>{m.sku}</span>}
                    {m.fromProject && <span style={{ color: C.gold }}>· from project list</span>}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <input style={{ ...S.input, width: 68, fontSize: 13, padding: "8px 10px", textAlign: "center" }} type="number" min="0" step="any" placeholder="Qty" value={m.qty} onChange={e => updMat(m.id, "qty", e.target.value)} />
                  <input style={{ ...S.input, width: 68, fontSize: 13, padding: "8px 10px" }} placeholder="Unit" value={m.unit} onChange={e => updMat(m.id, "unit", e.target.value)} />
                  <span style={{ color: C.muted, fontSize: 12 }}>@</span>
                  <input style={{ ...S.input, width: 88, fontSize: 13, padding: "8px 10px", textAlign: "center" }} type="number" min="0" step="0.01" placeholder="Cost ea" value={m.cost} onChange={e => updMat(m.id, "cost", e.target.value)} />
                  <span style={{ color: C.gold, fontWeight: 800, fontSize: 14 }}>{money(n(m.qty) * n(m.cost))}</span>
                  <button onClick={() => delMat(m.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", marginLeft: "auto" }}>{I.trash}</button>
                </div>
              </CostRow>
            ))}
          </div>

          {/* Other costs */}
          <div style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Other Costs — {money(otherTotal)}</h3>
              <button style={S.btnS} onClick={addOther}>{I.plus} Add</button>
            </div>
            {other.length === 0 && <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 14 }}>Permits, rentals, disposal, engineering, etc.</div>}
            {other.map(o => (
              <CostRow key={o.id}>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <input style={{ ...S.input, flex: 1, fontSize: 13, padding: "8px 10px" }} placeholder="Description (e.g. Permit fee)" value={o.name} onChange={e => updOther(o.id, "name", e.target.value)} />
                  <input style={{ ...S.input, width: 100, fontSize: 13, padding: "8px 10px", textAlign: "center" }} type="number" min="0" step="0.01" placeholder="Amount" value={o.amount} onChange={e => updOther(o.id, "amount", e.target.value)} />
                  <button onClick={() => delOther(o.id)} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
                </div>
              </CostRow>
            ))}
          </div>

          {/* Summary */}
          <div style={{ ...S.card, border: `1px solid ${C.gold}44` }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 15, fontWeight: 700, color: C.light }}>Summary</h3>
            {[["Labor", laborTotal], ["Materials", matTotal], ["Other", otherTotal]].map(([lab, val]) => (
              <div key={lab} style={{ display: "flex", justifyContent: "space-between", padding: "7px 0", fontSize: 13, color: C.muted }}>
                <span>{lab}</span><span style={{ color: C.light, fontWeight: 600 }}>{money(val)}</span>
              </div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", borderTop: `1px solid ${C.border}`, fontSize: 14, color: C.light, fontWeight: 700 }}>
              <span>Subtotal (cost)</span><span>{money(subtotal)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 0", fontSize: 13, color: C.muted }}>
              <span>Margin / markup</span>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input style={{ ...S.input, width: 74, fontSize: 13, padding: "6px 10px", textAlign: "center" }} type="number" min="0" step="1" placeholder="0" value={margin} onChange={e => { setMargin(e.target.value); setDirty(true); }} />
                <span>%</span>
                <span style={{ color: C.light, fontWeight: 600, minWidth: 80, textAlign: "right" }}>{money(marginAmt)}</span>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", padding: "14px 0 4px", borderTop: `2px solid ${C.gold}44`, fontSize: 18, fontWeight: 900, color: C.gold }}>
              <span>Total</span><span>{money(grand)}</span>
            </div>
            <button onClick={save} disabled={saving || !dirty} style={{ ...S.btnP, width: "100%", justifyContent: "center", marginTop: 16, opacity: (saving || !dirty) ? 0.5 : 1 }}>
              {saving ? "Saving..." : dirty ? <>{I.check} Save to Project</> : "Saved ✓"}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────
// ─── Today queue ─────────────────────────────────────────────────────────────
// The coordinator's first screen: everything waiting on the office, sorted by
// what costs money first. Each row opens the record it talks about.
// ─── Office board ────────────────────────────────────────────────────────────
// Jonathan's daily list for the coordinator, and her handoff back — in the
// Hub instead of an email + a notes app. Items are tied to jobs.
const OFFICE_CATS = ["SCHEDULE", "CALL", "EMAIL", "HUB", "UPLOAD", "FOLLOW-UP", "DRIVE", "LIST", "PERMIT", "OTHER"];
const officeCatColor = (c) => ({ SCHEDULE: C.gold, CALL: C.info, EMAIL: C.info, HUB: C.greenLight || C.success, UPLOAD: C.success, "FOLLOW-UP": C.warning, DRIVE: C.muted, LIST: C.muted, PERMIT: C.warning }[c] || C.muted);

// Parse the numbered "N  CATEGORY  Title … Do: … Before you start: … Result goes: …"
// text the drafting chat / PDF produces. Good enough for copy-paste; AI covers the rest.
function parseOfficeList(text) {
  const lines = String(text || "").replace(/\r/g, "").split("\n");
  const items = []; let cur = null;
  const catRe = new RegExp(`^\\s*(\\d{1,2})[.)]?\\s+(${OFFICE_CATS.join("|")})\\b\\s*(.*)$`, "i");
  lines.forEach(raw => {
    const line = raw.replace(/\s+/g, " ").trim();
    if (!line) return;
    const m = line.match(catRe);
    if (m) { cur = { category: m[2].toUpperCase(), title: m[3].trim(), body: "" }; items.push(cur); return; }
    if (cur) cur.body += (cur.body ? " " : "") + line;
  });
  return items.map(it => {
    // The PDF's two columns interleave when copied, so work on the whole blob
    const blob = `${it.title} ${it.body}`.replace(/\s+/g, " ").trim();
    const pos = (label) => { const m = blob.match(new RegExp(`${label}\\s*:`, "i")); return m ? m.index : -1; };
    const marks = [["do", pos("Do")], ["before", pos("Before you start")], ["result", pos("Result goes")]].filter(x => x[1] >= 0).sort((a, b) => a[1] - b[1]);
    const part = (key) => { const i = marks.findIndex(x => x[0] === key); if (i < 0) return ""; const start = marks[i][1]; const end = i + 1 < marks.length ? marks[i + 1][1] : blob.length; return blob.slice(start, end).replace(/^[^:]*:\s*/, "").trim(); };
    const doText = part("do"), before = part("before"), result = part("result");
    const firstMark = marks.length ? marks[0][1] : blob.length;
    const title = blob.slice(0, firstMark).trim();
    return { category: it.category, title: title || doText.slice(0, 60), do: doText || blob, before: before || "Nothing.", result, projectIds: [], unmatched: [], schedule: [] };
  });
}
// Match a job by WO number, then by client/name words, against the open Hub list
function officeMatchProjects(text, projects) {
  const t = String(text || ""); const hits = new Set();
  const wos = [...t.matchAll(/\b(?:WO|FSD|SQ)[-\s]?0*(\d{3,})\b/gi)].map(m => m[1]);
  projects.forEach(p => {
    const wo = String(p.workOrder || "").replace(/\D/g, "");
    if (wo && wos.some(w => wo.endsWith(w))) hits.add(p.id);
  });
  const words = new Set(t.toLowerCase().match(/[a-záéíóúñ]{4,}/g) || []);
  projects.forEach(p => {
    const last = ((p.client || p.name || "").trim().split(/\s+/).pop() || "").toLowerCase();
    if (last.length >= 4 && words.has(last)) hits.add(p.id);
  });
  return [...hits];
}

function OfficeBoard({ user, t, isAdmin }) {
  const today = jobDayKey(new Date().toISOString());
  const [day, setDay] = useState(today);
  const [view, setView] = useState("list"); // list | handoff
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [crew, setCrew] = useState([]);
  const [handoff, setHandoff] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [draft, setDraft] = useState("");
  const [preview, setPreview] = useState(null); // items waiting to be added
  const [busy, setBusy] = useState("");
  const [emailHer, setEmailHer] = useState(true);
  const [expanded, setExpanded] = useState({});
  const by = user?.name || user?.email || "";
  const coordinatorEmail = (crew.find(c => c.role === "coordinator") || {}).email || "customerservice@solarability.com";

  const seedingRef = useRef(false);
  const load = useCallback(async (d) => {
    let [its, ho, pj, cr] = await Promise.all([db.getOfficeTodos(d), db.getOfficeHandoff(d), db.getProjects().catch(() => []), db.getCrewDirectory()]);
    // Daily items: every recurring template gets one copy per day (today or
    // earlier — never for a future day), so each day has its own tick + note.
    const todayKey = jobDayKey(new Date().toISOString());
    const templates = its.filter(x => x.kind === "recurring" && x.status !== "retired");
    const isCopyOf = (x, tp) => x.day === d && x.kind === "daily" && (x.recurring_id === tp.id || (x.source === "recurring" && x.title === tp.title));
    const missing = d <= todayKey ? templates.filter(tp => !its.some(x => isCopyOf(x, tp))) : [];
    if (missing.length && !seedingRef.current) {
      seedingRef.current = true; // one seed per board, even if load() runs twice
      try {
        const rows = await db.seedOfficeDaily(missing.map((tp, i) => ({ day: d, seq: -100 + i, kind: "daily", category: tp.category, title: tp.title, do_text: tp.do_text, before_text: tp.before_text, result_text: tp.result_text, project_ids: [], unmatched: [], schedule: [], status: "open", note: "", source: "recurring", recurring_id: tp.id, created_by: tp.created_by || "" })));
        its = [...its, ...rows.filter(r => !its.some(x => x.id === r.id))];
      } catch(e) { console.warn("daily seed failed:", e); }
      seedingRef.current = false;
    }
    setItems(its); setHandoff(ho?.note || ""); setProjects(pj || []); setCrew(cr || []); setLoaded(true);
  }, []);
  const loadRef = useRef("");
  if (loadRef.current !== day) { loadRef.current = day; setLoaded(false); load(day); }

  const projById = useMemo(() => Object.fromEntries(projects.map(p => [p.id, p])), [projects]);
  const openProjects = useMemo(() => projects.filter(p => p.status !== "completed"), [projects]);
  const openJob = (id) => { window.location.hash = `projects/id/${encodeURIComponent(id)}`; };

  const isDailyCopy = it => it.kind === "daily" && (!!it.recurring_id || it.source === "recurring");
  const carried = items.filter(it => it.kind === "daily" && !isDailyCopy(it) && it.day < day && it.status === "open");
  const daily = items.filter(it => isDailyCopy(it) && it.day === day);
  const todays = items.filter(it => it.kind === "daily" && !isDailyCopy(it) && it.day === day);
  const standing = items.filter(it => it.kind === "standing");
  const templates = items.filter(it => it.kind === "recurring" && it.status !== "retired");
  const [editDaily, setEditDaily] = useState(false);
  const patch = async (it, p) => { setItems(list => list.map(x => x.id === it.id ? { ...x, ...p } : x)); await db.updateOfficeTodo(it.id, p); };
  const setStatus = (it, status) => patch(it, { status, done_at: status === "open" ? null : new Date().toISOString(), done_by: status === "open" ? "" : by });

  // ── adding ──
  const toRows = (list, kind = "daily") => list.map((it, i) => ({
    day, seq: (todays.length + i + 1), kind, category: it.category || "OTHER", title: it.title || "", do_text: it.do || "", before_text: it.before || "", result_text: it.result || "",
    project_ids: it.projectIds || [], unmatched: it.unmatched || [], schedule: it.schedule || [], status: "open", note: "", source: it.source || "manual", created_by: by,
  }));
  const structure = async () => {
    if (!draft.trim()) return;
    setBusy("ai");
    try {
      const slim = openProjects.map(p => ({ id: p.id, name: p.name, client: p.client, address: p.address, workOrder: p.workOrder, partner: p.partner, status: p.status }));
      const its = await db.officeAI(draft, slim, crew.filter(c => c.email).map(c => ({ name: c.name, email: c.email })));
      setPreview(its.map(it => ({ ...it, source: "ai" })));
    } catch(e) { alert(`Couldn't structure it: ${e.message}\n\nYou can still "Paste formatted list" or add a blank item.`); }
    setBusy("");
  };
  const pasteFormatted = () => {
    const its = parseOfficeList(draft);
    if (!its.length) return alert("Didn't find numbered items. Expected lines like:\n1  SCHEDULE  Micah, Shirley … Do: … Before you start: … Result goes: …");
    setPreview(its.map(it => ({ ...it, projectIds: officeMatchProjects(`${it.title} ${it.do}`, openProjects), source: "paste" })));
  };
  const addBlank = () => setPreview([{ category: "OTHER", title: "", do: "", before: "Nothing.", result: "", projectIds: [], unmatched: [], schedule: [], source: "manual" }]);
  const commitPreview = async () => {
    const list = (preview || []).filter(it => (it.title || it.do || "").trim());
    if (!list.length) return setPreview(null);
    setBusy("save");
    try {
      const rows = await db.addOfficeTodos(toRows(list));
      setItems(cur => [...cur, ...rows]); setPreview(null); setDraft("");
      if (emailHer && isAdmin) { const ok = await db.emailOfficeList(coordinatorEmail, day, rows, by); if (!ok) alert("Added to the Hub, but the email didn't go out (send-notification)."); }
    } catch(e) { alert(`Couldn't add: ${e.message || e}\n\nHas supabase/OFFICE_BOARD_SQL.md been run?`); }
    setBusy("");
  };
  const updPreview = (i, p) => setPreview(list => list.map((x, j) => j === i ? { ...x, ...p } : x));

  const smallIn = { ...S.input, padding: "8px 10px", fontSize: 13 };
  const statusTone = (st) => st === "done" ? C.success : st === "blocked" ? C.danger : C.border;
  const renderCard = (it, n) => {
    const open = !!expanded[it.id];
    const jobs = (it.project_ids || []).map(id => projById[id]).filter(Boolean);
    return (
      <div style={{ background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, borderLeft: `4px solid ${statusTone(it.status)}`, padding: "10px 12px", marginBottom: 8, opacity: it.status === "done" ? 0.75 : 1 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 220, cursor: "pointer" }} onClick={() => setExpanded(e => ({ ...e, [it.id]: !open }))}>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {n != null && <span style={{ color: C.muted, fontSize: 12, fontWeight: 700, width: 20 }}>{n}</span>}
              <span style={{ fontSize: 10.5, fontWeight: 800, letterSpacing: 0.5, color: officeCatColor(it.category), border: `1px solid ${officeCatColor(it.category)}66`, borderRadius: 6, padding: "1px 6px" }}>{it.category}</span>
              <span style={{ fontSize: 14, fontWeight: 700, color: C.light, textDecoration: it.status === "done" ? "line-through" : "none" }}>{it.title || (it.do_text || "").slice(0, 70)}</span>
              {it.day < day && <span style={{ fontSize: 11, color: C.warning }}>carried from {fmtJobDate(`${it.day}T12:00:00`, { month: "short", day: "numeric" })}</span>}
            </div>
            {!open && it.do_text && <div style={{ fontSize: 12.5, color: C.muted, marginTop: 3, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{it.do_text}</div>}
          </div>
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            {["open", "done", "blocked"].map(st => (
              <button key={st} type="button" onClick={() => setStatus(it, st)} style={{ ...S.btnS, padding: "4px 9px", fontSize: 11.5, fontWeight: 700, border: `1px solid ${it.status === st ? statusTone(st) : C.border}`, color: it.status === st ? (st === "open" ? C.light : statusTone(st)) : C.muted, background: it.status === st && st !== "open" ? `${statusTone(st)}22` : "transparent" }}>{st === "open" ? "Open" : st === "done" ? "✓ Done" : "Blocked"}</button>
            ))}
          </div>
        </div>
        {(jobs.length > 0 || (it.unmatched || []).length > 0 || (it.schedule || []).length > 0) && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
            {jobs.map(p => <button key={p.id} type="button" onClick={() => openJob(p.id)} style={{ ...S.btnS, padding: "3px 9px", fontSize: 11.5 }}>📁 {p.name}{p.workOrder ? ` · ${p.workOrder}` : ""}</button>)}
            {(it.unmatched || []).map((u, i) => <span key={i} style={{ fontSize: 11.5, color: C.danger, border: `1px solid ${C.danger}66`, borderRadius: 6, padding: "3px 8px" }}>⚠ {u} — not in Hub</span>)}
            {(it.schedule || []).map((sc, i) => <span key={i} style={{ fontSize: 11.5, color: C.gold, border: `1px solid ${C.gold}55`, borderRadius: 6, padding: "3px 8px" }}>📅 {sc.label} · {sc.date} {sc.window}{sc.crewEmails?.length ? ` · ${sc.crewEmails.map(e => e.split("@")[0]).join(", ")}` : ""}</span>)}
          </div>
        )}
        {open && (
          <div style={{ marginTop: 10, display: "grid", gap: 8, fontSize: 13, color: C.light, lineHeight: 1.55 }}>
            {it.do_text && <div><strong style={{ color: C.gold }}>Do:</strong> <span style={{ whiteSpace: "pre-wrap" }}>{it.do_text}</span></div>}
            {it.before_text && <div><strong style={{ color: C.muted }}>Before you start:</strong> {it.before_text}</div>}
            {it.result_text && <div><strong style={{ color: C.muted }}>Result goes:</strong> {it.result_text}</div>}
            <Field label="Handoff note (what happened, who confirmed, what's blocking)">
              <textarea style={{ ...S.textarea, minHeight: 56 }} rows={2} defaultValue={it.note || ""} onBlur={e => { if (e.target.value !== (it.note || "")) patch(it, { note: e.target.value }); }} placeholder="e.g. Paul confirmed, Terence confirmed. Victor waiting on client to pick a date." />
            </Field>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap", fontSize: 11, color: C.muted }}>
              <span>{it.done_at ? `${it.status} · ${it.done_by || ""} · ${fmtJobDateTime(it.done_at)}` : `added ${it.created_by ? `by ${it.created_by}` : ""} ${it.created_at ? fmtJobDate(it.created_at, { month: "short", day: "numeric" }) : ""}`}</span>
              <span style={{ display: "flex", gap: 10 }}>
                {it.kind !== "standing" && <button type="button" onClick={() => patch(it, { day: today, carried_from: it.carried_from || it.day })} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", fontSize: 11, textDecoration: "underline" }} title="Move to today">move to today</button>}
                <button type="button" onClick={() => { if (window.confirm("Delete this item?")) { db.deleteOfficeTodo(it.id); setItems(list => list.filter(x => x.id !== it.id)); } }} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>delete</button>
              </span>
            </div>
          </div>
        )}
        {!open && it.note && <div style={{ marginTop: 6, fontSize: 12, color: it.status === "blocked" ? C.danger : C.muted }}>↳ {it.note}</div>}
      </div>
    );
  };

  const dayLabel = fmtJobDate(`${day}T12:00:00`, { weekday: "long", month: "long", day: "numeric" });
  const counts = { open: [...carried, ...daily, ...todays].filter(i => i.status === "open").length, done: [...daily, ...todays].filter(i => i.status === "done").length, blocked: [...carried, ...daily, ...todays].filter(i => i.status === "blocked").length };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10, marginBottom: 14 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.light }}>Office</h2>
          <div style={{ fontSize: 13, color: C.muted }}>{dayLabel} · {counts.open} open{counts.done ? ` · ${counts.done} done` : ""}{counts.blocked ? ` · ${counts.blocked} blocked` : ""}</div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <input type="date" style={{ ...smallIn, width: 160, colorScheme: "dark" }} value={day} onChange={e => e.target.value && setDay(e.target.value)} />
          {day !== today && <button type="button" onClick={() => setDay(today)} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }}>Today</button>}
          <button type="button" onClick={() => setView(view === "list" ? "handoff" : "list")} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px", border: `1px solid ${view === "handoff" ? C.gold : C.border}` }}>{view === "list" ? "Handoff" : "To-do list"}</button>
          <button type="button" onClick={() => load(day)} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }}>Refresh</button>
        </div>
      </div>

      {view === "handoff" && (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.light }}>Handoff — {dayLabel}</h3>
          <div style={{ fontSize: 12, color: C.muted, marginBottom: 10 }}>What got done, what's blocked and why, who confirmed, who couldn't be reached. Jonathan reads this on the dashboard the next morning.</div>
          <textarea style={{ ...S.textarea, minHeight: 120 }} rows={5} value={handoff} onChange={e => setHandoff(e.target.value)} onBlur={() => db.saveOfficeHandoff(day, handoff, by)} placeholder={"Confirmed: Paul, Terence.\nCouldn't reach: Victor (left VM).\nStill open: permit for Will County — they want the scope drawing.\nInvoices with no Hub project: 1116."} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>Saves when you click away.</div>
          <div style={{ marginTop: 14 }}>
            {["done", "blocked", "open"].map(st => {
              const list = [...carried, ...daily, ...todays].filter(i => i.status === st);
              if (!list.length) return null;
              return (
                <div key={st} style={{ marginBottom: 12 }}>
                  <div style={{ ...S.label, marginBottom: 6, color: statusTone(st) === C.border ? C.muted : statusTone(st) }}>{st === "done" ? "Done" : st === "blocked" ? "Blocked" : "Still open"} ({list.length})</div>
                  {list.map(it => (
                    <div key={it.id} style={{ fontSize: 13, color: C.light, padding: "6px 10px", background: C.surface, borderRadius: 8, marginBottom: 4, borderLeft: `3px solid ${statusTone(st)}` }}>
                      <span style={{ color: officeCatColor(it.category), fontSize: 10.5, fontWeight: 800, marginRight: 6 }}>{it.category}</span>{it.title}
                      {it.note && <div style={{ fontSize: 12, color: st === "blocked" ? C.danger : C.muted, marginTop: 2 }}>↳ {it.note}</div>}
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {view === "list" && (
        <>
          {/* Add items */}
          <div style={{ ...S.card, border: `1px solid ${C.gold}44` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.light }}>Add to {day === today ? "today's" : "this day's"} list</h3>
              {isAdmin && <label style={{ fontSize: 12, color: C.muted, display: "flex", gap: 6, alignItems: "center" }}><input type="checkbox" checked={emailHer} onChange={e => setEmailHer(e.target.checked)} /> also email the list to {coordinatorEmail}</label>}
            </div>
            {!preview && (
              <>
                <textarea style={{ ...S.textarea, minHeight: 90 }} rows={4} value={draft} onChange={e => setDraft(e.target.value)}
                  placeholder={"Type it rough, the way you'd text it:\nSchedule omnidian Schmitz (Roscoe) for the 17th 9-12, assign to Giancarlo y Diego. Schedule IGS Jeffery Timmer 17th 10-2 same crew. Check WO already in hub, if not add, send invite, create folder and share with crew.\n\n…or paste the numbered list from the drafting chat."} />
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                  <button type="button" disabled={!!busy || !draft.trim()} onClick={structure} style={{ ...S.btnP, fontSize: 13, padding: "8px 14px", opacity: busy || !draft.trim() ? 0.6 : 1 }}>{busy === "ai" ? "Structuring…" : "✨ Structure it"}</button>
                  <button type="button" disabled={!!busy || !draft.trim()} onClick={pasteFormatted} style={{ ...S.btnS, fontSize: 13, padding: "8px 14px" }}>Paste formatted list</button>
                  <button type="button" disabled={!!busy} onClick={addBlank} style={{ ...S.btnS, fontSize: 13, padding: "8px 14px" }}>+ Blank item</button>
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>"Structure it" matches names and WO numbers to Hub jobs and crew, flags jobs that aren't in the Hub yet, and pulls out the visits to schedule. Nothing is added until you confirm.</div>
              </>
            )}
            {preview && (
              <div>
                <div style={{ fontSize: 13, color: C.light, fontWeight: 700, marginBottom: 8 }}>{preview.length} item{preview.length === 1 ? "" : "s"} — check, edit, then add</div>
                {preview.map((it, i) => {
                  const jobs = (it.projectIds || []).map(id => projById[id]).filter(Boolean);
                  return (
                    <div key={i} style={{ background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, padding: 10, marginBottom: 8, display: "grid", gap: 6 }}>
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <select style={{ ...S.select, width: 140, padding: "6px 8px", fontSize: 12 }} value={it.category} onChange={e => updPreview(i, { category: e.target.value })}>{OFFICE_CATS.map(c => <option key={c} value={c}>{c}</option>)}</select>
                        <input style={{ ...smallIn, flex: 1 }} value={it.title} onChange={e => updPreview(i, { title: e.target.value })} placeholder="Title" />
                        <button type="button" onClick={() => setPreview(list => list.filter((_, j) => j !== i))} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
                      </div>
                      <textarea style={{ ...S.textarea, minHeight: 60, fontSize: 13 }} rows={3} value={it.do} onChange={e => updPreview(i, { do: e.target.value })} placeholder="Do: …" />
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                        <input style={smallIn} value={it.before} onChange={e => updPreview(i, { before: e.target.value })} placeholder="Before you start: …" />
                        <input style={smallIn} value={it.result} onChange={e => updPreview(i, { result: e.target.value })} placeholder="Result goes: …" />
                      </div>
                      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                        {jobs.map(p => <span key={p.id} style={{ fontSize: 11.5, color: C.light, border: `1px solid ${C.border}`, borderRadius: 6, padding: "3px 8px" }}>📁 {p.name} <button type="button" onClick={() => updPreview(i, { projectIds: it.projectIds.filter(x => x !== p.id) })} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 0 }}>✕</button></span>)}
                        {(it.unmatched || []).map((u, k) => <span key={k} style={{ fontSize: 11.5, color: C.danger }}>⚠ {u} — not in Hub</span>)}
                        {(it.schedule || []).map((sc, k) => <span key={k} style={{ fontSize: 11.5, color: C.gold }}>📅 {sc.label} · {sc.date} {sc.window}</span>)}
                        <div style={{ minWidth: 200, flex: 1 }}>
                          <JobPicker projects={openProjects} value="" onPick={(id) => { if (id && !(it.projectIds || []).includes(id)) updPreview(i, { projectIds: [...(it.projectIds || []), id] }); }} />
                        </div>
                      </div>
                    </div>
                  );
                })}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button type="button" disabled={!!busy} onClick={commitPreview} style={{ ...S.btnP, fontSize: 13, padding: "8px 14px" }}>{busy === "save" ? "Adding…" : `Add ${preview.length} to the list${emailHer && isAdmin ? " + email" : ""}`}</button>
                  <button type="button" onClick={() => setPreview(null)} style={{ ...S.btnS, fontSize: 13, padding: "8px 14px" }}>Cancel</button>
                </div>
              </div>
            )}
          </div>

          {!loaded && <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>}
          {loaded && carried.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ ...S.label, marginBottom: 6, color: C.warning }}>Carried over ({carried.length})</div>
              {carried.map((it, i) => <span key={it.id}>{renderCard(it, i + 1)}</span>)}
            </div>
          )}
          {loaded && (daily.length > 0 || isAdmin) && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ ...S.label, marginBottom: 6, display: "flex", justifyContent: "space-between" }}><span>Every day ({daily.length})</span>
                {isAdmin && <button type="button" onClick={() => setEditDaily(v => !v)} style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", fontSize: 12 }}>{editDaily ? "done editing" : "edit daily items"}</button>}
              </div>
              {editDaily && (
                <div style={{ ...S.card, padding: 12, marginBottom: 8 }}>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 8 }}>These are added to each day's list automatically when that day comes (today's are already there). Nothing to save here — add or retire, and it applies from the next day on.</div>
                  {templates.map(tp => (
                    <div key={tp.id} style={{ display: "flex", gap: 8, alignItems: "center", fontSize: 13, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 10.5, fontWeight: 800, color: officeCatColor(tp.category) }}>{tp.category}</span>
                      <span style={{ flex: 1, color: C.light }}>{tp.title}{tp.do_text ? <span style={{ color: C.muted }}> — {tp.do_text}</span> : null}</span>
                      <button type="button" onClick={async () => { if (!window.confirm(`Retire "${tp.title}" from the daily list?`)) return; await db.updateOfficeTodo(tp.id, { status: "retired" }); setItems(list => list.map(x => x.id === tp.id ? { ...x, status: "retired" } : x)); }} style={{ background: "none", border: "none", color: C.danger, cursor: "pointer", fontSize: 11, textDecoration: "underline" }}>retire</button>
                    </div>
                  ))}
                  <button type="button" onClick={async () => {
                    const title = window.prompt("Daily item — title:"); if (!title) return;
                    const doText = window.prompt("Do (what exactly):", "") || "";
                    const result = window.prompt("Result goes:", "") || "";
                    const cat = (window.prompt(`Category (${OFFICE_CATS.join(", ")}):`, "HUB") || "HUB").toUpperCase();
                    const [tp] = await db.addOfficeTodos([{ day: today, seq: 0, kind: "recurring", category: OFFICE_CATS.includes(cat) ? cat : "OTHER", title, do_text: doText, before_text: "Nothing.", result_text: result, project_ids: [], unmatched: [], schedule: [], status: "open", note: "", source: "manual", created_by: by }]);
                    setItems(list => [...list, tp]); loadRef.current = ""; // reseed today
                  }} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px", marginTop: 8 }}>+ Daily item</button>
                </div>
              )}
              {daily.map(it => <span key={it.id}>{renderCard(it, null)}</span>)}
              {day > today && daily.length === 0 && templates.length > 0 && !editDaily && (
                <div style={{ background: C.surface, borderRadius: 10, border: `1px dashed ${C.border}`, padding: "10px 12px" }}>
                  <div style={{ fontSize: 12, color: C.muted, marginBottom: 6 }}>Added automatically the morning of {fmtJobDate(`${day}T12:00:00`, { weekday: "short", month: "short", day: "numeric" })} — each with its own tick and note:</div>
                  {templates.map(tp => <div key={tp.id} style={{ fontSize: 13, color: C.light, padding: "3px 0" }}><span style={{ fontSize: 10.5, fontWeight: 800, color: officeCatColor(tp.category), marginRight: 8 }}>{tp.category}</span>{tp.title}</div>)}
                </div>
              )}
              {daily.length === 0 && templates.length === 0 && !editDaily && <div style={{ color: C.muted, fontSize: 13 }}>No daily items yet — "edit daily items" to add portal checks, survey forwarding, etc.</div>}
            </div>
          )}
          {loaded && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ ...S.label, marginBottom: 6 }}>{day === today ? "Today" : dayLabel} ({todays.length})</div>
              {todays.length === 0 && <div style={{ color: C.muted, fontSize: 13, padding: "10px 0" }}>Nothing added for this day yet.</div>}
              {todays.map((it, i) => <span key={it.id}>{renderCard(it, carried.length + i + 1)}</span>)}
            </div>
          )}
          {loaded && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ ...S.label, marginBottom: 6, color: C.muted, display: "flex", justifyContent: "space-between" }}><span>Independent work list — when the day's list is done ({standing.length})</span>
                {isAdmin && <button type="button" onClick={async () => { const title = window.prompt("Standing item:"); if (!title) return; const rows = await db.addOfficeTodos(toRows([{ category: "LIST", title, do: "", before: "Nothing.", result: "" }], "standing")); setItems(cur => [...cur, ...rows]); }} style={{ background: "none", border: "none", color: C.gold, cursor: "pointer", fontSize: 12 }}>+ add</button>}
              </div>
              {standing.map(it => <span key={it.id}>{renderCard(it, null)}</span>)}
            </div>
          )}
          <div style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.6 }}>Invoices, visits with nothing filed, submit-to-partner due dates, stale tasks and permits are generated on the dashboard's Today queue — they don't need to be on this list.</div>
        </>
      )}
    </div>
  );
}

// Dashboard card: yesterday's handoff at a glance (admin), or a nudge to write it (coordinator)
function OfficeHandoffCard({ user, isAdmin, setPage }) {
  const [data, setData] = useState(null);
  const ref = useRef(false);
  if (!ref.current) {
    ref.current = true;
    (async () => {
      const hos = await db.getRecentHandoffs(5);
      const today = jobDayKey(new Date().toISOString());
      const last = hos.find(h => h.day < today) || hos[0] || null;
      const lastDay = last?.day || jobDayKey(new Date(Date.now() - 86400000).toISOString());
      const items = await db.getOfficeDay(lastDay);
      const open = await db.getOfficeTodos(today);
      setData({ last, lastDay, items, openToday: open.filter(i => i.kind !== "standing" && i.status === "open").length });
    })();
  }
  if (!data) return null;
  const done = data.items.filter(i => i.status === "done"), blocked = data.items.filter(i => i.status === "blocked"), open = data.items.filter(i => i.status === "open");
  if (!data.last && !data.items.length && !data.openToday) return null;
  return (
    <div style={{ ...S.card, marginBottom: 20, border: `1px solid ${blocked.length ? C.danger : C.border}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 6 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.light }}>Office · handoff {fmtJobDate(`${data.lastDay}T12:00:00`, { weekday: "short", month: "short", day: "numeric" })}
          <span style={{ color: C.muted, fontWeight: 500, fontSize: 12 }}> · {done.length} done{blocked.length ? ` · ${blocked.length} blocked` : ""}{open.length ? ` · ${open.length} carried` : ""}{data.openToday ? ` · ${data.openToday} open today` : ""}</span></h3>
        <button type="button" onClick={() => { window.location.hash = "office"; }} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }}>Open Office</button>
      </div>
      {data.last?.note ? <div style={{ fontSize: 13, color: C.light, whiteSpace: "pre-wrap", lineHeight: 1.55 }}>{data.last.note}</div>
        : <div style={{ fontSize: 13, color: C.muted }}>{isAdmin ? "No handoff note written for that day." : "Write today's handoff under Office → Handoff before you clock out."}</div>}
      {blocked.length > 0 && <div style={{ marginTop: 8 }}>{blocked.map(i => <div key={i.id} style={{ fontSize: 12.5, color: C.danger }}>⛔ {i.title}{i.note ? ` — ${i.note}` : ""}</div>)}</div>}
    </div>
  );
}

function TodayQueue({ user, submissions, setPage }) {
  const [projects, setProjects] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [comms, setComms] = useState([]);
  const [attaching, setAttaching] = useState(null); // comms row being attached to a job
  const [loaded, setLoaded] = useState(false);
  const money = canSeeMoney(user);
  const loadRef = useRef(false);
  const load = useCallback(async () => {
    const [p, q, e, c] = await Promise.all([
      db.getProjects(undefined, { fresh: true }).catch(() => []),
      money ? db.getServiceQuotes().catch(() => []) : Promise.resolve([]),
      money ? db.getRREstimates().catch(() => []) : Promise.resolve([]),
      db.getCommsQueue().catch(() => []),
    ]);
    setProjects(p || []); setQuotes(q || []); setEstimates(e || []); setComms(c || []); setLoaded(true);
  }, [money]);
  if (!loadRef.current) { loadRef.current = true; load(); }

  const todayKey = jobDayKey(new Date().toISOString());
  const tomorrowKey = jobDayKey(new Date(Date.now() + 86400000).toISOString());
  const daysAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  // Whole days from today to a YYYY-MM-DD (noon vs noon, so DST never shifts it). Negative = past.
  const dayDiff = (ymd) => { if (!ymd) return null; const d = new Date(`${ymd}T12:00:00`); const t0 = new Date(); t0.setHours(12, 0, 0, 0); return Math.round((d - t0) / 86400000); };
  const open = (hash) => { window.location.hash = hash; };
  const openProject = (id) => open(`projects/id/${encodeURIComponent(id)}`);

  const rows = [];
  const push = (group, r) => rows.push({ group, ...r });

  // 1. Visits today / tomorrow — crew, invite status
  projects.forEach(p => (p.visits || []).forEach(v => {
    if (v.cancelled || !v.scheduled_at || v.status === "done") return;
    const k = jobDayKey(v.scheduled_at);
    if (k !== todayKey && k !== tomorrowKey) return;
    const crew = (p.members || []).length;
    const invited = (v.invited || []).length;
    push("Visits today & tomorrow", {
      tone: invited === 0 || crew === 0 || !folderUrlOf(p) ? C.warning : C.success, when: v.scheduled_at,
      title: `${k === todayKey ? "Today" : "Tomorrow"} ${fmtJobTime(v.scheduled_at)} · ${p.name}${(p.visits || []).filter(x => !x.cancelled).length > 1 ? ` · ${v.label}` : ""}`,
      sub: `${p.address || ""}${crew ? ` · ${crew} crew` : " · no crew assigned"}${invited ? ` · ${invited} invited` : " · invite not sent"}${folderUrlOf(p) ? "" : " · NO PROTON FOLDER"}${permitRequired(p) && !p.permit.issuedOn ? " · PERMIT NOT ISSUED" : ""}`,
      action: () => open(`schedule/${encodeURIComponent(p.id)}`),
    });
  }));
  // 2. Past visits with nothing filed
  projects.forEach(p => (p.visits || []).forEach(v => {
    if (v.cancelled || !v.scheduled_at || v.status === "done") return;
    const k = jobDayKey(v.scheduled_at);
    const age = daysAgo(v.scheduled_at);
    if (k >= todayKey || age > 21 || (v.invited || []).length === 0) return;
    push("Visits done, nothing filed", { tone: C.danger, when: v.scheduled_at, title: `${p.name}${(p.visits || []).filter(x => !x.cancelled).length > 1 ? ` · ${v.label}` : ""}`, sub: `${fmtJobDate(v.scheduled_at, { weekday: "short", month: "short", day: "numeric" })} · no assessment or closeout yet (${age}d)`, action: () => openProject(p.id) });
  }));
  // 3. Reports waiting for review
  (submissions || []).filter(s => s.status === "submitted" && s.type !== "JHA").slice(-20).reverse().forEach(s => {
    const age = daysAgo(s.submittedAt);
    push("Reports to review", { tone: age >= 1 ? C.warning : C.info, when: s.submittedAt, title: `${s.type}${s.serviceType ? ` — ${s.serviceType}` : ""} · ${s.siteName || s.homeowner?.name || ""}`, sub: `by ${s.submittedBy || "—"} · ${age === 0 ? "today" : `${age}d ago`}${s.partnerName ? ` · ${s.partnerName}` : ""}${s.submitDue ? ` · due to partner ${s.submitDue}` : ""}`, action: () => open("admin") });
  });
  // 4. Submit to partner — due dates on open tasks
  projects.forEach(p => (p.tasks || []).forEach(tk => {
    if (tk.done || !isShareTask(tk)) return;
    const dd = dayDiff(tk.due);
    if (p.status === "completed") return;
    push("Submit to partner", { tone: dd === null ? C.muted : dd < 0 ? C.danger : dd === 0 ? C.warning : C.info, when: tk.due || "9999", title: `${tk.name} · ${p.name}`, sub: dd === null ? "no due date" : dd < 0 ? `OVERDUE ${-dd}d (${tk.due})` : dd === 0 ? "due TODAY" : `due in ${dd}d (${tk.due})`, action: () => openProject(p.id) });
  }));
  // 5. Invoices (money logins)
  if (money) projects.forEach(p => {
    const inv = p.invoice || {};
    if (inv.paidDate) return;
    const submitted = (p.tasks || []).some(tk => tk.done && isShareTask(tk));
    const doneOn = completedOn(p);
    const sinceDone = doneOn ? daysAgo(doneOn) : null;
    if (!inv.sentDate && (inv.number || inv.amount || submitted || p.status === "pending_submission" || p.status === "completed")) {
      push("Invoices", { tone: p.status === "completed" ? C.danger : C.warning, when: doneOn || p.updated_at || p.created_at || "", title: `Invoice not sent · ${p.name}`, sub: `${inv.amount ? `$${inv.amount}` : "no amount"}${inv.number ? ` · #${inv.number}` : ""}${submitted ? " · report already shared with partner" : ""}${sinceDone != null ? ` · work done ${sinceDone}d ago` : ""}`, action: () => openProject(p.id) });
    } else if (inv.sentDate) {
      // Partners pay about a month after the work: due soon at 25 days,
      // overdue past 30, counted from the invoice date.
      const age = daysAgo(inv.sentDate);
      if (age >= 25) push("Invoices", { tone: age >= 30 ? C.danger : C.warning, when: inv.sentDate, title: `${age >= 30 ? "Overdue" : "Due soon"} · ${p.name}`, sub: `#${inv.number || "—"} · $${inv.amount || "—"} · sent ${inv.sentDate} (${age}d)${sinceDone != null ? ` · work done ${sinceDone}d ago` : ""} — follow up for payment`, action: () => openProject(p.id) });
    }
  });
  // 6. Quotes / estimates waiting (money logins)
  if (money) {
    quotes.filter(q => q.status === "pending").forEach(q => {
      const age = q.receivedAt ? daysAgo(q.receivedAt) : 0;
      push("Quotes waiting on us", { tone: age >= 1 ? C.warning : C.info, when: q.receivedAt || "", title: `${q.partner} ${q.sqNumber || ""} · ${q.client || q.address}`, sub: `${q.serviceName || ""}${q.offered ? ` · offered $${q.offered}` : ""} · ${age === 0 ? "today" : `${age}d`}`, action: () => open("quotes") });
    });
    estimates.filter(e => e.status === "pending").forEach(e => {
      const age = e.createdAt ? daysAgo(e.createdAt) : 0;
      if (age < 7) return;
      push("Estimates waiting on the customer", { tone: age >= 14 ? C.warning : C.muted, when: e.createdAt, title: `${e.client || e.address} · $${Math.round(e.price || 0).toLocaleString()}`, sub: `sent ${age}d ago — follow up`, action: () => open("estimator") });
    });
  }

  // 7. Calls & texts the office hasn't dealt with (Quo)
  comms.forEach(c => {
    const proj = c.project_id ? projects.find(p => p.id === c.project_id) : null;
    const who = proj ? `${proj.client || proj.name}` : (c.matched_name || c.external_number || "unknown number");
    const what = c.kind === "sms" ? `"${(c.body || "").slice(0, 90)}${(c.body || "").length > 90 ? "…" : ""}"`
      : c.kind === "voicemail" ? `voicemail: ${(c.body || "").slice(0, 90) || "(no transcript)"}`
      : c.status === "missed" ? "missed call"
      : Array.isArray(c.next_steps) && c.next_steps.length ? `next: ${c.next_steps[0]}` : "call";
    const age = daysAgo(c.at);
    push("Calls & texts to handle", {
      tone: !c.project_id && !c.partner ? C.warning : (c.kind === "sms" || c.status === "missed" ? C.warning : C.info), when: c.at,
      title: `${commIcon(c)} ${who}${c.partner ? ` (${c.partner})` : ""}${!c.project_id ? " · not on a job" : ""}`,
      sub: `${what} · ${age === 0 ? fmtJobTime(c.at) : `${age}d ago`}`,
      comm: c,
      action: () => { if (c.project_id) openProject(c.project_id); else setAttaching(c); },
    });
  });
  const attachTo = async (c, projectId) => {
    if (!projectId) return;
    await db.attachComm(c.id, projectId);
    setComms(cs => cs.map(x => x.id === c.id ? { ...x, project_id: projectId } : x));
    setAttaching(null);
  };
  const handled = async (c) => { await db.markCommHandled(c.id); setComms(cs => cs.filter(x => x.id !== c.id)); };

  // 9. Permits & inspections — one row per permitted job that's waiting on something
  projects.filter(p => p.status !== "completed" && p.status !== "on_hold").forEach(p => {
    const st = permitStage(p);
    const pm = p.permit;
    if (st.key === "none" || st.key === "closed") return;
    const nextVisit = (p.visits || []).filter(v => !v.cancelled && v.scheduled_at && v.status !== "done").map(v => v.scheduled_at).sort()[0];
    const visitDays = nextVisit ? dayDiff(jobDayKey(nextVisit)) : null;
    const visitTxt = nextVisit ? ` · visit ${visitDays === 0 ? "TODAY" : visitDays < 0 ? `${-visitDays}d ago` : `in ${visitDays}d`}` : "";
    let tone = st.tone, when = "5", sub = st.label;
    if (st.key === "undecided") {
      // Only chase the decision when a visit is on the books — otherwise it's noise
      if (!nextVisit) return;
      tone = C.warning; when = "1"; sub = `Visit scheduled but nobody said if it needs a permit${visitTxt}`;
    } else if (st.key === "to_apply") {
      tone = nextVisit ? C.danger : C.warning; when = nextVisit ? "0" : "2"; sub = `${st.label}${visitTxt}`;
    } else if (st.key === "applied") {
      const d = pm.appliedOn ? -dayDiff(pm.appliedOn) : null;
      tone = d !== null && d > 14 ? C.warning : C.info; when = "3"; sub = `Waiting on ${pm.jurisdiction || "the city"}${d !== null ? ` · applied ${d}d ago` : ""}${d !== null && d > 14 ? " — follow up" : ""}${visitTxt}`;
      if (nextVisit && visitDays !== null && visitDays <= 2) { tone = C.danger; when = "0"; }
    } else if (st.key === "issued") {
      const visitsDone = (p.visits || []).some(v => v.status === "done");
      if (!visitsDone) return; // installed yet? no inspection to book
      tone = C.warning; when = "4"; sub = "Work done — schedule the inspection";
    } else if (st.key === "inspection") {
      const i = pm.inspections.filter(x => x.scheduledOn && !x.result).sort((a, b) => a.scheduledOn.localeCompare(b.scheduledOn))[0];
      const d = dayDiff(i.scheduledOn);
      tone = d < 0 ? C.danger : d === 0 ? C.warning : C.info; when = i.scheduledOn; sub = d < 0 ? `${i.type} was ${-d}d ago — enter the result` : d === 0 ? `${i.type} TODAY — someone on site?` : `${i.type} in ${d}d (${i.scheduledOn})`;
    } else if (st.key === "failed") {
      tone = C.danger; when = "0"; sub = `${st.label} — fix corrections, schedule re-inspection`;
    } else if (st.key === "passed") {
      tone = C.info; when = "6"; sub = "All inspections passed — enter the close-out date (and ComEd PTO if solar)";
    }
    push("Permits & inspections", { tone, when, title: `${p.name}${pm?.number ? ` · permit #${pm.number}` : ""}`, sub: `${sub}${pm?.jurisdiction && st.key !== "applied" ? ` · ${pm.jurisdiction}` : ""}`, action: () => openProject(p.id) });
  });

  // 8. Same house, two open jobs
  duplicateGroups(projects).forEach(g => {
    push("Possible duplicate jobs", { tone: C.warning, when: "0", title: `${g.length} open jobs at ${g[0].address}`, sub: g.map(x => x.name).join("  ·  ") + " — open one and use Merge", action: () => openProject(g[0].id) });
  });
  const groups = ["Possible duplicate jobs", "Visits today & tomorrow", "Permits & inspections", "Visits done, nothing filed", "Reports to review", "Submit to partner", "Invoices", "Quotes waiting on us", "Estimates waiting on the customer", "Calls & texts to handle"];
  const byGroup = groups.map(g => ({ g, items: rows.filter(r => r.group === g).sort((a, b) => String(a.when).localeCompare(String(b.when))) })).filter(x => x.items.length);
  const urgent = rows.filter(r => r.tone === C.danger).length;

  return (
    <div style={{ ...S.card, marginBottom: 20, border: `1px solid ${urgent ? C.danger : C.gold}55` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: C.light }}>Today <span style={{ color: C.muted, fontWeight: 500, fontSize: 12 }}>· {rows.length} waiting on the office{urgent ? ` · ${urgent} overdue` : ""}</span></h3>
        <button onClick={load} style={{ ...S.btnS, fontSize: 12, padding: "6px 10px" }}>Refresh</button>
      </div>
      {!loaded && <div style={{ color: C.muted, fontSize: 13 }}>Loading…</div>}
      {loaded && rows.length === 0 && <div style={{ color: C.success, fontSize: 13, fontWeight: 600 }}>✓ Nothing waiting. Visits are invited, reports reviewed, partners submitted, invoices out.</div>}
      {byGroup.map(({ g, items }) => (
        <div key={g} style={{ marginBottom: 12 }}>
          <div style={{ ...S.label, marginBottom: 6 }}>{g} <span style={{ color: C.muted, fontWeight: 500 }}>({items.length})</span></div>
          {items.map((r, i) => (
            <div key={i} onClick={r.action} style={{ display: "flex", gap: 10, alignItems: "center", padding: "9px 12px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, borderLeft: `4px solid ${r.tone}`, marginBottom: 6, cursor: "pointer" }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: C.light, overflowWrap: "anywhere" }}>{r.title}</div>
                <div style={{ fontSize: 12, color: r.tone === C.danger ? C.danger : C.muted, overflowWrap: "anywhere" }}>{r.sub}</div>
              </div>
              {r.comm && <button type="button" onClick={(e) => { e.stopPropagation(); handled(r.comm); }} title="Mark handled" style={{ ...S.btnS, fontSize: 11, padding: "4px 8px", flexShrink: 0 }}>✓</button>}
              <span style={{ color: C.muted, flexShrink: 0 }}>{I.chevron || "›"}</span>
            </div>
          ))}
        </div>
      ))}
      {attaching && (
        <Modal onClose={() => setAttaching(null)}>
          <h3 style={{ margin: "0 0 6px", fontSize: 16, fontWeight: 800, color: C.light }}>Attach to a job</h3>
          <div style={{ fontSize: 13, color: C.muted, marginBottom: 10 }}>{commIcon(attaching)} {attaching.external_number || "unknown"} · {fmtJobDateTime(attaching.at)}</div>
          {attaching.body && <div style={{ fontSize: 13, color: C.light, background: C.surface, borderRadius: 8, padding: 10, marginBottom: 10, whiteSpace: "pre-wrap" }}>{attaching.body}</div>}
          {Array.isArray(attaching.summary) && attaching.summary.length > 0 && <div style={{ fontSize: 12.5, color: C.light, marginBottom: 10 }}>{attaching.summary.map((s, i) => <div key={i}>• {s}</div>)}</div>}
          <JobPicker projects={projects.filter(p => p.status !== "completed")} value="" onPick={(id) => attachTo(attaching, id)} />
          <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>Tip: put this number in the job's Homeowner Phone and future calls/texts attach themselves.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button onClick={() => { handled(attaching); setAttaching(null); }} style={{ ...S.btnS, fontSize: 12 }}>Not a job — mark handled</button>
            <button onClick={() => setAttaching(null)} style={{ ...S.btnS, fontSize: 12, marginLeft: "auto" }}>Close</button>
          </div>
        </Modal>
      )}
    </div>
  );
}

function Dashboard({ submissions, setPage }) {
  const jha = submissions.filter(s => s.type === "JHA").length;
  const surv = submissions.filter(s => s.type === "Site Survey").length;
  const month = submissions.filter(s => new Date(s.submittedAt).getMonth() === new Date().getMonth()).length;
  const stats = [
    { label: "Total Forms", val: submissions.length, color: C.gold, icon: I.shield },
    { label: "JHA Reports", val: jha, color: C.warning, icon: I.hazard },
    { label: "Site Surveys", val: surv, color: C.info, icon: I.clip },
    { label: "This Month", val: month, color: C.success, icon: I.bolt },
  ];

  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: "0 0 4px" }}>Safety Dashboard</h2>
      <p style={{ color: C.muted, margin: "0 0 24px", fontSize: 13 }}>Overview of your safety submissions</p>

      <div style={{ display: "flex", gap: 10, marginBottom: 20, flexWrap: "wrap" }}>
        <button style={{ ...S.btnP, flex: 1, justifyContent: "center", minWidth: 160 }} onClick={() => setPage("jha")}>{I.hazard} New JHA</button>
        <button style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 160, borderColor: C.info, color: C.info }} onClick={() => setPage("survey")}>{I.clip} TSA Survey</button>
        <button style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 160, borderColor: C.success, color: C.success }} onClick={() => setPage("assessment")}>{I.wrench} Assessment</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 12, marginBottom: 20 }}>
        {stats.map(s => (
          <div key={s.label} style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div><div style={{ fontSize: 11, color: C.muted, marginBottom: 6, fontWeight: 600 }}>{s.label}</div><div style={{ fontSize: 30, fontWeight: 900, color: s.color, letterSpacing: -1 }}>{s.val}</div></div>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: `${s.color}18`, display: "flex", alignItems: "center", justifyContent: "center", color: s.color }}>{s.icon}</div>
            </div>
          </div>
        ))}
      </div>

      {submissions.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 50 }}>
          <div style={{ width: 50, height: 50, borderRadius: 14, background: `${C.gold}15`, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px", color: C.gold }}>{I.shield}</div>
          <div style={{ color: C.muted, fontSize: 14, fontWeight: 600 }}>No submissions yet</div>
          <div style={{ color: `${C.muted}66`, fontSize: 12, marginTop: 4 }}>Create your first report using the buttons above</div>
        </div>
      ) : (
        <div style={S.card}>
          <h3 style={{ margin: "0 0 14px", fontSize: 14, fontWeight: 700, color: C.light }}>Recent Submissions</h3>
          {submissions.slice(-15).reverse().map((s, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 6, flexWrap: "wrap", gap: 8 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: s.type === "JHA" ? C.warnBg : s.type === "Service Assessment" ? C.successBg : C.infoBg, display: "flex", alignItems: "center", justifyContent: "center", color: s.type === "JHA" ? C.warning : s.type === "Service Assessment" ? C.success : C.info, flexShrink: 0 }}>
                  {s.type === "JHA" ? I.hazard : s.type === "Service Assessment" ? I.wrench : I.clip}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: C.light, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.title || s.siteName}{s.serviceType ? ` — ${s.serviceType}` : ""}</div>
                  <div style={{ color: C.muted, fontSize: 11, marginTop: 1 }}>{s.location || s.address} · {s.supervisor || s.inspector}</div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                <span style={S.badge(STATUS_COLORS[s.status || "submitted"])}>{STATUS_LABELS[s.status || "submitted"]}</span>
                <button onClick={async () => generatePDF(await db.hydrateSubmission(s))} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", padding: 4 }} title="Export PDF">{I.download}</button>
                <span style={{ color: C.muted, fontSize: 11 }}>{new Date(s.submittedAt).toLocaleDateString()}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Admin Panel ─────────────────────────────────────────────────────────────
// Section divider inside the admin survey editor
const SecTitle = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "22px 0 12px" }}>
    <span style={{ fontSize: 11, letterSpacing: "1.4px", textTransform: "uppercase", fontWeight: 800, color: C.gold, whiteSpace: "nowrap" }}>{children}</span>
    <span style={{ flex: 1, height: 1, background: C.border }} />
  </div>
);

function AdminPanel({ submissions, setSubmissions }) {
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detail, setDetail] = useState(null);
  // Post-submission correction of the roof section and notes
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  // After approving a report: what the office does next, with the links
  const [postApprove, setPostApprove] = useState(null);
  // Service Assessment correction before approval
  const [saEditing, setSaEditing] = useState(false);
  const [sa, setSa] = useState({});
  const startSaEdit = (d) => {
    setSa({
      serviceType: d.serviceType || "",
      client: { name: "", address: "", phone: "", email: "", ...(d.client || {}) },
      referral: { source: "", name: "", ...(d.referral || {}) },
      system: { size: "", installer: "", installDate: "", panelMake: "", inverterMake: "", numPanels: "", ...(d.system || {}) },
      issue: d.issue || "", findings: d.findings || "", rootCause: d.rootCause || "", recommendations: d.recommendations || "",
      fee: d.fee ?? "",
      partnerFields: (d.partnerFields || []).map(pf => ({ ...pf })),
    });
    setSaEditing(true);
  };
  const so = (section, field, v) => setSa(p => ({ ...p, [section]: { ...p[section], [field]: v } }));
  const saveSaEdit = async (thenApprove) => {
    if (!detail?.id && USE_SUPABASE) { alert("This submission has no database record to update."); return; }
    setSavingEdit(true);
    const changes = { ...sa };
    if (!canSeeMoney(localCache.loadUser())) delete changes.fee;
    const ok = await db.updateSurveyFields(detail.id, changes);
    setSavingEdit(false);
    if (!ok && USE_SUPABASE) { alert("Failed to save — check your connection."); return; }
    const mirrored = { siteName: changes.client?.name || detail.siteName, address: changes.client?.address || detail.address,
      ...(thenApprove ? { status: "approved" } : {}) };
    const updated = { ...detail, ...changes, ...mirrored };
    setSubmissions(prev => prev.map(s => (s === detail || (s.id && s.id === detail.id)) ? updated : s));
    if (thenApprove && detail.id) db.updateStatus("survey_submissions", detail.id, "approved");
    setSaEditing(false);
    if (thenApprove) { setPostApprove({ type: detail.type, site: updated.siteName || "", projectId: detail.project_id || "", partner: updated.partnerName || "", due: updated.submitDue || "", serviceType: updated.serviceType || "" }); setDetail(null); }
    else setDetail(updated);
  };

  const startEdit = (d) => {
    setEdits({
      homeowner: { name: "", address: "", drawingsAvailable: "", ...(d.homeowner || {}) },
      meter: { make: "", locationDesc: "", utilityEntrance: "", transformerVolt: "", ...(d.meter || {}) },
      grounding: d.grounding || [],
      groundingOther: d.groundingOther || "",
      panel: { brand: "", mainBreakerRating: "", busBarRating: "", voltageMeasured: "", voltageType: "", spacesAvailable: "", locationDesc: "", ...(d.panel || {}) },
      subpanel: { exists: "", location: "", makeModel: "", mainBreakerRating: "", busBarRating: "", voltageMeasured: "", spacesAvailable: "", locationDesc: "", ...(d.subpanel || {}) },
      inverterLocation: d.inverterLocation || "",
      comms: { modemPresent: "", modemLocation: "", openPorts: "", hardwireRun: "", wifiSignal: "", cellularCoverage: "", ...(d.comms || {}) },
      interconnection: d.interconnection || [],
      interconnectionOther: d.interconnectionOther || "",
      electricalNotes: d.electricalNotes || "",
      roofing: { make: "", condition: "", roofAccess: "", atticAccess: "", ...(d.roofing || {}) },
      roof: { pitch: "", azimuth: "", buildingHeight: "", heightToEave: "", roofingType: "", roofingTypeOther: "", structureType: "", structureTypeOther: "", rafterSize: "", rafterSizeOther: "", rafterSpacing: "", rafterSpacingOther: "", ...(d.roof || {}) },
      // Per-plane details live on arrays. Surveys filed before Arrays existed
      // kept them on the roof; load those into Array 1 so editing keeps them.
      arrays: (d.arrays && d.arrays.length > 0)
        ? d.arrays.map(a => ({ ...a }))
        : (() => {
            const ro = d.roof || {};
            const withOther = (x, o) => x === "Other" && o ? o : (x || (o || ""));
            const hasLegacy = ["pitch", "azimuth", "structureType", "rafterSize", "rafterSpacing"].some(k => ro[k]);
            return hasLegacy ? [{
              id: 1, roof: "", angle: ro.pitch || "", azimuth: ro.azimuth || "",
              structureType: withOther(ro.structureType, ro.structureTypeOther),
              rafterSize: withOther(ro.rafterSize, ro.rafterSizeOther),
              rafterSpacing: withOther(ro.rafterSpacing, ro.rafterSpacingOther),
              notes: "", photoUrls: [],
            }] : [];
          })(),
      structureDetails: d.structureDetails || [],
      structureDetailsOther: d.structureDetailsOther || "",
      roofNotes: d.roofNotes || "",
      adders: { roofPitch: "", trenching: "", conduitRun: "", battery: "", rooftopAccess: "", rentals: "", roofAttachments: "", electricalUpgrades: "", groundingViolations: "", other: "", ...(d.adders || {}) },
    });
    setEditing(true);
  };

  // Helpers so the edit form reads like the original survey form
  const eo = (section, field, v) => setEdits(p => ({ ...p, [section]: { ...p[section], [field]: v } }));
  const ea = (idx, field, v) => setEdits(p => ({ ...p, arrays: (p.arrays || []).map((a, i) => i === idx ? { ...a, [field]: v } : a) }));
  const etoggle = (key, opt) => setEdits(p => ({
    ...p, [key]: (p[key] || []).includes(opt) ? p[key].filter(x => x !== opt) : [...(p[key] || []), opt],
  }));

  const saveEdit = async () => {
    if (!detail?.id) { alert("This submission has no database record to update."); return; }
    setSavingEdit(true);
    const ok = await db.updateSurveyFields(detail.id, edits);
    setSavingEdit(false);
    if (!ok) { alert("Failed to save — check your connection."); return; }
    // siteName/address mirror the columns we just updated server-side
    const mirrored = {
      ...(edits.homeowner?.name ? { siteName: edits.homeowner.name } : {}),
      ...(edits.homeowner?.address ? { address: edits.homeowner.address } : {}),
    };
    const updated = { ...detail, ...edits, ...mirrored };
    setSubmissions(prev => prev.map(s => s.id === detail.id ? { ...s, ...edits, ...mirrored } : s));
    setDetail(updated);
    setEditing(false);
  };

  const filtered = useMemo(() => {
    let list = submissions;
    if (filter !== "all") list = list.filter(s => s.status === filter || (filter === "jha" && s.type === "JHA") || (filter === "survey" && s.type === "Site Survey"));
    if (search) list = list.filter(s => JSON.stringify(s).toLowerCase().includes(search.toLowerCase()));
    return list.slice().reverse();
  }, [submissions, filter, search]);

  // Takes the submission itself, not a row index: the list is filtered and
  // reversed, so an index into it is not an index into `submissions`.
  const updateStatus = (target, status) => {
    const s = typeof target === "number" ? filtered[target] : target;
    if (!s) return;
    setSubmissions(prev => prev.map(x => (x === s || (x.id && x.id === s.id)) ? { ...x, status } : x));
    if (s.id) db.updateStatus(s.type === "JHA" ? "jha_submissions" : "survey_submissions", s.id, status);
  };

  const counts = {
    all: submissions.length,
    submitted: submissions.filter(s => s.status === "submitted").length,
    approved: submissions.filter(s => s.status === "approved").length,
    flagged: submissions.filter(s => s.status === "flagged").length,
  };

  return (
    <div style={{ animation: "fadeIn 0.25s ease" }}>
      <h2 style={{ fontSize: 24, fontWeight: 800, color: C.light, margin: "0 0 4px" }}>Admin Panel</h2>
      <p style={{ color: C.muted, margin: "0 0 24px", fontSize: 13 }}>Review, approve, and manage all safety submissions</p>
      {postApprove && (
        <div style={{ ...S.card, border: `1px solid ${C.success}66`, marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: C.success, fontWeight: 800, fontSize: 14 }}>✓ Approved — {postApprove.type}{postApprove.serviceType ? ` (${postApprove.serviceType})` : ""} · {postApprove.site}</div>
              <div style={{ color: C.light, fontSize: 13, marginTop: 6, lineHeight: 1.6 }}>
                {postApprove.partner
                  ? <>Next: submit the report and photos to <strong>{postApprove.partner}</strong>{postApprove.due ? <> by <strong style={{ color: C.warning }}>{postApprove.due}</strong></> : null}, tick "Submit to {postApprove.partner}" on the job, then enter the invoice.</>
                  : <>Next: send the customer their report, then enter the invoice on the job.</>}
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {postApprove.projectId && <button onClick={() => { window.location.hash = `projects/id/${encodeURIComponent(postApprove.projectId)}`; }} style={{ ...S.btnP, fontSize: 12, padding: "8px 12px" }}>Open job</button>}
              <button onClick={() => setPostApprove(null)} style={{ ...S.btnS, fontSize: 12, padding: "8px 12px" }}>Dismiss</button>
            </div>
          </div>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 20 }}>
        {[{ l: "Total", v: counts.all, c: C.gold }, { l: "Pending", v: counts.submitted, c: C.info }, { l: "Approved", v: counts.approved, c: C.success }, { l: "Flagged", v: counts.flagged, c: C.warning }].map(s => (
          <div key={s.l} style={{ ...S.card, padding: 16, textAlign: "center" }}>
            <div style={{ fontSize: 11, color: C.muted, fontWeight: 600 }}>{s.l}</div>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.c }}>{s.v}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <div style={{ position: "relative", flex: 1, minWidth: 200 }}>
          <div style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)", color: C.muted }}>{I.search}</div>
          <input style={{ ...S.input, paddingLeft: 38 }} placeholder="Search submissions..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        {["all", "submitted", "approved", "flagged"].map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{ ...S.btnS, padding: "8px 14px", fontSize: 12, background: filter === f ? `${C.gold}18` : "transparent", color: filter === f ? C.gold : C.muted, borderColor: filter === f ? `${C.gold}44` : C.border, textTransform: "capitalize" }}>{f}</button>
        ))}
      </div>

      {/* Table */}
      {filtered.length === 0 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 40 }}>
          <div style={{ color: C.muted, fontSize: 14 }}>No submissions match your filters</div>
        </div>
      ) : (
        <div style={S.card}>
          {filtered.map((s, idx) => (
            <div key={idx} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: C.surface, borderRadius: 10, border: `1px solid ${C.border}`, marginBottom: 6, flexWrap: "wrap" }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: s.type === "JHA" ? C.warnBg : s.type === "Service Assessment" ? C.successBg : C.infoBg, display: "flex", alignItems: "center", justifyContent: "center", color: s.type === "JHA" ? C.warning : s.type === "Service Assessment" ? C.success : C.info, flexShrink: 0 }}>
                {s.type === "JHA" ? I.hazard : s.type === "Service Assessment" ? I.wrench : I.clip}
              </div>
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: C.light }}>{s.title || s.siteName}{s.serviceType ? ` — ${s.serviceType}` : ""}</div>
                <div style={{ color: C.muted, fontSize: 11 }}>{s.submittedBy || s.inspector || s.supervisor} · {new Date(s.submittedAt).toLocaleDateString()}</div>
              </div>
              <span style={S.badge(STATUS_COLORS[s.status || "submitted"])}>{STATUS_LABELS[s.status || "submitted"]}</span>
              <div style={{ display: "flex", gap: 6 }}>
                <button onClick={async () => setDetail(await db.hydrateSubmission(s))} style={{ ...S.btnS, padding: "6px 10px", fontSize: 11 }} title="View">{I.eye}</button>
                <button onClick={async () => generatePDF(await db.hydrateSubmission(s))} style={{ ...S.btnS, padding: "6px 10px", fontSize: 11 }} title="PDF">{I.pdf}</button>
                <button onClick={() => updateStatus(s, "approved")} style={{ ...S.btnSuccess, padding: "6px 10px", fontSize: 11 }} title="Approve">{I.checkCircle}</button>
                <button onClick={() => updateStatus(s, "flagged")} style={{ ...S.btnDanger, padding: "6px 10px", fontSize: 11, background: `${C.warning}22`, color: C.warning, borderColor: `${C.warning}44` }} title="Flag">{I.flag}</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail Modal */}
      {detail && (
        <Modal onClose={() => { setDetail(null); setEditing(false); setSaEditing(false); }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
            <div>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: C.light }}>{detail.title || detail.siteName}</h3>
              <p style={{ margin: "4px 0 0", color: C.muted, fontSize: 13 }}>{detail.type} · {new Date(detail.submittedAt).toLocaleString()}</p>
            </div>
            <button onClick={() => setDetail(null)} style={{ background: "none", border: "none", color: C.muted, cursor: "pointer" }}>{I.x}</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
            {detail.type === "JHA" ? (
              <>{["location", "date", "supervisor", "crew"].map(k => detail[k] && <div key={k}><div style={S.label}>{k}</div><div style={{ color: C.light, fontSize: 14 }}>{detail[k]}</div></div>)}</>
            ) : (
              <>{["address", "date", "inspector", "weather", "systemSize"].map(k => detail[k] && <div key={k}><div style={S.label}>{k}</div><div style={{ color: C.light, fontSize: 14 }}>{detail[k]}{k === "systemSize" ? " kW" : ""}</div></div>)}</>
            )}
          </div>
          {detail.description && <div style={{ background: C.surface, borderRadius: 8, padding: 12, marginBottom: 12, fontSize: 13, color: C.light }}>{detail.description}</div>}
          {detail.hazards && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Hazards ({detail.hazards.length})</div>
              {detail.hazards.map((h, i) => <div key={i} style={{ background: C.surface, borderRadius: 8, padding: 10, marginBottom: 6, fontSize: 12 }}><span style={{ color: C.gold, fontWeight: 700 }}>Step {i + 1}:</span> <span style={{ color: C.light }}>{h.step}</span> → <span style={{ color: RISK_COLORS[h.risk] }}>{h.risk}</span> → <span style={{ color: C.muted }}>{h.control}</span></div>)}
            </div>
          )}
          {detail.checklist && Array.isArray(detail.checklist) && detail.checklist.length > 0 && (
            <div style={{ marginBottom: 12 }}>
              <div style={S.label}>Photo Checklist ({detail.checklist.filter(c => c.checked).length}/{detail.checklist.length} completed)</div>
              <div style={{ maxHeight: 400, overflowY: "auto", fontSize: 12 }}>
                {detail.checklist.map((c, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}22` }}>
                    <span style={{ color: c.checked ? C.success : C.danger, flexShrink: 0 }}>{c.checked ? "✓" : "✗"}</span>
                    <span style={{ color: C.light, flex: 1 }}>{c.label}</span>
                    {(() => {
                      // New surveys store photoUrls[]; older ones a single photoUrl
                      const urls = (Array.isArray(c.photoUrls) && c.photoUrls.length > 0)
                        ? c.photoUrls
                        : (c.photoUrl ? [c.photoUrl] : []);
                      const shown = urls.filter(u => u && !u.startsWith("data:"));
                      if (shown.length === 0) return null;
                      return (
                        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                          {shown.slice(0, 3).map((u, k) => (
                            <img loading="lazy" decoding="async" key={k} src={u} alt={c.label} onClick={() => openPhoto(u, shown)} style={{ width: 48, height: 48, objectFit: "cover", borderRadius: 6, border: `1px solid ${C.border}`, cursor: "zoom-in" }}
                              onError={(e) => { e.target.style.display = "none"; }} />
                          ))}
                          {shown.length > 3 && (
                            <div style={{ width: 48, height: 48, borderRadius: 6, border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: C.muted, fontWeight: 700 }}>
                              +{shown.length - 3}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            </div>
          )}
          {detail.notes && typeof detail.notes === "string" && <div><div style={S.label}>Notes</div><div style={{ color: C.light, fontSize: 13 }}>{detail.notes}</div></div>}
          
          {/* Electrical notes for surveys */}
          {detail.electricalNotes && <div style={{ marginTop: 8 }}><div style={S.label}>Electrical Notes</div><div style={{ color: C.light, fontSize: 13 }}>{detail.electricalNotes}</div></div>}
          
          {/* TSA Survey sections */}
          {detail.type === "Site Survey" && detail.meter && (
            <div style={{ marginTop: 12 }}>
              <div style={S.label}>Meter / Service</div>
              <div style={{ background: C.surface, borderRadius: 8, padding: 10, fontSize: 12, color: C.light }}>
                {detail.meter.make && <div>Make: {detail.meter.make}</div>}
                {detail.meter.utilityEntrance && <div>Entrance: {detail.meter.utilityEntrance}</div>}
                {detail.meter.transformerVolt && <div>Voltage: {detail.meter.transformerVolt}</div>}
              </div>
            </div>
          )}
          {detail.type === "Site Survey" && detail.panel && (
            <div style={{ marginTop: 8 }}>
              <div style={S.label}>Panel</div>
              <div style={{ background: C.surface, borderRadius: 8, padding: 10, fontSize: 12, color: C.light }}>
                {detail.panel.brand && <div>Brand: {detail.panel.brand}</div>}
                {detail.panel.mainBreakerRating && <div>Main Breaker: {detail.panel.mainBreakerRating}A</div>}
                {detail.panel.busBarRating && <div>Bus Bar: {detail.panel.busBarRating}A</div>}
                {detail.panel.voltageMeasured && <div>Voltage: {detail.panel.voltageMeasured} {detail.panel.voltageType || ""}</div>}
                {detail.panel.spacesAvailable && <div>Spaces: {detail.panel.spacesAvailable}</div>}
              </div>
            </div>
          )}
          {detail.type === "Site Survey" && (detail.arrays || []).length > 0 && (
            <div style={{ marginTop: 8, marginBottom: 8 }}>
              <div style={S.label}>Arrays</div>
              {detail.arrays.map(a => (
                <div key={a.id} style={{ background: C.surface, borderRadius: 8, padding: 10, fontSize: 12, color: C.light, marginBottom: 6 }}>
                  <div style={{ fontWeight: 700, color: C.gold, marginBottom: 3 }}>Array {a.id}{a.roof ? ` — ${a.roof}` : ""}</div>
                  <div>Pitch: {a.angle || "—"} · Azimuth: {a.azimuth || "—"}</div>
                  <div>{a.structureType || "Structure —"} · {a.rafterSize || "—"} @ {a.rafterSpacing || "—"}</div>
                  {a.notes && <div style={{ color: C.muted, marginTop: 3 }}>{a.notes}</div>}
                  {(a.photoUrls || []).length > 0 && <div style={{ color: C.muted, marginTop: 3 }}>📷 {a.photoUrls.length} photo{a.photoUrls.length === 1 ? "" : "s"}</div>}
                </div>
              ))}
            </div>
          )}

          {detail.type === "Site Survey" && detail.interconnection?.length > 0 && (
            <div style={{ marginTop: 8 }}><div style={S.label}>Interconnection</div><div style={{ color: C.light, fontSize: 12 }}>{detail.interconnection.join(", ")}</div></div>
          )}
          
          {/* Service Assessment details */}
          {detail.type === "Service Assessment" && (
            <div style={{ marginTop: 8 }}>
              {detail.partnerName && <div style={{ marginBottom: 8 }}><span style={S.badge(C.gold)}>Partner: {detail.partnerName}</span></div>}
              {detail.serviceType && <div style={{ marginBottom: 8 }}><div style={S.label}>Service Type</div><div style={{ color: C.gold, fontSize: 14, fontWeight: 700 }}>{detail.serviceType}</div></div>}
              {detail.partnerFields?.length > 0 && (
                <div style={{ marginBottom: 8 }}>
                  <div style={S.label}>{detail.partnerName} — Required Fields</div>
                  <div style={{ background: C.surface, borderRadius: 8, padding: 10, fontSize: 12, color: C.light }}>
                    {detail.partnerFields.map((pf, i) => <div key={i}>{pf.label}: {pf.value || "—"}</div>)}
                  </div>
                </div>
              )}
              {detail.referral?.name && <div style={{ marginBottom: 8 }}><div style={S.label}>Referred By</div><div style={{ color: C.light, fontSize: 13 }}>{detail.referral.name}{detail.referral.source ? ` (${detail.referral.source})` : ""}</div></div>}
              {detail.client?.phone && <div style={{ marginBottom: 8 }}><div style={S.label}>Phone</div><div style={{ color: C.light, fontSize: 13 }}>{detail.client.phone}</div></div>}
              {detail.system && (detail.system.size || detail.system.installer) && (
                <div style={{ marginBottom: 8 }}>
                  <div style={S.label}>System Info</div>
                  <div style={{ background: C.surface, borderRadius: 8, padding: 10, fontSize: 12, color: C.light }}>
                    {detail.system.size && <div>Size: {detail.system.size} kW</div>}
                    {detail.system.numPanels && <div>Panels: {detail.system.numPanels}</div>}
                    {detail.system.installer && <div>Installer: {detail.system.installer}</div>}
                    {detail.system.installDate && <div>Install Date: {detail.system.installDate}</div>}
                    {detail.system.panelMake && <div>Panel: {detail.system.panelMake}</div>}
                    {detail.system.inverterMake && <div>Inverter: {detail.system.inverterMake}</div>}
                  </div>
                </div>
              )}
              {detail.issue && <div style={{ marginBottom: 8 }}><div style={S.label}>Reported Issue</div><div style={{ color: C.light, fontSize: 13, background: C.surface, borderRadius: 8, padding: 10 }}>{detail.issue}</div></div>}
              {detail.rootCause && <div style={{ marginBottom: 8 }}><div style={S.label}>Root Cause</div><div style={{ color: C.light, fontSize: 13, background: C.surface, borderRadius: 8, padding: 10 }}>{detail.rootCause}</div></div>}
              {detail.recommendations && <div style={{ marginBottom: 8 }}><div style={S.label}>Recommendations</div><div style={{ color: C.light, fontSize: 13, background: C.surface, borderRadius: 8, padding: 10 }}>{detail.recommendations}</div></div>}
              {detail.findings && <div style={{ marginBottom: 8 }}><div style={S.label}>Additional Findings</div><div style={{ color: C.light, fontSize: 13 }}>{detail.findings}</div></div>}
            </div>
          )}
          
          {/* Photos - search ALL possible data sources */}
          {(() => {
            const ap = []; const seen = new Set();
            const add = (u, lb, nm) => { if (!u || seen.has(u) || u.startsWith("data:")) return; seen.add(u); ap.push({ url: u, label: lb || "", name: nm || "" }); };
            // Labeled sources first; flat photo_urls last (see generatePDF)
            (detail.partnerPhotos || []).forEach(pp => (pp.urls || []).forEach(u => add(u, pp.label)));
            const addFromItem = (ci) => {
              if (Array.isArray(ci.photoUrls)) ci.photoUrls.forEach(u => add(u, ci.label, ci.photoName));
              add(ci.photoUrl, ci.label, ci.photoName); // legacy single-photo surveys
            };
            if (Array.isArray(detail.checklist)) detail.checklist.forEach(addFromItem);
            if (Array.isArray(detail.photoChecklist)) detail.photoChecklist.forEach(addFromItem);
            if (detail.checklistData?.checklistPhotos) detail.checklistData.checklistPhotos.forEach(cp => add(cp.url, cp.label, cp.name));
            (detail.arrays || []).forEach(a => (a.photoUrls || []).forEach(u => add(u, `Array ${a.id}${a.roof ? " — " + a.roof : ""}`)));
            if (Array.isArray(detail.photos)) detail.photos.forEach(p => add(typeof p === "string" ? p : p.url, p.label, p.name));
            if (Array.isArray(detail.photo_urls)) detail.photo_urls.forEach(u => add(typeof u === "string" ? u : u.url));
            // Try parsing photo_urls if it's a string (JSON stringified)
            if (typeof detail.photo_urls === "string") { try { JSON.parse(detail.photo_urls).forEach(u => add(u)); } catch(e) {} }
            if (ap.length === 0) return null;
            return (<div style={{ marginTop: 12 }}>
              <div style={S.label}>Photos ({ap.length})</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
                {ap.map((p, i) => (<div key={i} style={{ background: C.surface, borderRadius: 10, overflow: "hidden", border: `1px solid ${C.border}` }}>
                  <img loading="lazy" decoding="async" src={p.url} alt={p.label || ""} onClick={() => openPhoto(p.url, ap.map(x => x.url))} style={{ width: "100%", height: 100, objectFit: "cover", display: "block", cursor: "zoom-in" }} onError={(e) => { e.target.parentElement.style.display = "none"; }} />
                  {(p.label || p.name) && <div style={{ padding: "6px 8px", fontSize: 10, color: C.muted, fontWeight: 600 }}>{p.label || p.name}</div>}
                </div>))}
              </div>
            </div>);
          })()}
          {/* Service Assessment correction — fix what the crew typed before it goes out */}
          {saEditing && detail.type === "Service Assessment" && (
            <div style={{ ...S.card, border: `1px solid ${C.gold}44`, marginTop: 16 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.gold }}>Editing Service Assessment</h3>
              <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>Photos and time on site stay as the crew captured them. Everything else here can be corrected.</p>

              <SecTitle>Client</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Client Name"><input style={S.input} value={sa.client.name} onChange={e => so("client", "name", e.target.value)} /></Field>
                <Field label="Phone"><input style={S.input} value={sa.client.phone} onChange={e => so("client", "phone", e.target.value)} /></Field>
                <div style={{ gridColumn: "1/-1" }}><Field label="Address"><input style={S.input} value={sa.client.address} onChange={e => so("client", "address", e.target.value)} /></Field></div>
                <Field label="Email"><input style={S.input} value={sa.client.email} onChange={e => so("client", "email", e.target.value)} /></Field>
                <Field label="Service Type">
                  <select style={S.input} value={sa.serviceType} onChange={e => setSa(p => ({ ...p, serviceType: e.target.value }))}>
                    {[...new Set([sa.serviceType, "Installation Closeout", ...SERVICE_TYPES.map(s => s.label)].filter(Boolean))].map(l => <option key={l} value={l}>{l}</option>)}
                  </select>
                </Field>
                <Field label="Referred By"><input style={S.input} value={sa.referral.name} onChange={e => so("referral", "name", e.target.value)} /></Field>
                <Field label="Referral Source"><input style={S.input} value={sa.referral.source} onChange={e => so("referral", "source", e.target.value)} /></Field>
              </div>

              <SecTitle>System</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Field label="Size (kW)"><input style={S.input} value={sa.system.size} onChange={e => so("system", "size", e.target.value)} /></Field>
                <Field label="# Panels"><input style={S.input} value={sa.system.numPanels} onChange={e => so("system", "numPanels", e.target.value)} /></Field>
                <Field label="Install Date"><input style={S.input} value={sa.system.installDate} onChange={e => so("system", "installDate", e.target.value)} /></Field>
                <Field label="Installer"><input style={S.input} value={sa.system.installer} onChange={e => so("system", "installer", e.target.value)} /></Field>
                <Field label="Panel Make"><input style={S.input} value={sa.system.panelMake} onChange={e => so("system", "panelMake", e.target.value)} /></Field>
                <Field label="Inverter Make"><input style={S.input} value={sa.system.inverterMake} onChange={e => so("system", "inverterMake", e.target.value)} /></Field>
              </div>

              {sa.partnerFields.length > 0 && (<>
                <SecTitle>{detail.partnerName || "Partner"} — Required Fields</SecTitle>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  {sa.partnerFields.map((pf, i) => (
                    <Field key={i} label={pf.label}><input style={S.input} value={pf.value || ""} onChange={e => setSa(p => ({ ...p, partnerFields: p.partnerFields.map((x, j) => j === i ? { ...x, value: e.target.value } : x) }))} /></Field>
                  ))}
                </div>
              </>)}

              <SecTitle>Assessment</SecTitle>
              <Field label="Reported Issue"><textarea style={S.textarea} value={sa.issue} onChange={e => setSa(p => ({ ...p, issue: e.target.value }))} /></Field>
              <Field label="Root Cause"><textarea style={S.textarea} value={sa.rootCause} onChange={e => setSa(p => ({ ...p, rootCause: e.target.value }))} /></Field>
              <Field label="Recommendations"><textarea style={S.textarea} value={sa.recommendations} onChange={e => setSa(p => ({ ...p, recommendations: e.target.value }))} /></Field>
              <Field label="Additional Findings"><textarea style={S.textarea} value={sa.findings} onChange={e => setSa(p => ({ ...p, findings: e.target.value }))} /></Field>
              {canSeeMoney(localCache.loadUser()) && <Field label="Fee ($)"><input style={{ ...S.input, maxWidth: 160 }} value={sa.fee} onChange={e => setSa(p => ({ ...p, fee: e.target.value }))} /></Field>}

              <div style={{ display: "flex", gap: 8, marginTop: 8, flexWrap: "wrap" }}>
                <button disabled={savingEdit} onClick={() => saveSaEdit(false)} style={{ ...S.btnP, flex: 1, justifyContent: "center", minWidth: 130 }}>{savingEdit ? "Saving…" : "Save changes"}</button>
                <button disabled={savingEdit} onClick={() => saveSaEdit(true)} style={{ ...S.btnSuccess, flex: 1, justifyContent: "center", minWidth: 130 }}>{I.checkCircle} Save & Approve</button>
                <button disabled={savingEdit} onClick={() => setSaEditing(false)} style={{ ...S.btnS, justifyContent: "center" }}>Cancel</button>
              </div>
            </div>
          )}
          {/* Full post-submission correction of the survey */}
          {editing && (detail.type === "Site Survey") && (
            <div style={{ ...S.card, border: `1px solid ${C.gold}44`, marginTop: 16 }}>
              <h3 style={{ margin: "0 0 4px", fontSize: 15, fontWeight: 700, color: C.gold }}>Editing Survey</h3>
              <p style={{ color: C.muted, fontSize: 12, margin: "0 0 16px" }}>Photos and the photo checklist aren't editable here — those stay as the crew captured them.</p>

              <SecTitle>Homeowner</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Homeowner Name"><input style={S.input} value={edits.homeowner.name} onChange={e => eo("homeowner", "name", e.target.value)} /></Field>
                <Field label="Address"><input style={S.input} value={edits.homeowner.address} onChange={e => eo("homeowner", "address", e.target.value)} /></Field>
                <Field label="Drawings Available">
                  <select style={S.select} value={edits.homeowner.drawingsAvailable} onChange={e => eo("homeowner", "drawingsAvailable", e.target.value)}>
                    <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </Field>
              </div>

              <SecTitle>Utility Meter</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Meter Make"><input style={S.input} value={edits.meter.make} onChange={e => eo("meter", "make", e.target.value)} /></Field>
                <Field label="Meter Location"><input style={S.input} value={edits.meter.locationDesc} onChange={e => eo("meter", "locationDesc", e.target.value)} /></Field>
                <Field label="Utility Entrance"><input style={S.input} value={edits.meter.utilityEntrance} onChange={e => eo("meter", "utilityEntrance", e.target.value)} /></Field>
                <Field label="Transformer Voltage"><input style={S.input} value={edits.meter.transformerVolt} onChange={e => eo("meter", "transformerVolt", e.target.value)} /></Field>
              </div>

              <SecTitle>Grounding</SecTitle>
              <Field label="Grounding Type(s)">
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {GROUNDING_OPTIONS.map(opt => (
                    <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.light, cursor: "pointer" }}>
                      <input type="checkbox" checked={(edits.grounding || []).includes(opt)} onChange={() => etoggle("grounding", opt)} /> {opt}
                    </label>
                  ))}
                  <input style={{ ...S.input, marginTop: 4, fontSize: 13 }} placeholder="Other..." value={edits.groundingOther} onChange={e => setEdits(p => ({ ...p, groundingOther: e.target.value }))} />
                </div>
              </Field>

              <SecTitle>Main Service Panel</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Panel Brand"><input style={S.input} value={edits.panel.brand} onChange={e => eo("panel", "brand", e.target.value)} /></Field>
                <Field label="Main Breaker (A)"><input style={S.input} value={edits.panel.mainBreakerRating} onChange={e => eo("panel", "mainBreakerRating", e.target.value)} /></Field>
                <Field label="Bus Bar (A)"><input style={S.input} value={edits.panel.busBarRating} onChange={e => eo("panel", "busBarRating", e.target.value)} /></Field>
                <Field label="Voltage Measured"><input style={S.input} value={edits.panel.voltageMeasured} onChange={e => eo("panel", "voltageMeasured", e.target.value)} /></Field>
                <Field label="Voltage Type"><input style={S.input} value={edits.panel.voltageType} onChange={e => eo("panel", "voltageType", e.target.value)} /></Field>
                <Field label="Spaces Available"><input style={S.input} value={edits.panel.spacesAvailable} onChange={e => eo("panel", "spacesAvailable", e.target.value)} /></Field>
                <Field label="Panel Location"><input style={S.input} value={edits.panel.locationDesc} onChange={e => eo("panel", "locationDesc", e.target.value)} /></Field>
              </div>

              <SecTitle>Subpanel</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Subpanel Exists?">
                  <select style={S.select} value={edits.subpanel.exists} onChange={e => eo("subpanel", "exists", e.target.value)}>
                    <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </Field>
                <Field label="Location"><input style={S.input} value={edits.subpanel.location} onChange={e => eo("subpanel", "location", e.target.value)} /></Field>
                <Field label="Make / Model"><input style={S.input} value={edits.subpanel.makeModel} onChange={e => eo("subpanel", "makeModel", e.target.value)} /></Field>
                <Field label="Main Breaker (A)"><input style={S.input} value={edits.subpanel.mainBreakerRating} onChange={e => eo("subpanel", "mainBreakerRating", e.target.value)} /></Field>
                <Field label="Bus Bar (A)"><input style={S.input} value={edits.subpanel.busBarRating} onChange={e => eo("subpanel", "busBarRating", e.target.value)} /></Field>
                <Field label="Voltage Measured"><input style={S.input} value={edits.subpanel.voltageMeasured} onChange={e => eo("subpanel", "voltageMeasured", e.target.value)} /></Field>
                <Field label="Spaces Available"><input style={S.input} value={edits.subpanel.spacesAvailable} onChange={e => eo("subpanel", "spacesAvailable", e.target.value)} /></Field>
                <Field label="Notes"><input style={S.input} value={edits.subpanel.locationDesc} onChange={e => eo("subpanel", "locationDesc", e.target.value)} /></Field>
              </div>

              <SecTitle>Inverter & Comms</SecTitle>
              <Field label="Proposed Inverter Location"><input style={S.input} value={edits.inverterLocation} onChange={e => setEdits(p => ({ ...p, inverterLocation: e.target.value }))} /></Field>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Modem Present?">
                  <select style={S.select} value={edits.comms.modemPresent} onChange={e => eo("comms", "modemPresent", e.target.value)}>
                    <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </Field>
                <Field label="Modem Location"><input style={S.input} value={edits.comms.modemLocation} onChange={e => eo("comms", "modemLocation", e.target.value)} /></Field>
                <Field label="Open Ports"><input style={S.input} value={edits.comms.openPorts} onChange={e => eo("comms", "openPorts", e.target.value)} /></Field>
                <Field label="Hardwire Run Possible"><input style={S.input} value={edits.comms.hardwireRun} onChange={e => eo("comms", "hardwireRun", e.target.value)} /></Field>
                <Field label="WiFi Signal"><input style={S.input} value={edits.comms.wifiSignal} onChange={e => eo("comms", "wifiSignal", e.target.value)} /></Field>
                <Field label="Cellular Coverage"><input style={S.input} value={edits.comms.cellularCoverage} onChange={e => eo("comms", "cellularCoverage", e.target.value)} /></Field>
              </div>

              <SecTitle>Interconnection</SecTitle>
              <Field label="Interconnection Strategy">
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {INTERCONNECTION_OPTIONS.map(opt => (
                    <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.light, cursor: "pointer" }}>
                      <input type="checkbox" checked={(edits.interconnection || []).includes(opt)} onChange={() => etoggle("interconnection", opt)} /> {opt}
                    </label>
                  ))}
                  <input style={{ ...S.input, marginTop: 4, fontSize: 13 }} placeholder="Other..." value={edits.interconnectionOther} onChange={e => setEdits(p => ({ ...p, interconnectionOther: e.target.value }))} />
                </div>
              </Field>
              <Field label="Electrical Notes">
                <textarea style={S.textarea} rows={3} value={edits.electricalNotes} onChange={e => setEdits(p => ({ ...p, electricalNotes: e.target.value }))} />
              </Field>

              <SecTitle>Roofing</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Field label="Roofing Make"><input style={S.input} value={edits.roofing.make} onChange={e => eo("roofing", "make", e.target.value)} /></Field>
                <Field label="Roof Condition"><input style={S.input} value={edits.roofing.condition} onChange={e => eo("roofing", "condition", e.target.value)} /></Field>
                <Field label="Roof Access?">
                  <select style={S.select} value={edits.roofing.roofAccess} onChange={e => eo("roofing", "roofAccess", e.target.value)}>
                    <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </Field>
                <Field label="Attic Access?">
                  <select style={S.select} value={edits.roofing.atticAccess} onChange={e => eo("roofing", "atticAccess", e.target.value)}>
                    <option value="">—</option><option value="Yes">Yes</option><option value="No">No</option>
                  </select>
                </Field>
                <Field label="Building Height"><input style={S.input} value={edits.roof.buildingHeight} onChange={e => eo("roof", "buildingHeight", e.target.value)} /></Field>
                <Field label="Height to Eave"><input style={S.input} value={edits.roof.heightToEave} onChange={e => eo("roof", "heightToEave", e.target.value)} /></Field>
              </div>

              {[
                { label: "Roofing Type", key: "roofingType", otherKey: "roofingTypeOther", opts: ROOFING_TYPES },
              ].map(fld => (
                <Field key={fld.key} label={fld.label}>
                  <select style={S.select} value={edits.roof[fld.key]} onChange={e => eo("roof", fld.key, e.target.value)}>
                    <option value="">—</option>
                    {fld.opts.map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                  <input style={{ ...S.input, marginTop: 8 }} placeholder="Other..." value={edits.roof[fld.otherKey]} onChange={e => eo("roof", fld.otherKey, e.target.value)} />
                </Field>
              ))}

              <Field label="Additional Structure Details">
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {STRUCTURE_DETAILS.map(opt => (
                    <label key={opt} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.light, cursor: "pointer" }}>
                      <input type="checkbox" checked={(edits.structureDetails || []).includes(opt)} onChange={() => etoggle("structureDetails", opt)} /> {opt}
                    </label>
                  ))}
                  <input style={{ ...S.input, marginTop: 4, fontSize: 13 }} placeholder="Other details..." value={edits.structureDetailsOther} onChange={e => setEdits(p => ({ ...p, structureDetailsOther: e.target.value }))} />
                </div>
              </Field>
              <Field label="Roof Notes">
                <textarea style={S.textarea} rows={3} value={edits.roofNotes} onChange={e => setEdits(p => ({ ...p, roofNotes: e.target.value }))} />
              </Field>

              <SecTitle>Arrays</SecTitle>
              {(edits.arrays || []).length === 0 && <div style={{ color: C.muted, fontSize: 12, marginBottom: 10 }}>No arrays recorded on this survey.</div>}
              {(edits.arrays || []).map((a, i) => (
                <div key={i} style={{ padding: 12, background: C.surface, borderRadius: 8, marginBottom: 10, border: `1px solid ${C.border}` }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.gold }}>Array {a.id || i + 1}</div>
                    <button type="button" onClick={() => { if (window.confirm(`Remove Array ${a.id || i + 1}? Its photos stay in the survey's photo list.`)) setEdits(p => ({ ...p, arrays: p.arrays.filter((_, j) => j !== i) })); }}
                      style={{ background: "none", border: "none", color: C.danger, cursor: "pointer" }}>{I.trash}</button>
                  </div>
                  <Field label="Roof Plane"><input style={S.input} value={a.roof || ""} onChange={e => ea(i, "roof", e.target.value)} /></Field>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Field label="Pitch"><input style={S.input} value={a.angle || ""} onChange={e => ea(i, "angle", e.target.value)} /></Field>
                    <Field label="Azimuth"><input style={S.input} value={a.azimuth || ""} onChange={e => ea(i, "azimuth", e.target.value)} /></Field>
                    <Field label="Structure Type"><select style={S.select} value={a.structureType || ""} onChange={e => ea(i, "structureType", e.target.value)}>
                      <option value="">—</option>
                      {[...STRUCTURE_TYPES, ...(a.structureType && !STRUCTURE_TYPES.includes(a.structureType) ? [a.structureType] : [])].map(o => <option key={o} value={o}>{o}</option>)}
                    </select></Field>
                    <Field label="Rafter / Truss Size"><select style={S.select} value={a.rafterSize || ""} onChange={e => ea(i, "rafterSize", e.target.value)}>
                      <option value="">—</option>
                      {[...RAFTER_SIZES, ...(a.rafterSize && !RAFTER_SIZES.includes(a.rafterSize) ? [a.rafterSize] : [])].map(o => <option key={o} value={o}>{o}</option>)}
                    </select></Field>
                    <Field label="Spacing"><select style={S.select} value={a.rafterSpacing || ""} onChange={e => ea(i, "rafterSpacing", e.target.value)}>
                      <option value="">—</option>
                      {[...RAFTER_SPACINGS, ...(a.rafterSpacing && !RAFTER_SPACINGS.includes(a.rafterSpacing) ? [a.rafterSpacing] : [])].map(o => <option key={o} value={o}>{o}</option>)}
                    </select></Field>
                  </div>
                  <Field label="Notes"><input style={S.input} value={a.notes || ""} onChange={e => ea(i, "notes", e.target.value)} /></Field>
                </div>
              ))}
              <button type="button" onClick={() => setEdits(p => ({ ...p, arrays: [...(p.arrays || []), { id: Math.max(0, ...(p.arrays || []).map(x => Number(x.id) || 0)) + 1, roof: "", angle: "", azimuth: "", structureType: "", rafterSize: "", rafterSpacing: "", notes: "", photoUrls: [] }] }))}
                style={{ ...S.btnS, width: "100%", justifyContent: "center", marginBottom: 6 }}>{I.plus} Add Array</button>

              <SecTitle>Adders</SecTitle>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[
                  ["roofPitch", "Steep Roof Adder"], ["trenching", "Trenching"], ["conduitRun", "Conduit Run"],
                  ["battery", "Battery"], ["rooftopAccess", "Rooftop Access"], ["rentals", "Rentals"],
                  ["roofAttachments", "Roof Attachments"], ["electricalUpgrades", "Electrical Upgrades"],
                  ["groundingViolations", "Grounding Violations"], ["other", "Other"],
                ].map(([k, lbl]) => (
                  <Field key={k} label={lbl}><input style={S.input} value={edits.adders[k]} onChange={e => eo("adders", k, e.target.value)} /></Field>
                ))}
              </div>

              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                <button onClick={() => setEditing(false)} style={{ ...S.btnS, flex: 1, justifyContent: "center" }}>Cancel</button>
                <button onClick={saveEdit} disabled={savingEdit} style={{ ...S.btnP, flex: 2, justifyContent: "center", opacity: savingEdit ? 0.6 : 1 }}>
                  {savingEdit ? "Saving..." : <>{I.check} Save Changes</>}
                </button>
              </div>
            </div>
          )}

          {detail.type === "Service Assessment" && (detail.missingPhotos || []).length > 0 && (
            <div style={{ marginTop: 16, padding: "10px 12px", background: `${C.danger}12`, border: `1px solid ${C.danger}44`, borderRadius: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.danger, marginBottom: 4 }}>Required photos not captured — shows on the PDF</div>
              <div style={{ fontSize: 12, color: C.light, marginBottom: 8 }}>{detail.missingPhotos.join(" · ")}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                <button onClick={async () => {
                  if (!window.confirm("Remove the 'Required photos not captured' notice from this report for good? The photos stay as they are; only the notice goes.")) return;
                  const ok = await db.updateSurveyFields(detail.id, { missingPhotos: [], missingPhotosClearedBy: "admin", missingPhotosClearedAt: new Date().toISOString() });
                  if (!ok && USE_SUPABASE) return alert("Could not update the report — check your connection.");
                  const cleared = { ...detail, missingPhotos: [] };
                  setSubmissions(p => p.map(s => s === detail || (s.id && s.id === detail.id) ? cleared : s));
                  setDetail(cleared);
                }} style={{ ...S.btnS, fontSize: 12, padding: "6px 12px", color: C.danger, borderColor: `${C.danger}66` }}>Remove notice permanently</button>
                <span style={{ fontSize: 11, color: C.muted }}>or use "Export without notice" below to leave the record as is</span>
              </div>
            </div>
          )}

          <div style={{ display: "flex", gap: 8, marginTop: 20, flexWrap: "wrap" }}>
            {!editing && detail.type === "Site Survey" && (
              <button onClick={() => startEdit(detail)} style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 130, borderColor: C.gold, color: C.gold }}>{I.clip} Edit</button>
            )}
            {!saEditing && detail.type === "Service Assessment" && (
              <button onClick={() => startSaEdit(detail)} style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 130, borderColor: C.gold, color: C.gold }}>{I.clip} Edit</button>
            )}
            <button onClick={() => generatePDF(detail)} style={{ ...S.btnP, flex: 1, justifyContent: "center", minWidth: 130 }}>{I.pdf} Export PDF</button>
            {detail.type === "Service Assessment" && (detail.missingPhotos || []).length > 0 && (
              <button onClick={() => generatePDF(detail, { hideMissing: true })} style={{ ...S.btnS, flex: 1, justifyContent: "center", minWidth: 130 }} title="Same PDF without the red 'Required photos not captured' box">{I.pdf} Export without notice</button>
            )}
            {!saEditing && !editing && (
              <button onClick={() => {
                setSubmissions(p => p.map(s => (s === detail || (s.id && s.id === detail.id)) ? { ...s, status: "approved" } : s));
                if (detail.id) db.updateStatus(detail.type === "JHA" ? "jha_submissions" : "survey_submissions", detail.id, "approved");
                if (detail.type !== "JHA") setPostApprove({ type: detail.type, site: detail.siteName || detail.client?.name || "", projectId: detail.project_id || "", partner: detail.partnerName || "", due: detail.submitDue || "", serviceType: detail.serviceType || "" });
                setDetail(null);
              }} style={{ ...S.btnSuccess, flex: 1, justifyContent: "center", minWidth: 130 }}>{I.checkCircle} Approve</button>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────
export default function App() {
  const [user, setUser] = useState(null);
  const [page, setPage] = useState("dashboard");
  const [jobMapFocus, setJobMapFocus] = useState(null); // project id to open on the Job Map
  const [submissions, setSubmissions] = useState([]);
  const [showSuccess, setShowSuccess] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isOnline, setIsOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [lang, setLang] = useState(getLang());
  const t = T[lang] || T.en;

  // Track online/offline status
  useEffect(() => {
    const on = () => setIsOnline(true), off = () => setIsOnline(false);
    window.addEventListener("online", on); window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  // Load saved submissions when user logs in
  const loadSubmissions = useCallback(async () => {
    setLoadingData(true);
    try {
      const [jhas, surveys] = await Promise.all([db.getJHAs(), db.getSurveys({ light: true })]);
      const allJHA = (jhas || []).map(mapJHARow);
      const allSurvey = (surveys || []).map(mapSurveyRow);
      const all = [...allJHA, ...allSurvey].sort((a, b) => new Date(a.submittedAt) - new Date(b.submittedAt));
      setSubmissions(all);
      localCache.saveSubmissions(all);
    } catch (e) {
      console.warn("Failed to load from server, using cache:", e);
      setSubmissions(localCache.loadSubmissions());
    }
    // Check offline queue
    try {
      const pending = await offlineQueue.getAll();
      setPendingCount(pending.length);
    } catch(e) {}
    setLoadingData(false);
  }, []);

  // Sync offline queue when coming back online
  const syncOffline = useCallback(async () => {
    if (!user || syncing) return;
    setSyncing(true);
    try {
      const results = await offlineQueue.syncAll(user.id);
      if (results.synced > 0) {
        await loadSubmissions();
      }
      const pending = await offlineQueue.getAll();
      setPendingCount(pending.length);
    } catch(e) { console.warn("Sync error:", e); }
    setSyncing(false);
  }, [user, syncing, loadSubmissions]);

  // Auto-sync when coming online
  const prevOnline = useRef(isOnline);
  useEffect(() => {
    if (isOnline && !prevOnline.current && user) syncOffline();
    prevOnline.current = isOnline;
  }, [isOnline, user, syncOffline]);
  // Refresh the submission list when the tab comes back to the foreground —
  // the office keeps the Hub open all day and crew file forms in between.
  useEffect(() => {
    if (!user) return;
    const onVis = () => { if (document.visibilityState === "visible") loadSubmissions(); };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [user, loadSubmissions]);

  // Load data when user logs in
  const handleLogin = useCallback(async (userData) => {
    setUser(userData);
    localCache.saveUser(userData);
    // Small delay to let React render, then load
    setTimeout(() => loadSubmissions(), 100);
  }, [loadSubmissions]);

  // Try auto-login from cache
  const checkedCache = useRef(false);
  if (!checkedCache.current && !user) {
    checkedCache.current = true;
    const cached = localCache.loadUser();
    if (cached && cached.email) {
      setUser(cached);
      setTimeout(() => loadSubmissions(), 100);
    }
  }

  const isAdmin = user?.role === "admin";
  const isSafety = user?.role === "safety" || isAdmin;
  const isCoordinator = user?.role === "coordinator";
  // Coordinator needs admin-level VISIBILITY in projects and schedule —
  // full pipeline, invoice aging, crew assignment — without the admin panel.
  const canManage = isAdmin || isCoordinator;
  const moneyOk = canSeeMoney(user);

  // Deep link from calendar invites: hub.solarability.com/#procedures/<id>
  const [procFocus, setProcFocus] = useState(null);
  const [procRunFor, setProcRunFor] = useState(null); // project id to start a run against
  const [truckInit, setTruckInit] = useState(null);   // { list, job } to open Truck Check with
  const [formForProject, setFormForProject] = useState(null); // open a form pre-linked to this job
  const [formVisit, setFormVisit] = useState(null);           // …and to one of its visits
  const [sunBreak, setSunBreak] = useState(false);
  const [projectPartner, setProjectPartner] = useState(null);  // preset the Projects partner filter
  const [projectOpenId, setProjectOpenId] = useState(null);    // open this job's editor straight away
  // Leaving the form clears the pre-link, so opening a fresh one from the
  // sidebar later isn't still tied to the last job.
  useEffect(() => {
    if (!["survey", "assessment", "install", "jha"].includes(page)) { setFormForProject(null); setFormVisit(null); }
  }, [page]);
  useEffect(() => {
    const applyHash = () => {
      const h = (window.location.hash || "").replace(/^#/, "");
      if (!h) return;
      const [route, id, sub, extra] = h.split("/").map(decodeURIComponent);
      if (route === "procedures") {
        setPage("procedures");
        if (id) setProcFocus(id);
        // #procedures/<id>/run/<projectId> — open straight into a run for that job
        // #procedures/<id>/resume/<runId> — reopen a saved, unfinished run
        setProcRunFor(sub === "run" && extra ? extra : sub === "resume" && extra ? `resume:${extra}` : null);
      }
      if (route === "truckcheck") {
        // #truckcheck/<list>/<job>
        setTruckInit({ list: id || null, job: sub || "" });
        setPage("truckcheck");
      }
      if (route === "schedule") setPage("schedule");
      if (route === "crew") setPage("crew");
      if (route === "contacts") setPage("contacts");
      if (route === "quotes") setPage("quotes");
      if (route === "estimator") setPage("estimator");
      // #projects/<partner> — open Projects filtered to that partner
      if (route === "projects") {
        if (id === "id" && sub) { setProjectOpenId(sub); setProjectPartner(null); }
        else setProjectPartner(id || null);
        setPage("projects");
      }
      if (route === "admin") setPage("admin");
      if (route === "office") setPage("office");
      if (route === "dashboard") setPage("dashboard");
      // #form/assessment/<projectId> or #form/survey/<projectId>
      if (route === "form" && (id === "assessment" || id === "survey" || id === "install" || id === "jha")) {
        setFormForProject(sub || null);
        setFormVisit(extra || null);
        setPage(id);
      }
    };
    applyHash();
    window.addEventListener("hashchange", applyHash);
    return () => window.removeEventListener("hashchange", applyHash);
  }, []);

  const handleSubmit = useCallback(async (data) => {
    setSubmissions(p => { const next = [...p, data]; localCache.saveSubmissions(next); return next; });
    setShowSuccess(data.type);
    // Check if it was saved offline
    try {
      const pending = await offlineQueue.getAll();
      setPendingCount(pending.length);
    } catch(e) {}
  }, []);

  const closeSuccess = useCallback((where) => { setShowSuccess(null); if (where === "schedule") { window.location.hash = "schedule"; setPage("schedule"); } else setPage("dashboard"); }, []);

  const counts = useMemo(() => ({
    total: submissions.length,
    pending: submissions.filter(s => s.status === "submitted").length,
  }), [submissions]);

  if (!user) return <LoginScreen onLogin={handleLogin} />;

  return (
    <div style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif", background: `radial-gradient(ellipse at 20% 0%, ${C.card} 0%, ${C.bg} 60%)`, minHeight: "100vh", color: C.light }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800;900&display=swap');
        @keyframes fadeIn { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes slideR { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        * { box-sizing: border-box; }
        /* Long URLs and serial numbers wrap instead of stretching the page */
        #root div, #root span, #root td, #root a, #root li, #root p { overflow-wrap: anywhere; min-width: 0; }
        #root img, #root svg, #root canvas, #root video { max-width: 100%; }
        input:focus, textarea:focus, select:focus { border-color: ${C.gold} !important; }
        button:hover { opacity: 0.9; }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-thumb { background: ${C.border}; border-radius: 3px; }
        @media (min-width: 769px) { .sb-desktop { display: flex !important; flex-direction: column; } .mob-hdr { display: none !important; } .main-c { margin-left: 250px !important; } }
        @media (max-width: 768px) {
          .main-c { margin-left: 0 !important; padding: 16px !important; padding-top: 68px !important; }
          .main-c div[style*="grid-template-columns: 1fr 1fr"], .main-c div[style*="grid-template-columns:1fr 1fr"] { grid-template-columns: 1fr !important; }
          input, select, textarea { font-size: 16px !important; } /* prevents iOS zoom on focus */
          button { min-height: 42px; }
        }
      `}</style>

      <Sidebar page={page} setPage={setPage} user={user} onLogout={() => { db.signOut(); setUser(null); localCache.save("user", null); }} counts={counts} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} lang={lang} setLang={setLang} t={t} />

      {sunBreak && <SunBreak onClose={() => setSunBreak(false)} />}

      {/* Mobile Header */}
      <div className="mob-hdr" style={{ position: "fixed", top: 0, left: 0, right: 0, height: 54, background: C.card, borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px", zIndex: 90 }}>
        <button onClick={() => setMobileOpen(true)} style={{ background: "none", border: "none", color: C.light, cursor: "pointer" }}>{I.menu}</button>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <LogoMark size={26} onClick={() => setSunBreak(true)} />
          <span style={{ fontWeight: 800, fontSize: 14 }}>SolarAbility</span>
        </div>
        <div style={{ width: 24 }} />
      </div>

      {/* Connection Status Banner */}
      {(!isOnline || pendingCount > 0) && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200, padding: "10px 20px", display: "flex", alignItems: "center", justifyContent: "center", gap: 12,
          background: !isOnline ? C.warnBg : C.infoBg, borderTop: `1px solid ${!isOnline ? `${C.warning}44` : `${C.info}44`}` }}>
          <div style={{ width: 8, height: 8, borderRadius: "50%", background: !isOnline ? C.warning : C.info, animation: !isOnline ? "pulse 2s infinite" : "none" }} />
          <span style={{ fontSize: 13, color: !isOnline ? C.warning : C.info, fontWeight: 600 }}>
            {!isOnline ? "You're offline — forms will be saved locally and synced when you reconnect" :
              syncing ? "Syncing offline submissions..." :
              `${pendingCount} form(s) saved offline`}
          </span>
          {isOnline && pendingCount > 0 && !syncing && (
            <button onClick={syncOffline} style={{ ...S.btnP, padding: "6px 16px", fontSize: 12 }}>Sync Now</button>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="main-c" style={{ marginLeft: 250, padding: page === "truckcheck" ? 0 : "28px 36px", minHeight: "100vh", paddingBottom: page === "truckcheck" ? 0 : ((!isOnline || pendingCount > 0) ? 60 : 28) }}>
        {loadingData && (
          <div style={{ textAlign: "center", padding: 40, color: C.muted }}>
            <div style={{ animation: "pulse 1.5s infinite", fontSize: 14 }}>Loading your submissions...</div>
          </div>
        )}
        {!loadingData && page === "dashboard" && canManage && <OfficeHandoffCard user={user} isAdmin={isAdmin} setPage={setPage} />}
        {!loadingData && page === "dashboard" && canManage && <TodayQueue user={user} submissions={submissions} setPage={setPage} />}
        {!loadingData && page === "dashboard" && <Dashboard submissions={submissions} setPage={setPage} />}
        {page === "jha" && <JHAForm key={`jha-${formForProject || ""}`} onSubmit={handleSubmit} onBack={() => setPage("dashboard")} user={user} initialProjectId={formForProject} initialVisitId={formVisit} />}
        {page === "survey" && <SurveyForm key={`survey-${formForProject || ""}`} onSubmit={handleSubmit} onBack={() => setPage("dashboard")} user={user} initialProjectId={formForProject} initialVisitId={formVisit} />}
        {page === "assessment" && <ServiceAssessment key={`assess-${formForProject || ""}`} onSubmit={handleSubmit} onBack={() => setPage("dashboard")} user={user} initialProjectId={formForProject} initialVisitId={formVisit} />}
        {page === "install" && <ServiceAssessment key={`install-${formForProject || ""}`} mode="install" onSubmit={handleSubmit} onBack={() => setPage("dashboard")} user={user} initialProjectId={formForProject} initialVisitId={formVisit} />}
        {page === "truckcheck" && <TruckCheck key={truckInit ? `${truckInit.list}|${truckInit.job}` : "default"} initialList={truckInit?.list} initialJob={truckInit?.job} />}
        {page === "procedures" && <Procedures user={user} t={t} isAdmin={isAdmin} focusId={procFocus} runForProject={procRunFor} onFocusHandled={() => { setProcFocus(null); setProcRunFor(null); }} />}
        {page === "schedule" && <ScheduleView user={user} t={t} isAdmin={isAdmin} canManage={canManage} />}
        {page === "jobmap" && moneyOk && <JobMap supabase={supabase} db={db} user={user} C={C} S={S} t={t} lang={lang} focusId={jobMapFocus} onFocusHandled={() => setJobMapFocus(null)} />}
        {page === "projects" && <ProjectTracker initialProjectId={projectOpenId} onProjectOpened={() => setProjectOpenId(null)} initialPartner={projectPartner} onPartnerHandled={() => setProjectPartner(null)} user={user} t={t} isAdmin={isAdmin} canManage={canManage}
          onOpenMap={moneyOk ? (id) => { setJobMapFocus(id); setPage("jobmap"); } : undefined} />}
        {page === "inventory" && <InventoryTracker user={user} t={t} />}
        {page === "safety" && isSafety && <SafetyCenter user={user} t={t} isAdmin={isAdmin} />}
        {page === "costing" && moneyOk && <CostTracker user={user} t={t} />}
        {page === "partners" && isAdmin && <PartnerDirectory user={user} t={t} />}
        {page === "crew" && moneyOk && <CrewView user={user} t={t} lang={lang} />}
        {page === "contacts" && moneyOk && <CompanyContacts user={user} t={t} lang={lang} canManage={moneyOk} />}
        {page === "quotes" && moneyOk && <ServiceQuotes user={user} t={t} lang={lang} canManage={moneyOk} />}
        {page === "estimator" && moneyOk && <RREstimator user={user} t={t} lang={lang} />}
        {page === "admin" && (isAdmin || isSafety || isCoordinator) && <AdminPanel submissions={submissions} setSubmissions={setSubmissions} />}
        {page === "office" && (isAdmin || isCoordinator) && <OfficeBoard user={user} t={t} isAdmin={isAdmin} />}
      </div>

      <PhotoViewer />
      {showSuccess && (
        <Modal onClose={closeSuccess}>
          <div style={{ textAlign: "center" }}>
            <div style={{ width: 64, height: 64, borderRadius: "50%", background: C.successBg, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
              <svg width="32" height="32" fill="none" viewBox="0 0 24 24"><path d="M5 13l4 4L19 7" stroke={C.success} strokeWidth="3" strokeLinecap="round"/></svg>
            </div>
            <h3 style={{ margin: "0 0 8px", fontSize: 20, fontWeight: 700, color: C.light }}>Submitted Successfully!</h3>
            <p style={{ color: C.muted, margin: "0 0 6px", fontSize: 14 }}>Your {showSuccess} has been saved{isOnline ? " to the database" : " offline"}.</p>
            {!isOnline && <p style={{ color: C.warning, margin: "0 0 6px", fontSize: 13 }}>It will sync automatically when you're back online.</p>}
            <p style={{ color: C.muted, margin: "0 0 24px", fontSize: 13 }}>{isOnline ? "The office has been notified." : "The office will be notified when it syncs."}</p>
            <div style={{ display: "flex", gap: 8, justifyContent: "center", flexWrap: "wrap" }}>
              <button style={S.btnP} onClick={() => closeSuccess("schedule")}>{I.calendar || null} Back to today's schedule</button>
              <button style={S.btnS} onClick={() => closeSuccess("dashboard")}>Dashboard</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
